import Konva from "konva";
import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import { GlobalWorkerOptions, getDocument } from "pdfjs-dist";
import pdfWorkerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import { Fragment, useEffect, useMemo, useRef, useState, type CSSProperties, type ChangeEvent } from "react";
import { Circle, Group, Image as KonvaImage, Layer, Line, Rect, Stage, Text } from "react-konva";
import { useParams } from "react-router-dom";
import {
  patrolHipsSpans,
  patrolWaveSpans,
  type PatrolSettings,
  type PatrolSystemForSettings,
} from "../../../http/patrol/types/patrolSettings";
import { usePatrolSettings } from "../../../http/patrol/usePatrolSettings";
import { useProjectsApi } from "../../../http/useProjects";
import { getMinutesRoundedTimestamp } from "../../../utils";
import { filterShuttles } from "../patrol/patrolUtils";
import multiGableRoofIconUrl from "./MultiGableRoofs.svg?url";
import multiShedRoofIconUrl from "./MultiShedRoof.svg?url";
import type { PatrolProps } from "./Patrol";
import singlePitchedRoofIconUrl from "./SinglePitchedRoof.svg?url";
import squaredFlatRoofIconUrl from "./SquaredFlatRoof.svg?url";
import curvedRoofIconUrl from "./curvedRoof.svg?url";
import flatRoofIconUrl from "./flatRoof.svg?url";
import hipRoofIconUrl from "./hiproof.svg?url";
import { BackgroundToolbar } from "./konva-canvas/BackgroundToolbar";
import { CanvasIconNode } from "./konva-canvas/CanvasIconNode";
import { GoogleMapBackgroundDialog, type GoogleMapCapture } from "./konva-canvas/GoogleMapBackgroundDialog";
import { IconPickerPanel } from "./konva-canvas/IconPickerPanel";
import { LayerPanel } from "./konva-canvas/LayerPanel";
import { PrimitiveStatusBar } from "./konva-canvas/PrimitiveStatusBar";
import {
  PrintExportPanel,
  type PrintColorMode,
  type PrintColorPreview,
  type PrintPaperFormat,
  type PrintTitleBlockMode,
} from "./konva-canvas/PrintExportPanel";
import { SettingsPanel } from "./konva-canvas/SettingsPanel";
import { buildAutomaticLifeLinePaths, type AutomaticLifeLineSourceShape } from "./konva-canvas/automaticLifeLineRules";
import {
  cloneBackgroundAsset as cloneBackgroundAssetRule,
  cloneHistorySnapshot as cloneHistorySnapshotRule,
  clonePoint as clonePointRule,
} from "./konva-canvas/backgroundHistoryUtils";
import { AUTOCAD_STANDARD_COLORS, getCadColorValue, type CadColorName } from "./konva-canvas/cadColors";
import {
  createCanvasObjectStoragePayload,
  restoreCanvasObjectsFromPayload,
  type CanvasObjectStoragePayload,
} from "./konva-canvas/canvasObjectStorage";
import {
  buildAutoDimensionsForShape as buildAutoDimensionsForShapeRule,
  getArrowHeadPoints as getArrowHeadPointsRule,
  getDimensionLayout as getDimensionLayoutRule,
  getDimensionOffsetFromPoint as getDimensionOffsetFromPointRule,
  getSlashMarkerPoints as getSlashMarkerPointsRule,
} from "./konva-canvas/dimensionRules";
import {
  getGripAnchorPoint as getGripAnchorPointRule,
  getGripCurrentPoint as getGripCurrentPointRule,
  getShapeGripPoints as getShapeGripPointsRule,
  isHoleGripUpdateValid as isHoleGripUpdateValidRule,
} from "./konva-canvas/gripRules";
import { CANVAS_ICON_LIBRARY } from "./konva-canvas/iconLibrary";
import {
  createGripClickHandler as createGripClickHandlerRule,
  createGripDragHandlers as createGripDragHandlersRule,
  handleCanvasDoubleClick as handleCanvasDoubleClickRule,
  handleCanvasMouseDown as handleCanvasMouseDownRule,
  handleCanvasMouseLeave as handleCanvasMouseLeaveRule,
  handleCanvasMouseMove as handleCanvasMouseMoveRule,
  handleCanvasMouseUp as handleCanvasMouseUpRule,
  handleCanvasWheel as handleCanvasWheelRule,
} from "./konva-canvas/interactionHandlers";
import { calculateRenderedLifeLines, type VertexCode } from "./konva-canvas/life-line-algorithm";
import {
  applyMoveState as applyMoveStateRule,
  buildMoveState as buildMoveStateRule,
  duplicateCopiedEntitiesInSequence as duplicateCopiedEntitiesInSequenceRule,
  duplicateEntities as duplicateEntitiesRule,
  parseCopySequenceCount as parseCopySequenceCountRule,
} from "./konva-canvas/moveCopyRules";
import {
  buildRectanglePolyline as buildRectanglePolylineRule,
  buildSinglePointCircleState as buildSinglePointCircleStateRule,
  getActiveCircleRadius as getActiveCircleRadiusRule,
} from "./konva-canvas/primitiveRules";
import {
  buildCurvedRoofGeometry as buildCurvedRoofGeometryRule,
  buildHipOrValley as buildHipOrValleyRule,
  buildMultiGableRoofGeometry as buildMultiGableRoofGeometryRule,
  buildMultiShedRoofGeometry as buildMultiShedRoofGeometryRule,
  buildRoofGeometry as buildRoofGeometryRule,
  buildSinglePitchedRoofGeometry as buildSinglePitchedRoofGeometryRule,
  buildTwoPitchRoofGeometry as buildTwoPitchRoofGeometryRule,
  normalizeRoofOutline as normalizeRoofOutlineRule,
} from "./konva-canvas/roofRules";
import {
  boundsIntersect as boundsIntersectRule,
  getDimensionBounds as getDimensionBoundsRule,
  getSelectionBounds as getSelectionBoundsRule,
  getShapeBounds as getShapeBoundsRule,
} from "./konva-canvas/selectionBounds";
import {
  addDimensionOsnapCandidates as addDimensionOsnapCandidatesRule,
  addShapeOsnapCandidates as addShapeOsnapCandidatesRule,
} from "./konva-canvas/snappingRules";
import type {
  BackgroundAsset,
  BackgroundCropRect,
  BackgroundReference,
  BackgroundReferenceInput,
  BackgroundReferenceLine,
  HistorySnapshot,
  PanState,
  PendingHistoryAction,
  SelectionBox,
  ViewportState,
} from "./konva-canvas/stateTypes";
import {
  buildOffsetShapePreview as buildOffsetShapePreviewRule,
  buildOffsetSourceShape as buildOffsetSourceShapeRule,
} from "./konva-canvas/transformRules";
import type {
  ArcShape,
  CanvasIconEntity,
  CanvasPoint,
  CircleHole,
  CircleShape,
  ConstructionLine,
  Dimension,
  EntityLayerName,
  EntityStyleKind,
  HatchPattern,
  HoleGripKind,
  MoveState,
  OffsetSourceContour,
  OsnapPoint,
  OsnapType,
  PolylineLikeShape,
  Shape,
  ShapeGripDescriptor,
  TextEntity,
  TextJustificationPoint,
} from "./konva-canvas/types";
import twoPitchRoofIconUrl from "./tetto2Falde.svg?url";

type ToolMode =
  | "select"
  | "roof"
  | "multiGableRoof"
  | "gableRoof"
  | "curvedRoof"
  | "singlePitchedRoof"
  | "multiShedRoof"
  | "roofGronda"
  | "roofColmo"
  | "roofDiagonal"
  | "roofSide"
  | "roofHipValley"
  | "move"
  | "copy"
  | "scale"
  | "rotate"
  | "offset"
  | "dimensionObject"
  | "polyline"
  | "buildPolyline"
  | "hatch"
  | "obstaclePolyline"
  | "obstacleCircle"
  | "holePolyline"
  | "holeCircle"
  | "singlePointCircle"
  | "singleLifeLine"
  | "automaticLifeLine"
  | "pointSeries"
  | "rectangle"
  | "circle"
  | "arc"
  | "ruler"
  | "text"
  | "icon"
  | "dimension";

type HoleToolMode = "holePolyline" | "holeCircle";
type ObstacleToolMode = "obstaclePolyline" | "obstacleCircle";
type SurfaceFeatureToolMode = HoleToolMode | ObstacleToolMode;
type CurveToolMode = "buildPolyline" | "circle" | "arc" | "text" | "hatch" | "icon";

type RoofCreationToolMode =
  | "roof"
  | "multiGableRoof"
  | "gableRoof"
  | "curvedRoof"
  | "singlePitchedRoof"
  | "multiShedRoof"
  | "polyline"
  | "rectangle";

type RoofGroupCategory = "Flat" | "Curved" | "Pitched";
type FloatingPanelId =
  | "background"
  | "drawing"
  | "system"
  | "settingsDock"
  | "exportDock"
  | "settingsPanel"
  | "billOfMaterials"
  | "layerPanel"
  | "propertiesPanel"
  | "printExport"
  | "iconPicker";

const DEFAULT_FLOATING_PANEL_Z_INDEXES: Record<FloatingPanelId, number> = {
  background: 30,
  drawing: 24,
  system: 23,
  settingsDock: 21,
  exportDock: 21,
  settingsPanel: 25,
  billOfMaterials: 25,
  layerPanel: 25,
  propertiesPanel: 25,
  printExport: 25,
  iconPicker: 25,
};

const normalizeRoofCompatibilityText = (value: string | undefined) =>
  (value ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const isCurvedRoofCompatibilityName = (value: string | undefined) => {
  const normalized = normalizeRoofCompatibilityText(value);
  return (
    normalized.includes("curv") ||
    normalized.includes("round") ||
    normalized.includes("barrel") ||
    normalized.includes("vault") ||
    normalized.includes("arc")
  );
};

type LineType = "solid" | "dashed" | "dashed-x2" | "dash-dot" | "dash-dot-x2" | "dot-dot" | "dot-dot-x2";
type RoofComponentLabelPosition = "bottom" | "middle" | "top";
type VertexShape = "circle" | "square" | "cross";
type SafetySystemSettings = {
  vertexShape: VertexShape;
  strokeWidth: number;
  lineType: LineType;
  maxSpacing: number;
  maxSingleLineLength: number;
  maxCornersPerLifeline: number;
  chamferAcuteAngles: boolean;
  chamferSegmentLength: number;
  intermediateAngleMax: number;
  rectCurveMinAngle: number;
  rectCurveMaxAngle: number;
  bendCurveMinAngle: number;
  bendCurveMaxAngle: number;
};
type EntityTypeStyle = {
  strokeColor: string;
  strokeWidth: number;
  lineType: LineType;
  vertexColor: string;
  vertexSize: number;
  vertexShape: VertexShape;
  gripColor: string;
  gripSize: number;
  labelText: string;
  labelHeight: number;
  labelPosition: RoofComponentLabelPosition;
};
const LINE_TYPE_VALUES: readonly LineType[] = [
  "solid",
  "dashed",
  "dashed-x2",
  "dash-dot",
  "dash-dot-x2",
  "dot-dot",
  "dot-dot-x2",
];
const LINE_TYPE_LABELS: Record<LineType, string> = {
  solid: "Solid",
  dashed: "Dashed",
  "dashed-x2": "Dashed x2",
  "dash-dot": "Dash-Dot",
  "dash-dot-x2": "Dash-Dot x2",
  "dot-dot": "Dot-Dot",
  "dot-dot-x2": "Dot-Dot x2",
};
const VERTEX_SHAPE_VALUES: readonly VertexShape[] = ["circle", "square", "cross"];
const ROOF_COMPONENT_LABEL_POSITION_VALUES: readonly RoofComponentLabelPosition[] = ["bottom", "middle", "top"];

type DraggedGrip = {
  polylineId: string;
  vertexIndex: number;
  linkedVertexIndices?: number[];
  holeIndex?: number;
  holeGripKind?: HoleGripKind;
  anchor: CanvasPoint;
  shapeKind: Shape["kind"];
};

type SegmentStretchState = {
  polylineId: string;
  vertexIndices: number[];
  originalPoints: number[];
  startPointer: CanvasPoint;
  shapeKind: PolylineLikeShape["kind"];
  holeIndex?: number;
  excludedPoints?: CanvasPoint[];
  excludedSegments?: SnapExcludedSegment[];
};

type ActiveRectangle = {
  start: CanvasPoint;
  edgeEnd: CanvasPoint | null;
  pointer: CanvasPoint;
};

type ActiveCircle = {
  center: CanvasPoint;
  pointer: CanvasPoint;
};

type ActiveArc = {
  center: CanvasPoint;
  start: CanvasPoint | null;
  pointer: CanvasPoint;
};

type MultiGableRoofDraftGeometry = ReturnType<typeof buildMultiGableRoofGeometryRule>;
type MultiGableRoofDraftLine = MultiGableRoofDraftGeometry["roofLines"][number];
type MultiGableRoofDraftStage =
  | "mainRakes"
  | "secondaryRakes"
  | "hipValleyEaveA"
  | "hipValleyEaveB"
  | "hipValleyRidgeA"
  | "hipValleyRidgeB";

type MultiGableRoofDraft = {
  outline: CanvasPoint[];
  mainRakes: number[];
  secondaryRakes: number[];
  roofLines: MultiGableRoofDraftLine[];
  hipsAndValleys: MultiGableRoofDraftLine[];
  stage: MultiGableRoofDraftStage;
  pendingType: "hip" | "valley";
  pendingEaveAIndex?: number;
  pendingEaveBIndex?: number;
  pendingRidgeA?: MultiGableRoofDraftLine;
};

type RoofHipValleyDraft = {
  stage: "eaveA" | "eaveB" | "ridgeA" | "ridgeB";
  eaveA?: PolylineLikeShape;
  eaveB?: PolylineLikeShape;
  ridgeA?: PolylineLikeShape;
  ridgeB?: PolylineLikeShape;
};

type SelectableEntitySnapshot = {
  id: string;
  name: string;
  strokeColor: string;
  strokeWidth: number;
  layer: EntityLayerName;
  roofGroupId?: string;
  roofGroupName?: string;
  roofGroupCategory?: RoofGroupCategory;
  roofTypeId?: string;
  roofTypeName?: string;
  roofPitch?: number;
  roofMaterialId?: string;
  roofMaterialName?: string;
  roofSubstructureId?: string;
  roofSubstructureName?: string;
  roofSubstructureHeight?: number;
  roofSubstructureSizeHeight?: number;
  roofSubstructureSizeWidth?: number;
  roofMetalPlateThickness?: number;
  roofSheetMaterialId?: string;
  roofSheetMaterialName?: string;
  roofUseOnlyAnchorPoints?: boolean;
  roofSystemId?: string;
  roofSystemName?: string;
  roofNormativeId?: string;
  roofNormativeName?: string;
  roofSeamsSpan?: number;
  roofInsulationThickness?: number;
  roofWaveSpan?: number;
  roofHipsSpan?: number;
  roofSupportMaterialId?: string;
  roofSupportMaterialName?: string;
  roofTerminalCurveSupportId?: string;
  roofTerminalCurveSupportCode?: string;
  roofTerminalCurveSupportName?: string;
  roofIntermediateSupportId?: string;
  roofIntermediateSupportCode?: string;
  roofIntermediateSupportName?: string;
  roofAnchorPointMaterialId?: string;
  roofAnchorPointMaterialName?: string;
  roofAnchorPointMappingId?: string;
  roofAnchorPointCode?: string;
  roofAnchorPointName?: string;
  roofComponentMaterialId?: string;
  roofComponentMaterialName?: string;
  roofComponentAbsorberId?: string;
  roofComponentAbsorberCode?: string;
  roofComponentAbsorberName?: string;
  roofComponentSystemTypeId?: string;
  roofComponentSystemTypeName?: string;
  roofFixed?: boolean;
  roofShuttleId?: string;
  roofShuttleCode?: string;
  roofShuttleName?: string;
  roofNrOfShuttles?: number;
  roofMembraneId?: string;
  roofMembraneName?: string;
  safetySystemId?: string;
  safetySystemName?: string;
  anchorPointAdditionalProductCodes?: string;
  anchorPointOptionalProductCodes?: string;
  isObstacle?: boolean;
  fallSide?: boolean;
  kind: "polyline" | "rectangle" | "circle" | "arc" | "dimension" | "construction-line" | "text" | "icon";
};

type SnapExcludedSegment = {
  start: CanvasPoint;
  end: CanvasPoint;
};

type ActiveDimension = {
  start: CanvasPoint;
  end: CanvasPoint | null;
  pointer: CanvasPoint;
};

type ActiveObjectDimension = {
  shapeId: string;
  offset: number;
};

type ActiveRuler = {
  start: CanvasPoint;
  end: CanvasPoint | null;
  pointer: CanvasPoint;
};
type LifeLineLabelVertex = {
  x: number;
  y: number;
  label: string;
  displayLabel: string;
};
type LifeLineLabelPlacement = {
  x: number;
  y: number;
};
type LifeLineVertexRoleOccurrence = {
  role: string;
  lineNumber: number;
};
type LifeLinePositionLabel = {
  position: number;
  displayLabel: string;
  ownerShapeId: string;
  ownerVertexIndex: number;
  occurrenceCount: number;
};
type SharedLifeLineVertexOccurrence = CanvasPoint & {
  shapeId: string;
  vertexIndex: number;
  lineNumber: number;
  roles: string[];
};
type LabelCollisionRect = {
  left: number;
  top: number;
  right: number;
  bottom: number;
};

type OsnapTrackingLine = {
  id: string;
  origin: CanvasPoint;
  start: CanvasPoint;
  end: CanvasPoint;
};

type AutoDimensionDefinition = {
  start: CanvasPoint;
  end: CanvasPoint;
  offset: number;
  dimensionType: "linear" | "radius";
};

type ActiveOffset = {
  entityId: string;
  pointer: CanvasPoint;
  sourceContour: OffsetSourceContour;
};

type ActiveHoleHost = {
  hostId: string;
};

type InvalidGripFeedback = {
  point: CanvasPoint;
};

type SelectionCycleState = {
  point: CanvasPoint;
  ids: string[];
  index: number;
};

type GripContextMenuState = {
  entityId: string;
  vertexIndex: number;
  linkedVertexIndices?: number[];
  holeIndex?: number;
  holeGripKind?: HoleGripKind;
  x: number;
  y: number;
};

type DirectionLockAxis = "horizontal" | "vertical";
type RoofComponentToolMode = "roofGronda" | "roofColmo" | "roofDiagonal" | "roofSide";
type SafetySystemOption = string;
type ShortcutCommand =
  | "select"
  | "rectangle"
  | "polyline"
  | "hatch"
  | "singlePointCircle"
  | "dimension"
  | "ruler"
  | "circle"
  | "arc"
  | "move"
  | "copy"
  | "scale"
  | "rotate"
  | "fit";
type TransformState = {
  mode: "scale" | "rotate";
  entityIds: string[];
  basePoint: CanvasPoint;
  referencePoint?: CanvasPoint;
};

type NumericEditState =
  | {
      kind: "polyline";
      anchor: CanvasPoint;
      direction: CanvasPoint;
      directionValue: string;
      position: CanvasPoint;
      value: string;
    }
  | {
      kind: "rectangle-base";
      start: CanvasPoint;
      direction: CanvasPoint;
      directionValue: string;
      position: CanvasPoint;
      value: string;
    }
  | {
      kind: "rectangle-height";
      start: CanvasPoint;
      edgeEnd: CanvasPoint;
      direction: CanvasPoint;
      directionValue: string;
      position: CanvasPoint;
      value: string;
    }
  | {
      kind: "multi-shed-count";
      rectangle: ActiveRectangle;
      totalLength: number;
      value: string;
    }
  | {
      kind: "circle";
      center: CanvasPoint;
      direction: CanvasPoint;
      directionValue: string;
      position: CanvasPoint;
      value: string;
    }
  | {
      kind: "arc";
      center: CanvasPoint;
      start: CanvasPoint | null;
      direction: CanvasPoint;
      directionValue: string;
      position: CanvasPoint;
      value: string;
    }
  | {
      kind: "single-anchor-point";
      center: CanvasPoint;
      position: CanvasPoint;
      value: string;
    }
  | {
      kind: "point-series";
      anchor: CanvasPoint;
      direction: CanvasPoint;
      directionValue: string;
      position: CanvasPoint;
      lineDistanceValue: string;
      value: string;
    }
  | {
      kind: "offset";
      entityId: string;
      position: CanvasPoint;
      value: string;
    }
  | {
      kind: "move";
      anchor: CanvasPoint;
      direction: CanvasPoint;
      directionValue: string;
      position: CanvasPoint;
      value: string;
    }
  | {
      kind: "scale-rotate";
      mode: "scale" | "rotate";
      basePoint: CanvasPoint;
      position: CanvasPoint;
      value: string;
    }
  | {
      kind: "grip";
      entityId: string;
      shapeKind: Shape["kind"];
      vertexIndex: number;
      linkedVertexIndices?: number[];
      holeIndex?: number;
      holeGripKind?: HoleGripKind;
      anchor: CanvasPoint;
      direction: CanvasPoint;
      directionValue: string;
      position: CanvasPoint;
      value: string;
    }
  | {
      kind: "segment-stretch";
      entityId: string;
      shapeKind: PolylineLikeShape["kind"];
      vertexIndices: number[];
      holeIndex?: number;
      originalPoints: number[];
      anchor: CanvasPoint;
      direction: CanvasPoint;
      directionValue: string;
      position: CanvasPoint;
      value: string;
    }
  | {
      kind: "dimension-object";
      entityId: string;
      position: CanvasPoint;
      value: string;
    }
  | {
      kind: "automatic-lifeline-offset";
      position: CanvasPoint;
      value: string;
    }
  | {
      kind: "tracking";
      origin: CanvasPoint;
      direction: CanvasPoint;
      directionValue: string;
      position: CanvasPoint;
      value: string;
    };

type UnitOption = "px" | "mm" | "cm" | "m" | "in" | "ft";
type DimensionMarkerStyle = "filled-arrow" | "slash" | "dot";
type DecimalSeparator = "comma" | "point";

type OsnapSettings = Record<OsnapType, boolean>;

const SELECT_HIT_THRESHOLD = 8;
const GRIP_RADIUS = 5;
const GRIP_HIT_STROKE = 18;
const OSNAP_THRESHOLD = 14;
const OSNAP_TRACKING_DELAY_MS = 1000;
const MIN_RECTANGLE_SIDE = 12;
const MIN_ZOOM = 0.000001;
const ZOOM_FACTOR = 1.08;
const FIT_MARGIN = 48;
const SINGLE_POINT_CIRCLE_DEFAULT_RADIUS = 2000;
const SETTINGS_STORAGE_KEY = "MyProject Cad Settings";
const SETTINGS_STORAGE_VERSION = 9;
const DEFAULT_ENTITY_LINE_WIDTH = 3;
const DEFAULT_PREVIEW_LINE_WIDTH = 1;
const DIMENSION_TEXT_GAP = 10;
const MIN_DIMENSION_TEXT_GAP = -48;
const MAX_DIMENSION_TEXT_GAP = 48;
const DIMENSION_TEXT_AVERAGE_CHAR_WIDTH_FACTOR = 0.5;
const AUTO_DIMENSION_OFFSET = 1500;
const AUTOMATIC_LIFELINE_DEFAULT_OFFSET = 2000;
const SELECTION_DRAG_THRESHOLD = 6;
const BACKGROUND_ID = "background-asset";
const DEFAULT_BACKGROUND_HEIGHT = 50000;
const DEFAULT_VIEW_HEIGHT = 25000;
const DEFAULT_GRID_SPACING = 1000;
const DEFAULT_GRID_SNAP_STEP = 100;
const DEFAULT_SHAPE_COLOR = "#0053b3";
const DEFAULT_DIMENSION_COLOR = "#495057";
const MAX_UNDO_HISTORY = 5;
const MAX_REDO_HISTORY = 1;
const STATUS_BAR_PLACEHOLDER = "Select a command or a an object";
const OBJECT_SELECTION_CURSOR =
  'url("data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%2224%22 height=%2224%22 viewBox=%220 0 24 24%22%3E%3Crect x=%227%22 y=%227%22 width=%2210%22 height=%2210%22 fill=%22none%22 stroke=%22%23000%22 stroke-width=%222%22/%3E%3C/svg%3E") 12 12, default';
const PRINT_PAPER_SIZES_MM: Record<PrintPaperFormat, { width: number; height: number }> = {
  A2: { width: 594, height: 420 },
  A3: { width: 420, height: 297 },
  A4: { width: 297, height: 210 },
};
const PRINT_LAYOUT_MARGIN_MM = 18;
const PRINT_TITLE_BLOCK_RESERVED_HEIGHT_MM = 42;
const PRINT_SCALE_STEPS = [1, 2, 5, 10, 20, 25, 50, 75, 100, 125, 150, 200, 250, 500, 1000];
const ROOF_LAYER: EntityLayerName = "Roof";
const SAFETY_LAYER: EntityLayerName = "Fall Protection Systems";
const DIMENSIONS_LAYER: EntityLayerName = "Dimensions";
const LABELS_LAYER: EntityLayerName = "Labels";
const TEXT_LAYER: EntityLayerName = "Text";
const TEXT_TOOL_FONTS = ["Arial", "Helvetica", "Times New Roman", "Courier New", "Verdana"] as const;
const DEFAULT_TEXT_TOOL_HEIGHT = "20";
const DEFAULT_TEXT_TOOL_VALUE = "Text";
const DEFAULT_TEXT_TOOL_FONT = "Arial";
const DEFAULT_TEXT_TOOL_JUSTIFICATION: TextJustificationPoint = "left";
const DEFAULT_TEXT_TOOL_COLOR = getCadColorValue("15");
const AUTO_CREATE_ROOF_COMPONENT_LABELS = false;
const DEFAULT_HATCH_PATTERN: HatchPattern = "diagonal";
const DEFAULT_HATCH_SCALE = "1";
const DEFAULT_HATCH_COLOR = getCadColorValue("8");
const DEFAULT_HATCH_MODE: "pick" | "build" = "pick";
const DEFAULT_ICON_TOOL_COLOR = getCadColorValue("6");
const DEFAULT_ICON_TOOL_SCALE = "1";
const ICON_BASE_SIZE = 48;
const ICON_SCALE_TO_CANVAS_FACTOR = 10;
const DEFAULT_ICON_LAYER: EntityLayerName = LABELS_LAYER;
const HATCH_BASE_SPACING = 250;
const NON_FLAT_ROOF_SURFACE_STYLE_KINDS = new Set<EntityStyleKind>([
  "automatic-pitched-roof",
  "single-gable-roof",
  "curved-roof",
  "single-pitched-roof",
  "multi-shed-roof",
  "multi-gable-roof",
]);
const ROOF_SURFACE_STYLE_KINDS = new Set<EntityStyleKind>([
  "custom-flat-roof",
  "rectangle",
  ...NON_FLAT_ROOF_SURFACE_STYLE_KINDS,
]);
const TEXT_BOX_PADDING = 6;
const DEFAULT_TEXT_BOX_WIDTH = 160;
const DEFAULT_TEXT_BOX_HEIGHT = 60;
const MIN_TEXT_BOX_WIDTH = 40;
const MIN_TEXT_BOX_HEIGHT = 24;
const ENTITY_STYLE_OPTIONS: Array<{ kind: EntityStyleKind; label: string }> = [
  { kind: "custom-flat-roof", label: "Custom Flat Roof" },
  { kind: "rectangle", label: "Flat Roof" },
  { kind: "automatic-pitched-roof", label: "Automatic Pitched Roof" },
  { kind: "single-gable-roof", label: "Single Gable Roof" },
  { kind: "curved-roof", label: "Curved Roof" },
  { kind: "single-pitched-roof", label: "Single Pitched Roof" },
  { kind: "multi-shed-roof", label: "Multi Shed Roof" },
  { kind: "multi-gable-roof", label: "Multi Gable Roof" },
  { kind: "roof-eave", label: "Roof Eave / Gronda" },
  { kind: "roof-ridge", label: "Roof Ridge / Colmo" },
  { kind: "roof-hip", label: "Roof Hip" },
  { kind: "roof-valley", label: "Roof Valley" },
  { kind: "roof-rake", label: "Roof Rake / Side" },
  { kind: "rectangular-hole", label: "Rectangular Hole" },
  { kind: "circular-hole", label: "Circular Hole" },
  { kind: "polygonal-obstacle", label: "Polygonal Obstacle" },
  { kind: "circular-obstacle", label: "Circular Obstacle" },
  { kind: "single-lifeline", label: "Single Life Line" },
  { kind: "single-anchor-point", label: "Anchor Point" },
  { kind: "point-series", label: "Anchor Point Series" },
  { kind: "polyline", label: "Build Polyline" },
  { kind: "hatch", label: "Hatch" },
  { kind: "construction-line", label: "Construction Line" },
  { kind: "ruler", label: "Ruler" },
  { kind: "osnap-tracking", label: "OSNAP Tracking Line" },
  { kind: "circle", label: "Circle" },
  { kind: "arc", label: "Arc" },
  { kind: "dimension", label: "Dimension" },
  { kind: "dimension-object", label: "Dimension Object" },
  { kind: "text", label: "Text" },
  { kind: "icon", label: "Icon" },
];
const SHORTCUT_COMMAND_OPTIONS: Array<{ command: ShortcutCommand; label: string }> = [
  { command: "select", label: "Selezione" },
  { command: "rectangle", label: "Flat Roof" },
  { command: "polyline", label: "Custom Flat Room" },
  { command: "hatch", label: "Hatch" },
  { command: "singlePointCircle", label: "Anchor Point" },
  { command: "dimension", label: "Dimension" },
  { command: "ruler", label: "Ruler" },
  { command: "circle", label: "Circle" },
  { command: "arc", label: "Arc" },
  { command: "move", label: "Muovi" },
  { command: "copy", label: "Copy" },
  { command: "scale", label: "Scala" },
  { command: "rotate", label: "Ruota" },
  { command: "fit", label: "Adatta allo schermo" },
];
const DEFAULT_SHORTCUTS: Record<ShortcutCommand, string> = {
  select: "Space",
  rectangle: "R",
  polyline: "P",
  hatch: "Shift+H",
  singlePointCircle: "H",
  dimension: "D",
  ruler: "Shift+R",
  circle: "C",
  arc: "",
  move: "M",
  copy: "Y",
  scale: "S",
  rotate: "O",
  fit: "F",
};
const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);
const isLineType = (value: unknown): value is LineType =>
  typeof value === "string" && LINE_TYPE_VALUES.includes(value as LineType);
const isVertexShape = (value: unknown): value is VertexShape =>
  typeof value === "string" && VERTEX_SHAPE_VALUES.includes(value as VertexShape);
const isRoofComponentLabelPosition = (value: unknown): value is RoofComponentLabelPosition =>
  typeof value === "string" && ROOF_COMPONENT_LABEL_POSITION_VALUES.includes(value as RoofComponentLabelPosition);
const getStoredFiniteNumber = (value: unknown, fallback: number, min = 0) =>
  typeof value === "number" && Number.isFinite(value) ? Math.max(min, value) : fallback;
const POINT_SERIES_DEFAULT_MAX_LENGTH = 2000;
const ENTITY_LAYER_OPTIONS: EntityLayerName[] = [ROOF_LAYER, SAFETY_LAYER, DIMENSIONS_LAYER, LABELS_LAYER, TEXT_LAYER];
const ENTITY_ASSIGNABLE_LAYER_OPTIONS: EntityLayerName[] = [
  ROOF_LAYER,
  SAFETY_LAYER,
  DIMENSIONS_LAYER,
  LABELS_LAYER,
  TEXT_LAYER,
];
const DEFAULT_LAYER_ORDER: EntityLayerName[] = [ROOF_LAYER, SAFETY_LAYER, DIMENSIONS_LAYER, LABELS_LAYER, TEXT_LAYER];
const getLayerDisplayName = (layer: EntityLayerName) => (layer === SAFETY_LAYER ? "Fall Protection Systems" : layer);
const DEFAULT_VISIBLE_LAYERS: Record<EntityLayerName, boolean> = {
  [ROOF_LAYER]: true,
  [SAFETY_LAYER]: true,
  [DIMENSIONS_LAYER]: true,
  [LABELS_LAYER]: true,
  [TEXT_LAYER]: true,
};
const DEFAULT_LOCKED_LAYERS: Record<EntityLayerName, boolean> = {
  [ROOF_LAYER]: false,
  [SAFETY_LAYER]: false,
  [DIMENSIONS_LAYER]: false,
  [LABELS_LAYER]: false,
  [TEXT_LAYER]: false,
};
const DEFAULT_LAYER_COLORS: Record<EntityLayerName, string> = {
  [ROOF_LAYER]: DEFAULT_SHAPE_COLOR,
  [SAFETY_LAYER]: "black",
  [DIMENSIONS_LAYER]: DEFAULT_DIMENSION_COLOR,
  [LABELS_LAYER]: "#16a34a",
  [TEXT_LAYER]: "#111827",
};
const EDITABLE_SELECT_BACKGROUND_COLOR = "#e7f0ff";
const EDITABLE_SELECT_TEXT_COLOR = "#0b4f9c";
const EDITABLE_SELECT_BORDER_COLOR = "#1d5cab";
const SELECTED_SYSTEM_SECTION_BACKGROUND_COLOR = "rgba(156, 163, 175, 0.2)";
const PATROL_MAX_SPAN_TO_CANVAS_FACTOR = 1000;
const PATROL_DEFAULT_SAFETY_SYSTEM_SETTINGS: SafetySystemSettings = {
  vertexShape: "circle",
  strokeWidth: 3,
  lineType: "solid",
  maxSpacing: 7500,
  maxSingleLineLength: 180 * PATROL_MAX_SPAN_TO_CANVAS_FACTOR,
  maxCornersPerLifeline: 0,
  chamferAcuteAngles: true,
  chamferSegmentLength: 2000,
  intermediateAngleMax: 106,
  rectCurveMinAngle: 90,
  rectCurveMaxAngle: 105,
  bendCurveMinAngle: 105,
  bendCurveMaxAngle: 160,
};
const DEFAULT_SAFETY_SYSTEM_SETTINGS: Record<SafetySystemOption, SafetySystemSettings> = {
  PATROL: PATROL_DEFAULT_SAFETY_SYSTEM_SETTINGS,
};
const getSafetySystemPresetKey = (system?: Pick<PatrolSystemForSettings, "name"> | null) => {
  const name = system?.name.toUpperCase() ?? "";
  if (name.includes("BORDER")) return "BORDER";
  if (name.includes("H-RAIL") || name.includes("H RAIL")) return "H-RAIL";
  return "PATROL";
};
const getDefaultSafetySystemSettings = (
  system?: Pick<PatrolSystemForSettings, "name" | "maxCornersPerLifeline"> | null,
): SafetySystemSettings => ({
  ...(DEFAULT_SAFETY_SYSTEM_SETTINGS[getSafetySystemPresetKey(system)] ?? PATROL_DEFAULT_SAFETY_SYSTEM_SETTINGS),
  maxCornersPerLifeline: Math.max(0, Math.floor(Number(system?.maxCornersPerLifeline) || 0)),
  chamferAcuteAngles: true,
});
const normalizeSafetySystemSettings = (settings: SafetySystemSettings): SafetySystemSettings => ({
  ...settings,
  chamferAcuteAngles: true,
});
const normalizeStoredSafetySystemSettings = (
  storedSettings: unknown,
  fallbackSettings: SafetySystemSettings,
): SafetySystemSettings => {
  const stored = isRecord(storedSettings) ? storedSettings : {};

  return normalizeSafetySystemSettings({
    vertexShape: isVertexShape(stored["vertexShape"]) ? stored["vertexShape"] : fallbackSettings.vertexShape,
    strokeWidth: getStoredFiniteNumber(stored["strokeWidth"], fallbackSettings.strokeWidth, 0.5),
    lineType: isLineType(stored["lineType"]) ? stored["lineType"] : fallbackSettings.lineType,
    maxSpacing: getStoredFiniteNumber(stored["maxSpacing"], fallbackSettings.maxSpacing),
    maxSingleLineLength: getStoredFiniteNumber(stored["maxSingleLineLength"], fallbackSettings.maxSingleLineLength),
    maxCornersPerLifeline: Math.max(
      0,
      Math.floor(getStoredFiniteNumber(stored["maxCornersPerLifeline"], fallbackSettings.maxCornersPerLifeline)),
    ),
    chamferAcuteAngles:
      typeof stored["chamferAcuteAngles"] === "boolean"
        ? stored["chamferAcuteAngles"]
        : fallbackSettings.chamferAcuteAngles,
    chamferSegmentLength: getStoredFiniteNumber(stored["chamferSegmentLength"], fallbackSettings.chamferSegmentLength),
    intermediateAngleMax: getStoredFiniteNumber(stored["intermediateAngleMax"], fallbackSettings.intermediateAngleMax),
    rectCurveMinAngle: getStoredFiniteNumber(stored["rectCurveMinAngle"], fallbackSettings.rectCurveMinAngle),
    rectCurveMaxAngle: getStoredFiniteNumber(stored["rectCurveMaxAngle"], fallbackSettings.rectCurveMaxAngle),
    bendCurveMinAngle: getStoredFiniteNumber(stored["bendCurveMinAngle"], fallbackSettings.bendCurveMinAngle),
    bendCurveMaxAngle: getStoredFiniteNumber(stored["bendCurveMaxAngle"], fallbackSettings.bendCurveMaxAngle),
  });
};
const getSafetySystemIcon = (system: Pick<PatrolSystemForSettings, "name">) =>
  system.name.trim().charAt(0).toUpperCase() || "S";
const getPatrolSystemMaxSpanBetweenIntermediates = (
  settings: Pick<PatrolSettings, "materials"> | undefined,
  systemId: string,
  preferredAbsorberId?: string | null,
) => {
  const absorberMatches =
    settings?.materials
      .flatMap((material) => material.absorbers ?? [])
      .flatMap((absorber) => {
        const systemMapping = absorber.systems.find((system) => system.systemId === systemId);
        return systemMapping ? [{ absorber, systemMapping }] : [];
      }) ?? [];

  const preferredMatch = preferredAbsorberId
    ? absorberMatches.find((match) => match.absorber.item.id === preferredAbsorberId)
    : null;
  const spearEvoMatch = absorberMatches.find((match) => {
    const itemText = `${match.absorber.item.code} ${match.absorber.item.name ?? ""} ${
      match.absorber.item.description
    }`.toLowerCase();
    return itemText.includes("spearevo") || itemText.includes("spear evo");
  });
  const fallbackMatch = [...absorberMatches].sort((left, right) => left.absorber.order - right.absorber.order)[0];
  const maxSpan = (preferredMatch ?? spearEvoMatch ?? fallbackMatch)?.systemMapping.maxSpanIntermediate;

  return typeof maxSpan === "number" && Number.isFinite(maxSpan) && maxSpan > 0
    ? maxSpan * PATROL_MAX_SPAN_TO_CANVAS_FACTOR
    : null;
};
const getPreferredPatrolSystemId = (
  systems: PatrolSystemForSettings[],
  configuredSystemId?: string,
  currentSystemId?: string,
) => {
  if (currentSystemId && systems.some((system) => system.id === currentSystemId)) return currentSystemId;

  const towerSystem = systems.find((system) => normalizeRoofCompatibilityText(system.name).includes("tower"));
  if (towerSystem) return towerSystem.id;

  const configuredSystem = systems.find((system) => system.id === configuredSystemId);
  if (configuredSystem) return configuredSystem.id;

  return systems[0]?.id ?? "";
};
const PREVIEW_STROKE_COLOR = "#393939";
const PREVIEW_FILL_COLOR = "#f5d29e";
const PREVIEW_TEXT_COLOR = "#393939";
const createDefaultEntityStyle = (
  strokeColor: CadColorName,
  vertexColor: CadColorName,
  gripColor: CadColorName,
  options: Partial<
    Pick<
      EntityTypeStyle,
      | "strokeWidth"
      | "lineType"
      | "vertexShape"
      | "vertexSize"
      | "gripSize"
      | "labelText"
      | "labelHeight"
      | "labelPosition"
    >
  > = {},
): EntityTypeStyle => ({
  strokeColor: getCadColorValue(strokeColor),
  strokeWidth: options.strokeWidth ?? 1,
  lineType: options.lineType ?? "solid",
  vertexColor: getCadColorValue(vertexColor),
  vertexSize: options.vertexSize ?? 5,
  vertexShape: options.vertexShape ?? "circle",
  gripColor: getCadColorValue(gripColor),
  gripSize: options.gripSize ?? 5,
  labelText: options.labelText ?? "",
  labelHeight: options.labelHeight ?? 24,
  labelPosition: options.labelPosition ?? "middle",
});
const DEFAULT_ENTITY_TYPE_STYLES: Record<EntityStyleKind, EntityTypeStyle> = {
  polyline: createDefaultEntityStyle("12", "3", "17"),
  hatch: createDefaultEntityStyle("8", "8", "17"),
  "dashed-polyline": createDefaultEntityStyle("4", "11", "18", { lineType: "dashed" }),
  "construction-line": createDefaultEntityStyle("17", "4", "18", { lineType: "dashed" }),
  ruler: createDefaultEntityStyle("12", "4", "18", { lineType: "dashed" }),
  "point-series": createDefaultEntityStyle("11", "3", "16", { lineType: "dashed" }),
  "osnap-tracking": createDefaultEntityStyle("12", "4", "18", { lineType: "dash-dot" }),
  "custom-flat-roof": createDefaultEntityStyle("15", "10", "16"),
  rectangle: createDefaultEntityStyle("15", "13", "17"),
  "automatic-pitched-roof": createDefaultEntityStyle("15", "12", "18"),
  "single-gable-roof": createDefaultEntityStyle("15", "14", "16"),
  "curved-roof": createDefaultEntityStyle("15", "11", "17", { lineType: "dash-dot" }),
  "single-pitched-roof": createDefaultEntityStyle("15", "3", "18"),
  "multi-shed-roof": createDefaultEntityStyle("15", "4", "16"),
  "multi-gable-roof": createDefaultEntityStyle("15", "2", "17"),
  "roof-eave": createDefaultEntityStyle("12", "3", "18", { labelText: "G", labelPosition: "bottom" }),
  "roof-ridge": createDefaultEntityStyle("3", "12", "17", { labelText: "C", labelPosition: "middle" }),
  "roof-hip": createDefaultEntityStyle("8", "5", "16", { labelText: "H", labelPosition: "top" }),
  "roof-valley": createDefaultEntityStyle("8", "5", "16", { labelText: "V", labelPosition: "bottom" }),
  "roof-hip-valley": createDefaultEntityStyle("8", "5", "16"),
  "roof-rake": createDefaultEntityStyle("10", "6", "18", { labelText: "R", labelPosition: "top" }),
  "polygonal-obstacle": createDefaultEntityStyle("15", "8", "16"),
  "circular-obstacle": createDefaultEntityStyle("16", "1", "17"),
  "rectangular-hole": createDefaultEntityStyle("18", "5", "19", { lineType: "dashed" }),
  "circular-hole": createDefaultEntityStyle("19", "4", "20", { lineType: "dashed" }),
  circle: createDefaultEntityStyle("14", "2", "16"),
  arc: createDefaultEntityStyle("6", "9", "17"),
  dimension: createDefaultEntityStyle("17", "4", "18"),
  "dimension-object": createDefaultEntityStyle("18", "5", "19"),
  text: createDefaultEntityStyle("15", "4", "18"),
  icon: createDefaultEntityStyle("6", "4", "18"),
  "single-lifeline": createDefaultEntityStyle("15", "3", "16", { strokeWidth: 3 }),
  "single-anchor-point": createDefaultEntityStyle("1", "4", "16", {
    strokeWidth: 3,
    lineType: "dashed",
    vertexSize: 3.5,
  }),
};

const DEFAULT_OSNAPS: OsnapSettings = {
  END: true,
  MID: true,
  CENTRE: true,
  PERP: true,
  QUADRANT: true,
  TANGENT: true,
  INT: true,
};
const UNIT_FACTORS: Record<UnitOption, number> = {
  px: 1,
  mm: 1,
  cm: 0.1,
  m: 0.001,
  in: 1 / 25.4,
  ft: 1 / 304.8,
};

type KonvaCanvasProps = Pick<PatrolProps, "configuration" | "isAdmin">;

export default function KonvaCanvas({ configuration, isAdmin }: KonvaCanvasProps) {
  const { configurationId } = useParams<{ configurationId: string }>();
  const aborter = useRef(new AbortController());
  const { isLoading, data: settings } = usePatrolSettings(aborter.current.signal, configurationId);
  const projectsApi = useProjectsApi();
  const userProjectRequest = projectsApi.getProject(aborter.current.signal);
  const adminProjectRequest = projectsApi.getAdminProject(aborter.current.signal);

  const patrolLifeLineSystems = useMemo(
    () => settings?.systems.filter((system) => system.isLifeLine) ?? [],
    [settings?.systems],
  );
  const patrolAnchorPointSystems = useMemo(
    () => settings?.systems.filter((system) => system.isAnchorPoint) ?? [],
    [settings?.systems],
  );

  const stageRef = useRef<Konva.Stage>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const backgroundInputRef = useRef<HTMLInputElement>(null);
  const canvasObjectsInputRef = useRef<HTMLInputElement>(null);
  const backgroundToolbarRef = useRef<HTMLDivElement>(null);
  const toolGroupCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const osnapTrackingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const gripContextMenuRef = useRef<HTMLDivElement>(null);
  const systemSettingsPanelRef = useRef<HTMLElement>(null);
  const storedSelectedSafetySystemRef = useRef<string | null>(null);
  const segmentStretchRef = useRef<SegmentStretchState | null>(null);
  const lastCommandRef = useRef<ToolMode | null>(null);
  const activePolylineRef = useRef<number[] | null>(null);
  const numericInputRef = useRef<HTMLInputElement>(null);
  const directionInputRef = useRef<HTMLInputElement>(null);
  const lineDistanceInputRef = useRef<HTMLInputElement>(null);
  const referenceLengthInputRef = useRef<HTMLInputElement>(null);
  const textEditInputRef = useRef<HTMLTextAreaElement>(null);
  const directionLockAxisRef = useRef<DirectionLockAxis | null>(null);
  const hasInitializedViewportRef = useRef(false);
  const shiftLockTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const historyActionRef = useRef<PendingHistoryAction | null>(null);
  const isRestoringHistoryRef = useRef(false);
  const latestHistorySnapshotRef = useRef<HistorySnapshot | null>(null);
  const roofSkeletonBuilderRef = useRef<{
    init: () => Promise<void>;
    buildFromPolygon: (coordinates: number[][][]) => {
      vertices: [number, number, number][];
      polygons: number[][];
    } | null;
  } | null>(null);
  const roofSkeletonInitPromiseRef = useRef<Promise<void> | null>(null);
  const pendingCreatedRoofSelectionIdsRef = useRef<string[] | null>(null);
  const lastAutoOpenedRoofSelectionKeyRef = useRef<string | null>(null);
  const [viewportSize, setViewportSize] = useState({ width: 960, height: 640 });
  const [viewport, setViewport] = useState<ViewportState>({ x: 0, y: 0, scale: 1 });
  const [toolMode, setToolMode] = useState<ToolMode>("select");
  const [polylines, setPolylines] = useState<Shape[]>([]);
  const [dimensions, setDimensions] = useState<Dimension[]>([]);
  const [textEntities, setTextEntities] = useState<TextEntity[]>([]);
  const [iconEntities, setIconEntities] = useState<CanvasIconEntity[]>([]);
  const [backgroundAsset, setBackgroundAsset] = useState<BackgroundAsset | null>(null);
  const [backgroundTransparency, setBackgroundTransparency] = useState(50);
  const [backgroundStatus, setBackgroundStatus] = useState<string | null>(null);
  const [isBackgroundLoading, setIsBackgroundLoading] = useState(false);
  const [isBackgroundToolbarOpen, setIsBackgroundToolbarOpen] = useState(true);
  const [backgroundToolbarWidth, setBackgroundToolbarWidth] = useState<number | null>(null);
  const [isBackgroundReferenceMode, setIsBackgroundReferenceMode] = useState(false);
  const [isBackgroundCropMode, setIsBackgroundCropMode] = useState(false);
  const [isGoogleMapDialogOpen, setIsGoogleMapDialogOpen] = useState(false);
  const [backgroundCropRect, setBackgroundCropRect] = useState<BackgroundCropRect | null>(null);
  const [backgroundReferenceLine, setBackgroundReferenceLine] = useState<BackgroundReferenceLine | null>(null);
  const [backgroundReferenceInput, setBackgroundReferenceInput] = useState<BackgroundReferenceInput | null>(null);
  const [backgroundAlignmentAngleDraft, setBackgroundAlignmentAngleDraft] = useState("0");
  const [activePolyline, setActivePolyline] = useState<number[] | null>(null);
  const [activeRectangle, setActiveRectangle] = useState<ActiveRectangle | null>(null);
  const [multiGableRoofDraft, setMultiGableRoofDraft] = useState<MultiGableRoofDraft | null>(null);
  const [roofHipValleyDraft, setRoofHipValleyDraft] = useState<RoofHipValleyDraft | null>(null);
  const [activeCircle, setActiveCircle] = useState<ActiveCircle | null>(null);
  const [activeArc, setActiveArc] = useState<ActiveArc | null>(null);
  const [activePointSeries, setActivePointSeries] = useState<number[] | null>(null);
  const [activeRuler, setActiveRuler] = useState<ActiveRuler | null>(null);
  const [constructionLines, setConstructionLines] = useState<ConstructionLine[]>([]);
  const [textToolValue, setTextToolValue] = useState(DEFAULT_TEXT_TOOL_VALUE);
  const [textToolHeight, setTextToolHeight] = useState(DEFAULT_TEXT_TOOL_HEIGHT);
  const [textToolJustification, setTextToolJustification] = useState<TextJustificationPoint>(
    DEFAULT_TEXT_TOOL_JUSTIFICATION,
  );
  const [textToolColor, setTextToolColor] = useState(DEFAULT_TEXT_TOOL_COLOR);
  const [textToolFont, setTextToolFont] = useState(DEFAULT_TEXT_TOOL_FONT);
  const [hatchToolPattern, setHatchToolPattern] = useState<HatchPattern>(DEFAULT_HATCH_PATTERN);
  const [hatchToolScale, setHatchToolScale] = useState(DEFAULT_HATCH_SCALE);
  const [hatchToolColor, setHatchToolColor] = useState(DEFAULT_HATCH_COLOR);
  const [hatchToolMode, setHatchToolMode] = useState<"pick" | "build">(DEFAULT_HATCH_MODE);
  const [iconToolColor, setIconToolColor] = useState(DEFAULT_ICON_TOOL_COLOR);
  const [iconToolScale, setIconToolScale] = useState(DEFAULT_ICON_TOOL_SCALE);
  const [isIconPickerOpen, setIsIconPickerOpen] = useState(false);
  const [pendingIconId, setPendingIconId] = useState<string | null>(null);
  const [iconPreviewPoint, setIconPreviewPoint] = useState<CanvasPoint | null>(null);
  const [osnapTrackingLines, setOsnapTrackingLines] = useState<OsnapTrackingLine[]>([]);
  const [activeDimension, setActiveDimension] = useState<ActiveDimension | null>(null);
  const [activeObjectDimension, setActiveObjectDimension] = useState<ActiveObjectDimension | null>(null);
  const [hoverSnap, setHoverSnap] = useState<OsnapPoint | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [selectionCycle, setSelectionCycle] = useState<SelectionCycleState | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingTextId, setEditingTextId] = useState<string | null>(null);
  const [editingTextDraft, setEditingTextDraft] = useState("");
  const [draggedGrip, setDraggedGrip] = useState<DraggedGrip | null>(null);
  const [draggedSegmentStretch, setDraggedSegmentStretch] = useState<SegmentStretchState | null>(null);
  const [draggedDimensionId, setDraggedDimensionId] = useState<string | null>(null);
  const [panState, setPanState] = useState<PanState | null>(null);
  const [selectionBox, setSelectionBox] = useState<SelectionBox | null>(null);
  const [moveState, setMoveState] = useState<MoveState | null>(null);
  const [transformState, setTransformState] = useState<TransformState | null>(null);
  const [activeOffset, setActiveOffset] = useState<ActiveOffset | null>(null);
  const [activeHoleHost, setActiveHoleHost] = useState<ActiveHoleHost | null>(null);
  const [invalidGripFeedback, setInvalidGripFeedback] = useState<InvalidGripFeedback | null>(null);
  const [gripContextMenu, setGripContextMenu] = useState<GripContextMenuState | null>(null);
  const [numericEdit, setNumericEdit] = useState<NumericEditState | null>(null);
  const [activeNumericInput, setActiveNumericInput] = useState<"value" | "direction" | "lineDistance">("value");
  const [overwriteNumericInput, setOverwriteNumericInput] = useState<"value" | "direction" | "lineDistance" | null>(
    null,
  );
  const [directionLockAxis, setDirectionLockAxis] = useState<DirectionLockAxis | null>(null);
  const [isToolbarOpen, setIsToolbarOpen] = useState(true);
  const [openToolGroupMenu, setOpenToolGroupMenu] = useState<
    | "roofCreation"
    | "roofComponent"
    | "surfaceFeature"
    | "dimension"
    | "lifeline"
    | "anchor"
    | "curve"
    | "transform"
    | "cleanup"
    | null
  >(null);
  const [primaryRoofCreationTool, setPrimaryRoofCreationTool] = useState<RoofCreationToolMode>("roof");
  const [primaryRoofComponentTool, setPrimaryRoofComponentTool] = useState<RoofComponentToolMode>("roofGronda");
  const [primarySurfaceFeatureTool, setPrimarySurfaceFeatureTool] = useState<SurfaceFeatureToolMode>("holePolyline");
  const [primaryDimensionTool, setPrimaryDimensionTool] = useState<"dimension" | "dimensionObject">("dimension");
  const [primaryLifeLineTool, setPrimaryLifeLineTool] = useState<"singleLifeLine" | "automaticLifeLine">(
    "singleLifeLine",
  );
  const [automaticLifeLineOffsetDistance, setAutomaticLifeLineOffsetDistance] = useState(
    AUTOMATIC_LIFELINE_DEFAULT_OFFSET,
  );
  const [automaticLifeLineSourceIds, setAutomaticLifeLineSourceIds] = useState<string[] | null>(null);
  const [automaticLifeLinePreviewPoint, setAutomaticLifeLinePreviewPoint] = useState<CanvasPoint | null>(null);
  const [primaryAnchorTool, setPrimaryAnchorTool] = useState<"singlePointCircle" | "pointSeries">("singlePointCircle");
  const [failedRoofMaterialImageUrl, setFailedRoofMaterialImageUrl] = useState<string | null>(null);
  const [failedRoofSubstructureImageUrl, setFailedRoofSubstructureImageUrl] = useState<string | null>(null);
  const [failedLifeLineSystemImageUrl, setFailedLifeLineSystemImageUrl] = useState<string | null>(null);
  const [failedAnchorPointSystemImageUrl, setFailedAnchorPointSystemImageUrl] = useState<string | null>(null);
  const [failedRoofShuttleImageUrl, setFailedRoofShuttleImageUrl] = useState<string | null>(null);
  const [primaryTransformTool, setPrimaryTransformTool] = useState<
    "move" | "copy" | "explode" | "scale" | "rotate" | "offset"
  >("move");
  const [primaryCleanupTool, setPrimaryCleanupTool] = useState<"delete" | "clear">("delete");
  const [primaryCurveTool, setPrimaryCurveTool] = useState<CurveToolMode>("circle");
  const [floatingPanelZIndexes, setFloatingPanelZIndexes] = useState<Record<FloatingPanelId, number>>(
    DEFAULT_FLOATING_PANEL_Z_INDEXES,
  );
  const bringFloatingPanelToFront = (panel: FloatingPanelId) => {
    setFloatingPanelZIndexes((current) => {
      const maxZIndex = Math.max(...Object.values(current));
      if (current[panel] === maxZIndex) return current;
      return {
        ...current,
        [panel]: maxZIndex + 1,
      };
    });
  };
  const getFloatingPanelStyle = (panel: FloatingPanelId, extraStyle: CSSProperties = {}): CSSProperties => ({
    ...extraStyle,
    zIndex: floatingPanelZIndexes[panel],
  });
  const [lifeLineRoofSelectionWarning, setLifeLineRoofSelectionWarning] = useState<string | null>(null);
  const [selectedSafetySystem, setSelectedSafetySystem] = useState<SafetySystemOption>("");
  const [selectedAnchorPointSystem, setSelectedAnchorPointSystem] = useState<SafetySystemOption>("");
  const [fallbackSupportSeamsSpan, setFallbackSupportSeamsSpan] = useState("");
  const [fallbackSupportInsulationThickness, setFallbackSupportInsulationThickness] = useState("");
  const [fallbackSupportMaterialId, setFallbackSupportMaterialId] = useState("");
  const [fallbackRoofComponentMaterialId, setFallbackRoofComponentMaterialId] = useState("");
  const [fallbackRoofComponentAbsorberId, setFallbackRoofComponentAbsorberId] = useState("");
  const [fallbackRoofComponentSystemTypeId, setFallbackRoofComponentSystemTypeId] = useState("");
  const [fallbackRoofFixed, setFallbackRoofFixed] = useState(configuration.data?.fixed ?? false);
  const [fallbackRoofShuttleId, setFallbackRoofShuttleId] = useState(configuration.data?.shuttleId ?? "");
  const [fallbackRoofNrOfShuttles, setFallbackRoofNrOfShuttles] = useState(
    configuration.data?.nrOfShuttles != null ? String(configuration.data.nrOfShuttles) : "",
  );
  const [fallbackTerminalCurveSupportId, setFallbackTerminalCurveSupportId] = useState("");
  const [fallbackIntermediateSupportId, setFallbackIntermediateSupportId] = useState("");
  const [fallbackAnchorPointMaterialId, setFallbackAnchorPointMaterialId] = useState("");
  const [fallbackAnchorPointMappingId, setFallbackAnchorPointMappingId] = useState("");
  const [fallbackLifeLineNormativeId, setFallbackLifeLineNormativeId] = useState("");
  const [fallbackAnchorPointNormativeId, setFallbackAnchorPointNormativeId] = useState("");
  const [safetySystemSettings, setSafetySystemSettings] =
    useState<Record<SafetySystemOption, SafetySystemSettings>>(DEFAULT_SAFETY_SYSTEM_SETTINGS);
  const [keyboardShortcuts, setKeyboardShortcuts] = useState<Record<ShortcutCommand, string>>(DEFAULT_SHORTCUTS);
  const [isEntityPropertiesOpen, setIsEntityPropertiesOpen] = useState(true);
  const [activeConfigurationPage, setActiveConfigurationPage] = useState<
    "roofProperty" | "lifelineSystems" | "anchorPoints"
  >("roofProperty");
  const [activeRoofPropertyTab, setActiveRoofPropertyTab] = useState<"roofInfo" | "substructure">("roofInfo");
  const [activeLifeLineTab, setActiveLifeLineTab] = useState<"info" | "components" | "shuttles">("info");
  const [activeAnchorPointTab, setActiveAnchorPointTab] = useState<"anchorPoint">("anchorPoint");
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isLayerVisibilityOpen, setIsLayerVisibilityOpen] = useState(false);
  const [isPropertiesOpen, setIsPropertiesOpen] = useState(false);
  const [isBillOfMaterialsOpen, setIsBillOfMaterialsOpen] = useState(false);
  const [isPrintExportOpen, setIsPrintExportOpen] = useState(false);
  const [isExportToolbarOpen, setIsExportToolbarOpen] = useState(false);
  const [printPaperFormat, setPrintPaperFormat] = useState<PrintPaperFormat>("A3");
  const [printTitleBlockMode, setPrintTitleBlockMode] = useState<PrintTitleBlockMode>("standard");
  const [printColorMode, setPrintColorMode] = useState<PrintColorMode>("plot");
  const [savedTitleBlockEntityIds, setSavedTitleBlockEntityIds] = useState<string[]>([]);
  const [printExportStatus, setPrintExportStatus] = useState<string | null>(null);
  const [drawingUnit, setDrawingUnit] = useState<UnitOption>("m");
  const [gridSpacing, setGridSpacing] = useState(DEFAULT_GRID_SPACING);
  const [gridSpacingDraft, setGridSpacingDraft] = useState(String(DEFAULT_GRID_SPACING * UNIT_FACTORS.mm));
  const [isGridSnapEnabled, setIsGridSnapEnabled] = useState(true);
  const [gridSnapStep, setGridSnapStep] = useState(DEFAULT_GRID_SNAP_STEP);
  const [gridSnapStepDraft, setGridSnapStepDraft] = useState(String(DEFAULT_GRID_SNAP_STEP * UNIT_FACTORS.mm));
  const [enabledOsnaps, setEnabledOsnaps] = useState<OsnapSettings>(DEFAULT_OSNAPS);
  const [entityLineWidth, setEntityLineWidth] = useState(DEFAULT_ENTITY_LINE_WIDTH);
  const [previewLineWidth, setPreviewLineWidth] = useState(DEFAULT_PREVIEW_LINE_WIDTH);
  const [dimensionTextHeight, setDimensionTextHeight] = useState(12);
  const [dimensionTextHeightDraft, setDimensionTextHeightDraft] = useState("12");
  const [dimensionTextGap, setDimensionTextGap] = useState(DIMENSION_TEXT_GAP);
  const [dimensionTextGapDraft, setDimensionTextGapDraft] = useState(String(DIMENSION_TEXT_GAP));
  const [dimensionMarkerStyle, setDimensionMarkerStyle] = useState<DimensionMarkerStyle>("filled-arrow");
  const [decimalSeparator, setDecimalSeparator] = useState<DecimalSeparator>("comma");
  const [entityNameDraft, setEntityNameDraft] = useState("");
  const [entityColorDraft, setEntityColorDraft] = useState(DEFAULT_SHAPE_COLOR);
  const [entityLayerDraft, setEntityLayerDraft] = useState<EntityLayerName | "">(ROOF_LAYER);
  const [isEntityColorPaletteOpen, setIsEntityColorPaletteOpen] = useState(false);
  const [isEntityVertexColorPaletteOpen, setIsEntityVertexColorPaletteOpen] = useState(false);
  const [pointSeriesMaxLength, setPointSeriesMaxLength] = useState(POINT_SERIES_DEFAULT_MAX_LENGTH);
  const [undoStack, setUndoStack] = useState<HistorySnapshot[]>([]);
  const [redoStack, setRedoStack] = useState<HistorySnapshot[]>([]);

  useEffect(() => {
    if (!isEntityPropertiesOpen || typeof window === "undefined") return;

    const panel = systemSettingsPanelRef.current;
    if (!panel) return;

    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    if (!context) return;

    const fitSystemSettingsSelectText = () => {
      panel.querySelectorAll("select").forEach((select) => {
        const realOptionCount = Array.from(select.options).filter((option) => option.value.trim() !== "").length;
        const isEditableSelection = !select.disabled && realOptionCount > 1;
        const selectedText = select.options[select.selectedIndex]?.textContent?.trim() ?? "";
        select.style.setProperty("font-size", "14px", "important");
        select.style.setProperty("color", isEditableSelection ? EDITABLE_SELECT_TEXT_COLOR : "#000000", "important");

        if (isEditableSelection) {
          select.style.setProperty("background-color", EDITABLE_SELECT_BACKGROUND_COLOR, "important");
          select.style.setProperty("border-color", EDITABLE_SELECT_BORDER_COLOR, "important");
        } else {
          select.style.removeProperty("background-color");
          select.style.removeProperty("border-color");
        }

        if (!selectedText || select.clientWidth <= 0) return;

        const style = window.getComputedStyle(select);
        const horizontalPadding = Number.parseFloat(style.paddingLeft) + Number.parseFloat(style.paddingRight);
        const availableWidth = Math.max(select.clientWidth - horizontalPadding - 34, 32);
        context.font = `${style.fontStyle} ${style.fontVariant} ${style.fontWeight} 14px ${style.fontFamily}`;
        const selectedTextWidth = context.measureText(selectedText).width;

        if (selectedTextWidth <= availableWidth) return;

        const fittedFontSize = Math.max(10, Math.floor((availableWidth / selectedTextWidth) * 14));
        select.style.setProperty("font-size", `${fittedFontSize}px`, "important");
      });
    };

    fitSystemSettingsSelectText();

    const resizeObserver = new ResizeObserver(fitSystemSettingsSelectText);
    resizeObserver.observe(panel);
    window.addEventListener("resize", fitSystemSettingsSelectText);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", fitSystemSettingsSelectText);
    };
  });

  useEffect(() => {
    if (patrolLifeLineSystems.length === 0 && patrolAnchorPointSystems.length === 0) return;

    setSafetySystemSettings((current) => {
      const next = { ...current };
      [...patrolLifeLineSystems, ...patrolAnchorPointSystems].forEach((system) => {
        const maxSpanBetweenIntermediates = getPatrolSystemMaxSpanBetweenIntermediates(
          settings,
          system.id,
          configuration.data?.absorberId,
        );
        const defaultSettings = {
          ...getDefaultSafetySystemSettings(system),
          ...(maxSpanBetweenIntermediates ? { maxSpacing: maxSpanBetweenIntermediates } : {}),
        };
        const existingSettings = next[system.id];
        if (!existingSettings) {
          next[system.id] = defaultSettings;
        } else if (
          (maxSpanBetweenIntermediates && existingSettings.maxSpacing !== maxSpanBetweenIntermediates) ||
          existingSettings.maxCornersPerLifeline !== defaultSettings.maxCornersPerLifeline
        ) {
          next[system.id] = {
            ...existingSettings,
            ...(maxSpanBetweenIntermediates ? { maxSpacing: maxSpanBetweenIntermediates } : {}),
            maxCornersPerLifeline: defaultSettings.maxCornersPerLifeline,
          };
        }
      });
      return next;
    });

    setSelectedSafetySystem((current) => {
      const storedSystem = storedSelectedSafetySystemRef.current;
      if (storedSystem) {
        if (patrolLifeLineSystems.some((system) => system.id === storedSystem)) {
          storedSelectedSafetySystemRef.current = null;
          return storedSystem;
        }

        if (patrolLifeLineSystems.length === 0) return current;
        storedSelectedSafetySystemRef.current = null;
      }
      return getPreferredPatrolSystemId(patrolLifeLineSystems, configuration.data?.systemId, current);
    });

    setSelectedAnchorPointSystem((current) => {
      return getPreferredPatrolSystemId(patrolAnchorPointSystems, configuration.data?.systemId, current);
    });
  }, [
    configuration.data?.absorberId,
    configuration.data?.systemId,
    patrolAnchorPointSystems,
    patrolLifeLineSystems,
    settings,
  ]);
  const [historyRevision, setHistoryRevision] = useState(0);
  const [entityTypeStyles, setEntityTypeStyles] =
    useState<Record<EntityStyleKind, EntityTypeStyle>>(DEFAULT_ENTITY_TYPE_STYLES);
  const [visibleLayers, setVisibleLayers] = useState<Record<EntityLayerName, boolean>>(DEFAULT_VISIBLE_LAYERS);
  const [lockedLayers, setLockedLayers] = useState<Record<EntityLayerName, boolean>>(DEFAULT_LOCKED_LAYERS);
  const [layerColors, setLayerColors] = useState<Record<EntityLayerName, string>>(DEFAULT_LAYER_COLORS);
  const [layerOrder, setLayerOrder] = useState<EntityLayerName[]>(DEFAULT_LAYER_ORDER);

  const clonePoint = (point: CanvasPoint): CanvasPoint => clonePointRule(point);
  const cloneCircleHole = (hole: CircleHole): CircleHole => ({
    center: clonePoint(hole.center),
    radius: hole.radius,
  });

  const cloneBackgroundAsset = (asset: BackgroundAsset | null): BackgroundAsset | null =>
    cloneBackgroundAssetRule(asset);

  const cloneShape = (shape: Shape): Shape => {
    if (shape.kind === "circle" || shape.kind === "arc") {
      return {
        ...shape,
        center: clonePoint(shape.center),
      };
    }

    return {
      ...shape,
      points: [...shape.points],
      holes: shape.holes?.map((hole) => [...hole]),
      circleHoles: shape.circleHoles?.map(cloneCircleHole),
      lifeLineVertices: shape.lifeLineVertices?.map((vertex) => ({ ...vertex })),
      lifeLineDesignPoints: shape.lifeLineDesignPoints?.map(clonePoint),
    };
  };

  const cloneDimension = (dimension: Dimension): Dimension => ({
    ...dimension,
    start: clonePoint(dimension.start),
    end: clonePoint(dimension.end),
  });

  const cloneConstructionLine = (line: ConstructionLine): ConstructionLine => ({
    ...line,
    start: clonePoint(line.start),
    end: clonePoint(line.end),
  });

  const cloneTextEntity = (textEntity: TextEntity): TextEntity => ({
    ...textEntity,
    position: clonePoint(textEntity.position),
  });

  const cloneIconEntity = (iconEntity: CanvasIconEntity): CanvasIconEntity => ({
    ...iconEntity,
    position: clonePoint(iconEntity.position),
    rotation: iconEntity.rotation ?? 0,
  });

  const cloneHistorySnapshot = (snapshot: HistorySnapshot): HistorySnapshot =>
    cloneHistorySnapshotRule(
      snapshot,
      cloneShape,
      cloneDimension,
      cloneConstructionLine,
      cloneTextEntity,
      cloneIconEntity,
    );

  const arePointsEqual = (pointA: CanvasPoint, pointB: CanvasPoint) => pointA.x === pointB.x && pointA.y === pointB.y;

  const areBackgroundReferencesEqual = (
    referenceA: BackgroundReference | null,
    referenceB: BackgroundReference | null,
  ) => {
    if (referenceA === referenceB) return true;
    if (!referenceA || !referenceB) return false;

    return (
      arePointsEqual(referenceA.startLocal, referenceB.startLocal) &&
      arePointsEqual(referenceA.endLocal, referenceB.endLocal) &&
      referenceA.realLength === referenceB.realLength
    );
  };

  const getDefaultShapeLayer = (kind: Shape["kind"]): EntityLayerName =>
    kind === "polyline" || kind === "rectangle" ? ROOF_LAYER : SAFETY_LAYER;

  const getArcStartPoint = (arc: Pick<ArcShape, "center" | "radius" | "startAngle">) => ({
    x: arc.center.x + Math.cos(arc.startAngle) * arc.radius,
    y: arc.center.y + Math.sin(arc.startAngle) * arc.radius,
  });

  const getArcEndPoint = (arc: Pick<ArcShape, "center" | "radius" | "endAngle">) => ({
    x: arc.center.x + Math.cos(arc.endAngle) * arc.radius,
    y: arc.center.y + Math.sin(arc.endAngle) * arc.radius,
  });

  const getArcSweep = (arc: Pick<ArcShape, "startAngle" | "endAngle">) =>
    (((arc.endAngle - arc.startAngle) % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);

  const getDistanceToArc = (
    point: CanvasPoint,
    arc: Pick<ArcShape, "center" | "radius" | "startAngle" | "endAngle">,
  ) => {
    const angle = Math.atan2(point.y - arc.center.y, point.x - arc.center.x);
    const sweep = getArcSweep(arc);
    const effectiveSweep = sweep === 0 ? Math.PI * 2 : sweep;
    const relativeAngle = (((angle - arc.startAngle) % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);

    if (relativeAngle <= effectiveSweep) {
      return Math.abs(getDistance(point, arc.center) - arc.radius);
    }

    return Math.min(getDistance(point, getArcStartPoint(arc)), getDistance(point, getArcEndPoint(arc)));
  };

  const getArcRenderPoints = (arc: Pick<ArcShape, "center" | "radius" | "startAngle" | "endAngle">) => {
    const sweep = getArcSweep(arc);
    const effectiveSweep = sweep === 0 ? Math.PI * 2 : sweep;
    const segmentCount = Math.max(12, Math.ceil((effectiveSweep * arc.radius) / 500));
    const points: number[] = [];

    for (let index = 0; index <= segmentCount; index += 1) {
      const angle = arc.startAngle + (effectiveSweep * index) / segmentCount;
      points.push(arc.center.x + Math.cos(angle) * arc.radius, arc.center.y + Math.sin(angle) * arc.radius);
    }

    return points;
  };

  const normalizeLayerOrder = (order: unknown): EntityLayerName[] => {
    const validLayers = new Set<EntityLayerName>(ENTITY_LAYER_OPTIONS);
    const normalized = Array.isArray(order)
      ? order.filter((layer): layer is EntityLayerName => validLayers.has(layer))
      : [];
    return [...normalized, ...ENTITY_LAYER_OPTIONS.filter((layer) => !normalized.includes(layer))];
  };

  const moveLayerZ = (layer: EntityLayerName, direction: "up" | "down") => {
    setLayerOrder((current) => {
      const normalized = normalizeLayerOrder(current);
      const index = normalized.indexOf(layer);
      const nextIndex = direction === "up" ? index + 1 : index - 1;
      if (index < 0 || nextIndex < 0 || nextIndex >= normalized.length) {
        return normalized;
      }

      const nextOrder = [...normalized];
      const currentLayer = nextOrder[index];
      const targetLayer = nextOrder[nextIndex];
      if (!currentLayer || !targetLayer) return normalized;
      nextOrder[index] = targetLayer;
      nextOrder[nextIndex] = currentLayer;
      return nextOrder;
    });
  };

  const getShapeLayer = (shape: Shape) => shape.layer ?? getDefaultShapeLayer(shape.kind);

  const getDimensionLayer = (dimension: Dimension) => dimension.layer ?? DIMENSIONS_LAYER;

  const isGridLikeEntity = (entity: { id?: string; name?: string }) => {
    const id = entity.id?.toLowerCase() ?? "";
    const name = entity.name?.toLowerCase() ?? "";
    return (
      id.startsWith("grid-") ||
      id.includes("-grid-") ||
      id.includes("grid") ||
      id.includes("griglia") ||
      name === "grid" ||
      name.startsWith("grid ") ||
      name.includes(" grid") ||
      name.includes("grid") ||
      name === "griglia" ||
      name.startsWith("griglia ") ||
      name.includes(" griglia") ||
      name.includes("griglia")
    );
  };

  const getLegacyRoofGroupCategory = (shape: Shape): RoofGroupCategory | null => {
    if (shape.isObstacle) return null;

    const name = shape.name.toLowerCase();
    if (
      name.startsWith("flat roof") ||
      name.startsWith("custom flat roof") ||
      name.startsWith("custom flat room") ||
      shape.kind === "rectangle"
    ) {
      return "Flat";
    }

    if (isCurvedRoofCompatibilityName(shape.name)) {
      return "Curved";
    }

    if (getShapeLayer(shape) === ROOF_LAYER) {
      return "Pitched";
    }

    return null;
  };

  const getRoofContextId = (shape: Shape) => {
    if (getShapeLayer(shape) !== ROOF_LAYER || shape.isObstacle) return null;
    return shape.roofGroupId ?? shape.id;
  };
  const roofContextIds = useMemo(() => {
    const ids = new Set<string>();
    polylines.forEach((shape) => {
      const contextId = getRoofContextId(shape);
      if (contextId) ids.add(contextId);
    });
    return Array.from(ids);
  }, [polylines]);
  const selectedRoofContextId = useMemo(() => {
    const selectedRoofShapes = polylines.filter((shape) => selectedIds.includes(shape.id) && getRoofContextId(shape));
    if (selectedRoofShapes.length === 0) return null;

    const firstSelectedRoofShape = selectedRoofShapes[0];
    if (!firstSelectedRoofShape) return null;

    const firstContextId = getRoofContextId(firstSelectedRoofShape);
    if (!firstContextId) return null;

    return selectedRoofShapes.every((shape) => getRoofContextId(shape) === firstContextId) ? firstContextId : null;
  }, [polylines, selectedIds]);
  const selectedRoofCompatibilityShapes = useMemo(
    () =>
      selectedRoofContextId ? polylines.filter((shape) => getRoofContextId(shape) === selectedRoofContextId) : null,
    [polylines, selectedRoofContextId],
  );
  const roofCompatibilitySourceShapes = selectedRoofCompatibilityShapes ?? polylines;
  const lifeLineRoofSelectionRequired = roofContextIds.length > 1 && !selectedRoofContextId;

  const activeRoofGroupCategories = useMemo(() => {
    const categories = new Set<RoofGroupCategory>();

    roofCompatibilitySourceShapes.forEach((shape) => {
      if (getShapeLayer(shape) !== ROOF_LAYER) return;
      const category = shape.roofGroupCategory ?? getLegacyRoofGroupCategory(shape);
      if (category) {
        categories.add(category);
      }
    });

    return Array.from(categories).sort();
  }, [roofCompatibilitySourceShapes]);
  const hasRoofCategoryFilter = activeRoofGroupCategories.length > 0;

  const compatibleRoofCategoryIds = useMemo(() => {
    if (activeRoofGroupCategories.length === 0 || !settings?.roofTypes) {
      return null;
    }

    const typedCategoryIds = new Set<string>();
    const untypedCategories = new Set<RoofGroupCategory>();

    roofCompatibilitySourceShapes.forEach((shape) => {
      if (getShapeLayer(shape) !== ROOF_LAYER || shape.isObstacle) return;
      const roofType = shape.roofTypeId
        ? settings.roofTypes.find((candidate) => candidate.id === shape.roofTypeId)
        : undefined;
      if (roofType) {
        typedCategoryIds.add(roofType.roofCategory.id);
        return;
      }

      const category = shape.roofGroupCategory ?? getLegacyRoofGroupCategory(shape);
      if (category) {
        untypedCategories.add(category);
      }
    });

    const ids = new Set<string>();
    let hasCurvedCategoryMatch = false;
    typedCategoryIds.forEach((id) => ids.add(id));

    settings.roofTypes.forEach((roofType) => {
      const category = roofType.roofCategory;
      const isCurvedCategory =
        isCurvedRoofCompatibilityName(category.name) || isCurvedRoofCompatibilityName(roofType.name);

      if (untypedCategories.has("Flat") && category.isFlat) {
        ids.add(category.id);
      }

      if (untypedCategories.has("Curved") && !category.isFlat && isCurvedCategory) {
        ids.add(category.id);
        hasCurvedCategoryMatch = true;
      }

      if (untypedCategories.has("Pitched") && !category.isFlat && !isCurvedCategory) {
        ids.add(category.id);
      }
    });

    if (untypedCategories.has("Curved") && !hasCurvedCategoryMatch) {
      settings.roofTypes.forEach((roofType) => {
        if (!roofType.roofCategory.isFlat) {
          ids.add(roofType.roofCategory.id);
        }
      });
    }

    return ids;
  }, [activeRoofGroupCategories.length, roofCompatibilitySourceShapes, settings?.roofTypes]);

  const activeRoofCompatibilityFilters = useMemo(() => {
    const materialIds = new Set<string>();
    const substructureIds = new Set<string>();

    roofCompatibilitySourceShapes.forEach((shape) => {
      if (getShapeLayer(shape) !== ROOF_LAYER || shape.isObstacle) return;
      if (shape.roofMaterialId) materialIds.add(shape.roofMaterialId);
      if (shape.roofSubstructureId) substructureIds.add(shape.roofSubstructureId);
    });

    return {
      materialIds,
      substructureIds,
    };
  }, [roofCompatibilitySourceShapes]);

  const compatibleSystemIdsFromMappings = useMemo(() => {
    if (!compatibleRoofCategoryIds || compatibleRoofCategoryIds.size === 0) {
      return null;
    }

    const ids = new Set<string>();
    settings?.systemMappings.forEach((mapping) => {
      if (!compatibleRoofCategoryIds.has(mapping.roofCategoryId)) return;
      if (
        activeRoofCompatibilityFilters.materialIds.size > 0 &&
        !activeRoofCompatibilityFilters.materialIds.has(mapping.roofMaterialId)
      ) {
        return;
      }
      if (
        activeRoofCompatibilityFilters.substructureIds.size > 0 &&
        (!mapping.roofSubstructureId || !activeRoofCompatibilityFilters.substructureIds.has(mapping.roofSubstructureId))
      ) {
        return;
      }
      ids.add(mapping.systemId);
    });

    return ids;
  }, [activeRoofCompatibilityFilters, compatibleRoofCategoryIds, settings?.systemMappings]);

  const filteredPatrolLifeLineSystems = useMemo(() => {
    if (!hasRoofCategoryFilter || !compatibleSystemIdsFromMappings) return patrolLifeLineSystems;
    return patrolLifeLineSystems.filter((system) => compatibleSystemIdsFromMappings.has(system.id));
  }, [compatibleSystemIdsFromMappings, hasRoofCategoryFilter, patrolLifeLineSystems]);

  const filteredPatrolAnchorPointSystems = useMemo(() => {
    if (!hasRoofCategoryFilter || !compatibleSystemIdsFromMappings) return patrolAnchorPointSystems;
    return patrolAnchorPointSystems.filter((system) => compatibleSystemIdsFromMappings.has(system.id));
  }, [compatibleSystemIdsFromMappings, hasRoofCategoryFilter, patrolAnchorPointSystems]);

  const safetySystemOptions = useMemo(
    () =>
      filteredPatrolLifeLineSystems.map((system) => ({
        value: system.id,
        label: system.name,
        icon: getSafetySystemIcon(system),
        description: system.name,
      })),
    [filteredPatrolLifeLineSystems],
  );
  const anchorPointSystemOptions = useMemo(
    () =>
      filteredPatrolAnchorPointSystems.map((system) => ({
        value: system.id,
        label: system.name,
        icon: getSafetySystemIcon(system),
        description: system.name,
      })),
    [filteredPatrolAnchorPointSystems],
  );

  useEffect(() => {
    setSelectedSafetySystem((current) => {
      const storedSystem = storedSelectedSafetySystemRef.current;
      if (storedSystem) {
        if (filteredPatrolLifeLineSystems.some((system) => system.id === storedSystem)) {
          storedSelectedSafetySystemRef.current = null;
          return storedSystem;
        }

        if (filteredPatrolLifeLineSystems.length === 0) return current;
        storedSelectedSafetySystemRef.current = null;
      }
      return getPreferredPatrolSystemId(filteredPatrolLifeLineSystems, configuration.data?.systemId, current);
    });
  }, [configuration.data?.systemId, filteredPatrolLifeLineSystems]);

  useEffect(() => {
    setSelectedAnchorPointSystem((current) => {
      return getPreferredPatrolSystemId(filteredPatrolAnchorPointSystems, configuration.data?.systemId, current);
    });
  }, [configuration.data?.systemId, filteredPatrolAnchorPointSystems]);

  useEffect(() => {
    if (!lifeLineRoofSelectionRequired) {
      setLifeLineRoofSelectionWarning(null);
    }
  }, [lifeLineRoofSelectionRequired]);

  const isLayerVisible = (layer: EntityLayerName) => visibleLayers[layer];
  const areLabelsVisible = () => isLayerVisible(LABELS_LAYER);

  const isLayerLocked = (layer: EntityLayerName) => lockedLayers[layer];

  const getLayerColor = (layer: EntityLayerName) => layerColors[layer];
  const getEntityTypeStyle = (kind: EntityStyleKind) => entityTypeStyles[kind] ?? DEFAULT_ENTITY_TYPE_STYLES[kind];
  const getEntityTypeLineDash = (kind: EntityStyleKind) => {
    const lineType = getEntityTypeStyle(kind).lineType;

    if (lineType === "dashed") {
      return [getScreenInvariantSize(10), getScreenInvariantSize(6)];
    }

    if (lineType === "dashed-x2") {
      return [getScreenInvariantSize(20), getScreenInvariantSize(12)];
    }

    if (lineType === "dash-dot") {
      return [
        getScreenInvariantSize(12),
        getScreenInvariantSize(5),
        getScreenInvariantSize(1.5),
        getScreenInvariantSize(5),
      ];
    }

    if (lineType === "dash-dot-x2") {
      return [
        getScreenInvariantSize(24),
        getScreenInvariantSize(10),
        getScreenInvariantSize(3),
        getScreenInvariantSize(10),
      ];
    }

    if (lineType === "dot-dot") {
      return [getScreenInvariantSize(1.5), getScreenInvariantSize(5)];
    }

    if (lineType === "dot-dot-x2") {
      return [getScreenInvariantSize(3), getScreenInvariantSize(10)];
    }

    return undefined;
  };
  const isNonFlatRoofSurfaceStyleKind = (kind?: EntityStyleKind | null) =>
    kind != null && NON_FLAT_ROOF_SURFACE_STYLE_KINDS.has(kind);
  const getShapeStyleKind = (shape: Shape): EntityStyleKind => {
    if (shape.entityStyleKind) return shape.entityStyleKind;
    if (shape.isObstacle) return shape.kind === "circle" ? "circular-obstacle" : "polygonal-obstacle";
    if (shape.kind !== "circle" && shape.kind !== "arc" && (shape.lifeLineVertices?.length ?? 0) > 0) {
      return "single-lifeline";
    }
    if (shape.kind === "rectangle") return "rectangle";
    if (shape.kind === "circle") return "circle";
    if (shape.kind === "arc") return "arc";
    if (shape.name.startsWith("Gronda")) return "roof-eave";
    if (shape.name.startsWith("Colmo")) return "roof-ridge";
    if (shape.name.startsWith("HIP") || shape.name.startsWith("Diagonale")) return "roof-hip";
    if (shape.name.startsWith("Valley")) return "roof-valley";
    if (shape.name.startsWith("RAKE") || shape.name.startsWith("BL")) return "roof-rake";
    if (shape.name.startsWith("Custom Flat Roof") || shape.name.startsWith("Custom Flat Room")) {
      return "custom-flat-roof";
    }
    return "polyline";
  };

  const isShapeVisible = (shape: Shape) => isLayerVisible(getShapeLayer(shape));

  const isDimensionVisible = (dimension: Dimension) => isLayerVisible(getDimensionLayer(dimension));

  const isTextEntityVisible = (textEntity: TextEntity) => isLayerVisible(getTextEntityLayer(textEntity));

  const isIconEntityVisible = (iconEntity: CanvasIconEntity) => isLayerVisible(getIconEntityLayer(iconEntity));

  const isShapeLocked = (shape: Shape) => isLayerLocked(getShapeLayer(shape));

  const isDimensionLocked = (dimension: Dimension) => isLayerLocked(getDimensionLayer(dimension));

  const isTextEntityLocked = (textEntity: TextEntity) => isLayerLocked(getTextEntityLayer(textEntity));

  const isIconEntityLocked = (iconEntity: CanvasIconEntity) => isLayerLocked(getIconEntityLayer(iconEntity));

  const isGripModeActive = Boolean(editingId && editingId !== BACKGROUND_ID);

  const isShapeInteractive = (shape: Shape) =>
    isShapeVisible(shape) && !isShapeLocked(shape) && (!isGripModeActive || shape.id === editingId);

  const isDimensionInteractive = (dimension: Dimension) =>
    isDimensionVisible(dimension) && !isDimensionLocked(dimension) && (!isGripModeActive || dimension.id === editingId);

  const isTextEntityInteractive = (textEntity: TextEntity) =>
    isTextEntityVisible(textEntity) &&
    !isTextEntityLocked(textEntity) &&
    (!isGripModeActive || textEntity.id === editingId);

  const isIconEntityInteractive = (iconEntity: CanvasIconEntity) =>
    isIconEntityVisible(iconEntity) &&
    !isIconEntityLocked(iconEntity) &&
    (!isGripModeActive || iconEntity.id === editingId);

  const isConstructionLineInteractive = (line: ConstructionLine) => {
    const layer = line.layer ?? DIMENSIONS_LAYER;
    return isLayerVisible(layer) && !isLayerLocked(layer) && !isGripModeActive;
  };

  const areShapesEqual = (shapeA: Shape, shapeB: Shape) => {
    if (
      shapeA.id !== shapeB.id ||
      shapeA.kind !== shapeB.kind ||
      shapeA.name !== shapeB.name ||
      shapeA.strokeColor !== shapeB.strokeColor ||
      shapeA.strokeWidth !== shapeB.strokeWidth ||
      shapeA.entityStyleKind !== shapeB.entityStyleKind ||
      shapeA.roofGroupId !== shapeB.roofGroupId ||
      shapeA.roofGroupName !== shapeB.roofGroupName ||
      shapeA.roofGroupCategory !== shapeB.roofGroupCategory ||
      shapeA.roofTypeId !== shapeB.roofTypeId ||
      shapeA.roofTypeName !== shapeB.roofTypeName ||
      shapeA.roofPitch !== shapeB.roofPitch ||
      shapeA.roofMaterialId !== shapeB.roofMaterialId ||
      shapeA.roofMaterialName !== shapeB.roofMaterialName ||
      shapeA.roofSubstructureId !== shapeB.roofSubstructureId ||
      shapeA.roofSubstructureName !== shapeB.roofSubstructureName ||
      shapeA.roofSubstructureHeight !== shapeB.roofSubstructureHeight ||
      shapeA.roofSubstructureSizeHeight !== shapeB.roofSubstructureSizeHeight ||
      shapeA.roofSubstructureSizeWidth !== shapeB.roofSubstructureSizeWidth ||
      shapeA.roofMetalPlateThickness !== shapeB.roofMetalPlateThickness ||
      shapeA.roofSheetMaterialId !== shapeB.roofSheetMaterialId ||
      shapeA.roofSheetMaterialName !== shapeB.roofSheetMaterialName ||
      shapeA.roofUseOnlyAnchorPoints !== shapeB.roofUseOnlyAnchorPoints ||
      shapeA.roofSystemId !== shapeB.roofSystemId ||
      shapeA.roofSystemName !== shapeB.roofSystemName ||
      shapeA.roofNormativeId !== shapeB.roofNormativeId ||
      shapeA.roofNormativeName !== shapeB.roofNormativeName ||
      shapeA.roofSeamsSpan !== shapeB.roofSeamsSpan ||
      shapeA.roofInsulationThickness !== shapeB.roofInsulationThickness ||
      shapeA.roofWaveSpan !== shapeB.roofWaveSpan ||
      shapeA.roofHipsSpan !== shapeB.roofHipsSpan ||
      shapeA.roofSupportMaterialId !== shapeB.roofSupportMaterialId ||
      shapeA.roofSupportMaterialName !== shapeB.roofSupportMaterialName ||
      shapeA.roofTerminalCurveSupportId !== shapeB.roofTerminalCurveSupportId ||
      shapeA.roofTerminalCurveSupportCode !== shapeB.roofTerminalCurveSupportCode ||
      shapeA.roofTerminalCurveSupportName !== shapeB.roofTerminalCurveSupportName ||
      shapeA.roofIntermediateSupportId !== shapeB.roofIntermediateSupportId ||
      shapeA.roofIntermediateSupportCode !== shapeB.roofIntermediateSupportCode ||
      shapeA.roofIntermediateSupportName !== shapeB.roofIntermediateSupportName ||
      shapeA.roofAnchorPointMaterialId !== shapeB.roofAnchorPointMaterialId ||
      shapeA.roofAnchorPointMaterialName !== shapeB.roofAnchorPointMaterialName ||
      shapeA.roofAnchorPointMappingId !== shapeB.roofAnchorPointMappingId ||
      shapeA.roofAnchorPointCode !== shapeB.roofAnchorPointCode ||
      shapeA.roofAnchorPointName !== shapeB.roofAnchorPointName ||
      shapeA.roofComponentMaterialId !== shapeB.roofComponentMaterialId ||
      shapeA.roofComponentMaterialName !== shapeB.roofComponentMaterialName ||
      shapeA.roofComponentAbsorberId !== shapeB.roofComponentAbsorberId ||
      shapeA.roofComponentAbsorberCode !== shapeB.roofComponentAbsorberCode ||
      shapeA.roofComponentAbsorberName !== shapeB.roofComponentAbsorberName ||
      shapeA.roofComponentSystemTypeId !== shapeB.roofComponentSystemTypeId ||
      shapeA.roofComponentSystemTypeName !== shapeB.roofComponentSystemTypeName ||
      shapeA.roofFixed !== shapeB.roofFixed ||
      shapeA.roofShuttleId !== shapeB.roofShuttleId ||
      shapeA.roofShuttleCode !== shapeB.roofShuttleCode ||
      shapeA.roofShuttleName !== shapeB.roofShuttleName ||
      shapeA.roofNrOfShuttles !== shapeB.roofNrOfShuttles ||
      shapeA.roofMembraneId !== shapeB.roofMembraneId ||
      shapeA.roofMembraneName !== shapeB.roofMembraneName ||
      shapeA.safetySystemId !== shapeB.safetySystemId ||
      shapeA.safetySystemName !== shapeB.safetySystemName ||
      getShapeLayer(shapeA) !== getShapeLayer(shapeB) ||
      shapeA.closed !== shapeB.closed
    ) {
      return false;
    }

    if (shapeA.kind === "circle" && shapeB.kind === "circle") {
      return arePointsEqual(shapeA.center, shapeB.center) && shapeA.radius === shapeB.radius;
    }

    if (shapeA.kind === "arc" && shapeB.kind === "arc") {
      return (
        arePointsEqual(shapeA.center, shapeB.center) &&
        shapeA.radius === shapeB.radius &&
        shapeA.startAngle === shapeB.startAngle &&
        shapeA.endAngle === shapeB.endAngle
      );
    }

    if (shapeA.kind === "circle" || shapeB.kind === "circle" || shapeA.kind === "arc" || shapeB.kind === "arc") {
      return false;
    }

    const lifeLineVerticesA = shapeA.lifeLineVertices ?? [];
    const lifeLineVerticesB = shapeB.lifeLineVertices ?? [];
    const lifeLineDesignPointsA = shapeA.lifeLineDesignPoints ?? [];
    const lifeLineDesignPointsB = shapeB.lifeLineDesignPoints ?? [];

    return (
      shapeA.lifeLineLineNumber === shapeB.lifeLineLineNumber &&
      shapeA.points.length === shapeB.points.length &&
      shapeA.points.every((value, index) => {
        const otherValue = shapeB.points[index];
        return otherValue != null && value === otherValue;
      }) &&
      (shapeA.holes?.length ?? 0) === (shapeB.holes?.length ?? 0) &&
      (shapeA.holes ?? []).every(
        (hole, holeIndex) =>
          hole.length === (shapeB.holes?.[holeIndex]?.length ?? -1) &&
          hole.every((value, valueIndex) => value === shapeB.holes?.[holeIndex]?.[valueIndex]),
      ) &&
      (shapeA.circleHoles?.length ?? 0) === (shapeB.circleHoles?.length ?? 0) &&
      (shapeA.circleHoles ?? []).every((hole, holeIndex) => {
        const otherHole = shapeB.circleHoles?.[holeIndex];
        return !!otherHole && arePointsEqual(hole.center, otherHole.center) && hole.radius === otherHole.radius;
      }) &&
      lifeLineVerticesA.length === lifeLineVerticesB.length &&
      lifeLineVerticesA.every((vertex, index) => {
        const otherVertex = lifeLineVerticesB[index];
        return (
          !!otherVertex &&
          vertex.x === otherVertex.x &&
          vertex.y === otherVertex.y &&
          vertex.kind === otherVertex.kind &&
          vertex.label === otherVertex.label &&
          vertex.displayLabel === otherVertex.displayLabel
        );
      }) &&
      lifeLineDesignPointsA.length === lifeLineDesignPointsB.length &&
      lifeLineDesignPointsA.every((point, index) => {
        const otherPoint = lifeLineDesignPointsB[index];
        return !!otherPoint && arePointsEqual(point, otherPoint);
      })
    );
  };

  const areDimensionsEqual = (dimensionA: Dimension, dimensionB: Dimension) =>
    dimensionA.id === dimensionB.id &&
    dimensionA.name === dimensionB.name &&
    dimensionA.strokeColor === dimensionB.strokeColor &&
    dimensionA.strokeWidth === dimensionB.strokeWidth &&
    (dimensionA.dimensionType ?? "linear") === (dimensionB.dimensionType ?? "linear") &&
    getDimensionLayer(dimensionA) === getDimensionLayer(dimensionB) &&
    dimensionA.offset === dimensionB.offset &&
    arePointsEqual(dimensionA.start, dimensionB.start) &&
    arePointsEqual(dimensionA.end, dimensionB.end);

  const getConstructionLineStyle = (line: ConstructionLine) =>
    getEntityTypeStyle(line.entityStyleKind ?? "construction-line");

  const getConstructionLineLayer = (line: ConstructionLine) => line.layer ?? DIMENSIONS_LAYER;

  const getConstructionLineStrokeColor = (line: ConstructionLine) =>
    line.strokeColor ?? getConstructionLineStyle(line).strokeColor;

  const getConstructionLineStrokeWidth = (line: ConstructionLine) =>
    line.strokeWidth ?? getConstructionLineStyle(line).strokeWidth;

  const areConstructionLinesEqual = (lineA: ConstructionLine, lineB: ConstructionLine) =>
    lineA.id === lineB.id &&
    getConstructionLineStrokeColor(lineA) === getConstructionLineStrokeColor(lineB) &&
    getConstructionLineStrokeWidth(lineA) === getConstructionLineStrokeWidth(lineB) &&
    getConstructionLineLayer(lineA) === getConstructionLineLayer(lineB) &&
    (lineA.entityStyleKind ?? "construction-line") === (lineB.entityStyleKind ?? "construction-line") &&
    arePointsEqual(lineA.start, lineB.start) &&
    arePointsEqual(lineA.end, lineB.end);

  const getTextEntityLayer = (textEntity: TextEntity) => textEntity.layer ?? TEXT_LAYER;

  const getIconEntityLayer = (iconEntity: CanvasIconEntity) => iconEntity.layer ?? DEFAULT_ICON_LAYER;

  const areTextEntitiesEqual = (textA: TextEntity, textB: TextEntity) =>
    textA.id === textB.id &&
    textA.name === textB.name &&
    textA.strokeColor === textB.strokeColor &&
    textA.strokeWidth === textB.strokeWidth &&
    getTextEntityLayer(textA) === getTextEntityLayer(textB) &&
    (textA.entityStyleKind ?? "text") === (textB.entityStyleKind ?? "text") &&
    textA.text === textB.text &&
    textA.height === textB.height &&
    textA.boxWidth === textB.boxWidth &&
    textA.boxHeight === textB.boxHeight &&
    textA.fontFamily === textB.fontFamily &&
    textA.justification === textB.justification &&
    arePointsEqual(textA.position, textB.position);

  const areIconEntitiesEqual = (iconA: CanvasIconEntity, iconB: CanvasIconEntity) =>
    iconA.id === iconB.id &&
    iconA.name === iconB.name &&
    iconA.strokeColor === iconB.strokeColor &&
    iconA.strokeWidth === iconB.strokeWidth &&
    getIconEntityLayer(iconA) === getIconEntityLayer(iconB) &&
    (iconA.entityStyleKind ?? "icon") === (iconB.entityStyleKind ?? "icon") &&
    iconA.iconId === iconB.iconId &&
    iconA.size === iconB.size &&
    iconA.mainColor === iconB.mainColor &&
    (iconA.rotation ?? 0) === (iconB.rotation ?? 0) &&
    arePointsEqual(iconA.position, iconB.position);

  const areBackgroundAssetsEqual = (assetA: BackgroundAsset | null, assetB: BackgroundAsset | null) => {
    if (assetA === assetB) return true;
    if (!assetA || !assetB) return false;

    return (
      assetA.kind === assetB.kind &&
      assetA.name === assetB.name &&
      assetA.image === assetB.image &&
      arePointsEqual(assetA.center, assetB.center) &&
      assetA.width === assetB.width &&
      assetA.height === assetB.height &&
      assetA.rotation === assetB.rotation &&
      areBackgroundReferencesEqual(assetA.reference, assetB.reference)
    );
  };

  const areHistorySnapshotsEqual = (snapshotA: HistorySnapshot, snapshotB: HistorySnapshot) =>
    snapshotA.backgroundTransparency === snapshotB.backgroundTransparency &&
    snapshotA.polylines.length === snapshotB.polylines.length &&
    snapshotA.dimensions.length === snapshotB.dimensions.length &&
    snapshotA.constructionLines.length === snapshotB.constructionLines.length &&
    (snapshotA.textEntities ?? []).length === (snapshotB.textEntities ?? []).length &&
    (snapshotA.iconEntities ?? []).length === (snapshotB.iconEntities ?? []).length &&
    snapshotA.polylines.every((shape, index) => {
      const matchingShape = snapshotB.polylines[index];
      return matchingShape ? areShapesEqual(shape, matchingShape) : false;
    }) &&
    snapshotA.dimensions.every((dimension, index) => {
      const matchingDimension = snapshotB.dimensions[index];
      return matchingDimension ? areDimensionsEqual(dimension, matchingDimension) : false;
    }) &&
    snapshotA.constructionLines.every((line, index) => {
      const matchingLine = snapshotB.constructionLines[index];
      return matchingLine ? areConstructionLinesEqual(line, matchingLine) : false;
    }) &&
    (snapshotA.textEntities ?? []).every((textEntity, index) => {
      const matchingTextEntity = (snapshotB.textEntities ?? [])[index];
      return matchingTextEntity ? areTextEntitiesEqual(textEntity, matchingTextEntity) : false;
    }) &&
    (snapshotA.iconEntities ?? []).every((iconEntity, index) => {
      const matchingIconEntity = (snapshotB.iconEntities ?? [])[index];
      return matchingIconEntity ? areIconEntitiesEqual(iconEntity, matchingIconEntity) : false;
    }) &&
    areBackgroundAssetsEqual(snapshotA.backgroundAsset, snapshotB.backgroundAsset);

  const captureHistorySnapshot = (): HistorySnapshot => ({
    polylines: polylines.map(cloneShape),
    dimensions: dimensions.map(cloneDimension),
    constructionLines: constructionLines.map(cloneConstructionLine),
    textEntities: textEntities.map(cloneTextEntity),
    iconEntities: iconEntities.map(cloneIconEntity),
    backgroundAsset: cloneBackgroundAsset(backgroundAsset),
    backgroundTransparency,
  });

  const beginHistoryAction = () => {
    if (isRestoringHistoryRef.current || historyActionRef.current) return;

    historyActionRef.current = {
      before: latestHistorySnapshotRef.current
        ? cloneHistorySnapshot(latestHistorySnapshotRef.current)
        : captureHistorySnapshot(),
      finalizeRequested: false,
    };
  };

  const finishHistoryAction = () => {
    if (isRestoringHistoryRef.current || !historyActionRef.current) return;

    historyActionRef.current.finalizeRequested = true;
    setHistoryRevision((current) => current + 1);
  };

  const cancelHistoryAction = () => {
    historyActionRef.current = null;
  };

  const runHistoryAction = (callback: () => void) => {
    beginHistoryAction();
    callback();
    finishHistoryAction();
  };

  const resetInteractionState = () => {
    setSelectedIds([]);
    setSelectionCycle(null);
    setEditingId(null);
    setDraggedGrip(null);
    segmentStretchRef.current = null;
    setDraggedSegmentStretch(null);
    setDraggedDimensionId(null);
    setMoveState(null);
    setTransformState(null);
    setActiveOffset(null);
    setActiveHoleHost(null);
    setNumericEdit(null);
    setDirectionLockAxis(null);
    setActivePolyline(null);
    setActiveRectangle(null);
    setActiveCircle(null);
    setActivePointSeries(null);
    setActiveRuler(null);
    setActiveDimension(null);
    setActiveObjectDimension(null);
    setHoverSnap(null);
    setSelectionBox(null);
    setPanState(null);
    setIsBackgroundReferenceMode(false);
    setIsBackgroundCropMode(false);
    setBackgroundCropRect(null);
    setBackgroundReferenceLine(null);
    setBackgroundReferenceInput(null);
    setPendingIconId(null);
    setIconPreviewPoint(null);
  };

  const applyHistorySnapshot = (snapshot: HistorySnapshot) => {
    historyActionRef.current = null;
    isRestoringHistoryRef.current = true;
    setPolylines(snapshot.polylines.map(cloneShape));
    setDimensions(snapshot.dimensions.map(cloneDimension));
    setConstructionLines(snapshot.constructionLines.map(cloneConstructionLine));
    setTextEntities((snapshot.textEntities ?? []).map(cloneTextEntity));
    setIconEntities((snapshot.iconEntities ?? []).map(cloneIconEntity));
    setBackgroundAsset(cloneBackgroundAsset(snapshot.backgroundAsset));
    setBackgroundTransparency(snapshot.backgroundTransparency);
    resetInteractionState();
  };

  const handleUndo = () => {
    if (undoStack.length === 0) return;

    const previousSnapshot = undoStack[undoStack.length - 1];
    if (!previousSnapshot) return;
    const currentSnapshot = latestHistorySnapshotRef.current ?? captureHistorySnapshot();
    setUndoStack((current) => current.slice(0, -1));
    setRedoStack([cloneHistorySnapshot(currentSnapshot)].slice(-MAX_REDO_HISTORY));
    applyHistorySnapshot(previousSnapshot);
  };

  const handleRedo = () => {
    if (redoStack.length === 0) return;

    const nextSnapshot = redoStack[redoStack.length - 1];
    if (!nextSnapshot) return;
    const currentSnapshot = latestHistorySnapshotRef.current ?? captureHistorySnapshot();
    setRedoStack([]);
    setUndoStack((current) => [...current, cloneHistorySnapshot(currentSnapshot)].slice(-MAX_UNDO_HISTORY));
    applyHistorySnapshot(nextSnapshot);
  };

  const selectedEntities: SelectableEntitySnapshot[] = [
    ...polylines
      .filter((shape) => selectedIds.includes(shape.id))
      .map((shape) => ({
        id: shape.id,
        name: shape.name,
        strokeColor: shape.strokeColor,
        strokeWidth: shape.strokeWidth,
        layer: getShapeLayer(shape),
        roofGroupId: shape.roofGroupId,
        roofGroupName: shape.roofGroupName,
        roofGroupCategory: shape.roofGroupCategory,
        roofTypeId: shape.roofTypeId,
        roofTypeName: shape.roofTypeName,
        roofPitch: shape.roofPitch,
        roofMaterialId: shape.roofMaterialId,
        roofMaterialName: shape.roofMaterialName,
        roofSubstructureId: shape.roofSubstructureId,
        roofSubstructureName: shape.roofSubstructureName,
        roofSubstructureHeight: shape.roofSubstructureHeight,
        roofSubstructureSizeHeight: shape.roofSubstructureSizeHeight,
        roofSubstructureSizeWidth: shape.roofSubstructureSizeWidth,
        roofMetalPlateThickness: shape.roofMetalPlateThickness,
        roofSheetMaterialId: shape.roofSheetMaterialId,
        roofSheetMaterialName: shape.roofSheetMaterialName,
        roofUseOnlyAnchorPoints: shape.roofUseOnlyAnchorPoints,
        roofSystemId: shape.roofSystemId,
        roofSystemName: shape.roofSystemName,
        roofNormativeId: shape.roofNormativeId,
        roofNormativeName: shape.roofNormativeName,
        roofSeamsSpan: shape.roofSeamsSpan,
        roofInsulationThickness: shape.roofInsulationThickness,
        roofWaveSpan: shape.roofWaveSpan,
        roofHipsSpan: shape.roofHipsSpan,
        roofSupportMaterialId: shape.roofSupportMaterialId,
        roofSupportMaterialName: shape.roofSupportMaterialName,
        roofTerminalCurveSupportId: shape.roofTerminalCurveSupportId,
        roofTerminalCurveSupportCode: shape.roofTerminalCurveSupportCode,
        roofTerminalCurveSupportName: shape.roofTerminalCurveSupportName,
        roofIntermediateSupportId: shape.roofIntermediateSupportId,
        roofIntermediateSupportCode: shape.roofIntermediateSupportCode,
        roofIntermediateSupportName: shape.roofIntermediateSupportName,
        roofAnchorPointMaterialId: shape.roofAnchorPointMaterialId,
        roofAnchorPointMaterialName: shape.roofAnchorPointMaterialName,
        roofAnchorPointMappingId: shape.roofAnchorPointMappingId,
        roofAnchorPointCode: shape.roofAnchorPointCode,
        roofAnchorPointName: shape.roofAnchorPointName,
        roofComponentMaterialId: shape.roofComponentMaterialId,
        roofComponentMaterialName: shape.roofComponentMaterialName,
        roofComponentAbsorberId: shape.roofComponentAbsorberId,
        roofComponentAbsorberCode: shape.roofComponentAbsorberCode,
        roofComponentAbsorberName: shape.roofComponentAbsorberName,
        roofComponentSystemTypeId: shape.roofComponentSystemTypeId,
        roofComponentSystemTypeName: shape.roofComponentSystemTypeName,
        roofFixed: shape.roofFixed,
        roofShuttleId: shape.roofShuttleId,
        roofShuttleCode: shape.roofShuttleCode,
        roofShuttleName: shape.roofShuttleName,
        roofNrOfShuttles: shape.roofNrOfShuttles,
        roofMembraneId: shape.roofMembraneId,
        roofMembraneName: shape.roofMembraneName,
        safetySystemId: shape.safetySystemId,
        safetySystemName: shape.safetySystemName,
        anchorPointAdditionalProductCodes: shape.anchorPointAdditionalProductCodes,
        anchorPointOptionalProductCodes: shape.anchorPointOptionalProductCodes,
        isObstacle: shape.isObstacle,
        fallSide: shape.fallSide,
        kind: shape.kind,
      })),
    ...dimensions
      .filter((dimension) => selectedIds.includes(dimension.id))
      .map((dimension) => ({
        id: dimension.id,
        name: dimension.name,
        strokeColor: dimension.strokeColor,
        strokeWidth: dimension.strokeWidth,
        layer: getDimensionLayer(dimension),
        kind: "dimension" as const,
      })),
    ...constructionLines
      .filter((line) => selectedIds.includes(line.id))
      .map((line) => ({
        id: line.id,
        name: "Construction Line",
        strokeColor: getConstructionLineStrokeColor(line),
        strokeWidth: getConstructionLineStrokeWidth(line),
        layer: getConstructionLineLayer(line),
        kind: "construction-line" as const,
      })),
    ...textEntities
      .filter((textEntity) => selectedIds.includes(textEntity.id))
      .map((textEntity) => ({
        id: textEntity.id,
        name: textEntity.name,
        strokeColor: textEntity.strokeColor,
        strokeWidth: textEntity.strokeWidth,
        layer: getTextEntityLayer(textEntity),
        kind: "text" as const,
      })),
    ...iconEntities
      .filter((iconEntity) => selectedIds.includes(iconEntity.id))
      .map((iconEntity) => ({
        id: iconEntity.id,
        name: iconEntity.name,
        strokeColor: iconEntity.strokeColor,
        strokeWidth: iconEntity.strokeWidth,
        layer: getIconEntityLayer(iconEntity),
        kind: "icon" as const,
      })),
  ];
  const firstSelectedEntity = selectedEntities[0] ?? null;
  const singleSelectedEntity = selectedEntities.length === 1 ? firstSelectedEntity : null;
  const singleSelectedShape =
    singleSelectedEntity && singleSelectedEntity.kind !== "dimension"
      ? (polylines.find((shape) => shape.id === singleSelectedEntity.id) ?? null)
      : null;
  const commonSelectedColor =
    firstSelectedEntity && selectedEntities.every((entity) => entity.strokeColor === firstSelectedEntity.strokeColor)
      ? firstSelectedEntity.strokeColor
      : null;
  const commonSelectedLayer =
    firstSelectedEntity && selectedEntities.every((entity) => entity.layer === firstSelectedEntity.layer)
      ? firstSelectedEntity.layer
      : null;
  const selectedEntityStyleKinds: EntityStyleKind[] = [
    ...polylines.filter((shape) => selectedIds.includes(shape.id)).map((shape) => getShapeStyleKind(shape)),
    ...dimensions
      .filter((dimension) => selectedIds.includes(dimension.id))
      .map((dimension) => dimension.entityStyleKind ?? "dimension"),
    ...constructionLines
      .filter((line) => selectedIds.includes(line.id))
      .map((line) => line.entityStyleKind ?? "construction-line"),
    ...textEntities.filter((textEntity) => selectedIds.includes(textEntity.id)).map(() => "text" as const),
    ...iconEntities.filter((iconEntity) => selectedIds.includes(iconEntity.id)).map(() => "icon" as const),
  ];
  const commonSelectedEntityStyleKind =
    selectedEntityStyleKinds.length > 0 &&
    selectedEntityStyleKinds.every((kind) => kind === selectedEntityStyleKinds[0])
      ? selectedEntityStyleKinds[0]
      : null;
  const commonSelectedEntityTypeStyle = commonSelectedEntityStyleKind
    ? getEntityTypeStyle(commonSelectedEntityStyleKind)
    : null;
  const commonSelectedEntityStyleLabel =
    ENTITY_STYLE_OPTIONS.find((option) => option.kind === commonSelectedEntityStyleKind)?.label ??
    commonSelectedEntityStyleKind;
  const selectedEntityHasVertexStyle = polylines.some((shape) => selectedIds.includes(shape.id));
  const selectedCustomFlatRoof =
    selectedEntities.length === 1 &&
    singleSelectedShape &&
    !singleSelectedShape.isObstacle &&
    getShapeLayer(singleSelectedShape) === ROOF_LAYER &&
    (singleSelectedShape.name.startsWith("Custom Flat Roof") || singleSelectedShape.name.startsWith("Custom Flat Room"))
      ? singleSelectedShape
      : null;
  const selectedAnchorPointShape =
    selectedEntities.length === 1 &&
    singleSelectedShape?.entityStyleKind === "single-anchor-point" &&
    singleSelectedShape.kind === "circle"
      ? singleSelectedShape
      : null;
  const selectedLifeLineShape =
    selectedEntities.length === 1 &&
    singleSelectedShape &&
    singleSelectedShape.kind !== "circle" &&
    singleSelectedShape.kind !== "arc" &&
    (singleSelectedShape.entityStyleKind === "single-lifeline" ||
      (singleSelectedShape.lifeLineVertices?.length ?? 0) > 0)
      ? singleSelectedShape
      : null;
  useEffect(() => {
    if (!selectedLifeLineShape) return;

    setIsEntityPropertiesOpen(true);
    setActiveConfigurationPage("lifelineSystems");
    setActiveLifeLineTab("info");

    if (selectedLifeLineShape.safetySystemId) {
      setSelectedSafetySystem(selectedLifeLineShape.safetySystemId);
    }
  }, [selectedLifeLineShape?.id, selectedLifeLineShape?.safetySystemId]);
  useEffect(() => {
    if (!selectedAnchorPointShape) return;

    setIsEntityPropertiesOpen(true);
    setActiveConfigurationPage("anchorPoints");
    setActiveAnchorPointTab("anchorPoint");

    if (selectedAnchorPointShape.safetySystemId) {
      setSelectedAnchorPointSystem(selectedAnchorPointShape.safetySystemId);
    }

    setFallbackAnchorPointNormativeId(selectedAnchorPointShape.roofNormativeId ?? "");
    setFallbackSupportSeamsSpan(
      selectedAnchorPointShape.roofSeamsSpan != null ? String(selectedAnchorPointShape.roofSeamsSpan) : "",
    );
    setFallbackSupportInsulationThickness(
      selectedAnchorPointShape.roofInsulationThickness != null
        ? String(selectedAnchorPointShape.roofInsulationThickness)
        : "",
    );
    setFallbackAnchorPointMaterialId(selectedAnchorPointShape.roofAnchorPointMaterialId ?? "");
    setFallbackAnchorPointMappingId(selectedAnchorPointShape.roofAnchorPointMappingId ?? "");
  }, [
    selectedAnchorPointShape?.id,
    selectedAnchorPointShape?.safetySystemId,
    selectedAnchorPointShape?.roofNormativeId,
    selectedAnchorPointShape?.roofSeamsSpan,
    selectedAnchorPointShape?.roofInsulationThickness,
    selectedAnchorPointShape?.roofAnchorPointMaterialId,
    selectedAnchorPointShape?.roofAnchorPointMappingId,
  ]);
  const commonSelectedRoofGroup =
    firstSelectedEntity?.roofGroupId &&
    selectedEntities.every((entity) => entity.roofGroupId === firstSelectedEntity.roofGroupId)
      ? {
          id: firstSelectedEntity.roofGroupId,
          name: firstSelectedEntity.roofGroupName ?? firstSelectedEntity.roofGroupId,
          category: firstSelectedEntity.roofGroupCategory,
        }
      : selectedCustomFlatRoof
        ? {
            id: selectedCustomFlatRoof.id,
            name: selectedCustomFlatRoof.roofGroupName ?? selectedCustomFlatRoof.name,
            category: "Flat" as const,
          }
        : null;
  const selectedRoofGroupShapes = commonSelectedRoofGroup
    ? polylines.filter((shape) =>
        commonSelectedRoofGroup.id === selectedCustomFlatRoof?.id
          ? shape.id === selectedCustomFlatRoof.id
          : shape.roofGroupId === commonSelectedRoofGroup.id,
      )
    : [];
  const selectedIsRoofSelection =
    selectedEntities.length > 0 &&
    selectedEntities.every((entity) => {
      const selectedShape = polylines.find((shape) => shape.id === entity.id);
      return Boolean(selectedShape && getRoofContextId(selectedShape));
    });
  const selectedIsLifeLineSelection =
    selectedEntities.length > 0 &&
    selectedEntities.every((entity) => {
      const selectedShape = polylines.find((shape) => shape.id === entity.id);
      return Boolean(
        selectedShape &&
        selectedShape.kind !== "circle" &&
        selectedShape.kind !== "arc" &&
        (selectedShape.entityStyleKind === "single-lifeline" || (selectedShape.lifeLineVertices?.length ?? 0) > 0),
      );
    });
  const selectedIsAnchorPointSelection =
    selectedEntities.length > 0 &&
    selectedEntities.every((entity) => {
      const selectedShape = polylines.find((shape) => shape.id === entity.id);
      return Boolean(
        selectedShape && selectedShape.entityStyleKind === "single-anchor-point" && selectedShape.kind === "circle",
      );
    });
  const getCommonSelectedRoofValue = <K extends keyof Shape>(key: K) => {
    const firstShape = selectedRoofGroupShapes[0];
    if (!firstShape) return "";
    const firstValue = firstShape[key];
    return selectedRoofGroupShapes.every((shape) => shape[key] === firstValue) ? String(firstValue ?? "") : "";
  };
  const commonSelectedRoofReference = getCommonSelectedRoofValue("roofGroupName");
  const isRoofTypeCompatibleWithGroupCategory = (roofType: NonNullable<typeof settings>["roofTypes"][number]) => {
    if (!commonSelectedRoofGroup?.category) return true;
    const category = roofType.roofCategory;
    const isCurvedCategory =
      isCurvedRoofCompatibilityName(category.name) || isCurvedRoofCompatibilityName(roofType.name);

    if (commonSelectedRoofGroup.category === "Flat") return category.isFlat;
    if (commonSelectedRoofGroup.category === "Curved") return !category.isFlat && isCurvedCategory;
    return !category.isFlat && !isCurvedCategory;
  };
  const commonSelectedRoofMaterialId = getCommonSelectedRoofValue("roofMaterialId");
  const commonSelectedRoofTypeId = getCommonSelectedRoofValue("roofTypeId");
  const commonSelectedRoofPitch = getCommonSelectedRoofValue("roofPitch");
  const commonSelectedRoofSubstructureId = getCommonSelectedRoofValue("roofSubstructureId");
  const commonSelectedRoofSubstructureHeight = getCommonSelectedRoofValue("roofSubstructureHeight");
  const commonSelectedRoofSubstructureSizeHeight = getCommonSelectedRoofValue("roofSubstructureSizeHeight");
  const commonSelectedRoofSubstructureSizeWidth = getCommonSelectedRoofValue("roofSubstructureSizeWidth");
  const commonSelectedRoofMetalPlateThickness = getCommonSelectedRoofValue("roofMetalPlateThickness");
  const commonSelectedRoofSheetMaterialId = getCommonSelectedRoofValue("roofSheetMaterialId");
  const commonSelectedRoofUseOnlyAnchorPoints = getCommonSelectedRoofValue("roofUseOnlyAnchorPoints") === "true";
  const commonSelectedRoofSystemId = getCommonSelectedRoofValue("roofSystemId");
  const commonSelectedRoofNormativeId = getCommonSelectedRoofValue("roofNormativeId");
  const commonSelectedRoofSeamsSpan = getCommonSelectedRoofValue("roofSeamsSpan");
  const commonSelectedRoofInsulationThickness = getCommonSelectedRoofValue("roofInsulationThickness");
  const commonSelectedRoofWaveSpan = getCommonSelectedRoofValue("roofWaveSpan");
  const commonSelectedRoofHipsSpan = getCommonSelectedRoofValue("roofHipsSpan");
  const commonSelectedRoofSupportMaterialId = getCommonSelectedRoofValue("roofSupportMaterialId");
  const commonSelectedRoofTerminalCurveSupportId = getCommonSelectedRoofValue("roofTerminalCurveSupportId");
  const commonSelectedRoofTerminalCurveSupportCode = getCommonSelectedRoofValue("roofTerminalCurveSupportCode");
  const commonSelectedRoofIntermediateSupportId = getCommonSelectedRoofValue("roofIntermediateSupportId");
  const commonSelectedRoofIntermediateSupportCode = getCommonSelectedRoofValue("roofIntermediateSupportCode");
  const commonSelectedRoofAnchorPointMaterialId = getCommonSelectedRoofValue("roofAnchorPointMaterialId");
  const commonSelectedRoofAnchorPointMappingId = getCommonSelectedRoofValue("roofAnchorPointMappingId");
  const commonSelectedRoofAnchorPointCode = getCommonSelectedRoofValue("roofAnchorPointCode");
  const commonSelectedRoofComponentMaterialId = getCommonSelectedRoofValue("roofComponentMaterialId");
  const commonSelectedRoofComponentAbsorberId = getCommonSelectedRoofValue("roofComponentAbsorberId");
  const commonSelectedRoofComponentSystemTypeId = getCommonSelectedRoofValue("roofComponentSystemTypeId");
  const commonSelectedRoofFixedValue = getCommonSelectedRoofValue("roofFixed");
  const commonSelectedRoofFixed = commonSelectedRoofFixedValue === "true";
  const commonSelectedRoofShuttleId = getCommonSelectedRoofValue("roofShuttleId");
  const commonSelectedRoofNrOfShuttles = getCommonSelectedRoofValue("roofNrOfShuttles");
  const effectiveRoofComponentMaterialId = commonSelectedRoofGroup
    ? commonSelectedRoofComponentMaterialId
    : fallbackRoofComponentMaterialId;
  const effectiveRoofComponentAbsorberId = commonSelectedRoofGroup
    ? commonSelectedRoofComponentAbsorberId
    : fallbackRoofComponentAbsorberId;
  const effectiveRoofComponentSystemTypeId = commonSelectedRoofGroup
    ? commonSelectedRoofComponentSystemTypeId
    : fallbackRoofComponentSystemTypeId;
  const effectiveRoofFixed = commonSelectedRoofGroup ? commonSelectedRoofFixed : fallbackRoofFixed;
  const effectiveRoofShuttleId = commonSelectedRoofGroup ? commonSelectedRoofShuttleId : fallbackRoofShuttleId;
  const effectiveRoofNrOfShuttles = commonSelectedRoofGroup ? commonSelectedRoofNrOfShuttles : fallbackRoofNrOfShuttles;
  const effectiveRoofSeamsSpan = commonSelectedRoofGroup ? commonSelectedRoofSeamsSpan : fallbackSupportSeamsSpan;
  const effectiveRoofInsulationThickness = commonSelectedRoofGroup
    ? commonSelectedRoofInsulationThickness
    : fallbackSupportInsulationThickness;
  const effectiveSupportMaterialId = commonSelectedRoofGroup
    ? commonSelectedRoofSupportMaterialId
    : fallbackSupportMaterialId;
  const effectiveTerminalCurveSupportId = commonSelectedRoofGroup
    ? commonSelectedRoofTerminalCurveSupportId
    : fallbackTerminalCurveSupportId;
  const effectiveIntermediateSupportId = commonSelectedRoofGroup
    ? commonSelectedRoofIntermediateSupportId
    : fallbackIntermediateSupportId;
  const effectiveAnchorPointMaterialId = commonSelectedRoofGroup
    ? commonSelectedRoofAnchorPointMaterialId
    : fallbackAnchorPointMaterialId;
  const effectiveAnchorPointMappingId = commonSelectedRoofGroup
    ? commonSelectedRoofAnchorPointMappingId
    : fallbackAnchorPointMappingId;
  const roofTypeOptions = (settings?.roofTypes ?? []).filter(isRoofTypeCompatibleWithGroupCategory);
  const selectedRoofType = settings?.roofTypes.find((roofType) => roofType.id === commonSelectedRoofTypeId);
  const getSelectedRoofTypeIconUrl = () => {
    const roofTypeName = normalizeRoofCompatibilityText(selectedRoofType?.name);
    const categoryName = normalizeRoofCompatibilityText(selectedRoofType?.roofCategory.name);
    const combinedName = `${roofTypeName} ${categoryName}`;
    const hasAny = (patterns: string[]) => patterns.some((pattern) => combinedName.includes(pattern));

    if (hasAny(["curv", "round", "rounded", "barrel", "vault"])) return curvedRoofIconUrl;
    if (hasAny(["multi shed", "multished", "multi-shed", "shed multiplo"])) return multiShedRoofIconUrl;
    if (hasAny(["shed", "single pitched", "monopitch", "mono pitch", "monofalda", "mono falda", "1 falda"])) {
      return singlePitchedRoofIconUrl;
    }
    if (hasAny(["multi gable", "multigable", "multi-gable", "gable multiplo", "multi falda", "multifalda"])) {
      return multiGableRoofIconUrl;
    }
    if (hasAny(["padiglione", "hip", "hipped", "4 falde", "quattro falde"])) return hipRoofIconUrl;
    if (hasAny(["gable", "2 falde", "due falde", "two pitch", "two-pitch", "double pitch"])) {
      return twoPitchRoofIconUrl;
    }
    if (selectedRoofType?.roofCategory.isFlat || commonSelectedRoofGroup?.category === "Flat")
      return squaredFlatRoofIconUrl;
    if (selectedRoofType?.hasHips) return hipRoofIconUrl;
    if (selectedRoofType?.canBeMultiple) return multiGableRoofIconUrl;
    if (commonSelectedRoofGroup?.category === "Curved") return curvedRoofIconUrl;
    if (commonSelectedRoofGroup?.category === "Pitched") return twoPitchRoofIconUrl;
    return flatRoofIconUrl;
  };
  const selectedRoofTypeIconUrl = commonSelectedRoofGroup ? getSelectedRoofTypeIconUrl() : null;
  const selectedRoofTypeIconLabel = selectedRoofType?.name ?? commonSelectedRoofGroup?.category ?? "Roof";
  const selectedRoofPropertyCategoryIds = (() => {
    if (!commonSelectedRoofGroup?.category || !settings?.roofTypes) return new Set<string>();

    const ids = new Set<string>();
    if (selectedRoofType) {
      ids.add(selectedRoofType.roofCategory.id);
      return ids;
    }

    let hasCurvedCategoryMatch = false;

    settings.roofTypes.forEach((roofType) => {
      const category = roofType.roofCategory;
      const isCurvedCategory =
        isCurvedRoofCompatibilityName(category.name) || isCurvedRoofCompatibilityName(roofType.name);

      if (commonSelectedRoofGroup.category === "Flat" && category.isFlat) {
        ids.add(category.id);
      }

      if (commonSelectedRoofGroup.category === "Curved" && !category.isFlat && isCurvedCategory) {
        ids.add(category.id);
        hasCurvedCategoryMatch = true;
      }

      if (commonSelectedRoofGroup.category === "Pitched" && !category.isFlat && !isCurvedCategory) {
        ids.add(category.id);
      }
    });

    if (commonSelectedRoofGroup.category === "Curved" && !hasCurvedCategoryMatch) {
      settings.roofTypes.forEach((roofType) => {
        if (!roofType.roofCategory.isFlat) {
          ids.add(roofType.roofCategory.id);
        }
      });
    }

    return ids;
  })();
  const roofMaterialOptions = (() => {
    const allMaterials = settings?.roofMaterials ?? [];
    if (selectedRoofPropertyCategoryIds.size === 0) return allMaterials;

    const materialIds = new Set(
      (settings?.systemMappings ?? [])
        .filter((mapping) => selectedRoofPropertyCategoryIds.has(mapping.roofCategoryId))
        .map((mapping) => mapping.roofMaterialId),
    );
    return allMaterials.filter((material) => materialIds.has(material.id));
  })();
  const roofSubstructureOptions = (() => {
    const allSubstructures = settings?.roofSubstructures ?? [];
    if (selectedRoofPropertyCategoryIds.size === 0) return allSubstructures;

    const substructureIds = new Set(
      (settings?.systemMappings ?? [])
        .filter((mapping) => selectedRoofPropertyCategoryIds.has(mapping.roofCategoryId))
        .filter((mapping) => !commonSelectedRoofMaterialId || mapping.roofMaterialId === commonSelectedRoofMaterialId)
        .map((mapping) => mapping.roofSubstructureId)
        .filter((id): id is string => Boolean(id)),
    );
    return allSubstructures.filter((substructure) => substructureIds.has(substructure.id));
  })();
  const selectedRoofSystemMappings = (settings?.systemMappings ?? []).filter((mapping) => {
    if (selectedRoofPropertyCategoryIds.size > 0 && !selectedRoofPropertyCategoryIds.has(mapping.roofCategoryId)) {
      return false;
    }
    if (commonSelectedRoofMaterialId && mapping.roofMaterialId !== commonSelectedRoofMaterialId) {
      return false;
    }
    if (
      commonSelectedRoofSubstructureId &&
      (!mapping.roofSubstructureId || mapping.roofSubstructureId !== commonSelectedRoofSubstructureId)
    ) {
      return false;
    }
    return true;
  });
  const formatMappingValue = (value: unknown, suffix = "") => {
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (typeof value === "number" || typeof value === "string") return `${value}${suffix}`;
    if (
      value &&
      typeof value === "object" &&
      "height" in value &&
      "width" in value &&
      typeof value.height === "number" &&
      typeof value.width === "number"
    ) {
      return `${value.height}x${value.width} mm`;
    }
    return "";
  };
  const formatUniqueMappingValues = (values: unknown[], suffix = "") => {
    const uniqueValues = Array.from(
      new Set(values.map((value) => formatMappingValue(value, suffix)).filter((value) => value !== "")),
    );
    if (uniqueValues.length === 0) return "N/D";
    return uniqueValues.join(" / ");
  };
  const selectedRoofMappingSummary = {
    minSubstructureHeight: formatUniqueMappingValues(
      selectedRoofSystemMappings.map((mapping) => mapping.minSubstructureHeight),
      " mm",
    ),
    minSubstructureSize: formatUniqueMappingValues(
      selectedRoofSystemMappings.map((mapping) => mapping.minSubstructureSize),
    ),
    minMetalPlateThickness: formatUniqueMappingValues(
      selectedRoofSystemMappings.map((mapping) => mapping.minMetalPlateThickness),
      " mm",
    ),
    roofWeightRequired: formatUniqueMappingValues(
      selectedRoofSystemMappings.map((mapping) => mapping.roofWeightRequired),
    ),
    seamsSpanRequired: formatUniqueMappingValues(
      selectedRoofSystemMappings.map((mapping) => mapping.seamsSpanRequired),
    ),
  };
  const selectedRoofMaterial = settings?.roofMaterials.find((material) => material.id === commonSelectedRoofMaterialId);
  const selectedRoofSubstructure = settings?.roofSubstructures.find(
    (substructure) => substructure.id === commonSelectedRoofSubstructureId,
  );
  const selectedRoofSheetRequirementText = normalizeRoofCompatibilityText(
    [
      selectedRoofType?.name,
      selectedRoofType?.roofCategory.name,
      selectedRoofMaterial?.name,
      selectedRoofSubstructure?.name,
    ]
      .filter((value): value is string => Boolean(value))
      .join(" "),
  );
  const hasSelectedRoofSheetRequirementWord = (patterns: string[]) =>
    patterns.some((pattern) => selectedRoofSheetRequirementText.includes(pattern));
  const shouldForceRoofSheetMaterialForProfile =
    (hasSelectedRoofSheetRequirementWord(["ondulat", "undulat", "corrugat", "grecat", "trapez", "wave", "wavy"]) &&
      hasSelectedRoofSheetRequirementWord(["metal", "sheet", "lamiera", "acciaio", "steel"])) ||
    (hasSelectedRoofSheetRequirementWord(["sandwich"]) &&
      hasSelectedRoofSheetRequirementWord(["panel", "pannell", "metal", "sheet", "lamiera", "acciaio", "steel"]));
  const shouldShowRoofSheetMaterials =
    Boolean(selectedRoofMaterial?.sheetMaterialRequired) || shouldForceRoofSheetMaterialForProfile;
  const roofSheetMaterialOptions = settings?.sheetMaterials ?? [];
  const formatRoofSheetMaterialOption = (sheetMaterial: (typeof roofSheetMaterialOptions)[number]) =>
    `${sheetMaterial.material} - Min Value ${sheetMaterial.minValue}`;
  const selectedRoofMaterialPictureLabel = selectedRoofMaterial?.name?.trim() || "Material";
  const selectedRoofMaterialImageUrl = selectedRoofMaterial?.image
    ? `${import.meta.env.VITE_BUCKET_URL}${selectedRoofMaterial.image}?${getMinutesRoundedTimestamp()}`
    : null;
  const shouldShowRoofMaterialImage =
    Boolean(selectedRoofMaterialImageUrl) && failedRoofMaterialImageUrl !== selectedRoofMaterialImageUrl;
  const selectedRoofSubstructurePictureLabel = selectedRoofSubstructure?.name?.trim() || "Roof Substructure";
  const selectedRoofSubstructureImageUrl = selectedRoofSubstructure?.image
    ? `${import.meta.env.VITE_BUCKET_URL}${selectedRoofSubstructure.image}?${getMinutesRoundedTimestamp()}`
    : null;
  const shouldShowRoofSubstructureImage =
    Boolean(selectedRoofSubstructureImageUrl) && failedRoofSubstructureImageUrl !== selectedRoofSubstructureImageUrl;
  useEffect(() => {
    setFailedRoofMaterialImageUrl(null);
  }, [selectedRoofMaterial?.image]);
  useEffect(() => {
    setFailedRoofSubstructureImageUrl(null);
  }, [selectedRoofSubstructure?.image]);
  const selectedLifeLineDefaultSystem = settings?.systems.find((system) => system.id === selectedSafetySystem);
  const selectedLifeLineSystemPictureLabel = selectedLifeLineDefaultSystem?.name?.trim() || "System";
  const selectedLifeLineSystemImageUrl = selectedLifeLineDefaultSystem?.image
    ? `${import.meta.env.VITE_BUCKET_URL}${selectedLifeLineDefaultSystem.image}?${getMinutesRoundedTimestamp()}`
    : null;
  const shouldShowLifeLineSystemImage =
    Boolean(selectedLifeLineSystemImageUrl) && failedLifeLineSystemImageUrl !== selectedLifeLineSystemImageUrl;
  useEffect(() => {
    setFailedLifeLineSystemImageUrl(null);
  }, [selectedLifeLineDefaultSystem?.image]);
  const effectiveLifeLineSystemSettings = normalizeSafetySystemSettings(
    safetySystemSettings[selectedSafetySystem] ?? getDefaultSafetySystemSettings(selectedLifeLineDefaultSystem),
  );
  const selectedAnchorPointDefaultSystem = settings?.systems.find((system) => system.id === selectedAnchorPointSystem);
  const selectedAnchorPointSystemPictureLabel = selectedAnchorPointDefaultSystem?.name?.trim() || "Anchor Point";
  const selectedAnchorPointSystemImageUrl = selectedAnchorPointDefaultSystem?.image
    ? `${import.meta.env.VITE_BUCKET_URL}${selectedAnchorPointDefaultSystem.image}?${getMinutesRoundedTimestamp()}`
    : null;
  const shouldShowAnchorPointSystemImage =
    Boolean(selectedAnchorPointSystemImageUrl) && failedAnchorPointSystemImageUrl !== selectedAnchorPointSystemImageUrl;
  useEffect(() => {
    setFailedAnchorPointSystemImageUrl(null);
  }, [selectedAnchorPointDefaultSystem?.image]);
  const selectedRoofSystem =
    settings?.systems.find((system) => system.id === commonSelectedRoofSystemId) ??
    (commonSelectedRoofUseOnlyAnchorPoints ? selectedAnchorPointDefaultSystem : selectedLifeLineDefaultSystem);
  const isTClampsSystem = (system?: Pick<PatrolSystemForSettings, "name"> | null) =>
    normalizeRoofCompatibilityText(system?.name)
      .replace(/[^a-z0-9]/g, "")
      .includes("tclamp");
  const selectedLifeLineNormativeOptions = selectedLifeLineDefaultSystem?.normatives ?? [];
  const selectedAnchorPointNormativeOptions = selectedAnchorPointDefaultSystem?.normatives ?? [];
  const getPreferredNormativeId = (options: typeof selectedLifeLineNormativeOptions, preferredId: string) =>
    options.some((normative) => normative.id === preferredId) ? preferredId : (options[0]?.id ?? "");
  const effectiveLifeLineNormativeId = getPreferredNormativeId(
    selectedLifeLineNormativeOptions,
    commonSelectedRoofGroup && !commonSelectedRoofUseOnlyAnchorPoints
      ? commonSelectedRoofNormativeId
      : fallbackLifeLineNormativeId,
  );
  const effectiveAnchorPointNormativeId = getPreferredNormativeId(
    selectedAnchorPointNormativeOptions,
    commonSelectedRoofGroup && commonSelectedRoofUseOnlyAnchorPoints
      ? commonSelectedRoofNormativeId
      : fallbackAnchorPointNormativeId,
  );
  const selectedLifeLineNormative = selectedLifeLineNormativeOptions.find(
    (normative) => normative.id === effectiveLifeLineNormativeId,
  );
  const selectedAnchorPointNormative = selectedAnchorPointNormativeOptions.find(
    (normative) => normative.id === effectiveAnchorPointNormativeId,
  );
  const selectedLifeLineSystemMappings = selectedSafetySystem
    ? selectedRoofSystemMappings.filter((mapping) => mapping.systemId === selectedSafetySystem)
    : [];
  const selectedAnchorPointSystemMappings = selectedAnchorPointSystem
    ? selectedRoofSystemMappings.filter((mapping) => mapping.systemId === selectedAnchorPointSystem)
    : [];
  const selectedCurrentRoofSystemMappings = selectedRoofSystem?.id
    ? selectedRoofSystemMappings.filter((mapping) => mapping.systemId === selectedRoofSystem.id)
    : [];
  const roofComponentMaterialOptions = (settings?.materials ?? []).filter((material) =>
    material.absorbers?.some((absorber) =>
      absorber.systems.some((absorberSystem) => absorberSystem.systemId === selectedRoofSystem?.id),
    ),
  );
  const selectedRoofComponentMaterial =
    roofComponentMaterialOptions.find((material) => material.id === effectiveRoofComponentMaterialId) ??
    roofComponentMaterialOptions[0] ??
    null;
  const roofComponentAbsorberOptions =
    selectedRoofComponentMaterial?.absorbers?.filter((absorber) =>
      absorber.systems.some((absorberSystem) => absorberSystem.systemId === selectedRoofSystem?.id),
    ) ?? [];
  const selectedRoofComponentAbsorber =
    roofComponentAbsorberOptions.find((absorber) => absorber.item.id === effectiveRoofComponentAbsorberId) ??
    roofComponentAbsorberOptions[0] ??
    null;
  const roofComponentSystemTypeIds = new Set(
    (settings?.curveIntermediates ?? [])
      .filter(
        (curveIntermediate) =>
          Boolean(selectedRoofComponentMaterial?.id) &&
          curveIntermediate.materialsIds.includes(selectedRoofComponentMaterial?.id ?? "") &&
          Boolean(selectedRoofSystem?.id) &&
          curveIntermediate.systemsIds.includes(selectedRoofSystem?.id ?? ""),
      )
      .flatMap((curveIntermediate) => curveIntermediate.systemTypesIds),
  );
  const roofComponentSystemTypeOptions = (settings?.systemTypes ?? []).filter((systemType) =>
    roofComponentSystemTypeIds.has(systemType.id),
  );
  const selectedRoofComponentSystemType =
    roofComponentSystemTypeOptions.find((systemType) => systemType.id === effectiveRoofComponentSystemTypeId) ??
    roofComponentSystemTypeOptions[0] ??
    null;
  const roofShuttleOptions = filterShuttles(
    settings?.shuttles ?? [],
    effectiveRoofFixed,
    selectedRoofComponentSystemType,
    selectedRoofSystem,
    selectedRoofComponentMaterial,
  );
  const selectedRoofShuttle =
    roofShuttleOptions.find((shuttle) => shuttle.id === effectiveRoofShuttleId) ?? roofShuttleOptions[0] ?? null;
  const selectedRoofShuttlePictureLabel =
    selectedRoofShuttle?.item.name?.trim() || selectedRoofShuttle?.item.code?.trim() || "Shuttle";
  const selectedRoofShuttleImageUrl = selectedRoofShuttle?.item.image
    ? `${import.meta.env.VITE_BUCKET_URL}${selectedRoofShuttle.item.image}?${getMinutesRoundedTimestamp()}`
    : null;
  const shouldShowRoofShuttleImage =
    Boolean(selectedRoofShuttleImageUrl) && failedRoofShuttleImageUrl !== selectedRoofShuttleImageUrl;
  useEffect(() => {
    setFailedRoofShuttleImageUrl(null);
  }, [selectedRoofShuttle?.item.image]);
  const selectedRoofShuttleMaxUsers =
    selectedRoofComponentAbsorber?.systems.find((system) => system.systemId === selectedRoofSystem?.id)?.maxUsers ?? 0;
  const parsedRoofNrOfShuttlesValue = Number(effectiveRoofNrOfShuttles.replace(",", "."));
  const parsedEffectiveRoofNrOfShuttles = Number.isFinite(parsedRoofNrOfShuttlesValue)
    ? parsedRoofNrOfShuttlesValue
    : selectedRoofShuttleMaxUsers || null;
  const isRoofShuttleQuantityOverMax =
    effectiveRoofFixed &&
    selectedRoofShuttleMaxUsers > 0 &&
    parsedEffectiveRoofNrOfShuttles != null &&
    parsedEffectiveRoofNrOfShuttles > selectedRoofShuttleMaxUsers;
  const labelLanguageCode = (configuration.language || "xx").toUpperCase();
  const patrolLabelCode = `TARGA${labelLanguageCode}`;
  const lifeLineLabelCode = `TARGAHOR${labelLanguageCode}`;
  const roofComponentTerminals =
    selectedRoofComponentMaterial?.terminals?.filter((terminal) =>
      terminal.systemIds.some((systemId) => systemId === selectedRoofSystem?.id),
    ) ?? [];
  const roofComponentTerminalDisplayItems =
    roofComponentTerminals.length > 0 ? roofComponentTerminals : (selectedRoofComponentMaterial?.terminals ?? []);
  const roofComponentCurveIntermediates = (settings?.curveIntermediates ?? []).filter(
    (curveIntermediate) =>
      Boolean(selectedRoofComponentMaterial?.id) &&
      curveIntermediate.materialsIds.includes(selectedRoofComponentMaterial?.id ?? "") &&
      Boolean(selectedRoofComponentSystemType?.id) &&
      curveIntermediate.systemTypesIds.includes(selectedRoofComponentSystemType?.id ?? "") &&
      Boolean(selectedRoofSystem?.id) &&
      curveIntermediate.systemsIds.includes(selectedRoofSystem?.id ?? ""),
  );
  const roofComponentIntermediates = roofComponentCurveIntermediates.filter(
    (curveIntermediate) => curveIntermediate.minAngle >= 165,
  );
  const roofComponentCurves = roofComponentCurveIntermediates.filter(
    (curveIntermediate) => curveIntermediate.maxAngle < 165,
  );
  const roofComponentFallbackCurveIntermediates = (settings?.curveIntermediates ?? []).filter(
    (curveIntermediate) =>
      Boolean(selectedRoofComponentMaterial?.id) &&
      curveIntermediate.materialsIds.includes(selectedRoofComponentMaterial?.id ?? "") &&
      Boolean(selectedRoofSystem?.id) &&
      curveIntermediate.systemsIds.includes(selectedRoofSystem?.id ?? ""),
  );
  const roofComponentIntermediateDisplayItems =
    roofComponentIntermediates.length > 0
      ? roofComponentIntermediates
      : roofComponentFallbackCurveIntermediates.filter((curveIntermediate) => curveIntermediate.minAngle >= 165);
  const roofComponentCurveDisplayItems =
    roofComponentCurves.length > 0
      ? roofComponentCurves
      : roofComponentFallbackCurveIntermediates.filter((curveIntermediate) => curveIntermediate.maxAngle < 165);
  const lifeLineSeamsSpanRequired = selectedLifeLineSystemMappings.some((mapping) => mapping.seamsSpanRequired);
  const anchorPointSeamsSpanRequired = selectedAnchorPointSystemMappings.some((mapping) => mapping.seamsSpanRequired);
  const shouldUseLifeLineSeamsSpanInput = isTClampsSystem(selectedLifeLineDefaultSystem);
  const shouldUseAnchorPointSeamsSpanInput = isTClampsSystem(selectedAnchorPointDefaultSystem);
  const shouldUseSelectedRoofSeamsSpanInput = isTClampsSystem(selectedRoofSystem);
  const isRoofSubstructureSelectionRequired = roofSubstructureOptions.length > 0;
  const areRoofSubstructureBaseSelectionsComplete =
    Boolean(commonSelectedRoofMaterialId) &&
    (!isRoofSubstructureSelectionRequired || Boolean(commonSelectedRoofSubstructureId));
  const areRoofSubstructureStarterSelectionsComplete =
    areRoofSubstructureBaseSelectionsComplete &&
    (!shouldShowRoofSheetMaterials || Boolean(commonSelectedRoofSheetMaterialId));
  const selectedRequiredRoofSystemMappings = areRoofSubstructureStarterSelectionsComplete
    ? selectedCurrentRoofSystemMappings
    : [];
  const shouldShowSubstructureHeight = selectedRequiredRoofSystemMappings.some(
    (mapping) => mapping.minSubstructureHeight != null,
  );
  const shouldShowSubstructureSize = selectedRequiredRoofSystemMappings.some(
    (mapping) => mapping.minSubstructureSize != null,
  );
  const shouldShowMetalPlateThickness = selectedRequiredRoofSystemMappings.some(
    (mapping) => mapping.minMetalPlateThickness != null,
  );
  const shouldShowSeamsSpan = selectedRequiredRoofSystemMappings.some((mapping) => mapping.seamsSpanRequired);
  const shouldShowRoofInsulationThickness =
    areRoofSubstructureStarterSelectionsComplete && !!selectedRoofSystem?.insulationThicknessRequired;
  const shouldShowRoofWaveSpan =
    areRoofSubstructureStarterSelectionsComplete &&
    !!selectedRoofSystem?.waveSpanRequired &&
    !commonSelectedRoofUseOnlyAnchorPoints;
  const shouldShowRoofSubstructureSelection =
    Boolean(commonSelectedRoofMaterialId) && isRoofSubstructureSelectionRequired;
  const shouldShowRoofSheetMaterialSelection =
    areRoofSubstructureBaseSelectionsComplete && shouldShowRoofSheetMaterials;
  const shouldShowRoofHipsSpan = areRoofSubstructureBaseSelectionsComplete && shouldShowRoofSheetMaterials;
  const isFilledRoofValue = (value: string) => value.trim() !== "";
  const isSelectedRoofSubstructureComplete =
    Boolean(commonSelectedRoofGroup) &&
    isFilledRoofValue(commonSelectedRoofMaterialId) &&
    (!shouldShowRoofSheetMaterialSelection || isFilledRoofValue(commonSelectedRoofSheetMaterialId)) &&
    (!shouldShowRoofSubstructureSelection || isFilledRoofValue(commonSelectedRoofSubstructureId)) &&
    (!shouldShowSubstructureSize ||
      (isFilledRoofValue(commonSelectedRoofSubstructureSizeHeight) &&
        isFilledRoofValue(commonSelectedRoofSubstructureSizeWidth))) &&
    (!shouldShowSubstructureHeight || isFilledRoofValue(commonSelectedRoofSubstructureHeight)) &&
    (!shouldShowMetalPlateThickness || isFilledRoofValue(commonSelectedRoofMetalPlateThickness)) &&
    (!shouldShowSeamsSpan || isFilledRoofValue(effectiveRoofSeamsSpan)) &&
    (!shouldShowRoofInsulationThickness || isFilledRoofValue(effectiveRoofInsulationThickness)) &&
    (!shouldShowRoofWaveSpan || isFilledRoofValue(commonSelectedRoofWaveSpan)) &&
    (!shouldShowRoofHipsSpan || isFilledRoofValue(commonSelectedRoofHipsSpan));
  const selectedRoofAutoNavigationKey = commonSelectedRoofGroup
    ? `${commonSelectedRoofGroup.id}:${[...selectedIds].sort().join("|")}`
    : "";
  useEffect(() => {
    if (!commonSelectedRoofGroup || !selectedRoofAutoNavigationKey) {
      lastAutoOpenedRoofSelectionKeyRef.current = null;
      return;
    }

    if (lastAutoOpenedRoofSelectionKeyRef.current === selectedRoofAutoNavigationKey) return;

    lastAutoOpenedRoofSelectionKeyRef.current = selectedRoofAutoNavigationKey;
    setIsEntityPropertiesOpen(true);
    setActiveConfigurationPage("roofProperty");
    setActiveRoofPropertyTab(isSelectedRoofSubstructureComplete ? "roofInfo" : "substructure");
  }, [commonSelectedRoofGroup?.id, isSelectedRoofSubstructureComplete, selectedRoofAutoNavigationKey]);
  const selectedPropertiesAutoNavigationKey = selectedEntities.length > 0 ? [...selectedIds].sort().join("|") : "";
  useEffect(() => {
    if (!selectedPropertiesAutoNavigationKey) return;
    if (selectedIsRoofSelection || selectedIsLifeLineSelection || selectedIsAnchorPointSelection) return;

    setIsPropertiesOpen(true);
    bringFloatingPanelToFront("propertiesPanel");
  }, [
    selectedIsAnchorPointSelection,
    selectedIsLifeLineSelection,
    selectedIsRoofSelection,
    selectedPropertiesAutoNavigationKey,
  ]);
  const parseStoredRoofNumber = (value: string) => {
    const parsed = Number(value.replace(",", "."));
    return Number.isFinite(parsed) ? parsed : null;
  };
  const commonSelectedRoofSeamsSpanNumber = parseStoredRoofNumber(effectiveRoofSeamsSpan);
  const lifeLineSeamsSpanNumber = lifeLineSeamsSpanRequired ? commonSelectedRoofSeamsSpanNumber : null;
  const anchorPointSeamsSpanNumber = anchorPointSeamsSpanRequired ? commonSelectedRoofSeamsSpanNumber : null;
  const commonSelectedRoofInsulationThicknessNumber = parseStoredRoofNumber(effectiveRoofInsulationThickness);
  const baseFilteredLifeLineDefaultSupports =
    selectedLifeLineDefaultSystem?.supports?.filter((support) => {
      if (support.maxSeamsSpan && lifeLineSeamsSpanNumber && lifeLineSeamsSpanNumber > support.maxSeamsSpan) {
        return false;
      }
      if (
        selectedLifeLineNormative?.excludedItems?.map((excludedItem) => excludedItem.item.id).includes(support.item.id)
      ) {
        return false;
      }
      if (
        support.maxInsulationThickness &&
        commonSelectedRoofInsulationThicknessNumber &&
        commonSelectedRoofInsulationThicknessNumber > support.maxInsulationThickness
      ) {
        return false;
      }
      if (selectedRoofType?.roofCategory.isFlat && !support.flatRoofCompatible) {
        return false;
      }
      return true;
    }) ?? [];
  const lifeLineSupportMaterialOptions = Array.from(
    new Map(
      baseFilteredLifeLineDefaultSupports.flatMap((support) =>
        support.supportMaterial ? ([[support.supportMaterial.id, support.supportMaterial]] as const) : [],
      ),
    ).values(),
  );
  const effectiveLifeLineSupportMaterialId = lifeLineSupportMaterialOptions.some(
    (material) => material.id === effectiveSupportMaterialId,
  )
    ? effectiveSupportMaterialId
    : (lifeLineSupportMaterialOptions[0]?.id ?? "");
  const selectedLifeLineSupportMaterial = lifeLineSupportMaterialOptions.find(
    (material) => material.id === effectiveLifeLineSupportMaterialId,
  );
  const filteredLifeLineDefaultSupports = baseFilteredLifeLineDefaultSupports.filter(
    (support) =>
      !effectiveLifeLineSupportMaterialId ||
      !support.supportMaterial ||
      support.supportMaterial.id === effectiveLifeLineSupportMaterialId,
  );
  const lifeLineTerminalCurveSupportOptions = filteredLifeLineDefaultSupports.filter(
    (support) => support.forTerminalsAndCurves,
  );
  const lifeLineIntermediateSupportOptions = filteredLifeLineDefaultSupports.filter(
    (support) => support.forIntermediates,
  );
  const selectedEffectiveTerminalCurveSupport =
    lifeLineTerminalCurveSupportOptions.find((support) => support.item.id === effectiveTerminalCurveSupportId) ??
    baseFilteredLifeLineDefaultSupports.find((support) => support.item.id === effectiveTerminalCurveSupportId) ??
    null;
  const selectedEffectiveIntermediateSupport =
    lifeLineIntermediateSupportOptions.find((support) => support.item.id === effectiveIntermediateSupportId) ??
    baseFilteredLifeLineDefaultSupports.find((support) => support.item.id === effectiveIntermediateSupportId) ??
    null;
  const effectiveTerminalCurveSupportCode =
    selectedEffectiveTerminalCurveSupport?.item.code || commonSelectedRoofTerminalCurveSupportCode;
  const effectiveIntermediateSupportCode =
    selectedEffectiveIntermediateSupport?.item.code || commonSelectedRoofIntermediateSupportCode;
  const getAnchorPointMappingMaterialLabel = (
    mapping: {
      materialId?: string;
      material?: { name?: string | null };
      supportMaterial?: { name?: string | null };
    } | null,
  ) => {
    if (!mapping) return "";
    return (
      mapping.material?.name ||
      mapping.supportMaterial?.name ||
      settings?.materials.find((material) => material.id === mapping.materialId)?.name ||
      mapping.materialId ||
      ""
    );
  };
  const anchorPointBaseCodeOptions = (settings?.anchorMappings ?? []).filter((mapping) => {
    if (mapping.systemId !== selectedAnchorPointSystem) return false;
    if (mapping.roofSubstructureId && mapping.roofSubstructureId !== selectedRoofSubstructure?.id) return false;
    if (
      mapping.minSubstructureHeight &&
      commonSelectedRoofSubstructureHeight &&
      Number(commonSelectedRoofSubstructureHeight) < mapping.minSubstructureHeight
    ) {
      return false;
    }
    if (
      mapping.maxSeamsSpan != null &&
      anchorPointSeamsSpanNumber != null &&
      anchorPointSeamsSpanNumber > mapping.maxSeamsSpan
    ) {
      return false;
    }
    if (
      mapping.maxInsulationThickness != null &&
      commonSelectedRoofInsulationThicknessNumber != null &&
      commonSelectedRoofInsulationThicknessNumber > mapping.maxInsulationThickness
    ) {
      return false;
    }
    if (
      selectedAnchorPointNormative?.excludedItems?.map((excludedItem) => excludedItem.item.id).includes(mapping.item.id)
    ) {
      return false;
    }
    return true;
  });
  const anchorPointMaterialOptions = Array.from(
    new Map(
      anchorPointBaseCodeOptions.flatMap((mapping) =>
        mapping.materialId
          ? ([
              [mapping.materialId, { id: mapping.materialId, name: getAnchorPointMappingMaterialLabel(mapping) }],
            ] as const)
          : [],
      ),
    ).values(),
  );
  const resolvedAnchorPointMaterialId = anchorPointMaterialOptions.some(
    (material) => material.id === effectiveAnchorPointMaterialId,
  )
    ? effectiveAnchorPointMaterialId
    : (anchorPointMaterialOptions[0]?.id ?? "");
  const selectedAnchorPointMaterial = anchorPointMaterialOptions.find(
    (material) => material.id === resolvedAnchorPointMaterialId,
  );
  const anchorPointDefaultCodeOptions = anchorPointBaseCodeOptions.filter(
    (mapping) =>
      !resolvedAnchorPointMaterialId || !mapping.materialId || mapping.materialId === resolvedAnchorPointMaterialId,
  );
  const selectedEffectiveAnchorPointMapping =
    anchorPointDefaultCodeOptions.find((mapping) => mapping.id === effectiveAnchorPointMappingId) ?? null;
  const effectiveAnchorPointCode = selectedEffectiveAnchorPointMapping?.item.code || commonSelectedRoofAnchorPointCode;
  const effectiveAnchorPointMaterialLabel =
    selectedAnchorPointMaterial?.name || getAnchorPointMappingMaterialLabel(selectedEffectiveAnchorPointMapping);
  const getCompatibleSupportExtras = (
    system: typeof selectedLifeLineDefaultSystem,
    kind: "lifeline" | "anchorPoint",
  ) => {
    const substructureHeight = parseStoredRoofNumber(commonSelectedRoofSubstructureHeight) ?? 0;
    const insulationThickness = commonSelectedRoofInsulationThicknessNumber ?? 0;
    const totalThickness = substructureHeight + insulationThickness;

    return (
      system?.supportExtras?.filter((extra) => {
        if (kind === "lifeline" && !extra.forLifeLines) return false;
        if (kind === "anchorPoint" && !extra.forAnchorPoints) return false;
        if (
          extra.roofSubstructure &&
          selectedRoofSubstructure &&
          extra.roofSubstructure.id !== selectedRoofSubstructure.id
        ) {
          return false;
        }
        if (extra.minThickness != null && totalThickness < extra.minThickness) return false;
        if (extra.maxThickness != null && totalThickness > extra.maxThickness) return false;
        return true;
      }) ?? []
    );
  };
  const anchorPointAdditionalProducts = getCompatibleSupportExtras(selectedAnchorPointDefaultSystem, "anchorPoint");
  const lifeLineAdditionalProducts = getCompatibleSupportExtras(selectedLifeLineDefaultSystem, "lifeline");
  const getCompatibleSupportOptionals = (
    system: typeof selectedLifeLineDefaultSystem,
    extras: ReturnType<typeof getCompatibleSupportExtras>,
    kind: "lifeline" | "anchorPoint",
  ) => {
    const extraIds = new Set(extras.map((extra) => extra.id));
    return (
      system?.supportOptionals?.filter((optional) => {
        if (kind === "lifeline" && !optional.forLifeLines) return false;
        if (kind === "anchorPoint" && !optional.forAnchorPoints) return false;
        return !optional.supportExtraDependency || extraIds.has(optional.supportExtraDependency.id);
      }) ?? []
    );
  };
  const anchorPointOptionalProducts = getCompatibleSupportOptionals(
    selectedAnchorPointDefaultSystem,
    anchorPointAdditionalProducts,
    "anchorPoint",
  );
  const displayedAnchorPointProductCodes = [
    selectedAnchorPointShape?.anchorPointAdditionalProductCodes,
    selectedAnchorPointShape?.anchorPointOptionalProductCodes,
  ]
    .filter(Boolean)
    .join(" / ");
  const effectiveAnchorPointProductCodes =
    displayedAnchorPointProductCodes ||
    [...anchorPointAdditionalProducts, ...anchorPointOptionalProducts]
      .map((product) => product.item.code)
      .filter(Boolean)
      .join(" / ");
  const lifeLineOptionalProducts = getCompatibleSupportOptionals(
    selectedLifeLineDefaultSystem,
    lifeLineAdditionalProducts,
    "lifeline",
  );
  const lifeLineInsulationThicknessRequired = !!selectedLifeLineDefaultSystem?.insulationThicknessRequired;
  const anchorPointInsulationThicknessRequired = !!selectedAnchorPointDefaultSystem?.insulationThicknessRequired;
  const shouldShowLifeLineTerminalCurveSupportSelect = !!selectedLifeLineDefaultSystem?.supportRequired;
  const shouldShowLifeLineIntermediateSupportSelect = !!selectedLifeLineDefaultSystem?.supportRequired;
  const shouldShowAnchorPointMappingSelect =
    !!selectedAnchorPointDefaultSystem && anchorPointDefaultCodeOptions.length !== 1;
  const singleSelectedRoofLine =
    singleSelectedShape?.layer === ROOF_LAYER &&
    singleSelectedShape.kind !== "circle" &&
    singleSelectedShape.kind !== "arc"
      ? singleSelectedShape
      : null;

  useEffect(() => {
    latestHistorySnapshotRef.current = captureHistorySnapshot();

    if (isRestoringHistoryRef.current) {
      isRestoringHistoryRef.current = false;
    }
  }, [polylines, dimensions, constructionLines, textEntities, iconEntities, backgroundAsset, backgroundTransparency]);

  useEffect(() => {
    const action = historyActionRef.current;
    const currentSnapshot = latestHistorySnapshotRef.current;
    if (!action?.finalizeRequested || !currentSnapshot || isRestoringHistoryRef.current) {
      return;
    }

    if (!areHistorySnapshotsEqual(action.before, currentSnapshot)) {
      setUndoStack((current) => [...current, cloneHistorySnapshot(action.before)].slice(-MAX_UNDO_HISTORY));
      setRedoStack([]);
    }

    historyActionRef.current = null;
  }, [
    polylines,
    dimensions,
    constructionLines,
    textEntities,
    iconEntities,
    backgroundAsset,
    backgroundTransparency,
    historyRevision,
  ]);

  useEffect(() => {
    const pendingIds = pendingCreatedRoofSelectionIdsRef.current;
    if (!pendingIds || pendingIds.length === 0) return;

    const existingIds = pendingIds.filter((id) => polylines.some((shape) => shape.id === id));
    if (existingIds.length === 0) return;

    const existingCreatedRoofShapes = polylines.filter((shape) => existingIds.includes(shape.id));
    const createdRoofGroupId = existingCreatedRoofShapes[0]?.roofGroupId ?? existingIds[0];

    pendingCreatedRoofSelectionIdsRef.current = null;
    setSelectedIds(existingIds);
    setSelectionCycle(null);
    setEditingId(null);
    setIsEntityPropertiesOpen(true);
    setActiveConfigurationPage("roofProperty");
    setActiveRoofPropertyTab("substructure");
    lastAutoOpenedRoofSelectionKeyRef.current = `${createdRoofGroupId}:${[...existingIds].sort().join("|")}`;
  }, [polylines]);

  useEffect(() => {
    const element = viewportRef.current;
    if (!element) return;

    const updateSize = () => {
      setViewportSize({
        width: Math.max(320, Math.floor(element.clientWidth)),
        height: Math.max(360, Math.floor(element.clientHeight)),
      });
    };

    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (hasInitializedViewportRef.current) return;
    if (viewportSize.height <= 0) return;

    hasInitializedViewportRef.current = true;
    setViewport({
      x: 0,
      y: 0,
      scale: viewportSize.height / DEFAULT_VIEW_HEIGHT,
    });
  }, [viewportSize.height]);

  useEffect(() => {
    GlobalWorkerOptions.workerSrc = pdfWorkerUrl;
  }, []);

  useEffect(() => {
    if (!configuration.project?.id) return;

    const request = isAdmin ? adminProjectRequest : userProjectRequest;
    request.trigger(configuration.project?.id).catch((error) => {
      console.error("Could not load project details for Google Map background.", error);
    });
  }, [adminProjectRequest.trigger, configuration.project?.id, isAdmin, userProjectRequest.trigger]);

  useEffect(() => () => clearPendingToolGroupClose(), []);

  useEffect(() => {
    activePolylineRef.current = activePolyline;
  }, [activePolyline]);

  useEffect(() => {
    if (!isBackgroundToolbarOpen || !backgroundToolbarRef.current) {
      return;
    }

    setBackgroundToolbarWidth(backgroundToolbarRef.current.offsetWidth);
  }, [
    isBackgroundToolbarOpen,
    backgroundAsset,
    backgroundTransparency,
    isBackgroundReferenceMode,
    isBackgroundCropMode,
    backgroundAlignmentAngleDraft,
  ]);

  const focusNumericField = (target: "value" | "direction" | "lineDistance") => {
    setActiveNumericInput(target);
    setOverwriteNumericInput(target);

    if (typeof window === "undefined") return;

    window.requestAnimationFrame(() => {
      if (target === "direction") {
        directionInputRef.current?.focus();
        directionInputRef.current?.select();
        return;
      }

      if (target === "lineDistance") {
        lineDistanceInputRef.current?.focus();
        lineDistanceInputRef.current?.select();
        return;
      }

      numericInputRef.current?.focus();
      numericInputRef.current?.select();
    });
  };

  useEffect(() => {
    if (!numericEdit) return;
    if (draggedGrip) return;
    if (numericEditSupportsDirection(numericEdit) && activeNumericInput === "direction") {
      directionInputRef.current?.focus();
      directionInputRef.current?.select();
      return;
    }

    if (numericEdit.kind === "point-series" && activeNumericInput === "lineDistance") {
      lineDistanceInputRef.current?.focus();
      lineDistanceInputRef.current?.select();
      return;
    }

    numericInputRef.current?.focus();
    numericInputRef.current?.select();
  }, [activeNumericInput, numericEdit?.kind, draggedGrip, draggedSegmentStretch]);

  useEffect(() => {
    if (draggedGrip || draggedSegmentStretch) return;
    focusNumericField("value");
  }, [numericEdit?.kind, draggedGrip, draggedSegmentStretch]);

  useEffect(() => {
    directionLockAxisRef.current = directionLockAxis;
  }, [directionLockAxis]);

  const clearDirectionLock = () => {
    directionLockAxisRef.current = null;
    setDirectionLockAxis(null);
  };

  useEffect(() => {
    if (directionLockAxis && numericEditSupportsDirection(numericEdit)) {
      focusNumericField("value");
    }
  }, [directionLockAxis, numericEdit]);

  useEffect(() => {
    if (!backgroundReferenceInput) return;
    referenceLengthInputRef.current?.focus();
    referenceLengthInputRef.current?.select();
  }, [Boolean(backgroundReferenceInput)]);

  useEffect(() => {
    if (!backgroundAsset?.reference) {
      setBackgroundAlignmentAngleDraft("0");
      return;
    }

    const referenceWorld = getBackgroundReferenceWorldPoints(backgroundAsset);
    if (!referenceWorld) return;

    const angleDeg =
      (Math.atan2(referenceWorld.end.y - referenceWorld.start.y, referenceWorld.end.x - referenceWorld.start.x) * 180) /
      Math.PI;

    setBackgroundAlignmentAngleDraft(angleDeg.toFixed(2));
  }, [
    backgroundAsset?.reference?.startLocal.x,
    backgroundAsset?.reference?.startLocal.y,
    backgroundAsset?.reference?.endLocal.x,
    backgroundAsset?.reference?.endLocal.y,
    backgroundAsset?.rotation,
  ]);

  useEffect(() => {
    setGridSpacingDraft(formatMeasurementValue(gridSpacing));
  }, [gridSpacing, drawingUnit]);

  useEffect(() => {
    setGridSnapStepDraft(formatMeasurementValue(gridSnapStep));
  }, [gridSnapStep, drawingUnit]);

  useEffect(() => {
    const selectedSystem = patrolLifeLineSystems.find((system) => system.id === selectedSafetySystem);
    const systemSettings = safetySystemSettings[selectedSafetySystem] ?? getDefaultSafetySystemSettings(selectedSystem);
    setEntityTypeStyles((current) => ({
      ...current,
      polyline: {
        ...current.polyline,
        strokeWidth: systemSettings.strokeWidth,
        lineType: systemSettings.lineType,
        vertexShape: systemSettings.vertexShape,
      },
    }));
  }, [patrolLifeLineSystems, selectedSafetySystem, safetySystemSettings]);

  useEffect(() => {
    const selectedSystem = patrolAnchorPointSystems.find((system) => system.id === selectedAnchorPointSystem);
    const systemSettings =
      safetySystemSettings[selectedAnchorPointSystem] ?? getDefaultSafetySystemSettings(selectedSystem);
    setEntityTypeStyles((current) => ({
      ...current,
      "single-anchor-point": {
        ...current["single-anchor-point"],
        strokeWidth: systemSettings.strokeWidth,
        lineType: systemSettings.lineType,
        vertexShape: systemSettings.vertexShape,
      },
    }));
  }, [patrolAnchorPointSystems, safetySystemSettings, selectedAnchorPointSystem]);

  useEffect(() => {
    setDimensionTextHeightDraft(String(dimensionTextHeight));
  }, [dimensionTextHeight]);

  useEffect(() => {
    setDimensionTextGapDraft(String(dimensionTextGap));
  }, [dimensionTextGap]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const rawSettings = window.localStorage.getItem(SETTINGS_STORAGE_KEY);
    if (!rawSettings) return;

    try {
      const parsed = JSON.parse(rawSettings) as Partial<{
        version: number;
        drawingUnit: UnitOption;
        decimalSeparator: DecimalSeparator;
        gridSpacing: number;
        isGridSnapEnabled: boolean;
        gridSnapStep: number;
        enabledOsnaps: Partial<OsnapSettings>;
        entityLineWidth: number;
        previewLineWidth: number;
        dimensionTextHeight: number;
        dimensionTextGap: number;
        dimensionMarkerStyle: DimensionMarkerStyle;
        selectedSafetySystem: string;
        safetySystemSettings: Record<string, unknown>;
        keyboardShortcuts: Partial<Record<ShortcutCommand, string>>;
        entityTypeStyles: Partial<Record<EntityStyleKind, Partial<EntityTypeStyle>>>;
        visibleLayers: Partial<Record<EntityLayerName, boolean>>;
        lockedLayers: Partial<Record<EntityLayerName, boolean>>;
        layerColors: Partial<Record<EntityLayerName, string>>;
        layerOrder: unknown;
      }>;
      const isLegacySettings = !parsed.version || parsed.version < SETTINGS_STORAGE_VERSION;

      if (parsed.drawingUnit && parsed.drawingUnit in UNIT_FACTORS) {
        setDrawingUnit(parsed.drawingUnit);
      }

      if (parsed.decimalSeparator === "comma" || parsed.decimalSeparator === "point") {
        setDecimalSeparator(parsed.decimalSeparator);
      }

      if (isLegacySettings) {
        setGridSpacing(DEFAULT_GRID_SPACING);
        setGridSnapStep(DEFAULT_GRID_SNAP_STEP);
      } else {
        if (typeof parsed.gridSpacing === "number" && Number.isFinite(parsed.gridSpacing)) {
          setGridSpacing(Math.max(1, parsed.gridSpacing));
        }

        if (typeof parsed.gridSnapStep === "number" && Number.isFinite(parsed.gridSnapStep)) {
          setGridSnapStep(Math.max(0.001, parsed.gridSnapStep));
        }
      }

      if (typeof parsed.isGridSnapEnabled === "boolean") {
        setIsGridSnapEnabled(parsed.isGridSnapEnabled);
      }

      if (parsed.enabledOsnaps) {
        setEnabledOsnaps((current) => ({
          ...current,
          ...parsed.enabledOsnaps,
        }));
      }

      if (typeof parsed.entityLineWidth === "number" && Number.isFinite(parsed.entityLineWidth)) {
        setEntityLineWidth(parsed.entityLineWidth);
      }

      if (isLegacySettings) {
        setPreviewLineWidth(DEFAULT_PREVIEW_LINE_WIDTH);
      } else if (typeof parsed.previewLineWidth === "number" && Number.isFinite(parsed.previewLineWidth)) {
        setPreviewLineWidth(parsed.previewLineWidth);
      }

      if (typeof parsed.dimensionTextHeight === "number" && Number.isFinite(parsed.dimensionTextHeight)) {
        setDimensionTextHeight(parsed.dimensionTextHeight);
      }

      if (typeof parsed.dimensionTextGap === "number" && Number.isFinite(parsed.dimensionTextGap)) {
        setDimensionTextGap(
          Math.min(MAX_DIMENSION_TEXT_GAP, Math.max(MIN_DIMENSION_TEXT_GAP, parsed.dimensionTextGap)),
        );
      }

      if (
        parsed.dimensionMarkerStyle === "filled-arrow" ||
        parsed.dimensionMarkerStyle === "slash" ||
        parsed.dimensionMarkerStyle === "dot"
      ) {
        setDimensionMarkerStyle(parsed.dimensionMarkerStyle);
      }

      if (typeof parsed.selectedSafetySystem === "string") {
        storedSelectedSafetySystemRef.current = parsed.selectedSafetySystem;
        setSelectedSafetySystem(parsed.selectedSafetySystem);
      }

      if (parsed.safetySystemSettings && isRecord(parsed.safetySystemSettings)) {
        setSafetySystemSettings((current) => {
          const nextSettings = { ...current };
          for (const [system, storedSettings] of Object.entries(parsed.safetySystemSettings ?? {})) {
            nextSettings[system] = normalizeStoredSafetySystemSettings(
              storedSettings,
              current[system] ?? PATROL_DEFAULT_SAFETY_SYSTEM_SETTINGS,
            );
          }
          return nextSettings;
        });
      }

      if (parsed.keyboardShortcuts && isRecord(parsed.keyboardShortcuts)) {
        setKeyboardShortcuts((current) => {
          const nextShortcuts = { ...current };
          for (const { command } of SHORTCUT_COMMAND_OPTIONS) {
            const shortcut = parsed.keyboardShortcuts?.[command];
            if (typeof shortcut === "string") {
              nextShortcuts[command] = shortcut;
            }
          }
          return nextShortcuts;
        });
      }

      if (parsed.entityTypeStyles) {
        const parsedEntityTypeStyles = parsed.entityTypeStyles;
        setEntityTypeStyles((current) => {
          const nextStyles = { ...current };
          for (const kind of Object.keys(parsedEntityTypeStyles) as EntityStyleKind[]) {
            const parsedStyle = parsedEntityTypeStyles[kind];
            if (!parsedStyle || !current[kind]) continue;

            nextStyles[kind] = {
              strokeColor:
                isLegacySettings && (ROOF_SURFACE_STYLE_KINDS.has(kind) || kind === "text")
                  ? getCadColorValue("15")
                  : typeof parsedStyle.strokeColor === "string"
                    ? parsedStyle.strokeColor
                    : current[kind].strokeColor,
              strokeWidth:
                typeof parsedStyle.strokeWidth === "number" && Number.isFinite(parsedStyle.strokeWidth)
                  ? parsedStyle.strokeWidth
                  : current[kind].strokeWidth,
              lineType: isLineType(parsedStyle.lineType) ? parsedStyle.lineType : current[kind].lineType,
              vertexColor:
                typeof parsedStyle.vertexColor === "string" ? parsedStyle.vertexColor : current[kind].vertexColor,
              vertexSize:
                typeof parsedStyle.vertexSize === "number" && Number.isFinite(parsedStyle.vertexSize)
                  ? parsedStyle.vertexSize
                  : current[kind].vertexSize,
              vertexShape: isVertexShape(parsedStyle.vertexShape) ? parsedStyle.vertexShape : current[kind].vertexShape,
              gripColor: typeof parsedStyle.gripColor === "string" ? parsedStyle.gripColor : current[kind].gripColor,
              gripSize:
                typeof parsedStyle.gripSize === "number" && Number.isFinite(parsedStyle.gripSize)
                  ? parsedStyle.gripSize
                  : current[kind].gripSize,
              labelText: typeof parsedStyle.labelText === "string" ? parsedStyle.labelText : current[kind].labelText,
              labelHeight:
                typeof parsedStyle.labelHeight === "number" && Number.isFinite(parsedStyle.labelHeight)
                  ? Math.max(1, parsedStyle.labelHeight)
                  : current[kind].labelHeight,
              labelPosition: isRoofComponentLabelPosition(parsedStyle.labelPosition)
                ? parsedStyle.labelPosition
                : current[kind].labelPosition,
            };
          }
          return nextStyles;
        });
      }

      if (parsed.visibleLayers) {
        setVisibleLayers((current) => ({
          ...current,
          ...parsed.visibleLayers,
        }));
      }

      if (parsed.lockedLayers) {
        setLockedLayers((current) => ({
          ...current,
          ...parsed.lockedLayers,
        }));
      }

      if (parsed.layerColors) {
        setLayerColors((current) => ({
          ...current,
          ...parsed.layerColors,
        }));
      }

      if (parsed.layerOrder) {
        setLayerOrder(normalizeLayerOrder(parsed.layerOrder));
      }
    } catch {
      window.localStorage.removeItem(SETTINGS_STORAGE_KEY);
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;

    window.localStorage.setItem(
      SETTINGS_STORAGE_KEY,
      JSON.stringify({
        version: SETTINGS_STORAGE_VERSION,
        drawingUnit,
        decimalSeparator,
        gridSpacing,
        isGridSnapEnabled,
        gridSnapStep,
        enabledOsnaps,
        entityLineWidth,
        previewLineWidth,
        dimensionTextHeight,
        dimensionTextGap,
        dimensionMarkerStyle,
        selectedSafetySystem,
        safetySystemSettings,
        keyboardShortcuts,
        entityTypeStyles,
        visibleLayers,
        lockedLayers,
        layerColors,
        layerOrder,
      }),
    );
  }, [
    drawingUnit,
    decimalSeparator,
    gridSpacing,
    isGridSnapEnabled,
    gridSnapStep,
    enabledOsnaps,
    entityLineWidth,
    previewLineWidth,
    dimensionTextHeight,
    dimensionTextGap,
    dimensionMarkerStyle,
    selectedSafetySystem,
    safetySystemSettings,
    keyboardShortcuts,
    entityTypeStyles,
    visibleLayers,
    lockedLayers,
    layerColors,
    layerOrder,
  ]);

  useEffect(() => {
    if (!gripContextMenu || typeof window === "undefined") return;

    const handlePointerDown = (event: MouseEvent) => {
      if (gripContextMenuRef.current?.contains(event.target as Node)) {
        return;
      }

      setGripContextMenu(null);
    };

    window.addEventListener("mousedown", handlePointerDown);
    return () => window.removeEventListener("mousedown", handlePointerDown);
  }, [gripContextMenu]);

  const deleteSelected = () => {
    const deletableIds = selectedIds.filter((id) => id !== BACKGROUND_ID);
    if (deletableIds.length === 0) {
      setSelectedIds([]);
      return;
    }
    const deletableShapeLabelNames = new Set(
      polylines.filter((shape) => deletableIds.includes(shape.id)).map((shape) => `${shape.name} letter`),
    );
    const deletableRoofGroupIds = new Set(
      polylines
        .filter((shape) => deletableIds.includes(shape.id) && shape.roofGroupId)
        .map((shape) => shape.roofGroupId as string),
    );

    runHistoryAction(() => {
      setPolylines((current) => current.filter((polyline) => !deletableIds.includes(polyline.id)));
      setDimensions((current) => current.filter((dimension) => !deletableIds.includes(dimension.id)));
      setConstructionLines((current) => current.filter((line) => !deletableIds.includes(line.id)));
      setTextEntities((current) =>
        current.filter(
          (textEntity) =>
            !deletableIds.includes(textEntity.id) &&
            !deletableShapeLabelNames.has(textEntity.name) &&
            !(textEntity.roofGroupId && deletableRoofGroupIds.has(textEntity.roofGroupId)),
        ),
      );
      setIconEntities((current) => current.filter((iconEntity) => !deletableIds.includes(iconEntity.id)));
      setSelectedIds([]);
      setEditingId(null);
      setDraggedGrip(null);
      segmentStretchRef.current = null;
      setDraggedSegmentStretch(null);
      setDraggedDimensionId(null);
      setMoveState(null);
      setTransformState(null);
      setActiveOffset(null);
      setInvalidGripFeedback(null);
      setNumericEdit(null);
      setDirectionLockAxis(null);
      setIsBackgroundReferenceMode(false);
      setIsBackgroundCropMode(false);
      setBackgroundCropRect(null);
      setBackgroundReferenceLine(null);
      setBackgroundReferenceInput(null);
    });
  };

  const updateSelectedEntityStrokeColor = (strokeColor: string) => {
    const selectedIdSet = new Set(selectedIds);
    runHistoryAction(() => {
      setPolylines((current) =>
        current.map((shape) =>
          selectedIdSet.has(shape.id)
            ? {
                ...shape,
                strokeColor,
              }
            : shape,
        ),
      );
      setDimensions((current) =>
        current.map((dimension) =>
          selectedIdSet.has(dimension.id)
            ? {
                ...dimension,
                strokeColor,
              }
            : dimension,
        ),
      );
      setConstructionLines((current) =>
        current.map((line) =>
          selectedIdSet.has(line.id)
            ? {
                ...line,
                strokeColor,
              }
            : line,
        ),
      );
      setTextEntities((current) =>
        current.map((textEntity) =>
          selectedIdSet.has(textEntity.id)
            ? {
                ...textEntity,
                strokeColor,
              }
            : textEntity,
        ),
      );
      setIconEntities((current) =>
        current.map((iconEntity) =>
          selectedIdSet.has(iconEntity.id)
            ? {
                ...iconEntity,
                strokeColor,
                mainColor: strokeColor,
              }
            : iconEntity,
        ),
      );
    });
  };

  const updateSelectedEntityLayer = (layer: EntityLayerName) => {
    const selectedIdSet = new Set(selectedIds);
    const nextColor = getLayerColor(layer);
    runHistoryAction(() => {
      setPolylines((current) =>
        current.map((shape) =>
          selectedIdSet.has(shape.id)
            ? {
                ...shape,
                layer,
                strokeColor: nextColor,
              }
            : shape,
        ),
      );
      setDimensions((current) =>
        current.map((dimension) =>
          selectedIdSet.has(dimension.id)
            ? {
                ...dimension,
                layer,
                strokeColor: nextColor,
              }
            : dimension,
        ),
      );
      setConstructionLines((current) =>
        current.map((line) =>
          selectedIdSet.has(line.id)
            ? {
                ...line,
                layer,
                strokeColor: nextColor,
              }
            : line,
        ),
      );
      setTextEntities((current) =>
        current.map((textEntity) =>
          selectedIdSet.has(textEntity.id)
            ? {
                ...textEntity,
                layer,
                strokeColor: nextColor,
              }
            : textEntity,
        ),
      );
      setIconEntities((current) =>
        current.map((iconEntity) =>
          selectedIdSet.has(iconEntity.id)
            ? {
                ...iconEntity,
                layer,
                strokeColor: nextColor,
                mainColor: nextColor,
              }
            : iconEntity,
        ),
      );
    });
  };

  const updateSelectedRoofFallSide = (fallSide: boolean) => {
    const selectedIdSet = new Set(selectedIds);
    runHistoryAction(() => {
      setPolylines((current) =>
        current.map((shape) =>
          selectedIdSet.has(shape.id) && getShapeLayer(shape) === ROOF_LAYER
            ? {
                ...shape,
                fallSide,
              }
            : shape,
        ),
      );
    });
  };

  const updateSelectedRoofBuildProperty = (
    field:
      | "roofType"
      | "pitch"
      | "material"
      | "substructure"
      | "substructureHeight"
      | "substructureSizeHeight"
      | "substructureSizeWidth"
      | "metalPlateThickness"
      | "sheetMaterial"
      | "useOnlyAnchorPoints"
      | "system"
      | "normative"
      | "seamsSpan"
      | "insulationThickness"
      | "waveSpan"
      | "hipsSpan"
      | "supportMaterial"
      | "terminalCurveSupport"
      | "intermediateSupport"
      | "anchorPointMaterial"
      | "anchorPointMapping",
    id: string,
  ) => {
    if (!commonSelectedRoofGroup) return;

    const selectedRoofType = settings?.roofTypes.find((roofType) => roofType.id === id);
    const selectedMaterial = settings?.roofMaterials.find((material) => material.id === id);
    const selectedSubstructure = settings?.roofSubstructures.find((substructure) => substructure.id === id);
    const selectedSheetMaterial = settings?.sheetMaterials.find((sheetMaterial) => sheetMaterial.id === id);
    const selectedSystem = settings?.systems.find((system) => system.id === id);
    const selectedNormative = selectedRoofSystem?.normatives?.find((normative) => normative.id === id);
    const selectedSupportMaterial = lifeLineSupportMaterialOptions.find((material) => material.id === id);
    const selectedTerminalCurveSupport = lifeLineTerminalCurveSupportOptions.find((support) => support.item.id === id);
    const selectedIntermediateSupport = lifeLineIntermediateSupportOptions.find((support) => support.item.id === id);
    const selectedAnchorPointMaterial = anchorPointMaterialOptions.find((material) => material.id === id);
    const selectedAnchorPointMapping = anchorPointDefaultCodeOptions.find((mapping) => mapping.id === id);
    const parsedNumber = id === "" ? undefined : (parseLocalizedNumber(id) ?? undefined);

    runHistoryAction(() => {
      setPolylines((current) =>
        current.map((shape) => {
          const isInRoofGroup =
            commonSelectedRoofGroup.id === selectedCustomFlatRoof?.id
              ? shape.id === selectedCustomFlatRoof.id
              : shape.roofGroupId === commonSelectedRoofGroup.id;

          if (!isInRoofGroup) return shape;

          if (field === "roofType") {
            return {
              ...shape,
              roofTypeId: selectedRoofType?.id,
              roofTypeName: selectedRoofType?.name,
              roofMaterialId: undefined,
              roofMaterialName: undefined,
              roofSubstructureId: undefined,
              roofSubstructureName: undefined,
              roofSubstructureHeight: undefined,
              roofSubstructureSizeHeight: undefined,
              roofSubstructureSizeWidth: undefined,
              roofMetalPlateThickness: undefined,
              roofSheetMaterialId: undefined,
              roofSheetMaterialName: undefined,
              roofSeamsSpan: undefined,
              roofInsulationThickness: undefined,
              roofWaveSpan: undefined,
              roofHipsSpan: undefined,
              roofMembraneId: undefined,
              roofMembraneName: undefined,
              roofSupportMaterialId: undefined,
              roofSupportMaterialName: undefined,
              roofTerminalCurveSupportId: undefined,
              roofTerminalCurveSupportCode: undefined,
              roofTerminalCurveSupportName: undefined,
              roofIntermediateSupportId: undefined,
              roofIntermediateSupportCode: undefined,
              roofIntermediateSupportName: undefined,
              roofAnchorPointMappingId: undefined,
              roofAnchorPointCode: undefined,
              roofAnchorPointName: undefined,
              roofComponentMaterialId: undefined,
              roofComponentMaterialName: undefined,
              roofComponentAbsorberId: undefined,
              roofComponentAbsorberCode: undefined,
              roofComponentAbsorberName: undefined,
              roofComponentSystemTypeId: undefined,
              roofComponentSystemTypeName: undefined,
            };
          }

          if (field === "pitch") {
            return {
              ...shape,
              roofPitch: parsedNumber,
            };
          }

          if (field === "material") {
            return {
              ...shape,
              roofMaterialId: selectedMaterial?.id,
              roofMaterialName: selectedMaterial?.name,
              roofSubstructureId: undefined,
              roofSubstructureName: undefined,
              roofSubstructureHeight: undefined,
              roofSubstructureSizeHeight: undefined,
              roofSubstructureSizeWidth: undefined,
              roofMetalPlateThickness: undefined,
              roofSheetMaterialId: undefined,
              roofSheetMaterialName: undefined,
              roofSeamsSpan: undefined,
              roofInsulationThickness: undefined,
              roofWaveSpan: undefined,
              roofHipsSpan: undefined,
              roofMembraneId: undefined,
              roofMembraneName: undefined,
              roofSupportMaterialId: undefined,
              roofSupportMaterialName: undefined,
              roofTerminalCurveSupportId: undefined,
              roofTerminalCurveSupportCode: undefined,
              roofTerminalCurveSupportName: undefined,
              roofIntermediateSupportId: undefined,
              roofIntermediateSupportCode: undefined,
              roofIntermediateSupportName: undefined,
              roofAnchorPointMappingId: undefined,
              roofAnchorPointCode: undefined,
              roofAnchorPointName: undefined,
              roofComponentMaterialId: undefined,
              roofComponentMaterialName: undefined,
              roofComponentAbsorberId: undefined,
              roofComponentAbsorberCode: undefined,
              roofComponentAbsorberName: undefined,
              roofComponentSystemTypeId: undefined,
              roofComponentSystemTypeName: undefined,
            };
          }

          if (field === "substructure") {
            return {
              ...shape,
              roofSubstructureId: selectedSubstructure?.id,
              roofSubstructureName: selectedSubstructure?.name,
              roofSubstructureHeight: undefined,
              roofSubstructureSizeHeight: undefined,
              roofSubstructureSizeWidth: undefined,
              roofMetalPlateThickness: undefined,
              roofSystemId: undefined,
              roofSystemName: undefined,
              roofNormativeId: undefined,
              roofNormativeName: undefined,
              roofSeamsSpan: undefined,
              roofInsulationThickness: undefined,
              roofWaveSpan: undefined,
              roofSupportMaterialId: undefined,
              roofSupportMaterialName: undefined,
              roofTerminalCurveSupportId: undefined,
              roofTerminalCurveSupportCode: undefined,
              roofTerminalCurveSupportName: undefined,
              roofIntermediateSupportId: undefined,
              roofIntermediateSupportCode: undefined,
              roofIntermediateSupportName: undefined,
              roofAnchorPointMappingId: undefined,
              roofAnchorPointCode: undefined,
              roofAnchorPointName: undefined,
            };
          }

          if (field === "substructureHeight") {
            return {
              ...shape,
              roofSubstructureHeight: parsedNumber,
              roofAnchorPointMappingId: undefined,
              roofAnchorPointCode: undefined,
              roofAnchorPointName: undefined,
              roofComponentMaterialId: undefined,
              roofComponentMaterialName: undefined,
              roofComponentAbsorberId: undefined,
              roofComponentAbsorberCode: undefined,
              roofComponentAbsorberName: undefined,
              roofComponentSystemTypeId: undefined,
              roofComponentSystemTypeName: undefined,
            };
          }

          if (field === "substructureSizeHeight") {
            return {
              ...shape,
              roofSubstructureSizeHeight: parsedNumber,
              roofAnchorPointMappingId: undefined,
              roofAnchorPointCode: undefined,
              roofAnchorPointName: undefined,
              roofComponentMaterialId: undefined,
              roofComponentMaterialName: undefined,
              roofComponentAbsorberId: undefined,
              roofComponentAbsorberCode: undefined,
              roofComponentAbsorberName: undefined,
              roofComponentSystemTypeId: undefined,
              roofComponentSystemTypeName: undefined,
            };
          }

          if (field === "substructureSizeWidth") {
            return {
              ...shape,
              roofSubstructureSizeWidth: parsedNumber,
              roofAnchorPointMappingId: undefined,
              roofAnchorPointCode: undefined,
              roofAnchorPointName: undefined,
              roofComponentMaterialId: undefined,
              roofComponentMaterialName: undefined,
              roofComponentAbsorberId: undefined,
              roofComponentAbsorberCode: undefined,
              roofComponentAbsorberName: undefined,
              roofComponentSystemTypeId: undefined,
              roofComponentSystemTypeName: undefined,
            };
          }

          if (field === "metalPlateThickness") {
            return {
              ...shape,
              roofMetalPlateThickness: parsedNumber,
            };
          }

          if (field === "sheetMaterial") {
            return {
              ...shape,
              roofSheetMaterialId: selectedSheetMaterial?.id,
              roofSheetMaterialName: selectedSheetMaterial
                ? formatRoofSheetMaterialOption(selectedSheetMaterial)
                : undefined,
              roofSeamsSpan: undefined,
              roofInsulationThickness: undefined,
              roofWaveSpan: undefined,
              roofHipsSpan: undefined,
              roofSupportMaterialId: undefined,
              roofSupportMaterialName: undefined,
              roofTerminalCurveSupportId: undefined,
              roofTerminalCurveSupportCode: undefined,
              roofTerminalCurveSupportName: undefined,
              roofIntermediateSupportId: undefined,
              roofIntermediateSupportCode: undefined,
              roofIntermediateSupportName: undefined,
              roofAnchorPointMappingId: undefined,
              roofAnchorPointCode: undefined,
              roofAnchorPointName: undefined,
              roofComponentMaterialId: undefined,
              roofComponentMaterialName: undefined,
              roofComponentAbsorberId: undefined,
              roofComponentAbsorberCode: undefined,
              roofComponentAbsorberName: undefined,
              roofComponentSystemTypeId: undefined,
              roofComponentSystemTypeName: undefined,
            };
          }

          if (field === "useOnlyAnchorPoints") {
            const useOnlyAnchorPoints = id === "true";
            const defaultSystem = useOnlyAnchorPoints
              ? filteredPatrolAnchorPointSystems.find((system) => system.id === selectedAnchorPointSystem)
              : filteredPatrolLifeLineSystems.find((system) => system.id === selectedSafetySystem);

            return {
              ...shape,
              roofUseOnlyAnchorPoints: useOnlyAnchorPoints,
              roofSystemId: defaultSystem?.id,
              roofSystemName: defaultSystem?.name,
              roofNormativeId: undefined,
              roofNormativeName: undefined,
              roofSubstructureHeight: undefined,
              roofSubstructureSizeHeight: undefined,
              roofSubstructureSizeWidth: undefined,
              roofMetalPlateThickness: undefined,
              roofSeamsSpan: undefined,
              roofInsulationThickness: undefined,
              roofWaveSpan: undefined,
              roofHipsSpan: undefined,
              roofSupportMaterialId: undefined,
              roofSupportMaterialName: undefined,
              roofTerminalCurveSupportId: undefined,
              roofTerminalCurveSupportCode: undefined,
              roofTerminalCurveSupportName: undefined,
              roofIntermediateSupportId: undefined,
              roofIntermediateSupportCode: undefined,
              roofIntermediateSupportName: undefined,
              roofAnchorPointMappingId: undefined,
              roofAnchorPointCode: undefined,
              roofAnchorPointName: undefined,
            };
          }

          if (field === "system") {
            return {
              ...shape,
              roofSystemId: selectedSystem?.id,
              roofSystemName: selectedSystem?.name,
              roofNormativeId: undefined,
              roofNormativeName: undefined,
              roofSubstructureHeight: undefined,
              roofSubstructureSizeHeight: undefined,
              roofSubstructureSizeWidth: undefined,
              roofMetalPlateThickness: undefined,
              roofSeamsSpan: undefined,
              roofInsulationThickness: undefined,
              roofWaveSpan: undefined,
              roofHipsSpan: undefined,
              roofSupportMaterialId: undefined,
              roofSupportMaterialName: undefined,
              roofTerminalCurveSupportId: undefined,
              roofTerminalCurveSupportCode: undefined,
              roofTerminalCurveSupportName: undefined,
              roofIntermediateSupportId: undefined,
              roofIntermediateSupportCode: undefined,
              roofIntermediateSupportName: undefined,
              roofAnchorPointMappingId: undefined,
              roofAnchorPointCode: undefined,
              roofAnchorPointName: undefined,
            };
          }

          if (field === "normative") {
            return {
              ...shape,
              roofSystemId: shape.roofSystemId ?? selectedRoofSystem?.id,
              roofSystemName: shape.roofSystemName ?? selectedRoofSystem?.name,
              roofNormativeId: selectedNormative?.id,
              roofNormativeName: selectedNormative?.name,
              roofSupportMaterialId: undefined,
              roofSupportMaterialName: undefined,
              roofTerminalCurveSupportId: undefined,
              roofTerminalCurveSupportCode: undefined,
              roofTerminalCurveSupportName: undefined,
              roofIntermediateSupportId: undefined,
              roofIntermediateSupportCode: undefined,
              roofIntermediateSupportName: undefined,
              roofAnchorPointMappingId: undefined,
              roofAnchorPointCode: undefined,
              roofAnchorPointName: undefined,
            };
          }

          if (field === "seamsSpan") {
            return {
              ...shape,
              roofSeamsSpan: parsedNumber,
              roofSupportMaterialId: undefined,
              roofSupportMaterialName: undefined,
              roofTerminalCurveSupportId: undefined,
              roofTerminalCurveSupportCode: undefined,
              roofTerminalCurveSupportName: undefined,
              roofIntermediateSupportId: undefined,
              roofIntermediateSupportCode: undefined,
              roofIntermediateSupportName: undefined,
              roofAnchorPointMappingId: undefined,
              roofAnchorPointCode: undefined,
              roofAnchorPointName: undefined,
            };
          }

          if (field === "waveSpan") {
            return {
              ...shape,
              roofWaveSpan: parsedNumber,
              roofSupportMaterialId: undefined,
              roofSupportMaterialName: undefined,
              roofTerminalCurveSupportId: undefined,
              roofTerminalCurveSupportCode: undefined,
              roofTerminalCurveSupportName: undefined,
              roofIntermediateSupportId: undefined,
              roofIntermediateSupportCode: undefined,
              roofIntermediateSupportName: undefined,
            };
          }

          if (field === "hipsSpan") {
            return {
              ...shape,
              roofHipsSpan: parsedNumber,
              roofSupportMaterialId: undefined,
              roofSupportMaterialName: undefined,
              roofTerminalCurveSupportId: undefined,
              roofTerminalCurveSupportCode: undefined,
              roofTerminalCurveSupportName: undefined,
              roofIntermediateSupportId: undefined,
              roofIntermediateSupportCode: undefined,
              roofIntermediateSupportName: undefined,
            };
          }

          if (field === "insulationThickness") {
            return {
              ...shape,
              roofInsulationThickness: parsedNumber,
              roofSupportMaterialId: undefined,
              roofSupportMaterialName: undefined,
              roofTerminalCurveSupportId: undefined,
              roofTerminalCurveSupportCode: undefined,
              roofTerminalCurveSupportName: undefined,
              roofIntermediateSupportId: undefined,
              roofIntermediateSupportCode: undefined,
              roofIntermediateSupportName: undefined,
              roofAnchorPointMappingId: undefined,
              roofAnchorPointCode: undefined,
              roofAnchorPointName: undefined,
            };
          }

          if (field === "supportMaterial") {
            return {
              ...shape,
              roofSupportMaterialId: selectedSupportMaterial?.id,
              roofSupportMaterialName: selectedSupportMaterial?.name,
              roofTerminalCurveSupportId: undefined,
              roofTerminalCurveSupportCode: undefined,
              roofTerminalCurveSupportName: undefined,
              roofIntermediateSupportId: undefined,
              roofIntermediateSupportCode: undefined,
              roofIntermediateSupportName: undefined,
            };
          }

          if (field === "terminalCurveSupport") {
            return {
              ...shape,
              roofTerminalCurveSupportId: selectedTerminalCurveSupport?.item.id,
              roofTerminalCurveSupportCode: selectedTerminalCurveSupport?.item.code,
              roofTerminalCurveSupportName: selectedTerminalCurveSupport?.item.name,
              roofIntermediateSupportId:
                selectedTerminalCurveSupport?.forIntermediates && !shape.roofIntermediateSupportId
                  ? selectedTerminalCurveSupport.item.id
                  : shape.roofIntermediateSupportId,
              roofIntermediateSupportCode:
                selectedTerminalCurveSupport?.forIntermediates && !shape.roofIntermediateSupportId
                  ? selectedTerminalCurveSupport.item.code
                  : shape.roofIntermediateSupportCode,
              roofIntermediateSupportName:
                selectedTerminalCurveSupport?.forIntermediates && !shape.roofIntermediateSupportId
                  ? selectedTerminalCurveSupport.item.name
                  : shape.roofIntermediateSupportName,
            };
          }

          if (field === "intermediateSupport") {
            return {
              ...shape,
              roofIntermediateSupportId: selectedIntermediateSupport?.item.id,
              roofIntermediateSupportCode: selectedIntermediateSupport?.item.code,
              roofIntermediateSupportName: selectedIntermediateSupport?.item.name,
            };
          }

          if (field === "anchorPointMaterial") {
            return {
              ...shape,
              roofAnchorPointMaterialId: selectedAnchorPointMaterial?.id,
              roofAnchorPointMaterialName: selectedAnchorPointMaterial?.name,
              roofAnchorPointMappingId: undefined,
              roofAnchorPointCode: undefined,
              roofAnchorPointName: undefined,
            };
          }

          if (field === "anchorPointMapping") {
            return {
              ...shape,
              roofAnchorPointMappingId: selectedAnchorPointMapping?.id,
              roofAnchorPointCode: selectedAnchorPointMapping?.item.code,
              roofAnchorPointName: selectedAnchorPointMapping?.item.name,
            };
          }

          return shape;
        }),
      );
    });
  };

  const updateSelectedRoofReference = (value: string) => {
    if (!commonSelectedRoofGroup) return;

    const roofReference = value === "" ? undefined : value;
    runHistoryAction(() => {
      setPolylines((current) =>
        current.map((shape) => {
          const isInRoofGroup =
            commonSelectedRoofGroup.id === selectedCustomFlatRoof?.id
              ? shape.id === selectedCustomFlatRoof.id
              : shape.roofGroupId === commonSelectedRoofGroup.id;

          return isInRoofGroup
            ? {
                ...shape,
                roofGroupName: roofReference,
              }
            : shape;
        }),
      );
    });
  };

  const updateSelectedRoofComponentProperty = (field: "material" | "absorber" | "systemType", id: string) => {
    const selectedMaterial = roofComponentMaterialOptions.find((material) => material.id === id);
    const selectedAbsorber = roofComponentAbsorberOptions.find((absorber) => absorber.item.id === id);
    const selectedSystemType = roofComponentSystemTypeOptions.find((systemType) => systemType.id === id);

    if (!commonSelectedRoofGroup) {
      if (field === "material") {
        setFallbackRoofComponentMaterialId(selectedMaterial?.id ?? "");
        setFallbackRoofComponentAbsorberId("");
        setFallbackRoofComponentSystemTypeId("");
        return;
      }

      if (field === "absorber") {
        setFallbackRoofComponentAbsorberId(selectedAbsorber?.item.id ?? "");
        return;
      }

      setFallbackRoofComponentSystemTypeId(selectedSystemType?.id ?? "");
      return;
    }

    runHistoryAction(() => {
      setPolylines((current) =>
        current.map((shape) => {
          const isInRoofGroup =
            commonSelectedRoofGroup.id === selectedCustomFlatRoof?.id
              ? shape.id === selectedCustomFlatRoof.id
              : shape.roofGroupId === commonSelectedRoofGroup.id;

          if (!isInRoofGroup) return shape;

          if (field === "material") {
            return {
              ...shape,
              roofComponentMaterialId: selectedMaterial?.id,
              roofComponentMaterialName: selectedMaterial?.name,
              roofComponentAbsorberId: undefined,
              roofComponentAbsorberCode: undefined,
              roofComponentAbsorberName: undefined,
              roofComponentSystemTypeId: undefined,
              roofComponentSystemTypeName: undefined,
            };
          }

          if (field === "absorber") {
            return {
              ...shape,
              roofComponentAbsorberId: selectedAbsorber?.item.id,
              roofComponentAbsorberCode: selectedAbsorber?.item.code,
              roofComponentAbsorberName: selectedAbsorber?.item.name,
            };
          }

          return {
            ...shape,
            roofComponentSystemTypeId: selectedSystemType?.id,
            roofComponentSystemTypeName: selectedSystemType?.name,
          };
        }),
      );
    });
  };

  const parseShuttleQuantity = (value: string) => {
    const parsed = Number(value.replace(",", "."));
    return Number.isFinite(parsed) ? Math.max(0, Math.floor(parsed)) : undefined;
  };

  const updateSelectedRoofShuttleProperty = (field: "fixed" | "shuttle" | "nrOfShuttles", value: string) => {
    const nextFixed = field === "fixed" ? value === "true" : effectiveRoofFixed;
    const nextShuttleOptions = filterShuttles(
      settings?.shuttles ?? [],
      nextFixed,
      selectedRoofComponentSystemType,
      selectedRoofSystem,
      selectedRoofComponentMaterial,
    );
    const nextShuttle =
      field === "shuttle"
        ? (nextShuttleOptions.find((shuttle) => shuttle.id === value) ?? null)
        : (nextShuttleOptions.find((shuttle) => shuttle.id === effectiveRoofShuttleId) ??
          nextShuttleOptions[0] ??
          null);

    if (!commonSelectedRoofGroup) {
      if (field === "fixed") {
        setFallbackRoofFixed(nextFixed);
        setFallbackRoofShuttleId(nextShuttle?.id ?? "");
        return;
      }

      if (field === "shuttle") {
        setFallbackRoofShuttleId(nextShuttle?.id ?? "");
        return;
      }

      setFallbackRoofNrOfShuttles(value);
      return;
    }

    runHistoryAction(() => {
      setPolylines((current) =>
        current.map((shape) => {
          const isInRoofGroup =
            commonSelectedRoofGroup.id === selectedCustomFlatRoof?.id
              ? shape.id === selectedCustomFlatRoof.id
              : shape.roofGroupId === commonSelectedRoofGroup.id;

          if (!isInRoofGroup) return shape;

          if (field === "nrOfShuttles") {
            return {
              ...shape,
              roofNrOfShuttles: parseShuttleQuantity(value),
            };
          }

          return {
            ...shape,
            roofFixed: nextFixed,
            roofShuttleId: nextShuttle?.id,
            roofShuttleCode: nextShuttle?.item.code,
            roofShuttleName: nextShuttle?.item.name,
          };
        }),
      );
    });
  };

  useEffect(() => {
    const nextMaterialId = selectedRoofComponentMaterial?.id;
    const nextAbsorberId = selectedRoofComponentAbsorber?.item.id;
    const nextSystemTypeId = selectedRoofComponentSystemType?.id;

    if (!commonSelectedRoofGroup) {
      if (
        (fallbackRoofComponentMaterialId || "") === (nextMaterialId || "") &&
        (fallbackRoofComponentAbsorberId || "") === (nextAbsorberId || "") &&
        (fallbackRoofComponentSystemTypeId || "") === (nextSystemTypeId || "")
      ) {
        return;
      }

      setFallbackRoofComponentMaterialId(nextMaterialId ?? "");
      setFallbackRoofComponentAbsorberId(nextAbsorberId ?? "");
      setFallbackRoofComponentSystemTypeId(nextSystemTypeId ?? "");
      return;
    }

    if (
      (commonSelectedRoofComponentMaterialId || "") === (nextMaterialId || "") &&
      (commonSelectedRoofComponentAbsorberId || "") === (nextAbsorberId || "") &&
      (commonSelectedRoofComponentSystemTypeId || "") === (nextSystemTypeId || "")
    ) {
      return;
    }

    setPolylines((current) =>
      current.map((shape) => {
        const isInRoofGroup =
          commonSelectedRoofGroup.id === selectedCustomFlatRoof?.id
            ? shape.id === selectedCustomFlatRoof.id
            : shape.roofGroupId === commonSelectedRoofGroup.id;

        if (!isInRoofGroup) return shape;

        return {
          ...shape,
          roofComponentMaterialId: selectedRoofComponentMaterial?.id,
          roofComponentMaterialName: selectedRoofComponentMaterial?.name,
          roofComponentAbsorberId: selectedRoofComponentAbsorber?.item.id,
          roofComponentAbsorberCode: selectedRoofComponentAbsorber?.item.code,
          roofComponentAbsorberName: selectedRoofComponentAbsorber?.item.name,
          roofComponentSystemTypeId: selectedRoofComponentSystemType?.id,
          roofComponentSystemTypeName: selectedRoofComponentSystemType?.name,
        };
      }),
    );
  }, [
    commonSelectedRoofComponentAbsorberId,
    commonSelectedRoofComponentMaterialId,
    commonSelectedRoofComponentSystemTypeId,
    commonSelectedRoofGroup,
    fallbackRoofComponentAbsorberId,
    fallbackRoofComponentMaterialId,
    fallbackRoofComponentSystemTypeId,
    selectedCustomFlatRoof?.id,
    selectedRoofComponentAbsorber,
    selectedRoofComponentMaterial,
    selectedRoofComponentSystemType,
  ]);

  useEffect(() => {
    const nextShuttleId = selectedRoofShuttle?.id;

    if (!commonSelectedRoofGroup) {
      if ((fallbackRoofShuttleId || "") === (nextShuttleId || "")) return;

      setFallbackRoofShuttleId(nextShuttleId ?? "");
      return;
    }

    if ((commonSelectedRoofShuttleId || "") === (nextShuttleId || "")) return;

    setPolylines((current) =>
      current.map((shape) => {
        const isInRoofGroup =
          commonSelectedRoofGroup.id === selectedCustomFlatRoof?.id
            ? shape.id === selectedCustomFlatRoof.id
            : shape.roofGroupId === commonSelectedRoofGroup.id;

        if (!isInRoofGroup) return shape;

        return {
          ...shape,
          roofFixed: effectiveRoofFixed,
          roofShuttleId: selectedRoofShuttle?.id,
          roofShuttleCode: selectedRoofShuttle?.item.code,
          roofShuttleName: selectedRoofShuttle?.item.name,
        };
      }),
    );
  }, [
    commonSelectedRoofGroup,
    commonSelectedRoofShuttleId,
    effectiveRoofFixed,
    fallbackRoofShuttleId,
    selectedCustomFlatRoof?.id,
    selectedRoofShuttle,
  ]);

  const updateFallProtectionSupportProperty = (
    field:
      | "seamsSpan"
      | "insulationThickness"
      | "supportMaterial"
      | "terminalCurveSupport"
      | "intermediateSupport"
      | "anchorPointMaterial"
      | "anchorPointMapping",
    value: string,
  ) => {
    if (commonSelectedRoofGroup) {
      updateSelectedRoofBuildProperty(field, value);
      return;
    }

    if (field === "seamsSpan") {
      setFallbackSupportSeamsSpan(value);
      setFallbackSupportMaterialId("");
      setFallbackTerminalCurveSupportId("");
      setFallbackIntermediateSupportId("");
      setFallbackAnchorPointMaterialId("");
      setFallbackAnchorPointMappingId("");
      return;
    }

    if (field === "insulationThickness") {
      setFallbackSupportInsulationThickness(value);
      setFallbackSupportMaterialId("");
      setFallbackTerminalCurveSupportId("");
      setFallbackIntermediateSupportId("");
      setFallbackAnchorPointMaterialId("");
      setFallbackAnchorPointMappingId("");
      return;
    }

    if (field === "supportMaterial") {
      setFallbackSupportMaterialId(value);
      setFallbackTerminalCurveSupportId("");
      setFallbackIntermediateSupportId("");
      return;
    }

    if (field === "terminalCurveSupport") {
      setFallbackTerminalCurveSupportId(value);
      const selectedSupport = lifeLineTerminalCurveSupportOptions.find((support) => support.item.id === value);
      if (selectedSupport?.forIntermediates && !fallbackIntermediateSupportId) {
        setFallbackIntermediateSupportId(value);
      }
      return;
    }

    if (field === "intermediateSupport") {
      setFallbackIntermediateSupportId(value);
      return;
    }

    if (field === "anchorPointMaterial") {
      setFallbackAnchorPointMaterialId(value);
      setFallbackAnchorPointMappingId("");
      return;
    }

    setFallbackAnchorPointMappingId(value);
  };

  useEffect(() => {
    const currentSupportMaterial =
      lifeLineSupportMaterialOptions.find((material) => material.id === effectiveLifeLineSupportMaterialId) ?? null;
    const nextSupportMaterial = currentSupportMaterial ?? lifeLineSupportMaterialOptions[0] ?? null;
    const currentTerminalSupport =
      lifeLineTerminalCurveSupportOptions.find((support) => support.item.id === effectiveTerminalCurveSupportId) ??
      null;
    const nextTerminalSupport = currentTerminalSupport ?? lifeLineTerminalCurveSupportOptions[0] ?? null;
    const currentIntermediateSupport =
      lifeLineIntermediateSupportOptions.find((support) => support.item.id === effectiveIntermediateSupportId) ?? null;
    const nextIntermediateSupport =
      currentIntermediateSupport ??
      (nextTerminalSupport?.forIntermediates ? nextTerminalSupport : null) ??
      lifeLineIntermediateSupportOptions[0] ??
      null;
    const currentAnchorPointMaterial =
      anchorPointMaterialOptions.find((material) => material.id === resolvedAnchorPointMaterialId) ?? null;
    const nextAnchorPointMaterial = currentAnchorPointMaterial ?? anchorPointMaterialOptions[0] ?? null;
    const currentAnchorPointMapping =
      anchorPointDefaultCodeOptions.find((mapping) => mapping.id === effectiveAnchorPointMappingId) ?? null;
    const nextAnchorPointMapping = currentAnchorPointMapping ?? anchorPointDefaultCodeOptions[0] ?? null;

    const nextSupportMaterialId = nextSupportMaterial?.id;
    const nextTerminalId = nextTerminalSupport?.item.id;
    const nextIntermediateId = nextIntermediateSupport?.item.id;
    const nextAnchorPointMaterialId = nextAnchorPointMaterial?.id;
    const nextAnchorPointId = nextAnchorPointMapping?.id;

    if (
      (effectiveLifeLineSupportMaterialId || "") === (nextSupportMaterialId || "") &&
      (effectiveTerminalCurveSupportId || "") === (nextTerminalId || "") &&
      (effectiveIntermediateSupportId || "") === (nextIntermediateId || "") &&
      (resolvedAnchorPointMaterialId || "") === (nextAnchorPointMaterialId || "") &&
      (effectiveAnchorPointMappingId || "") === (nextAnchorPointId || "")
    ) {
      return;
    }

    if (!commonSelectedRoofGroup) {
      setFallbackSupportMaterialId(nextSupportMaterialId ?? "");
      setFallbackTerminalCurveSupportId(nextTerminalId ?? "");
      setFallbackIntermediateSupportId(nextIntermediateId ?? "");
      setFallbackAnchorPointMaterialId(nextAnchorPointMaterialId ?? "");
      setFallbackAnchorPointMappingId(nextAnchorPointId ?? "");
      return;
    }

    setPolylines((current) =>
      current.map((shape) => {
        const isInRoofGroup =
          commonSelectedRoofGroup.id === selectedCustomFlatRoof?.id
            ? shape.id === selectedCustomFlatRoof.id
            : shape.roofGroupId === commonSelectedRoofGroup.id;

        if (!isInRoofGroup) return shape;

        return {
          ...shape,
          roofSupportMaterialId: nextSupportMaterial?.id,
          roofSupportMaterialName: nextSupportMaterial?.name,
          roofTerminalCurveSupportId: nextTerminalSupport?.item.id ?? shape.roofTerminalCurveSupportId,
          roofTerminalCurveSupportCode: nextTerminalSupport?.item.code ?? shape.roofTerminalCurveSupportCode,
          roofTerminalCurveSupportName: nextTerminalSupport?.item.name ?? shape.roofTerminalCurveSupportName,
          roofIntermediateSupportId: nextIntermediateSupport?.item.id ?? shape.roofIntermediateSupportId,
          roofIntermediateSupportCode: nextIntermediateSupport?.item.code ?? shape.roofIntermediateSupportCode,
          roofIntermediateSupportName: nextIntermediateSupport?.item.name ?? shape.roofIntermediateSupportName,
          roofAnchorPointMaterialId: nextAnchorPointMaterial?.id,
          roofAnchorPointMaterialName: nextAnchorPointMaterial?.name,
          roofAnchorPointMappingId: nextAnchorPointMapping?.id,
          roofAnchorPointCode: nextAnchorPointMapping?.item.code,
          roofAnchorPointName: nextAnchorPointMapping?.item.name,
        };
      }),
    );
  }, [
    anchorPointDefaultCodeOptions,
    anchorPointMaterialOptions,
    commonSelectedRoofGroup,
    effectiveAnchorPointMappingId,
    effectiveLifeLineSupportMaterialId,
    effectiveIntermediateSupportId,
    effectiveTerminalCurveSupportId,
    lifeLineSupportMaterialOptions,
    lifeLineIntermediateSupportOptions,
    lifeLineTerminalCurveSupportOptions,
    resolvedAnchorPointMaterialId,
    selectedCustomFlatRoof?.id,
  ]);

  const updateEntityTypeStyle = <K extends keyof EntityTypeStyle>(
    kind: EntityStyleKind,
    key: K,
    value: EntityTypeStyle[K],
  ) => {
    setEntityTypeStyles((current) => ({
      ...current,
      [kind]: {
        ...current[kind],
        [key]: value,
      },
    }));
  };

  const updateSafetySystemSetting = <K extends keyof SafetySystemSettings>(
    system: SafetySystemOption,
    key: K,
    value: SafetySystemSettings[K],
  ) => {
    setSafetySystemSettings((current) => ({
      ...current,
      [system]: {
        ...(current[system] ?? getDefaultSafetySystemSettings(settings?.systems.find((item) => item.id === system))),
        [key]: value,
      },
    }));
  };

  const updateSingleSelectedEntityName = (name: string) => {
    if (!singleSelectedEntity) return;

    setPolylines((current) =>
      current.map((shape) =>
        shape.id === singleSelectedEntity.id
          ? {
              ...shape,
              name,
            }
          : shape,
      ),
    );
    setDimensions((current) =>
      current.map((dimension) =>
        dimension.id === singleSelectedEntity.id
          ? {
              ...dimension,
              name,
            }
          : dimension,
      ),
    );
    setTextEntities((current) =>
      current.map((textEntity) =>
        textEntity.id === singleSelectedEntity.id
          ? {
              ...textEntity,
              name,
              text: name,
            }
          : textEntity,
      ),
    );
    setIconEntities((current) =>
      current.map((iconEntity) =>
        iconEntity.id === singleSelectedEntity.id
          ? {
              ...iconEntity,
              name,
            }
          : iconEntity,
      ),
    );
  };

  const buildMoveState = (
    mode: "move" | "copy",
    entityIds: string[],
    anchorPoint: CanvasPoint,
    shapeSource: Shape[] = polylines,
    dimensionSource: Dimension[] = dimensions,
    snapExcludedEntityIds: string[] = entityIds,
  ): MoveState =>
    buildMoveStateRule(
      mode,
      entityIds,
      anchorPoint,
      shapeSource,
      dimensionSource,
      iconEntities,
      snapExcludedEntityIds,
    ) as MoveState;

  const applyMoveState = (
    move: MoveState,
    pointer: CanvasPoint,
    shapeSource: Shape[] = polylines,
    dimensionSource: Dimension[] = dimensions,
    iconSource: CanvasIconEntity[] = iconEntities,
  ) => {
    const nextState = applyMoveStateRule(shapeSource, dimensionSource, iconSource, move, pointer) as {
      shapes: Shape[];
      dimensions: Dimension[];
      iconEntities: CanvasIconEntity[];
    };
    setPolylines(nextState.shapes);
    setDimensions(nextState.dimensions);
    setIconEntities(nextState.iconEntities);
    return nextState;
  };

  const scalePointAround = (point: CanvasPoint, origin: CanvasPoint, factor: number): CanvasPoint => ({
    x: origin.x + (point.x - origin.x) * factor,
    y: origin.y + (point.y - origin.y) * factor,
  });

  const rotatePointAround = (point: CanvasPoint, origin: CanvasPoint, angle: number): CanvasPoint => {
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    const deltaX = point.x - origin.x;
    const deltaY = point.y - origin.y;
    return {
      x: origin.x + deltaX * cos - deltaY * sin,
      y: origin.y + deltaX * sin + deltaY * cos,
    };
  };

  const getSignedAngleBetweenVectors = (origin: CanvasPoint, firstPoint: CanvasPoint, secondPoint: CanvasPoint) => {
    const first = {
      x: firstPoint.x - origin.x,
      y: firstPoint.y - origin.y,
    };
    const second = {
      x: secondPoint.x - origin.x,
      y: secondPoint.y - origin.y,
    };
    const firstLength = Math.hypot(first.x, first.y);
    const secondLength = Math.hypot(second.x, second.y);
    if (firstLength <= 0.0001 || secondLength <= 0.0001) return 0;

    const cross = first.x * second.y - first.y * second.x;
    const dot = first.x * second.x + first.y * second.y;
    return (Math.atan2(cross, dot) * 180) / Math.PI;
  };

  const mapCoordinateArray = (points: number[], mapper: (point: CanvasPoint) => CanvasPoint) =>
    points.flatMap((_, index, source) => {
      if (index % 2 !== 0) return [];
      const x = source[index];
      const y = source[index + 1];
      if (x == null || y == null) return [];
      const nextPoint = mapper({ x, y });
      return [nextPoint.x, nextPoint.y];
    });

  const transformShapeAround = (
    shape: Shape,
    transformPoint: (point: CanvasPoint) => CanvasPoint,
    scaleFactor = 1,
    rotationAngle = 0,
  ): Shape => {
    if (shape.kind === "circle") {
      return {
        ...shape,
        center: transformPoint(shape.center),
        radius: Math.max(0.001, shape.radius * Math.abs(scaleFactor)),
      };
    }

    if (shape.kind === "arc") {
      return {
        ...shape,
        center: transformPoint(shape.center),
        radius: Math.max(0.001, shape.radius * Math.abs(scaleFactor)),
        startAngle: shape.startAngle + rotationAngle,
        endAngle: shape.endAngle + rotationAngle,
      };
    }

    return {
      ...shape,
      points: mapCoordinateArray(shape.points, transformPoint),
      holes: shape.holes?.map((hole) => mapCoordinateArray(hole, transformPoint)),
      circleHoles: shape.circleHoles?.map((hole) => ({
        center: transformPoint(hole.center),
        radius: Math.max(0.001, hole.radius * Math.abs(scaleFactor)),
      })),
    };
  };

  const transformDimensionAround = (
    dimension: Dimension,
    transformPoint: (point: CanvasPoint) => CanvasPoint,
    scaleFactor = 1,
  ): Dimension => ({
    ...dimension,
    start: transformPoint(dimension.start),
    end: transformPoint(dimension.end),
    offset: dimension.offset * Math.abs(scaleFactor),
  });

  const transformIconAround = (
    iconEntity: CanvasIconEntity,
    transformPoint: (point: CanvasPoint) => CanvasPoint,
    scaleFactor = 1,
    rotationAngle = 0,
  ): CanvasIconEntity => ({
    ...iconEntity,
    position: transformPoint(iconEntity.position),
    size: Math.max(1, iconEntity.size * Math.abs(scaleFactor)),
    rotation: (iconEntity.rotation ?? 0) + (rotationAngle * 180) / Math.PI,
  });

  const applyScaleRotateTransform = (state: TransformState, value: number) => {
    const entityIdSet = new Set(state.entityIds);
    const scaleFactor = state.mode === "scale" ? Math.max(0.0001, value) : 1;
    const rotationAngle = state.mode === "rotate" ? (value * Math.PI) / 180 : 0;
    const transformPoint =
      state.mode === "scale"
        ? (point: CanvasPoint) => scalePointAround(point, state.basePoint, scaleFactor)
        : (point: CanvasPoint) => rotatePointAround(point, state.basePoint, rotationAngle);

    runHistoryAction(() => {
      setPolylines((current) =>
        current.map((shape) =>
          entityIdSet.has(shape.id) ? transformShapeAround(shape, transformPoint, scaleFactor, rotationAngle) : shape,
        ),
      );
      setDimensions((current) =>
        current.map((dimension) =>
          entityIdSet.has(dimension.id) ? transformDimensionAround(dimension, transformPoint, scaleFactor) : dimension,
        ),
      );
      setIconEntities((current) =>
        current.map((iconEntity) =>
          entityIdSet.has(iconEntity.id)
            ? transformIconAround(iconEntity, transformPoint, scaleFactor, rotationAngle)
            : iconEntity,
        ),
      );
    });
  };

  const cancelActiveTransform = () => {
    if (transformState) {
      setTransformState(null);
      setNumericEdit(null);
      setHoverSnap(null);
      return;
    }

    if (!moveState) return;

    if (moveState.mode === "copy") {
      const historySnapshot = historyActionRef.current?.before;
      if (historySnapshot) {
        applyHistorySnapshot(historySnapshot);
      } else {
        cancelHistoryAction();
        setMoveState(null);
        setNumericEdit(null);
        setHoverSnap(null);
      }
      return;
    }

    applyMoveState(moveState, moveState.anchorPoint);
    cancelHistoryAction();
    setMoveState(null);
    setNumericEdit(null);
    setHoverSnap(null);
  };

  const duplicateEntities = (entityIds: string[]) => {
    const duplicates = duplicateEntitiesRule(
      polylines,
      dimensions,
      iconEntities,
      entityIds,
      () => `polyline-${crypto.randomUUID()}`,
      () => `dimension-${crypto.randomUUID()}`,
      () => `icon-${crypto.randomUUID()}`,
    ) as {
      entityIds: string[];
      shapes: Shape[];
      dimensions: Dimension[];
      iconEntities: CanvasIconEntity[];
    };

    setPolylines((current) => [...current, ...duplicates.shapes]);
    setDimensions((current) => [...current, ...duplicates.dimensions]);
    setIconEntities((current) => [...current, ...duplicates.iconEntities]);

    return duplicates;
  };

  const parseCopySequenceCount = (value?: string | null) => parseCopySequenceCountRule(value);

  const duplicateCopiedEntitiesInSequence = (
    entityIds: string[],
    deltaX: number,
    deltaY: number,
    totalCopies: number,
    shapeSource: Shape[] = polylines,
    dimensionSource: Dimension[] = dimensions,
    iconSource: CanvasIconEntity[] = iconEntities,
  ) => {
    const duplicates = duplicateCopiedEntitiesInSequenceRule(
      shapeSource,
      dimensionSource,
      iconSource,
      entityIds,
      deltaX,
      deltaY,
      totalCopies,
      () => `polyline-${crypto.randomUUID()}`,
      () => `dimension-${crypto.randomUUID()}`,
      () => `icon-${crypto.randomUUID()}`,
    ) as {
      entityIds: string[];
      shapes: Shape[];
      dimensions: Dimension[];
      iconEntities: CanvasIconEntity[];
    };

    if (duplicates.shapes.length > 0) {
      setPolylines((current) => [...current, ...duplicates.shapes]);
    }
    if (duplicates.dimensions.length > 0) {
      setDimensions((current) => [...current, ...duplicates.dimensions]);
    }
    if (duplicates.iconEntities.length > 0) {
      setIconEntities((current) => [...current, ...duplicates.iconEntities]);
    }

    return duplicates.entityIds;
  };

  const commitOffset = (
    shape: Shape,
    pointer: CanvasPoint,
    distance: number,
    sourceContour: OffsetSourceContour = { kind: "outer" },
  ) => {
    const preview = buildOffsetShapePreview(shape, pointer, distance, sourceContour);
    if (!preview) return null;

    const sourceShape = buildOffsetSourceShape(shape, sourceContour);
    if (!sourceShape) return null;
    const nextName = `${shape.name} offset`;

    if (preview.kind === "circle" && sourceShape.kind === "circle") {
      const nextShape: CircleShape = {
        ...sourceShape,
        id: `polyline-${crypto.randomUUID()}`,
        name: nextName,
        center: { ...preview.center },
        radius: preview.radius,
      };

      setPolylines((current) => [...current, nextShape]);
      return nextShape.id;
    }

    if (preview.kind === "polyline" && sourceShape.kind !== "circle" && sourceShape.kind !== "arc") {
      const nextShape: PolylineLikeShape = {
        ...sourceShape,
        id: `polyline-${crypto.randomUUID()}`,
        name: nextName,
        points: [...preview.points],
        holes: undefined,
        circleHoles: undefined,
      };

      setPolylines((current) => [...current, nextShape]);
      return nextShape.id;
    }

    return null;
  };

  const isRoofComponentToolMode = (mode: ToolMode): mode is RoofComponentToolMode =>
    mode === "roofGronda" || mode === "roofColmo" || mode === "roofDiagonal" || mode === "roofSide";

  const getRoofComponentPrefix = (mode: RoofComponentToolMode) =>
    mode === "roofGronda" ? "Gronda" : mode === "roofColmo" ? "Colmo" : mode === "roofDiagonal" ? "HIP" : "BL";

  const getRoofComponentStyleKind = (prefix: "Gronda" | "Colmo" | "HIP" | "BL" | "RAKE" | "Valley"): EntityStyleKind =>
    prefix === "Gronda"
      ? "roof-eave"
      : prefix === "Colmo"
        ? "roof-ridge"
        : prefix === "BL" || prefix === "RAKE"
          ? "roof-rake"
          : prefix === "Valley"
            ? "roof-valley"
            : "roof-hip";

  const getRoofComponentFallSide = (prefix: "Gronda" | "Colmo" | "HIP" | "BL" | "RAKE" | "Valley") =>
    prefix === "Gronda" || prefix === "BL" || prefix === "RAKE";

  const getRoofShapeFallSide = (shape: Shape) => {
    if (typeof shape.fallSide === "boolean") return shape.fallSide;
    if (shape.name.startsWith("Gronda")) return getRoofComponentFallSide("Gronda");
    if (shape.name.startsWith("RAKE")) return getRoofComponentFallSide("RAKE");
    if (shape.name.startsWith("BL")) return getRoofComponentFallSide("BL");
    if (shape.name.startsWith("Colmo")) return getRoofComponentFallSide("Colmo");
    if (shape.name.startsWith("HIP") || shape.name.startsWith("Diagonale")) return getRoofComponentFallSide("HIP");
    if (shape.name.startsWith("Valley")) return getRoofComponentFallSide("Valley");
    if (
      !shape.isObstacle &&
      getShapeLayer(shape) === ROOF_LAYER &&
      (shape.kind === "rectangle" ||
        shape.name.startsWith("Flat Roof") ||
        shape.name.startsWith("Custom Flat Roof") ||
        shape.name.startsWith("Custom Flat Room"))
    ) {
      return true;
    }
    return false;
  };

  const getRoofCreationGroupCategory = (mode: RoofCreationToolMode): RoofGroupCategory => {
    if (mode === "polyline" || mode === "rectangle") return "Flat";
    if (mode === "curvedRoof") return "Curved";
    return "Pitched";
  };

  const getRoofCreationStyleKind = (mode: RoofCreationToolMode): EntityStyleKind =>
    mode === "polyline"
      ? "custom-flat-roof"
      : mode === "rectangle"
        ? "rectangle"
        : mode === "roof"
          ? "automatic-pitched-roof"
          : mode === "gableRoof"
            ? "single-gable-roof"
            : mode === "curvedRoof"
              ? "curved-roof"
              : mode === "singlePitchedRoof"
                ? "single-pitched-roof"
                : mode === "multiShedRoof"
                  ? "multi-shed-roof"
                  : "multi-gable-roof";

  const getDefaultRoofTypeForCreation = (mode: RoofCreationToolMode, category: RoofGroupCategory) => {
    const roofTypes = settings?.roofTypes ?? [];
    const compatibleRoofTypes = roofTypes.filter((roofType) => {
      const roofCategory = roofType.roofCategory;
      const isCurvedCategory =
        isCurvedRoofCompatibilityName(roofCategory.name) || isCurvedRoofCompatibilityName(roofType.name);

      if (category === "Flat") return roofCategory.isFlat;
      if (category === "Curved") return !roofCategory.isFlat && isCurvedCategory;
      return !roofCategory.isFlat && !isCurvedCategory;
    });

    const typeNameIncludes = (roofType: (typeof compatibleRoofTypes)[number], patterns: string[]) => {
      const normalizedName = normalizeRoofCompatibilityText(roofType.name);
      return patterns.some((pattern) => normalizedName.includes(pattern));
    };

    const byName = (patterns: string[]) => compatibleRoofTypes.find((roofType) => typeNameIncludes(roofType, patterns));

    if (mode === "polyline") {
      return (
        compatibleRoofTypes.find((roofType) => roofType.canBeCustom) ??
        byName(["custom", "flat"]) ??
        compatibleRoofTypes[0]
      );
    }

    if (mode === "rectangle") {
      return (
        byName(["flat"]) ?? compatibleRoofTypes.find((roofType) => !roofType.canBeCustom) ?? compatibleRoofTypes[0]
      );
    }

    if (mode === "curvedRoof") {
      return byName(["rounded", "round", "curv"]) ?? compatibleRoofTypes[0];
    }

    if (mode === "multiShedRoof") {
      return (
        byName(["multi shed", "shed", "sheds"]) ??
        compatibleRoofTypes.find((roofType) => roofType.canBeMultiple) ??
        compatibleRoofTypes[0]
      );
    }

    if (mode === "singlePitchedRoof") {
      return byName(["single pitched", "mono", "monofalda", "shed", "pitched"]) ?? compatibleRoofTypes[0];
    }

    if (mode === "multiGableRoof") {
      return (
        byName(["multi gable", "gable", "pitched"]) ??
        compatibleRoofTypes.find((roofType) => roofType.canBeMultiple || roofType.hasHips) ??
        compatibleRoofTypes[0]
      );
    }

    return byName(["pitched", "gable", "hip"]) ?? compatibleRoofTypes[0];
  };

  const createRoofGroupMeta = (
    category: RoofGroupCategory,
    existingShapes: Shape[],
    creationMode?: RoofCreationToolMode,
  ) => {
    const groupCount = new Set(existingShapes.map((shape) => shape.roofGroupId).filter(Boolean)).size;
    const defaultRoofType = creationMode ? getDefaultRoofTypeForCreation(creationMode, category) : undefined;
    return {
      roofGroupId: `roof-group-${crypto.randomUUID()}`,
      roofGroupName: `${category} ${groupCount + 1}`,
      roofGroupCategory: category,
      ...(defaultRoofType
        ? {
            roofTypeId: defaultRoofType.id,
            roofTypeName: defaultRoofType.name,
          }
        : {}),
    };
  };

  const getRoofLineKindFromName = (name: string) =>
    name.startsWith("Gronda")
      ? "Gronda"
      : name.startsWith("RAKE")
        ? "RAKE"
        : name.startsWith("BL")
          ? "BL"
          : name.startsWith("Colmo")
            ? "Colmo"
            : name.startsWith("HIP") || name.startsWith("Diagonale")
              ? "HIP"
              : name.startsWith("Valley")
                ? "Valley"
                : null;

  const getRoofComponentToolLabel = (mode: RoofComponentToolMode) =>
    mode === "roofGronda"
      ? "Eaves line"
      : mode === "roofColmo"
        ? "Ridge line"
        : mode === "roofDiagonal"
          ? "Hip/Valley line"
          : "Rake line";

  const areCanvasPointsEqual = (pointA: CanvasPoint, pointB: CanvasPoint) =>
    Math.abs(pointA.x - pointB.x) < 0.0001 && Math.abs(pointA.y - pointB.y) < 0.0001;

  const getPointFromCoordinateArray = (points: number[], index: number): CanvasPoint | null => {
    const x = points[index];
    const y = points[index + 1];
    return x == null || y == null ? null : { x, y };
  };

  const getShapeEndpointPair = (shape: PolylineLikeShape) => {
    if (shape.points.length < 4) return null;
    const start = getPointFromCoordinateArray(shape.points, 0);
    const end = getPointFromCoordinateArray(shape.points, shape.points.length - 2);
    if (!start || !end) return null;
    return {
      start,
      end,
    };
  };

  const reversePolylinePoints = (points: number[]) => {
    const reversed: number[] = [];
    for (let index = points.length - 2; index >= 0; index -= 2) {
      const x = points[index];
      const y = points[index + 1];
      if (x == null || y == null) continue;
      reversed.push(x, y);
    }
    return reversed;
  };

  const mergeConnectedRoofComponentShapes = (shapes: PolylineLikeShape[], prefix: "Gronda" | "Colmo") => {
    const remaining = shapes.map((shape) => ({ ...shape, points: [...shape.points] }));
    const merged: PolylineLikeShape[] = [];

    while (remaining.length > 0) {
      const seed = remaining.shift();
      if (!seed) continue;

      let chain = [...seed.points];
      let extended = true;

      while (extended) {
        extended = false;
        const chainShape = { ...seed, points: chain };
        const chainEndpoints = getShapeEndpointPair(chainShape);
        if (!chainEndpoints) break;

        for (let index = 0; index < remaining.length; index += 1) {
          const candidate = remaining[index];
          if (!candidate) continue;
          const candidateEndpoints = getShapeEndpointPair(candidate);
          if (!candidateEndpoints) continue;

          if (areCanvasPointsEqual(chainEndpoints.end, candidateEndpoints.start)) {
            chain = [...chain, ...candidate.points.slice(2)];
          } else if (areCanvasPointsEqual(chainEndpoints.end, candidateEndpoints.end)) {
            chain = [...chain, ...reversePolylinePoints(candidate.points).slice(2)];
          } else if (areCanvasPointsEqual(chainEndpoints.start, candidateEndpoints.end)) {
            chain = [...candidate.points.slice(0, -2), ...chain];
          } else if (areCanvasPointsEqual(chainEndpoints.start, candidateEndpoints.start)) {
            chain = [...reversePolylinePoints(candidate.points).slice(0, -2), ...chain];
          } else {
            continue;
          }

          remaining.splice(index, 1);
          extended = true;
          break;
        }
      }

      merged.push({
        ...seed,
        points: chain,
        name: `${prefix} ${merged.length + 1}`,
        fallSide: getRoofComponentFallSide(prefix),
      });
    }

    return merged;
  };

  const finalizeActiveRoofComponentPolyline = (mode: RoofComponentToolMode) => {
    const points = activePolylineRef.current;
    if (!points) return;

    const committedPoints = points.slice(0, -2);
    if (committedPoints.length <= 2) {
      setActivePolyline(null);
      setDirectionLockAxis(null);
      clearOsnapTracking();
      return;
    }

    const prefix = getRoofComponentPrefix(mode);
    if (!prefix) return;
    const styleKind = getRoofComponentStyleKind(prefix);
    const polylineStyle = getEntityTypeStyle(styleKind);
    const shouldMerge = prefix === "Gronda" || prefix === "Colmo";

    runHistoryAction(() => {
      const current = polylines;
      const newShape: PolylineLikeShape = {
        id: `polyline-${crypto.randomUUID()}`,
        points: committedPoints,
        closed: false,
        kind: "polyline",
        layer: ROOF_LAYER,
        entityStyleKind: styleKind,
        name: `${prefix} ${current.filter((shape) => shape.name.startsWith(prefix)).length + 1}`,
        strokeColor: polylineStyle.strokeColor,
        strokeWidth: polylineStyle.strokeWidth,
        fallSide: getRoofComponentFallSide(prefix),
      };

      if (!shouldMerge) {
        setPolylines([...current, newShape]);
        setTextEntities((currentLabels) => [...currentLabels, ...createRoofComponentLabels([newShape])]);
        return;
      }

      const samePrefixShapes = current.filter(
        (shape): shape is PolylineLikeShape => shape.kind !== "circle" && shape.name.startsWith(prefix),
      );
      const samePrefixLabelNames = new Set(samePrefixShapes.map((shape) => `${shape.name} letter`));
      const otherShapes = current.filter((shape) => !shape.name.startsWith(prefix));
      const mergedShapes = mergeConnectedRoofComponentShapes([...samePrefixShapes, newShape], prefix);

      setPolylines([...otherShapes, ...mergedShapes]);
      setTextEntities((currentLabels) => [
        ...currentLabels.filter((textEntity) => !samePrefixLabelNames.has(textEntity.name)),
        ...createRoofComponentLabels(mergedShapes),
      ]);
    });

    setActivePolyline(null);
    setDirectionLockAxis(null);
    clearOsnapTracking();
  };

  const finalizeActivePolyline = (options: { closed?: boolean } = {}) => {
    const points = activePolylineRef.current;
    if (!points) return;

    const committedPoints = points.slice(0, -2);
    if (committedPoints.length <= 2) {
      setActivePolyline(null);
      setDirectionLockAxis(null);
      clearOsnapTracking();
      return;
    }

    if (toolMode === "singleLifeLine") {
      runHistoryAction(() => {
        const polylineStyle = getEntityTypeStyle("single-lifeline");
        const lifeLineSystemMeta = getSelectedLifeLineSystemMeta();
        const isClosedLifeLine = options.closed === true;
        const lifeLineDesignPoints = getLifeLineDesignPoints(committedPoints, isClosedLifeLine);
        const { result: lifeLineResult, renderedLifeLines } = calculateRenderedLifeLines(lifeLineDesignPoints, {
          closed: isClosedLifeLine,
          systemMaxSpan: effectiveLifeLineSystemSettings.maxSpacing,
          chamferAcuteAngles: effectiveLifeLineSystemSettings.chamferAcuteAngles,
          chamferSegmentLength: effectiveLifeLineSystemSettings.chamferSegmentLength,
          maxSingleLineLength: effectiveLifeLineSystemSettings.maxSingleLineLength,
          maxCornersPerLifeline: effectiveLifeLineSystemSettings.maxCornersPerLifeline,
        });

        setPolylines((current) => {
          const nextLifeLineIndex = current.filter((shape) => shape.name.startsWith("Life Line")).length + 1;
          const lifeLineShapes = renderedLifeLines.flatMap((renderedLifeLine) => {
            if (renderedLifeLine.points.length < 4) return [];

            return [
              {
                id: `polyline-${crypto.randomUUID()}`,
                points: renderedLifeLine.points,
                closed: isClosedLifeLine && renderedLifeLines.length === 1,
                kind: "polyline" as const,
                layer: SAFETY_LAYER,
                entityStyleKind: "single-lifeline" as const,
                name:
                  lifeLineResult.lines.length > 1
                    ? `Life Line ${nextLifeLineIndex}.${renderedLifeLine.lineNumber}`
                    : `Life Line ${nextLifeLineIndex}`,
                strokeColor: polylineStyle.strokeColor,
                strokeWidth: polylineStyle.strokeWidth,
                disableFill: true,
                lifeLineVertices: renderedLifeLine.lifeLineVertices,
                lifeLineDesignPoints: renderedLifeLine.designPoints,
                lifeLineLineNumber: renderedLifeLine.lineNumber,
                ...lifeLineSystemMeta,
              },
            ];
          });

          return lifeLineShapes.length > 0 ? [...current, ...lifeLineShapes] : current;
        });
      });
      setActivePolyline(null);
      setDirectionLockAxis(null);
      clearOsnapTracking();
      return;
    }

    runHistoryAction(() => {
      const polylineStyle = getEntityTypeStyle("custom-flat-roof");
      setPolylines((current) => {
        const roofGroupMeta = createRoofGroupMeta("Flat", current, "polyline");
        const roofShape: Shape = {
          id: `polyline-${crypto.randomUUID()}`,
          points: committedPoints,
          closed: true,
          kind: "polyline",
          layer: ROOF_LAYER,
          entityStyleKind: "custom-flat-roof",
          name: getNextEntityName("polyline", current, dimensions),
          strokeColor: polylineStyle.strokeColor,
          strokeWidth: polylineStyle.strokeWidth,
          fallSide: true,
          ...roofGroupMeta,
        };
        pendingCreatedRoofSelectionIdsRef.current = [roofShape.id];
        return [...current, roofShape];
      });
    });
    setActivePolyline(null);
    setDirectionLockAxis(null);
    clearOsnapTracking();
  };

  const closeActivePolyline = (options: { disableFill?: boolean } = {}) => {
    const points = activePolylineRef.current;
    if (!points) return;

    const committedPoints = points.slice(0, -2);
    if (committedPoints.length < 4) {
      setActivePolyline(null);
      setDirectionLockAxis(null);
      clearOsnapTracking();
      return;
    }

    runHistoryAction(() => {
      const polylineStyle = getEntityTypeStyle("polyline");
      setPolylines((current) => [
        ...current,
        {
          id: `polyline-${crypto.randomUUID()}`,
          points: committedPoints,
          closed: true,
          kind: "polyline",
          layer: ROOF_LAYER,
          entityStyleKind: "polyline",
          name: getNextEntityName("polyline", current, dimensions),
          strokeColor: polylineStyle.strokeColor,
          strokeWidth: polylineStyle.strokeWidth,
          disableFill: options.disableFill,
        },
      ]);
    });
    setActivePolyline(null);
    setDirectionLockAxis(null);
    clearOsnapTracking();
  };

  const closeActiveHatchPolyline = () => {
    const points = activePolylineRef.current;
    if (!points) return;

    const committedPoints = points.slice(0, -2);
    if (committedPoints.length < 4) {
      setActivePolyline(null);
      setDirectionLockAxis(null);
      clearOsnapTracking();
      return;
    }

    runHistoryAction(() => {
      setPolylines((current) => [
        ...current,
        {
          ...buildHatchShapeFromPoints(committedPoints, { layer: ROOF_LAYER, kind: "polyline" }),
          name: getNextNamedEntityName("Hatch", current),
        },
      ]);
    });
    setActivePolyline(null);
    setDirectionLockAxis(null);
    clearOsnapTracking();
  };

  const closeActiveObstaclePolyline = () => {
    const points = activePolylineRef.current;
    if (!points) return;

    const committedPoints = points.slice(0, -2);
    if (committedPoints.length < 6) {
      setActivePolyline(null);
      setNumericEdit(null);
      setDirectionLockAxis(null);
      clearOsnapTracking();
      return;
    }

    runHistoryAction(() => {
      const polylineStyle = getEntityTypeStyle("polygonal-obstacle");
      setPolylines((current) => [
        ...current,
        {
          id: `polyline-${crypto.randomUUID()}`,
          points: committedPoints,
          closed: true,
          kind: "polyline",
          isObstacle: true,
          layer: ROOF_LAYER,
          entityStyleKind: "polygonal-obstacle",
          name: getNextObstacleName(current),
          strokeColor: polylineStyle.strokeColor,
          strokeWidth: polylineStyle.strokeWidth,
        },
      ]);
    });
    setActivePolyline(null);
    setActivePointSeries(null);
    setNumericEdit(null);
    setDirectionLockAxis(null);
    clearOsnapTracking();
  };

  const closeActiveHolePolyline = () => {
    const points = activePolylineRef.current;
    const host = activeHoleHost ? getShapeById(activeHoleHost.hostId) : null;
    if (!points || !isHoleHostShape(host)) return;

    const committedPoints = points.slice(0, -2);
    if (committedPoints.length < 6) {
      setActivePolyline(null);
      setNumericEdit(null);
      setDirectionLockAxis(null);
      clearOsnapTracking();
      return;
    }

    if (!isHoleInsideShape(host, committedPoints)) {
      setActivePolyline(null);
      setNumericEdit(null);
      setDirectionLockAxis(null);
      clearOsnapTracking();
      return;
    }

    runHistoryAction(() => {
      appendHoleToShape(host.id, committedPoints);
    });

    setSelectedIds([host.id]);
    setEditingId(null);
    setActivePolyline(null);
    setNumericEdit(null);
    setDirectionLockAxis(null);
    clearOsnapTracking();
  };

  const finalizeCircleHole = (circle: ActiveCircle) => {
    const host = activeHoleHost ? getShapeById(activeHoleHost.hostId) : null;
    const radius = getActiveCircleRadius(circle);
    if (!isHoleHostShape(host) || radius <= 0) {
      setActiveCircle(null);
      setNumericEdit(null);
      setDirectionLockAxis(null);
      return null;
    }

    const hole: CircleHole = {
      center: clonePoint(circle.center),
      radius,
    };
    if (!isHoleInsideShape(host, hole)) {
      setActiveCircle(null);
      setNumericEdit(null);
      setDirectionLockAxis(null);
      return null;
    }

    runHistoryAction(() => {
      appendCircleHoleToShape(host.id, hole);
    });

    setSelectedIds([host.id]);
    setEditingId(null);
    setActiveCircle(null);
    setNumericEdit(null);
    setDirectionLockAxis(null);
    setHoverSnap(null);
    clearOsnapTracking();
    return host.id;
  };

  useEffect(() => {
    const clearShiftLockTimer = () => {
      if (!shiftLockTimeoutRef.current) return;
      clearTimeout(shiftLockTimeoutRef.current);
      shiftLockTimeoutRef.current = null;
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const isTypingTarget =
        target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target?.isContentEditable;

      if (toolMode === "multiGableRoof" && multiGableRoofDraft && event.key === "Enter") {
        event.preventDefault();
        event.stopImmediatePropagation();
        commitMultiGableRoofDraft(multiGableRoofDraft);
        return;
      }

      if (backgroundReferenceInput && event.key === "Enter") {
        event.preventDefault();
        commitBackgroundReferenceScale();
        return;
      }

      if (numericEdit && event.key === "Enter" && !isTypingTarget) {
        event.preventDefault();
        commitNumericEdit();
        return;
      }

      if (
        event.key === "Enter" &&
        !isTypingTarget &&
        toolMode === "select" &&
        !editingId &&
        !selectionBox &&
        !isGripModeActive
      ) {
        const lastCommand = lastCommandRef.current;
        if (lastCommand && lastCommand !== "select") {
          event.preventDefault();
          handleToolChange(lastCommand);
        }
        return;
      }

      if (
        event.key === "Tab" &&
        !isTypingTarget &&
        toolMode === "select" &&
        !editingId &&
        !selectionBox &&
        selectedIds.length <= 1 &&
        selectionCycle &&
        selectionCycle.ids.length > 1
      ) {
        event.preventDefault();
        const availableIds = selectionCycle.ids.filter(
          (id) =>
            polylines.some((shape) => shape.id === id) ||
            dimensions.some((dimension) => dimension.id === id) ||
            constructionLines.some((line) => line.id === id),
        );
        if (availableIds.length <= 1) {
          setSelectionCycle(null);
          return;
        }

        const selectedIndex = selectedIds.length === 1 && selectedIds[0] ? availableIds.indexOf(selectedIds[0]) : -1;
        const baseIndex = selectedIndex >= 0 ? selectedIndex : selectionCycle.index;
        const step = event.shiftKey ? -1 : 1;
        const nextIndex = (baseIndex + step + availableIds.length) % availableIds.length;
        const nextId = availableIds[nextIndex];
        if (!nextId) return;
        setSelectedIds([nextId]);
        setEditingId(null);
        setSelectionCycle({
          point: selectionCycle.point,
          ids: availableIds,
          index: nextIndex,
        });
        return;
      }

      if (!isTypingTarget && (event.ctrlKey || event.metaKey)) {
        const key = event.key.toLowerCase();

        if (key === "z" && !event.shiftKey) {
          event.preventDefault();
          handleUndo();
          return;
        }

        if (key === "y" || (key === "z" && event.shiftKey)) {
          event.preventDefault();
          handleRedo();
          return;
        }
      }

      if (event.code === "Space" && !isTypingTarget) {
        event.preventDefault();
        setToolMode("select");
        setEditingId(null);
        setActivePolyline(null);
        setActiveRectangle(null);
        setActiveCircle(null);
        setActiveArc(null);
        setActiveRuler(null);
        setActiveDimension(null);
        setHoverSnap(null);
        setNumericEdit(null);
        clearOsnapTracking();
        return;
      }

      if ((event.key === "Delete" || event.key === "Backspace") && !isTypingTarget) {
        if (selectedIds.length > 0) {
          event.preventDefault();
          deleteSelected();
        }
        return;
      }

      if (event.key === "Escape") {
        if (gripContextMenu) {
          setGripContextMenu(null);
          return;
        }

        if (openToolGroupMenu) {
          setOpenToolGroupMenu(null);
          return;
        }

        if (isBackgroundCropMode) {
          cancelBackgroundCrop();
          return;
        }

        if (backgroundReferenceInput || isBackgroundReferenceMode) {
          cancelBackgroundReference();
          return;
        }

        clearOsnapTracking();

        if (multiGableRoofDraft) {
          setMultiGableRoofDraft(null);
          setToolMode("select");
          setHoverSnap(null);
          return;
        }

        if (roofHipValleyDraft) {
          setRoofHipValleyDraft(null);
          setToolMode("select");
          setHoverSnap(null);
          return;
        }

        if (isGripModeActive) {
          setToolMode("select");
          setEditingId(null);
          setDraggedGrip(null);
          segmentStretchRef.current = null;
          setDraggedSegmentStretch(null);
          setDraggedDimensionId(null);
          setInvalidGripFeedback(null);
          setGripContextMenu(null);
          setHoverSnap(null);
          setNumericEdit(null);
          return;
        }

        if (toolMode === "select") {
          if (selectionBox) {
            setSelectionBox(null);
            setHoverSnap(null);
            return;
          }

          if (selectedIds.length > 0) {
            setSelectedIds((current) => current.slice(0, -1));
            setSelectionCycle(null);
            setEditingId(null);
            setHoverSnap(null);
            return;
          }
        }

        if (
          (toolMode === "rectangle" ||
            toolMode === "gableRoof" ||
            toolMode === "curvedRoof" ||
            toolMode === "singlePitchedRoof" ||
            toolMode === "multiShedRoof") &&
          (numericEdit?.kind === "rectangle-height" || numericEdit?.kind === "multi-shed-count")
        ) {
          commitNumericEdit();
          return;
        }

        if (
          (moveState && (toolMode === "move" || toolMode === "copy")) ||
          (transformState && (toolMode === "scale" || toolMode === "rotate"))
        ) {
          cancelActiveTransform();
          setSelectedIds([]);
          setEditingId(null);
          return;
        }

        if (toolMode === "dimensionObject" && activeObjectDimension) {
          setActiveObjectDimension(null);
          setNumericEdit(null);
          setHoverSnap(null);
          return;
        }

        if (
          (toolMode === "move" || toolMode === "copy" || toolMode === "scale" || toolMode === "rotate") &&
          selectedIds.length > 0
        ) {
          setSelectedIds([]);
          setEditingId(null);
          return;
        }

        if (toolMode === "holePolyline") {
          if (activePolylineRef.current) {
            setActivePolyline(null);
            setNumericEdit(null);
            setDirectionLockAxis(null);
            setHoverSnap(null);
            return;
          }

          if (activeHoleHost) {
            setActiveHoleHost(null);
            setHoverSnap(null);
            return;
          }
        }

        if (toolMode === "holeCircle") {
          if (activeCircle) {
            setActiveCircle(null);
            setNumericEdit(null);
            setDirectionLockAxis(null);
            setHoverSnap(null);
            return;
          }

          if (activeHoleHost) {
            setActiveHoleHost(null);
            setHoverSnap(null);
            return;
          }
        }

        if (toolMode === "singlePointCircle") {
          setActiveCircle(null);
          setNumericEdit(null);
          setDirectionLockAxis(null);
          setHoverSnap(null);
          setToolMode("select");
          return;
        }

        if (numericEdit) {
          setNumericEdit(null);
          setActiveOffset(null);
          return;
        }

        if (toolMode === "buildPolyline") {
          closeActivePolyline({ disableFill: true });
          return;
        }

        if (toolMode === "hatch" && hatchToolMode === "build") {
          closeActiveHatchPolyline();
          return;
        }

        if (toolMode === "polyline" || toolMode === "singleLifeLine") {
          finalizeActivePolyline();
          return;
        }

        if (isRoofComponentToolMode(toolMode)) {
          finalizeActiveRoofComponentPolyline(toolMode);
          return;
        }

        if (toolMode === "roof" || toolMode === "multiGableRoof") {
          if (toolMode === "multiGableRoof") {
            closeActiveMultiGableRoof();
          } else {
            void closeActiveRoof();
          }
          return;
        }

        if (
          toolMode === "gableRoof" ||
          toolMode === "curvedRoof" ||
          toolMode === "singlePitchedRoof" ||
          toolMode === "multiShedRoof"
        ) {
          setActiveRectangle(null);
          setNumericEdit(null);
          setDirectionLockAxis(null);
          setHoverSnap(null);
          return;
        }

        if (toolMode === "obstaclePolyline") {
          closeActiveObstaclePolyline();
          return;
        }

        if (toolMode === "rectangle") {
          setActiveRectangle(null);
          return;
        }

        if (toolMode === "circle") {
          setActiveCircle(null);
          return;
        }

        if (toolMode === "arc") {
          setActiveArc(null);
          setHoverSnap(null);
          return;
        }

        if (toolMode === "obstacleCircle") {
          setActiveCircle(null);
          return;
        }

        if (toolMode === "ruler") {
          setActiveRuler(null);
          setHoverSnap(null);
          return;
        }

        if (toolMode === "dimension") {
          setActiveDimension(null);
          return;
        }

        setEditingId(null);
        return;
      }

      if (!isTypingTarget) {
        const shortcutCommand = getShortcutCommandForEvent(event);
        if (shortcutCommand) {
          event.preventDefault();
          if (shortcutCommand === "fit") {
            fitToScreen();
            return;
          }
          handleToolChange(shortcutCommand);
          return;
        }
      }

      if (
        event.key === "Shift" &&
        !event.repeat &&
        (!numericEdit || ((toolMode === "move" || toolMode === "copy") && moveState))
      ) {
        clearShiftLockTimer();
        if (
          (toolMode === "polyline" ||
            toolMode === "singleLifeLine" ||
            toolMode === "buildPolyline" ||
            (toolMode === "hatch" && hatchToolMode === "build") ||
            toolMode === "roof" ||
            toolMode === "multiGableRoof" ||
            isRoofComponentToolMode(toolMode)) &&
          activePolylineRef.current
        ) {
          shiftLockTimeoutRef.current = setTimeout(() => {
            const current = activePolylineRef.current;
            if (!current) return;

            const anchor = getLastCommittedPoint(current);
            if (!anchor) return;

            const previewX = current[current.length - 2];
            const previewY = current[current.length - 1];
            if (previewX == null || previewY == null) return;
            const preview = {
              x: previewX,
              y: previewY,
            };
            const deltaX = preview.x - anchor.x;
            const deltaY = preview.y - anchor.y;

            setDirectionLockAxis(Math.abs(deltaX) >= Math.abs(deltaY) ? "horizontal" : "vertical");
          }, 500);
        } else if (isBackgroundReferenceMode && backgroundReferenceLine && !backgroundReferenceInput) {
          shiftLockTimeoutRef.current = setTimeout(() => {
            const preview = backgroundReferenceLine.end ?? backgroundReferenceLine.pointer;
            const deltaX = preview.x - backgroundReferenceLine.start.x;
            const deltaY = preview.y - backgroundReferenceLine.start.y;

            setDirectionLockAxis(Math.abs(deltaX) >= Math.abs(deltaY) ? "horizontal" : "vertical");
          }, 500);
        } else if ((toolMode === "move" || toolMode === "copy") && moveState) {
          shiftLockTimeoutRef.current = setTimeout(() => {
            const previewEdit = numericEdit;
            const preview = previewEdit && previewEdit.kind === "move" ? previewEdit.position : moveState.anchorPoint;
            const deltaX = preview.x - moveState.anchorPoint.x;
            const deltaY = preview.y - moveState.anchorPoint.y;

            setDirectionLockAxis(Math.abs(deltaX) >= Math.abs(deltaY) ? "horizontal" : "vertical");
          }, 500);
        } else if (toolMode === "ruler" && activeRuler && activeRuler.end == null) {
          const currentRuler = activeRuler;
          shiftLockTimeoutRef.current = setTimeout(() => {
            const preview = currentRuler.pointer;
            const deltaX = preview.x - currentRuler.start.x;
            const deltaY = preview.y - currentRuler.start.y;

            setDirectionLockAxis(Math.abs(deltaX) >= Math.abs(deltaY) ? "horizontal" : "vertical");
          }, 500);
        }
      }
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      if (event.key !== "Shift") return;
      clearShiftLockTimer();
      setDirectionLockAxis(null);
    };

    window.addEventListener("keydown", handleKeyDown, { capture: true });
    window.addEventListener("keyup", handleKeyUp);
    return () => {
      clearShiftLockTimer();
      window.removeEventListener("keydown", handleKeyDown, { capture: true });
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [
    selectedIds,
    selectionCycle,
    editingId,
    selectionBox,
    toolMode,
    viewportSize,
    polylines,
    dimensions,
    constructionLines,
    numericEdit,
    multiGableRoofDraft,
    activeCircle,
    backgroundReferenceInput,
    backgroundReferenceLine,
    isBackgroundReferenceMode,
    isBackgroundCropMode,
    backgroundAsset,
    undoStack,
    redoStack,
  ]);

  const getPointerPosition = (event: Konva.KonvaEventObject<MouseEvent>) => {
    const stage = event.target.getStage();
    const pointer = stage?.getPointerPosition();
    if (!pointer) return null;

    return {
      x: (pointer.x - viewport.x) / viewport.scale,
      y: (pointer.y - viewport.y) / viewport.scale,
    };
  };

  const getCurrentPointerWorldPosition = (): CanvasPoint => {
    const pointer = stageRef.current?.getPointerPosition();
    if (pointer) {
      return {
        x: (pointer.x - viewport.x) / viewport.scale,
        y: (pointer.y - viewport.y) / viewport.scale,
      };
    }

    return {
      x: (viewportSize.width / 2 - viewport.x) / viewport.scale,
      y: (viewportSize.height / 2 - viewport.y) / viewport.scale,
    };
  };

  const getDistance = (pointA: CanvasPoint, pointB: CanvasPoint) => {
    return Math.hypot(pointA.x - pointB.x, pointA.y - pointB.y);
  };

  const getNormalizedDirection = (from: CanvasPoint, to: CanvasPoint) => {
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const length = Math.hypot(dx, dy);

    if (length === 0) {
      return { x: 1, y: 0 };
    }

    return {
      x: dx / length,
      y: dy / length,
    };
  };

  const normalizeAngleDegrees = (angle: number) => {
    const normalized = ((angle % 360) + 360) % 360;
    return normalized === 360 ? 0 : normalized;
  };

  const getDirectionAngleDegrees = (direction: CanvasPoint) =>
    normalizeAngleDegrees((Math.atan2(-direction.y, direction.x) * 180) / Math.PI);

  const getDirectionValue = (direction: CanvasPoint) => formatLocalizedNumber(getDirectionAngleDegrees(direction), 2);

  const getDirectionVectorFromAngle = (angleDegrees: number): CanvasPoint => {
    const angle = (normalizeAngleDegrees(angleDegrees) * Math.PI) / 180;
    return {
      x: Math.cos(angle),
      y: -Math.sin(angle),
    };
  };

  const numericEditSupportsDirection = (
    edit: NumericEditState | null,
  ): edit is Extract<
    NumericEditState,
    {
      kind:
        | "polyline"
        | "rectangle-base"
        | "rectangle-height"
        | "circle"
        | "arc"
        | "move"
        | "grip"
        | "segment-stretch"
        | "tracking";
    }
  > =>
    !!edit &&
    edit.kind !== "offset" &&
    edit.kind !== "single-anchor-point" &&
    edit.kind !== "dimension-object" &&
    edit.kind !== "multi-shed-count";

  const clearOsnapTracking = () => {
    if (osnapTrackingTimeoutRef.current) {
      clearTimeout(osnapTrackingTimeoutRef.current);
      osnapTrackingTimeoutRef.current = null;
    }
    setOsnapTrackingLines([]);
    setNumericEdit((current) => (current?.kind === "tracking" ? null : current));
  };

  const canStartFromOsnapTracking = () =>
    !activePolyline &&
    !activeRectangle &&
    !activeCircle &&
    !activeArc &&
    !activePointSeries &&
    !activeRuler &&
    !activeDimension &&
    !activeObjectDimension &&
    !moveState &&
    !transformState &&
    !activeOffset &&
    !isBackgroundReferenceMode &&
    !isBackgroundCropMode &&
    (toolMode === "polyline" ||
      toolMode === "singleLifeLine" ||
      toolMode === "buildPolyline" ||
      (toolMode === "hatch" && hatchToolMode === "build") ||
      isRoofComponentToolMode(toolMode) ||
      toolMode === "roof" ||
      toolMode === "multiGableRoof" ||
      toolMode === "obstaclePolyline" ||
      toolMode === "holePolyline" ||
      toolMode === "rectangle" ||
      toolMode === "gableRoof" ||
      toolMode === "curvedRoof" ||
      toolMode === "singlePitchedRoof" ||
      toolMode === "multiShedRoof" ||
      toolMode === "circle" ||
      toolMode === "arc" ||
      toolMode === "ruler" ||
      toolMode === "dimension");

  const createOsnapTrackingLines = (origin: CanvasPoint) => {
    const extension = Math.max(
      DEFAULT_VIEW_HEIGHT,
      viewportSize.width / viewport.scale,
      viewportSize.height / viewport.scale,
    );
    const horizontal: OsnapTrackingLine = {
      id: `tracking-horizontal-${origin.x}-${origin.y}`,
      origin,
      start: { x: origin.x - extension, y: origin.y },
      end: { x: origin.x + extension, y: origin.y },
    };
    const vertical: OsnapTrackingLine = {
      id: `tracking-vertical-${origin.x}-${origin.y}`,
      origin,
      start: { x: origin.x, y: origin.y - extension },
      end: { x: origin.x, y: origin.y + extension },
    };

    setOsnapTrackingLines((current) => {
      const exists = current.some(
        (line) => Math.abs(line.origin.x - origin.x) <= 0.0001 && Math.abs(line.origin.y - origin.y) <= 0.0001,
      );
      return exists ? current : [...current, horizontal, vertical];
    });

    if (!canStartFromOsnapTracking()) return;

    setNumericEdit((current) => {
      if (current && current.kind !== "tracking") return current;
      return {
        kind: "tracking",
        origin,
        direction: { x: 1, y: 0 },
        directionValue: getDirectionValue({ x: 1, y: 0 }),
        position: origin,
        value: formatMeasurementValue(0),
      };
    });
    focusNumericField("value");
  };

  useEffect(() => {
    if (osnapTrackingTimeoutRef.current) {
      clearTimeout(osnapTrackingTimeoutRef.current);
      osnapTrackingTimeoutRef.current = null;
    }

    if (!hoverSnap) return;
    if (toolMode === "select" && !isGripModeActive && !draggedGrip && !draggedSegmentStretch) return;

    const origin = { x: hoverSnap.x, y: hoverSnap.y };
    osnapTrackingTimeoutRef.current = setTimeout(() => {
      createOsnapTrackingLines(origin);
      osnapTrackingTimeoutRef.current = null;
    }, OSNAP_TRACKING_DELAY_MS);

    return () => {
      if (osnapTrackingTimeoutRef.current) {
        clearTimeout(osnapTrackingTimeoutRef.current);
        osnapTrackingTimeoutRef.current = null;
      }
    };
  }, [hoverSnap?.x, hoverSnap?.y, hoverSnap?.type, toolMode, isGripModeActive, draggedGrip, draggedSegmentStretch]);

  useEffect(
    () => () => {
      if (osnapTrackingTimeoutRef.current) {
        clearTimeout(osnapTrackingTimeoutRef.current);
      }
    },
    [],
  );

  const getShapeGripPoints = (shape: Shape): ShapeGripDescriptor[] =>
    getShapeGripPointsRule(shape) as ShapeGripDescriptor[];

  const getLifeLineDesignGripPoint = (shape: Shape, vertexIndex: number, holeIndex?: number) => {
    if (
      shape.kind === "circle" ||
      shape.kind === "arc" ||
      holeIndex != null ||
      !shape.lifeLineDesignPoints ||
      shape.lifeLineDesignPoints.length === 0
    ) {
      return null;
    }

    const point = shape.lifeLineDesignPoints[Math.floor(vertexIndex / 2)] ?? shape.lifeLineDesignPoints[0];
    return point ? clonePoint(point) : null;
  };

  const getGripAnchorPoint = (
    shape: Shape,
    vertexIndex: number,
    holeIndex?: number,
    holeGripKind?: HoleGripKind,
  ): CanvasPoint => {
    const lifeLineDesignPoint = getLifeLineDesignGripPoint(shape, vertexIndex, holeIndex);
    if (lifeLineDesignPoint) return lifeLineDesignPoint;

    return getGripAnchorPointRule(shape, vertexIndex, holeIndex, holeGripKind) as CanvasPoint;
  };

  const getGripExcludedSnapSegments = (
    shape: Shape,
    vertexIndex: number,
    holeIndex?: number,
    holeGripKind?: HoleGripKind,
  ): SnapExcludedSegment[] => {
    if (shape.kind === "circle" || shape.kind === "arc") return [];
    if (holeGripKind && holeGripKind !== "polygon-vertex") return [];

    const designPoints =
      holeIndex == null && shape.lifeLineDesignPoints && shape.lifeLineDesignPoints.length > 0
        ? shape.lifeLineDesignPoints
        : null;
    const contour = designPoints
      ? flattenCanvasPoints(designPoints)
      : holeIndex == null
        ? shape.points
        : shape.holes?.[holeIndex];
    if (!contour || contour.length < 4) return [];

    const vertexCount = Math.floor(contour.length / 2);
    const vertexPosition = Math.floor(vertexIndex / 2);
    if (vertexPosition < 0 || vertexPosition >= vertexCount) return [];

    const getVertex = (position: number): CanvasPoint | null => {
      const x = contour[position * 2];
      const y = contour[position * 2 + 1];
      return x == null || y == null ? null : { x, y };
    };
    const segments: SnapExcludedSegment[] = [];

    if (vertexPosition > 0) {
      const start = getVertex(vertexPosition - 1);
      const end = getVertex(vertexPosition);
      if (start && end) segments.push({ start, end });
    } else if (shape.closed && vertexCount > 2) {
      const start = getVertex(vertexCount - 1);
      const end = getVertex(0);
      if (start && end) segments.push({ start, end });
    }

    if (vertexPosition < vertexCount - 1) {
      const start = getVertex(vertexPosition);
      const end = getVertex(vertexPosition + 1);
      if (start && end) segments.push({ start, end });
    } else if (shape.closed && vertexCount > 2) {
      const start = getVertex(vertexCount - 1);
      const end = getVertex(0);
      if (start && end) segments.push({ start, end });
    }

    return segments;
  };

  const getGripCurrentPoint = (
    shape: Shape,
    vertexIndex: number,
    holeIndex?: number,
    holeGripKind?: HoleGripKind,
  ): CanvasPoint => {
    const lifeLineDesignPoint = getLifeLineDesignGripPoint(shape, vertexIndex, holeIndex);
    if (lifeLineDesignPoint) return lifeLineDesignPoint;

    return getGripCurrentPointRule(shape, vertexIndex, holeIndex, holeGripKind) as CanvasPoint;
  };

  const getLifeLineSystemSettings = (shape: PolylineLikeShape) => {
    const systemId = shape.safetySystemId ?? selectedSafetySystem;
    const selectedSystem = settings?.systems.find((system) => system.id === systemId);
    return normalizeSafetySystemSettings(
      safetySystemSettings[systemId] ?? getDefaultSafetySystemSettings(selectedSystem),
    );
  };

  const buildRenderedLifeLineShapePatch = (
    shape: PolylineLikeShape,
    designPoints: CanvasPoint[],
    options: { disableAutoSplit?: boolean } = {},
  ): Pick<
    PolylineLikeShape,
    "points" | "closed" | "lifeLineVertices" | "lifeLineDesignPoints" | "lifeLineLineNumber"
  > | null => {
    if (designPoints.length < 2) return null;

    const systemSettings = getLifeLineSystemSettings(shape);
    const { renderedLifeLines } = calculateRenderedLifeLines(designPoints, {
      closed: shape.closed,
      systemMaxSpan: systemSettings.maxSpacing,
      chamferAcuteAngles: systemSettings.chamferAcuteAngles,
      chamferSegmentLength: systemSettings.chamferSegmentLength,
      maxSingleLineLength: options.disableAutoSplit ? 0 : systemSettings.maxSingleLineLength,
      maxCornersPerLifeline: options.disableAutoSplit ? 0 : systemSettings.maxCornersPerLifeline,
    });
    const renderedLifeLine =
      renderedLifeLines.find((line) => line.lineNumber === shape.lifeLineLineNumber) ?? renderedLifeLines[0];

    if (!renderedLifeLine) return null;

    return {
      points: renderedLifeLine.points,
      closed: shape.closed,
      lifeLineVertices: renderedLifeLine.lifeLineVertices,
      lifeLineDesignPoints: renderedLifeLine.designPoints,
      lifeLineLineNumber: renderedLifeLine.lineNumber,
    };
  };

  const buildLifeLineShapesFromDesignPoints = (
    baseShape: PolylineLikeShape,
    designPoints: CanvasPoint[],
    closed: boolean,
    namePrefix: string,
    existingShapes: Shape[],
    options: { disableAutoSplit?: boolean } = {},
  ): PolylineLikeShape[] => {
    if (designPoints.length < 2) return [];

    const systemSettings = getLifeLineSystemSettings(baseShape);
    const { result, renderedLifeLines } = calculateRenderedLifeLines(designPoints, {
      closed,
      systemMaxSpan: systemSettings.maxSpacing,
      chamferAcuteAngles: systemSettings.chamferAcuteAngles,
      chamferSegmentLength: systemSettings.chamferSegmentLength,
      maxSingleLineLength: options.disableAutoSplit ? 0 : systemSettings.maxSingleLineLength,
      maxCornersPerLifeline: options.disableAutoSplit ? 0 : systemSettings.maxCornersPerLifeline,
    });

    const baseName = namePrefix.trim() || getNextNamedEntityName("Life Line", existingShapes);
    return renderedLifeLines.flatMap((renderedLifeLine) => {
      if (renderedLifeLine.points.length < 4) return [];

      return [
        {
          ...baseShape,
          id: `polyline-${crypto.randomUUID()}`,
          points: renderedLifeLine.points,
          closed: closed && renderedLifeLines.length === 1,
          name: result.lines.length > 1 ? `${baseName}.${renderedLifeLine.lineNumber}` : baseName,
          lifeLineVertices: renderedLifeLine.lifeLineVertices,
          lifeLineDesignPoints: renderedLifeLine.designPoints,
          lifeLineLineNumber: renderedLifeLine.lineNumber,
        },
      ];
    });
  };

  const getLifeLineGripPoints = (
    shape: PolylineLikeShape,
    _fallbackGripPoints: ShapeGripDescriptor[],
  ): ShapeGripDescriptor[] => {
    if (shape.lifeLineDesignPoints && shape.lifeLineDesignPoints.length > 0) {
      return shape.lifeLineDesignPoints.flatMap((point, index) =>
        point
          ? [
              {
                point,
                vertexIndex: index * 2,
                gripKind: "vertex" as const,
              },
            ]
          : [],
      );
    }

    const editableVertices = (shape.lifeLineVertices ?? []).filter((vertex) => vertex.kind === "primary");
    if (editableVertices.length === 0) {
      return [];
    }

    const usedVertexIndices = new Set<number>();
    return editableVertices.flatMap((vertex) => {
      for (let index = 0; index < shape.points.length - 1; index += 2) {
        if (usedVertexIndices.has(index)) continue;

        const point = getPointFromCoordinateArray(shape.points, index);
        if (!point) continue;
        if (!areCanvasPointsEqual(point, vertex)) continue;

        usedVertexIndices.add(index);
        return [
          {
            point,
            vertexIndex: index,
            gripKind: "vertex" as const,
          },
        ];
      }

      return [];
    });
  };

  const isHoleGripUpdateValid = (
    shape: Shape,
    vertexIndex: number,
    nextPoint: CanvasPoint,
    linkedVertexIndices?: number[],
    holeIndex?: number,
    holeGripKind?: HoleGripKind,
  ) =>
    isHoleGripUpdateValidRule(
      shape,
      vertexIndex,
      nextPoint,
      getDistance,
      isHoleInsideShape,
      linkedVertexIndices,
      holeIndex,
      holeGripKind,
    );

  const applyShapeGripUpdate = (
    shape: Shape,
    vertexIndex: number,
    nextPoint: CanvasPoint,
    linkedVertexIndices?: number[],
    holeIndex?: number,
    holeGripKind?: HoleGripKind,
  ) => {
    if (holeIndex != null && holeGripKind === "circle-center") {
      updateCircleHole(shape.id, holeIndex, nextPoint, "center");
      return;
    }

    if (holeIndex != null && holeGripKind === "circle-radius") {
      updateCircleHole(shape.id, holeIndex, nextPoint, "radius");
      return;
    }

    if (holeIndex != null) {
      updatePolylineVertex(shape.id, vertexIndex, nextPoint, holeIndex);
      return;
    }

    if (shape.kind === "rectangle") {
      updateRectangleVertex(shape.id, vertexIndex, nextPoint);
      return;
    }

    if (shape.kind === "circle") {
      updateCircleRadius(shape.id, nextPoint);
      return;
    }

    if (shape.kind === "arc") {
      updateArcGrip(shape.id, vertexIndex, nextPoint);
      return;
    }

    updatePolylineVertex(shape.id, vertexIndex, nextPoint, holeIndex, linkedVertexIndices);
  };

  const updateGripNumericEditState = (
    shape: Shape,
    vertexIndex: number,
    anchor: CanvasPoint,
    position: CanvasPoint,
    linkedVertexIndices?: number[],
    holeIndex?: number,
    holeGripKind?: HoleGripKind,
  ) => {
    const direction = getNormalizedDirection(anchor, position);
    setNumericEdit({
      kind: "grip",
      entityId: shape.id,
      shapeKind: shape.kind,
      vertexIndex,
      linkedVertexIndices,
      holeIndex,
      holeGripKind,
      anchor,
      direction,
      directionValue: getDirectionValue(direction),
      position,
      value: formatMeasurementValue(getDistance(anchor, position)),
    });
  };

  const resolveGripPoint = (
    anchor: CanvasPoint,
    point: CanvasPoint,
    shiftKey: boolean,
    excludedPoint?: CanvasPoint | CanvasPoint[] | null,
    excludedSegments?: SnapExcludedSegment[] | null,
    allowGridFallback = false,
    requireIdentifiedLinearOsnap = false,
    excludedOsnapTypes: OsnapType[] = [],
  ) => {
    const excludedSnapPoints = [
      anchor,
      ...(Array.isArray(excludedPoint) ? excludedPoint : excludedPoint ? [excludedPoint] : []),
    ];
    const resolved = resolveSnapPoint(
      point,
      null,
      null,
      null,
      excludedSnapPoints,
      allowGridFallback,
      anchor,
      true,
      excludedSegments,
      requireIdentifiedLinearOsnap,
      excludedOsnapTypes,
    );
    let nextPoint = resolved.point;

    if (shiftKey) {
      nextPoint = applyPersistentDirectionLock(anchor, nextPoint, shiftKey);
    }

    return {
      point: nextPoint,
      osnap: resolved.osnap,
    };
  };

  const renderEntityVertexMarker = (
    kind: EntityStyleKind,
    point: CanvasPoint,
    key: string,
    extraProps: Record<string, unknown> = {},
    interactive = false,
    variant: "vertex" | "insert" = "vertex",
    visualOverrides?: Partial<
      Pick<EntityTypeStyle, "vertexColor" | "vertexSize" | "vertexShape" | "gripColor" | "gripSize">
    >,
  ) => {
    const style = getEntityTypeStyle(kind);
    const visualColor = interactive
      ? (visualOverrides?.gripColor ?? style.gripColor)
      : (visualOverrides?.vertexColor ?? style.vertexColor);
    const visualSize = interactive
      ? (visualOverrides?.gripSize ?? style.gripSize)
      : (visualOverrides?.vertexSize ?? style.vertexSize);
    const vertexShape = visualOverrides?.vertexShape ?? style.vertexShape;
    const size = getScreenInvariantSize(variant === "insert" ? Math.max(3, visualSize - 1.5) : visualSize);
    const hitRadius = getScreenInvariantSize(Math.max(visualSize, GRIP_HIT_STROKE / 2));

    return (
      <Group key={key} x={point.x} y={point.y} {...extraProps}>
        {interactive && <Circle radius={hitRadius} fill="rgba(0,0,0,0.01)" stroke="rgba(0,0,0,0)" />}
        {vertexShape === "circle" ? (
          <Circle
            radius={size}
            fill={variant === "insert" ? "#ffffff" : visualColor}
            stroke={variant === "insert" ? visualColor : "#ffffff"}
            strokeWidth={getScreenInvariantSize(1)}
            listening={false}
          />
        ) : vertexShape === "square" ? (
          <Line
            points={[-size, -size, size, -size, size, size, -size, size]}
            closed
            fill={variant === "insert" ? "#ffffff" : visualColor}
            stroke={variant === "insert" ? visualColor : "#ffffff"}
            strokeWidth={getScreenInvariantSize(1)}
            lineJoin="round"
            listening={false}
          />
        ) : (
          <>
            <Line
              points={[-size, -size, size, size]}
              stroke={visualColor}
              strokeWidth={getScreenInvariantSize(2)}
              lineCap="round"
              listening={false}
            />
            <Line
              points={[-size, size, size, -size]}
              stroke={visualColor}
              strokeWidth={getScreenInvariantSize(2)}
              lineCap="round"
              listening={false}
            />
          </>
        )}
      </Group>
    );
  };

  const getShapeById = (entityId: string) => {
    return polylines.find((shape) => shape.id === entityId && isShapeVisible(shape)) ?? null;
  };

  const isHoleHostShape = (shape: Shape | null): shape is PolylineLikeShape =>
    !!shape && shape.kind !== "circle" && shape.kind !== "arc" && shape.closed && !shape.isObstacle;

  const getObstacleShapes = (excludedShapeId?: string | null) =>
    polylines.filter((shape) => shape.isObstacle && shape.id !== excludedShapeId);

  const isPointInsideShapeArea = (shape: Shape, point: CanvasPoint) => {
    if (shape.kind === "circle") {
      return getDistance(point, shape.center) < shape.radius - 0.0001;
    }

    if (shape.kind === "arc") {
      return false;
    }

    if (!shape.closed || !isPointInsideClosedPolyline(point, shape.points)) {
      return false;
    }

    if ((shape.holes ?? []).some((hole) => isPointInsideClosedPolyline(point, hole))) {
      return false;
    }

    if ((shape.circleHoles ?? []).some((hole) => getDistance(point, hole.center) < hole.radius)) {
      return false;
    }

    return true;
  };

  const getHatchHostShapeAtPoint = (point: CanvasPoint): PolylineLikeShape | null => {
    const topToBottomLayers = [...normalizeLayerOrder(layerOrder)].reverse();

    for (const layer of topToBottomLayers) {
      const layerShapes = polylines.filter(
        (shape): shape is PolylineLikeShape =>
          getShapeLayer(shape) === layer &&
          isShapeInteractive(shape) &&
          shape.kind !== "circle" &&
          shape.kind !== "arc" &&
          shape.closed &&
          !shape.isObstacle &&
          shape.entityStyleKind !== "hatch",
      );

      for (let index = layerShapes.length - 1; index >= 0; index -= 1) {
        const shape = layerShapes[index];
        if (shape && isPointInsideShapeArea(shape, point)) {
          return shape;
        }
      }
    }

    return null;
  };

  const isLifeLineShape = (shape: Shape): shape is PolylineLikeShape =>
    shape.kind !== "circle" &&
    shape.kind !== "arc" &&
    (shape.entityStyleKind === "single-lifeline" || (shape.lifeLineVertices?.length ?? 0) > 0);

  const isCircleFullyInsideShapeArea = (hostShape: PolylineLikeShape, shape: CircleShape) => {
    const { center, radius } = shape;
    return (
      isPointInsideShapeArea(hostShape, center) &&
      isPointInsideShapeArea(hostShape, { x: center.x + radius, y: center.y }) &&
      isPointInsideShapeArea(hostShape, { x: center.x - radius, y: center.y }) &&
      isPointInsideShapeArea(hostShape, { x: center.x, y: center.y + radius }) &&
      isPointInsideShapeArea(hostShape, { x: center.x, y: center.y - radius })
    );
  };

  const isClosedForHatch = (shape: PolylineLikeShape) => shape.closed || isLifeLineShape(shape);

  const isPolylineFullyInsideShapeArea = (hostShape: PolylineLikeShape, shape: PolylineLikeShape) =>
    getRenderablePolylineVertices(shape.points, isClosedForHatch(shape)).every((vertex) =>
      isPointInsideShapeArea(hostShape, vertex),
    );

  const getHatchExclusionContours = (hostShape: PolylineLikeShape) => {
    const polygonHoles = hostShape.holes?.map((hole) => [...hole]) ?? [];
    const circleHoles = hostShape.circleHoles?.map(cloneCircleHole) ?? [];

    for (const shape of polylines) {
      const isLifeLineExclusion = isLifeLineShape(shape);
      if (
        shape.id === hostShape.id ||
        shape.entityStyleKind === "hatch" ||
        !isShapeVisible(shape) ||
        (getShapeLayer(shape) !== getShapeLayer(hostShape) && !isLifeLineExclusion)
      ) {
        continue;
      }

      if (shape.kind === "arc") continue;

      if (shape.kind === "circle") {
        if (isCircleFullyInsideShapeArea(hostShape, shape)) {
          circleHoles.push(cloneCircleHole({ center: shape.center, radius: shape.radius }));
        }
        continue;
      }

      if (!isClosedForHatch(shape)) continue;

      if (isPolylineFullyInsideShapeArea(hostShape, shape)) {
        const holePoints = flattenCanvasPoints(getRenderablePolylineVertices(shape.points, isClosedForHatch(shape)));
        if (holePoints.length >= 6) {
          polygonHoles.push(holePoints);
        }
      }
    }

    return {
      polygonHoles,
      circleHoles,
    };
  };

  const createHatchFromPickedShape = (point: CanvasPoint) => {
    const hostShape = getHatchHostShapeAtPoint(point);
    if (!hostShape) return false;
    const { polygonHoles, circleHoles } = getHatchExclusionContours(hostShape);

    runHistoryAction(() => {
      setPolylines((current) => [
        ...current,
        {
          ...buildHatchShapeFromPoints(hostShape.points, {
            kind: hostShape.kind,
            layer: getShapeLayer(hostShape),
            holes: polygonHoles,
            circleHoles,
          }),
          name: getNextNamedEntityName("Hatch", current),
        },
      ]);
    });

    return true;
  };

  const doesCircleIntersectObstacles = (center: CanvasPoint, radius: number, excludedShapeId?: string | null) => {
    const safeRadius = Math.max(0, radius);

    return getObstacleShapes(excludedShapeId).some((shape) => {
      if (shape.kind === "circle") {
        return getDistance(center, shape.center) < safeRadius + shape.radius - 0.0001;
      }

      if (shape.kind === "arc") {
        return false;
      }

      if (isPointInsideShapeArea(shape, center)) {
        return true;
      }

      const vertices = getPolylineVertices(shape.points);
      if (vertices.some((vertex) => getDistance(vertex, center) < safeRadius - 0.0001)) {
        return true;
      }

      return getContourSegments(shape.points).some(
        ([segmentStart, segmentEnd]) => getDistanceToSegment(center, segmentStart, segmentEnd) < safeRadius - 0.0001,
      );
    });
  };

  const buildCircularHolePoints = (center: CanvasPoint, radius: number, segments = 48) => {
    const safeRadius = Math.max(0, radius);
    const safeSegments = Math.max(12, Math.round(segments));
    const points: number[] = [];

    for (let index = 0; index < safeSegments; index += 1) {
      const angle = (index / safeSegments) * Math.PI * 2;
      points.push(center.x + Math.cos(angle) * safeRadius, center.y + Math.sin(angle) * safeRadius);
    }

    return points;
  };

  const getCircleHoleOutlinePoints = (hole: CircleHole) => buildCircularHolePoints(hole.center, hole.radius);

  const getContourSegments = (points: number[]) => {
    const vertices = getPolylineVertices(points);
    if (vertices.length < 2) return [] as Array<[CanvasPoint, CanvasPoint]>;

    const segments: Array<[CanvasPoint, CanvasPoint]> = [];
    for (let index = 0; index < vertices.length; index += 1) {
      const start = vertices[index];
      const end = vertices[(index + 1) % vertices.length];
      if (!start || !end) continue;
      segments.push([start, end]);
    }
    return segments;
  };

  const getOrientation = (first: CanvasPoint, second: CanvasPoint, third: CanvasPoint) => {
    const value = (second.y - first.y) * (third.x - second.x) - (second.x - first.x) * (third.y - second.y);
    if (Math.abs(value) < 0.0001) return 0;
    return value > 0 ? 1 : 2;
  };

  const isPointOnSegment = (point: CanvasPoint, start: CanvasPoint, end: CanvasPoint) =>
    point.x <= Math.max(start.x, end.x) + 0.0001 &&
    point.x >= Math.min(start.x, end.x) - 0.0001 &&
    point.y <= Math.max(start.y, end.y) + 0.0001 &&
    point.y >= Math.min(start.y, end.y) - 0.0001;

  const doSegmentsIntersect = (
    firstStart: CanvasPoint,
    firstEnd: CanvasPoint,
    secondStart: CanvasPoint,
    secondEnd: CanvasPoint,
  ) => {
    const orientation1 = getOrientation(firstStart, firstEnd, secondStart);
    const orientation2 = getOrientation(firstStart, firstEnd, secondEnd);
    const orientation3 = getOrientation(secondStart, secondEnd, firstStart);
    const orientation4 = getOrientation(secondStart, secondEnd, firstEnd);

    if (orientation1 !== orientation2 && orientation3 !== orientation4) {
      return true;
    }

    if (orientation1 === 0 && isPointOnSegment(secondStart, firstStart, firstEnd)) return true;
    if (orientation2 === 0 && isPointOnSegment(secondEnd, firstStart, firstEnd)) return true;
    if (orientation3 === 0 && isPointOnSegment(firstStart, secondStart, secondEnd)) return true;
    if (orientation4 === 0 && isPointOnSegment(firstEnd, secondStart, secondEnd)) return true;

    return false;
  };

  const doClosedContoursIntersect = (first: number[], second: number[]) => {
    const firstSegments = getContourSegments(first);
    const secondSegments = getContourSegments(second);

    return firstSegments.some(([firstStart, firstEnd]) =>
      secondSegments.some(([secondStart, secondEnd]) =>
        doSegmentsIntersect(firstStart, firstEnd, secondStart, secondEnd),
      ),
    );
  };

  const doesCircleHoleIntersectContour = (hole: CircleHole, contour: number[]) => {
    if (isPointInsideClosedPolyline(hole.center, contour)) {
      return true;
    }

    const contourVertices = getPolylineVertices(contour);
    if (contourVertices.some((vertex) => getDistance(vertex, hole.center) < hole.radius)) {
      return true;
    }

    return getContourSegments(contour).some(
      ([start, end]) => getDistanceToSegment(hole.center, start, end) < hole.radius,
    );
  };

  const appendHoleToShape = (shapeId: string, holePoints: number[]) => {
    setPolylines((current) =>
      current.map((shape) =>
        shape.id === shapeId && shape.kind !== "circle" && shape.kind !== "arc"
          ? {
              ...shape,
              holes: [...(shape.holes ?? []), [...holePoints]],
            }
          : shape,
      ),
    );
  };

  const appendCircleHoleToShape = (shapeId: string, hole: CircleHole) => {
    setPolylines((current) =>
      current.map((shape) =>
        shape.id === shapeId && shape.kind !== "circle" && shape.kind !== "arc"
          ? {
              ...shape,
              circleHoles: [...(shape.circleHoles ?? []), cloneCircleHole(hole)],
            }
          : shape,
      ),
    );
  };

  const isHoleInsideShape = (
    shape: PolylineLikeShape,
    hole: number[] | CircleHole,
    options?: {
      ignorePolygonHoleIndex?: number;
      ignoreCircleHoleIndex?: number;
    },
  ) => {
    const holePoints = Array.isArray(hole) ? hole : getCircleHoleOutlinePoints(hole);
    const vertices = getPolylineVertices(holePoints);
    if (vertices.length < 3) return false;

    const otherPolygonHoles = (shape.holes ?? []).filter((_value, index) => index !== options?.ignorePolygonHoleIndex);
    const otherCircleHoles = (shape.circleHoles ?? []).filter(
      (_value, index) => index !== options?.ignoreCircleHoleIndex,
    );

    if (doClosedContoursIntersect(holePoints, shape.points)) {
      return false;
    }

    if (otherPolygonHoles.some((existingHole) => doClosedContoursIntersect(holePoints, existingHole))) {
      return false;
    }

    if (
      !Array.isArray(hole) &&
      otherPolygonHoles.some((existingHole) => doesCircleHoleIntersectContour(hole, existingHole))
    ) {
      return false;
    }

    if (
      Array.isArray(hole) &&
      otherCircleHoles.some((existingHole) => doesCircleHoleIntersectContour(existingHole, hole))
    ) {
      return false;
    }

    if (!Array.isArray(hole)) {
      if (
        otherCircleHoles.some(
          (existingHole) => getDistance(existingHole.center, hole.center) < existingHole.radius + hole.radius,
        )
      ) {
        return false;
      }
    }

    return vertices.every(
      (vertex) =>
        isPointInsideClosedPolyline(vertex, shape.points) &&
        !otherPolygonHoles.some((existingHole) => isPointInsideClosedPolyline(vertex, existingHole)) &&
        !otherCircleHoles.some((existingHole) => getDistance(vertex, existingHole.center) < existingHole.radius),
    );
  };

  const formatLocalizedNumber = (value: number, maximumFractionDigits = 2) =>
    new Intl.NumberFormat(decimalSeparator === "comma" ? "it-IT" : "en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits,
    }).format(value);

  const getEntityTypeLabel = (kind: SelectableEntitySnapshot["kind"]) => {
    if (kind === "polyline") return "Custom Flat Room";
    if (kind === "rectangle") return "Flat Roof";
    if (kind === "circle") return "Circle";
    if (kind === "arc") return "Arc";
    if (kind === "construction-line") return "Construction Line";
    if (kind === "text") return "Text";
    if (kind === "icon") return "Icon";
    return "Dimension";
  };

  const getEntityBaseName = (kind: SelectableEntitySnapshot["kind"]) => {
    if (kind === "polyline") return "Custom Flat Room";
    if (kind === "rectangle") return "Flat Roof";
    if (kind === "circle") return "Circle";
    if (kind === "arc") return "Arc";
    if (kind === "construction-line") return "Construction Line";
    if (kind === "text") return "Text";
    if (kind === "icon") return "Icon";
    return "Dimension";
  };

  const getNextEntityName = (
    kind: SelectableEntitySnapshot["kind"],
    shapes: Shape[],
    currentDimensions: Dimension[],
  ) => {
    const count =
      kind === "dimension" || kind === "text" || kind === "icon"
        ? currentDimensions.length + 1
        : shapes.filter((shape) => shape.kind === kind).length + 1;
    return `${getEntityBaseName(kind)} ${count}`;
  };

  const getNextNamedEntityName = (prefix: string, shapes: Shape[]) =>
    `${prefix} ${shapes.filter((shape) => shape.name.startsWith(prefix)).length + 1}`;

  const getParsedHatchScale = () => {
    const parsed = parseLocalizedNumber(hatchToolScale);
    return parsed != null && Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
  };

  const getCurrentHatchStrokeColor = () => hatchToolColor || DEFAULT_HATCH_COLOR;

  const getParsedIconScale = () => {
    const parsed = parseLocalizedNumber(iconToolScale);
    return parsed != null && Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
  };

  const getCurrentIconSize = () => ICON_BASE_SIZE * getParsedIconScale() * ICON_SCALE_TO_CANVAS_FACTOR;

  const getCurrentIconColor = () => iconToolColor || DEFAULT_ICON_TOOL_COLOR;

  const buildHatchShapeFromPoints = (
    points: number[],
    options?: {
      kind?: PolylineLikeShape["kind"];
      layer?: EntityLayerName;
      holes?: number[][];
      circleHoles?: CircleHole[];
    },
  ): PolylineLikeShape => {
    const hatchStyle = getEntityTypeStyle("hatch");
    return {
      id: `polyline-${crypto.randomUUID()}`,
      points,
      holes: options?.holes?.map((hole) => [...hole]),
      circleHoles: options?.circleHoles?.map(cloneCircleHole),
      closed: true,
      kind: options?.kind ?? "polyline",
      layer: options?.layer ?? ROOF_LAYER,
      entityStyleKind: "hatch",
      name: getNextNamedEntityName("Hatch", polylines),
      strokeColor: getCurrentHatchStrokeColor(),
      strokeWidth: hatchStyle.strokeWidth,
      disableFill: true,
      hatchPattern: hatchToolPattern,
      hatchScale: getParsedHatchScale(),
    };
  };

  const getObstacleEntityLabel = (shape: Shape | null) => {
    if (!shape?.isObstacle) return null;
    return shape.kind === "circle" ? "Circular obstacle" : "Obstacle";
  };

  const getNextObstacleName = (shapes: Shape[]) => `Obstacle ${shapes.filter((shape) => shape.isObstacle).length + 1}`;

  const toAlphaColor = (color: string, alpha: number) => {
    const normalized = color.trim();
    const sixDigitHex = normalized.match(/^#([0-9a-f]{6})$/i);
    if (!sixDigitHex) return `rgba(34, 139, 230, ${alpha})`;

    const hex = sixDigitHex[1];
    if (!hex) return `rgba(34, 139, 230, ${alpha})`;
    const red = Number.parseInt(hex.slice(0, 2), 16);
    const green = Number.parseInt(hex.slice(2, 4), 16);
    const blue = Number.parseInt(hex.slice(4, 6), 16);
    return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
  };

  const parseLocalizedNumber = (value: string | null | undefined) => {
    if (typeof value !== "string") return null;
    const trimmed = value.trim().replace(/\s/g, "");
    if (!trimmed) return null;

    const normalized =
      decimalSeparator === "comma"
        ? trimmed.includes(",")
          ? trimmed.replace(/\./g, "").replace(",", ".")
          : trimmed
        : trimmed.replace(/,/g, "");
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const getOverwriteInputCharacter = (key: string, code?: string) => {
    if (/^[0-9]$/.test(key)) return key;
    if (key === "." || key === "," || code === "NumpadDecimal") {
      return decimalSeparator === "comma" ? "," : ".";
    }
    if (key === "-") return key;
    return null;
  };

  const normalizeShortcut = (value: string) =>
    value
      .trim()
      .split("+")
      .map((part) => part.trim())
      .filter(Boolean)
      .map((part) => {
        const lower = part.toLowerCase();
        if (lower === "control") return "ctrl";
        if (lower === "cmd" || lower === "command" || lower === "meta") return "meta";
        if (lower === "option") return "alt";
        if (lower === " ") return "space";
        return lower;
      })
      .join("+");

  const getEventShortcut = (event: KeyboardEvent) => {
    const key = event.key === " " ? "space" : event.key.toLowerCase();
    const parts = [
      event.ctrlKey ? "ctrl" : "",
      event.metaKey ? "meta" : "",
      event.altKey ? "alt" : "",
      event.shiftKey && key !== "shift" ? "shift" : "",
      key,
    ].filter(Boolean);
    return parts.join("+");
  };

  const getShortcutCommandForEvent = (event: KeyboardEvent): ShortcutCommand | null => {
    const eventShortcut = getEventShortcut(event);
    const match = SHORTCUT_COMMAND_OPTIONS.find(({ command }) => {
      const configured = normalizeShortcut(keyboardShortcuts[command] ?? "");
      return configured !== "" && configured === eventShortcut;
    });
    return match?.command ?? null;
  };

  const handleShortcutChange = (command: ShortcutCommand, value: string) => {
    setKeyboardShortcuts((current) => ({
      ...current,
      [command]: value,
    }));
  };

  useEffect(() => {
    if (singleSelectedEntity) {
      setEntityNameDraft(singleSelectedEntity.name);
    } else {
      setEntityNameDraft("");
    }

    setEntityColorDraft(commonSelectedColor ?? selectedEntities[0]?.strokeColor ?? DEFAULT_SHAPE_COLOR);

    setEntityLayerDraft(commonSelectedLayer ?? "");
  }, [
    singleSelectedEntity?.id,
    singleSelectedEntity?.name,
    commonSelectedColor,
    commonSelectedLayer,
    polylines,
    dimensions,
    textEntities,
    iconEntities,
    selectedIds,
  ]);

  useEffect(() => {
    if (selectedEntities.length === 0) {
      setIsEntityColorPaletteOpen(false);
    }
  }, [selectedEntities.length]);

  useEffect(() => {
    setSelectedIds((current) =>
      current.filter((id) => {
        if (id === BACKGROUND_ID) return true;
        const shape = polylines.find((item) => item.id === id);
        if (shape) return isShapeInteractive(shape);
        const dimension = dimensions.find((item) => item.id === id);
        if (dimension) return isDimensionInteractive(dimension);
        const constructionLine = constructionLines.find((line) => line.id === id);
        if (constructionLine) return isConstructionLineInteractive(constructionLine);
        const textEntity = textEntities.find((item) => item.id === id);
        if (textEntity) return isTextEntityInteractive(textEntity);
        const iconEntity = iconEntities.find((item) => item.id === id);
        if (iconEntity) return isIconEntityInteractive(iconEntity);
        return false;
      }),
    );

    setEditingId((current) => {
      if (!current || current === BACKGROUND_ID) return current;
      const shape = polylines.find((item) => item.id === current);
      if (shape) return isShapeInteractive(shape) ? current : null;
      const dimension = dimensions.find((item) => item.id === current);
      if (dimension) return isDimensionInteractive(dimension) ? current : null;
      const textEntity = textEntities.find((item) => item.id === current);
      if (textEntity) return isTextEntityInteractive(textEntity) ? current : null;
      const iconEntity = iconEntities.find((item) => item.id === current);
      if (iconEntity) return isIconEntityInteractive(iconEntity) ? current : null;
      return null;
    });
  }, [visibleLayers, lockedLayers, polylines, dimensions, constructionLines, textEntities, iconEntities, editingId]);

  useEffect(() => {
    if (!editingTextId) return;

    const textEntity = textEntities.find((item) => item.id === editingTextId);
    if (!textEntity || !isTextEntityInteractive(textEntity)) {
      cancelTextEditing();
    }
  }, [editingTextId, textEntities, visibleLayers, lockedLayers]);

  const getScreenInvariantSize = (size: number) => size / viewport.scale;
  const getTextEntityWidth = (
    textEntity: Pick<TextEntity, "text" | "height"> & Partial<Pick<TextEntity, "boxWidth">>,
  ) =>
    Math.max(
      MIN_TEXT_BOX_WIDTH,
      textEntity.boxWidth ?? textEntity.text.length * textEntity.height * 0.6 + TEXT_BOX_PADDING * 2,
    );
  const getTextEntityHeight = (textEntity: Pick<TextEntity, "height"> & Partial<Pick<TextEntity, "boxHeight">>) =>
    Math.max(MIN_TEXT_BOX_HEIGHT, textEntity.boxHeight ?? textEntity.height + TEXT_BOX_PADDING * 2);
  const getTextEntityOffset = (
    textEntity: Pick<TextEntity, "text" | "height" | "justification"> &
      Partial<Pick<TextEntity, "boxWidth" | "boxHeight">>,
  ) => {
    const width = getTextEntityWidth(textEntity);
    const boxHeight = getTextEntityHeight(textEntity);
    if (textEntity.justification === "middle") return { offsetX: width / 2, offsetY: boxHeight / 2 };
    if (textEntity.justification === "right") return { offsetX: width, offsetY: boxHeight / 2 };
    if (textEntity.justification === "bottom") return { offsetX: width / 2, offsetY: boxHeight };
    return { offsetX: 0, offsetY: boxHeight / 2 };
  };
  const getTextEntityBounds = (textEntity: TextEntity) => {
    const offset = getTextEntityOffset(textEntity);
    const minX = textEntity.position.x - offset.offsetX;
    const minY = textEntity.position.y - offset.offsetY;

    return {
      minX,
      minY,
      maxX: minX + getTextEntityWidth(textEntity),
      maxY: minY + getTextEntityHeight(textEntity),
    };
  };
  const getIconEntityBounds = (iconEntity: CanvasIconEntity) => {
    const rotation = ((iconEntity.rotation ?? 0) * Math.PI) / 180;
    const cos = Math.cos(rotation);
    const sin = Math.sin(rotation);
    const corners = [
      { x: 0, y: 0 },
      { x: iconEntity.size, y: 0 },
      { x: iconEntity.size, y: -iconEntity.size },
      { x: 0, y: -iconEntity.size },
    ].map((corner) => ({
      x: iconEntity.position.x + corner.x * cos - corner.y * sin,
      y: iconEntity.position.y + corner.x * sin + corner.y * cos,
    }));
    const xs = corners.map((corner) => corner.x);
    const ys = corners.map((corner) => corner.y);

    return {
      minX: Math.min(...xs),
      minY: Math.min(...ys),
      maxX: Math.max(...xs),
      maxY: Math.max(...ys),
    };
  };
  const getTextEntityPositionFromBounds = (
    bounds: { minX: number; minY: number; maxX: number; maxY: number },
    textEntity: Pick<TextEntity, "text" | "height" | "justification">,
  ) => {
    const offset = getTextEntityOffset({
      ...textEntity,
      boxWidth: bounds.maxX - bounds.minX,
      boxHeight: bounds.maxY - bounds.minY,
    });

    return {
      x: bounds.minX + offset.offsetX,
      y: bounds.minY + offset.offsetY,
    };
  };
  const getDistanceToBounds = (
    point: CanvasPoint,
    bounds: { minX: number; minY: number; maxX: number; maxY: number },
  ) => {
    const dx = point.x < bounds.minX ? bounds.minX - point.x : point.x > bounds.maxX ? point.x - bounds.maxX : 0;
    const dy = point.y < bounds.minY ? bounds.minY - point.y : point.y > bounds.maxY ? point.y - bounds.maxY : 0;
    return Math.hypot(dx, dy);
  };
  const startTextEditing = (textEntity: TextEntity) => {
    setEditingTextId(textEntity.id);
    setEditingTextDraft(textEntity.text);
    setEditingId(null);
    window.setTimeout(() => {
      textEditInputRef.current?.focus();
      textEditInputRef.current?.select();
    }, 0);
  };
  const cancelTextEditing = () => {
    setEditingTextId(null);
    setEditingTextDraft("");
  };
  const commitTextEditing = () => {
    if (!editingTextId) return;

    const nextText = editingTextDraft;
    const currentText = textEntities.find((textEntity) => textEntity.id === editingTextId);
    if (currentText && currentText.text !== nextText) {
      runHistoryAction(() => {
        setTextEntities((current) =>
          current.map((textEntity) =>
            textEntity.id === editingTextId
              ? {
                  ...textEntity,
                  name: nextText.trim() || DEFAULT_TEXT_TOOL_VALUE,
                  text: nextText,
                }
              : textEntity,
          ),
        );
      });
    }

    cancelTextEditing();
  };
  const getDimensionTextAnchor = (text: string) => {
    const fontSize = getScreenInvariantSize(dimensionTextHeight);

    return {
      fontSize,
      offsetX: getScreenInvariantSize(
        (text.length * dimensionTextHeight * DIMENSION_TEXT_AVERAGE_CHAR_WIDTH_FACTOR) / 2,
      ),
      offsetY: fontSize,
    };
  };
  const formatMeasurement = (length: number) => {
    const converted = length * UNIT_FACTORS[drawingUnit];
    return `${formatLocalizedNumber(converted, 2)} ${drawingUnit}`;
  };
  const formatAreaMeasurement = (area: number) => {
    const converted = area * UNIT_FACTORS[drawingUnit] * UNIT_FACTORS[drawingUnit];
    return `${formatLocalizedNumber(converted, 2)} ${drawingUnit}²`;
  };
  const formatMeasurementValue = (length: number) => {
    const converted = length * UNIT_FACTORS[drawingUnit];
    return formatLocalizedNumber(converted, 2);
  };
  const parseMeasurementValue = (value: string) => {
    const parsed = parseLocalizedNumber(value);
    if (parsed == null || !Number.isFinite(parsed)) return null;
    return parsed / UNIT_FACTORS[drawingUnit];
  };
  const getDefaultOffsetDistance = () => {
    if (drawingUnit === "ft" || drawingUnit === "in") {
      return 6 / UNIT_FACTORS.ft;
    }

    return 2000;
  };
  const commitGridSpacingDraft = () => {
    const parsed = parseLocalizedNumber(gridSpacingDraft);
    if (parsed == null || !Number.isFinite(parsed)) {
      setGridSpacingDraft(formatMeasurementValue(gridSpacing));
      return;
    }

    setGridSpacing(Math.max(1, parsed / UNIT_FACTORS[drawingUnit]));
  };
  const commitGridSnapStepDraft = () => {
    const parsed = parseLocalizedNumber(gridSnapStepDraft);
    if (parsed == null || !Number.isFinite(parsed)) {
      setGridSnapStepDraft(formatMeasurementValue(gridSnapStep));
      return;
    }

    setGridSnapStep(Math.max(0.001, parsed / UNIT_FACTORS[drawingUnit]));
  };
  const commitDimensionTextHeightDraft = () => {
    const parsed = parseLocalizedNumber(dimensionTextHeightDraft);
    if (parsed == null || !Number.isFinite(parsed)) {
      setDimensionTextHeightDraft(String(dimensionTextHeight));
      return;
    }

    setDimensionTextHeight(Math.min(48, Math.max(6, parsed)));
  };
  const commitDimensionTextGapDraft = () => {
    const parsed = parseLocalizedNumber(dimensionTextGapDraft);
    if (parsed == null || !Number.isFinite(parsed)) {
      setDimensionTextGapDraft(String(dimensionTextGap));
      return;
    }

    setDimensionTextGap(Math.min(MAX_DIMENSION_TEXT_GAP, Math.max(MIN_DIMENSION_TEXT_GAP, parsed)));
  };
  const getGridSnapStepInWorldUnits = () => gridSnapStep;
  const applyGridSnap = (point: CanvasPoint, excludedPoints?: CanvasPoint[] | null) => {
    if (!isGridSnapEnabled) return point;

    const step = getGridSnapStepInWorldUnits();
    if (!Number.isFinite(step) || step <= 0) return point;

    const snappedPoint = {
      x: Math.round(point.x / step) * step,
      y: Math.round(point.y / step) * step,
    };

    if (
      excludedPoints?.some(
        (excludedPoint) =>
          Math.abs(snappedPoint.x - excludedPoint.x) < 0.0001 && Math.abs(snappedPoint.y - excludedPoint.y) < 0.0001,
      )
    ) {
      return point;
    }

    return snappedPoint;
  };
  const getOsnapPriority = (type: OsnapPoint["type"]) =>
    type === "END" || type === "MID" || type === "CENTRE" ? 0 : type === "INT" ? 1 : 2;
  const getSegmentIntersection = (
    startA: CanvasPoint,
    endA: CanvasPoint,
    startB: CanvasPoint,
    endB: CanvasPoint,
  ): CanvasPoint | null => {
    const denominator = (endA.x - startA.x) * (endB.y - startB.y) - (endA.y - startA.y) * (endB.x - startB.x);
    if (Math.abs(denominator) <= 0.000001) return null;

    const deltaX = startB.x - startA.x;
    const deltaY = startB.y - startA.y;
    const t = (deltaX * (endB.y - startB.y) - deltaY * (endB.x - startB.x)) / denominator;
    const u = (deltaX * (endA.y - startA.y) - deltaY * (endA.x - startA.x)) / denominator;
    if (t < -0.000001 || t > 1.000001 || u < -0.000001 || u > 1.000001) return null;

    return {
      x: startA.x + t * (endA.x - startA.x),
      y: startA.y + t * (endA.y - startA.y),
    };
  };
  const getDistanceToLine = (point: CanvasPoint, start: CanvasPoint, end: CanvasPoint) => {
    const deltaX = end.x - start.x;
    const deltaY = end.y - start.y;
    const length = Math.hypot(deltaX, deltaY);
    if (length <= 0.0001) return Number.POSITIVE_INFINITY;
    return Math.abs(deltaY * point.x - deltaX * point.y + end.x * start.y - end.y * start.x) / length;
  };
  const getInfiniteLineIntersection = (
    startA: CanvasPoint,
    endA: CanvasPoint,
    startB: CanvasPoint,
    endB: CanvasPoint,
  ): CanvasPoint | null => {
    const denominator = (endA.x - startA.x) * (endB.y - startB.y) - (endA.y - startA.y) * (endB.x - startB.x);
    if (Math.abs(denominator) <= 0.000001) return null;

    const deltaX = startB.x - startA.x;
    const deltaY = startB.y - startA.y;
    const t = (deltaX * (endB.y - startB.y) - deltaY * (endB.x - startB.x)) / denominator;
    return {
      x: startA.x + t * (endA.x - startA.x),
      y: startA.y + t * (endA.y - startA.y),
    };
  };
  const getShapeSegments = (shape: Shape) => {
    if (shape.kind === "circle" || shape.kind === "arc") return [] as Array<{ start: CanvasPoint; end: CanvasPoint }>;
    const segments: Array<{ start: CanvasPoint; end: CanvasPoint }> = [];
    for (let index = 0; index <= shape.points.length - 4; index += 2) {
      const start = getPointFromCoordinateArray(shape.points, index);
      const end = getPointFromCoordinateArray(shape.points, index + 2);
      if (start && end) segments.push({ start, end });
    }
    if (shape.closed && shape.points.length >= 6) {
      const start = getPointFromCoordinateArray(shape.points, shape.points.length - 2);
      const end = getPointFromCoordinateArray(shape.points, 0);
      if (start && end) segments.push({ start, end });
    }
    return segments;
  };
  const explodeSelectedPolylines = () => {
    const selectedIdSet = new Set(selectedIds);
    const explodableShapes = polylines.filter(
      (shape): shape is PolylineLikeShape =>
        selectedIdSet.has(shape.id) &&
        (shape.kind === "polyline" || shape.kind === "rectangle") &&
        getShapeSegments(shape).length > 1,
    );

    if (explodableShapes.length === 0) return;

    const newSelectedIds: string[] = [];
    const replacementMap = new Map<string, Shape[]>();
    explodableShapes.forEach((shape) => {
      const {
        holes: _holes,
        circleHoles: _circleHoles,
        showVertexMarkersOnly: _showVertexMarkersOnly,
        ...segmentBase
      } = shape;

      const segmentShapes = getShapeSegments(shape).map((segment, index) => {
        const id = `polyline-${crypto.randomUUID()}`;
        newSelectedIds.push(id);
        return {
          ...segmentBase,
          id,
          points: [segment.start.x, segment.start.y, segment.end.x, segment.end.y],
          closed: false,
          kind: "polyline" as const,
          name: `${shape.name} ${index + 1}`,
          disableFill: true,
        };
      });
      replacementMap.set(shape.id, segmentShapes);
    });

    runHistoryAction(() => {
      setPolylines((current) =>
        current.flatMap((shape) => {
          return replacementMap.get(shape.id) ?? [shape];
        }),
      );
      setSelectedIds(newSelectedIds);
      setEditingId(null);
      setSelectionCycle(null);
    });
  };
  const getMidPoint = (pointA: CanvasPoint, pointB: CanvasPoint) => ({
    x: (pointA.x + pointB.x) / 2,
    y: (pointA.y + pointB.y) / 2,
  });
  const getOffsetLabelPoint = (pointA: CanvasPoint, pointB: CanvasPoint, offset = 14) => {
    const midPoint = getMidPoint(pointA, pointB);
    const dx = pointB.x - pointA.x;
    const dy = pointB.y - pointA.y;
    const length = Math.hypot(dx, dy);

    if (length === 0) {
      return {
        x: midPoint.x,
        y: midPoint.y - getScreenInvariantSize(offset),
      };
    }

    return {
      x: midPoint.x + (-dy / length) * getScreenInvariantSize(offset),
      y: midPoint.y + (dx / length) * getScreenInvariantSize(offset),
    };
  };
  const getCursorSideLabelPoint = (point: CanvasPoint, offsetX = 18, offsetY = -18) => ({
    x: point.x + getScreenInvariantSize(offsetX),
    y: point.y + getScreenInvariantSize(offsetY),
  });

  const getPolylineVertices = (points: number[]) => {
    const vertices: CanvasPoint[] = [];

    for (let index = 0; index < points.length - 1; index += 2) {
      const x = points[index];
      const y = points[index + 1];
      if (x == null || y == null) {
        continue;
      }

      const nextVertex = {
        x,
        y,
      };
      const previousVertex = vertices[vertices.length - 1];

      if (
        previousVertex &&
        Math.abs(previousVertex.x - nextVertex.x) <= 0.0001 &&
        Math.abs(previousVertex.y - nextVertex.y) <= 0.0001
      ) {
        continue;
      }

      vertices.push(nextVertex);
    }

    return vertices;
  };

  const areLifeLineDesignPointsClosed = (vertices: CanvasPoint[]) => {
    if (vertices.length <= 2) return false;
    const first = vertices[0];
    const last = vertices[vertices.length - 1];
    return !!first && !!last && areCanvasPointsEqual(first, last);
  };

  const getLifeLineDesignPoints = (points: number[], closed: boolean) => {
    const vertices = getPolylineVertices(points);
    return closed && areLifeLineDesignPointsClosed(vertices) ? vertices.slice(0, -1) : vertices;
  };

  const activeLifeLinePreview = useMemo(() => {
    if (toolMode !== "singleLifeLine" || !activePolyline || activePolyline.length < 4) {
      return [];
    }

    const activeLifeLineVertices = getPolylineVertices(activePolyline);
    const isClosedPreview = areLifeLineDesignPointsClosed(activeLifeLineVertices);
    return calculateRenderedLifeLines(getLifeLineDesignPoints(activePolyline, isClosedPreview), {
      closed: isClosedPreview,
      systemMaxSpan: effectiveLifeLineSystemSettings.maxSpacing,
      chamferAcuteAngles: effectiveLifeLineSystemSettings.chamferAcuteAngles,
      chamferSegmentLength: effectiveLifeLineSystemSettings.chamferSegmentLength,
      maxSingleLineLength: effectiveLifeLineSystemSettings.maxSingleLineLength,
      maxCornersPerLifeline: effectiveLifeLineSystemSettings.maxCornersPerLifeline,
    }).renderedLifeLines;
  }, [
    activePolyline,
    effectiveLifeLineSystemSettings.chamferAcuteAngles,
    effectiveLifeLineSystemSettings.chamferSegmentLength,
    effectiveLifeLineSystemSettings.maxCornersPerLifeline,
    effectiveLifeLineSystemSettings.maxSingleLineLength,
    effectiveLifeLineSystemSettings.maxSpacing,
    toolMode,
  ]);

  const getAutomaticLifeLineOffsetDistance = () => {
    const parsedDistance =
      numericEdit?.kind === "automatic-lifeline-offset" ? parseMeasurementValue(numericEdit.value) : null;
    return Math.max(0, parsedDistance ?? automaticLifeLineOffsetDistance);
  };

  const showAutomaticLifeLineOffsetInput = (offsetDistance = automaticLifeLineOffsetDistance) => {
    setNumericEdit({
      kind: "automatic-lifeline-offset",
      position: { x: 0, y: 0 },
      value: formatMeasurementValue(offsetDistance),
    });
  };

  const commitAutomaticLifeLineFromSourceShapes = (
    sourceShapes: AutomaticLifeLineSourceShape[],
    offsetDistance = getAutomaticLifeLineOffsetDistance(),
    offsetSidePoint?: CanvasPoint | null,
  ) => {
    const automaticPaths = buildAutomaticLifeLinePaths(sourceShapes, {
      flatRoofOffsetDistance: offsetDistance,
      ridgeOffsetDistance: offsetDistance,
      flatRoofOffsetSidePoint: offsetSidePoint,
    });
    if (automaticPaths.length === 0) {
      setLifeLineRoofSelectionWarning("Select a roof or roof part to create an automatic lifeline.");
      return false;
    }

    const polylineStyle = getEntityTypeStyle("single-lifeline");
    const arcStyle = getEntityTypeStyle("arc");
    const lifeLineSystemMeta = getSelectedLifeLineSystemMeta();
    const nextShapes: PolylineLikeShape[] = [];
    const nextArcShapes: ArcShape[] = [];
    let nextLifeLineIndex = polylines.filter((shape) => shape.name.startsWith("Life Line")).length + 1;
    let nextArcIndex = polylines.filter((shape) => shape.name.startsWith("Arc")).length + 1;

    automaticPaths.forEach((path) => {
      path.arcs?.forEach((arc) => {
        nextArcShapes.push({
          id: `polyline-${crypto.randomUUID()}`,
          closed: false,
          kind: "arc",
          center: clonePoint(arc.center),
          radius: arc.radius,
          startAngle: arc.startAngle,
          endAngle: arc.endAngle,
          entityStyleKind: "arc",
          layer: SAFETY_LAYER,
          name: `Arc ${nextArcIndex++}`,
          strokeColor: arcStyle.strokeColor,
          strokeWidth: arcStyle.strokeWidth,
        });
      });

      const { result: lifeLineResult, renderedLifeLines } = calculateRenderedLifeLines(path.points, {
        closed: path.closed,
        systemMaxSpan: effectiveLifeLineSystemSettings.maxSpacing,
        chamferAcuteAngles: effectiveLifeLineSystemSettings.chamferAcuteAngles,
        chamferSegmentLength: effectiveLifeLineSystemSettings.chamferSegmentLength,
        maxSingleLineLength: effectiveLifeLineSystemSettings.maxSingleLineLength,
        maxCornersPerLifeline: effectiveLifeLineSystemSettings.maxCornersPerLifeline,
      });
      const baseName = `Life Line ${nextLifeLineIndex}`;
      nextLifeLineIndex += 1;

      renderedLifeLines.forEach((renderedLifeLine) => {
        if (renderedLifeLine.points.length < 4) return;

        nextShapes.push({
          id: `polyline-${crypto.randomUUID()}`,
          points: renderedLifeLine.points,
          closed: path.closed && renderedLifeLines.length === 1,
          kind: "polyline",
          layer: SAFETY_LAYER,
          entityStyleKind: "single-lifeline",
          name: lifeLineResult.lines.length > 1 ? `${baseName}.${renderedLifeLine.lineNumber}` : baseName,
          strokeColor: polylineStyle.strokeColor,
          strokeWidth: polylineStyle.strokeWidth,
          disableFill: true,
          lifeLineVertices: renderedLifeLine.lifeLineVertices,
          lifeLineDesignPoints: renderedLifeLine.designPoints,
          lifeLineLineNumber: renderedLifeLine.lineNumber,
          ...lifeLineSystemMeta,
        });
      });
    });

    if (nextShapes.length === 0) {
      setLifeLineRoofSelectionWarning("No automatic lifeline path could be created from the selection.");
      return false;
    }

    runHistoryAction(() => {
      setPolylines((current) => [...current, ...nextShapes, ...nextArcShapes]);
    });

    setSelectedIds(nextShapes.map((shape) => shape.id));
    setSelectionCycle(null);
    setEditingId(null);
    setToolMode("select");
    setAutomaticLifeLineSourceIds(null);
    setAutomaticLifeLinePreviewPoint(null);
    setHoverSnap(null);
    setLifeLineRoofSelectionWarning(null);
    setActivePolyline(null);
    setNumericEdit(null);
    clearOsnapTracking();
    return true;
  };

  const getRenderablePolylineVertices = (points: number[], closed: boolean) => {
    const vertices = getPolylineVertices(points);
    if (!closed || vertices.length < 2) return vertices;

    const first = vertices[0];
    const last = vertices[vertices.length - 1];
    if (first && last && Math.abs(first.x - last.x) <= 0.0001 && Math.abs(first.y - last.y) <= 0.0001) {
      return vertices.slice(0, -1);
    }

    return vertices;
  };

  const buildPointSeriesPointsFromControlPoints = (controlPoints: CanvasPoint[], maxLength: number) => {
    if (controlPoints.length < 2) {
      return flattenCanvasPoints(controlPoints);
    }

    const safeMaxLength = Math.max(0.001, maxLength);
    const points: CanvasPoint[] = [controlPoints[0]!];

    for (let index = 1; index < controlPoints.length; index += 1) {
      const start = controlPoints[index - 1]!;
      const end = controlPoints[index]!;
      const distance = getDistance(start, end);
      const segmentCount = Math.max(1, Math.ceil(distance / safeMaxLength));

      for (let step = 1; step <= segmentCount; step += 1) {
        const ratio = step / segmentCount;
        points.push({
          x: start.x + (end.x - start.x) * ratio,
          y: start.y + (end.y - start.y) * ratio,
        });
      }
    }

    return flattenCanvasPoints(points);
  };

  const flattenCanvasPoints = (points: CanvasPoint[]) => points.flatMap((point) => [point.x, point.y]);

  const getVisibleWorldBounds = () => {
    const minX = -viewport.x / viewport.scale;
    const minY = -viewport.y / viewport.scale;
    const maxX = minX + viewportSize.width / viewport.scale;
    const maxY = minY + viewportSize.height / viewport.scale;

    return { minX, minY, maxX, maxY };
  };

  const rotateVector = (point: CanvasPoint, angleDeg: number) => {
    const angle = (angleDeg * Math.PI) / 180;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);

    return {
      x: point.x * cos - point.y * sin,
      y: point.x * sin + point.y * cos,
    };
  };

  const getBackgroundWorldPoint = (asset: BackgroundAsset, localPoint: CanvasPoint) => {
    const rotated = rotateVector(localPoint, asset.rotation);
    return {
      x: asset.center.x + rotated.x,
      y: asset.center.y + rotated.y,
    };
  };

  const getBackgroundLocalPoint = (asset: BackgroundAsset, worldPoint: CanvasPoint) => {
    const translated = {
      x: worldPoint.x - asset.center.x,
      y: worldPoint.y - asset.center.y,
    };

    return rotateVector(translated, -asset.rotation);
  };

  const getBackgroundCorners = (asset: BackgroundAsset) => {
    const halfWidth = asset.width / 2;
    const halfHeight = asset.height / 2;

    return [
      getBackgroundWorldPoint(asset, { x: -halfWidth, y: -halfHeight }),
      getBackgroundWorldPoint(asset, { x: halfWidth, y: -halfHeight }),
      getBackgroundWorldPoint(asset, { x: halfWidth, y: halfHeight }),
      getBackgroundWorldPoint(asset, { x: -halfWidth, y: halfHeight }),
    ];
  };

  const getBackgroundBounds = (asset: BackgroundAsset) => {
    const corners = getBackgroundCorners(asset);

    return {
      minX: Math.min(...corners.map((corner) => corner.x)),
      minY: Math.min(...corners.map((corner) => corner.y)),
      maxX: Math.max(...corners.map((corner) => corner.x)),
      maxY: Math.max(...corners.map((corner) => corner.y)),
    };
  };

  const getBackgroundReferenceWorldPoints = (asset: BackgroundAsset) => {
    if (!asset.reference) return null;

    return {
      start: getBackgroundWorldPoint(asset, asset.reference.startLocal),
      end: getBackgroundWorldPoint(asset, asset.reference.endLocal),
    };
  };

  const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

  const getBackgroundCropWorldBounds = (asset: BackgroundAsset, start: CanvasPoint, end: CanvasPoint) => {
    const selectionBounds = getSelectionBounds(start, end);
    const backgroundBounds = getBackgroundBounds(asset);

    return {
      minX: clamp(selectionBounds.minX, backgroundBounds.minX, backgroundBounds.maxX),
      minY: clamp(selectionBounds.minY, backgroundBounds.minY, backgroundBounds.maxY),
      maxX: clamp(selectionBounds.maxX, backgroundBounds.minX, backgroundBounds.maxX),
      maxY: clamp(selectionBounds.maxY, backgroundBounds.minY, backgroundBounds.maxY),
    };
  };

  const fitBoundsToViewport = (bounds: { minX: number; minY: number; maxX: number; maxY: number }) => {
    const width = Math.max(1, bounds.maxX - bounds.minX);
    const height = Math.max(1, bounds.maxY - bounds.minY);
    const nextScale = Math.max(
      MIN_ZOOM,
      Math.min((viewportSize.width - FIT_MARGIN * 2) / width, (viewportSize.height - FIT_MARGIN * 2) / height),
    );

    setViewport({
      x: FIT_MARGIN - bounds.minX * nextScale + (viewportSize.width - FIT_MARGIN * 2 - width * nextScale) / 2,
      y: FIT_MARGIN - bounds.minY * nextScale + (viewportSize.height - FIT_MARGIN * 2 - height * nextScale) / 2,
      scale: nextScale,
    });
  };

  const getShapeBounds = (shape: Shape) => getShapeBoundsRule(shape);

  const getDimensionBounds = (dimension: Dimension) => getDimensionBoundsRule(dimension, getDimensionLayout);

  const getSelectionBounds = (start: CanvasPoint, end: CanvasPoint) => getSelectionBoundsRule(start, end);

  const boundsIntersect = (
    first: { minX: number; minY: number; maxX: number; maxY: number },
    second: { minX: number; minY: number; maxX: number; maxY: number },
  ) => boundsIntersectRule(first, second);

  const getDimensionGeometry = (start: CanvasPoint, end: CanvasPoint, pointer: CanvasPoint) => {
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const length = Math.hypot(dx, dy);
    if (length === 0) return null;

    const ux = dx / length;
    const uy = dy / length;
    const nx = -uy;
    const ny = ux;
    const offset = (pointer.x - start.x) * nx + (pointer.y - start.y) * ny;
    const layout = getDimensionLayout(start, end, offset);
    if (!layout) return null;

    return {
      offset,
      length,
      ...layout,
    };
  };

  const getDimensionLayout = (start: CanvasPoint, end: CanvasPoint, offset: number) =>
    getDimensionLayoutRule(start, end, offset, dimensionTextGap);

  const getArrowHeadPoints = (tip: CanvasPoint, direction: CanvasPoint, size: number, width: number) =>
    getArrowHeadPointsRule(tip, direction, size, width);

  const getSlashMarkerPoints = (tip: CanvasPoint, direction: CanvasPoint, size: number) =>
    getSlashMarkerPointsRule(tip, direction, size);

  const getDimensionOffsetFromPoint = (start: CanvasPoint, end: CanvasPoint, point: CanvasPoint) =>
    getDimensionOffsetFromPointRule(start, end, point);

  const getShapeReferenceCenter = (shape: Shape) => {
    if (shape.kind === "circle" || shape.kind === "arc") {
      return clonePoint(shape.center);
    }

    const bounds = getPolylineCenter(shape.points);
    return bounds?.center ?? getPolylineVertices(shape.points)[0] ?? { x: 0, y: 0 };
  };

  const buildAutoDimensionsForShape = (
    shape: Shape,
    offsetDistance = AUTO_DIMENSION_OFFSET,
  ): AutoDimensionDefinition[] => buildAutoDimensionsForShapeRule(shape, offsetDistance);

  const autoDimensionShape = (shape: Shape, offsetDistance = AUTO_DIMENSION_OFFSET) => {
    const definitions = buildAutoDimensionsForShape(shape, offsetDistance);
    if (definitions.length === 0) return [] as string[];

    const createdIds: string[] = [];
    const dimensionStyle = getEntityTypeStyle("dimension-object");
    runHistoryAction(() => {
      setDimensions((current) => {
        const nextDimensions = definitions.map((definition, index) => {
          const id = `dimension-${crypto.randomUUID()}`;
          createdIds.push(id);
          return {
            id,
            start: clonePoint(definition.start),
            end: clonePoint(definition.end),
            offset: definition.offset,
            dimensionType: definition.dimensionType ?? "linear",
            layer: DIMENSIONS_LAYER,
            entityStyleKind: "dimension-object",
            name: `${shape.name} Dimension ${current.length + index + 1}`,
            strokeColor: dimensionStyle.strokeColor,
            strokeWidth: dimensionStyle.strokeWidth,
          } satisfies Dimension;
        });

        return [...current, ...nextDimensions];
      });
    });

    return createdIds;
  };

  const renderDimensionMarker = (tip: CanvasPoint, direction: CanvasPoint, color: string, key: string) => {
    if (dimensionMarkerStyle === "dot") {
      return (
        <Circle
          key={key}
          x={tip.x}
          y={tip.y}
          radius={getScreenInvariantSize(3.5)}
          fill={color}
          stroke={color}
          strokeWidth={getScreenInvariantSize(1)}
          listening={false}
        />
      );
    }

    if (dimensionMarkerStyle === "slash") {
      return (
        <Line
          key={key}
          points={getSlashMarkerPoints(tip, direction, getScreenInvariantSize(12))}
          stroke={color}
          strokeWidth={getScreenInvariantSize(1.6)}
          lineCap="round"
          listening={false}
        />
      );
    }

    return (
      <Line
        key={key}
        points={getArrowHeadPoints(tip, direction, getScreenInvariantSize(10), getScreenInvariantSize(3.5))}
        closed
        fill={color}
        stroke={color}
        strokeWidth={getScreenInvariantSize(1)}
        listening={false}
      />
    );
  };

  const renderBackgroundReferenceTerminal = (
    point: CanvasPoint,
    key: string,
    options?: {
      draggable?: boolean;
      onDragStart?: (event: Konva.KonvaEventObject<DragEvent>) => void;
      onDragMove?: (event: Konva.KonvaEventObject<DragEvent>) => void;
      onDragEnd?: (event: Konva.KonvaEventObject<DragEvent>) => void;
    },
  ) => {
    const halfSize = getScreenInvariantSize(7);
    const strokeWidth = getScreenInvariantSize(1.8);
    const hitRadius = getScreenInvariantSize(GRIP_HIT_STROKE / 2);
    const draggable = options?.draggable ?? true;

    return (
      <Group
        key={key}
        x={point.x}
        y={point.y}
        draggable={draggable}
        onMouseDown={(event) => {
          event.cancelBubble = true;
        }}
        onMouseUp={(event) => {
          event.cancelBubble = true;
        }}
        onDragStart={options?.onDragStart}
        onDragMove={options?.onDragMove}
        onDragEnd={options?.onDragEnd}
      >
        <Circle radius={hitRadius} fill="#ffffff" opacity={0.01} strokeEnabled={false} />
        <Line
          points={[-halfSize, -halfSize, halfSize, halfSize]}
          stroke="#d9480f"
          strokeWidth={strokeWidth}
          lineCap="round"
          listening={false}
        />
        <Line
          points={[-halfSize, halfSize, halfSize, -halfSize]}
          stroke="#d9480f"
          strokeWidth={strokeWidth}
          lineCap="round"
          listening={false}
        />
      </Group>
    );
  };

  const renderCircleCenterMarker = (
    point: CanvasPoint,
    key: string,
    color: string,
    options?: {
      draggable?: boolean;
      listening?: boolean;
      onDragMove?: (event: Konva.KonvaEventObject<DragEvent>) => void;
    },
  ) => {
    const size = getScreenInvariantSize(7);
    const strokeWidth = getScreenInvariantSize(1.6);
    const listening = options?.listening ?? true;

    return (
      <Group
        key={key}
        x={point.x}
        y={point.y}
        draggable={options?.draggable ?? false}
        listening={listening}
        onMouseDown={(event) => {
          event.cancelBubble = true;
        }}
        onMouseUp={(event) => {
          event.cancelBubble = true;
        }}
        onDragMove={options?.onDragMove}
      >
        <Circle radius={getScreenInvariantSize(12)} fill="#ffffff" opacity={0.01} strokeEnabled={false} />
        <Line points={[-size, 0, size, 0]} stroke={color} strokeWidth={strokeWidth} lineCap="round" listening={false} />
        <Line points={[0, -size, 0, size]} stroke={color} strokeWidth={strokeWidth} lineCap="round" listening={false} />
        <Circle radius={getScreenInvariantSize(2.2)} fill={color} listening={false} />
      </Group>
    );
  };

  const renderCircleCenterDot = (point: CanvasPoint, key: string, color: string) =>
    (() => {
      const arcRadius = getScreenInvariantSize(10);
      const arcStrokeWidth = getScreenInvariantSize(2.4);
      const centerCircleRadius = getScreenInvariantSize(3.4);
      const squareHalfWidth = getScreenInvariantSize(5.7);
      const squareHalfHeight = getScreenInvariantSize(5.4);
      const arrowLength = getScreenInvariantSize(4.4);
      const arrowHalfHeight = getScreenInvariantSize(3);
      const rectColor = "#ff4d4d";
      const arrowTip = { x: arcRadius + arrowLength, y: 0 };
      const arrowBaseTop = { x: arcRadius, y: -arrowHalfHeight };
      const arrowBaseBottom = { x: arcRadius, y: arrowHalfHeight };
      const arcPoints: number[] = [];
      const startAngle = (95 * Math.PI) / 180;
      const endAngle = (360 * Math.PI) / 180;
      const segments = 28;

      for (let index = 0; index <= segments; index += 1) {
        const t = index / segments;
        const angle = startAngle + (endAngle - startAngle) * t;
        arcPoints.push(Math.cos(angle) * arcRadius, Math.sin(angle) * arcRadius);
      }

      return (
        <Group key={key} x={point.x} y={point.y} listening={false}>
          <Line
            points={arcPoints}
            stroke={color}
            strokeWidth={arcStrokeWidth}
            lineCap="butt"
            lineJoin="round"
            listening={false}
          />
          <Line
            points={[arrowBaseTop.x, arrowBaseTop.y, arrowTip.x, arrowTip.y, arrowBaseBottom.x, arrowBaseBottom.y]}
            closed
            fill={color}
            stroke={color}
            strokeWidth={getScreenInvariantSize(0.5)}
            listening={false}
          />
          <Line
            points={[
              -squareHalfWidth,
              -squareHalfHeight,
              squareHalfWidth,
              -squareHalfHeight,
              squareHalfWidth,
              squareHalfHeight,
              -squareHalfWidth,
              squareHalfHeight,
            ]}
            closed
            stroke={rectColor}
            strokeWidth={getScreenInvariantSize(1)}
            listening={false}
          />
          <Circle radius={centerCircleRadius} fill={color} listening={false} />
        </Group>
      );
    })();

  const activateBackgroundReferenceEditing = () => {
    if (!backgroundAsset?.reference) return;
    setSelectedIds([]);
    setEditingId(BACKGROUND_ID);
  };

  const fitToScreen = () => {
    const allBounds = [
      ...(backgroundAsset ? [getBackgroundBounds(backgroundAsset)] : []),
      ...polylines.filter(isShapeVisible).map(getShapeBounds),
      ...dimensions.filter(isDimensionVisible).map(getDimensionBounds),
      ...iconEntities.filter(isIconEntityVisible).map(getIconEntityBounds),
    ];

    if (allBounds.length === 0) {
      setViewport({ x: 0, y: 0, scale: 1 });
      return;
    }

    let minX = Number.POSITIVE_INFINITY;
    let minY = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;

    for (const bounds of allBounds) {
      minX = Math.min(minX, bounds.minX);
      minY = Math.min(minY, bounds.minY);
      maxX = Math.max(maxX, bounds.maxX);
      maxY = Math.max(maxY, bounds.maxY);
    }

    fitBoundsToViewport({ minX, minY, maxX, maxY });
  };

  const loadHtmlImage = (src: string) =>
    new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new window.Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error("Impossible to load the image."));
      image.src = src;
    });

  const fitBackgroundToViewport = (width: number, height: number) => {
    fitBoundsToViewport({
      minX: 0,
      minY: 0,
      maxX: Math.max(1, width),
      maxY: Math.max(1, height),
    });
  };

  const getBackgroundWorldSize = (sourceWidth: number, sourceHeight: number) => {
    const safeHeight = Math.max(1, sourceHeight);
    const scale = DEFAULT_BACKGROUND_HEIGHT / safeHeight;

    return {
      width: sourceWidth * scale,
      height: DEFAULT_BACKGROUND_HEIGHT,
    };
  };

  const handleBackgroundUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    setIsBackgroundLoading(true);
    setBackgroundStatus(null);

    try {
      if (file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf")) {
        const pdf = await getDocument({ data: await file.arrayBuffer() }).promise;
        const page = await pdf.getPage(1);
        const displayViewport = page.getViewport({ scale: 1 });
        const renderViewport = page.getViewport({ scale: 2 });
        const canvas = document.createElement("canvas");
        canvas.width = Math.ceil(renderViewport.width);
        canvas.height = Math.ceil(renderViewport.height);
        const context = canvas.getContext("2d");

        if (!context) {
          throw new Error("Context canvas PDF not available.");
        }

        await page.render({
          canvas,
          canvasContext: context,
          viewport: renderViewport,
        }).promise;

        const image = await loadHtmlImage(canvas.toDataURL("image/png"));
        const worldSize = getBackgroundWorldSize(displayViewport.width, displayViewport.height);
        runHistoryAction(() => {
          setBackgroundAsset({
            kind: "pdf",
            name: file.name,
            image,
            center: {
              x: worldSize.width / 2,
              y: worldSize.height / 2,
            },
            width: worldSize.width,
            height: worldSize.height,
            rotation: 0,
            reference: null,
          });
        });
        setBackgroundStatus(`PDF caricato: ${file.name}`);
        fitBackgroundToViewport(worldSize.width, worldSize.height);
        return;
      }

      if (!file.type.startsWith("image/")) {
        throw new Error("Formato non supportato. Usa PDF o immagine raster.");
      }

      const objectUrl = URL.createObjectURL(file);

      try {
        const image = await loadHtmlImage(objectUrl);
        const worldSize = getBackgroundWorldSize(image.naturalWidth, image.naturalHeight);
        runHistoryAction(() => {
          setBackgroundAsset({
            kind: "image",
            name: file.name,
            image,
            center: {
              x: worldSize.width / 2,
              y: worldSize.height / 2,
            },
            width: worldSize.width,
            height: worldSize.height,
            rotation: 0,
            reference: null,
          });
        });
        setBackgroundStatus(`Immagine caricata: ${file.name}`);
        fitBackgroundToViewport(worldSize.width, worldSize.height);
      } finally {
        URL.revokeObjectURL(objectUrl);
      }
    } catch (error) {
      console.error(error);
      setBackgroundStatus(error instanceof Error ? error.message : "Errore durante il caricamento dello sfondo.");
    } finally {
      setIsBackgroundLoading(false);
    }
  };

  const handleGoogleMapCapture = async (capture: GoogleMapCapture) => {
    setIsBackgroundLoading(true);
    setBackgroundStatus(null);

    try {
      const response = await fetch(capture.imageUrl);
      if (!response.ok) {
        throw new Error(`Google Map screenshot non disponibile (${response.status}).`);
      }

      const objectUrl = URL.createObjectURL(await response.blob());

      try {
        const image = await loadHtmlImage(objectUrl);
        const width = Math.max(1, capture.widthMeters * 1000);
        const height = Math.max(1, capture.heightMeters * 1000);

        runHistoryAction(() => {
          setBackgroundAsset({
            kind: "image",
            name: capture.name,
            image,
            center: {
              x: width / 2,
              y: height / 2,
            },
            width,
            height,
            rotation: 0,
            reference: {
              startLocal: {
                x: -width / 2,
                y: 0,
              },
              endLocal: {
                x: width / 2,
                y: 0,
              },
              realLength: width,
            },
          });
        });

        setBackgroundTransparency(0);
        setIsGoogleMapDialogOpen(false);
        setIsBackgroundToolbarOpen(true);
        setIsBackgroundReferenceMode(false);
        setIsBackgroundCropMode(false);
        setBackgroundCropRect(null);
        setBackgroundReferenceLine(null);
        setBackgroundReferenceInput({ value: formatMeasurementValue(width) });
        setSelectedIds([]);
        setEditingId(BACKGROUND_ID);
        setBackgroundStatus(`Google Map importata: ${formatMeasurement(width)} x ${formatMeasurement(height)}.`);
        fitBackgroundToViewport(width, height);
      } finally {
        URL.revokeObjectURL(objectUrl);
      }
    } catch (error) {
      console.error(error);
      setBackgroundStatus(error instanceof Error ? error.message : "Errore durante l'import da Google Map.");
    } finally {
      setIsBackgroundLoading(false);
    }
  };

  const handleExportCanvasObjects = () => {
    const payload = createCanvasObjectStoragePayload({
      configurationId,
      shapes: polylines,
      dimensions,
      textEntities,
      iconEntities,
      backgroundAsset,
      backgroundTransparency,
    });
    const blob = new Blob([JSON.stringify(payload, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `canvas-objects-${configurationId ?? "draft"}-${Date.now()}.json`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const isCanvasObjectStoragePayload = (value: unknown): value is CanvasObjectStoragePayload => {
    if (!value || typeof value !== "object") return false;

    const candidate = value as Partial<CanvasObjectStoragePayload>;
    return candidate.schemaVersion === 1 && Array.isArray(candidate.objects);
  };

  const handleImportCanvasObjects = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    try {
      const parsed = JSON.parse(await file.text()) as unknown;

      if (!isCanvasObjectStoragePayload(parsed)) {
        throw new Error("Invalid canvas objects file.");
      }

      const restored = restoreCanvasObjectsFromPayload(parsed);
      runHistoryAction(() => {
        setPolylines(restored.shapes);
        setDimensions(restored.dimensions);
        setTextEntities(restored.textEntities);
        setIconEntities(restored.iconEntities);
      });
      setSelectedIds([]);
      setEditingId(null);
      setGripContextMenu(null);
      setActivePolyline(null);
      setNumericEdit(null);
      setHoverSnap(null);
      setPrintExportStatus(
        `Imported ${
          restored.shapes.length +
          restored.dimensions.length +
          restored.textEntities.length +
          restored.iconEntities.length
        } canvas objects.`,
      );
    } catch (error) {
      console.error(error);
      setPrintExportStatus(error instanceof Error ? error.message : "Could not import canvas objects.");
    }
  };

  const clearBackground = () => {
    runHistoryAction(() => {
      setBackgroundAsset(null);
      setBackgroundStatus(null);
      setSelectedIds((current) => current.filter((id) => id !== BACKGROUND_ID));
      setEditingId((current) => (current === BACKGROUND_ID ? null : current));
      setIsBackgroundReferenceMode(false);
      setIsBackgroundCropMode(false);
      setBackgroundCropRect(null);
      setBackgroundReferenceLine(null);
      setBackgroundReferenceInput(null);
    });
  };

  const cancelBackgroundReference = () => {
    setIsBackgroundReferenceMode(false);
    setBackgroundReferenceLine(null);
    setBackgroundReferenceInput(null);
  };

  const cancelBackgroundCrop = () => {
    setIsBackgroundCropMode(false);
    setBackgroundCropRect(null);
  };

  const applyBackgroundCrop = async (start: CanvasPoint, end: CanvasPoint) => {
    if (!backgroundAsset) return;

    const cropBounds = getBackgroundCropWorldBounds(backgroundAsset, start, end);
    const cropWidth = cropBounds.maxX - cropBounds.minX;
    const cropHeight = cropBounds.maxY - cropBounds.minY;

    if (cropWidth < 1 || cropHeight < 1) {
      setBackgroundStatus("Area di crop troppo piccola.");
      return;
    }

    const sourceWidth = backgroundAsset.image.naturalWidth || backgroundAsset.image.width;
    const sourceHeight = backgroundAsset.image.naturalHeight || backgroundAsset.image.height;
    const pxPerWorldX = sourceWidth / Math.max(1, backgroundAsset.width);
    const pxPerWorldY = sourceHeight / Math.max(1, backgroundAsset.height);

    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(cropWidth * pxPerWorldX));
    canvas.height = Math.max(1, Math.round(cropHeight * pxPerWorldY));
    const context = canvas.getContext("2d");

    if (!context) {
      setBackgroundStatus("Contesto canvas crop non disponibile.");
      return;
    }

    context.clearRect(0, 0, canvas.width, canvas.height);
    context.save();
    context.scale(pxPerWorldX, pxPerWorldY);
    context.translate(-cropBounds.minX, -cropBounds.minY);
    context.translate(backgroundAsset.center.x, backgroundAsset.center.y);
    context.rotate((backgroundAsset.rotation * Math.PI) / 180);
    context.drawImage(
      backgroundAsset.image,
      -backgroundAsset.width / 2,
      -backgroundAsset.height / 2,
      backgroundAsset.width,
      backgroundAsset.height,
    );
    context.restore();

    const nextImage = await loadHtmlImage(canvas.toDataURL("image/png"));
    const cropCenterWorld = {
      x: (cropBounds.minX + cropBounds.maxX) / 2,
      y: (cropBounds.minY + cropBounds.maxY) / 2,
    };
    const nextReference = backgroundAsset.reference
      ? (() => {
          const referenceWorld = getBackgroundReferenceWorldPoints(backgroundAsset);
          if (!referenceWorld) return null;

          const isInside = [referenceWorld.start, referenceWorld.end].every(
            (point) =>
              point.x >= cropBounds.minX &&
              point.x <= cropBounds.maxX &&
              point.y >= cropBounds.minY &&
              point.y <= cropBounds.maxY,
          );

          if (!isInside) return null;

          return {
            ...backgroundAsset.reference,
            startLocal: {
              x: referenceWorld.start.x - cropCenterWorld.x,
              y: referenceWorld.start.y - cropCenterWorld.y,
            },
            endLocal: {
              x: referenceWorld.end.x - cropCenterWorld.x,
              y: referenceWorld.end.y - cropCenterWorld.y,
            },
          };
        })()
      : null;

    runHistoryAction(() => {
      setBackgroundAsset({
        ...backgroundAsset,
        image: nextImage,
        center: cropCenterWorld,
        width: cropWidth,
        height: cropHeight,
        rotation: 0,
        reference: nextReference,
      });
    });
    setBackgroundReferenceInput(nextReference ? { value: formatMeasurementValue(nextReference.realLength) } : null);
    setBackgroundStatus("Cropped background.");
  };

  const commitBackgroundReferenceScale = () => {
    if (!backgroundAsset || !backgroundAsset.reference || !backgroundReferenceInput) return;

    const realLength = parseMeasurementValue(backgroundReferenceInput.value);
    const referenceWorld = getBackgroundReferenceWorldPoints(backgroundAsset);
    if (!referenceWorld) return;

    const measuredLength = getDistance(referenceWorld.start, referenceWorld.end);

    if (realLength == null || realLength <= 0 || measuredLength <= 0) {
      return;
    }

    const scaleFactor = realLength / measuredLength;
    const scaledAnchorLocal = {
      x: backgroundAsset.reference.startLocal.x * scaleFactor,
      y: backgroundAsset.reference.startLocal.y * scaleFactor,
    };
    const scaledEndLocal = {
      x: backgroundAsset.reference.endLocal.x * scaleFactor,
      y: backgroundAsset.reference.endLocal.y * scaleFactor,
    };
    const rotatedAnchor = rotateVector(scaledAnchorLocal, backgroundAsset.rotation);
    const nextBackground: BackgroundAsset = {
      ...backgroundAsset,
      width: backgroundAsset.width * scaleFactor,
      height: backgroundAsset.height * scaleFactor,
      center: {
        x: referenceWorld.start.x - rotatedAnchor.x,
        y: referenceWorld.start.y - rotatedAnchor.y,
      },
      reference: {
        startLocal: scaledAnchorLocal,
        endLocal: scaledEndLocal,
        realLength,
      },
    };

    runHistoryAction(() => {
      setBackgroundAsset((current) => {
        if (!current || !current.reference) return current;
        return nextBackground;
      });
    });

    setBackgroundStatus(`Scaled background to ${formatMeasurement(realLength)}.`);
    setIsBackgroundReferenceMode(false);
    setBackgroundReferenceLine(null);
    setBackgroundReferenceInput({ value: formatMeasurementValue(realLength) });
    setSelectedIds([]);
    setEditingId(BACKGROUND_ID);

    const nextBounds = getBackgroundBounds(nextBackground);
    if (
      nextBounds.maxX - nextBounds.minX > viewportSize.width / viewport.scale ||
      nextBounds.maxY - nextBounds.minY > viewportSize.height / viewport.scale
    ) {
      fitBoundsToViewport(nextBounds);
    }
  };

  const parseAlignmentAngle = () => {
    const parsed = Number(backgroundAlignmentAngleDraft.replace(",", "."));
    if (!Number.isFinite(parsed)) return null;
    return parsed;
  };

  const getCounterclockwiseAngleTarget = (angle: number, targets: number[]) => {
    let closestTarget = targets[0];
    let closestDelta = Number.NEGATIVE_INFINITY;

    for (const target of targets) {
      const counterclockwiseDistance = (((angle - target) % 360) + 360) % 360;
      const delta = -counterclockwiseDistance;

      if (delta > closestDelta) {
        closestDelta = delta;
        closestTarget = target;
      }
    }

    return { target: closestTarget, delta: closestDelta };
  };

  const alignBackgroundReference = (mode: "horizontal" | "vertical") => {
    if (!backgroundAsset?.reference) return;

    const angle = parseAlignmentAngle();
    if (angle == null) {
      setBackgroundStatus("Angolo non valido.");
      return;
    }

    const target = getCounterclockwiseAngleTarget(angle, mode === "horizontal" ? [0, 180, -180] : [90, -90]);

    runHistoryAction(() => {
      setBackgroundAsset((current) =>
        current
          ? {
              ...current,
              rotation: current.rotation + target.delta,
            }
          : current,
      );
    });

    if (target.target == null) return;
    setBackgroundAlignmentAngleDraft(target.target.toFixed(2));
    setBackgroundStatus(mode === "horizontal" ? "Background aligned horizontally." : "Background aligned vertically.");
  };

  const zoomAtScreenPoint = (screenPoint: CanvasPoint, factor: number) => {
    setViewport((current) => {
      const nextScale = Math.max(MIN_ZOOM, current.scale * factor);
      const worldPoint = {
        x: (screenPoint.x - current.x) / current.scale,
        y: (screenPoint.y - current.y) / current.scale,
      };

      return {
        x: screenPoint.x - worldPoint.x * nextScale,
        y: screenPoint.y - worldPoint.y * nextScale,
        scale: nextScale,
      };
    });
  };

  const getLastCommittedPoint = (points: number[]) => {
    if (points.length < 4) return null;

    return getPointFromCoordinateArray(points, points.length - 4);
  };

  const lockToSpecificAxis = (anchor: CanvasPoint, point: CanvasPoint, axis: DirectionLockAxis) => {
    if (axis === "horizontal") {
      return { x: point.x, y: anchor.y };
    }

    return { x: anchor.x, y: point.y };
  };

  const getDirectionLockAxisForPoint = (anchor: CanvasPoint, point: CanvasPoint): DirectionLockAxis =>
    Math.abs(point.x - anchor.x) >= Math.abs(point.y - anchor.y) ? "horizontal" : "vertical";

  const applyPersistentDirectionLock = (anchor: CanvasPoint, point: CanvasPoint, shiftKey: boolean) => {
    if (!shiftKey) {
      return point;
    }

    const axis = directionLockAxisRef.current ?? getDirectionLockAxisForPoint(anchor, point);
    if (directionLockAxisRef.current == null) {
      directionLockAxisRef.current = axis;
      setDirectionLockAxis(axis);
    }

    return lockToSpecificAxis(anchor, point, axis);
  };

  const getDistanceToSegment = (point: CanvasPoint, start: CanvasPoint, end: CanvasPoint) => {
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const lengthSquared = dx * dx + dy * dy;

    if (lengthSquared === 0) {
      return getDistance(point, start);
    }

    const projection = ((point.x - start.x) * dx + (point.y - start.y) * dy) / lengthSquared;
    const clamped = Math.max(0, Math.min(1, projection));

    return getDistance(point, {
      x: start.x + clamped * dx,
      y: start.y + clamped * dy,
    });
  };

  const expandCollisionRect = (rect: LabelCollisionRect, padding: number): LabelCollisionRect => ({
    left: rect.left - padding,
    top: rect.top - padding,
    right: rect.right + padding,
    bottom: rect.bottom + padding,
  });

  const doCollisionRectsOverlap = (rectA: LabelCollisionRect, rectB: LabelCollisionRect) =>
    rectA.left < rectB.right && rectA.right > rectB.left && rectA.top < rectB.bottom && rectA.bottom > rectB.top;

  const getCollisionRectOverlapArea = (rectA: LabelCollisionRect, rectB: LabelCollisionRect) => {
    if (!doCollisionRectsOverlap(rectA, rectB)) return 0;
    return (
      (Math.min(rectA.right, rectB.right) - Math.max(rectA.left, rectB.left)) *
      (Math.min(rectA.bottom, rectB.bottom) - Math.max(rectA.top, rectB.top))
    );
  };

  const isPointInCollisionRect = (point: CanvasPoint, rect: LabelCollisionRect) =>
    point.x >= rect.left && point.x <= rect.right && point.y >= rect.top && point.y <= rect.bottom;

  const doesSegmentIntersectCollisionRect = (start: CanvasPoint, end: CanvasPoint, rect: LabelCollisionRect) => {
    if (isPointInCollisionRect(start, rect) || isPointInCollisionRect(end, rect)) {
      return true;
    }

    const rectEdges = [
      { start: { x: rect.left, y: rect.top }, end: { x: rect.right, y: rect.top } },
      { start: { x: rect.right, y: rect.top }, end: { x: rect.right, y: rect.bottom } },
      { start: { x: rect.right, y: rect.bottom }, end: { x: rect.left, y: rect.bottom } },
      { start: { x: rect.left, y: rect.bottom }, end: { x: rect.left, y: rect.top } },
    ];

    return rectEdges.some((edge) => getSegmentIntersection(start, end, edge.start, edge.end) != null);
  };

  const getSegmentsFromFlatPoints = (points: number[], closed: boolean) => {
    const vertices = getPolylineVertices(points);
    const segments: Array<{ start: CanvasPoint; end: CanvasPoint }> = [];

    for (let index = 0; index < vertices.length - 1; index += 1) {
      const start = vertices[index];
      const end = vertices[index + 1];
      if (start && end) {
        segments.push({ start, end });
      }
    }

    if (closed && vertices.length > 2) {
      const start = vertices[vertices.length - 1];
      const end = vertices[0];
      if (start && end && !areCanvasPointsEqual(start, end)) {
        segments.push({ start, end });
      }
    }

    return segments;
  };

  const getLifeLineLabelPlacements = (
    points: number[],
    closed: boolean,
    vertices: LifeLineLabelVertex[],
    placementSeed = 0,
  ): LifeLineLabelPlacement[] => {
    const labelHeight = getScreenInvariantSize(13);
    const minLabelWidth = getScreenInvariantSize(18);
    const charWidth = getScreenInvariantSize(6.4);
    const labelPadding = getScreenInvariantSize(2.5);
    const linePadding = getScreenInvariantSize(3);
    const baseGap = getScreenInvariantSize(4);
    const segments = getSegmentsFromFlatPoints(points, closed);
    const placedRects: LabelCollisionRect[] = [];

    const makeRect = (x: number, y: number, label: string): LabelCollisionRect => {
      const width = Math.max(minLabelWidth, label.length * charWidth);
      return {
        left: x,
        top: y,
        right: x + width,
        bottom: y + labelHeight,
      };
    };

    return vertices.map((vertex) => {
      const label = vertex.displayLabel || vertex.label;
      const width = Math.max(minLabelWidth, label.length * charWidth);
      const labelHash = Array.from(label).reduce((hash, char) => hash + char.charCodeAt(0), 0);
      const candidateDistances = [
        baseGap,
        baseGap + getScreenInvariantSize(4),
        baseGap + getScreenInvariantSize(9),
        baseGap + getScreenInvariantSize(16),
      ];
      const candidateOffsets = candidateDistances.flatMap((gap) => [
        { x: gap, y: -labelHeight - gap },
        { x: gap, y: gap },
        { x: -width - gap, y: -labelHeight - gap },
        { x: -width - gap, y: gap },
        { x: -width / 2, y: -labelHeight - gap },
        { x: -width / 2, y: gap },
        { x: gap, y: -labelHeight / 2 },
        { x: -width - gap, y: -labelHeight / 2 },
      ]);
      const candidateStartIndex = Math.abs(labelHash + placementSeed * 7) % candidateOffsets.length;
      const orderedCandidateOffsets = [
        ...candidateOffsets.slice(candidateStartIndex),
        ...candidateOffsets.slice(0, candidateStartIndex),
      ];

      const bestCandidate = orderedCandidateOffsets.reduce(
        (best, offset, candidateIndex) => {
          const x = vertex.x + offset.x;
          const y = vertex.y + offset.y;
          const rect = makeRect(x, y, label);
          const expandedLabelRect = expandCollisionRect(rect, labelPadding);
          const expandedLineRect = expandCollisionRect(rect, linePadding);

          const labelCollisionScore = placedRects.reduce(
            (score, placedRect) =>
              score + getCollisionRectOverlapArea(expandedLabelRect, expandCollisionRect(placedRect, labelPadding)),
            0,
          );
          const lineCollisionCount = segments.filter((segment) =>
            doesSegmentIntersectCollisionRect(segment.start, segment.end, expandedLineRect),
          ).length;
          const center = {
            x: rect.left + (rect.right - rect.left) / 2,
            y: rect.top + (rect.bottom - rect.top) / 2,
          };
          const nearestLineDistance = segments.reduce(
            (distance, segment) => Math.min(distance, getDistanceToSegment(center, segment.start, segment.end)),
            Number.POSITIVE_INFINITY,
          );
          const distancePenalty = Number.isFinite(nearestLineDistance)
            ? Math.max(0, getScreenInvariantSize(12) - nearestLineDistance)
            : 0;
          const score =
            labelCollisionScore * 1000 + lineCollisionCount * 100000 + distancePenalty * 50 + candidateIndex;

          return score < best.score
            ? {
                score,
                rect,
              }
            : best;
        },
        {
          score: Number.POSITIVE_INFINITY,
          rect: makeRect(vertex.x + baseGap, vertex.y - labelHeight - baseGap, label),
        },
      );

      placedRects.push(bestCandidate.rect);

      return {
        x: bestCandidate.rect.left,
        y: bestCandidate.rect.top,
      };
    });
  };

  const isPointInsideClosedPolyline = (point: CanvasPoint, points: number[]) => {
    let inside = false;

    for (
      let currentIndex = 0, previousIndex = points.length - 2;
      currentIndex < points.length;
      previousIndex = currentIndex, currentIndex += 2
    ) {
      const currentX = points[currentIndex];
      const currentY = points[currentIndex + 1];
      const previousX = points[previousIndex];
      const previousY = points[previousIndex + 1];
      if (currentX == null || currentY == null || previousX == null || previousY == null) continue;

      const intersects =
        currentY > point.y !== previousY > point.y &&
        point.x < ((previousX - currentX) * (point.y - currentY)) / (previousY - currentY) + currentX;

      if (intersects) {
        inside = !inside;
      }
    }

    return inside;
  };

  const getPolylineCenter = (points: number[]) => {
    if (points.length < 4) return null;

    let minX = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let minY = Number.POSITIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;

    for (let index = 0; index < points.length; index += 2) {
      const point = getPointFromCoordinateArray(points, index);
      if (!point) continue;
      minX = Math.min(minX, point.x);
      maxX = Math.max(maxX, point.x);
      minY = Math.min(minY, point.y);
      maxY = Math.max(maxY, point.y);
    }

    if (!Number.isFinite(minX) || !Number.isFinite(maxX) || !Number.isFinite(minY) || !Number.isFinite(maxY)) {
      return null;
    }

    return {
      center: {
        x: (minX + maxX) / 2,
        y: (minY + maxY) / 2,
      },
      minX,
      maxX,
      minY,
      maxY,
    };
  };

  const addShapeOsnapCandidates = (
    candidates: OsnapPoint[],
    shape:
      | Shape
      | {
          kind: "circle";
          center: CanvasPoint;
          radius: number;
        }
      | {
          kind: "arc";
          center: CanvasPoint;
          radius: number;
          startAngle: number;
          endAngle: number;
        }
      | {
          kind: "polyline" | "rectangle";
          points: number[];
        },
    anchor: CanvasPoint | null,
  ) => {
    const beforeIndex = candidates.length;
    addShapeOsnapCandidatesRule(candidates, shape, anchor);

    if (!("id" in shape) || (shape.kind !== "polyline" && shape.kind !== "rectangle")) {
      return;
    }

    for (let index = beforeIndex; index < candidates.length; index += 1) {
      const candidate = candidates[index];
      if (!candidate) continue;
      candidate.sourceEntityId = shape.id;
      candidate.sourceContour = "outer";
      if (candidate.type === "MID" || candidate.type === "PERP") {
        const segmentIndex = index - beforeIndex - shape.points.length / 2;
        if (segmentIndex >= 0) {
          candidate.sourceSegmentStartVertexIndex = segmentIndex * 2;
        }
      }
    }

    for (const [holeIndex, hole] of (shape.holes ?? []).entries()) {
      const holeStartIndex = candidates.length;
      addShapeOsnapCandidatesRule(candidates, { kind: "polyline", points: hole }, anchor);
      for (let index = holeStartIndex; index < candidates.length; index += 1) {
        const candidate = candidates[index];
        if (!candidate) continue;
        candidate.sourceEntityId = shape.id;
        candidate.sourceContour = "hole";
        candidate.sourceHoleIndex = holeIndex;
        if (candidate.type === "MID" || candidate.type === "PERP") {
          const segmentIndex = index - holeStartIndex - hole.length / 2;
          if (segmentIndex >= 0) {
            candidate.sourceSegmentStartVertexIndex = segmentIndex * 2;
          }
        }
      }
    }
  };

  const addDimensionOsnapCandidates = (
    candidates: OsnapPoint[],
    dimension: Pick<Dimension, "start" | "end" | "offset">,
    anchor: CanvasPoint | null,
  ) => addDimensionOsnapCandidatesRule(candidates, dimension, anchor, getDimensionLayout);

  const addActivePolylineOsnapCandidates = (
    candidates: OsnapPoint[],
    activePoints: number[] | null | undefined,
    anchor: CanvasPoint | null,
  ) => {
    if (!activePoints || activePoints.length < 4) return;
    const firstX = activePoints[0];
    const firstY = activePoints[1];
    if (typeof firstX !== "number" || typeof firstY !== "number") return;

    candidates.push({
      x: firstX,
      y: firstY,
      type: "END",
      distance: 0,
    });

    const committedPoints = activePoints.slice(0, -2);
    if (committedPoints.length < 6) return;

    const snapSourcePoints = committedPoints.slice(0, -2);
    if (snapSourcePoints.length < 4) return;

    addShapeOsnapCandidatesRule(candidates, { kind: "polyline", points: snapSourcePoints }, anchor);
  };

  const getActivePolylineOsnapSegments = (activePoints: number[] | null | undefined) => {
    if (!activePoints || activePoints.length < 8) return [] as Array<{ start: CanvasPoint; end: CanvasPoint }>;

    const committedPoints = activePoints.slice(0, -2);
    const snapSourcePoints = committedPoints.slice(0, -2);
    const segments: Array<{ start: CanvasPoint; end: CanvasPoint }> = [];

    for (let index = 0; index <= snapSourcePoints.length - 4; index += 2) {
      const startX = snapSourcePoints[index];
      const startY = snapSourcePoints[index + 1];
      const endX = snapSourcePoints[index + 2];
      const endY = snapSourcePoints[index + 3];
      if (
        typeof startX !== "number" ||
        typeof startY !== "number" ||
        typeof endX !== "number" ||
        typeof endY !== "number"
      ) {
        continue;
      }
      segments.push({
        start: { x: startX, y: startY },
        end: { x: endX, y: endY },
      });
    }

    return segments;
  };

  const getNearestOsnap = (
    pointer: CanvasPoint,
    activePoints?: number[] | null,
    previewMove?: MoveState | null,
    excludedEntityIds?: string[] | null,
    excludedSnapPoints?: CanvasPoint[] | null,
    anchorPoint?: CanvasPoint | null,
    ignoreGripModeEntityFilter = false,
    excludedSnapSegments?: SnapExcludedSegment[] | null,
    requireIdentifiedLinearOsnap = false,
    excludedOsnapTypes: OsnapType[] = [],
  ) => {
    const candidates: OsnapPoint[] = [];
    const lastCommittedPoint = activePoints ? getLastCommittedPoint(activePoints) : null;
    const anchor =
      anchorPoint ??
      (lastCommittedPoint && typeof lastCommittedPoint.x === "number" && typeof lastCommittedPoint.y === "number"
        ? lastCommittedPoint
        : null);
    const snapThreshold = getScreenInvariantSize(OSNAP_THRESHOLD);
    const previewEntityIds = previewMove ? new Set(previewMove.entityIds) : null;
    const excludedIdSet = excludedEntityIds && excludedEntityIds.length > 0 ? new Set(excludedEntityIds) : null;
    const excludedPoints = excludedSnapPoints ?? [];
    const excludedSegments = excludedSnapSegments ?? [];

    addActivePolylineOsnapCandidates(candidates, activePoints, anchor);

    for (const polyline of polylines) {
      if (excludedIdSet?.has(polyline.id)) continue;
      if (isGridLikeEntity(polyline)) continue;
      if (!ignoreGripModeEntityFilter && isGripModeActive && polyline.id !== editingId) continue;
      if (!isShapeVisible(polyline)) continue;
      if (previewEntityIds?.has(polyline.id)) continue;
      addShapeOsnapCandidates(candidates, polyline, anchor);
    }

    for (const dimension of dimensions) {
      if (excludedIdSet?.has(dimension.id)) continue;
      if (isGridLikeEntity(dimension)) continue;
      if (!ignoreGripModeEntityFilter && isGripModeActive && dimension.id !== editingId) continue;
      if (!isDimensionVisible(dimension)) continue;
      if (previewEntityIds?.has(dimension.id)) continue;
      addDimensionOsnapCandidates(candidates, dimension, anchor);
    }

    if (enabledOsnaps.INT) {
      const visibleSegments: Array<{
        start: CanvasPoint;
        end: CanvasPoint;
        entityId?: string;
      }> = [];
      const activePolylineSegments = getActivePolylineOsnapSegments(activePoints);

      for (const shape of polylines) {
        if (excludedIdSet?.has(shape.id)) continue;
        if (isGridLikeEntity(shape)) continue;
        if (!ignoreGripModeEntityFilter && isGripModeActive && shape.id !== editingId) continue;
        if (!isShapeVisible(shape)) continue;
        if (previewEntityIds?.has(shape.id)) continue;
        for (const segment of getShapeSegments(shape)) {
          visibleSegments.push({ ...segment, entityId: shape.id });
        }
      }

      for (const dimension of dimensions) {
        if (excludedIdSet?.has(dimension.id)) continue;
        if (isGridLikeEntity(dimension)) continue;
        if (!ignoreGripModeEntityFilter && isGripModeActive && dimension.id !== editingId) continue;
        if (!isDimensionVisible(dimension)) continue;
        if (previewEntityIds?.has(dimension.id)) continue;
        visibleSegments.push({ start: dimension.start, end: dimension.end, entityId: dimension.id });
      }

      const activeSegmentStartIndex = visibleSegments.length;
      visibleSegments.push(...activePolylineSegments);

      for (let firstIndex = 0; firstIndex < visibleSegments.length; firstIndex += 1) {
        for (let secondIndex = firstIndex + 1; secondIndex < visibleSegments.length; secondIndex += 1) {
          if (firstIndex >= activeSegmentStartIndex && secondIndex >= activeSegmentStartIndex) continue;
          const first = visibleSegments[firstIndex];
          const second = visibleSegments[secondIndex];
          if (!first || !second) continue;
          const intersection = getSegmentIntersection(first.start, first.end, second.start, second.end);
          if (!intersection) continue;
          candidates.push({
            ...intersection,
            type: "INT",
            distance: 0,
            sourceEntityId: first.entityId,
          });
        }
      }

      const trackingConstructionLines = osnapTrackingLines.map((line) => ({
        start: line.start,
        end: line.end,
      }));
      const temporaryConstructionLines = [
        ...constructionLines.filter((line) => !isGridLikeEntity(line)),
        ...trackingConstructionLines,
      ];

      for (const constructionLine of temporaryConstructionLines) {
        for (const segment of visibleSegments) {
          const intersection = getInfiniteLineIntersection(
            constructionLine.start,
            constructionLine.end,
            segment.start,
            segment.end,
          );
          if (!intersection) continue;
          candidates.push({
            ...intersection,
            type: "INT",
            distance: 0,
            sourceEntityId: segment.entityId,
          });
        }
      }

      for (let firstIndex = 0; firstIndex < temporaryConstructionLines.length; firstIndex += 1) {
        for (let secondIndex = firstIndex + 1; secondIndex < temporaryConstructionLines.length; secondIndex += 1) {
          const first = temporaryConstructionLines[firstIndex];
          const second = temporaryConstructionLines[secondIndex];
          if (!first || !second) continue;
          const intersection = getInfiniteLineIntersection(first.start, first.end, second.start, second.end);
          if (!intersection) continue;
          candidates.push({
            ...intersection,
            type: "INT",
            distance: 0,
          });
        }
      }
    }

    if (previewMove) {
      const deltaX = pointer.x - previewMove.anchorPoint.x;
      const deltaY = pointer.y - previewMove.anchorPoint.y;

      for (const snapshot of previewMove.shapeSnapshots) {
        if (snapshot.kind === "circle") {
          addShapeOsnapCandidates(
            candidates,
            {
              kind: "circle",
              center: {
                x: snapshot.center.x + deltaX,
                y: snapshot.center.y + deltaY,
              },
              radius: snapshot.radius,
            },
            anchor,
          );
          continue;
        }

        if (snapshot.kind === "arc") {
          addShapeOsnapCandidates(
            candidates,
            {
              kind: "arc",
              center: {
                x: snapshot.center.x + deltaX,
                y: snapshot.center.y + deltaY,
              },
              radius: snapshot.radius,
              startAngle: snapshot.startAngle,
              endAngle: snapshot.endAngle,
            },
            anchor,
          );
          continue;
        }

        addShapeOsnapCandidates(
          candidates,
          {
            kind: snapshot.kind,
            points: snapshot.points.map((value, index) => (index % 2 === 0 ? value + deltaX : value + deltaY)),
          },
          anchor,
        );
      }

      for (const snapshot of previewMove.dimensionSnapshots) {
        addDimensionOsnapCandidates(
          candidates,
          {
            start: {
              x: snapshot.start.x + deltaX,
              y: snapshot.start.y + deltaY,
            },
            end: {
              x: snapshot.end.x + deltaX,
              y: snapshot.end.y + deltaY,
            },
            offset: snapshot.offset,
          },
          anchor,
        );
      }
    }

    let closestSnap: OsnapPoint | null = null;

    for (const candidate of candidates) {
      if (!enabledOsnaps[candidate.type]) continue;
      if (excludedOsnapTypes.includes(candidate.type)) continue;
      if (
        requireIdentifiedLinearOsnap &&
        (candidate.type === "END" || candidate.type === "MID" || candidate.type === "PERP") &&
        !candidate.sourceEntityId
      ) {
        continue;
      }
      if (
        excludedPoints.some(
          (excludedPoint) =>
            Math.abs(candidate.x - excludedPoint.x) < 0.0001 && Math.abs(candidate.y - excludedPoint.y) < 0.0001,
        )
      ) {
        continue;
      }
      if (excludedSegments.some((segment) => getDistanceToSegment(candidate, segment.start, segment.end) <= 0.0001)) {
        continue;
      }
      const distance = getDistance(pointer, candidate);
      if (distance > snapThreshold) continue;

      const candidatePriority = getOsnapPriority(candidate.type);
      const closestPriority = closestSnap ? getOsnapPriority(closestSnap.type) : Number.POSITIVE_INFINITY;
      const priorityTolerance = snapThreshold * 0.4;

      if (
        !closestSnap ||
        (candidatePriority < closestPriority && distance <= closestSnap.distance + priorityTolerance) ||
        (candidatePriority === closestPriority && distance < closestSnap.distance)
      ) {
        closestSnap = { ...candidate, distance };
      }
    }

    return closestSnap;
  };

  const resolvePolylinePoint = (point: CanvasPoint, shiftKey: boolean, current: number[] | null) => {
    const osnap = getNearestOsnap(point, current);
    let nextPoint: CanvasPoint = osnap ? { x: osnap.x, y: osnap.y } : applyGridSnap(point);

    if (shiftKey && current) {
      const anchor = getLastCommittedPoint(current);
      if (anchor) {
        nextPoint = applyPersistentDirectionLock(anchor, nextPoint, shiftKey);
      }
    }

    return { point: nextPoint, osnap };
  };

  const resolvePointSeriesPoint = (point: CanvasPoint, shiftKey: boolean, current: number[] | null) => {
    const osnap = getNearestOsnap(point, current);
    let nextPoint: CanvasPoint = osnap ? { x: osnap.x, y: osnap.y } : applyGridSnap(point);

    if (shiftKey && current && current.length >= 2) {
      const anchor = getPointFromCoordinateArray(current, 0);
      if (anchor) {
        nextPoint = applyPersistentDirectionLock(anchor, nextPoint, shiftKey);
      }
    }

    return { point: nextPoint, osnap };
  };

  const resolveSnapPoint = (
    point: CanvasPoint,
    activePoints?: number[] | null,
    previewMove?: MoveState | null,
    excludedEntityIds?: string[] | null,
    excludedSnapPoints?: CanvasPoint[] | null,
    allowGridFallback = true,
    anchorPoint?: CanvasPoint | null,
    ignoreGripModeEntityFilter = false,
    excludedSnapSegments?: SnapExcludedSegment[] | null,
    requireIdentifiedLinearOsnap = false,
    excludedOsnapTypes: OsnapType[] = [],
  ) => {
    const osnap = getNearestOsnap(
      point,
      activePoints,
      previewMove,
      excludedEntityIds,
      excludedSnapPoints,
      anchorPoint,
      ignoreGripModeEntityFilter,
      excludedSnapSegments,
      requireIdentifiedLinearOsnap,
      excludedOsnapTypes,
    );
    return {
      point: osnap ? { x: osnap.x, y: osnap.y } : allowGridFallback ? applyGridSnap(point, excludedSnapPoints) : point,
      osnap,
    };
  };

  const resolveMoveCopyPoint = (point: CanvasPoint, shiftKey: boolean, currentMove: MoveState | null) => {
    const resolved =
      currentMove?.mode === "move"
        ? resolveSnapPoint(point, null, null, currentMove.entityIds, null, true, currentMove.anchorPoint)
        : currentMove?.mode === "copy"
          ? resolveSnapPoint(
              point,
              null,
              null,
              currentMove.entityIds,
              [currentMove.anchorPoint],
              false,
              currentMove.anchorPoint,
            )
          : resolveSnapPoint(point, null, currentMove, null, null, true, currentMove?.anchorPoint);
    let nextPoint = resolved.point;

    if (shiftKey && currentMove) {
      nextPoint = applyPersistentDirectionLock(currentMove.anchorPoint, nextPoint, shiftKey);
    }

    return {
      point: nextPoint,
      osnap: resolved.osnap,
    };
  };

  const resolveScaleRotatePoint = (point: CanvasPoint) =>
    resolveSnapPoint(point, null, null, null, null, true, null, true);

  const resolveIconPlacementPoint = (point: CanvasPoint) =>
    resolveSnapPoint(point, null, null, null, null, true, null, true);

  const resolveArcPoint = (point: CanvasPoint, shiftKey: boolean, arc: ActiveArc | null) => {
    const resolved = resolveSnapPoint(
      point,
      getActiveArcSnapPoints(arc),
      null,
      null,
      null,
      true,
      arc?.center ?? null,
      true,
    );
    let nextPoint = resolved.point;

    if (shiftKey && arc) {
      nextPoint = applyPersistentDirectionLock(arc.center, nextPoint, shiftKey);
    }

    return {
      point: nextPoint,
      osnap: resolved.osnap,
    };
  };

  const resolveBackgroundReferencePoint = (
    point: CanvasPoint,
    shiftKey: boolean,
    current: BackgroundReferenceLine | null,
  ) => {
    let nextPoint = point;

    if (shiftKey && current) {
      nextPoint = applyPersistentDirectionLock(current.start, nextPoint, shiftKey);
    }

    return nextPoint;
  };

  const shouldFinishPolylineOnStartPoint = (point: CanvasPoint, current: number[] | null) => {
    if (!current) return false;

    const committedPoints = current.slice(0, -2);
    if (committedPoints.length < 4) return false;
    const snapThreshold = getScreenInvariantSize(OSNAP_THRESHOLD);

    return getDistance(point, getPointFromCoordinateArray(current, 0) ?? point) <= snapThreshold;
  };

  const buildOffsetSourceShape = (shape: Shape, sourceContour: OffsetSourceContour): Shape | null =>
    buildOffsetSourceShapeRule(shape, sourceContour) as Shape | null;

  const buildOffsetShapePreview = (
    shape: Shape,
    pointer: CanvasPoint,
    distance: number,
    sourceContour: OffsetSourceContour = { kind: "outer" },
  ) => buildOffsetShapePreviewRule(shape, pointer, distance, sourceContour);

  const getEntityHitIds = (point: CanvasPoint) => {
    const hitThreshold = getScreenInvariantSize(SELECT_HIT_THRESHOLD);
    const hitIds: string[] = [];
    const pushHitId = (id: string) => {
      if (!hitIds.includes(id)) {
        hitIds.push(id);
      }
    };
    const constructionHits: Array<{ id: string; distance: number }> = [];

    for (const constructionLine of constructionLines) {
      const lineLayer = getConstructionLineLayer(constructionLine);
      if (!isLayerVisible(lineLayer) || isLayerLocked(lineLayer)) continue;
      const distance = getDistanceToLine(point, constructionLine.start, constructionLine.end);
      if (distance <= hitThreshold) {
        constructionHits.push({ id: constructionLine.id, distance });
      }
    }

    constructionHits.sort((left, right) => left.distance - right.distance).forEach((hit) => pushHitId(hit.id));

    const topToBottomLayers = [...normalizeLayerOrder(layerOrder)].reverse();

    for (const layer of topToBottomLayers) {
      const layerHits = new Map<string, number>();
      const recordLayerHit = (id: string, distance: number) => {
        if (distance > hitThreshold) return;
        const currentDistance = layerHits.get(id);
        if (currentDistance == null || distance < currentDistance) {
          layerHits.set(id, distance);
        }
      };

      for (const polyline of polylines) {
        if (getShapeLayer(polyline) !== layer) continue;
        if (!isShapeInteractive(polyline)) continue;
        if (polyline.kind === "circle") {
          const centerDistance = getDistance(point, polyline.center);
          const edgeDistance = Math.abs(centerDistance - polyline.radius);
          recordLayerHit(polyline.id, edgeDistance);
          continue;
        }

        if (polyline.kind === "arc") {
          const arcDistance = getDistanceToArc(point, polyline);
          recordLayerHit(polyline.id, arcDistance);
          continue;
        }

        for (const contour of [polyline.points, ...(polyline.holes ?? [])]) {
          for (let index = 0; index <= contour.length - 4; index += 2) {
            const start = getPointFromCoordinateArray(contour, index);
            const end = getPointFromCoordinateArray(contour, index + 2);
            if (!start || !end) continue;
            const distance = getDistanceToSegment(point, start, end);
            recordLayerHit(polyline.id, distance);
          }

          if (polyline.closed && contour.length >= 6) {
            const start = getPointFromCoordinateArray(contour, contour.length - 2);
            const end = getPointFromCoordinateArray(contour, 0);
            if (!start || !end) continue;
            const distance = getDistanceToSegment(point, start, end);
            recordLayerHit(polyline.id, distance);
          }
        }

        for (const hole of polyline.circleHoles ?? []) {
          const centerDistance = getDistance(point, hole.center);
          const edgeDistance = Math.abs(centerDistance - hole.radius);
          recordLayerHit(polyline.id, edgeDistance);
        }
      }

      for (const dimension of dimensions) {
        if (getDimensionLayer(dimension) !== layer) continue;
        if (!isDimensionInteractive(dimension)) continue;
        const geometry = getDimensionLayout(dimension.start, dimension.end, dimension.offset);
        if (!geometry) continue;

        const dimensionDistance = getDistanceToSegment(
          point,
          geometry.extensionStartOffset,
          geometry.extensionEndOffset,
        );

        recordLayerHit(dimension.id, dimensionDistance);
      }

      for (const textEntity of textEntities) {
        if (getTextEntityLayer(textEntity) !== layer) continue;
        if (!isTextEntityInteractive(textEntity)) continue;

        recordLayerHit(textEntity.id, getDistanceToBounds(point, getTextEntityBounds(textEntity)));
      }

      for (const iconEntity of iconEntities) {
        if (getIconEntityLayer(iconEntity) !== layer) continue;
        if (!isIconEntityInteractive(iconEntity)) continue;

        recordLayerHit(iconEntity.id, getDistanceToBounds(point, getIconEntityBounds(iconEntity)));
      }

      Array.from(layerHits.entries())
        .sort((left, right) => left[1] - right[1])
        .forEach(([id]) => pushHitId(id));
    }

    return hitIds;
  };

  const getEntityHitId = (point: CanvasPoint) => getEntityHitIds(point)[0] ?? null;

  const getAutomaticLifeLineSourceShapesFromIds = (ids: string[]) => {
    const sourceShapes = ids.flatMap((id) => {
      const shape = getShapeById(id);
      return shape ? [shape] : [];
    });
    const automaticLifeLineRoofGroupIds = new Set(
      sourceShapes
        .filter(
          (shape) =>
            shape.roofGroupId &&
            (shape.roofGroupCategory === "Flat" ||
              shape.roofGroupCategory === "Curved" ||
              shape.entityStyleKind === "single-gable-roof" ||
              shape.entityStyleKind === "multi-gable-roof" ||
              shape.entityStyleKind === "single-pitched-roof" ||
              shape.entityStyleKind === "multi-shed-roof" ||
              shape.entityStyleKind === "roof-ridge" ||
              shape.entityStyleKind === "roof-hip" ||
              shape.entityStyleKind === "roof-valley" ||
              shape.entityStyleKind === "roof-hip-valley"),
        )
        .map((shape) => shape.roofGroupId as string),
    );

    if (automaticLifeLineRoofGroupIds.size === 0) return sourceShapes;

    const sourceIds = new Set(sourceShapes.map((shape) => shape.id));
    const roofGroupShapes = polylines.filter(
      (shape) => shape.roofGroupId && automaticLifeLineRoofGroupIds.has(shape.roofGroupId) && !sourceIds.has(shape.id),
    );

    return [...sourceShapes, ...roofGroupShapes];
  };

  const getAutomaticLifeLineSourceIdsFromShapes = (sourceShapes: AutomaticLifeLineSourceShape[]) =>
    Array.from(new Set(sourceShapes.map((shape) => shape.id)));

  const getAutomaticLifeLineSourceShapesAtPoint = (point: CanvasPoint) => {
    const hitShape = getEntityHitIds(point)
      .map((id) => getShapeById(id))
      .find((shape): shape is Shape => Boolean(shape && shape.kind !== "circle" && shape.kind !== "arc"));

    if (!hitShape) return [];

    if (hitShape.roofGroupId) {
      const groupShapes = getAutomaticLifeLineSourceShapesFromIds(getRoofGroupMemberIds(hitShape.roofGroupId));
      return groupShapes.length > 0 ? groupShapes : [hitShape];
    }

    return [hitShape];
  };

  const automaticLifeLineOffsetPreview = useMemo(() => {
    if (
      toolMode !== "automaticLifeLine" ||
      !automaticLifeLinePreviewPoint ||
      !automaticLifeLineSourceIds ||
      automaticLifeLineSourceIds.length === 0
    ) {
      return [];
    }

    const sourceShapes = getAutomaticLifeLineSourceShapesFromIds(automaticLifeLineSourceIds);
    if (sourceShapes.length === 0) return [];

    const paths = buildAutomaticLifeLinePaths(sourceShapes, {
      flatRoofOffsetDistance: getAutomaticLifeLineOffsetDistance(),
      ridgeOffsetDistance: getAutomaticLifeLineOffsetDistance(),
      flatRoofOffsetSidePoint: automaticLifeLinePreviewPoint,
    });

    return paths.flatMap((path, pathIndex) =>
      calculateRenderedLifeLines(path.points, {
        closed: path.closed,
        systemMaxSpan: effectiveLifeLineSystemSettings.maxSpacing,
        chamferAcuteAngles: effectiveLifeLineSystemSettings.chamferAcuteAngles,
        chamferSegmentLength: effectiveLifeLineSystemSettings.chamferSegmentLength,
        maxSingleLineLength: effectiveLifeLineSystemSettings.maxSingleLineLength,
        maxCornersPerLifeline: effectiveLifeLineSystemSettings.maxCornersPerLifeline,
      }).renderedLifeLines.map((renderedLifeLine) => ({
        ...renderedLifeLine,
        previewKey: `${pathIndex}-${renderedLifeLine.lineNumber}`,
      })),
    );
  }, [
    automaticLifeLinePreviewPoint,
    automaticLifeLineSourceIds,
    automaticLifeLineOffsetDistance,
    effectiveLifeLineSystemSettings.chamferAcuteAngles,
    effectiveLifeLineSystemSettings.chamferSegmentLength,
    effectiveLifeLineSystemSettings.maxCornersPerLifeline,
    effectiveLifeLineSystemSettings.maxSingleLineLength,
    effectiveLifeLineSystemSettings.maxSpacing,
    numericEdit,
    polylines,
    toolMode,
  ]);

  const handleAutomaticLifeLineClick = (point: CanvasPoint) => {
    if (automaticLifeLineSourceIds && automaticLifeLineSourceIds.length > 0) {
      const sourceShapes = getAutomaticLifeLineSourceShapesFromIds(automaticLifeLineSourceIds);
      if (sourceShapes.length === 0) {
        setAutomaticLifeLineSourceIds(null);
        setSelectedIds([]);
        setNumericEdit(null);
        setLifeLineRoofSelectionWarning("Select a roof or roof part to create an automatic lifeline.");
        return false;
      }

      return commitAutomaticLifeLineFromSourceShapes(sourceShapes, getAutomaticLifeLineOffsetDistance(), point);
    }

    const sourceShapes = getAutomaticLifeLineSourceShapesAtPoint(point);
    if (sourceShapes.length === 0) {
      setLifeLineRoofSelectionWarning("Select a roof or roof part to create an automatic lifeline.");
      return false;
    }

    const sourceIds = getAutomaticLifeLineSourceIdsFromShapes(sourceShapes);
    setAutomaticLifeLineSourceIds(sourceIds);
    setAutomaticLifeLinePreviewPoint(null);
    setSelectedIds(sourceIds);
    setEditingId(null);
    setSelectionCycle(null);
    setLifeLineRoofSelectionWarning(null);
    showAutomaticLifeLineOffsetInput();
    return true;
  };

  const getSelectedEntityAreaHitId = (point: CanvasPoint) => {
    const selectedIdSet = new Set(selectedIds);
    const topToBottomLayers = [...normalizeLayerOrder(layerOrder)].reverse();

    for (const layer of topToBottomLayers) {
      const hitShape = polylines.find(
        (shape) =>
          selectedIdSet.has(shape.id) &&
          getShapeLayer(shape) === layer &&
          isShapeInteractive(shape) &&
          isPointInsideShapeArea(shape, point),
      );

      if (hitShape) {
        return hitShape.id;
      }

      const hitIcon = iconEntities.find(
        (iconEntity) =>
          selectedIdSet.has(iconEntity.id) &&
          getIconEntityLayer(iconEntity) === layer &&
          isIconEntityInteractive(iconEntity) &&
          getDistanceToBounds(point, getIconEntityBounds(iconEntity)) === 0,
      );

      if (hitIcon) {
        return hitIcon.id;
      }
    }

    return null;
  };

  const getOffsetTargetAtPoint = (
    point: CanvasPoint,
  ): { entityId: string; sourceContour: OffsetSourceContour } | null => {
    let closestTarget: { entityId: string; sourceContour: OffsetSourceContour } | null = null;
    let closestDistance = Number.POSITIVE_INFINITY;
    const hitThreshold = getScreenInvariantSize(SELECT_HIT_THRESHOLD);

    for (const polyline of polylines) {
      if (!isShapeInteractive(polyline)) continue;

      if (polyline.kind === "circle") {
        const centerDistance = getDistance(point, polyline.center);
        const edgeDistance = Math.abs(centerDistance - polyline.radius);
        if (edgeDistance <= hitThreshold && edgeDistance < closestDistance) {
          closestDistance = edgeDistance;
          closestTarget = {
            entityId: polyline.id,
            sourceContour: { kind: "outer" },
          };
        }
        continue;
      }

      if (polyline.kind === "arc") {
        continue;
      }

      const contourTargets: Array<{ points: number[]; sourceContour: OffsetSourceContour }> = [
        {
          points: polyline.points,
          sourceContour: { kind: "outer" },
        },
        ...(polyline.holes ?? []).map((hole, holeIndex) => ({
          points: hole,
          sourceContour: { kind: "polygon-hole" as const, holeIndex },
        })),
      ];

      for (const contourTarget of contourTargets) {
        const contour = contourTarget.points;
        for (let index = 0; index <= contour.length - 4; index += 2) {
          const start = getPointFromCoordinateArray(contour, index);
          const end = getPointFromCoordinateArray(contour, index + 2);
          if (!start || !end) continue;
          const distance = getDistanceToSegment(point, start, end);

          if (distance <= hitThreshold && distance < closestDistance) {
            closestDistance = distance;
            closestTarget = {
              entityId: polyline.id,
              sourceContour: contourTarget.sourceContour,
            };
          }
        }

        if (polyline.closed && contour.length >= 6) {
          const start = getPointFromCoordinateArray(contour, contour.length - 2);
          const end = getPointFromCoordinateArray(contour, 0);
          if (!start || !end) continue;
          const distance = getDistanceToSegment(point, start, end);

          if (distance <= hitThreshold && distance < closestDistance) {
            closestDistance = distance;
            closestTarget = {
              entityId: polyline.id,
              sourceContour: contourTarget.sourceContour,
            };
          }
        }
      }

      for (const [holeIndex, hole] of (polyline.circleHoles ?? []).entries()) {
        const centerDistance = getDistance(point, hole.center);
        const edgeDistance = Math.abs(centerDistance - hole.radius);
        if (edgeDistance <= hitThreshold && edgeDistance < closestDistance) {
          closestDistance = edgeDistance;
          closestTarget = {
            entityId: polyline.id,
            sourceContour: {
              kind: "circle-hole",
              holeIndex,
            },
          };
        }
      }
    }

    return closestTarget;
  };

  const updatePolylineVertex = (
    polylineId: string,
    vertexIndex: number,
    nextPoint: CanvasPoint,
    holeIndex?: number,
    linkedVertexIndices?: number[],
  ) => {
    setPolylines((current) => {
      const target = current.find(
        (shape): shape is PolylineLikeShape =>
          shape.id === polylineId && shape.kind !== "circle" && shape.kind !== "arc",
      );

      if (target && holeIndex == null && (target.lifeLineVertices?.length ?? 0) > 0) {
        const targetDesignPoints =
          target.lifeLineDesignPoints?.map((point) => ({ ...point })) ??
          getLifeLineGripPoints(target, getShapeGripPoints(target))
            .filter((grip) => grip.gripKind === "vertex")
            .map((grip) => grip.point);
        const designPointIndex = Math.floor(vertexIndex / 2);
        const sharedSourcePoints = [
          designPointIndex >= 0 && designPointIndex < targetDesignPoints.length
            ? targetDesignPoints[designPointIndex]
            : null,
          ...(linkedVertexIndices ?? []).map((linkedVertexIndex) => {
            const linkedDesignPointIndex = Math.floor(linkedVertexIndex / 2);
            return linkedDesignPointIndex >= 0 && linkedDesignPointIndex < targetDesignPoints.length
              ? targetDesignPoints[linkedDesignPointIndex]
              : null;
          }),
        ].filter((point): point is CanvasPoint => point != null);

        if (sharedSourcePoints.length === 0) {
          return current;
        }

        return current.map((polyline) => {
          if (polyline.kind === "circle" || polyline.kind === "arc" || (polyline.lifeLineVertices?.length ?? 0) === 0) {
            return polyline;
          }

          const designPoints =
            polyline.lifeLineDesignPoints?.map((point) => ({ ...point })) ??
            getLifeLineGripPoints(polyline, getShapeGripPoints(polyline))
              .filter((grip) => grip.gripKind === "vertex")
              .map((grip) => grip.point);
          let hasSharedVertex = false;
          const nextDesignPoints = designPoints.map((point) => {
            if (!sharedSourcePoints.some((sourcePoint) => areCanvasPointsEqual(sourcePoint, point))) {
              return point;
            }

            hasSharedVertex = true;
            return nextPoint;
          });

          if (!hasSharedVertex || nextDesignPoints.length < 2) {
            return polyline;
          }

          const renderedLifeLinePatch = buildRenderedLifeLineShapePatch(polyline, nextDesignPoints, {
            disableAutoSplit: true,
          });
          return renderedLifeLinePatch
            ? {
                ...polyline,
                ...renderedLifeLinePatch,
              }
            : polyline;
        });
      }

      return current.map((polyline) => {
        if (polyline.id !== polylineId || polyline.kind === "circle" || polyline.kind === "arc") return polyline;

        if (holeIndex != null) {
          const nextHoles = (polyline.holes ?? []).map((hole) => [...hole]);
          const targetHole = nextHoles[holeIndex];
          if (!targetHole) return polyline;
          targetHole[vertexIndex] = nextPoint.x;
          targetHole[vertexIndex + 1] = nextPoint.y;
          return { ...polyline, holes: nextHoles };
        }

        if ((polyline.lifeLineVertices?.length ?? 0) > 0) {
          const designPoints =
            polyline.lifeLineDesignPoints?.map((point) => ({ ...point })) ??
            getLifeLineGripPoints(polyline, getShapeGripPoints(polyline))
              .filter((grip) => grip.gripKind === "vertex")
              .map((grip) => grip.point);
          const designPointIndex = Math.floor(vertexIndex / 2);
          if (designPointIndex >= 0 && designPointIndex < designPoints.length) {
            designPoints[designPointIndex] = nextPoint;
          }
          linkedVertexIndices?.forEach((linkedVertexIndex) => {
            const linkedDesignPointIndex = Math.floor(linkedVertexIndex / 2);
            if (linkedDesignPointIndex >= 0 && linkedDesignPointIndex < designPoints.length) {
              designPoints[linkedDesignPointIndex] = nextPoint;
            }
          });

          if (designPoints.length >= 2) {
            const renderedLifeLinePatch = buildRenderedLifeLineShapePatch(polyline, designPoints, {
              disableAutoSplit: true,
            });
            if (renderedLifeLinePatch) {
              return {
                ...polyline,
                ...renderedLifeLinePatch,
              };
            }
          }
        }

        const nextPoints = polyline.points.slice();
        nextPoints[vertexIndex] = nextPoint.x;
        nextPoints[vertexIndex + 1] = nextPoint.y;
        linkedVertexIndices?.forEach((linkedVertexIndex) => {
          nextPoints[linkedVertexIndex] = nextPoint.x;
          nextPoints[linkedVertexIndex + 1] = nextPoint.y;
        });

        return { ...polyline, points: nextPoints };
      });
    });
  };

  const getRenderableVertexCount = (points: number[], closed: boolean) => {
    const vertices = getPolylineVertices(points);
    if (!closed || vertices.length < 2) {
      return vertices.length;
    }

    const first = vertices[0];
    const last = vertices[vertices.length - 1];
    if (!first || !last) return vertices.length;
    if (Math.abs(first.x - last.x) <= 0.0001 && Math.abs(first.y - last.y) <= 0.0001) {
      return Math.max(0, vertices.length - 1);
    }

    return vertices.length;
  };

  const getRenderableVertexCountForShape = (shape: PolylineLikeShape) =>
    getRenderableVertexCount(shape.points, shape.closed);

  const getCoincidentClosingVertexIndex = (points: number[]) => {
    const vertexCount = Math.floor(points.length / 2);
    if (vertexCount < 2) return null;

    const firstX = points[0];
    const firstY = points[1];
    const lastX = points[(vertexCount - 1) * 2];
    const lastY = points[(vertexCount - 1) * 2 + 1];
    if (firstX == null || firstY == null || lastX == null || lastY == null) return null;

    const first = { x: firstX, y: firstY };
    const last = { x: lastX, y: lastY };
    if (Math.abs(first.x - last.x) <= 0.0001 && Math.abs(first.y - last.y) <= 0.0001) {
      return (vertexCount - 1) * 2;
    }

    return null;
  };

  const getSegmentStretchVertexIndices = (points: number[], closed: boolean, segmentIndex: number) => {
    const renderableVertexCount = getRenderableVertexCount(points, closed);
    if (renderableVertexCount < 2) return [];
    if (!closed && segmentIndex >= renderableVertexCount - 1) return [];

    const startVertexIndex = segmentIndex * 2;
    const endVertexIndex = ((segmentIndex + 1) % renderableVertexCount) * 2;
    const vertexIndices = [startVertexIndex, endVertexIndex];
    const closingVertexIndex = closed ? getCoincidentClosingVertexIndex(points) : null;

    if (closingVertexIndex != null && vertexIndices.includes(0)) {
      vertexIndices.push(closingVertexIndex);
    }

    return Array.from(new Set(vertexIndices));
  };

  const applySegmentStretch = (stretchState: SegmentStretchState, pointer: CanvasPoint) => {
    const deltaX = pointer.x - stretchState.startPointer.x;
    const deltaY = pointer.y - stretchState.startPointer.y;

    setPolylines((current) =>
      current.map((polyline) => {
        if (polyline.id !== stretchState.polylineId || polyline.kind === "circle" || polyline.kind === "arc") {
          return polyline;
        }

        const moveVertices = (points: number[]) => {
          const nextPoints = points.slice();
          stretchState.vertexIndices.forEach((vertexIndex) => {
            const originalX = stretchState.originalPoints[vertexIndex];
            const originalY = stretchState.originalPoints[vertexIndex + 1];
            if (vertexIndex < 0 || vertexIndex + 1 >= nextPoints.length || originalX == null || originalY == null) {
              return;
            }
            nextPoints[vertexIndex] = originalX + deltaX;
            nextPoints[vertexIndex + 1] = originalY + deltaY;
          });
          return nextPoints;
        };

        if (stretchState.holeIndex != null) {
          const nextHoles = (polyline.holes ?? []).map((hole, index) =>
            index === stretchState.holeIndex ? moveVertices(hole) : [...hole],
          );
          return { ...polyline, holes: nextHoles };
        }

        return { ...polyline, points: moveVertices(polyline.points) };
      }),
    );
  };

  const updateSegmentStretchNumericEditState = (stretchState: SegmentStretchState, position: CanvasPoint) => {
    const direction = getNormalizedDirection(stretchState.startPointer, position);
    setNumericEdit({
      kind: "segment-stretch",
      entityId: stretchState.polylineId,
      shapeKind: stretchState.shapeKind,
      vertexIndices: stretchState.vertexIndices,
      holeIndex: stretchState.holeIndex,
      originalPoints: stretchState.originalPoints,
      anchor: stretchState.startPointer,
      direction,
      directionValue: getDirectionValue(direction),
      position,
      value: formatMeasurementValue(getDistance(stretchState.startPointer, position)),
    });
  };

  const insertPolylineVertex = (
    polylineId: string,
    segmentStartVertexIndex: number,
    nextPoint: CanvasPoint,
    holeIndex?: number,
  ) => {
    setPolylines((current) =>
      current.map((polyline) => {
        if (polyline.id !== polylineId || polyline.kind === "circle" || polyline.kind === "arc") return polyline;
        if (holeIndex == null && polyline.kind === "rectangle") return polyline;

        if (holeIndex != null) {
          const nextHoles = (polyline.holes ?? []).map((hole) => [...hole]);
          const targetHole = nextHoles[holeIndex];
          if (!targetHole) return polyline;

          const insertAt = Math.min(segmentStartVertexIndex + 2, targetHole.length);
          targetHole.splice(insertAt, 0, nextPoint.x, nextPoint.y);
          return { ...polyline, holes: nextHoles };
        }

        const nextPoints = polyline.points.slice();
        const insertAt = Math.min(segmentStartVertexIndex + 2, nextPoints.length);
        nextPoints.splice(insertAt, 0, nextPoint.x, nextPoint.y);
        return { ...polyline, points: nextPoints };
      }),
    );
  };

  const getNewVertexNearPoint = (vertices: CanvasPoint[], vertexPosition: number, closed: boolean) => {
    const source = vertices[vertexPosition];
    if (!source) return null;

    const next = vertices[closed ? (vertexPosition + 1) % vertices.length : vertexPosition + 1];
    const previous = vertices[closed ? (vertexPosition - 1 + vertices.length) % vertices.length : vertexPosition - 1];
    const visibleOffset = getScreenInvariantSize(24);

    if (next && !areCanvasPointsEqual(source, next)) {
      const distance = getDistance(source, next);
      const ratio = Math.min(0.35, visibleOffset / distance);
      return {
        point: {
          x: source.x + (next.x - source.x) * ratio,
          y: source.y + (next.y - source.y) * ratio,
        },
        insertIndex: vertexPosition + 1,
      };
    }

    if (previous && !areCanvasPointsEqual(source, previous)) {
      const distance = getDistance(source, previous);
      const ratio = Math.min(0.35, visibleOffset / distance);
      const direction = {
        x: (source.x - previous.x) / distance,
        y: (source.y - previous.y) / distance,
      };
      const insertIndex = closed ? vertexPosition + 1 : Math.min(vertexPosition + 1, vertices.length);

      return {
        point: {
          x: source.x + direction.x * distance * ratio,
          y: source.y + direction.y * distance * ratio,
        },
        insertIndex,
      };
    }

    return {
      point: {
        x: source.x + visibleOffset,
        y: source.y,
      },
      insertIndex: vertexPosition + 1,
    };
  };

  const insertPolylineVertexNearVertex = (polylineId: string, vertexIndex: number, holeIndex?: number) => {
    setPolylines((current) =>
      current.map((polyline) => {
        if (polyline.id !== polylineId || polyline.kind === "circle" || polyline.kind === "arc") return polyline;
        if (holeIndex == null && polyline.kind === "rectangle") return polyline;

        const vertexPosition = Math.floor(vertexIndex / 2);

        if (holeIndex != null) {
          const nextHoles = (polyline.holes ?? []).map((hole) => [...hole]);
          const targetHole = nextHoles[holeIndex];
          if (!targetHole) return polyline;

          const vertices = getPolylineVertices(targetHole);
          const placement = getNewVertexNearPoint(vertices, vertexPosition, true);
          if (!placement) return polyline;

          const insertAt = Math.min(Math.max(placement.insertIndex * 2, 0), targetHole.length);
          targetHole.splice(insertAt, 0, placement.point.x, placement.point.y);
          return { ...polyline, holes: nextHoles };
        }

        if ((polyline.lifeLineVertices?.length ?? 0) > 0) {
          const designPoints =
            polyline.lifeLineDesignPoints?.map((point) => ({ ...point })) ??
            getLifeLineGripPoints(polyline, getShapeGripPoints(polyline))
              .filter((grip) => grip.gripKind === "vertex")
              .map((grip) => grip.point);
          const placement = getNewVertexNearPoint(designPoints, vertexPosition, polyline.closed);
          if (!placement) return polyline;

          const nextDesignPoints = designPoints.slice();
          nextDesignPoints.splice(
            Math.min(Math.max(placement.insertIndex, 0), nextDesignPoints.length),
            0,
            placement.point,
          );
          const renderedLifeLinePatch = buildRenderedLifeLineShapePatch(polyline, nextDesignPoints, {
            disableAutoSplit: true,
          });
          return renderedLifeLinePatch
            ? {
                ...polyline,
                ...renderedLifeLinePatch,
              }
            : polyline;
        }

        const vertices = getPolylineVertices(polyline.points);
        const placement = getNewVertexNearPoint(vertices, vertexPosition, polyline.closed);
        if (!placement) return polyline;

        const nextPoints = polyline.points.slice();
        const insertAt = Math.min(Math.max(placement.insertIndex * 2, 0), nextPoints.length);
        nextPoints.splice(insertAt, 0, placement.point.x, placement.point.y);
        return { ...polyline, points: nextPoints };
      }),
    );
  };

  const removePolylineVertex = (
    polylineId: string,
    vertexIndex: number,
    holeIndex?: number,
    linkedVertexIndices?: number[],
  ) => {
    setPolylines((current) =>
      current.map((polyline) => {
        if (polyline.id !== polylineId || polyline.kind === "circle" || polyline.kind === "arc") return polyline;
        if (holeIndex == null && polyline.kind === "rectangle") return polyline;
        if (linkedVertexIndices && linkedVertexIndices.length > 0) return polyline;

        if (holeIndex != null) {
          const nextHoles = (polyline.holes ?? []).map((hole) => [...hole]);
          const targetHole = nextHoles[holeIndex];
          if (!targetHole) return polyline;

          if (getPolylineVertices(targetHole).length <= 3) {
            return polyline;
          }

          targetHole.splice(vertexIndex, 2);
          return { ...polyline, holes: nextHoles };
        }

        if ((polyline.lifeLineVertices?.length ?? 0) > 0) {
          const designPoints =
            polyline.lifeLineDesignPoints?.map((point) => ({ ...point })) ??
            getLifeLineGripPoints(polyline, getShapeGripPoints(polyline))
              .filter((grip) => grip.gripKind === "vertex")
              .map((grip) => grip.point);
          const designPointIndex = Math.floor(vertexIndex / 2);
          const minimumLifeLineDesignPointCount = polyline.closed ? 3 : 2;
          if (
            designPointIndex < 0 ||
            designPointIndex >= designPoints.length ||
            designPoints.length <= minimumLifeLineDesignPointCount
          ) {
            return polyline;
          }

          const nextDesignPoints = designPoints.filter((_, index) => index !== designPointIndex);
          const renderedLifeLinePatch = buildRenderedLifeLineShapePatch(polyline, nextDesignPoints, {
            disableAutoSplit: true,
          });
          return renderedLifeLinePatch
            ? {
                ...polyline,
                ...renderedLifeLinePatch,
              }
            : polyline;
        }

        const minimumVertexCount = polyline.closed ? 3 : 2;
        if (getRenderableVertexCount(polyline.points, polyline.closed) <= minimumVertexCount) {
          return polyline;
        }

        const nextPoints = polyline.points.slice();
        nextPoints.splice(vertexIndex, 2);
        return { ...polyline, points: nextPoints };
      }),
    );
  };

  const openOrSplitPolylineAtVertex = (polylineId: string, vertexIndex: number) => {
    setPolylines((current) => {
      const target = current.find(
        (shape): shape is PolylineLikeShape =>
          shape.id === polylineId && shape.kind === "polyline" && !shape.holes?.length && !shape.circleHoles?.length,
      );
      if (!target) return current;

      if ((target.lifeLineVertices?.length ?? 0) > 0) {
        const designPoints = (
          target.lifeLineDesignPoints?.map((point) => ({ ...point })) ??
          getLifeLineGripPoints(target, getShapeGripPoints(target))
            .filter((grip) => grip.gripKind === "vertex")
            .map((grip) => grip.point)
        ).filter((point): point is CanvasPoint => point != null);
        const vertexPosition = Math.floor(vertexIndex / 2);
        if (vertexPosition < 0 || vertexPosition >= designPoints.length) return current;

        if (target.closed && vertexPosition === 0) {
          if (designPoints.length < 3) return current;

          const firstDesignPoint = designPoints[0];
          if (!firstDesignPoint) return current;
          const openedDesignPoints = [...designPoints, firstDesignPoint];
          const renderedLifeLinePatch = buildRenderedLifeLineShapePatch(
            { ...target, closed: false },
            openedDesignPoints,
          );
          return current.map((shape) =>
            shape.id === polylineId && shape.kind === "polyline" && renderedLifeLinePatch
              ? {
                  ...shape,
                  ...renderedLifeLinePatch,
                }
              : shape,
          );
        }

        if (target.closed && designPoints.length < 3) {
          return current;
        }

        if (!target.closed && (vertexPosition <= 0 || vertexPosition >= designPoints.length - 1)) {
          return current;
        }

        const firstDesignPoints = designPoints.slice(0, vertexPosition + 1);
        const secondDesignPoints = target.closed
          ? [...designPoints.slice(vertexPosition), designPoints[0]].filter(
              (point): point is CanvasPoint => point != null,
            )
          : designPoints.slice(vertexPosition);
        const firstLifeLineShapes = buildLifeLineShapesFromDesignPoints(
          target,
          firstDesignPoints,
          false,
          target.name,
          current,
          { disableAutoSplit: true },
        );
        const secondLifeLineShapes = buildLifeLineShapesFromDesignPoints(
          target,
          secondDesignPoints,
          false,
          getNextNamedEntityName("Life Line", current),
          current,
          { disableAutoSplit: true },
        );
        const replacementShapes = [
          ...firstLifeLineShapes.map((shape, index) =>
            index === 0
              ? {
                  ...shape,
                  id: target.id,
                  name: target.name,
                }
              : shape,
          ),
          ...secondLifeLineShapes,
        ];

        return replacementShapes.length > 0
          ? [...current.filter((shape) => shape.id !== polylineId), ...replacementShapes]
          : current;
      }

      const vertices = getPolylineVertices(target.points);
      const vertexPosition = Math.floor(vertexIndex / 2);
      if (vertexPosition < 0 || vertexPosition >= vertices.length) return current;

      if (target.closed) {
        if (vertices.length < 3) return current;

        const openedVertices = [...vertices.slice(vertexPosition), ...vertices.slice(0, vertexPosition + 1)];

        return current.map((shape) =>
          shape.id === polylineId && shape.kind === "polyline"
            ? {
                ...shape,
                points: flattenCanvasPoints(openedVertices),
                closed: false,
              }
            : shape,
        );
      }

      if (vertexPosition <= 0 || vertexPosition >= vertices.length - 1) {
        return current;
      }

      const firstVertices = vertices.slice(0, vertexPosition + 1);
      const secondVertices = vertices.slice(vertexPosition);
      const baseShapes = current.filter((shape) => shape.id !== polylineId);
      const secondShape: PolylineLikeShape = {
        ...target,
        id: `polyline-${crypto.randomUUID()}`,
        name: getNextEntityName("polyline", current, dimensions),
        points: flattenCanvasPoints(secondVertices),
        closed: false,
      };

      return [
        ...baseShapes,
        {
          ...target,
          points: flattenCanvasPoints(firstVertices),
          closed: false,
        },
        secondShape,
      ];
    });
  };

  const updateCircleHole = (
    polylineId: string,
    holeIndex: number,
    nextPoint: CanvasPoint,
    mode: "center" | "radius",
  ) => {
    setPolylines((current) =>
      current.map((polyline) => {
        if (polyline.id !== polylineId || polyline.kind === "circle" || polyline.kind === "arc") return polyline;

        const nextCircleHoles = (polyline.circleHoles ?? []).map(cloneCircleHole);
        const targetHole = nextCircleHoles[holeIndex];
        if (!targetHole) return polyline;

        if (mode === "center") {
          targetHole.center = clonePoint(nextPoint);
        } else {
          targetHole.radius = Math.max(1, getDistance(targetHole.center, nextPoint));
        }

        return {
          ...polyline,
          circleHoles: nextCircleHoles,
        };
      }),
    );
  };

  const updateCircleRadius = (circleId: string, nextPoint: CanvasPoint) => {
    setPolylines((current) =>
      current.map((shape) => {
        if (shape.id !== circleId || shape.kind !== "circle") {
          return shape;
        }

        return {
          ...shape,
          radius: Math.max(1, getDistance(shape.center, nextPoint)),
        };
      }),
    );
  };

  const updateArcGrip = (arcId: string, vertexIndex: number, nextPoint: CanvasPoint) => {
    setPolylines((current) =>
      current.map((shape) => {
        if (shape.id !== arcId || shape.kind !== "arc") {
          return shape;
        }

        const angle = Math.atan2(nextPoint.y - shape.center.y, nextPoint.x - shape.center.x);

        if (vertexIndex === 1) {
          return {
            ...shape,
            radius: Math.max(1, getDistance(shape.center, nextPoint)),
          };
        }

        return {
          ...shape,
          startAngle: vertexIndex === 0 ? angle : shape.startAngle,
          endAngle: vertexIndex === 2 ? angle : shape.endAngle,
        };
      }),
    );
  };

  const updateBackgroundReferencePoint = (handle: "start" | "end", worldPoint: CanvasPoint) => {
    setBackgroundAsset((current) => {
      if (!current || !current.reference) return current;

      const localPoint = getBackgroundLocalPoint(current, worldPoint);

      return {
        ...current,
        reference: {
          ...current.reference,
          startLocal: handle === "start" ? localPoint : current.reference.startLocal,
          endLocal: handle === "end" ? localPoint : current.reference.endLocal,
        },
      };
    });
  };

  const updateDimensionOffset = (dimensionId: string, nextPoint: CanvasPoint) => {
    setDimensions((current) =>
      current.map((dimension) => {
        if (dimension.id !== dimensionId) return dimension;

        return {
          ...dimension,
          offset: getDimensionOffsetFromPoint(dimension.start, dimension.end, nextPoint),
        };
      }),
    );
  };

  const updateRectangleVertex = (rectangleId: string, vertexIndex: number, nextPoint: CanvasPoint) => {
    setPolylines((current) =>
      current.map((polyline) => {
        if (polyline.id !== rectangleId || polyline.kind !== "rectangle") {
          return polyline;
        }

        const points = polyline.points;
        if (points.length !== 8) {
          return polyline;
        }

        const corners = [0, 2, 4, 6]
          .map((index) => getPointFromCoordinateArray(points, index))
          .filter((corner): corner is CanvasPoint => corner != null);
        if (corners.length !== 4) {
          return polyline;
        }

        const draggedCornerIndex = vertexIndex / 2;
        const oppositeCornerIndex = (draggedCornerIndex + 2) % 4;
        const oppositeCorner = corners[oppositeCornerIndex];
        const firstCorner = corners[0];
        const secondCorner = corners[1];
        const fourthCorner = corners[3];
        if (!oppositeCorner || !firstCorner || !secondCorner || !fourthCorner) {
          return polyline;
        }

        const widthAxis = {
          x: secondCorner.x - firstCorner.x,
          y: secondCorner.y - firstCorner.y,
        };
        const widthLength = Math.hypot(widthAxis.x, widthAxis.y);
        if (widthLength === 0) {
          return polyline;
        }

        const heightAxis = {
          x: fourthCorner.x - firstCorner.x,
          y: fourthCorner.y - firstCorner.y,
        };
        const heightLength = Math.hypot(heightAxis.x, heightAxis.y);
        if (heightLength === 0) {
          return polyline;
        }

        const u = {
          x: widthAxis.x / widthLength,
          y: widthAxis.y / widthLength,
        };
        const v = {
          x: heightAxis.x / heightLength,
          y: heightAxis.y / heightLength,
        };

        const delta = {
          x: nextPoint.x - oppositeCorner.x,
          y: nextPoint.y - oppositeCorner.y,
        };

        const projectedWidth = Math.max(MIN_RECTANGLE_SIDE, Math.abs(delta.x * u.x + delta.y * u.y));
        const projectedHeight = Math.max(MIN_RECTANGLE_SIDE, Math.abs(delta.x * v.x + delta.y * v.y));

        const widthVector = {
          x: projectedWidth * u.x,
          y: projectedWidth * u.y,
        };
        const heightVector = {
          x: projectedHeight * v.x,
          y: projectedHeight * v.y,
        };

        let orientedCorners: CanvasPoint[];

        switch (draggedCornerIndex) {
          case 0:
            orientedCorners = [
              {
                x: oppositeCorner.x - widthVector.x - heightVector.x,
                y: oppositeCorner.y - widthVector.y - heightVector.y,
              },
              {
                x: oppositeCorner.x - heightVector.x,
                y: oppositeCorner.y - heightVector.y,
              },
              oppositeCorner,
              {
                x: oppositeCorner.x - widthVector.x,
                y: oppositeCorner.y - widthVector.y,
              },
            ];
            break;
          case 1:
            orientedCorners = [
              {
                x: oppositeCorner.x - heightVector.x,
                y: oppositeCorner.y - heightVector.y,
              },
              {
                x: oppositeCorner.x + widthVector.x - heightVector.x,
                y: oppositeCorner.y + widthVector.y - heightVector.y,
              },
              {
                x: oppositeCorner.x + widthVector.x,
                y: oppositeCorner.y + widthVector.y,
              },
              oppositeCorner,
            ];
            break;
          case 2:
            orientedCorners = [
              oppositeCorner,
              {
                x: oppositeCorner.x + widthVector.x,
                y: oppositeCorner.y + widthVector.y,
              },
              {
                x: oppositeCorner.x + widthVector.x + heightVector.x,
                y: oppositeCorner.y + widthVector.y + heightVector.y,
              },
              {
                x: oppositeCorner.x + heightVector.x,
                y: oppositeCorner.y + heightVector.y,
              },
            ];
            break;
          case 3:
          default:
            orientedCorners = [
              {
                x: oppositeCorner.x - widthVector.x,
                y: oppositeCorner.y - widthVector.y,
              },
              oppositeCorner,
              {
                x: oppositeCorner.x + heightVector.x,
                y: oppositeCorner.y + heightVector.y,
              },
              {
                x: oppositeCorner.x - widthVector.x + heightVector.x,
                y: oppositeCorner.y - widthVector.y + heightVector.y,
              },
            ];
            break;
        }

        const nextPoints = orientedCorners.flatMap((corner) => [corner.x, corner.y]);
        return { ...polyline, points: nextPoints };
      }),
    );
  };

  const handleToolChange = (mode: ToolMode) => {
    if (toolGroupCloseTimeoutRef.current) {
      clearTimeout(toolGroupCloseTimeoutRef.current);
      toolGroupCloseTimeoutRef.current = null;
    }
    setOpenToolGroupMenu(null);
    setIsIconPickerOpen(mode === "icon");
    if (mode !== "icon") {
      setPendingIconId(null);
      setIconPreviewPoint(null);
    }
    if (mode !== "select") {
      lastCommandRef.current = mode;
    }

    const shouldPreserveSelectedRoofForLifeLine =
      (mode === "singleLifeLine" || mode === "automaticLifeLine") && selectedRoofContextId != null;

    if (mode === "singleLifeLine" && !selectedRoofContextId) {
      if (lifeLineRoofSelectionRequired) {
        setLifeLineRoofSelectionWarning(
          "Click the specific roof for this lifeline. The system list will be filtered by the selected roof.",
        );
      } else {
        setLifeLineRoofSelectionWarning(null);
      }
      setIsEntityPropertiesOpen(true);
      setActiveConfigurationPage("lifelineSystems");
      setActiveLifeLineTab("info");
    } else if (mode === "automaticLifeLine") {
      setLifeLineRoofSelectionWarning("Select or click a roof or roof part to create an automatic lifeline.");
      setIsEntityPropertiesOpen(true);
      setActiveConfigurationPage("lifelineSystems");
      setActiveLifeLineTab("info");
    } else {
      setLifeLineRoofSelectionWarning(null);
    }

    if (
      (mode === "singlePointCircle" || mode === "pointSeries") &&
      !polylines.some((shape) => shape.entityStyleKind === "single-anchor-point")
    ) {
      setIsEntityPropertiesOpen(true);
      setActiveConfigurationPage("anchorPoints");
      setActiveAnchorPointTab("anchorPoint");
    }

    if (
      (moveState && (toolMode === "move" || toolMode === "copy")) ||
      (transformState && (toolMode === "scale" || toolMode === "rotate"))
    ) {
      cancelActiveTransform();
    }

    clearOsnapTracking();
    setToolMode(mode);
    setDraggedGrip(null);
    segmentStretchRef.current = null;
    setDraggedSegmentStretch(null);
    setSelectionCycle(null);
    setMoveState(null);
    setTransformState(null);
    setActiveOffset(null);
    setActiveHoleHost(null);
    setInvalidGripFeedback(null);
    setGripContextMenu(null);
    setActiveRectangle(null);
    setActiveCircle(null);
    setActiveArc(null);
    setActivePointSeries(null);
    setActiveRuler(null);
    setActiveDimension(null);
    setActiveObjectDimension(null);
    setNumericEdit(null);
    setAutomaticLifeLineSourceIds(null);
    setAutomaticLifeLinePreviewPoint(null);
    setDirectionLockAxis(null);
    setIsBackgroundReferenceMode(false);
    setIsBackgroundCropMode(false);
    setBackgroundCropRect(null);
    setBackgroundReferenceLine(null);
    setBackgroundReferenceInput(null);
    if (mode !== "multiGableRoof") {
      setMultiGableRoofDraft(null);
    }
    setRoofHipValleyDraft(mode === "roofHipValley" ? { stage: "eaveA" } : null);

    if (
      mode === "select" ||
      mode === "move" ||
      mode === "copy" ||
      mode === "scale" ||
      mode === "rotate" ||
      mode === "offset" ||
      mode === "dimensionObject" ||
      mode === "automaticLifeLine" ||
      (mode === "hatch" && hatchToolMode === "pick") ||
      mode === "pointSeries" ||
      mode === "holePolyline" ||
      mode === "holeCircle"
    ) {
      setEditingId(null);
      setActivePolyline(null);
      setHoverSnap(null);
      if (mode === "holePolyline" || mode === "holeCircle") {
        const selectedHost =
          selectedIds.length === 1 && selectedIds[0]
            ? getShapeById(selectedIds[0])
            : (selectedIds.map((id) => getShapeById(id)).find(isHoleHostShape) ?? null);
        if (isHoleHostShape(selectedHost)) {
          setActiveHoleHost({ hostId: selectedHost.id });
        }
      }
      return;
    }

    if (!shouldPreserveSelectedRoofForLifeLine) {
      setSelectedIds([]);
    }
    setEditingId(null);
    if (mode === "singlePointCircle") {
      startSinglePointCircleTool();
    }
  };

  useEffect(() => {
    if (toolMode !== "hatch") return;
    setActivePolyline(null);
    setHoverSnap(null);
    setNumericEdit(null);
    setDirectionLockAxis(null);
  }, [hatchToolMode, toolMode]);

  const clearPendingToolGroupClose = () => {
    if (toolGroupCloseTimeoutRef.current) {
      clearTimeout(toolGroupCloseTimeoutRef.current);
      toolGroupCloseTimeoutRef.current = null;
    }
  };

  const scheduleToolGroupClose = () => {
    if (!openToolGroupMenu) return;
    clearPendingToolGroupClose();
    toolGroupCloseTimeoutRef.current = setTimeout(() => {
      setOpenToolGroupMenu(null);
      toolGroupCloseTimeoutRef.current = null;
    }, 300);
  };

  const handlePrimaryRoofCreationToolClick = () => {
    handleToolChange(primaryRoofCreationTool);
  };

  const handleRoofCreationToolSelection = (
    mode:
      | "roof"
      | "multiGableRoof"
      | "gableRoof"
      | "curvedRoof"
      | "singlePitchedRoof"
      | "multiShedRoof"
      | "polyline"
      | "rectangle",
  ) => {
    setPrimaryRoofCreationTool(mode);
    handleToolChange(mode);
  };

  const getRoofCreationToolLabel = (
    mode:
      | "roof"
      | "multiGableRoof"
      | "gableRoof"
      | "curvedRoof"
      | "singlePitchedRoof"
      | "multiShedRoof"
      | "polyline"
      | "rectangle",
  ) =>
    mode === "roof"
      ? "Automatic Pitched Roof"
      : mode === "multiGableRoof"
        ? "Multi Gable Roof"
        : mode === "gableRoof"
          ? "Single Gable Roof"
          : mode === "curvedRoof"
            ? "Curved Roof"
            : mode === "singlePitchedRoof"
              ? "Single Pitched Roof"
              : mode === "multiShedRoof"
                ? "Multi Shed Roof"
                : mode === "polyline"
                  ? "Custom Flat Room"
                  : "Flat Roof";

  const isPrimaryRoofCreationToolActive =
    primaryRoofCreationTool === "roof"
      ? toolMode === "roof"
      : primaryRoofCreationTool === "multiGableRoof"
        ? toolMode === "multiGableRoof"
        : primaryRoofCreationTool === "gableRoof"
          ? toolMode === "gableRoof"
          : primaryRoofCreationTool === "curvedRoof"
            ? toolMode === "curvedRoof"
            : primaryRoofCreationTool === "singlePitchedRoof"
              ? toolMode === "singlePitchedRoof"
              : primaryRoofCreationTool === "multiShedRoof"
                ? toolMode === "multiShedRoof"
                : primaryRoofCreationTool === "polyline"
                  ? toolMode === "polyline"
                  : toolMode === "rectangle";

  const handlePrimaryRoofComponentToolClick = () => {
    handleToolChange(primaryRoofComponentTool);
  };

  const handleRoofComponentToolSelection = (mode: RoofComponentToolMode) => {
    setPrimaryRoofComponentTool(mode);
    handleToolChange(mode);
  };

  const handlePrimarySurfaceFeatureToolClick = () => {
    handleToolChange(primarySurfaceFeatureTool);
  };

  const handleSurfaceFeatureToolSelection = (mode: SurfaceFeatureToolMode) => {
    setPrimarySurfaceFeatureTool(mode);
    handleToolChange(mode);
  };

  const handlePrimaryDimensionToolClick = () => {
    handleToolChange(primaryDimensionTool);
  };

  const handleDimensionToolSelection = (mode: "dimension" | "dimensionObject") => {
    setPrimaryDimensionTool(mode);
    handleToolChange(mode);
  };

  const handlePrimaryLifeLineToolClick = () => {
    handleLifeLineToolSelection(primaryLifeLineTool);
  };

  const handleLifeLineToolSelection = (mode: "singleLifeLine" | "automaticLifeLine") => {
    setPrimaryLifeLineTool(mode);

    const selectedSourceShapes =
      mode === "automaticLifeLine" ? getAutomaticLifeLineSourceShapesFromIds(selectedIds) : [];
    if (mode === "automaticLifeLine") {
      setLifeLineRoofSelectionWarning("Select a roof or roof part to create an automatic lifeline.");
    }

    handleToolChange(mode);

    if (mode === "automaticLifeLine" && selectedSourceShapes.length > 0) {
      const sourceIds = getAutomaticLifeLineSourceIdsFromShapes(selectedSourceShapes);
      setAutomaticLifeLineSourceIds(sourceIds);
      setSelectedIds(sourceIds);
      setLifeLineRoofSelectionWarning(null);
      showAutomaticLifeLineOffsetInput();
    }
  };

  const handlePrimaryAnchorToolClick = () => {
    handleToolChange(primaryAnchorTool);
  };

  const handleAnchorToolSelection = (mode: "singlePointCircle" | "pointSeries") => {
    setPrimaryAnchorTool(mode);
    handleToolChange(mode);
  };

  const getAnchorToolLabel = (mode: "singlePointCircle" | "pointSeries" | "singleLifeLine" | "automaticLifeLine") =>
    mode === "singlePointCircle"
      ? "Insert Anchor Point"
      : mode === "pointSeries"
        ? "Single Anchor Points in Series"
        : mode === "automaticLifeLine"
          ? "Automatic Life Line"
          : "Single Life Line";

  const renderAnchorToolIcon = (mode: "singlePointCircle" | "pointSeries" | "singleLifeLine" | "automaticLifeLine") =>
    mode === "singlePointCircle" ? (
      <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-none stroke-current">
        <circle cx="12" cy="12" r="6.5" strokeWidth="1.8" strokeDasharray="2 2" />
        <path d="M12 8v8M8 12h8" strokeWidth="1.8" strokeLinecap="round" />
        <circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none" />
      </svg>
    ) : mode === "pointSeries" ? (
      <svg viewBox="0 0 64 64" aria-hidden="true" className="h-5 w-5 fill-none stroke-current">
        <path d="M10 38h44" strokeWidth="3" strokeLinecap="round" strokeDasharray="6 6" />
        <circle cx="14" cy="38" r="4" fill="currentColor" stroke="none" />
        <circle cx="32" cy="38" r="4" fill="currentColor" stroke="none" />
        <circle cx="50" cy="38" r="4" fill="currentColor" stroke="none" />
      </svg>
    ) : mode === "automaticLifeLine" ? (
      <svg viewBox="0 0 64 64" aria-hidden="true" className="h-5 w-5 fill-none stroke-current">
        <path d="M8 44 18 24h28l10 20Z" strokeWidth="3" strokeLinejoin="round" />
        <path d="M14 40c10-16 24-16 36-2" strokeWidth="4" strokeLinecap="round" />
        <path d="M20 34h.01M32 30h.01M44 34h.01" strokeWidth="5" strokeLinecap="round" />
      </svg>
    ) : (
      <svg viewBox="0 0 64 64" aria-hidden="true" className="h-5 w-5 fill-none stroke-current">
        <path d="M10 40 24 28l16 10 14-14" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="10" cy="40" r="4" fill="currentColor" stroke="none" />
        <circle cx="54" cy="24" r="4" fill="currentColor" stroke="none" />
      </svg>
    );

  const isPrimaryLifeLineToolActive =
    primaryLifeLineTool === "singleLifeLine" ? toolMode === "singleLifeLine" : toolMode === "automaticLifeLine";
  const isPrimaryAnchorToolActive =
    primaryAnchorTool === "singlePointCircle" ? toolMode === "singlePointCircle" : toolMode === "pointSeries";

  const handlePrimaryCurveToolClick = () => {
    handleToolChange(primaryCurveTool);
  };

  const handleCurveToolSelection = (mode: CurveToolMode) => {
    setPrimaryCurveTool(mode);
    handleToolChange(mode);
  };

  const handlePrimaryTransformToolClick = () => {
    if (primaryTransformTool === "explode") {
      explodeSelectedPolylines();
      return;
    }

    handleToolChange(primaryTransformTool);
  };

  const handleTransformToolSelection = (mode: "move" | "copy" | "explode" | "scale" | "rotate" | "offset") => {
    setPrimaryTransformTool(mode);
    if (mode === "explode") {
      setOpenToolGroupMenu(null);
      explodeSelectedPolylines();
      return;
    }

    handleToolChange(mode);
  };

  const handlePrimaryCleanupToolClick = () => {
    if (primaryCleanupTool === "delete") {
      deleteSelected();
      return;
    }

    clearCanvas();
  };

  const handleCleanupToolSelection = (mode: "delete" | "clear") => {
    setPrimaryCleanupTool(mode);
    setOpenToolGroupMenu(null);
    if (mode === "delete") {
      deleteSelected();
      return;
    }

    clearCanvas();
  };

  const getToolbarToneClass = (
    tone: "white" | "gray" | "green" | "red" | "blue" | "teal" | "black",
    isActive: boolean,
  ) => {
    const textClass =
      tone === "gray"
        ? "text-[#6b7280]"
        : tone === "green"
          ? "text-[#16a34a]"
          : tone === "red"
            ? "text-[#dc2626]"
            : tone === "blue"
              ? "text-[#2563eb]"
              : tone === "teal"
                ? "text-[#0f766e]"
                : "text-[#111827]";

    if (isActive) {
      return `border-2 border-[#1d5cab] bg-white ${textClass}`;
    }

    return `border-[rgba(74,58,31,0.12)] bg-[rgba(255,255,255,0.9)] ${textClass}`;
  };

  const buildRectanglePolyline = (rectangle: ActiveRectangle) => buildRectanglePolylineRule(rectangle);

  const getActiveCircleRadius = (circle: ActiveCircle) => getActiveCircleRadiusRule(circle);

  const buildSinglePointCircleState = (
    center: CanvasPoint,
    radius = SINGLE_POINT_CIRCLE_DEFAULT_RADIUS,
  ): ActiveCircle => buildSinglePointCircleStateRule(center, radius);

  const getSelectedAnchorPointSystemMeta = () => {
    const selectedSystem = patrolAnchorPointSystems.find((system) => system.id === selectedAnchorPointSystem);
    const selectedMapping = selectedEffectiveAnchorPointMapping ?? anchorPointDefaultCodeOptions[0] ?? null;
    const selectedMaterial =
      selectedAnchorPointMaterial ??
      anchorPointMaterialOptions.find((material) => material.id === selectedMapping?.materialId) ??
      null;
    const additionalProductCodes = anchorPointAdditionalProducts
      .map((product) => product.item.code)
      .filter(Boolean)
      .join(" / ");
    const optionalProductCodes = anchorPointOptionalProducts
      .map((product) => product.item.code)
      .filter(Boolean)
      .join(" / ");
    const seamsSpan = parseStoredRoofNumber(effectiveRoofSeamsSpan);
    const insulationThickness = parseStoredRoofNumber(effectiveRoofInsulationThickness);

    return selectedSystem
      ? {
          safetySystemId: selectedSystem.id,
          safetySystemName: selectedSystem.name,
          roofSystemId: selectedSystem.id,
          roofSystemName: selectedSystem.name,
          roofNormativeId: selectedAnchorPointNormative?.id,
          roofNormativeName: selectedAnchorPointNormative?.name,
          roofSeamsSpan: seamsSpan ?? undefined,
          roofInsulationThickness: insulationThickness ?? undefined,
          roofAnchorPointMaterialId: selectedMaterial?.id,
          roofAnchorPointMaterialName: selectedMaterial?.name,
          roofAnchorPointMappingId: selectedMapping?.id,
          roofAnchorPointCode: selectedMapping?.item.code,
          roofAnchorPointName: selectedMapping?.item.name,
          anchorPointAdditionalProductCodes: additionalProductCodes || undefined,
          anchorPointOptionalProductCodes: optionalProductCodes || undefined,
        }
      : {};
  };

  const getSelectedLifeLineSystemMeta = () => {
    const selectedSystem = patrolLifeLineSystems.find((system) => system.id === selectedSafetySystem);
    return selectedSystem
      ? {
          safetySystemId: selectedSystem.id,
          safetySystemName: selectedSystem.name,
        }
      : {};
  };

  const commitPointSeriesAnchor = (anchorPoint: CanvasPoint) => {
    if (doesCircleIntersectObstacles(anchorPoint, pointSeriesMaxLength)) {
      return null;
    }

    const pointSeriesStyle = getEntityTypeStyle("single-anchor-point");
    const anchorPointSystemMeta = getSelectedAnchorPointSystemMeta();
    const nextId = `polyline-${crypto.randomUUID()}`;
    runHistoryAction(() => {
      setPolylines((current) => [
        ...current,
        {
          id: nextId,
          center: anchorPoint,
          radius: pointSeriesMaxLength,
          closed: true,
          kind: "circle",
          isObstacle: false,
          entityStyleKind: "single-anchor-point",
          layer: SAFETY_LAYER,
          name: getNextEntityName("circle", current, dimensions),
          strokeColor: pointSeriesStyle.strokeColor,
          strokeWidth: pointSeriesStyle.strokeWidth,
          ...anchorPointSystemMeta,
        },
      ]);
    });

    return nextId;
  };

  const commitPointSeriesAnchors = (points: CanvasPoint[], skipFirstPoint = false) => {
    if (points.length < 2) return 0;

    const pointSeriesStyle = getEntityTypeStyle("single-anchor-point");
    const anchorPointSystemMeta = getSelectedAnchorPointSystemMeta();
    const anchorPoints = skipFirstPoint ? points.slice(1) : points;
    const uniquePoints = anchorPoints.filter((point, index, array) => {
      const previous = array[index - 1];
      return !previous || previous.x !== point.x || previous.y !== point.y;
    });

    if (uniquePoints.length === 0) return 0;

    runHistoryAction(() => {
      setPolylines((current) => {
        const nextShapes = [...current];

        for (const anchorPoint of uniquePoints) {
          if (doesCircleIntersectObstacles(anchorPoint, pointSeriesMaxLength)) {
            continue;
          }

          nextShapes.push({
            id: `polyline-${crypto.randomUUID()}`,
            center: anchorPoint,
            radius: pointSeriesMaxLength,
            closed: true,
            kind: "circle",
            isObstacle: false,
            entityStyleKind: "single-anchor-point",
            layer: SAFETY_LAYER,
            name: getNextEntityName("circle", nextShapes, dimensions),
            strokeColor: pointSeriesStyle.strokeColor,
            strokeWidth: pointSeriesStyle.strokeWidth,
            ...anchorPointSystemMeta,
          });
        }

        return nextShapes;
      });
    });

    return uniquePoints.length;
  };

  const startSinglePointCircleTool = () => {
    const center = getCurrentPointerWorldPosition();
    const circle = buildSinglePointCircleState(center);
    setActiveCircle(circle);
    setNumericEdit({
      kind: "single-anchor-point",
      center,
      position: center,
      value: formatMeasurementValue(SINGLE_POINT_CIRCLE_DEFAULT_RADIUS),
    });
    focusNumericField("value");
  };

  const ensureRoofSkeletonBuilder = async () => {
    if (roofSkeletonBuilderRef.current) {
      return roofSkeletonBuilderRef.current;
    }

    if (!roofSkeletonInitPromiseRef.current) {
      roofSkeletonInitPromiseRef.current = (async () => {
        const skeletonModule = (await import("straight-skeleton")) as any;
        const builder = skeletonModule.SkeletonBuilder ?? skeletonModule.default?.SkeletonBuilder ?? null;
        if (!builder) {
          throw new Error("Straight skeleton non disponibile.");
        }
        await builder.init();
        roofSkeletonBuilderRef.current = builder;
      })();
    }

    await roofSkeletonInitPromiseRef.current;
    if (!roofSkeletonBuilderRef.current) {
      throw new Error("Straight skeleton non disponibile.");
    }

    return roofSkeletonBuilderRef.current;
  };

  const getNextNamedRoofPart = (prefix: string, shapes: Shape[]) => {
    const count = shapes.filter((shape) => shape.name.startsWith(prefix)).length + 1;
    return `${prefix} ${count}`;
  };

  const getRoofOutlineVertices = (outlinePoints: number[]) => {
    const vertices: CanvasPoint[] = [];
    for (let index = 0; index + 1 < outlinePoints.length; index += 2) {
      const x = outlinePoints[index];
      const y = outlinePoints[index + 1];
      if (typeof x === "number" && typeof y === "number") {
        vertices.push({ x, y });
      }
    }
    return vertices;
  };

  const getFlatPointsFromVertices = (vertices: CanvasPoint[]) => vertices.flatMap((point) => [point.x, point.y]);

  const getPolygonSignedArea = (vertices: CanvasPoint[]) => {
    let area = 0;
    vertices.forEach((point, index) => {
      const nextPoint = vertices[(index + 1) % vertices.length];
      if (!nextPoint) return;
      area += point.x * nextPoint.y - nextPoint.x * point.y;
    });
    return area / 2;
  };
  const getFlatPolygonArea = (points: number[]) => Math.abs(getPolygonSignedArea(getPolylineVertices(points)));
  const selectedRoofSurfaceArea = selectedRoofGroupShapes.reduce((total, shape) => {
    if (shape.kind !== "polyline" && shape.kind !== "rectangle") return total;
    if (!shape.closed || shape.points.length < 6) return total;

    const holesArea = (shape.holes ?? []).reduce((sum: number, hole: number[]) => sum + getFlatPolygonArea(hole), 0);
    const circleHolesArea = (shape.circleHoles ?? []).reduce(
      (sum, hole) => sum + Math.PI * hole.radius * hole.radius,
      0,
    );

    return total + Math.max(0, getFlatPolygonArea(shape.points) - holesArea - circleHolesArea);
  }, 0);

  const getOutlineVertexIndex = (point: CanvasPoint, vertices: CanvasPoint[]) => {
    const matchTolerance = 0.5;
    let closestIndex = -1;
    let closestDistance = Number.POSITIVE_INFINITY;
    vertices.forEach((vertex, index) => {
      const distance = Math.hypot(vertex.x - point.x, vertex.y - point.y);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });
    return closestDistance <= matchTolerance ? closestIndex : -1;
  };

  const isConcaveOutlineVertex = (vertices: CanvasPoint[], vertexIndex: number) => {
    if (vertices.length < 3 || vertexIndex < 0) return false;
    const previousPoint = vertices[(vertexIndex - 1 + vertices.length) % vertices.length];
    const currentPoint = vertices[vertexIndex];
    const nextPoint = vertices[(vertexIndex + 1) % vertices.length];
    if (!previousPoint || !currentPoint || !nextPoint) return false;

    const cross =
      (currentPoint.x - previousPoint.x) * (nextPoint.y - currentPoint.y) -
      (currentPoint.y - previousPoint.y) * (nextPoint.x - currentPoint.x);
    const polygonOrientation = Math.sign(getPolygonSignedArea(vertices)) || 1;
    return Math.sign(cross) !== 0 && Math.sign(cross) !== polygonOrientation;
  };

  const classifyRoofDiagonalLine = (linePoints: number[], outlinePoints: number[]): "HIP" | "Valley" => {
    if (linePoints.length < 4) return "HIP";
    const vertices = getRoofOutlineVertices(outlinePoints);
    const startX = linePoints[0];
    const startY = linePoints[1];
    const endX = linePoints[linePoints.length - 2];
    const endY = linePoints[linePoints.length - 1];
    if (
      typeof startX !== "number" ||
      typeof startY !== "number" ||
      typeof endX !== "number" ||
      typeof endY !== "number"
    ) {
      return "HIP";
    }
    const startPoint = { x: startX, y: startY };
    const endPoint = { x: endX, y: endY };
    const startVertexIndex = getOutlineVertexIndex(startPoint, vertices);
    const endVertexIndex = getOutlineVertexIndex(endPoint, vertices);
    const outlineVertexIndex = startVertexIndex >= 0 ? startVertexIndex : endVertexIndex;

    return isConcaveOutlineVertex(vertices, outlineVertexIndex) ? "Valley" : "HIP";
  };

  const createRoofSurfaceShape = (
    outlinePoints: number[],
    currentShapes: Shape[],
    style: EntityTypeStyle,
    roofGroupMeta: ReturnType<typeof createRoofGroupMeta>,
    styleKind: EntityStyleKind,
  ): Shape => ({
    id: `polyline-${crypto.randomUUID()}`,
    points: outlinePoints,
    closed: true,
    kind: "polyline",
    layer: ROOF_LAYER,
    entityStyleKind: styleKind,
    name: `Superficie ${roofGroupMeta.roofGroupName ?? getNextEntityName("polyline", currentShapes, dimensions)}`,
    strokeColor: style.strokeColor,
    strokeWidth: style.strokeWidth,
    fallSide: true,
    disableFill: isNonFlatRoofSurfaceStyleKind(styleKind),
    ...roofGroupMeta,
  });

  const getRoofComponentStyleKindFromName = (name: string): EntityStyleKind | null => {
    const kind = getRoofLineKindFromName(name);
    return kind ? getRoofComponentStyleKind(kind) : null;
  };

  const createRoofComponentLabel = (shape: Shape): TextEntity | null => {
    if (shape.kind === "circle" || shape.kind === "arc") return null;

    const styleKind = getRoofComponentStyleKindFromName(shape.name) ?? shape.entityStyleKind;
    if (
      styleKind !== "roof-eave" &&
      styleKind !== "roof-ridge" &&
      styleKind !== "roof-hip" &&
      styleKind !== "roof-valley" &&
      styleKind !== "roof-rake" &&
      styleKind !== "roof-hip-valley"
    ) {
      return null;
    }

    const style = getEntityTypeStyle(styleKind);
    const labelText = style.labelText.trim();
    if (!labelText) return null;

    const labelHeight = Math.max(1, style.labelHeight);
    const segments = getShapeSegments(shape);
    const segment = segments.reduce<{ start: CanvasPoint; end: CanvasPoint; length: number } | null>(
      (best, candidate) => {
        const length = getDistance(candidate.start, candidate.end);
        if (length <= 0.0001) return best;
        return !best || length > best.length ? { ...candidate, length } : best;
      },
      null,
    );
    if (!segment) return null;

    const midpoint = {
      x: (segment.start.x + segment.end.x) / 2,
      y: (segment.start.y + segment.end.y) / 2,
    };
    const unitX = (segment.end.x - segment.start.x) / segment.length;
    const unitY = (segment.end.y - segment.start.y) / segment.length;
    const normal = { x: -unitY, y: unitX };
    const offsetDistance =
      style.labelPosition === "middle" ? 0 : labelHeight * 1.25 * (style.labelPosition === "top" ? -1 : 1);
    const position = {
      x: midpoint.x + normal.x * offsetDistance,
      y: midpoint.y + normal.y * offsetDistance,
    };
    const boxWidth = Math.max(MIN_TEXT_BOX_WIDTH, labelText.length * labelHeight * 0.65 + TEXT_BOX_PADDING * 2);
    const boxHeight = Math.max(MIN_TEXT_BOX_HEIGHT, labelHeight + TEXT_BOX_PADDING * 2);

    return {
      id: `text-${crypto.randomUUID()}`,
      name: `${shape.name} letter`,
      kind: "text",
      position,
      text: labelText,
      height: labelHeight,
      boxWidth,
      boxHeight,
      fontFamily: DEFAULT_TEXT_TOOL_FONT,
      justification: "middle",
      layer: LABELS_LAYER,
      entityStyleKind: "text",
      strokeColor: style.strokeColor,
      strokeWidth: style.strokeWidth,
      roofGroupId: shape.roofGroupId,
      roofGroupName: shape.roofGroupName,
      roofGroupCategory: shape.roofGroupCategory,
    };
  };

  const createRoofComponentLabels = (shapes: Shape[]) =>
    AUTO_CREATE_ROOF_COMPONENT_LABELS
      ? shapes.flatMap((shape) => {
          const label = createRoofComponentLabel(shape);
          return label ? [label] : [];
        })
      : [];

  const appendRoofGeometryShapes = (roofGeometry: {
    outlinePoints: number[];
    ridgeLines: number[][];
    diagonalLines: number[][];
  }) => {
    const eaveStyle = getEntityTypeStyle("roof-eave");
    const ridgeStyle = getEntityTypeStyle("roof-ridge");
    runHistoryAction(() => {
      const current = polylines;
      const roofGroupMeta = createRoofGroupMeta("Pitched", current, "roof");
      let hipCount = current.filter(
        (shape) => shape.name.startsWith("HIP") || shape.name.startsWith("Diagonale"),
      ).length;
      let valleyCount = current.filter((shape) => shape.name.startsWith("Valley")).length;
      const classifiedDiagonalShapes = roofGeometry.diagonalLines.map((linePoints) => {
        const diagonalType = classifyRoofDiagonalLine(linePoints, roofGeometry.outlinePoints);
        const diagonalStyleKind = getRoofComponentStyleKind(diagonalType);
        const diagonalStyle = getEntityTypeStyle(diagonalStyleKind);
        const name = diagonalType === "HIP" ? `HIP ${++hipCount}` : `Valley ${++valleyCount}`;
        return {
          id: `polyline-${crypto.randomUUID()}`,
          points: linePoints,
          closed: false,
          kind: "polyline" as const,
          layer: ROOF_LAYER,
          entityStyleKind: diagonalStyleKind,
          name,
          strokeColor: diagonalStyle.strokeColor,
          strokeWidth: diagonalStyle.strokeWidth,
          fallSide: getRoofComponentFallSide(diagonalType),
          ...roofGroupMeta,
        };
      });
      const nextShapes: Shape[] = [
        {
          id: `polyline-${crypto.randomUUID()}`,
          points: roofGeometry.outlinePoints,
          closed: true,
          kind: "polyline",
          layer: ROOF_LAYER,
          entityStyleKind: "roof-eave",
          name: getNextNamedRoofPart("Gronda", current),
          strokeColor: eaveStyle.strokeColor,
          strokeWidth: eaveStyle.strokeWidth,
          fallSide: getRoofComponentFallSide("Gronda"),
          ...roofGroupMeta,
        },
        ...classifiedDiagonalShapes,
        ...roofGeometry.ridgeLines.map((linePoints, index) => ({
          id: `polyline-${crypto.randomUUID()}`,
          points: linePoints,
          closed: false,
          kind: "polyline" as const,
          layer: ROOF_LAYER,
          entityStyleKind: "roof-ridge" as const,
          name: `Colmo ${current.filter((shape) => shape.name.startsWith("Colmo")).length + index + 1}`,
          strokeColor: ridgeStyle.strokeColor,
          strokeWidth: ridgeStyle.strokeWidth,
          fallSide: getRoofComponentFallSide("Colmo"),
          ...roofGroupMeta,
        })),
      ];

      pendingCreatedRoofSelectionIdsRef.current = nextShapes.map((shape) => shape.id);
      setPolylines([...current, ...nextShapes]);
      setTextEntities((currentLabels) => [...currentLabels, ...createRoofComponentLabels(nextShapes)]);
    });
  };

  const appendTwoPitchRoofGeometryShapes = (
    roofGeometry: { outlinePoints: number[]; ridgeLines: number[][] },
    options: {
      dashedRidge?: boolean;
      groupCategory?: RoofGroupCategory;
      swapEavesAndRakes?: boolean;
      creationMode?: RoofCreationToolMode;
    } = {},
  ) => {
    if (roofGeometry.outlinePoints.length < 8) return;

    const [x1, y1, x2, y2, x3, y3, x4, y4] = roofGeometry.outlinePoints;
    if (
      typeof x1 !== "number" ||
      typeof y1 !== "number" ||
      typeof x2 !== "number" ||
      typeof y2 !== "number" ||
      typeof x3 !== "number" ||
      typeof y3 !== "number" ||
      typeof x4 !== "number" ||
      typeof y4 !== "number"
    ) {
      return;
    }
    const firstAndOppositeLines: number[][] = [
      [x1, y1, x2, y2],
      [x4, y4, x3, y3],
    ];
    const sideLines: number[][] = [
      [x2, y2, x3, y3],
      [x4, y4, x1, y1],
    ];
    const grondaLines = options.swapEavesAndRakes ? sideLines : firstAndOppositeLines;
    const rakeLines = options.swapEavesAndRakes ? firstAndOppositeLines : sideLines;
    const surfaceStyleKind = getRoofCreationStyleKind(options.creationMode ?? "gableRoof");
    const surfaceStyle = getEntityTypeStyle(surfaceStyleKind);
    const eaveStyle = getEntityTypeStyle("roof-eave");
    const rakeStyle = getEntityTypeStyle("roof-rake");
    const ridgeStyle = getEntityTypeStyle(options.dashedRidge ? "dashed-polyline" : "roof-ridge");

    runHistoryAction(() => {
      const current = polylines;
      const roofGroupMeta = createRoofGroupMeta(
        options.groupCategory ?? "Pitched",
        current,
        options.creationMode ?? "gableRoof",
      );
      const grondaStartCount = current.filter((shape) => shape.name.startsWith("Gronda")).length;
      const rakeStartCount = current.filter((shape) => shape.name.startsWith("RAKE")).length;
      const colmoStartCount = current.filter((shape) => shape.name.startsWith("Colmo")).length;
      const nextShapes: Shape[] = [
        createRoofSurfaceShape(roofGeometry.outlinePoints, current, surfaceStyle, roofGroupMeta, surfaceStyleKind),
        ...grondaLines.map((linePoints, index) => ({
          id: `polyline-${crypto.randomUUID()}`,
          points: linePoints,
          closed: false,
          kind: "polyline" as const,
          layer: ROOF_LAYER,
          entityStyleKind: "roof-eave" as const,
          name: `Gronda ${grondaStartCount + index + 1}`,
          strokeColor: eaveStyle.strokeColor,
          strokeWidth: eaveStyle.strokeWidth,
          fallSide: getRoofComponentFallSide("Gronda"),
          ...roofGroupMeta,
        })),
        ...rakeLines.map((linePoints, index) => ({
          id: `polyline-${crypto.randomUUID()}`,
          points: linePoints,
          closed: false,
          kind: "polyline" as const,
          layer: ROOF_LAYER,
          entityStyleKind: "roof-rake" as const,
          name: `RAKE ${rakeStartCount + index + 1}`,
          strokeColor: rakeStyle.strokeColor,
          strokeWidth: rakeStyle.strokeWidth,
          fallSide: getRoofComponentFallSide("RAKE"),
          ...roofGroupMeta,
        })),
        ...roofGeometry.ridgeLines.map((linePoints, index) => ({
          id: `polyline-${crypto.randomUUID()}`,
          points: linePoints,
          closed: false,
          kind: "polyline" as const,
          layer: ROOF_LAYER,
          name: `Colmo ${colmoStartCount + index + 1}`,
          strokeColor: ridgeStyle.strokeColor,
          strokeWidth: ridgeStyle.strokeWidth,
          entityStyleKind: (options.dashedRidge ? "dashed-polyline" : "roof-ridge") as EntityStyleKind,
          fallSide: getRoofComponentFallSide("Colmo"),
          ...roofGroupMeta,
        })),
      ];

      pendingCreatedRoofSelectionIdsRef.current = nextShapes.map((shape) => shape.id);
      setPolylines([...current, ...nextShapes]);
      setTextEntities((currentLabels) => [...currentLabels, ...createRoofComponentLabels(nextShapes)]);
    });
  };

  const appendSinglePitchedRoofGeometryShapes = (
    roofGeometry: ReturnType<typeof buildSinglePitchedRoofGeometryRule>,
  ) => {
    if (!roofGeometry) return;

    const surfaceStyleKind = getRoofCreationStyleKind("singlePitchedRoof");
    const surfaceStyle = getEntityTypeStyle(surfaceStyleKind);
    const eaveStyle = getEntityTypeStyle("roof-eave");
    const ridgeStyle = getEntityTypeStyle("roof-ridge");
    const rakeStyle = getEntityTypeStyle("roof-rake");

    runHistoryAction(() => {
      const current = polylines;
      const roofGroupMeta = createRoofGroupMeta("Pitched", current, "singlePitchedRoof");
      const grondaStartCount = current.filter((shape) => shape.name.startsWith("Gronda")).length;
      const rakeStartCount = current.filter((shape) => shape.name.startsWith("RAKE")).length;
      const colmoStartCount = current.filter((shape) => shape.name.startsWith("Colmo")).length;
      const nextShapes: Shape[] = [
        createRoofSurfaceShape(roofGeometry.outlinePoints, current, surfaceStyle, roofGroupMeta, surfaceStyleKind),
        {
          id: `polyline-${crypto.randomUUID()}`,
          points: roofGeometry.eaveLine,
          closed: false,
          kind: "polyline",
          layer: ROOF_LAYER,
          entityStyleKind: "roof-eave",
          name: `Gronda ${grondaStartCount + 1}`,
          strokeColor: eaveStyle.strokeColor,
          strokeWidth: eaveStyle.strokeWidth,
          fallSide: getRoofComponentFallSide("Gronda"),
          ...roofGroupMeta,
        },
        {
          id: `polyline-${crypto.randomUUID()}`,
          points: roofGeometry.colmoLine,
          closed: false,
          kind: "polyline",
          layer: ROOF_LAYER,
          entityStyleKind: "roof-ridge",
          name: `Colmo ${colmoStartCount + 1}`,
          strokeColor: ridgeStyle.strokeColor,
          strokeWidth: ridgeStyle.strokeWidth,
          fallSide: getRoofComponentFallSide("Colmo"),
          ...roofGroupMeta,
        },
        ...roofGeometry.rakeLines.map((linePoints, index) => ({
          id: `polyline-${crypto.randomUUID()}`,
          points: linePoints,
          closed: false,
          kind: "polyline" as const,
          layer: ROOF_LAYER,
          entityStyleKind: "roof-rake" as const,
          name: `RAKE ${rakeStartCount + index + 1}`,
          strokeColor: rakeStyle.strokeColor,
          strokeWidth: rakeStyle.strokeWidth,
          fallSide: getRoofComponentFallSide("RAKE"),
          ...roofGroupMeta,
        })),
      ];

      pendingCreatedRoofSelectionIdsRef.current = nextShapes.map((shape) => shape.id);
      setPolylines([...current, ...nextShapes]);
      setTextEntities((currentLabels) => [...currentLabels, ...createRoofComponentLabels(nextShapes)]);
    });
  };

  const appendMultiShedRoofGeometryShapes = (roofGeometry: ReturnType<typeof buildMultiShedRoofGeometryRule>) => {
    if (!roofGeometry) return;

    const surfaceStyleKind = getRoofCreationStyleKind("multiShedRoof");
    const surfaceStyle = getEntityTypeStyle(surfaceStyleKind);
    const eaveStyle = getEntityTypeStyle("roof-eave");
    const ridgeStyle = getEntityTypeStyle("roof-ridge");
    const rakeStyle = getEntityTypeStyle("roof-rake");

    runHistoryAction(() => {
      const current = polylines;
      const roofGroupMeta = createRoofGroupMeta("Pitched", current, "multiShedRoof");
      const grondaStartCount = current.filter((shape) => shape.name.startsWith("Gronda")).length;
      const rakeStartCount = current.filter((shape) => shape.name.startsWith("RAKE")).length;
      const colmoStartCount = current.filter((shape) => shape.name.startsWith("Colmo")).length;
      const roofSurfaceShape = createRoofSurfaceShape(
        roofGeometry.outlinePoints,
        current,
        surfaceStyle,
        roofGroupMeta,
        surfaceStyleKind,
      );
      const grondaShapes = roofGeometry.eaveLines.map((linePoints, index) => ({
        id: `polyline-${crypto.randomUUID()}`,
        points: linePoints,
        closed: false,
        kind: "polyline" as const,
        layer: ROOF_LAYER,
        entityStyleKind: "roof-eave" as const,
        name: `Gronda ${grondaStartCount + index + 1}`,
        strokeColor: eaveStyle.strokeColor,
        strokeWidth: eaveStyle.strokeWidth,
        fallSide: getRoofComponentFallSide("Gronda"),
        ...roofGroupMeta,
      }));
      const colmoShapes = roofGeometry.colmoLines.map((linePoints, index) => ({
        id: `polyline-${crypto.randomUUID()}`,
        points: linePoints,
        closed: false,
        kind: "polyline" as const,
        layer: ROOF_LAYER,
        entityStyleKind: "roof-ridge" as const,
        name: `Colmo ${colmoStartCount + index + 1}`,
        strokeColor: ridgeStyle.strokeColor,
        strokeWidth: ridgeStyle.strokeWidth,
        fallSide: getRoofComponentFallSide("Colmo"),
        ...roofGroupMeta,
      }));
      const rakeShapes = roofGeometry.rakeLines.map((linePoints, index) => ({
        id: `polyline-${crypto.randomUUID()}`,
        points: linePoints,
        closed: false,
        kind: "polyline" as const,
        layer: ROOF_LAYER,
        entityStyleKind: "roof-rake" as const,
        name: `RAKE ${rakeStartCount + index + 1}`,
        strokeColor: rakeStyle.strokeColor,
        strokeWidth: rakeStyle.strokeWidth,
        fallSide: getRoofComponentFallSide("RAKE"),
        ...roofGroupMeta,
      }));

      const nextShapes = [roofSurfaceShape, ...grondaShapes, ...colmoShapes, ...rakeShapes];
      pendingCreatedRoofSelectionIdsRef.current = nextShapes.map((shape) => shape.id);
      setPolylines([...current, ...nextShapes]);
      setTextEntities((currentLabels) => [...currentLabels, ...createRoofComponentLabels(nextShapes)]);
    });
  };

  const appendMultiGableRoofGeometryShapes = (roofGeometry: ReturnType<typeof buildMultiGableRoofGeometryRule>) => {
    if (roofGeometry.outline.length < 3) return;

    const surfaceStyleKind = getRoofCreationStyleKind("multiGableRoof");
    const surfaceStyle = getEntityTypeStyle(surfaceStyleKind);
    const eaveStyle = getEntityTypeStyle("roof-eave");
    const rakeStyle = getEntityTypeStyle("roof-rake");
    runHistoryAction(() => {
      const current = polylines;
      const roofGroupMeta = createRoofGroupMeta("Pitched", current, "multiGableRoof");
      const grondaStartCount = current.filter((shape) => shape.name.startsWith("Gronda")).length;
      const rakeStartCount = current.filter((shape) => shape.name.startsWith("RAKE")).length;
      const colmoStartCount = current.filter((shape) => shape.name.startsWith("Colmo")).length;
      const hipStartCount = current.filter(
        (shape) => shape.name.startsWith("HIP") || shape.name.startsWith("Diagonale"),
      ).length;
      const valleyStartCount = current.filter((shape) => shape.name.startsWith("Valley")).length;
      const rakeIndexSet = new Set([...roofGeometry.mainRakes, ...roofGeometry.secondaryRakes]);
      let grondaCount = grondaStartCount;
      let rakeCount = rakeStartCount;
      let colmoCount = colmoStartCount;
      let hipCount = hipStartCount;
      let valleyCount = valleyStartCount;
      const roofSurfaceShape = createRoofSurfaceShape(
        getFlatPointsFromVertices(roofGeometry.outline),
        current,
        surfaceStyle,
        roofGroupMeta,
        surfaceStyleKind,
      );

      const edgeShapes: Shape[] = roofGeometry.outline.map((point, index) => {
        const nextPoint = roofGeometry.outline[(index + 1) % roofGeometry.outline.length] ?? point;
        const isRake = rakeIndexSet.has(index);
        const name = isRake ? `RAKE ${++rakeCount}` : `Gronda ${++grondaCount}`;

        return {
          id: `polyline-${crypto.randomUUID()}`,
          points: [point.x, point.y, nextPoint.x, nextPoint.y],
          closed: false,
          kind: "polyline" as const,
          layer: ROOF_LAYER,
          entityStyleKind: isRake ? "roof-rake" : "roof-eave",
          name,
          strokeColor: isRake ? rakeStyle.strokeColor : eaveStyle.strokeColor,
          strokeWidth: isRake ? rakeStyle.strokeWidth : eaveStyle.strokeWidth,
          fallSide: getRoofComponentFallSide(isRake ? "RAKE" : "Gronda"),
          ...roofGroupMeta,
        };
      });

      const roofLineShapes: Shape[] = roofGeometry.roofLines.map((line) => {
        const prefix =
          line.type === "mainRidge" || line.type === "secondaryRidge"
            ? "Colmo"
            : line.type === "hip"
              ? "HIP"
              : "Valley";
        const styleKind = getRoofComponentStyleKind(prefix);
        const style = getEntityTypeStyle(styleKind);
        const name =
          prefix === "Colmo"
            ? `Colmo ${++colmoCount}`
            : prefix === "HIP"
              ? `HIP ${++hipCount}`
              : `Valley ${++valleyCount}`;

        return {
          id: `polyline-${crypto.randomUUID()}`,
          points: [line.start.x, line.start.y, line.end.x, line.end.y],
          closed: false,
          kind: "polyline" as const,
          layer: ROOF_LAYER,
          entityStyleKind: styleKind,
          name,
          strokeColor: style.strokeColor,
          strokeWidth: style.strokeWidth,
          fallSide: getRoofComponentFallSide(prefix),
          ...roofGroupMeta,
        };
      });

      const nextShapes = [roofSurfaceShape, ...edgeShapes, ...roofLineShapes];
      pendingCreatedRoofSelectionIdsRef.current = nextShapes.map((shape) => shape.id);
      setPolylines([...current, ...nextShapes]);
      setTextEntities((currentLabels) => [...currentLabels, ...createRoofComponentLabels(nextShapes)]);
    });
  };

  const buildMultiGableDraftGeometry = (
    draft: Pick<MultiGableRoofDraft, "outline" | "mainRakes" | "secondaryRakes" | "hipsAndValleys">,
  ) => {
    const firstMainRake = draft.mainRakes[0];
    const secondMainRake = draft.mainRakes[1];

    return buildMultiGableRoofGeometryRule({
      points: draft.outline,
      mainRakes:
        typeof firstMainRake === "number" && typeof secondMainRake === "number"
          ? [firstMainRake, secondMainRake]
          : null,
      secondaryRakes: draft.secondaryRakes,
      hipsAndValleys: draft.hipsAndValleys,
    });
  };

  const getMultiGableEdgeAtPoint = (point: CanvasPoint, outline: CanvasPoint[]) => {
    const tolerance = Math.max(8 / viewport.scale, 0.25);
    let bestIndex = -1;
    let bestDistance = Number.POSITIVE_INFINITY;

    outline.forEach((start, index) => {
      const end = outline[(index + 1) % outline.length];
      if (!end) return;
      const edgeDistance = getDistanceToSegment(point, start, end);
      if (edgeDistance < bestDistance) {
        bestDistance = edgeDistance;
        bestIndex = index;
      }
    });

    return bestDistance <= tolerance ? bestIndex : -1;
  };

  const getMultiGableRidgeAtPoint = (point: CanvasPoint, roofLines: MultiGableRoofDraftLine[]) => {
    const tolerance = Math.max(8 / viewport.scale, 0.25);
    let bestLine: MultiGableRoofDraftLine | null = null;
    let bestDistance = Number.POSITIVE_INFINITY;

    roofLines
      .filter((line) => line.type === "mainRidge" || line.type === "secondaryRidge")
      .forEach((line) => {
        const lineDistance = getDistanceToSegment(point, line.start, line.end);
        if (lineDistance < bestDistance) {
          bestDistance = lineDistance;
          bestLine = line;
        }
      });

    return bestDistance <= tolerance ? bestLine : null;
  };

  const getMultiGableHipValleyType = (
    outline: CanvasPoint[],
    eaveAIndex: number,
    eaveBIndex: number,
  ): "hip" | "valley" => {
    const nextA = (eaveAIndex + 1) % outline.length;
    const nextB = (eaveBIndex + 1) % outline.length;
    const commonVertexIndex =
      nextA === eaveBIndex ? eaveBIndex : nextB === eaveAIndex ? eaveAIndex : eaveAIndex === nextB ? eaveAIndex : -1;

    return isConcaveOutlineVertex(outline, commonVertexIndex) ? "valley" : "hip";
  };

  const commitMultiGableRoofDraft = (draft: MultiGableRoofDraft) => {
    const roofGeometry = buildMultiGableDraftGeometry(draft);
    appendMultiGableRoofGeometryShapes(roofGeometry);
    setMultiGableRoofDraft(null);
    setToolMode("select");
    setHoverSnap(null);
    clearOsnapTracking();
  };

  const advanceMultiGableSecondaryRakes = (draft: MultiGableRoofDraft) => {
    const roofGeometry = buildMultiGableDraftGeometry(draft);
    setMultiGableRoofDraft({
      ...draft,
      roofLines: roofGeometry.roofLines,
      stage: "hipValleyEaveA",
    });
  };

  const handleMultiGableDraftClick = (point: CanvasPoint) => {
    if (!multiGableRoofDraft) return false;

    if (multiGableRoofDraft.stage === "mainRakes") {
      const edgeIndex = getMultiGableEdgeAtPoint(point, multiGableRoofDraft.outline);
      if (edgeIndex < 0) return true;
      const nextMainRakes = multiGableRoofDraft.mainRakes.includes(edgeIndex)
        ? multiGableRoofDraft.mainRakes.filter((index) => index !== edgeIndex)
        : [...multiGableRoofDraft.mainRakes, edgeIndex].slice(-2);
      const nextDraft = {
        ...multiGableRoofDraft,
        mainRakes: nextMainRakes,
      };
      setMultiGableRoofDraft(
        nextMainRakes.length === 2
          ? {
              ...nextDraft,
              roofLines: buildMultiGableDraftGeometry(nextDraft).roofLines,
              stage: "secondaryRakes",
            }
          : nextDraft,
      );
      return true;
    }

    if (multiGableRoofDraft.stage === "secondaryRakes") {
      const edgeIndex = getMultiGableEdgeAtPoint(point, multiGableRoofDraft.outline);
      if (edgeIndex < 0 || multiGableRoofDraft.mainRakes.includes(edgeIndex)) return true;
      const nextSecondaryRakes = multiGableRoofDraft.secondaryRakes.includes(edgeIndex)
        ? multiGableRoofDraft.secondaryRakes.filter((index) => index !== edgeIndex)
        : [...multiGableRoofDraft.secondaryRakes, edgeIndex];
      const nextDraft = {
        ...multiGableRoofDraft,
        secondaryRakes: nextSecondaryRakes,
      };
      setMultiGableRoofDraft({
        ...nextDraft,
        roofLines: buildMultiGableDraftGeometry(nextDraft).roofLines,
      });
      return true;
    }

    if (multiGableRoofDraft.stage === "hipValleyEaveA" || multiGableRoofDraft.stage === "hipValleyEaveB") {
      const edgeIndex = getMultiGableEdgeAtPoint(point, multiGableRoofDraft.outline);
      if (
        edgeIndex < 0 ||
        multiGableRoofDraft.mainRakes.includes(edgeIndex) ||
        multiGableRoofDraft.secondaryRakes.includes(edgeIndex)
      ) {
        return true;
      }
      setMultiGableRoofDraft({
        ...multiGableRoofDraft,
        stage: multiGableRoofDraft.stage === "hipValleyEaveA" ? "hipValleyEaveB" : "hipValleyRidgeA",
        pendingEaveAIndex:
          multiGableRoofDraft.stage === "hipValleyEaveA" ? edgeIndex : multiGableRoofDraft.pendingEaveAIndex,
        pendingEaveBIndex:
          multiGableRoofDraft.stage === "hipValleyEaveB" ? edgeIndex : multiGableRoofDraft.pendingEaveBIndex,
      });
      return true;
    }

    const ridge = getMultiGableRidgeAtPoint(point, multiGableRoofDraft.roofLines);
    if (!ridge) return true;

    if (multiGableRoofDraft.stage === "hipValleyRidgeA") {
      setMultiGableRoofDraft({
        ...multiGableRoofDraft,
        stage: "hipValleyRidgeB",
        pendingRidgeA: ridge,
      });
      return true;
    }

    if (
      multiGableRoofDraft.pendingEaveAIndex == null ||
      multiGableRoofDraft.pendingEaveBIndex == null ||
      !multiGableRoofDraft.pendingRidgeA
    ) {
      return true;
    }

    const hipOrValley = buildHipOrValleyRule(
      multiGableRoofDraft.outline,
      getMultiGableHipValleyType(
        multiGableRoofDraft.outline,
        multiGableRoofDraft.pendingEaveAIndex,
        multiGableRoofDraft.pendingEaveBIndex,
      ),
      multiGableRoofDraft.pendingEaveAIndex,
      multiGableRoofDraft.pendingEaveBIndex,
      multiGableRoofDraft.pendingRidgeA,
      ridge,
    );
    if (!hipOrValley) return true;

    const nextDraft = {
      ...multiGableRoofDraft,
      hipsAndValleys: [...multiGableRoofDraft.hipsAndValleys, hipOrValley],
      stage: "hipValleyEaveA" as const,
      pendingEaveAIndex: undefined,
      pendingEaveBIndex: undefined,
      pendingRidgeA: undefined,
    };
    setMultiGableRoofDraft({
      ...nextDraft,
      roofLines: buildMultiGableDraftGeometry(nextDraft).roofLines,
    });
    return true;
  };

  const getRoofLineAtPoint = (point: CanvasPoint, allowedKinds: Array<"Gronda" | "Colmo">) => {
    const tolerance = Math.max(8 / viewport.scale, 0.25);
    let bestShape: PolylineLikeShape | null = null;
    let bestDistance = Number.POSITIVE_INFINITY;

    for (const shape of polylines) {
      if (shape.kind === "circle" || shape.kind === "arc") continue;
      if (getShapeLayer(shape) !== ROOF_LAYER) continue;
      const roofKind = getRoofLineKindFromName(shape.name);
      if (!roofKind) continue;
      const normalizedKind = roofKind === "RAKE" || roofKind === "BL" ? "Gronda" : roofKind;
      if (!allowedKinds.includes(normalizedKind as "Gronda" | "Colmo")) continue;

      for (const segment of getShapeSegments(shape)) {
        const distance = getDistanceToSegment(point, segment.start, segment.end);
        if (distance < bestDistance) {
          bestDistance = distance;
          bestShape = shape as PolylineLikeShape;
        }
      }
    }

    return bestDistance <= tolerance ? bestShape : null;
  };

  const getRoofLineEndpointPair = (shape: PolylineLikeShape) => {
    const endpoints = getShapeEndpointPair(shape);
    if (endpoints) return endpoints;
    const segments = getShapeSegments(shape);
    return segments[0] ?? null;
  };

  const getRoofHipValleyBoundaryOutlinePoints = () => {
    const boundarySegments = polylines
      .filter((shape): shape is PolylineLikeShape => {
        if (shape.kind === "circle" || shape.kind === "arc") return false;
        if (getShapeLayer(shape) !== ROOF_LAYER) return false;
        const roofKind = getRoofLineKindFromName(shape.name);
        return roofKind === "Gronda" || roofKind === "RAKE" || roofKind === "BL";
      })
      .flatMap((shape) => getShapeSegments(shape));

    if (boundarySegments.length < 3) return null;

    const firstSegment = boundarySegments[0];
    if (!firstSegment) return null;
    const outline: CanvasPoint[] = [{ ...firstSegment.start }, { ...firstSegment.end }];
    const remaining = boundarySegments.slice(1);

    while (remaining.length > 0) {
      const last = outline[outline.length - 1];
      if (!last) break;
      const matchIndex = remaining.findIndex(
        (segment) => areCanvasPointsEqual(segment.start, last) || areCanvasPointsEqual(segment.end, last),
      );
      if (matchIndex < 0) break;
      const segment = remaining[matchIndex];
      if (!segment) break;
      outline.push(areCanvasPointsEqual(segment.start, last) ? { ...segment.end } : { ...segment.start });
      remaining.splice(matchIndex, 1);
      const firstOutlinePoint = outline[0];
      const lastOutlinePoint = outline[outline.length - 1];
      if (
        outline.length > 2 &&
        firstOutlinePoint &&
        lastOutlinePoint &&
        areCanvasPointsEqual(firstOutlinePoint, lastOutlinePoint)
      ) {
        outline.pop();
        break;
      }
    }

    return outline.length >= 3 ? getFlatPointsFromVertices(outline) : null;
  };

  const commitRoofHipValleyDraft = (draft: Required<RoofHipValleyDraft>) => {
    const eaveA = getRoofLineEndpointPair(draft.eaveA);
    const eaveB = getRoofLineEndpointPair(draft.eaveB);
    const ridgeA = getRoofLineEndpointPair(draft.ridgeA);
    const ridgeB = getRoofLineEndpointPair(draft.ridgeB);
    if (!eaveA || !eaveB || !ridgeA || !ridgeB) return;
    const pointsToValidate = [
      eaveA.start,
      eaveA.end,
      eaveB.start,
      eaveB.end,
      ridgeA.start,
      ridgeA.end,
      ridgeB.start,
      ridgeB.end,
    ];
    if (pointsToValidate.some((point) => typeof point.x !== "number" || typeof point.y !== "number")) return;
    const [eaveAStart, eaveAEnd, eaveBStart, eaveBEnd, ridgeAStart, ridgeAEnd, ridgeBStart, ridgeBEnd] =
      pointsToValidate as [
        CanvasPoint,
        CanvasPoint,
        CanvasPoint,
        CanvasPoint,
        CanvasPoint,
        CanvasPoint,
        CanvasPoint,
        CanvasPoint,
      ];

    const start =
      getSegmentIntersection(eaveAStart, eaveAEnd, eaveBStart, eaveBEnd) ??
      getInfiniteLineIntersection(eaveAStart, eaveAEnd, eaveBStart, eaveBEnd);
    const end =
      getSegmentIntersection(ridgeAStart, ridgeAEnd, ridgeBStart, ridgeBEnd) ??
      getInfiniteLineIntersection(ridgeAStart, ridgeAEnd, ridgeBStart, ridgeBEnd);

    if (!start || !end) return;

    const boundaryOutlinePoints = getRoofHipValleyBoundaryOutlinePoints();
    const lineType = boundaryOutlinePoints
      ? classifyRoofDiagonalLine([start.x, start.y, end.x, end.y], boundaryOutlinePoints)
      : "HIP";
    const styleKind = getRoofComponentStyleKind(lineType);
    const polylineStyle = getEntityTypeStyle(styleKind);

    runHistoryAction(() => {
      const current = polylines;
      const count = current.filter((shape) =>
        lineType === "HIP"
          ? shape.name.startsWith("HIP") || shape.name.startsWith("Diagonale")
          : shape.name.startsWith("Valley"),
      ).length;
      const nextShape: Shape = {
        id: `polyline-${crypto.randomUUID()}`,
        points: [start.x, start.y, end.x, end.y],
        closed: false,
        kind: "polyline" as const,
        layer: ROOF_LAYER,
        entityStyleKind: styleKind,
        name: `${lineType} ${count + 1}`,
        strokeColor: polylineStyle.strokeColor,
        strokeWidth: polylineStyle.strokeWidth,
        fallSide: getRoofComponentFallSide(lineType),
      };

      setPolylines([...current, nextShape]);
      setTextEntities((currentLabels) => [...currentLabels, ...createRoofComponentLabels([nextShape])]);
    });

    setRoofHipValleyDraft({ stage: "eaveA" });
    setHoverSnap(null);
  };

  const handleRoofHipValleyClick = (point: CanvasPoint) => {
    const draft = roofHipValleyDraft ?? { stage: "eaveA" as const };

    if (draft.stage === "eaveA" || draft.stage === "eaveB") {
      const eave = getRoofLineAtPoint(point, ["Gronda"]);
      if (!eave) return true;
      setSelectedIds([eave.id]);
      setEditingId(null);
      setRoofHipValleyDraft({
        ...draft,
        stage: draft.stage === "eaveA" ? "eaveB" : "ridgeA",
        eaveA: draft.stage === "eaveA" ? eave : draft.eaveA,
        eaveB: draft.stage === "eaveB" ? eave : draft.eaveB,
      });
      return true;
    }

    const ridge = getRoofLineAtPoint(point, ["Colmo"]);
    if (!ridge) return true;
    setSelectedIds([ridge.id]);
    setEditingId(null);

    if (draft.stage === "ridgeA") {
      setRoofHipValleyDraft({
        ...draft,
        stage: "ridgeB",
        ridgeA: ridge,
      });
      return true;
    }

    if (draft.eaveA && draft.eaveB && draft.ridgeA) {
      commitRoofHipValleyDraft({
        stage: "ridgeB",
        eaveA: draft.eaveA,
        eaveB: draft.eaveB,
        ridgeA: draft.ridgeA,
        ridgeB: ridge,
      });
    }
    return true;
  };

  const closeActiveRoof = async () => {
    const points = activePolylineRef.current;
    if (!points) return;

    const committedPoints = points.slice(0, -2);
    if (committedPoints.length < 6) {
      setActivePolyline(null);
      setNumericEdit(null);
      setDirectionLockAxis(null);
      clearOsnapTracking();
      return;
    }

    try {
      const normalizedOutline = normalizeRoofOutlineRule(committedPoints);
      if (!normalizedOutline) {
        setActivePolyline(null);
        setNumericEdit(null);
        setDirectionLockAxis(null);
        clearOsnapTracking();
        return;
      }

      const builder = await ensureRoofSkeletonBuilder();
      const skeleton = builder.buildFromPolygon(normalizedOutline.polygon as number[][][]);
      const roofGeometry = buildRoofGeometryRule(normalizedOutline.outlinePoints, skeleton);

      if (!roofGeometry) {
        console.error("Impossible to build roof geometry from the given outline and skeleton.");
        return;
      }

      appendRoofGeometryShapes(roofGeometry);
    } catch (error) {
      console.error("Error building roof geometry from straight skeleton.", error);
    } finally {
      setActivePolyline(null);
      setNumericEdit(null);
      setDirectionLockAxis(null);
      setHoverSnap(null);
      clearOsnapTracking();
    }
  };

  const closeActiveMultiGableRoof = () => {
    const points = activePolylineRef.current;
    if (!points) return;

    const committedPoints = points.slice(0, -2);
    if (committedPoints.length < 8) {
      setActivePolyline(null);
      setNumericEdit(null);
      setDirectionLockAxis(null);
      clearOsnapTracking();
      return;
    }

    const normalizedOutline = normalizeRoofOutlineRule(committedPoints);
    if (!normalizedOutline) {
      setActivePolyline(null);
      setNumericEdit(null);
      setDirectionLockAxis(null);
      clearOsnapTracking();
      return;
    }

    const outline = getRoofOutlineVertices(normalizedOutline.outlinePoints);
    if (outline.length < 4) {
      setActivePolyline(null);
      setNumericEdit(null);
      setDirectionLockAxis(null);
      clearOsnapTracking();
      return;
    }

    setMultiGableRoofDraft({
      outline,
      mainRakes: [],
      secondaryRakes: [],
      roofLines: [],
      hipsAndValleys: [],
      stage: "mainRakes",
      pendingType: "hip",
    });
    setActivePolyline(null);
    setNumericEdit(null);
    setDirectionLockAxis(null);
    setHoverSnap(null);
    clearOsnapTracking();
  };

  const finalizeActiveGableRoof = (rectangle: ActiveRectangle) => {
    const outlinePoints = buildRectanglePolyline(rectangle);
    if (!outlinePoints) {
      setActiveRectangle(null);
      setNumericEdit(null);
      setDirectionLockAxis(null);
      setHoverSnap(null);
      clearOsnapTracking();
      return;
    }

    const isCurvedRoof = toolMode === "curvedRoof";
    const roofGeometry = isCurvedRoof
      ? buildCurvedRoofGeometryRule(outlinePoints)
      : buildTwoPitchRoofGeometryRule(outlinePoints);
    if (roofGeometry) {
      appendTwoPitchRoofGeometryShapes(roofGeometry, {
        dashedRidge: isCurvedRoof,
        groupCategory: getRoofCreationGroupCategory(isCurvedRoof ? "curvedRoof" : "gableRoof"),
        creationMode: isCurvedRoof ? "curvedRoof" : "gableRoof",
        swapEavesAndRakes: isCurvedRoof,
      });
    }

    setActiveRectangle(null);
    setNumericEdit(null);
    setDirectionLockAxis(null);
    setHoverSnap(null);
    clearOsnapTracking();
  };

  const finalizeActiveSinglePitchedRoof = (rectangle: ActiveRectangle) => {
    const outlinePoints = buildRectanglePolyline(rectangle);
    if (!outlinePoints) {
      setActiveRectangle(null);
      setNumericEdit(null);
      setDirectionLockAxis(null);
      setHoverSnap(null);
      clearOsnapTracking();
      return;
    }

    appendSinglePitchedRoofGeometryShapes(buildSinglePitchedRoofGeometryRule(outlinePoints));

    setActiveRectangle(null);
    setNumericEdit(null);
    setDirectionLockAxis(null);
    setHoverSnap(null);
    clearOsnapTracking();
  };

  const finalizeActiveMultiShedRoof = (rectangle: ActiveRectangle, shedCount: number) => {
    const outlinePoints = buildRectanglePolyline(rectangle);
    if (!outlinePoints) {
      setActiveRectangle(null);
      setNumericEdit(null);
      setDirectionLockAxis(null);
      setHoverSnap(null);
      clearOsnapTracking();
      return;
    }

    appendMultiShedRoofGeometryShapes(buildMultiShedRoofGeometryRule(outlinePoints, shedCount));

    setActiveRectangle(null);
    setNumericEdit(null);
    setDirectionLockAxis(null);
    setHoverSnap(null);
    clearOsnapTracking();
  };

  const finalizeActiveRectangle = (rectangle: ActiveRectangle) => {
    const rectanglePoints = buildRectanglePolyline(rectangle);

    if (rectanglePoints) {
      const rectangleStyle = getEntityTypeStyle("rectangle");
      runHistoryAction(() => {
        setPolylines((existing) => {
          const roofGroupMeta = createRoofGroupMeta("Flat", existing, "rectangle");
          const rectangleShape: Shape = {
            id: `polyline-${crypto.randomUUID()}`,
            points: rectanglePoints,
            closed: true,
            kind: "rectangle",
            layer: ROOF_LAYER,
            entityStyleKind: "rectangle",
            name: getNextEntityName("rectangle", existing, dimensions),
            strokeColor: rectangleStyle.strokeColor,
            strokeWidth: rectangleStyle.strokeWidth,
            fallSide: true,
            ...roofGroupMeta,
          };
          pendingCreatedRoofSelectionIdsRef.current = [rectangleShape.id];
          return [...existing, rectangleShape];
        });
      });
    }

    setActiveRectangle(null);
    setNumericEdit(null);
    setDirectionLockAxis(null);
    setHoverSnap(null);
    clearOsnapTracking();
  };

  const updateActiveRectangleBaseLength = (length: number, start: CanvasPoint, direction: CanvasPoint) => {
    const clampedLength = Math.max(MIN_RECTANGLE_SIDE, length);
    const nextEdgeEnd = {
      x: start.x + direction.x * clampedLength,
      y: start.y + direction.y * clampedLength,
    };

    setActiveRectangle((current) =>
      current
        ? {
            ...current,
            edgeEnd: nextEdgeEnd,
            pointer: nextEdgeEnd,
          }
        : current,
    );

    setNumericEdit((current) =>
      current?.kind === "rectangle-base"
        ? {
            ...current,
            direction,
            directionValue: getDirectionValue(direction),
            position: nextEdgeEnd,
            value: formatMeasurementValue(clampedLength),
          }
        : current,
    );
  };

  const updateActiveRectangleHeight = (
    length: number,
    start: CanvasPoint,
    edgeEnd: CanvasPoint,
    direction: CanvasPoint,
  ) => {
    const clampedLength = Math.max(MIN_RECTANGLE_SIDE, length);
    const nextPointer = {
      x: start.x + direction.x * clampedLength,
      y: start.y + direction.y * clampedLength,
    };
    const nextRectangle: ActiveRectangle = {
      start,
      edgeEnd,
      pointer: nextPointer,
    };

    setActiveRectangle(nextRectangle);
    setNumericEdit((current) =>
      current?.kind === "rectangle-height"
        ? {
            ...current,
            direction,
            directionValue: getDirectionValue(direction),
            position: nextPointer,
            value: formatMeasurementValue(clampedLength),
          }
        : current,
    );
  };

  const updateActiveCircleFromLength = (length: number, center: CanvasPoint, direction: CanvasPoint) => {
    const clampedLength = Math.max(0, length);
    const nextPointer = {
      x: center.x + direction.x * clampedLength,
      y: center.y + direction.y * clampedLength,
    };

    setActiveCircle({
      center,
      pointer: nextPointer,
    });
    setNumericEdit((current) =>
      current?.kind === "circle"
        ? {
            ...current,
            direction,
            directionValue: getDirectionValue(direction),
            position: nextPointer,
            value: formatMeasurementValue(clampedLength),
          }
        : current,
    );
  };

  const finalizeCircleFromActiveState = () => {
    if (!activeCircle) return;

    finalizeCircle(activeCircle);
  };

  const finalizeCircle = (
    circle: ActiveCircle,
    styleKind: EntityStyleKind = "circle",
    options?: {
      isObstacle?: boolean;
      layer?: EntityLayerName;
      name?: string;
    },
  ) => {
    const radius = getActiveCircleRadius(circle);
    if (radius <= 0) {
      setActiveCircle(null);
      setNumericEdit(null);
      return null;
    }

    if (styleKind === "single-anchor-point" && doesCircleIntersectObstacles(circle.center, radius)) {
      return null;
    }

    const nextId = `polyline-${crypto.randomUUID()}`;
    const circleStyle = getEntityTypeStyle(styleKind);
    const anchorPointSystemMeta = styleKind === "single-anchor-point" ? getSelectedAnchorPointSystemMeta() : {};
    runHistoryAction(() => {
      setPolylines((existing) => [
        ...existing,
        {
          id: nextId,
          center: circle.center,
          radius,
          closed: true,
          kind: "circle",
          isObstacle: options?.isObstacle,
          entityStyleKind: styleKind,
          layer: options?.layer ?? SAFETY_LAYER,
          name: options?.name ?? getNextEntityName("circle", existing, dimensions),
          strokeColor: circleStyle.strokeColor,
          strokeWidth: circleStyle.strokeWidth,
          ...anchorPointSystemMeta,
        },
      ]);
    });

    setActiveCircle(null);
    setNumericEdit(null);
    clearOsnapTracking();
    return nextId;
  };

  const buildArcPoints = (arc: ActiveArc) => {
    if (!arc.start) return [];

    const radius = getDistance(arc.center, arc.start);
    if (radius <= 0) return [];

    const startAngle = Math.atan2(arc.start.y - arc.center.y, arc.start.x - arc.center.x);
    const endAngle = Math.atan2(arc.pointer.y - arc.center.y, arc.pointer.x - arc.center.x);
    const sweep = (((endAngle - startAngle) % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
    const effectiveSweep = sweep === 0 ? Math.PI * 2 : sweep;
    const segmentCount = Math.max(8, Math.ceil((effectiveSweep * radius) / 500));
    const points: number[] = [];

    for (let index = 0; index <= segmentCount; index += 1) {
      const angle = startAngle + (effectiveSweep * index) / segmentCount;
      points.push(arc.center.x + Math.cos(angle) * radius, arc.center.y + Math.sin(angle) * radius);
    }

    return points;
  };

  const getActiveArcSnapPoints = (arc: ActiveArc | null) => {
    if (!arc) return null;
    if (!arc.start) {
      return [arc.center.x, arc.center.y, arc.center.x, arc.center.y];
    }

    return [arc.center.x, arc.center.y, arc.start.x, arc.start.y];
  };

  const finalizeArc = (arc: ActiveArc) => {
    if (!arc.start) {
      setActiveArc(null);
      setNumericEdit(null);
      return null;
    }

    const radius = getDistance(arc.center, arc.start);
    if (radius <= 0) {
      setActiveArc(null);
      setNumericEdit(null);
      return null;
    }

    const startAngle = Math.atan2(arc.start.y - arc.center.y, arc.start.x - arc.center.x);
    const endAngle = Math.atan2(arc.pointer.y - arc.center.y, arc.pointer.x - arc.center.x);

    const nextId = `polyline-${crypto.randomUUID()}`;
    const arcStyle = getEntityTypeStyle("arc");
    runHistoryAction(() => {
      setPolylines((existing) => [
        ...existing,
        {
          id: nextId,
          closed: false,
          kind: "arc",
          center: clonePoint(arc.center),
          radius,
          startAngle,
          endAngle,
          entityStyleKind: "arc",
          layer: SAFETY_LAYER,
          name: getNextNamedEntityName("Arc", existing),
          strokeColor: arcStyle.strokeColor,
          strokeWidth: arcStyle.strokeWidth,
        },
      ]);
    });

    setActiveArc(null);
    setNumericEdit(null);
    clearOsnapTracking();
    return nextId;
  };

  const commitNumericEdit = () => {
    if (!numericEdit) return;

    const parsedLength = parseMeasurementValue(numericEdit.value);
    const copySequenceCount =
      numericEdit.kind === "move" && moveState?.mode === "copy" ? parseCopySequenceCount(numericEdit.value) : null;
    if (numericEdit.kind === "move" && copySequenceCount != null) {
      if (!moveState) return;

      const nextPoint = numericEdit.position;
      const movedState = applyMoveState(moveState, nextPoint);
      let nextSelectedIds = moveState.entityIds;
      const deltaX = nextPoint.x - moveState.anchorPoint.x;
      const deltaY = nextPoint.y - moveState.anchorPoint.y;
      if (Math.abs(deltaX) > 0.0001 || Math.abs(deltaY) > 0.0001) {
        const extraIds = duplicateCopiedEntitiesInSequence(
          moveState.entityIds,
          deltaX,
          deltaY,
          copySequenceCount,
          movedState.shapes,
          movedState.dimensions,
          movedState.iconEntities,
        );
        nextSelectedIds = [...moveState.entityIds, ...extraIds];
      }
      finishHistoryAction();
      setSelectedIds(nextSelectedIds);
      setMoveState(null);
      setNumericEdit(null);
      setHoverSnap(null);
      return;
    }

    if (numericEdit.kind === "scale-rotate") {
      const parsedTransformValue = parseLocalizedNumber(numericEdit.value);
      if (parsedTransformValue == null || !Number.isFinite(parsedTransformValue)) return;
      if (!transformState) return;
      applyScaleRotateTransform(transformState, parsedTransformValue);
      setTransformState(null);
      setNumericEdit(null);
      setHoverSnap(null);
      setEditingId(null);
      return;
    }

    if (parsedLength == null) return;

    if (numericEdit.kind === "automatic-lifeline-offset") {
      const clampedDistance = Math.max(0, parsedLength);
      setAutomaticLifeLineOffsetDistance(clampedDistance);

      if (
        toolMode === "automaticLifeLine" &&
        (!automaticLifeLineSourceIds || automaticLifeLineSourceIds.length === 0)
      ) {
        const selectedSourceShapes = getAutomaticLifeLineSourceShapesFromIds(selectedIds);
        if (selectedSourceShapes.length > 0) {
          const sourceIds = getAutomaticLifeLineSourceIdsFromShapes(selectedSourceShapes);
          setAutomaticLifeLineSourceIds(sourceIds);
          setSelectedIds(sourceIds);
          setLifeLineRoofSelectionWarning(null);
        }
      }

      setNumericEdit({
        ...numericEdit,
        value: formatMeasurementValue(clampedDistance),
      });
      return;
    }

    if (numericEdit.kind === "offset") {
      const sourceShape = getShapeById(numericEdit.entityId);
      if (!sourceShape || !activeOffset) return;

      beginHistoryAction();
      const nextId = commitOffset(sourceShape, activeOffset.pointer, parsedLength, activeOffset.sourceContour);
      if (!nextId) {
        cancelHistoryAction();
        return;
      }
      finishHistoryAction();

      setSelectedIds([nextId]);
      setActiveOffset(null);
      setNumericEdit(null);
      return;
    }

    if (numericEdit.kind === "dimension-object") {
      const shape = getShapeById(numericEdit.entityId);
      if (!shape) return;

      const createdIds = autoDimensionShape(shape, Math.max(0, parsedLength));
      if (createdIds.length === 0) {
        setSelectedIds([shape.id]);
        setEditingId(null);
        return;
      }

      setSelectedIds([shape.id]);
      setEditingId(null);
      setActiveObjectDimension(null);
      setNumericEdit(null);
      setToolMode("select");
      return;
    }

    const parsedDirectionAngle = numericEditSupportsDirection(numericEdit)
      ? parseLocalizedNumber(numericEdit.directionValue)
      : null;
    const nextDirection =
      numericEditSupportsDirection(numericEdit) && parsedDirectionAngle != null
        ? getDirectionVectorFromAngle(parsedDirectionAngle)
        : numericEditSupportsDirection(numericEdit)
          ? numericEdit.direction
          : null;

    if (numericEdit.kind === "tracking") {
      const direction = nextDirection ?? numericEdit.direction;
      const nextPoint = {
        x: numericEdit.origin.x + direction.x * Math.max(0, parsedLength),
        y: numericEdit.origin.y + direction.y * Math.max(0, parsedLength),
      };

      setNumericEdit(null);
      setHoverSnap(null);

      if (
        toolMode === "polyline" ||
        toolMode === "singleLifeLine" ||
        toolMode === "buildPolyline" ||
        (toolMode === "hatch" && hatchToolMode === "build") ||
        isRoofComponentToolMode(toolMode) ||
        toolMode === "roof" ||
        toolMode === "multiGableRoof" ||
        toolMode === "obstaclePolyline" ||
        toolMode === "holePolyline"
      ) {
        handlePolylineMouseDown(nextPoint, null);
        return;
      }

      if (
        toolMode === "rectangle" ||
        toolMode === "gableRoof" ||
        toolMode === "curvedRoof" ||
        toolMode === "singlePitchedRoof" ||
        toolMode === "multiShedRoof"
      ) {
        handleRectangleMouseDown(nextPoint);
        return;
      }

      if (toolMode === "circle" || toolMode === "holeCircle" || toolMode === "obstacleCircle") {
        handleCircleMouseDown(nextPoint);
        return;
      }

      if (toolMode === "arc") {
        handleArcMouseDown(nextPoint, null);
        return;
      }

      if (toolMode === "ruler") {
        handleRulerMouseDown(nextPoint);
        return;
      }

      if (toolMode === "dimension") {
        handleDimensionMouseDown(nextPoint);
      }
      return;
    }

    if (numericEdit.kind === "move") {
      if (!moveState) return;

      const nextPoint = {
        x: numericEdit.anchor.x + (nextDirection ?? numericEdit.direction).x * Math.max(0, parsedLength),
        y: numericEdit.anchor.y + (nextDirection ?? numericEdit.direction).y * Math.max(0, parsedLength),
      };

      applyMoveState(moveState, nextPoint);
      finishHistoryAction();
      setMoveState(null);
      setNumericEdit(null);
      setHoverSnap(null);
      return;
    }

    if (numericEdit.kind === "segment-stretch") {
      const direction = nextDirection ?? numericEdit.direction;
      const nextPoint = {
        x: numericEdit.anchor.x + direction.x * Math.max(0, parsedLength),
        y: numericEdit.anchor.y + direction.y * Math.max(0, parsedLength),
      };

      runHistoryAction(() => {
        applySegmentStretch(
          {
            polylineId: numericEdit.entityId,
            vertexIndices: numericEdit.vertexIndices,
            originalPoints: numericEdit.originalPoints,
            startPointer: numericEdit.anchor,
            shapeKind: numericEdit.shapeKind,
            holeIndex: numericEdit.holeIndex,
          },
          nextPoint,
        );
      });

      setDraggedSegmentStretch(null);
      setHoverSnap(null);
      setInvalidGripFeedback(null);
      clearOsnapTracking();
      setNumericEdit(null);
      return;
    }

    if (numericEdit.kind === "grip") {
      const shape = getShapeById(numericEdit.entityId);
      if (!shape) return;

      const direction = nextDirection ?? numericEdit.direction;
      const nextPoint = {
        x: numericEdit.anchor.x + direction.x * Math.max(0, parsedLength),
        y: numericEdit.anchor.y + direction.y * Math.max(0, parsedLength),
      };

      if (
        !isHoleGripUpdateValid(
          shape,
          numericEdit.vertexIndex,
          nextPoint,
          numericEdit.linkedVertexIndices,
          numericEdit.holeIndex,
          numericEdit.holeGripKind,
        )
      ) {
        setInvalidGripFeedback({ point: nextPoint });
        return;
      }

      runHistoryAction(() => {
        applyShapeGripUpdate(
          shape,
          numericEdit.vertexIndex,
          nextPoint,
          numericEdit.linkedVertexIndices,
          numericEdit.holeIndex,
          numericEdit.holeGripKind,
        );
      });

      setDraggedGrip(null);
      setHoverSnap(null);
      setInvalidGripFeedback(null);
      setNumericEdit(null);
      return;
    }

    if (numericEdit.kind === "polyline") {
      const direction = nextDirection ?? numericEdit.direction;
      const nextPoint = {
        x: numericEdit.anchor.x + direction.x * Math.max(0, parsedLength),
        y: numericEdit.anchor.y + direction.y * Math.max(0, parsedLength),
      };
      handlePolylineMouseDown(nextPoint, null);
      return;
    }

    if (numericEdit.kind === "rectangle-base") {
      const direction = nextDirection ?? numericEdit.direction;
      updateActiveRectangleBaseLength(parsedLength, numericEdit.start, direction);
      if (
        toolMode === "rectangle" ||
        toolMode === "gableRoof" ||
        toolMode === "curvedRoof" ||
        toolMode === "singlePitchedRoof" ||
        toolMode === "multiShedRoof"
      ) {
        const edgeEnd = {
          x: numericEdit.start.x + direction.x * Math.max(MIN_RECTANGLE_SIDE, parsedLength),
          y: numericEdit.start.y + direction.y * Math.max(MIN_RECTANGLE_SIDE, parsedLength),
        };
        const rakeDirection = {
          x: -direction.y,
          y: direction.x,
        };
        clearDirectionLock();
        setNumericEdit({
          kind: "rectangle-height",
          start: numericEdit.start,
          edgeEnd,
          direction: rakeDirection,
          directionValue: getDirectionValue(rakeDirection),
          position: edgeEnd,
          value: formatMeasurementValue(0),
        });
        focusNumericField("value");
        return;
      }
      setNumericEdit(null);
      return;
    }

    if (numericEdit.kind === "multi-shed-count") {
      const parsedShedCount = parseLocalizedNumber(numericEdit.value);
      const shedCount = Math.max(1, Math.floor(parsedShedCount ?? 1));
      finalizeActiveMultiShedRoof(numericEdit.rectangle, shedCount);
      return;
    }

    if (numericEdit.kind === "rectangle-height") {
      updateActiveRectangleHeight(
        parsedLength,
        numericEdit.start,
        numericEdit.edgeEnd,
        nextDirection ?? numericEdit.direction,
      );

      const finalRectangle: ActiveRectangle = {
        start: numericEdit.start,
        edgeEnd: numericEdit.edgeEnd,
        pointer: {
          x:
            numericEdit.start.x +
            (nextDirection ?? numericEdit.direction).x * Math.max(MIN_RECTANGLE_SIDE, parsedLength),
          y:
            numericEdit.start.y +
            (nextDirection ?? numericEdit.direction).y * Math.max(MIN_RECTANGLE_SIDE, parsedLength),
        },
      };
      if (toolMode === "gableRoof" || toolMode === "curvedRoof") {
        finalizeActiveGableRoof(finalRectangle);
      } else if (toolMode === "singlePitchedRoof") {
        finalizeActiveSinglePitchedRoof(finalRectangle);
      } else if (toolMode === "multiShedRoof") {
        const totalLength = Math.max(MIN_RECTANGLE_SIDE, parsedLength);
        setActiveRectangle(finalRectangle);
        setNumericEdit({
          kind: "multi-shed-count",
          rectangle: finalRectangle,
          totalLength,
          value: "2",
        });
        focusNumericField("value");
      } else {
        finalizeActiveRectangle(finalRectangle);
      }
      return;
    }

    if (numericEdit.kind === "single-anchor-point") {
      const clampedLength = Math.max(0, parsedLength);
      setActiveCircle((current) =>
        current
          ? {
              center: current.center,
              pointer: {
                x: current.center.x + clampedLength,
                y: current.center.y,
              },
            }
          : current,
      );
      setNumericEdit((current) =>
        current?.kind === "single-anchor-point"
          ? {
              ...current,
              value: formatMeasurementValue(clampedLength),
            }
          : current,
      );
      return;
    }

    if (numericEdit.kind === "arc") {
      if (!activeArc) return;

      const direction = nextDirection ?? numericEdit.direction;
      const nextPoint = {
        x: numericEdit.center.x + direction.x * Math.max(0, parsedLength),
        y: numericEdit.center.y + direction.y * Math.max(0, parsedLength),
      };

      if (!activeArc.start) {
        setActiveArc({
          center: activeArc.center,
          start: nextPoint,
          pointer: nextPoint,
        });
        setNumericEdit({
          ...numericEdit,
          start: nextPoint,
          direction,
          directionValue: getDirectionValue(direction),
          position: nextPoint,
          value: formatMeasurementValue(Math.max(0, parsedLength)),
        });
        focusNumericField("value");
        return;
      }

      const nextArc = {
        ...activeArc,
        pointer: nextPoint,
      };
      const nextId = finalizeArc(nextArc);
      if (!nextId) return;

      setSelectedIds([nextId]);
      setEditingId(null);
      setHoverSnap(null);
      setToolMode("select");
      return;
    }

    const pointSeriesEdit = numericEdit as NumericEditState | null;
    if (pointSeriesEdit?.kind === "point-series") {
      if (!activePointSeries || activePointSeries.length < 2) return;
      if (activeNumericInput === "lineDistance") {
        const parsedLineDistance = parseMeasurementValue(pointSeriesEdit.lineDistanceValue);
        if (parsedLineDistance == null) return;

        const direction =
          nextDirection ??
          (getDistance(pointSeriesEdit.anchor, pointSeriesEdit.position) > 0
            ? getNormalizedDirection(pointSeriesEdit.anchor, pointSeriesEdit.position)
            : { x: 1, y: 0 });
        setNumericEdit((current) =>
          current?.kind === "point-series"
            ? {
                ...current,
                position: {
                  x: current.anchor.x + direction.x * Math.max(0, parsedLineDistance),
                  y: current.anchor.y + direction.y * Math.max(0, parsedLineDistance),
                },
              }
            : current,
        );
        return;
      }

      setPointSeriesMaxLength(Math.max(0.001, parsedLength));
      setNumericEdit((current) =>
        current?.kind === "point-series"
          ? {
              ...current,
              value: formatMeasurementValue(Math.max(0.001, parsedLength)),
            }
          : current,
      );
      return;
    }

    const circleEdit = numericEdit as Extract<NumericEditState, { kind: "circle" }>;
    const circleDirection = nextDirection ?? circleEdit.direction;
    const clampedLength = Math.max(0, parsedLength);
    const nextCircle: ActiveCircle = {
      center: circleEdit.center,
      pointer: {
        x: circleEdit.center.x + circleDirection.x * clampedLength,
        y: circleEdit.center.y + circleDirection.y * clampedLength,
      },
    };

    updateActiveCircleFromLength(parsedLength, circleEdit.center, circleDirection);
    if (toolMode === "holeCircle") {
      finalizeCircleHole(nextCircle);
      return;
    }

    if (toolMode === "obstacleCircle") {
      finalizeCircle(nextCircle, "circular-obstacle", {
        isObstacle: true,
        layer: ROOF_LAYER,
        name: getNextObstacleName(polylines),
      });
      return;
    }

    finalizeCircle(nextCircle);
  };

  const handleNumericEditChange = (nextValue: string) => {
    setOverwriteNumericInput(null);
    setNumericEdit((current) => {
      if (!current) return current;

      if (current.kind === "single-anchor-point") {
        const parsed = parseMeasurementValue(nextValue);
        if (parsed != null) {
          setActiveCircle((existing) =>
            existing
              ? {
                  center: existing.center,
                  pointer: {
                    x: existing.center.x + Math.max(0, parsed),
                    y: existing.center.y,
                  },
                }
              : existing,
          );
        }
      }

      if (current.kind === "dimension-object") {
        const parsed = parseMeasurementValue(nextValue);
        if (parsed != null) {
          setActiveObjectDimension((existing) =>
            existing && existing.shapeId === current.entityId
              ? {
                  ...existing,
                  offset: Math.max(0, parsed),
                }
              : existing,
          );
        }
      }

      if (current.kind === "point-series" && activeNumericInput === "lineDistance") {
        const parsed = parseMeasurementValue(nextValue);
        const direction =
          getDistance(current.anchor, current.position) > 0
            ? getNormalizedDirection(current.anchor, current.position)
            : { x: 1, y: 0 };
        const nextState = {
          ...current,
          lineDistanceValue: nextValue,
          position:
            parsed != null
              ? {
                  x: current.anchor.x + direction.x * Math.max(0, parsed),
                  y: current.anchor.y + direction.y * Math.max(0, parsed),
                }
              : current.position,
        };
        setNumericEdit(nextState);
        return nextState;
      }

      return { ...current, value: nextValue };
    });
  };

  const handleNumericDirectionChange = (nextValue: string) => {
    setOverwriteNumericInput(null);
    setNumericEdit((current) =>
      current && numericEditSupportsDirection(current)
        ? {
            ...current,
            directionValue: nextValue,
          }
        : current,
    );
  };

  const handleOverwriteNumericInputKey = (
    event: React.KeyboardEvent<HTMLInputElement>,
    target: "value" | "direction" | "lineDistance",
  ) => {
    if (event.ctrlKey || event.metaKey || event.altKey) return false;

    const nextChar = getOverwriteInputCharacter(event.key, event.code);
    if (nextChar && overwriteNumericInput === target) {
      event.preventDefault();
      if (target === "value") {
        handleNumericEditChange(nextChar);
      } else if (target === "direction") {
        handleNumericDirectionChange(nextChar);
      } else {
        handleNumericEditChange(nextChar);
      }
      return true;
    }

    if ((event.key === "Backspace" || event.key === "Delete") && overwriteNumericInput === target) {
      event.preventDefault();
      if (target === "value") {
        handleNumericEditChange("");
      } else if (target === "direction") {
        handleNumericDirectionChange("");
      } else {
        handleNumericEditChange("");
      }
      return true;
    }

    return false;
  };

  const getRoofGroupMemberIds = (roofGroupId: string) =>
    polylines.filter((shape) => shape.roofGroupId === roofGroupId).map((shape) => shape.id);

  const selectRoofForLifeLineAtPoint = (point: CanvasPoint) => {
    const hitRoofShape = getEntityHitIds(point)
      .map((id) => polylines.find((shape) => shape.id === id) ?? null)
      .find((shape): shape is Shape => Boolean(shape && getRoofContextId(shape)));

    if (!hitRoofShape) {
      setLifeLineRoofSelectionWarning(
        "Click the specific roof for this lifeline. The system list will be filtered by the selected roof.",
      );
      return false;
    }

    const roofIds = hitRoofShape.roofGroupId ? getRoofGroupMemberIds(hitRoofShape.roofGroupId) : [hitRoofShape.id];
    setSelectedIds(roofIds.length > 0 ? roofIds : [hitRoofShape.id]);
    setSelectionCycle(null);
    setEditingId(null);
    setLifeLineRoofSelectionWarning(null);
    setIsEntityPropertiesOpen(true);
    setActiveConfigurationPage("lifelineSystems");
    setActiveLifeLineTab("info");
    return true;
  };

  const handleSelectionClick = (point: CanvasPoint, shiftKey: boolean) => {
    const hitIds = getEntityHitIds(point);
    const hitId = hitIds[0] ?? null;

    if (!hitId) {
      setSelectionCycle(null);
      if (!shiftKey) {
        setSelectedIds([]);
        setEditingId(null);
        cancelTextEditing();
      }
      return;
    }

    const hitShape = polylines.find((shape) => shape.id === hitId);
    const roofGroupMemberIds = hitShape?.roofGroupId ? getRoofGroupMemberIds(hitShape.roofGroupId) : [];
    const shouldSelectRoofGroup = roofGroupMemberIds.length > 1;

    setSelectionCycle(!shouldSelectRoofGroup && hitIds.length > 1 ? { point, ids: hitIds, index: 0 } : null);

    setSelectedIds((current) => {
      if (shouldSelectRoofGroup) {
        const isGroupAlreadySelected =
          roofGroupMemberIds.every((id) => current.includes(id)) &&
          current.some((id) => roofGroupMemberIds.includes(id));

        if (!shiftKey) {
          return isGroupAlreadySelected ? [hitId] : roofGroupMemberIds;
        }

        if (isGroupAlreadySelected) {
          return current.filter((id) => !roofGroupMemberIds.includes(id));
        }

        return [...new Set([...current, ...roofGroupMemberIds])];
      }

      if (!shiftKey) {
        return [hitId];
      }

      return current.includes(hitId) ? current.filter((id) => id !== hitId) : [...current, hitId];
    });

    if (editingId && editingId !== hitId && !shiftKey) {
      setEditingId(null);
    }

    if (!textEntities.some((textEntity) => textEntity.id === hitId)) {
      cancelTextEditing();
    }
  };

  const handleSelectionDoubleClick = (point: CanvasPoint) => {
    const hitIds = getEntityHitIds(point);
    const selectedHitId = hitIds.find((id) => selectedIds.includes(id)) ?? getSelectedEntityAreaHitId(point);
    const hitId = selectedHitId ?? hitIds[0] ?? null;
    if (!hitId) {
      setSelectionCycle(null);
      setEditingId(null);
      cancelTextEditing();
      return;
    }

    setSelectionCycle(hitIds.length > 1 ? { point, ids: hitIds, index: 0 } : null);

    const textEntity = textEntities.find((item) => item.id === hitId);
    if (textEntity) {
      setSelectedIds([hitId]);
      startTextEditing(textEntity);
      return;
    }

    if (!selectedIds.includes(hitId)) {
      setSelectedIds([hitId]);
      setEditingId(null);
      cancelTextEditing();
      return;
    }

    if (dimensions.some((dimension) => dimension.id === hitId) || getShapeById(hitId)) {
      setEditingId(hitId);
      cancelTextEditing();
      return;
    }

    if (iconEntities.some((iconEntity) => iconEntity.id === hitId)) {
      setEditingId(hitId);
      cancelTextEditing();
      return;
    }
  };

  const handlePolylineMouseDown = (point: CanvasPoint, osnap: OsnapPoint | null) => {
    const current = activePolylineRef.current;
    let anchor: CanvasPoint | null = null;

    if (current) {
      anchor = current.length < 4 ? getPointFromCoordinateArray(current, 0) : getLastCommittedPoint(current);
    }

    setActivePolyline((currentPolyline) => {
      setHoverSnap(osnap);

      if (!currentPolyline) {
        return [point.x, point.y, point.x, point.y];
      }

      const committedPoints = currentPolyline.slice(0, -2);
      return [...committedPoints, point.x, point.y, point.x, point.y];
    });

    if (anchor) {
      const length = getDistance(anchor, point);
      const direction = getNormalizedDirection(anchor, point);
      focusNumericField("value");
      setNumericEdit({
        kind: "polyline",
        anchor,
        direction,
        directionValue: getDirectionValue(direction),
        position: point,
        value: formatMeasurementValue(length),
      });
    } else {
      focusNumericField("value");
      setNumericEdit({
        kind: "polyline",
        anchor: point,
        direction: { x: 1, y: 0 },
        directionValue: getDirectionValue({ x: 1, y: 0 }),
        position: point,
        value: formatMeasurementValue(0),
      });
    }
  };

  const handleRectangleMouseDown = (point: CanvasPoint) => {
    if (toolMode === "multiShedRoof" && numericEdit?.kind === "multi-shed-count") {
      return;
    }

    if (!activeRectangle) {
      setActiveRectangle({
        start: point,
        edgeEnd: null,
        pointer: point,
      });
      focusNumericField("value");
      setNumericEdit({
        kind: "rectangle-base",
        start: point,
        direction: { x: 1, y: 0 },
        directionValue: getDirectionValue({ x: 1, y: 0 }),
        position: point,
        value: formatMeasurementValue(0),
      });
      return;
    }

    if (!activeRectangle.edgeEnd) {
      setActiveRectangle({
        ...activeRectangle,
        edgeEnd: point,
        pointer: point,
      });
      const direction = getNormalizedDirection(activeRectangle.start, point);

      if (
        toolMode === "rectangle" ||
        toolMode === "gableRoof" ||
        toolMode === "curvedRoof" ||
        toolMode === "singlePitchedRoof" ||
        toolMode === "multiShedRoof"
      ) {
        const rakeDirection = {
          x: -direction.y,
          y: direction.x,
        };
        clearDirectionLock();
        focusNumericField("value");
        setNumericEdit({
          kind: "rectangle-height",
          start: activeRectangle.start,
          edgeEnd: point,
          direction: rakeDirection,
          directionValue: getDirectionValue(rakeDirection),
          position: point,
          value: formatMeasurementValue(0),
        });
        return;
      }

      focusNumericField("value");
      setNumericEdit({
        kind: "rectangle-base",
        start: activeRectangle.start,
        direction,
        directionValue: getDirectionValue(direction),
        position: point,
        value: formatMeasurementValue(getDistance(activeRectangle.start, point)),
      });
      return;
    }

    const baseDx = activeRectangle.edgeEnd.x - activeRectangle.start.x;
    const baseDy = activeRectangle.edgeEnd.y - activeRectangle.start.y;
    const baseLength = Math.hypot(baseDx, baseDy);
    if (baseLength === 0) {
      setActiveRectangle(null);
      setNumericEdit(null);
      return;
    }

    const normal = {
      x: -baseDy / baseLength,
      y: baseDx / baseLength,
    };
    const offset = (point.x - activeRectangle.start.x) * normal.x + (point.y - activeRectangle.start.y) * normal.y;
    const directionSign = offset >= 0 ? 1 : -1;
    const heightDirection = {
      x: normal.x * directionSign,
      y: normal.y * directionSign,
    };
    const heightLength = Math.abs(offset);
    const nextPointer = {
      x: activeRectangle.start.x + heightDirection.x * heightLength,
      y: activeRectangle.start.y + heightDirection.y * heightLength,
    };

    const nextRectangle: ActiveRectangle = {
      ...activeRectangle,
      pointer: nextPointer,
    };

    setActiveRectangle(nextRectangle);

    if (toolMode === "rectangle") {
      finalizeActiveRectangle(nextRectangle);
      return;
    }

    if (toolMode === "gableRoof" || toolMode === "curvedRoof") {
      finalizeActiveGableRoof(nextRectangle);
      return;
    }

    if (toolMode === "singlePitchedRoof") {
      finalizeActiveSinglePitchedRoof(nextRectangle);
      return;
    }

    if (toolMode === "multiShedRoof") {
      setNumericEdit({
        kind: "multi-shed-count",
        rectangle: nextRectangle,
        totalLength: heightLength,
        value: "2",
      });
      focusNumericField("value");
      return;
    }

    focusNumericField("value");
    setNumericEdit({
      kind: "rectangle-height",
      start: activeRectangle.start,
      edgeEnd: activeRectangle.edgeEnd,
      direction: heightDirection,
      directionValue: getDirectionValue(heightDirection),
      position: nextPointer,
      value: formatMeasurementValue(heightLength),
    });
  };

  const handleCircleMouseDown = (point: CanvasPoint) => {
    const resolved = resolveSnapPoint(point, null, null, null, null, true, activeCircle?.center ?? null);
    setHoverSnap(resolved.osnap);

    if (!activeCircle) {
      setActiveCircle({
        center: resolved.point,
        pointer: resolved.point,
      });
      focusNumericField("value");
      setNumericEdit({
        kind: "circle",
        center: resolved.point,
        direction: { x: 1, y: 0 },
        directionValue: getDirectionValue({ x: 1, y: 0 }),
        position: resolved.point,
        value: formatMeasurementValue(0),
      });
      return;
    }

    if (numericEdit?.kind === "circle") {
      finalizeCircleFromActiveState();
      return;
    }

    const nextCircle = {
      ...activeCircle,
      pointer: resolved.point,
    };
    const radius = getActiveCircleRadius(nextCircle);
    const direction = getNormalizedDirection(nextCircle.center, resolved.point);

    setActiveCircle(nextCircle);
    focusNumericField("value");
    setNumericEdit({
      kind: "circle",
      center: nextCircle.center,
      direction,
      directionValue: getDirectionValue(direction),
      position: resolved.point,
      value: formatMeasurementValue(radius),
    });
  };

  const handleArcMouseDown = (point: CanvasPoint, osnap: OsnapPoint | null = null) => {
    setHoverSnap(osnap);
    if (!activeArc) {
      setActiveArc({
        center: point,
        start: null,
        pointer: point,
      });
      focusNumericField("value");
      setNumericEdit({
        kind: "arc",
        center: point,
        start: null,
        direction: { x: 1, y: 0 },
        directionValue: getDirectionValue({ x: 1, y: 0 }),
        position: point,
        value: formatMeasurementValue(0),
      });
      return;
    }

    if (!activeArc.start) {
      setActiveArc({
        ...activeArc,
        start: point,
        pointer: point,
      });
      const direction = getNormalizedDirection(activeArc.center, point);
      focusNumericField("value");
      setNumericEdit({
        kind: "arc",
        center: activeArc.center,
        start: point,
        direction,
        directionValue: getDirectionValue(direction),
        position: point,
        value: formatMeasurementValue(getDistance(activeArc.center, point)),
      });
      return;
    }

    const nextArc = {
      ...activeArc,
      pointer: point,
    };
    const nextId = finalizeArc(nextArc);
    if (!nextId) return;

    setSelectedIds([nextId]);
    setEditingId(null);
    setHoverSnap(null);
    startSinglePointCircleTool();
  };

  const handleTextMouseDown = (point: CanvasPoint) => {
    const parsedHeight = parseMeasurementValue(textToolHeight);
    const height = parsedHeight != null && Number.isFinite(parsedHeight) && parsedHeight > 0 ? parsedHeight : 20;
    const text = textToolValue.trim() || DEFAULT_TEXT_TOOL_VALUE;
    const textStyle = getEntityTypeStyle("text");
    const nextId = `text-${crypto.randomUUID()}`;
    const boxWidth = Math.max(DEFAULT_TEXT_BOX_WIDTH, text.length * height * 0.6 + TEXT_BOX_PADDING * 2);
    const boxHeight = Math.max(DEFAULT_TEXT_BOX_HEIGHT, height + TEXT_BOX_PADDING * 2);

    runHistoryAction(() => {
      setTextEntities((current) => [
        ...current,
        {
          id: nextId,
          name: text,
          kind: "text",
          position: clonePoint(point),
          text,
          height,
          boxWidth,
          boxHeight,
          fontFamily: textToolFont,
          justification: textToolJustification,
          layer: TEXT_LAYER,
          entityStyleKind: "text",
          strokeColor: textToolColor || textStyle.strokeColor,
          strokeWidth: textStyle.strokeWidth,
        },
      ]);
    });

    setSelectedIds([nextId]);
    setEditingId(null);
    cancelTextEditing();
    setHoverSnap(null);
  };

  const insertIconAtPoint = (iconId: string, point: CanvasPoint) => {
    const iconDefinition = CANVAS_ICON_LIBRARY.find((icon) => icon.id === iconId) ?? CANVAS_ICON_LIBRARY[0];
    const iconColor = getCurrentIconColor();
    const nextId = `icon-${crypto.randomUUID()}`;
    const iconStyle = getEntityTypeStyle("icon");
    const iconSize = getCurrentIconSize();

    runHistoryAction(() => {
      setIconEntities((current) => [
        ...current,
        {
          id: nextId,
          name: `${iconDefinition?.label ?? "Icon"} ${current.length + 1}`,
          kind: "icon",
          iconId,
          position: clonePoint(point),
          size: iconSize,
          mainColor: iconColor,
          rotation: 0,
          layer: DEFAULT_ICON_LAYER,
          entityStyleKind: "icon",
          strokeColor: iconColor || iconStyle.strokeColor,
          strokeWidth: iconStyle.strokeWidth,
        },
      ]);
    });

    setSelectedIds([nextId]);
    setEditingId(null);
    cancelTextEditing();
    setHoverSnap(null);
  };

  const handleIconPicked = (iconId: string) => {
    setPendingIconId(iconId);
    setIconPreviewPoint(null);
    setToolMode("icon");
    setSelectedIds([]);
    setEditingId(null);
    cancelTextEditing();
  };

  const handleIconMouseDown = (point: CanvasPoint) => {
    if (!pendingIconId) {
      setIsIconPickerOpen(true);
      bringFloatingPanelToFront("iconPicker");
      return;
    }

    const resolved = resolveIconPlacementPoint(point);
    insertIconAtPoint(pendingIconId, resolved.point);
    setPendingIconId(null);
    setIconPreviewPoint(null);
    setHoverSnap(null);
  };

  const handleObstacleCircleMouseDown = (point: CanvasPoint) => {
    const resolved = resolveSnapPoint(point, null, null, null, null, true, activeCircle?.center ?? null);
    setHoverSnap(resolved.osnap);

    if (!activeCircle) {
      setActiveCircle({
        center: resolved.point,
        pointer: resolved.point,
      });
      focusNumericField("value");
      setNumericEdit({
        kind: "circle",
        center: resolved.point,
        direction: { x: 1, y: 0 },
        directionValue: getDirectionValue({ x: 1, y: 0 }),
        position: resolved.point,
        value: formatMeasurementValue(0),
      });
      return;
    }

    if (numericEdit?.kind === "circle") {
      finalizeCircle(activeCircle, "circular-obstacle", {
        isObstacle: true,
        layer: ROOF_LAYER,
        name: getNextObstacleName(polylines),
      });
      return;
    }

    const nextCircle = {
      ...activeCircle,
      pointer: resolved.point,
    };
    const radius = getActiveCircleRadius(nextCircle);
    const direction = getNormalizedDirection(nextCircle.center, resolved.point);

    setActiveCircle(nextCircle);
    focusNumericField("value");
    setNumericEdit({
      kind: "circle",
      center: nextCircle.center,
      direction,
      directionValue: getDirectionValue(direction),
      position: resolved.point,
      value: formatMeasurementValue(radius),
    });
  };

  const handleHoleCircleMouseDown = (point: CanvasPoint) => {
    const host = activeHoleHost ? getShapeById(activeHoleHost.hostId) : null;

    if (!isHoleHostShape(host) && !activeCircle) {
      const hitId = getEntityHitId(point);
      const hitShape = hitId ? getShapeById(hitId) : null;
      if (isHoleHostShape(hitShape)) {
        setActiveHoleHost({ hostId: hitShape.id });
        setSelectedIds([hitShape.id]);
        setEditingId(null);
      }
      return;
    }

    const resolved = resolveSnapPoint(point, null, null, null, null, true, activeCircle?.center ?? null);
    setHoverSnap(resolved.osnap);

    if (!activeCircle) {
      setActiveCircle({
        center: resolved.point,
        pointer: resolved.point,
      });
      focusNumericField("value");
      setNumericEdit({
        kind: "circle",
        center: resolved.point,
        direction: { x: 1, y: 0 },
        directionValue: getDirectionValue({ x: 1, y: 0 }),
        position: resolved.point,
        value: formatMeasurementValue(0),
      });
      return;
    }

    if (numericEdit?.kind === "circle") {
      finalizeCircleHole(activeCircle);
      return;
    }

    const nextCircle = {
      ...activeCircle,
      pointer: resolved.point,
    };
    const radius = getActiveCircleRadius(nextCircle);
    const direction = getNormalizedDirection(nextCircle.center, resolved.point);

    setActiveCircle(nextCircle);
    focusNumericField("value");
    setNumericEdit({
      kind: "circle",
      center: nextCircle.center,
      direction,
      directionValue: getDirectionValue(direction),
      position: resolved.point,
      value: formatMeasurementValue(radius),
    });
  };

  const handleSinglePointCircleMouseDown = (point: CanvasPoint) => {
    const resolved = resolveSnapPoint(point);
    setHoverSnap(resolved.osnap);
    const radius =
      numericEdit?.kind === "single-anchor-point"
        ? (parseMeasurementValue(numericEdit.value) ?? SINGLE_POINT_CIRCLE_DEFAULT_RADIUS)
        : SINGLE_POINT_CIRCLE_DEFAULT_RADIUS;
    const nextId = finalizeCircle(
      buildSinglePointCircleState(resolved.point, Math.max(0, radius)),
      "single-anchor-point",
    );
    if (!nextId) return;

    setSelectedIds([nextId]);
    setEditingId(null);
    setHoverSnap(null);
    startSinglePointCircleTool();
  };

  const handlePointSeriesMouseDown = (point: CanvasPoint, shiftKey: boolean) => {
    const resolved = resolvePointSeriesPoint(point, shiftKey, activePointSeries);
    setHoverSnap(resolved.osnap);

    const currentPoints = activePointSeries ?? [];
    const nextPoint = [resolved.point.x, resolved.point.y];

    if (currentPoints.length === 0) {
      commitPointSeriesAnchor(resolved.point);
      setActivePointSeries(nextPoint);
      focusNumericField("value");
      setNumericEdit({
        kind: "point-series",
        anchor: resolved.point,
        direction: { x: 1, y: 0 },
        directionValue: getDirectionValue({ x: 1, y: 0 }),
        position: resolved.point,
        lineDistanceValue: formatMeasurementValue(0),
        value: formatMeasurementValue(pointSeriesMaxLength),
      });
      return;
    }

    const previousPoint = getPointFromCoordinateArray(currentPoints, currentPoints.length - 2);
    if (!previousPoint) return;
    const segmentPoints = buildPointSeriesPointsFromControlPoints(
      [previousPoint, resolved.point],
      pointSeriesMaxLength,
    ).reduce<CanvasPoint[]>((result, value, index, array) => {
      if (index % 2 !== 0) return result;
      const nextY = array[index + 1];
      if (nextY == null) return result;
      const nextAnchor = { x: value, y: nextY };
      const lastAnchor = result[result.length - 1];
      if (lastAnchor && lastAnchor.x === nextAnchor.x && lastAnchor.y === nextAnchor.y) {
        return result;
      }
      result.push(nextAnchor);
      return result;
    }, []);
    commitPointSeriesAnchors(segmentPoints);
    setActivePointSeries(null);
    setNumericEdit(null);
    setHoverSnap(null);
    setToolMode("select");
    clearOsnapTracking();
  };

  const handleDimensionMouseDown = (point: CanvasPoint) => {
    const activePoints =
      activeDimension?.end == null && activeDimension
        ? [activeDimension.start.x, activeDimension.start.y, activeDimension.start.x, activeDimension.start.y]
        : null;
    const resolved = resolveSnapPoint(point, activePoints);
    setHoverSnap(resolved.osnap);

    if (!activeDimension) {
      setActiveDimension({
        start: resolved.point,
        end: null,
        pointer: resolved.point,
      });
      return;
    }

    if (!activeDimension.end) {
      setActiveDimension({
        ...activeDimension,
        end: resolved.point,
        pointer: resolved.point,
      });
      return;
    }

    const geometry = getDimensionGeometry(activeDimension.start, activeDimension.end, resolved.point);
    if (geometry) {
      const end = activeDimension.end;
      if (!end) {
        setActiveDimension(null);
        return;
      }
      const dimensionStyle = getEntityTypeStyle("dimension");

      runHistoryAction(() => {
        setDimensions((current) => [
          ...current,
          {
            id: `dimension-${crypto.randomUUID()}`,
            start: activeDimension.start,
            end,
            offset: geometry.offset,
            dimensionType: "linear",
            layer: DIMENSIONS_LAYER,
            entityStyleKind: "dimension",
            name: getNextEntityName("dimension", polylines, current),
            strokeColor: dimensionStyle.strokeColor,
            strokeWidth: dimensionStyle.strokeWidth,
          },
        ]);
      });
    }

    setActiveDimension(null);
    clearOsnapTracking();
  };

  const handleDimensionObjectMouseDown = (point: CanvasPoint) => {
    const hitId = getEntityHitId(point);
    const shape = hitId ? getShapeById(hitId) : null;
    if (!shape) {
      setSelectedIds([]);
      setEditingId(null);
      setActiveObjectDimension(null);
      setNumericEdit(null);
      return;
    }

    setSelectedIds([shape.id]);
    setEditingId(null);
    setActiveObjectDimension({
      shapeId: shape.id,
      offset: AUTO_DIMENSION_OFFSET,
    });
    setNumericEdit({
      kind: "dimension-object",
      entityId: shape.id,
      position: getShapeReferenceCenter(shape),
      value: formatMeasurementValue(AUTO_DIMENSION_OFFSET),
    });
    focusNumericField("value");
  };

  const resolveRulerPoint = (point: CanvasPoint, shiftKey: boolean, current: ActiveRuler | null) => {
    const activePoints =
      current?.end == null && current ? [current.start.x, current.start.y, current.start.x, current.start.y] : null;
    const resolved = resolveSnapPoint(point, activePoints);
    const nextPoint =
      shiftKey && current && current.end == null
        ? applyPersistentDirectionLock(current.start, resolved.point, shiftKey)
        : resolved.point;

    return {
      point: nextPoint,
      osnap: resolved.osnap,
    };
  };

  const handleRulerMouseDown = (point: CanvasPoint, shiftKey = false) => {
    const resolved = resolveRulerPoint(point, shiftKey, activeRuler);
    setHoverSnap(resolved.osnap);

    if (!activeRuler || activeRuler.end) {
      setActiveRuler({
        start: resolved.point,
        end: null,
        pointer: resolved.point,
      });
      return;
    }

    const constructionLineStyle = getEntityTypeStyle("construction-line");
    runHistoryAction(() => {
      setConstructionLines((current) => [
        ...current,
        {
          id: `construction-${crypto.randomUUID()}`,
          start: activeRuler.start,
          end: resolved.point,
          strokeColor: constructionLineStyle.strokeColor,
          strokeWidth: constructionLineStyle.strokeWidth,
          layer: DIMENSIONS_LAYER,
          entityStyleKind: "construction-line",
        },
      ]);
    });
    setActiveRuler(null);
    setHoverSnap(null);
    clearOsnapTracking();
  };

  const resolveRectanglePoint = (point: CanvasPoint, shiftKey: boolean, current: ActiveRectangle | null) => {
    const activePoints =
      current == null
        ? null
        : current.edgeEnd == null
          ? [current.start.x, current.start.y, current.start.x, current.start.y]
          : [current.start.x, current.start.y, current.edgeEnd.x, current.edgeEnd.y];
    const resolved = resolveSnapPoint(
      point,
      activePoints,
      null,
      null,
      null,
      true,
      current?.edgeEnd ?? current?.start ?? null,
    );
    let nextPoint = resolved.point;

    if (shiftKey && current && !current.edgeEnd) {
      nextPoint = applyPersistentDirectionLock(current.start, nextPoint, shiftKey);
    }

    if (shiftKey && current?.edgeEnd) {
      nextPoint = applyPersistentDirectionLock(current.edgeEnd, nextPoint, shiftKey);
    }

    return {
      point: nextPoint,
      osnap: resolved.osnap,
    };
  };

  const handleScaleRotateMouseDown = (event: Konva.KonvaEventObject<MouseEvent>) => {
    const pos = getPointerPosition(event);
    if (!pos) return false;

    const resolved = resolveScaleRotatePoint(pos);
    setHoverSnap(resolved.osnap);

    if (selectedIds.length === 0) {
      const hitId = getEntityHitId(pos);
      if (!hitId) {
        setSelectedIds([]);
        setEditingId(null);
        setTransformState(null);
        setNumericEdit(null);
        return true;
      }

      setSelectedIds([hitId]);
      setEditingId(null);
      return true;
    }

    const mode = toolMode === "rotate" ? "rotate" : "scale";

    if (mode === "rotate" && transformState?.mode === "rotate" && !transformState.referencePoint) {
      setTransformState({
        ...transformState,
        referencePoint: resolved.point,
      });
      setNumericEdit({
        kind: "scale-rotate",
        mode,
        basePoint: transformState.basePoint,
        position: resolved.point,
        value: "0",
      });
      return true;
    }

    if (mode === "rotate" && transformState?.mode === "rotate" && transformState.referencePoint) {
      const angle = getSignedAngleBetweenVectors(
        transformState.basePoint,
        transformState.referencePoint,
        resolved.point,
      );
      applyScaleRotateTransform(transformState, angle);
      setTransformState(null);
      setNumericEdit(null);
      setHoverSnap(null);
      setEditingId(null);
      return true;
    }

    setEditingId(null);
    setTransformState({
      mode,
      entityIds: selectedIds,
      basePoint: resolved.point,
    });
    focusNumericField("value");
    setNumericEdit({
      kind: "scale-rotate",
      mode,
      basePoint: resolved.point,
      position: resolved.point,
      value: mode === "scale" ? "1" : "0",
    });
    return true;
  };

  const handleMouseDown = (event: Konva.KonvaEventObject<MouseEvent>) => {
    if (toolMode === "automaticLifeLine") {
      const pos = getPointerPosition(event);
      if (pos) {
        handleAutomaticLifeLineClick(pos);
      }
      return;
    }

    if (toolMode === "singleLifeLine" && lifeLineRoofSelectionRequired && !activePolylineRef.current) {
      const pos = getPointerPosition(event);
      if (pos) {
        selectRoofForLifeLineAtPoint(pos);
      }
      return;
    }

    if (toolMode === "roofHipValley") {
      const pos = getPointerPosition(event);
      if (pos && handleRoofHipValleyClick(pos)) return;
    }

    if (toolMode === "multiGableRoof" && multiGableRoofDraft) {
      const pos = getPointerPosition(event);
      if (pos && handleMultiGableDraftClick(pos)) return;
    }

    if (toolMode === "scale" || toolMode === "rotate") {
      if (handleScaleRotateMouseDown(event)) return;
    }

    if (toolMode === "text") {
      const pos = getPointerPosition(event);
      if (pos) {
        const hitTextEntity = getEntityHitIds(pos)
          .map((id) => textEntities.find((textEntity) => textEntity.id === id) ?? null)
          .find((textEntity): textEntity is TextEntity => Boolean(textEntity));
        if (hitTextEntity) {
          setSelectedIds([hitTextEntity.id]);
          cancelTextEditing();
          return;
        }

        handleTextMouseDown(pos);
      }
      return;
    }

    if (toolMode === "icon") {
      const pos = getPointerPosition(event);
      if (pos) {
        handleIconMouseDown(pos);
      }
      return;
    }

    if (toolMode === "hatch") {
      const pos = getPointerPosition(event);
      if (!pos) return;

      if (hatchToolMode === "pick") {
        if (createHatchFromPickedShape(pos)) {
          setHoverSnap(null);
        }
        return;
      }
    }

    return handleCanvasMouseDownRule(event, {
      openToolGroupMenu,
      setOpenToolGroupMenu,
      setPanState,
      getPointerPosition,
      isBackgroundCropMode,
      backgroundAsset,
      setBackgroundCropRect,
      isBackgroundReferenceMode,
      backgroundReferenceInput,
      backgroundReferenceLine,
      setBackgroundReferenceLine,
      resolveBackgroundReferencePoint,
      setBackgroundAsset,
      getBackgroundLocalPoint,
      getDistance,
      setBackgroundReferenceInput,
      formatMeasurementValue,
      setSelectedIds,
      editingId,
      setEditingId,
      backgroundId: BACKGROUND_ID,
      toolMode,
      setSelectionBox,
      resolveMoveCopyPoint,
      moveState,
      setHoverSnap,
      getEntityHitId,
      selectedIds,
      setMoveState,
      beginHistoryAction,
      buildMoveState,
      polylines,
      dimensions,
      focusNumericField,
      setNumericEdit,
      getDirectionValue,
      applyMoveState,
      finishHistoryAction,
      duplicateEntities,
      cancelHistoryAction,
      activeOffset,
      getOffsetTargetAtPoint,
      getShapeById,
      buildOffsetSourceShape,
      getDefaultOffsetDistance,
      setActiveOffset,
      parseMeasurementValue,
      numericEdit,
      commitOffset,
      activePolylineRef,
      isRoofComponentToolMode,
      finalizeActiveRoofComponentPolyline,
      finalizeActivePolyline,
      resolvePolylinePoint,
      shouldFinishPolylineOnStartPoint,
      closeActivePolyline,
      closeActiveHatchPolyline,
      closeActiveRoof,
      closeActiveMultiGableRoof,
      handlePolylineMouseDown,
      closeActiveObstaclePolyline,
      activeHoleHost,
      isHoleHostShape,
      setActiveHoleHost,
      closeActiveHolePolyline,
      handleHoleCircleMouseDown,
      resolveRectanglePoint,
      activeRectangle,
      handleRectangleMouseDown,
      handleCircleMouseDown,
      handleArcMouseDown,
      resolveArcPoint,
      handleObstacleCircleMouseDown,
      handleSinglePointCircleMouseDown,
      handlePointSeriesMouseDown,
      handleRulerMouseDown,
      resolveRulerPoint,
      handleDimensionMouseDown,
      handleDimensionObjectMouseDown,
      getNormalizedDirection,
    });
  };

  const handleMouseMove = (event: Konva.KonvaEventObject<MouseEvent>) => {
    if (toolMode === "icon") {
      const pos = getPointerPosition(event);
      if (!pos || !pendingIconId) {
        setIconPreviewPoint(null);
        setHoverSnap(null);
        return;
      }

      const resolved = resolveIconPlacementPoint(pos);
      setIconPreviewPoint(resolved.point);
      setHoverSnap(resolved.osnap);
      return;
    }

    if (toolMode === "automaticLifeLine") {
      const pos = getPointerPosition(event);
      setAutomaticLifeLinePreviewPoint(
        pos && automaticLifeLineSourceIds && automaticLifeLineSourceIds.length > 0 ? pos : null,
      );
      setHoverSnap(null);
      return;
    }

    if (toolMode === "scale" || toolMode === "rotate") {
      const pos = getPointerPosition(event);
      if (pos) {
        const resolved = resolveScaleRotatePoint(pos);
        setHoverSnap(resolved.osnap);
        if (toolMode === "rotate" && transformState?.mode === "rotate" && transformState.referencePoint) {
          const angle = getSignedAngleBetweenVectors(
            transformState.basePoint,
            transformState.referencePoint,
            resolved.point,
          );
          setNumericEdit((current) =>
            current?.kind === "scale-rotate"
              ? {
                  ...current,
                  position: resolved.point,
                  value: formatLocalizedNumber(angle, 2),
                }
              : current,
          );
        }
      }
      return;
    }

    return handleCanvasMouseMoveRule(event, {
      panState,
      setViewport,
      setPanState,
      getPointerPosition,
      isBackgroundCropMode,
      setBackgroundCropRect,
      isBackgroundReferenceMode,
      backgroundReferenceLine,
      backgroundReferenceInput,
      setBackgroundReferenceLine,
      resolveBackgroundReferencePoint,
      draggedGrip,
      toolMode,
      setSelectionBox,
      setHoverSnap,
      resolveMoveCopyPoint,
      moveState,
      applyMoveState,
      setNumericEdit,
      getNormalizedDirection,
      getDirectionValue,
      formatMeasurementValue,
      getDistance,
      setActiveOffset,
      activeRectangle,
      resolveRectanglePoint,
      setActiveRectangle,
      numericEdit,
      activeArc,
      getActiveArcSnapPoints,
      setActiveArc,
      setActiveCircle,
      resolveSnapPoint,
      resolveArcPoint,
      resolvePolylinePoint,
      resolvePointSeriesPoint,
      parseMeasurementValue,
      singlePointCircleDefaultRadius: SINGLE_POINT_CIRCLE_DEFAULT_RADIUS,
      buildSinglePointCircleState,
      pointSeriesMaxLength,
      activePointSeries,
      activeRuler,
      resolveRulerPoint,
      setActiveRuler,
      activeDimension,
      setActiveDimension,
      isRoofComponentToolMode,
      setActivePolyline,
    });
  };

  const handleMouseLeave = () => {
    setAutomaticLifeLinePreviewPoint(null);
    if (toolMode === "icon") {
      setIconPreviewPoint(null);
    }
    handleCanvasMouseLeaveRule(backgroundReferenceInput, setHoverSnap);
  };

  const handleMouseUp = (event: Konva.KonvaEventObject<MouseEvent>) =>
    handleCanvasMouseUpRule(event, {
      setPanState,
      isBackgroundCropMode,
      backgroundCropRect,
      setBackgroundCropRect,
      applyBackgroundCrop,
      setIsBackgroundCropMode,
      isBackgroundReferenceMode,
      activeOffset,
      toolMode,
      numericEdit,
      draggedGrip,
      draggedDimensionId,
      getPointerPosition,
      selectionBox,
      setSelectionBox,
      handleSelectionClick,
      getDistance,
      selectionDragThreshold: SELECTION_DRAG_THRESHOLD,
      viewport,
      getSelectionBounds,
      polylines,
      isShapeInteractive,
      boundsIntersect,
      getShapeBounds,
      dimensions,
      isDimensionInteractive,
      getDimensionBounds,
      textEntities,
      isTextEntityInteractive,
      getTextEntityBounds,
      iconEntities,
      isIconEntityInteractive,
      getIconEntityBounds,
      setSelectedIds,
      setEditingId,
      finishHistoryAction,
      setMoveState,
      setNumericEdit,
      setHoverSnap,
      focusNumericField,
    });

  const handleDoubleClick = (event: Konva.KonvaEventObject<MouseEvent>) =>
    handleCanvasDoubleClickRule(event, {
      isBackgroundReferenceMode,
      toolMode,
      getPointerPosition,
      handleSelectionDoubleClick,
    });

  const handleWheel = (event: Konva.KonvaEventObject<WheelEvent>) =>
    handleCanvasWheelRule(event, {
      zoomFactor: ZOOM_FACTOR,
      zoomAtScreenPoint,
    });

  const clearCanvas = () => {
    runHistoryAction(() => {
      setPolylines([]);
      setDimensions([]);
      setConstructionLines([]);
      setActivePolyline(null);
      setActiveRectangle(null);
      setActiveCircle(null);
      setActiveArc(null);
      setActiveDimension(null);
      setActiveObjectDimension(null);
      setHoverSnap(null);
      setSelectedIds([]);
      setEditingId(null);
      setDraggedGrip(null);
      setDraggedDimensionId(null);
      setMoveState(null);
      setActiveOffset(null);
      setNumericEdit(null);
      setGripContextMenu(null);
      setDirectionLockAxis(null);
      setIsBackgroundReferenceMode(false);
      setIsBackgroundCropMode(false);
      setBackgroundCropRect(null);
      setBackgroundReferenceLine(null);
      setBackgroundReferenceInput(null);
    });
  };

  const getGripDragHandlers = (
    polylineId: string,
    vertexIndex: number,
    linkedVertexIndices?: number[],
    holeIndex?: number,
    holeGripKind?: HoleGripKind,
  ) =>
    createGripDragHandlersRule({
      polylineId,
      vertexIndex,
      linkedVertexIndices,
      holeIndex,
      holeGripKind,
      findShape: (id) => polylines.find((entry) => entry.id === id),
      getGripAnchorPoint,
      getGripExcludedSnapSegments,
      getGripCurrentPoint,
      resolveGripPoint,
      isHoleGripUpdateValid,
      applyShapeGripUpdate,
      updateGripNumericEditState,
      beginHistoryAction,
      cancelHistoryAction,
      finishHistoryAction,
      focusNumericField,
      getCurrentPointerWorldPosition,
      draggedGrip,
      setDraggedGrip,
      setHoverSnap,
      setInvalidGripFeedback,
    });

  const getGripClickHandler = (
    polylineId: string,
    vertexIndex: number,
    holeIndex?: number,
    holeGripKind?: HoleGripKind,
  ) =>
    createGripClickHandlerRule({
      polylineId,
      vertexIndex,
      holeIndex,
      holeGripKind,
      getShapeById,
      getPolylineVertices,
    });

  const canRemoveGripVertex = (polylineId: string, holeIndex?: number, linkedVertexIndices?: number[]) => {
    const shape = getShapeById(polylineId);
    if (!shape || shape.kind === "circle" || shape.kind === "arc") return false;
    if (shape.kind === "rectangle" && holeIndex == null) return false;
    if (linkedVertexIndices && linkedVertexIndices.length > 0) return false;

    if (holeIndex != null) {
      const hole = shape.holes?.[holeIndex];
      return Boolean(hole && getPolylineVertices(hole).length > 3);
    }

    if ((shape.lifeLineVertices?.length ?? 0) > 0) {
      const designPointCount =
        shape.lifeLineDesignPoints?.length ??
        getLifeLineGripPoints(shape, getShapeGripPoints(shape)).filter((grip) => grip.gripKind === "vertex").length;
      const minimumLifeLineDesignPointCount = shape.closed ? 3 : 2;
      return designPointCount > minimumLifeLineDesignPointCount;
    }

    const minimumVertexCount = shape.closed ? 3 : 2;
    return getRenderableVertexCountForShape(shape) > minimumVertexCount;
  };

  const canInsertGripVertex = (polylineId: string, vertexIndex: number, holeIndex?: number) => {
    const shape = getShapeById(polylineId);
    if (!shape || shape.kind === "circle" || shape.kind === "arc") return false;
    if (shape.kind === "rectangle" && holeIndex == null) return false;

    const vertexPosition = Math.floor(vertexIndex / 2);

    if (holeIndex != null) {
      const hole = shape.holes?.[holeIndex];
      return Boolean(hole && vertexPosition >= 0 && vertexPosition < getPolylineVertices(hole).length);
    }

    if ((shape.lifeLineVertices?.length ?? 0) > 0) {
      const designPointCount =
        shape.lifeLineDesignPoints?.length ??
        getLifeLineGripPoints(shape, getShapeGripPoints(shape)).filter((grip) => grip.gripKind === "vertex").length;
      return vertexPosition >= 0 && vertexPosition < designPointCount;
    }

    return vertexPosition >= 0 && vertexPosition < getRenderableVertexCountForShape(shape);
  };

  const canOpenOrSplitGripVertex = (
    polylineId: string,
    vertexIndex: number,
    holeIndex?: number,
    linkedVertexIndices?: number[],
  ) => {
    if (holeIndex != null) return false;
    if (linkedVertexIndices && linkedVertexIndices.length > 0) return false;

    const shape = getShapeById(polylineId);
    if (!shape || shape.kind !== "polyline" || (shape.holes?.length ?? 0) > 0 || (shape.circleHoles?.length ?? 0) > 0) {
      return false;
    }

    if ((shape.lifeLineVertices?.length ?? 0) > 0) {
      const designPointCount =
        shape.lifeLineDesignPoints?.length ??
        getLifeLineGripPoints(shape, getShapeGripPoints(shape)).filter((grip) => grip.gripKind === "vertex").length;
      const vertexPosition = Math.floor(vertexIndex / 2);
      if (shape.closed) {
        return designPointCount >= 3 && vertexPosition >= 0 && vertexPosition < designPointCount;
      }

      return vertexPosition > 0 && vertexPosition < designPointCount - 1;
    }

    const vertices = getPolylineVertices(shape.points);
    const vertexPosition = Math.floor(vertexIndex / 2);
    if (shape.closed) {
      return vertices.length >= 3 && vertexPosition >= 0 && vertexPosition < vertices.length;
    }

    return vertexPosition > 0 && vertexPosition < vertices.length - 1;
  };

  const handleGripContextMenu =
    (
      polylineId: string,
      vertexIndex: number,
      linkedVertexIndices?: number[],
      holeIndex?: number,
      holeGripKind?: HoleGripKind,
    ) =>
    (event: Konva.KonvaEventObject<PointerEvent | MouseEvent>) => {
      event.evt.preventDefault();
      event.cancelBubble = true;

      if (holeGripKind === "circle-center" || holeGripKind === "circle-radius") {
        setGripContextMenu(null);
        return;
      }

      setGripContextMenu({
        entityId: polylineId,
        vertexIndex,
        linkedVertexIndices,
        holeIndex,
        holeGripKind,
        x: event.evt.clientX,
        y: event.evt.clientY,
      });
    };

  const handleGripPointerClick =
    (shape: Shape, grip: ShapeGripDescriptor) => (event: Konva.KonvaEventObject<MouseEvent>) => {
      if (grip.gripKind === "insert") {
        event.cancelBubble = true;
        runHistoryAction(() => {
          insertPolylineVertex(shape.id, grip.segmentStartVertexIndex ?? grip.vertexIndex, grip.point, grip.holeIndex);
        });
        setGripContextMenu(null);
        return;
      }

      getGripClickHandler(shape.id, grip.vertexIndex, grip.holeIndex, grip.holeGripKind)(event);
    };

  const handleRemoveGripVertex = () => {
    if (!gripContextMenu) return;

    runHistoryAction(() => {
      removePolylineVertex(
        gripContextMenu.entityId,
        gripContextMenu.vertexIndex,
        gripContextMenu.holeIndex,
        gripContextMenu.linkedVertexIndices,
      );
    });
    setGripContextMenu(null);
  };

  const handleInsertGripVertex = () => {
    if (!gripContextMenu) return;

    runHistoryAction(() => {
      insertPolylineVertexNearVertex(gripContextMenu.entityId, gripContextMenu.vertexIndex, gripContextMenu.holeIndex);
    });
    setEditingId(gripContextMenu.entityId);
    setSelectedIds((current) => (current.includes(gripContextMenu.entityId) ? current : [gripContextMenu.entityId]));
    setGripContextMenu(null);
  };

  const handleOpenOrSplitGripVertex = () => {
    if (!gripContextMenu) return;

    runHistoryAction(() => {
      openOrSplitPolylineAtVertex(gripContextMenu.entityId, gripContextMenu.vertexIndex);
    });
    setEditingId(gripContextMenu.entityId);
    setSelectedIds((current) => (current.includes(gripContextMenu.entityId) ? current : [gripContextMenu.entityId]));
    setGripContextMenu(null);
  };

  const hasSelectedEntities = selectedEntities.length > 0;
  const selectedEntityTypeLabel = hasSelectedEntities
    ? firstSelectedEntity &&
      selectedEntities.every(
        (entity) => entity.kind === firstSelectedEntity.kind && entity.isObstacle === firstSelectedEntity.isObstacle,
      )
      ? firstSelectedEntity.isObstacle
        ? (getObstacleEntityLabel(singleSelectedShape) ?? "Obstacle") + " (selected)"
        : getEntityTypeLabel(firstSelectedEntity.kind)
      : "Mixed selection"
    : "No selection";
  const selectedEntitySubtitle = hasSelectedEntities
    ? selectedEntities.length === 1
      ? "1 selected object"
      : `${selectedEntities.length} selected objects`
    : "Select an object on the canvas";
  const printableBoundsList = [
    ...polylines.map((shape) => getShapeBoundsRule(shape)),
    ...dimensions.map((dimension) =>
      getDimensionBoundsRule(dimension, (start, end, offset) =>
        getDimensionLayoutRule(start, end, offset, dimensionTextGap),
      ),
    ),
    ...textEntities.map(getTextEntityBounds),
    ...iconEntities.map(getIconEntityBounds),
  ];
  let printableBounds: { minX: number; minY: number; maxX: number; maxY: number } | null = null;
  for (const current of printableBoundsList) {
    printableBounds =
      printableBounds == null
        ? { ...current }
        : {
            minX: Math.min(printableBounds.minX, current.minX),
            minY: Math.min(printableBounds.minY, current.minY),
            maxX: Math.max(printableBounds.maxX, current.maxX),
            maxY: Math.max(printableBounds.maxY, current.maxY),
          };
  }
  const printPaperSize = PRINT_PAPER_SIZES_MM[printPaperFormat];
  const printablePaperWidth = Math.max(1, printPaperSize.width - PRINT_LAYOUT_MARGIN_MM * 2);
  const printablePaperHeight = Math.max(
    1,
    printPaperSize.height - PRINT_LAYOUT_MARGIN_MM * 2 - PRINT_TITLE_BLOCK_RESERVED_HEIGHT_MM,
  );
  const printRawScale =
    printableBounds != null
      ? Math.max(
          (printableBounds.maxX - printableBounds.minX) / printablePaperWidth,
          (printableBounds.maxY - printableBounds.minY) / printablePaperHeight,
        )
      : 1;
  const printScaleDenominator =
    PRINT_SCALE_STEPS.find((step) => step >= Math.max(1, printRawScale)) ??
    Math.ceil(Math.max(1, printRawScale) / 1000) * 1000;
  const computedPrintScaleLabel = printableBounds ? `Scala 1:${printScaleDenominator}` : "Scala auto";
  const getPrintOutputColor = (color: string, mode: PrintColorMode) => {
    if (mode === "canvas") return color;
    if (mode === "monochrome") return "#111827";

    const normalized = color.toLowerCase();
    if (normalized === "#ffffff" || normalized === "white") return "#d1d5db";
    if (normalized === "#ffff00" || normalized === "yellow") return "#a16207";
    if (normalized === "#00ff00" || normalized === "lime") return "#15803d";
    if (normalized === "#00ffff" || normalized === "cyan") return "#0e7490";
    if (normalized === "#ff00ff" || normalized === "magenta") return "#a21caf";
    return normalized;
  };
  const printColorPreview: PrintColorPreview[] = Array.from(
    new Map(
      [...polylines, ...dimensions, ...textEntities, ...iconEntities]
        .map((entity) => entity.strokeColor)
        .filter(Boolean)
        .slice(0, 5)
        .map((color, index) => [
          color.toLowerCase(),
          {
            source: color,
            output: getPrintOutputColor(color, printColorMode),
            label: `Colore ${index + 1}`,
          },
        ]),
    ).values(),
  );
  const selectedPrintableEntityIds = new Set(
    [...polylines, ...dimensions, ...textEntities, ...iconEntities].map((entity) => entity.id),
  );
  const savedTitleBlockEntityCount = savedTitleBlockEntityIds.filter((id) => selectedPrintableEntityIds.has(id)).length;

  const handleSaveTitleBlockSelection = () => {
    const printableSelection = selectedIds.filter((id) => selectedPrintableEntityIds.has(id));
    if (printableSelection.length === 0) {
      setPrintExportStatus("Select all the objects to save in the title block.");
      return;
    }

    setSavedTitleBlockEntityIds(printableSelection);
    setPrintTitleBlockMode("saved");
    setPrintExportStatus(`Cartiglio salvato con ${printableSelection.length} entita.`);
  };

  const handleExportPdf = async () => {
    const stage = stageRef.current;
    if (!stage) {
      setPrintExportStatus("Canvas non disponibile per export PDF.");
      return;
    }

    try {
      const pdf = await PDFDocument.create();
      const pageWidth = (printPaperSize.width * 72) / 25.4;
      const pageHeight = (printPaperSize.height * 72) / 25.4;
      const page = pdf.addPage([pageWidth, pageHeight]);
      const margin = (PRINT_LAYOUT_MARGIN_MM * 72) / 25.4;
      const titleBlockHeight = (PRINT_TITLE_BLOCK_RESERVED_HEIGHT_MM * 72) / 25.4;
      const imageDataUrl = stage.toDataURL({ pixelRatio: 2, mimeType: "image/png" });
      const image = await pdf.embedPng(imageDataUrl);
      const imageMaxWidth = pageWidth - margin * 2;
      const imageMaxHeight = pageHeight - margin * 2 - titleBlockHeight;
      const imageScale = Math.min(imageMaxWidth / image.width, imageMaxHeight / image.height);
      const imageWidth = image.width * imageScale;
      const imageHeight = image.height * imageScale;
      const font = await pdf.embedFont(StandardFonts.Helvetica);

      page.drawImage(image, {
        x: margin + (imageMaxWidth - imageWidth) / 2,
        y: margin + titleBlockHeight + (imageMaxHeight - imageHeight) / 2,
        width: imageWidth,
        height: imageHeight,
      });
      page.drawRectangle({
        x: margin,
        y: margin,
        width: pageWidth - margin * 2,
        height: titleBlockHeight,
        borderColor: rgb(0.12, 0.16, 0.22),
        borderWidth: 0.8,
      });
      page.drawText(`${printPaperFormat} - ${computedPrintScaleLabel}`, {
        x: margin + 12,
        y: margin + titleBlockHeight - 24,
        size: 10,
        font,
        color: rgb(0.12, 0.16, 0.22),
      });
      page.drawText(
        printTitleBlockMode === "saved"
          ? `Cartiglio salvato: ${savedTitleBlockEntityCount} entita`
          : "Cartiglio standard",
        {
          x: margin + 12,
          y: margin + 16,
          size: 8,
          font,
          color: rgb(0.29, 0.24, 0.15),
        },
      );

      const bytes = await pdf.save();
      const blob = new Blob([bytes as unknown as BlobPart], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `patrol-${printPaperFormat.toLowerCase()}-${Date.now()}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
      setPrintExportStatus(`PDF ${printPaperFormat} esportato con ${computedPrintScaleLabel}.`);
    } catch (error) {
      setPrintExportStatus(error instanceof Error ? error.message : "Export PDF non riuscito.");
    }
  };
  const isLifeLineRoofPickMode = toolMode === "singleLifeLine" && lifeLineRoofSelectionRequired && !activePolyline;
  const isObjectSelectionCursorMode =
    isLifeLineRoofPickMode ||
    toolMode === "automaticLifeLine" ||
    toolMode === "select" ||
    ((toolMode === "move" || toolMode === "copy" || toolMode === "scale" || toolMode === "rotate") &&
      selectedIds.length === 0 &&
      !moveState &&
      !transformState) ||
    (toolMode === "offset" && !activeOffset) ||
    (toolMode === "dimensionObject" && !activeObjectDimension) ||
    ((toolMode === "holePolyline" || toolMode === "holeCircle") && !activeHoleHost && !activePolyline && !activeCircle);
  const isCrosshairCursorMode =
    toolMode === "polyline" ||
    (toolMode === "singleLifeLine" && !isLifeLineRoofPickMode) ||
    toolMode === "buildPolyline" ||
    (toolMode === "hatch" && hatchToolMode === "pick") ||
    (toolMode === "hatch" && hatchToolMode === "build") ||
    isRoofComponentToolMode(toolMode) ||
    toolMode === "roof" ||
    toolMode === "multiGableRoof" ||
    toolMode === "gableRoof" ||
    toolMode === "curvedRoof" ||
    toolMode === "singlePitchedRoof" ||
    toolMode === "multiShedRoof" ||
    toolMode === "roofHipValley" ||
    toolMode === "obstaclePolyline" ||
    toolMode === "holePolyline" ||
    toolMode === "move" ||
    toolMode === "copy" ||
    toolMode === "scale" ||
    toolMode === "rotate" ||
    toolMode === "dimensionObject" ||
    toolMode === "rectangle" ||
    toolMode === "holeCircle" ||
    toolMode === "obstacleCircle" ||
    toolMode === "circle" ||
    toolMode === "arc" ||
    toolMode === "pointSeries" ||
    toolMode === "ruler" ||
    toolMode === "text" ||
    toolMode === "icon" ||
    toolMode === "dimension" ||
    toolMode === "offset";
  const viewportCursor = panState
    ? "grabbing"
    : isGripModeActive || draggedSegmentStretch
      ? "crosshair"
      : isObjectSelectionCursorMode
        ? OBJECT_SELECTION_CURSOR
        : isCrosshairCursorMode
          ? "crosshair"
          : undefined;

  useEffect(() => {
    const container = stageRef.current?.container();
    if (!container) return;

    const cursor = viewportCursor ?? "";
    container.style.cursor = cursor;
    container.querySelectorAll<HTMLElement>(".konvajs-content, canvas").forEach((element) => {
      element.style.cursor = cursor;
    });
  }, [viewportCursor]);

  const canUndo = undoStack.length > 0;
  const canRedo = redoStack.length > 0;
  const historyStatusLabel = `U ${undoStack.length}/${MAX_UNDO_HISTORY} · R ${redoStack.length}/${MAX_REDO_HISTORY}`;
  const numericEditLabel =
    numericEdit?.kind === "polyline"
      ? "Length"
      : numericEdit?.kind === "rectangle-base"
        ? toolMode === "curvedRoof"
          ? "RAKE / R"
          : toolMode === "gableRoof" || toolMode === "singlePitchedRoof" || toolMode === "multiShedRoof"
            ? "EAVE / Gronda"
            : "Base"
        : numericEdit?.kind === "rectangle-height"
          ? toolMode === "curvedRoof"
            ? "EAVE / Gronda"
            : toolMode === "gableRoof" || toolMode === "singlePitchedRoof"
              ? "RAKE / R"
              : "Roof Height (Side) "
          : numericEdit?.kind === "multi-shed-count"
            ? "Shed Count"
            : numericEdit?.kind === "circle"
              ? "Ray"
              : numericEdit?.kind === "arc"
                ? activeArc?.start == null
                  ? "Ray"
                  : "End Point"
                : numericEdit?.kind === "single-anchor-point"
                  ? "Ray"
                  : numericEdit?.kind === "point-series"
                    ? "Single Anchor Distance"
                    : numericEdit?.kind === "dimension-object"
                      ? "Dimension Distance"
                      : numericEdit?.kind === "offset"
                        ? "Offset"
                        : numericEdit?.kind === "automatic-lifeline-offset"
                          ? "Offset"
                          : numericEdit?.kind === "move"
                            ? "Length"
                            : numericEdit?.kind === "scale-rotate"
                              ? numericEdit.mode === "scale"
                                ? "Scala"
                                : "Angolo"
                              : numericEdit?.kind === "segment-stretch"
                                ? "Length"
                                : numericEdit?.kind === "grip"
                                  ? numericEdit.shapeKind === "circle" || numericEdit.holeGripKind === "circle-radius"
                                    ? "Raggio"
                                    : "Length"
                                  : numericEdit?.kind === "tracking"
                                    ? "OSNAP Offset"
                                    : null;
  const directionalNumericEdit = numericEditSupportsDirection(numericEdit) ? numericEdit : null;
  const showDirectionInput = directionalNumericEdit != null;
  const activeRulerEndPoint = activeRuler == null ? null : (activeRuler.end ?? activeRuler.pointer);
  const activeRulerLength =
    activeRuler && activeRulerEndPoint ? getDistance(activeRuler.start, activeRulerEndPoint) : null;
  const hasAutomaticLifeLineSource = (automaticLifeLineSourceIds?.length ?? 0) > 0;
  const statusBarTitle =
    numericEdit?.kind === "automatic-lifeline-offset"
      ? hasAutomaticLifeLineSource
        ? "Automatic Life Line"
        : "Select Roof"
      : lifeLineRoofSelectionWarning
        ? toolMode === "automaticLifeLine"
          ? "Automatic Life Line"
          : "Select Roof"
        : backgroundReferenceInput
          ? "Background Reference"
          : numericEditLabel
            ? "Active Input"
            : toolMode === "hatch"
              ? "Hatch"
              : "Status Bar";
  const statusBarHint =
    numericEdit?.kind === "automatic-lifeline-offset"
      ? hasAutomaticLifeLineSource
        ? "Set the offset value, move the pointer to preview it, then click the side to create the lifeline."
        : "Select a flat roof first. The default automatic lifeline offset is 2 m."
      : lifeLineRoofSelectionWarning
        ? lifeLineRoofSelectionWarning
        : backgroundReferenceInput
          ? "Enter the real measurement and press Enter or Apply."
          : numericEditLabel
            ? toolMode === "copy" && numericEdit?.kind === "move"
              ? moveState?.vectorLocked
                ? "Confirmed Line - Press Enter to create 3 total copies."
                : "Define the vector; after the second click you can type x3 to create a sequence."
              : showDirectionInput
                ? "Use Tab to switch between direction and value, then Press Enter to confirm."
                : "Modify the value and press Enter to confirm."
            : toolMode === "dimensionObject" && activeObjectDimension
              ? "Select the object, set the dimension distance and press Enter to confirm."
              : toolMode === "pointSeries"
                ? activePointSeries
                  ? "Direction, line and spacing are editable; use Shift to lock the angle."
                  : "Click the first point and then add Single Anchor in series."
                : toolMode === "singleLifeLine"
                  ? activePolyline
                    ? "Continue the Single Life Line or press Enter to confirm."
                    : isLifeLineRoofPickMode
                      ? "Click the specific roof for this lifeline."
                      : "Click the first point of the Single Life Line."
                  : toolMode === "automaticLifeLine"
                    ? hasAutomaticLifeLineSource
                      ? "Set the offset value, move the pointer to preview it, then click the side to create the lifeline."
                      : "Select a roof or roof part to create an automatic lifeline."
                    : toolMode === "hatch"
                      ? hatchToolMode === "pick"
                        ? "Choose pattern, color and scale, then click inside a closed polygon to create the hatch."
                        : activePolyline
                          ? "Continue the hatch boundary or click the initial point to close it."
                          : "Choose pattern, color and scale, then click the first point of the hatch boundary."
                      : STATUS_BAR_PLACEHOLDER;
  const effectiveStatusBarTitle =
    toolMode === "ruler" && !backgroundReferenceInput && !numericEditLabel
      ? "Ruler"
      : isRoofComponentToolMode(toolMode) && !backgroundReferenceInput && !numericEditLabel
        ? getRoofComponentToolLabel(toolMode)
        : toolMode === "rectangle" && !backgroundReferenceInput && !numericEditLabel
          ? "Flat Roof"
          : toolMode === "gableRoof" && !backgroundReferenceInput && !numericEditLabel
            ? "Single Gable Roof"
            : toolMode === "curvedRoof" && !backgroundReferenceInput && !numericEditLabel
              ? "Curved Roof"
              : toolMode === "singlePitchedRoof" && !backgroundReferenceInput && !numericEditLabel
                ? "Single Pitched Roof"
                : toolMode === "multiShedRoof" && !backgroundReferenceInput && !numericEditLabel
                  ? "Multi Shed Roof"
                  : toolMode === "multiGableRoof" && !backgroundReferenceInput && !numericEditLabel
                    ? multiGableRoofDraft
                      ? `Multi Gable Roof - ${
                          multiGableRoofDraft.stage === "mainRakes"
                            ? "MR"
                            : multiGableRoofDraft.stage === "secondaryRakes"
                              ? "SR"
                              : "H"
                        }`
                      : "Multi Gable Roof"
                    : toolMode === "roofHipValley" && !backgroundReferenceInput && !numericEditLabel
                      ? "Automatic Hip/Valley"
                      : toolMode === "automaticLifeLine" && !backgroundReferenceInput && !numericEditLabel
                        ? "Automatic Life Line"
                        : toolMode === "hatch" && !backgroundReferenceInput && !numericEditLabel
                          ? "Hatch"
                          : toolMode === "text" && !backgroundReferenceInput && !numericEditLabel
                            ? "Text"
                            : toolMode === "icon" && !backgroundReferenceInput && !numericEditLabel
                              ? "Icons"
                              : toolMode === "obstacleCircle" && !backgroundReferenceInput && !numericEditLabel
                                ? "Circular Obstacle"
                                : toolMode === "obstaclePolyline" && !backgroundReferenceInput && !numericEditLabel
                                  ? "Rectangular Obstacle"
                                  : toolMode === "dimensionObject" && !backgroundReferenceInput && !numericEditLabel
                                    ? "Dimension Object"
                                    : toolMode === "holeCircle" && !backgroundReferenceInput && !numericEditLabel
                                      ? "Circular Hole"
                                      : toolMode === "holePolyline" && !backgroundReferenceInput && !numericEditLabel
                                        ? "Rectangular Hole"
                                        : toolMode === "buildPolyline" && !backgroundReferenceInput && !numericEditLabel
                                          ? "Build Polyline"
                                          : toolMode === "singleLifeLine" &&
                                              !backgroundReferenceInput &&
                                              !numericEditLabel
                                            ? isLifeLineRoofPickMode
                                              ? "Select Roof"
                                              : "Single Life Line"
                                            : toolMode === "arc" && !backgroundReferenceInput && !numericEditLabel
                                              ? "Arc"
                                              : statusBarTitle;
  const effectiveStatusBarHint =
    toolMode === "ruler" && !backgroundReferenceInput && !numericEditLabel
      ? activeRuler == null
        ? "Select the first point with OSNAP enabled."
        : activeRuler.end == null
          ? `Select the second point. Length live: ${formatMeasurement(activeRulerLength ?? 0)}.`
          : `Length: ${formatMeasurement(activeRulerLength ?? 0)}. Click to start a new measurement.`
      : isRoofComponentToolMode(toolMode) && !backgroundReferenceInput && !numericEditLabel
        ? activePolyline == null
          ? `Select the first point: ${getRoofComponentToolLabel(toolMode)}.`
          : "Continue the polyline or press Esc to confirm."
        : toolMode === "singleLifeLine" && !backgroundReferenceInput && !numericEditLabel
          ? isLifeLineRoofPickMode
            ? "Click the specific roof for this lifeline."
            : activePolyline == null
              ? "Select the first point of the Single Life Line."
              : "Continue the Single Life Line or press Enter to confirm."
          : toolMode === "automaticLifeLine" && !backgroundReferenceInput && !numericEditLabel
            ? hasAutomaticLifeLineSource
              ? "Set the offset value, move the pointer to preview it, then click the side to create the lifeline."
              : "Select a roof or roof part to create an automatic lifeline."
            : toolMode === "text" && !backgroundReferenceInput && !numericEditLabel
              ? "Set text properties in the status bar, then click to place the text."
              : toolMode === "icon" && !backgroundReferenceInput && !numericEditLabel
                ? pendingIconId
                  ? "Move the preview with OSNAP, adjust color or scale, then click the canvas to insert."
                  : "Choose color and scale, then click an icon in the picker. Scale 1 inserts at 10x."
                : toolMode === "hatch" && !backgroundReferenceInput && !numericEditLabel
                  ? hatchToolMode === "pick"
                    ? "Set hatch options in the status bar, then click inside a closed polygon."
                    : activePolyline == null
                      ? "Set hatch options in the status bar, then click the first point of the hatch boundary."
                      : "Continue the hatch boundary or click the initial point to close it."
                  : toolMode === "rectangle" && !backgroundReferenceInput && !numericEditLabel
                    ? activeRectangle == null
                      ? "Select the first point of the base of the Flat Roof. Shift and OSNAP are enabled."
                      : activeRectangle.edgeEnd == null
                        ? "Select the second point of the base. Shift and OSNAP have priority over the typed value."
                        : "Select the lateral dimension to create the rectangle."
                    : (toolMode === "gableRoof" || toolMode === "curvedRoof") &&
                        !backgroundReferenceInput &&
                        !numericEditLabel
                      ? activeRectangle == null
                        ? toolMode === "curvedRoof"
                          ? "Select the first point of the RAKE/R. Shift and OSNAP are enabled."
                          : "Select the first point of the Ridge/Eave. Shift and OSNAP are enabled."
                        : activeRectangle.edgeEnd == null
                          ? toolMode === "curvedRoof"
                            ? "Select the second point of the RAKE/R. Shift and OSNAP have priority over the typed value."
                            : "Select the second point of the Ridge/Eave. Shift and OSNAP have priority over the typed value."
                          : toolMode === "curvedRoof"
                            ? "Select the lateral dimension of the Eave/Gutter; the ridge C will be created from RAKE to RAKE dashed."
                            : "Select the point of the Timpano/RAKE. OSNAP and Shift have priority over the typed value."
                      : toolMode === "singlePitchedRoof" && !backgroundReferenceInput && !numericEditLabel
                        ? activeRectangle == null
                          ? "Select the first point of the Eave line."
                          : activeRectangle.edgeEnd == null
                            ? "Select the second point of the Eave line."
                            : "Select the point of the lateral line R; the opposite C and the other R will be created automatically."
                        : toolMode === "multiShedRoof" && !backgroundReferenceInput && !numericEditLabel
                          ? activeRectangle == null
                            ? "Select the first point of the eave line."
                            : activeRectangle.edgeEnd == null
                              ? "Select the second point of the eave line."
                              : "Select the lateral dimension; then enter the number of sheds in the status bar."
                          : toolMode === "multiGableRoof" && !backgroundReferenceInput && !numericEditLabel
                            ? multiGableRoofDraft
                              ? multiGableRoofDraft.stage === "mainRakes"
                                ? `Select 2 main rakes on the perimeter. Selected: ${multiGableRoofDraft.mainRakes.length}/2.`
                                : multiGableRoofDraft.stage === "secondaryRakes"
                                  ? "Select/toggle secondary rakes on the perimeter, then use the H button."
                                  : multiGableRoofDraft.stage === "hipValleyEaveA"
                                    ? "Manually enter Hip or Valley. Click 1: select eave A."
                                    : multiGableRoofDraft.stage === "hipValleyEaveB"
                                      ? "Click 2: select eave B."
                                      : multiGableRoofDraft.stage === "hipValleyRidgeA"
                                        ? "Click 3: select ridge A."
                                        : "Click 4: select ridge B. Use End when you are done."
                              : activePolyline == null
                                ? "Select the first point of the perimeter Multi Gable Roof. Shift and OSNAP are enabled."
                                : "Continue the perimeter or click the initial point to start making roof choices."
                            : toolMode === "roofHipValley" && !backgroundReferenceInput && !numericEditLabel
                              ? roofHipValleyDraft?.stage === "eaveB"
                                ? "Click 2: select the second eave."
                                : roofHipValleyDraft?.stage === "ridgeA"
                                  ? "Click 3: select the first ridge."
                                  : roofHipValleyDraft?.stage === "ridgeB"
                                    ? "Click 4: select the second ridge. The HIP/Valley line will be created automatically."
                                    : "Click 1: select the first eave."
                              : toolMode === "obstacleCircle" && !backgroundReferenceInput && !numericEditLabel
                                ? activeCircle == null
                                  ? "Select the center of the circular obstacle with OSNAP enabled."
                                  : "Set the radius and confirm with a click or Enter."
                                : toolMode === "obstaclePolyline" && !backgroundReferenceInput && !numericEditLabel
                                  ? activePolyline == null
                                    ? "Click the vertices of the obstacle and close on the first point."
                                    : "Continue the outline of the obstacle or close on the first point."
                                  : toolMode === "dimensionObject" && !backgroundReferenceInput && !numericEditLabel
                                    ? "Click an object to create automatic dimension lines for its sides."
                                    : toolMode === "holeCircle" && !backgroundReferenceInput && !numericEditLabel
                                      ? activeHoleHost == null
                                        ? "Select a closed polyline or a Flat Roof as the surface to drill."
                                        : activeCircle == null
                                          ? "Select the center of the circular hole with OSNAP enabled."
                                          : "Set the radius and confirm with a click or Enter."
                                      : toolMode === "holePolyline" && !backgroundReferenceInput && !numericEditLabel
                                        ? activeHoleHost == null
                                          ? "Select a closed polyline or a Flat Roof as the surface to drill."
                                          : activePolyline == null
                                            ? "Click the vertices of the hole and close on the first point."
                                            : "Continue the outline of the hole or close on the first point."
                                        : toolMode === "buildPolyline" && !backgroundReferenceInput && !numericEditLabel
                                          ? activePolyline == null
                                            ? "Click the first point of the polyline."
                                            : "Continue the polyline or click the initial point to close it without fill."
                                          : toolMode === "arc" && !backgroundReferenceInput && !numericEditLabel
                                            ? activeArc == null
                                              ? "Click the center of the arc."
                                              : activeArc.start == null
                                                ? "Click the first point of the arc."
                                                : "Click the second point of the arc."
                                            : statusBarHint;
  const showMultiGableStatusActions = toolMode === "multiGableRoof" && multiGableRoofDraft != null;
  const canSelectMultiGableSecondaryRakes = (multiGableRoofDraft?.mainRakes.length ?? 0) === 2;
  const canSelectMultiGableHips = canSelectMultiGableSecondaryRakes;
  const canFinalizeMultiGableRoof = multiGableRoofDraft != null;
  const activeMultiGableStep =
    multiGableRoofDraft?.stage === "mainRakes"
      ? "mainRakes"
      : multiGableRoofDraft?.stage === "secondaryRakes"
        ? "secondaryRakes"
        : "hips";
  const formatStatusProductCodes = (products: Array<{ item: { code?: string | null } }>) =>
    Array.from(
      new Set(products.map((product) => product.item.code).filter((code): code is string => Boolean(code))),
    ).join(" / ");
  const activeSystemStatusSummary =
    toolMode === "singleLifeLine" || toolMode === "automaticLifeLine"
      ? {
          title: `Lifeline: ${selectedLifeLineDefaultSystem?.name ?? "No system"}`,
          codes: [
            effectiveTerminalCurveSupportCode ? `T/C ${effectiveTerminalCurveSupportCode}` : "",
            effectiveIntermediateSupportCode ? `I ${effectiveIntermediateSupportCode}` : "",
          ]
            .filter(Boolean)
            .join(" · "),
          products: formatStatusProductCodes([...lifeLineAdditionalProducts, ...lifeLineOptionalProducts]),
        }
      : toolMode === "singlePointCircle" || toolMode === "pointSeries"
        ? {
            title: `Anchor Point: ${selectedAnchorPointDefaultSystem?.name ?? "No system"}`,
            codes: [
              effectiveAnchorPointCode ? `Code ${effectiveAnchorPointCode}` : "",
              effectiveAnchorPointMaterialLabel ? `Material ${effectiveAnchorPointMaterialLabel}` : "",
            ]
              .filter(Boolean)
              .join(" · "),
            products: formatStatusProductCodes([...anchorPointAdditionalProducts, ...anchorPointOptionalProducts]),
          }
        : null;
  const iconPreviewEntity: CanvasIconEntity | null =
    pendingIconId && iconPreviewPoint
      ? (() => {
          const iconDefinition =
            CANVAS_ICON_LIBRARY.find((icon) => icon.id === pendingIconId) ?? CANVAS_ICON_LIBRARY[0];
          const iconStyle = getEntityTypeStyle("icon");
          const iconColor = getCurrentIconColor();

          return {
            id: "icon-preview",
            name: iconDefinition?.label ?? "Icon",
            kind: "icon",
            iconId: pendingIconId,
            position: clonePoint(iconPreviewPoint),
            size: getCurrentIconSize(),
            mainColor: iconColor,
            rotation: 0,
            layer: DEFAULT_ICON_LAYER,
            entityStyleKind: "icon",
            strokeColor: iconColor || iconStyle.strokeColor,
            strokeWidth: iconStyle.strokeWidth,
          };
        })()
      : null;
  const orderedLayerItems = normalizeLayerOrder(layerOrder).map((layer) => ({
    layer,
    shapes: polylines.filter((shape) => isShapeVisible(shape) && getShapeLayer(shape) === layer),
    dimensions: dimensions.filter(
      (dimension) => isDimensionVisible(dimension) && getDimensionLayer(dimension) === layer,
    ),
    textEntities: textEntities.filter(
      (textEntity) => isTextEntityVisible(textEntity) && getTextEntityLayer(textEntity) === layer,
    ),
    iconEntities: iconEntities.filter(
      (iconEntity) => isIconEntityVisible(iconEntity) && getIconEntityLayer(iconEntity) === layer,
    ),
  }));
  const editingTextEntity = editingTextId
    ? (textEntities.find((textEntity) => textEntity.id === editingTextId) ?? null)
    : null;
  const editingTextBounds = editingTextEntity ? getTextEntityBounds(editingTextEntity) : null;
  const editingTextInputStyle: CSSProperties | undefined =
    editingTextEntity && editingTextBounds
      ? {
          position: "absolute",
          left: viewport.x + editingTextBounds.minX * viewport.scale,
          top: viewport.y + editingTextBounds.minY * viewport.scale,
          width: Math.max(80, (editingTextBounds.maxX - editingTextBounds.minX) * viewport.scale),
          height: Math.max(28, (editingTextBounds.maxY - editingTextBounds.minY) * viewport.scale),
          fontFamily: editingTextEntity.fontFamily,
          fontSize: Math.max(10, editingTextEntity.height * viewport.scale),
          lineHeight: "1.2",
          color: editingTextEntity.strokeColor,
          border: "2px solid #1d5cab",
          borderRadius: 4,
          boxSizing: "border-box",
          background: "rgba(255,255,255,0.96)",
          padding: Math.max(2, TEXT_BOX_PADDING * viewport.scale),
          outline: "none",
          overflow: "auto",
          resize: "none",
          textAlign:
            editingTextEntity.justification === "middle" || editingTextEntity.justification === "bottom"
              ? "center"
              : editingTextEntity.justification === "right"
                ? "right"
                : "left",
          zIndex: 20,
        }
      : undefined;
  const normalizeLifeLineRoleCode = (code: VertexCode | string) => (code.match(/^T\d+$/) ? "T" : code);
  const getLifeLineVertexRoles = (vertex: NonNullable<PolylineLikeShape["lifeLineVertices"]>[number]) => {
    const codes = vertex.displayCodes?.length ? vertex.displayCodes : vertex.codes;
    const roles = codes?.map(normalizeLifeLineRoleCode).filter((code) => code.length > 0) ?? [];

    if (roles.length > 0) {
      return Array.from(new Set(roles));
    }

    const parsedRoles = (vertex.displayLabel || vertex.label)
      .replace(/^\d+/, "")
      .split("/")
      .map((code) => normalizeLifeLineRoleCode(code.trim()))
      .filter((code) => code.length > 0);

    return Array.from(new Set(parsedRoles));
  };
  const formatLifeLinePositionLabel = (position: number, roleOccurrences: LifeLineVertexRoleOccurrence[]) => {
    const uniqueRoleOccurrences = roleOccurrences.filter(
      (occurrence, index, occurrences) =>
        occurrences.findIndex(
          (candidate) => candidate.role === occurrence.role && candidate.lineNumber === occurrence.lineNumber,
        ) === index,
    );

    if (uniqueRoleOccurrences.length === 0) {
      return `P${position}`;
    }

    return [
      `P${position}`,
      ...uniqueRoleOccurrences.map((occurrence) => `${occurrence.role}(${occurrence.lineNumber})`),
    ].join(" - ");
  };
  const lifeLinePositionLabels = useMemo(() => {
    const groups: Array<{ point: CanvasPoint; occurrences: SharedLifeLineVertexOccurrence[] }> = [];
    let visibleLifeLineNumber = 1;

    normalizeLayerOrder(layerOrder).forEach((layer) => {
      polylines
        .filter((shape) => isShapeVisible(shape) && getShapeLayer(shape) === layer)
        .forEach((shape) => {
          if (shape.kind === "circle" || shape.kind === "arc" || (shape.lifeLineVertices?.length ?? 0) === 0) {
            return;
          }

          const lineNumber = visibleLifeLineNumber;
          visibleLifeLineNumber += 1;

          shape.lifeLineVertices?.forEach((vertex, vertexIndex) => {
            const occurrence: SharedLifeLineVertexOccurrence = {
              x: vertex.x,
              y: vertex.y,
              shapeId: shape.id,
              vertexIndex,
              lineNumber,
              roles: getLifeLineVertexRoles(vertex),
            };
            const existingGroup = groups.find((group) => areCanvasPointsEqual(group.point, vertex));

            if (existingGroup) {
              existingGroup.occurrences.push(occurrence);
            } else {
              groups.push({
                point: { x: vertex.x, y: vertex.y },
                occurrences: [occurrence],
              });
            }
          });
        });
    });

    const positionLabels = new Map<string, LifeLinePositionLabel>();
    groups.forEach((group, index) => {
      const owner = group.occurrences[0];
      if (!owner) {
        return;
      }

      const position = index + 1;
      const displayLabel = formatLifeLinePositionLabel(
        position,
        group.occurrences.flatMap((occurrence) =>
          occurrence.roles.map((role) => ({
            role,
            lineNumber: occurrence.lineNumber,
          })),
        ),
      );

      group.occurrences.forEach((occurrence) => {
        positionLabels.set(`${occurrence.shapeId}:${occurrence.vertexIndex}`, {
          position,
          displayLabel,
          ownerShapeId: owner.shapeId,
          ownerVertexIndex: owner.vertexIndex,
          occurrenceCount: group.occurrences.length,
        });
      });
    });

    return positionLabels;
  }, [layerOrder, polylines, visibleLayers]);

  const getPolylinePathLength = (points: number[], closed: boolean) => {
    const vertices = getRenderablePolylineVertices(points, closed);
    if (vertices.length < 2) return 0;

    const segmentCount = closed ? vertices.length : vertices.length - 1;
    let length = 0;
    for (let index = 0; index < segmentCount; index += 1) {
      const start = vertices[index];
      const end = vertices[(index + 1) % vertices.length];
      if (start && end) {
        length += getDistance(start, end);
      }
    }
    return length;
  };

  const billOfMaterialsSummary = useMemo(() => {
    const roleTotals = new Map<string, number>();
    const positionLabels = new Map<number, string>();
    const lines: Array<{
      id: string;
      name: string;
      lineNumber: number;
      length: number;
      positions: string[];
      roleCounts: Array<{ role: string; count: number }>;
    }> = [];
    let visibleLifeLineNumber = 1;

    normalizeLayerOrder(layerOrder).forEach((layer) => {
      polylines
        .filter((shape): shape is PolylineLikeShape => {
          if (!isShapeVisible(shape) || getShapeLayer(shape) !== layer) return false;
          return shape.kind !== "circle" && shape.kind !== "arc" && (shape.lifeLineVertices?.length ?? 0) > 0;
        })
        .forEach((shape) => {
          const lineRoleCounts = new Map<string, number>();
          const positions: string[] = [];

          shape.lifeLineVertices?.forEach((vertex, vertexIndex) => {
            const positionLabel = lifeLinePositionLabels.get(`${shape.id}:${vertexIndex}`);
            if (positionLabel) {
              if (
                positionLabel.ownerShapeId === shape.id &&
                positionLabel.ownerVertexIndex === vertexIndex &&
                !positionLabels.has(positionLabel.position)
              ) {
                positionLabels.set(positionLabel.position, positionLabel.displayLabel);
              }
              if (!positions.includes(positionLabel.displayLabel)) {
                positions.push(positionLabel.displayLabel);
              }
            }

            getLifeLineVertexRoles(vertex).forEach((role) => {
              lineRoleCounts.set(role, (lineRoleCounts.get(role) ?? 0) + 1);
              roleTotals.set(role, (roleTotals.get(role) ?? 0) + 1);
            });
          });

          lines.push({
            id: shape.id,
            name: shape.name,
            lineNumber: visibleLifeLineNumber,
            length: getPolylinePathLength(shape.points, shape.closed),
            positions,
            roleCounts: Array.from(lineRoleCounts.entries())
              .map(([role, count]) => ({ role, count }))
              .sort((left, right) => left.role.localeCompare(right.role)),
          });
          visibleLifeLineNumber += 1;
        });
    });

    const totalLength = lines.reduce((sum, line) => sum + line.length, 0);

    return {
      lines,
      totalLength,
      totalPositions: positionLabels.size,
      positionLabels: Array.from(positionLabels.entries())
        .sort(([left], [right]) => left - right)
        .map(([, label]) => label),
      roleTotals: Array.from(roleTotals.entries())
        .map(([role, count]) => ({ role, count }))
        .sort((left, right) => left.role.localeCompare(right.role)),
    };
  }, [layerOrder, lifeLinePositionLabels, polylines, visibleLayers]);

  const renderClosedShapeWithHoles = (
    polyline: PolylineLikeShape,
    isSelected: boolean,
    shapeLineDash: number[] | undefined,
  ) => {
    const fillColor = toAlphaColor(polyline.strokeColor, isSelected ? 0.22 : polyline.isObstacle ? 0.22 : 0.14);
    const strokeDash = isSelected ? [getScreenInvariantSize(10), getScreenInvariantSize(6)] : shapeLineDash;
    const strokeWidth = getScreenInvariantSize(isSelected ? polyline.strokeWidth + 1 : polyline.strokeWidth);

    return (
      <Group>
        <Line
          points={polyline.points}
          closed
          stroke={polyline.strokeColor}
          strokeWidth={strokeWidth}
          fill={fillColor}
          dash={strokeDash}
          lineCap="round"
          lineJoin="round"
        />
        {(polyline.holes ?? []).map((hole, index) => (
          <Fragment key={`${polyline.id}-hole-${index}`}>
            <Line points={hole} closed fill="#000000" globalCompositeOperation="destination-out" listening={false} />
            <Line
              points={hole}
              closed
              stroke={polyline.strokeColor}
              strokeWidth={strokeWidth}
              dash={strokeDash}
              lineCap="round"
              lineJoin="round"
            />
          </Fragment>
        ))}
        {(polyline.circleHoles ?? []).map((hole, index) => (
          <Fragment key={`${polyline.id}-circle-hole-${index}`}>
            <Circle
              x={hole.center.x}
              y={hole.center.y}
              radius={hole.radius}
              fill="#000000"
              globalCompositeOperation="destination-out"
              listening={false}
            />
            <Circle
              x={hole.center.x}
              y={hole.center.y}
              radius={hole.radius}
              stroke={polyline.strokeColor}
              strokeWidth={strokeWidth}
              dash={strokeDash}
              listening={false}
            />
          </Fragment>
        ))}
      </Group>
    );
  };

  const buildHatchSegments = (shape: PolylineLikeShape) => {
    const bounds = getShapeBounds(shape);
    const width = Math.max(1, bounds.maxX - bounds.minX);
    const height = Math.max(1, bounds.maxY - bounds.minY);
    const span = width + height;
    const spacing = Math.max(40, HATCH_BASE_SPACING * Math.max(0.1, shape.hatchScale ?? 1));
    const segments: number[][] = [];
    const addHorizontal = () => {
      for (let y = bounds.minY - spacing; y <= bounds.maxY + spacing; y += spacing) {
        segments.push([bounds.minX - spacing, y, bounds.maxX + spacing, y]);
      }
    };
    const addVertical = () => {
      for (let x = bounds.minX - spacing; x <= bounds.maxX + spacing; x += spacing) {
        segments.push([x, bounds.minY - spacing, x, bounds.maxY + spacing]);
      }
    };
    const addDiagonal = (ascending: boolean) => {
      for (let offset = -height - spacing; offset <= width + spacing; offset += spacing) {
        if (ascending) {
          segments.push([
            bounds.minX + offset,
            bounds.maxY + spacing,
            bounds.minX + offset + span,
            bounds.maxY + spacing - span,
          ]);
        } else {
          segments.push([
            bounds.minX + offset,
            bounds.minY - spacing,
            bounds.minX + offset + span,
            bounds.minY - spacing + span,
          ]);
        }
      }
    };

    switch (shape.hatchPattern ?? DEFAULT_HATCH_PATTERN) {
      case "horizontal":
        addHorizontal();
        break;
      case "vertical":
        addVertical();
        break;
      case "grid":
        addHorizontal();
        addVertical();
        break;
      case "cross":
        addDiagonal(false);
        addDiagonal(true);
        break;
      case "diagonal":
      default:
        addDiagonal(false);
        break;
    }

    return segments;
  };

  const renderHatchShape = (polyline: PolylineLikeShape, isSelected: boolean, shapeLineDash: number[] | undefined) => {
    const strokeDash = isSelected ? [getScreenInvariantSize(10), getScreenInvariantSize(6)] : shapeLineDash;
    const strokeWidth = getScreenInvariantSize(isSelected ? polyline.strokeWidth + 1 : polyline.strokeWidth);
    const hatchStrokeWidth = getScreenInvariantSize(Math.max(0.75, polyline.strokeWidth));
    const hatchSegments = buildHatchSegments(polyline);

    return (
      <Group>
        <Group
          clipFunc={(context) => {
            context.beginPath();
            context.moveTo(polyline.points[0] ?? 0, polyline.points[1] ?? 0);
            for (let index = 2; index < polyline.points.length; index += 2) {
              context.lineTo(polyline.points[index] ?? 0, polyline.points[index + 1] ?? 0);
            }
            context.closePath();
            for (const hole of polyline.holes ?? []) {
              context.moveTo(hole[0] ?? 0, hole[1] ?? 0);
              for (let index = 2; index < hole.length; index += 2) {
                context.lineTo(hole[index] ?? 0, hole[index + 1] ?? 0);
              }
              context.closePath();
            }
            for (const hole of polyline.circleHoles ?? []) {
              context.moveTo(hole.center.x + hole.radius, hole.center.y);
              context.arc(hole.center.x, hole.center.y, hole.radius, 0, Math.PI * 2);
              context.closePath();
            }
            context.clip("evenodd");
          }}
        >
          {hatchSegments.map((points, index) => (
            <Line
              key={`${polyline.id}-hatch-${index}`}
              points={points}
              stroke={polyline.strokeColor}
              strokeWidth={hatchStrokeWidth}
              lineCap="round"
              listening={false}
            />
          ))}
        </Group>
        <Line
          points={polyline.points}
          closed
          stroke={polyline.strokeColor}
          strokeWidth={strokeWidth}
          dash={strokeDash}
          lineCap="round"
          lineJoin="round"
        />
        {(polyline.holes ?? []).map((hole, index) => (
          <Line
            key={`${polyline.id}-hatch-hole-${index}`}
            points={hole}
            closed
            stroke={polyline.strokeColor}
            strokeWidth={strokeWidth}
            dash={strokeDash}
            lineCap="round"
            lineJoin="round"
            listening={false}
          />
        ))}
        {(polyline.circleHoles ?? []).map((hole, index) => (
          <Circle
            key={`${polyline.id}-hatch-circle-hole-${index}`}
            x={hole.center.x}
            y={hole.center.y}
            radius={hole.radius}
            stroke={polyline.strokeColor}
            strokeWidth={strokeWidth}
            dash={strokeDash}
            listening={false}
          />
        ))}
      </Group>
    );
  };

  const renderSegmentStretchHandles = (polyline: PolylineLikeShape) => {
    const calculatedLifeLineVertices = (polyline.lifeLineVertices ?? []).filter(
      (vertex) => vertex.kind === "generated",
    );

    const renderContourSegmentHandles = (points: number[], closed: boolean, holeIndex?: number) => {
      const vertices = getPolylineVertices(points);
      const renderableVertexCount = getRenderableVertexCount(points, closed);
      const segmentCount = closed ? renderableVertexCount : Math.max(0, renderableVertexCount - 1);
      if (segmentCount <= 0) return null;

      return Array.from({ length: segmentCount }, (_, segmentIndex) => {
        const start = vertices[segmentIndex];
        const end = vertices[(segmentIndex + 1) % renderableVertexCount];
        const vertexIndices = getSegmentStretchVertexIndices(points, closed, segmentIndex);
        if (!start || !end || vertexIndices.length < 2) return null;
        if (
          holeIndex == null &&
          calculatedLifeLineVertices.some(
            (vertex) => areCanvasPointsEqual(vertex, start) || areCanvasPointsEqual(vertex, end),
          )
        ) {
          return null;
        }

        const getRenderableSegment = (index: number): SnapExcludedSegment | null => {
          if (!closed && (index < 0 || index >= segmentCount)) return null;
          const normalizedIndex = (index + segmentCount) % segmentCount;
          const segmentStart = vertices[normalizedIndex];
          const segmentEnd = vertices[(normalizedIndex + 1) % renderableVertexCount];
          return segmentStart && segmentEnd ? { start: segmentStart, end: segmentEnd } : null;
        };
        const excludedSegments = [
          getRenderableSegment(segmentIndex - 1),
          getRenderableSegment(segmentIndex),
          getRenderableSegment(segmentIndex + 1),
        ].filter((segment): segment is SnapExcludedSegment => segment != null);
        const stretchHandlers = {
          onMouseDown: (event: Konva.KonvaEventObject<MouseEvent>) => {
            event.cancelBubble = true;
          },
          onDragStart: (event: Konva.KonvaEventObject<DragEvent>) => {
            event.cancelBubble = true;
            directionLockAxisRef.current = null;
            setDirectionLockAxis(null);
            const stretchState: SegmentStretchState = {
              polylineId: polyline.id,
              vertexIndices,
              originalPoints: points.slice(),
              startPointer: getCurrentPointerWorldPosition(),
              shapeKind: polyline.kind,
              holeIndex,
              excludedPoints: [start, end],
              excludedSegments,
            };
            beginHistoryAction();
            segmentStretchRef.current = stretchState;
            setDraggedSegmentStretch(stretchState);
            setInvalidGripFeedback(null);
            updateSegmentStretchNumericEditState(stretchState, stretchState.startPointer);
            setHoverSnap(null);
          },
          onDragMove: (event: Konva.KonvaEventObject<DragEvent>) => {
            event.cancelBubble = true;
            const stretchState = segmentStretchRef.current;
            if (!stretchState) return;

            const resolved = resolveGripPoint(
              stretchState.startPointer,
              getCurrentPointerWorldPosition(),
              event.evt.shiftKey,
              stretchState.excludedPoints,
              stretchState.excludedSegments,
              false,
              true,
              ["PERP"],
            );

            applySegmentStretch(stretchState, resolved.point);
            updateSegmentStretchNumericEditState(stretchState, resolved.point);
            setHoverSnap(resolved.osnap);
            setInvalidGripFeedback(null);
            event.target.position({ x: 0, y: 0 });
          },
          onDragEnd: (event: Konva.KonvaEventObject<DragEvent>) => {
            event.cancelBubble = true;
            const stretchState = segmentStretchRef.current;
            if (stretchState) {
              const resolved = resolveGripPoint(
                stretchState.startPointer,
                getCurrentPointerWorldPosition(),
                event.evt.shiftKey,
                stretchState.excludedPoints,
                stretchState.excludedSegments,
                false,
                true,
                ["PERP"],
              );
              applySegmentStretch(stretchState, resolved.point);
              updateSegmentStretchNumericEditState(stretchState, resolved.point);
            }
            event.target.position({ x: 0, y: 0 });
            segmentStretchRef.current = null;
            setDraggedSegmentStretch(null);
            setHoverSnap(null);
            setInvalidGripFeedback(null);
            clearOsnapTracking();
            directionLockAxisRef.current = null;
            setDirectionLockAxis(null);
            finishHistoryAction();
          },
        };

        return (
          <Fragment key={`${polyline.id}-segment-stretch-${holeIndex ?? "outer"}-${segmentIndex}`}>
            <Line
              points={[start.x, start.y, end.x, end.y]}
              stroke="rgba(217,72,15,0.01)"
              strokeWidth={getScreenInvariantSize(Math.max(GRIP_HIT_STROKE, 14))}
              hitStrokeWidth={getScreenInvariantSize(Math.max(GRIP_HIT_STROKE, 18))}
              lineCap="round"
              lineJoin="round"
              draggable
              {...stretchHandlers}
            />
          </Fragment>
        );
      });
    };

    return (
      <>
        {renderContourSegmentHandles(polyline.points, polyline.closed)}
        {(polyline.holes ?? []).map((hole, holeIndex) => (
          <Fragment key={`${polyline.id}-hole-segment-stretch-${holeIndex}`}>
            {renderContourSegmentHandles(hole, true, holeIndex)}
          </Fragment>
        ))}
      </>
    );
  };

  const renderShapeEntity = (polyline: Shape) => {
    const isSelected = selectedIds.includes(polyline.id);
    const isEditing = editingId === polyline.id;
    const labelsVisible = areLabelsVisible();
    const gripPoints = getShapeGripPoints(polyline);
    const styleKind = getShapeStyleKind(polyline);
    const entityStyle = getEntityTypeStyle(styleKind);
    const shapeLineDash = getEntityTypeLineDash(styleKind);
    const polylineShape =
      polyline.kind !== "circle" && polyline.kind !== "arc" ? (polyline as PolylineLikeShape) : null;
    const shouldRenderPolylineFill =
      polylineShape != null && !polylineShape.disableFill && !isNonFlatRoofSurfaceStyleKind(styleKind);
    const lifeLineVertices = polylineShape?.lifeLineVertices ?? [];
    const displayLifeLineVertices =
      polylineShape && lifeLineVertices.length > 0
        ? lifeLineVertices.map((vertex, index) => {
            const positionLabel = lifeLinePositionLabels.get(`${polylineShape.id}:${index}`);
            return positionLabel
              ? {
                  ...vertex,
                  label: positionLabel.displayLabel,
                  displayLabel: positionLabel.displayLabel,
                }
              : vertex;
          })
        : lifeLineVertices;
    const lifeLineLabelPlacements =
      polylineShape && displayLifeLineVertices.length > 0
        ? getLifeLineLabelPlacements(
            polylineShape.points,
            polylineShape.closed,
            displayLifeLineVertices,
            polylineShape.lifeLineLineNumber ?? 0,
          )
        : [];
    const calculatedLifeLineVertices = lifeLineVertices.filter((vertex) => vertex.kind === "generated");
    const isLifeLineShape = lifeLineVertices.length > 0;
    const visibleGripPoints =
      polylineShape && isLifeLineShape
        ? getLifeLineGripPoints(polylineShape, gripPoints)
        : calculatedLifeLineVertices.length > 0
          ? gripPoints.filter(
              (grip) => !calculatedLifeLineVertices.some((vertex) => areCanvasPointsEqual(vertex, grip.point)),
            )
          : gripPoints;
    const showVertexMarkersOnly = polylineShape?.showVertexMarkersOnly === true;
    const roofLinePrefix = polyline.name.startsWith("Gronda")
      ? "Gronda"
      : polyline.name.startsWith("Diagonale")
        ? "Diagonale"
        : polyline.name.startsWith("HIP")
          ? "HIP"
          : polyline.name.startsWith("Valley")
            ? "Valley"
            : polyline.name.startsWith("Colmo")
              ? "Colmo"
              : polyline.name.startsWith("BL")
                ? "BL"
                : polyline.name.startsWith("RAKE")
                  ? "RAKE"
                  : null;
    const roofLineDecoration =
      roofLinePrefix === "Gronda"
        ? { color: "#1d5cab", label: "E" }
        : roofLinePrefix === "Diagonale" || roofLinePrefix === "HIP"
          ? { color: "#d9480f", label: "H" }
          : roofLinePrefix === "Valley"
            ? { color: "#0891b2", label: "V" }
            : roofLinePrefix === "Colmo"
              ? { color: "#15803d", label: "C" }
              : roofLinePrefix === "BL"
                ? { color: "#495057", label: "R" }
                : roofLinePrefix === "RAKE"
                  ? { color: "#7c3aed", label: "R" }
                  : null;
    const roofLinePoints = polylineShape && polylineShape.points.length >= 4 ? polylineShape.points : null;
    const roofLineLabelPoint =
      roofLineDecoration && roofLinePoints
        ? (() => {
            const start = getPointFromCoordinateArray(roofLinePoints, 0);
            const end = getPointFromCoordinateArray(roofLinePoints, 2);
            if (!start || !end) return null;
            const direction = getNormalizedDirection(start, end);
            const normal = { x: -direction.y, y: direction.x };
            const midpoint = {
              x: (start.x + end.x) / 2,
              y: (start.y + end.y) / 2,
            };
            const labelOffset = getScreenInvariantSize(15);
            return {
              x: midpoint.x + normal.x * labelOffset,
              y: midpoint.y + normal.y * labelOffset,
            };
          })()
        : null;
    const roofLineColor = roofLineDecoration?.color ?? polyline.strokeColor;
    const roofStrokeColor = roofLineColor;

    return (
      <Fragment key={polyline.id}>
        {polyline.kind === "arc" ? (
          <Line
            points={getArcRenderPoints(polyline)}
            stroke={roofStrokeColor}
            strokeWidth={getScreenInvariantSize(isSelected ? polyline.strokeWidth + 1 : polyline.strokeWidth)}
            dash={isSelected ? [getScreenInvariantSize(10), getScreenInvariantSize(6)] : shapeLineDash}
            lineCap="round"
            lineJoin="round"
          />
        ) : polyline.kind === "circle" ? (
          <>
            <Circle
              x={polyline.center.x}
              y={polyline.center.y}
              radius={polyline.radius}
              stroke={roofStrokeColor}
              strokeWidth={getScreenInvariantSize(isSelected ? polyline.strokeWidth + 1 : polyline.strokeWidth)}
              fill={
                styleKind === "single-anchor-point"
                  ? undefined
                  : toAlphaColor(roofStrokeColor, isSelected ? 0.22 : polyline.isObstacle ? 0.22 : 0.14)
              }
              dash={isSelected ? [getScreenInvariantSize(10), getScreenInvariantSize(6)] : shapeLineDash}
            />
            {styleKind === "single-anchor-point"
              ? renderCircleCenterDot(polyline.center, `${polyline.id}-center-marker`, polyline.strokeColor)
              : renderCircleCenterMarker(polyline.center, `${polyline.id}-center-marker`, polyline.strokeColor, {
                  listening: false,
                })}
          </>
        ) : polyline.entityStyleKind === "hatch" ? (
          renderHatchShape(polyline, isSelected, shapeLineDash)
        ) : polyline.closed &&
          shouldRenderPolylineFill &&
          ((polyline.holes?.length ?? 0) > 0 || (polyline.circleHoles?.length ?? 0) > 0) ? (
          renderClosedShapeWithHoles(polyline, isSelected, shapeLineDash)
        ) : (
          <Line
            points={polyline.points}
            closed={polyline.closed}
            stroke={showVertexMarkersOnly ? roofStrokeColor : roofStrokeColor}
            strokeWidth={getScreenInvariantSize(
              isSelected ? polyline.strokeWidth + 1 : showVertexMarkersOnly ? 0.01 : polyline.strokeWidth,
            )}
            fill={
              polyline.closed && shouldRenderPolylineFill && !showVertexMarkersOnly
                ? toAlphaColor(roofStrokeColor, isSelected ? 0.22 : polyline.isObstacle ? 0.22 : 0.14)
                : undefined
            }
            dash={isSelected ? [getScreenInvariantSize(10), getScreenInvariantSize(6)] : shapeLineDash}
            lineCap="round"
            lineJoin="round"
          />
        )}

        {showVertexMarkersOnly &&
          polylineShape &&
          getRenderablePolylineVertices(polylineShape.points, polylineShape.closed).map((vertex, index) =>
            renderCircleCenterDot(vertex, `${polyline.id}-point-${index}`, roofStrokeColor),
          )}

        {displayLifeLineVertices.map((vertex, index) => {
          const isGenerated = vertex.kind === "generated";
          const isChamfer = vertex.kind === "chamfer";
          const isPrimary = vertex.kind === "primary";
          const isRenderedOnly = isGenerated || isChamfer;
          const markerRadius = getScreenInvariantSize(
            isRenderedOnly ? Math.max(3, entityStyle.vertexSize - 1.5) : entityStyle.vertexSize,
          );
          const label = vertex.displayLabel || vertex.label;
          const vertexColor = isEditing && isPrimary ? entityStyle.gripColor : entityStyle.vertexColor;
          const isHollowVertex = isEditing && isRenderedOnly;
          const labelPlacement = lifeLineLabelPlacements[index];
          const positionLabel = polylineShape ? lifeLinePositionLabels.get(`${polylineShape.id}:${index}`) : null;
          const shouldRenderLabel =
            !positionLabel ||
            (positionLabel.ownerShapeId === polyline.id && positionLabel.ownerVertexIndex === index) ||
            positionLabel.occurrenceCount < 2;

          return (
            <Group key={`${polyline.id}-lifeline-vertex-${index}`} x={vertex.x} y={vertex.y} listening={false}>
              <Circle
                radius={markerRadius}
                fill={isHollowVertex ? "#ffffff" : vertexColor}
                stroke={isHollowVertex ? vertexColor : "#ffffff"}
                strokeWidth={getScreenInvariantSize(1.25)}
                listening={false}
              />
              {labelsVisible && shouldRenderLabel && (
                <Text
                  x={labelPlacement ? labelPlacement.x - vertex.x : getScreenInvariantSize(6)}
                  y={labelPlacement ? labelPlacement.y - vertex.y : -getScreenInvariantSize(16)}
                  text={label}
                  fontSize={getScreenInvariantSize(11)}
                  fontStyle="bold"
                  fill={vertexColor}
                  listening={false}
                />
              )}
            </Group>
          );
        })}

        {labelsVisible && roofLineDecoration && roofLineLabelPoint && (
          <Text
            x={roofLineLabelPoint.x}
            y={roofLineLabelPoint.y}
            text={roofLineDecoration.label}
            offsetX={getScreenInvariantSize(4)}
            offsetY={getScreenInvariantSize(6)}
            fontSize={getScreenInvariantSize(12)}
            fontStyle="bold"
            fill={roofLineDecoration.color}
            listening={false}
          />
        )}

        {isEditing && polylineShape && !isLifeLineShape && renderSegmentStretchHandles(polylineShape)}

        {isEditing &&
          visibleGripPoints.map((grip, index) =>
            renderEntityVertexMarker(
              styleKind,
              grip.point,
              `${polyline.id}-grip-${grip.holeIndex ?? "outer"}-${index}`,
              {
                draggable: grip.gripKind !== "insert",
                onClick: handleGripPointerClick(polyline, grip),
                onContextMenu:
                  grip.gripKind === "insert"
                    ? undefined
                    : handleGripContextMenu(
                        polyline.id,
                        grip.vertexIndex,
                        grip.linkedVertexIndices,
                        grip.holeIndex,
                        grip.holeGripKind,
                      ),
                ...(grip.gripKind === "insert"
                  ? {}
                  : getGripDragHandlers(
                      polyline.id,
                      grip.vertexIndex,
                      grip.linkedVertexIndices,
                      grip.holeIndex,
                      grip.holeGripKind,
                    )),
              },
              true,
              grip.gripKind ?? "vertex",
            ),
          )}
      </Fragment>
    );
  };

  const renderDimensionEntity = (dimension: Dimension) => {
    const geometry = getDimensionLayout(dimension.start, dimension.end, dimension.offset);
    const isSelected = selectedIds.includes(dimension.id);
    const isEditing = editingId === dimension.id;
    const labelsVisible = areLabelsVisible();
    if (!geometry) return null;

    const measuredLength = getDistance(dimension.start, dimension.end);
    const dimensionText =
      dimension.dimensionType === "radius"
        ? `R ${formatMeasurement(measuredLength)}`
        : formatMeasurement(measuredLength);
    const textAnchor = getDimensionTextAnchor(dimensionText);

    return (
      <Fragment key={dimension.id}>
        <Line
          points={[
            geometry.extensionStart.x,
            geometry.extensionStart.y,
            geometry.extensionStartOffset.x,
            geometry.extensionStartOffset.y,
          ]}
          stroke={dimension.strokeColor}
          strokeWidth={getScreenInvariantSize(isSelected ? dimension.strokeWidth + 0.75 : dimension.strokeWidth)}
          dash={isSelected ? [getScreenInvariantSize(10), getScreenInvariantSize(6)] : undefined}
        />
        <Line
          points={[
            geometry.extensionEnd.x,
            geometry.extensionEnd.y,
            geometry.extensionEndOffset.x,
            geometry.extensionEndOffset.y,
          ]}
          stroke={dimension.strokeColor}
          strokeWidth={getScreenInvariantSize(isSelected ? dimension.strokeWidth + 0.75 : dimension.strokeWidth)}
          dash={isSelected ? [getScreenInvariantSize(10), getScreenInvariantSize(6)] : undefined}
        />
        <Line
          points={[
            geometry.extensionStartOffset.x,
            geometry.extensionStartOffset.y,
            geometry.extensionEndOffset.x,
            geometry.extensionEndOffset.y,
          ]}
          stroke={dimension.strokeColor}
          strokeWidth={getScreenInvariantSize(isSelected ? dimension.strokeWidth + 0.75 : dimension.strokeWidth)}
          dash={isSelected ? [getScreenInvariantSize(10), getScreenInvariantSize(6)] : undefined}
        />
        {renderDimensionMarker(
          geometry.extensionStartOffset,
          { x: geometry.ux, y: geometry.uy },
          dimension.strokeColor,
          `${dimension.id}-start-marker`,
        )}
        {renderDimensionMarker(
          geometry.extensionEndOffset,
          { x: -geometry.ux, y: -geometry.uy },
          dimension.strokeColor,
          `${dimension.id}-end-marker`,
        )}
        {labelsVisible && (
          <Text
            x={geometry.textPoint.x}
            y={geometry.textPoint.y}
            offsetX={textAnchor.offsetX}
            offsetY={textAnchor.offsetY}
            text={dimensionText}
            fontSize={textAnchor.fontSize}
            fontStyle="bold"
            fill={dimension.strokeColor}
            rotation={geometry.textRotation}
            listening={false}
          />
        )}
        {isEditing && (
          <Circle
            x={geometry.lineMidPoint.x}
            y={geometry.lineMidPoint.y}
            radius={getScreenInvariantSize(GRIP_RADIUS)}
            fill="#ffffff"
            stroke="#d9480f"
            strokeWidth={getScreenInvariantSize(1.5)}
            hitStrokeWidth={getScreenInvariantSize(GRIP_HIT_STROKE)}
            draggable
            onMouseDown={(event) => {
              event.cancelBubble = true;
            }}
            onDragStart={(event) => {
              event.cancelBubble = true;
              beginHistoryAction();
              setDraggedDimensionId(dimension.id);
            }}
            onDragMove={(event) => {
              event.cancelBubble = true;
              updateDimensionOffset(dimension.id, {
                x: event.target.x(),
                y: event.target.y(),
              });
            }}
            onDragEnd={(event) => {
              event.cancelBubble = true;
              updateDimensionOffset(dimension.id, {
                x: event.target.x(),
                y: event.target.y(),
              });
              finishHistoryAction();
              setDraggedDimensionId(null);
            }}
          />
        )}
      </Fragment>
    );
  };

  const updateTextEntityBoxBounds = (textEntityId: string, corner: "nw" | "ne" | "se" | "sw", point: CanvasPoint) => {
    setTextEntities((current) =>
      current.map((textEntity) => {
        if (textEntity.id !== textEntityId) return textEntity;

        const bounds = getTextEntityBounds(textEntity);
        const nextBounds = { ...bounds };

        if (corner === "nw" || corner === "sw") {
          nextBounds.minX = Math.min(point.x, bounds.maxX - MIN_TEXT_BOX_WIDTH);
        }
        if (corner === "ne" || corner === "se") {
          nextBounds.maxX = Math.max(point.x, bounds.minX + MIN_TEXT_BOX_WIDTH);
        }
        if (corner === "nw" || corner === "ne") {
          nextBounds.minY = Math.min(point.y, bounds.maxY - MIN_TEXT_BOX_HEIGHT);
        }
        if (corner === "sw" || corner === "se") {
          nextBounds.maxY = Math.max(point.y, bounds.minY + MIN_TEXT_BOX_HEIGHT);
        }

        return {
          ...textEntity,
          boxWidth: nextBounds.maxX - nextBounds.minX,
          boxHeight: nextBounds.maxY - nextBounds.minY,
          position: getTextEntityPositionFromBounds(nextBounds, textEntity),
        };
      }),
    );
  };

  const renderTextEntity = (textEntity: TextEntity) => {
    const isSelected = selectedIds.includes(textEntity.id);
    const bounds = getTextEntityBounds(textEntity);
    const textWidth = bounds.maxX - bounds.minX;
    const textHeight = bounds.maxY - bounds.minY;
    const contentWidth = Math.max(1, textWidth - TEXT_BOX_PADDING * 2);
    const contentHeight = Math.max(1, textHeight - TEXT_BOX_PADDING * 2);
    const textLayer = getTextEntityLayer(textEntity);
    const textAlign =
      textEntity.justification === "middle" || textEntity.justification === "bottom"
        ? "center"
        : textEntity.justification === "right"
          ? "right"
          : "left";
    const textVerticalAlign = textEntity.justification === "bottom" ? "bottom" : "middle";
    const resizeHandles = [
      { corner: "nw" as const, x: 0, y: 0 },
      { corner: "ne" as const, x: textWidth, y: 0 },
      { corner: "se" as const, x: textWidth, y: textHeight },
      { corner: "sw" as const, x: 0, y: textHeight },
    ];

    return (
      <Fragment key={textEntity.id}>
        <Group
          x={bounds.minX}
          y={bounds.minY}
          draggable={toolMode === "select" && !isLayerLocked(textLayer)}
          onMouseDown={(event) => {
            event.cancelBubble = true;
            if ((toolMode !== "select" && toolMode !== "text") || isLayerLocked(textLayer)) return;
            setSelectionCycle(null);
            setSelectedIds((current) =>
              event.evt.shiftKey && toolMode === "select"
                ? current.includes(textEntity.id)
                  ? current.filter((id) => id !== textEntity.id)
                  : [...current, textEntity.id]
                : [textEntity.id],
            );
            setEditingId(null);
            if (editingTextId && editingTextId !== textEntity.id) {
              cancelTextEditing();
            }
          }}
          onDblClick={(event) => {
            event.cancelBubble = true;
            if ((toolMode !== "select" && toolMode !== "text") || isLayerLocked(textLayer)) return;
            setSelectionCycle(null);
            setSelectedIds([textEntity.id]);
            startTextEditing(textEntity);
          }}
          onDragStart={(event) => {
            event.cancelBubble = true;
            beginHistoryAction();
          }}
          onDragMove={(event) => {
            event.cancelBubble = true;
            const nextBounds = {
              minX: event.target.x(),
              minY: event.target.y(),
              maxX: event.target.x() + textWidth,
              maxY: event.target.y() + textHeight,
            };
            setTextEntities((current) =>
              current.map((item) =>
                item.id === textEntity.id
                  ? { ...item, position: getTextEntityPositionFromBounds(nextBounds, item) }
                  : item,
              ),
            );
          }}
          onDragEnd={(event) => {
            event.cancelBubble = true;
            const nextBounds = {
              minX: event.target.x(),
              minY: event.target.y(),
              maxX: event.target.x() + textWidth,
              maxY: event.target.y() + textHeight,
            };
            setTextEntities((current) =>
              current.map((item) =>
                item.id === textEntity.id
                  ? { ...item, position: getTextEntityPositionFromBounds(nextBounds, item) }
                  : item,
              ),
            );
            finishHistoryAction();
          }}
        >
          <Rect
            x={0}
            y={0}
            width={textWidth}
            height={textHeight}
            fill="rgba(255,255,255,0.01)"
            stroke={isSelected ? "#1d5cab" : textEntity.strokeColor}
            strokeWidth={getScreenInvariantSize(isSelected ? 1.5 : Math.max(1, textEntity.strokeWidth))}
            dash={isSelected ? [getScreenInvariantSize(6), getScreenInvariantSize(4)] : undefined}
          />
          <Text
            x={TEXT_BOX_PADDING}
            y={TEXT_BOX_PADDING}
            width={contentWidth}
            height={contentHeight}
            text={textEntity.text}
            fontSize={textEntity.height}
            fontFamily={textEntity.fontFamily}
            fill={textEntity.strokeColor}
            align={textAlign}
            verticalAlign={textVerticalAlign}
          />
        </Group>
        {isSelected && (
          <>
            {resizeHandles.map((handle) => (
              <Circle
                key={`${textEntity.id}-${handle.corner}`}
                x={bounds.minX + handle.x}
                y={bounds.minY + handle.y}
                radius={getScreenInvariantSize(GRIP_RADIUS)}
                fill="#ffffff"
                stroke="#1d5cab"
                strokeWidth={getScreenInvariantSize(1.5)}
                hitStrokeWidth={getScreenInvariantSize(GRIP_HIT_STROKE)}
                draggable
                onMouseDown={(event) => {
                  event.cancelBubble = true;
                }}
                onDragStart={(event) => {
                  event.cancelBubble = true;
                  beginHistoryAction();
                }}
                onDragMove={(event) => {
                  event.cancelBubble = true;
                  updateTextEntityBoxBounds(textEntity.id, handle.corner, {
                    x: event.target.x(),
                    y: event.target.y(),
                  });
                }}
                onDragEnd={(event) => {
                  event.cancelBubble = true;
                  updateTextEntityBoxBounds(textEntity.id, handle.corner, {
                    x: event.target.x(),
                    y: event.target.y(),
                  });
                  finishHistoryAction();
                }}
              />
            ))}
          </>
        )}
      </Fragment>
    );
  };

  const renderIconEntity = (iconEntity: CanvasIconEntity) => {
    const isSelected = selectedIds.includes(iconEntity.id);
    const isEditing = editingId === iconEntity.id;
    const iconLayer = getIconEntityLayer(iconEntity);
    const rotation = ((iconEntity.rotation ?? 0) * Math.PI) / 180;
    const cos = Math.cos(rotation);
    const sin = Math.sin(rotation);
    const scaleGripPoint = {
      x: iconEntity.position.x + iconEntity.size * cos + iconEntity.size * sin,
      y: iconEntity.position.y + iconEntity.size * sin - iconEntity.size * cos,
    };
    const updateIconSizeFromGrip = (point: CanvasPoint) => {
      const dx = point.x - iconEntity.position.x;
      const dy = point.y - iconEntity.position.y;
      const localPoint = {
        x: dx * cos + dy * sin,
        y: -dx * sin + dy * cos,
      };
      const nextSize = Math.max(1, (localPoint.x - localPoint.y) / 2);

      setIconEntities((current) =>
        current.map((item) => (item.id === iconEntity.id ? { ...item, size: nextSize } : item)),
      );
    };

    return (
      <Fragment key={iconEntity.id}>
        <CanvasIconNode
          icon={iconEntity}
          isSelected={isSelected}
          isLocked={isLayerLocked(iconLayer)}
          toolMode={toolMode}
          selectedStrokeWidth={getScreenInvariantSize(1.5)}
          dash={[getScreenInvariantSize(6), getScreenInvariantSize(4)]}
          onSelect={(event) => {
            event.cancelBubble = true;
            if (toolMode !== "select" || isLayerLocked(iconLayer)) return;
            setSelectionCycle(null);
            setSelectedIds((current) =>
              event.evt.shiftKey
                ? current.includes(iconEntity.id)
                  ? current.filter((id) => id !== iconEntity.id)
                  : [...current, iconEntity.id]
                : [iconEntity.id],
            );
            setEditingId(null);
            cancelTextEditing();
          }}
          onDragStart={(event) => {
            event.cancelBubble = true;
            beginHistoryAction();
          }}
          onDragMove={(event) => {
            event.cancelBubble = true;
            const nextPosition = {
              x: event.target.x(),
              y: event.target.y(),
            };
            setIconEntities((current) =>
              current.map((item) => (item.id === iconEntity.id ? { ...item, position: nextPosition } : item)),
            );
          }}
          onDragEnd={(event) => {
            event.cancelBubble = true;
            const nextPosition = {
              x: event.target.x(),
              y: event.target.y(),
            };
            setIconEntities((current) =>
              current.map((item) => (item.id === iconEntity.id ? { ...item, position: nextPosition } : item)),
            );
            finishHistoryAction();
          }}
        />
        {isSelected && (
          <Circle
            x={iconEntity.position.x}
            y={iconEntity.position.y}
            radius={getScreenInvariantSize(4)}
            fill="#ffffff"
            stroke="#1d5cab"
            strokeWidth={getScreenInvariantSize(1.5)}
            listening={false}
          />
        )}
        {isEditing && (
          <Circle
            x={scaleGripPoint.x}
            y={scaleGripPoint.y}
            radius={getScreenInvariantSize(GRIP_RADIUS)}
            fill="#ffffff"
            stroke="#d9480f"
            strokeWidth={getScreenInvariantSize(1.5)}
            hitStrokeWidth={getScreenInvariantSize(GRIP_HIT_STROKE)}
            draggable
            onMouseDown={(event) => {
              event.cancelBubble = true;
            }}
            onDragStart={(event) => {
              event.cancelBubble = true;
              beginHistoryAction();
            }}
            onDragMove={(event) => {
              event.cancelBubble = true;
              updateIconSizeFromGrip({
                x: event.target.x(),
                y: event.target.y(),
              });
            }}
            onDragEnd={(event) => {
              event.cancelBubble = true;
              updateIconSizeFromGrip({
                x: event.target.x(),
                y: event.target.y(),
              });
              finishHistoryAction();
            }}
          />
        )}
      </Fragment>
    );
  };

  const renderAutoDimensionPreview = (shape: Shape, offsetDistance: number) => {
    const definitions = buildAutoDimensionsForShape(shape, offsetDistance);
    if (definitions.length === 0) return null;

    return (
      <Fragment key={`${shape.id}-auto-dimension-preview`}>
        {definitions.map((definition, index) => {
          const geometry = getDimensionLayout(definition.start, definition.end, definition.offset);
          if (!geometry) return null;

          const measuredLength = getDistance(definition.start, definition.end);
          const dimensionText =
            definition.dimensionType === "radius"
              ? `R ${formatMeasurement(measuredLength)}`
              : formatMeasurement(measuredLength);
          const textAnchor = getDimensionTextAnchor(dimensionText);

          return (
            <Fragment key={`${shape.id}-auto-dimension-preview-${index}`}>
              <Line
                points={[
                  geometry.extensionStart.x,
                  geometry.extensionStart.y,
                  geometry.extensionStartOffset.x,
                  geometry.extensionStartOffset.y,
                ]}
                stroke={PREVIEW_STROKE_COLOR}
                strokeWidth={getScreenInvariantSize(1.5)}
                dash={[getScreenInvariantSize(8), getScreenInvariantSize(4)]}
              />
              <Line
                points={[
                  geometry.extensionEnd.x,
                  geometry.extensionEnd.y,
                  geometry.extensionEndOffset.x,
                  geometry.extensionEndOffset.y,
                ]}
                stroke={PREVIEW_STROKE_COLOR}
                strokeWidth={getScreenInvariantSize(1.5)}
                dash={[getScreenInvariantSize(8), getScreenInvariantSize(4)]}
              />
              <Line
                points={[
                  geometry.extensionStartOffset.x,
                  geometry.extensionStartOffset.y,
                  geometry.extensionEndOffset.x,
                  geometry.extensionEndOffset.y,
                ]}
                stroke={PREVIEW_STROKE_COLOR}
                strokeWidth={getScreenInvariantSize(1.5)}
                dash={[getScreenInvariantSize(8), getScreenInvariantSize(4)]}
              />
              {renderDimensionMarker(
                geometry.extensionStartOffset,
                { x: geometry.ux, y: geometry.uy },
                PREVIEW_STROKE_COLOR,
                `${shape.id}-auto-dimension-preview-${index}-start-marker`,
              )}
              {renderDimensionMarker(
                geometry.extensionEndOffset,
                { x: -geometry.ux, y: -geometry.uy },
                PREVIEW_STROKE_COLOR,
                `${shape.id}-auto-dimension-preview-${index}-end-marker`,
              )}
              <Text
                x={geometry.textPoint.x}
                y={geometry.textPoint.y}
                text={dimensionText}
                offsetX={textAnchor.offsetX}
                offsetY={textAnchor.offsetY}
                fontSize={textAnchor.fontSize}
                fontStyle="bold"
                fill={PREVIEW_TEXT_COLOR}
                rotation={geometry.textRotation}
                listening={false}
              />
            </Fragment>
          );
        })}
      </Fragment>
    );
  };

  const renderRoofComponentIcon = (mode: RoofComponentToolMode) => {
    const label = mode === "roofGronda" ? "E" : mode === "roofColmo" ? "C" : mode === "roofDiagonal" ? "H" : "R";
    return (
      <svg viewBox="0 0 32 32" aria-hidden="true" className="h-5 w-5 fill-none">
        <path
          d={mode === "roofDiagonal" ? "M7 25 25 7" : mode === "roofSide" ? "M16 6v20" : "M6 16h20"}
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
        />
        <text x="16" y="12" textAnchor="middle" className="fill-current text-[8px] font-bold">
          {label}
        </text>
      </svg>
    );
  };

  const isSurfaceFeatureToolMode = (mode: ToolMode): mode is SurfaceFeatureToolMode =>
    mode === "holePolyline" || mode === "holeCircle" || mode === "obstaclePolyline" || mode === "obstacleCircle";

  const getSurfaceFeatureToolLabel = (mode: SurfaceFeatureToolMode) =>
    mode === "holePolyline"
      ? "Crea foro"
      : mode === "holeCircle"
        ? "Crea foro circolare"
        : mode === "obstaclePolyline"
          ? "Ostacolo poligonale"
          : "Ostacolo circolare";

  const renderSurfaceFeatureToolIcon = (mode: SurfaceFeatureToolMode) => {
    if (mode === "holePolyline") {
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-none stroke-current">
          <path d="M4 6h16v12H4z" strokeWidth="1.8" />
          <path d="M9 10h6v4H9z" strokeWidth="1.8" />
        </svg>
      );
    }

    if (mode === "holeCircle") {
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-none stroke-current">
          <rect x="4" y="5" width="16" height="14" rx="1.5" strokeWidth="1.8" />
          <circle cx="12" cy="12" r="3.5" strokeWidth="1.8" />
        </svg>
      );
    }

    if (mode === "obstaclePolyline") {
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-none stroke-current">
          <path d="M6 18 4 9l8-5 8 5-2 9H6Z" strokeWidth="1.8" strokeLinejoin="round" />
          <path d="M8 16h8" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      );
    }

    return (
      <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-none stroke-current">
        <circle cx="12" cy="12" r="7" strokeWidth="1.8" />
        <path d="M8.5 15.5 15.5 8.5" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    );
  };

  const renderTransformIcon = (mode: "move" | "copy" | "explode" | "scale" | "rotate" | "offset") => {
    if (mode === "copy") {
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-none stroke-current">
          <rect x="9" y="9" width="10" height="10" rx="1.5" strokeWidth="2" />
          <path
            d="M6 15H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h9a1 1 0 0 1 1 1v1"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    }

    if (mode === "explode") {
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-none stroke-current">
          <path d="M5 12h4M15 12h4M12 5v4M12 15v4" strokeWidth="2" strokeLinecap="round" />
          <path d="M7 7l2.5 2.5M14.5 14.5 17 17M17 7l-2.5 2.5M9.5 14.5 7 17" strokeWidth="1.8" strokeLinecap="round" />
          <circle cx="12" cy="12" r="2" strokeWidth="1.8" />
        </svg>
      );
    }

    if (mode === "scale") {
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-none stroke-current">
          <path d="M5 19 19 5M13 5h6v6M11 19H5v-6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <rect x="7" y="7" width="10" height="10" rx="1.5" strokeWidth="1.8" />
        </svg>
      );
    }

    if (mode === "rotate") {
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-none stroke-current">
          <path d="M17 5h4v4M20 6.5A8 8 0 1 1 12 4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <circle cx="12" cy="12" r="2" strokeWidth="1.8" />
        </svg>
      );
    }

    if (mode === "offset") {
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-none stroke-current">
          <path d="M5 8h11a3 3 0 0 1 0 6H9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path
            d="M5 12h11a3 3 0 0 1 0 6H9"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity="0.55"
          />
          <path d="M6 16h3M7.5 14.5v3" strokeWidth="2" strokeLinecap="round" />
        </svg>
      );
    }

    return (
      <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-none stroke-current">
        <path
          d="M12 3v18M3 12h18M8 7l4-4 4 4M8 17l4 4 4-4M7 8l-4 4 4 4M17 8l4 4-4 4"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  };

  const projectCoordinates = (isAdmin ? adminProjectRequest.data : userProjectRequest.data)?.coordinates ?? null;
  const canvasAspectRatio = viewportSize.width / Math.max(1, viewportSize.height);

  return (
    <div className="relative flex h-full min-h-0 flex-col bg-[#ece7dc] p-4">
      <GoogleMapBackgroundDialog
        isOpen={isGoogleMapDialogOpen}
        projectCoordinates={projectCoordinates}
        canvasAspectRatio={canvasAspectRatio}
        onClose={() => setIsGoogleMapDialogOpen(false)}
        onCapture={handleGoogleMapCapture}
      />
      <BackgroundToolbar
        containerStyle={
          !isBackgroundToolbarOpen && backgroundToolbarWidth
            ? getFloatingPanelStyle("background", { width: `${backgroundToolbarWidth}px` })
            : getFloatingPanelStyle("background")
        }
        onActivate={() => bringFloatingPanelToFront("background")}
        isOpen={isBackgroundToolbarOpen}
        toolbarRef={backgroundToolbarRef}
        inputRef={backgroundInputRef}
        onUpload={handleBackgroundUpload}
        onCollapse={() => setIsBackgroundToolbarOpen(false)}
        onExpand={() => setIsBackgroundToolbarOpen(true)}
        onGoogleMap={() => {
          setIsGoogleMapDialogOpen(true);
          setBackgroundStatus(null);
        }}
        onOpenFile={() => backgroundInputRef.current?.click()}
        status={backgroundStatus}
        transparency={backgroundTransparency}
        onTransparencyChange={setBackgroundTransparency}
        hasBackgroundAsset={Boolean(backgroundAsset)}
        isBackgroundLoading={isBackgroundLoading}
        isReferenceMode={isBackgroundReferenceMode}
        hasReference={Boolean(backgroundAsset?.reference)}
        onToggleReference={() => {
          if (isBackgroundReferenceMode) {
            cancelBackgroundReference();
            return;
          }

          if (backgroundAsset?.reference) {
            setBackgroundReferenceInput({
              value: formatMeasurementValue(backgroundAsset.reference.realLength),
            });
            setSelectedIds([]);
            setEditingId(BACKGROUND_ID);
            return;
          }

          setIsBackgroundReferenceMode(true);
          setBackgroundReferenceLine(null);
          setBackgroundReferenceInput(null);
          setEditingId(null);
        }}
        onAlignHorizontal={() => alignBackgroundReference("horizontal")}
        onAlignVertical={() => alignBackgroundReference("vertical")}
        alignmentAngleDraft={backgroundAlignmentAngleDraft}
        onAlignmentAngleDraftChange={setBackgroundAlignmentAngleDraft}
        isCropMode={isBackgroundCropMode}
        onToggleCrop={() => {
          if (isBackgroundCropMode) {
            cancelBackgroundCrop();
            return;
          }

          setIsBackgroundCropMode(true);
          setBackgroundCropRect(null);
          setIsBackgroundReferenceMode(false);
          setBackgroundReferenceLine(null);
        }}
        onClearBackground={() => {
          if (typeof window !== "undefined" && !window.confirm("vuoi Deletere lo sfondo?")) {
            return;
          }
          clearBackground();
        }}
      />
      <input
        ref={canvasObjectsInputRef}
        type="file"
        accept="application/json,.json"
        className="hidden"
        style={{ display: "none" }}
        tabIndex={-1}
        onChange={handleImportCanvasObjects}
      />

      <IconPickerPanel
        isOpen={isIconPickerOpen}
        panelStyle={getFloatingPanelStyle("iconPicker")}
        mainColor={iconToolColor}
        onActivate={() => bringFloatingPanelToFront("iconPicker")}
        onClose={() => setIsIconPickerOpen(false)}
        onPick={handleIconPicked}
      />

      {isToolbarOpen ? (
        <aside
          className="absolute left-7 top-7 z-20 flex w-[84px] flex-col gap-2 rounded-[6px] border border-[rgba(74,58,31,0.12)] bg-[rgba(245,240,230,0.94)] px-2 pb-3 pt-3 shadow-[0_20px_40px_rgba(58,45,20,0.14)] backdrop-blur-[10px]"
          style={getFloatingPanelStyle("drawing")}
          aria-label="Drawing Toolbar"
          onMouseDownCapture={() => bringFloatingPanelToFront("drawing")}
          onFocusCapture={() => bringFloatingPanelToFront("drawing")}
          onClickCapture={(event) => {
            const target = event.target as HTMLElement | null;
            if (target?.closest("button")) {
              setIsBackgroundToolbarOpen(false);
            }
          }}
        >
          <div className="flex items-center justify-center">
            <button
              type="button"
              className="inline-flex h-5 w-5 cursor-pointer items-center justify-center rounded-full border-0 bg-[rgba(33,27,18,0.09)] text-[#332917]"
              aria-label="Minimize toolbar"
              title="Minimize toolbar"
              onClick={() => {
                setOpenToolGroupMenu(null);
                setIsToolbarOpen(false);
              }}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true" className="h-[14px] w-[14px] fill-current">
                <path d="M4 7h16v2H4V7Zm0 5h16v2H4v-2Zm0 5h16v2H4v-2Z" />
              </svg>
            </button>
          </div>

          <div className="flex flex-col gap-1 [&_svg]:scale-[1.3]">
            <button
              type="button"
              className={`inline-flex h-10 w-10 self-center cursor-pointer items-center justify-center rounded-md border transition active:scale-110 ${getToolbarToneClass(
                toolMode === "select" ? "white" : "white",
                toolMode === "select",
              )}`}
              aria-label="Select"
              title="Select (Space)"
              onMouseEnter={scheduleToolGroupClose}
              onClick={() => handleToolChange("select")}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-current">
                <path d="M5 3v15l4.6-4.3 3.3 7.3 2.4-1.1-3.2-7.2H18L5 3Z" />
              </svg>
            </button>

            <div
              className="relative self-center"
              onMouseEnter={() => {
                clearPendingToolGroupClose();
                setOpenToolGroupMenu("roofCreation");
              }}
            >
              <button
                type="button"
                className={`relative inline-flex h-10 w-10 self-center cursor-pointer items-center justify-center rounded-md border transition active:scale-110 ${getToolbarToneClass(
                  "gray",
                  isPrimaryRoofCreationToolActive,
                )}`}
                aria-label={getRoofCreationToolLabel(primaryRoofCreationTool)}
                title={getRoofCreationToolLabel(primaryRoofCreationTool)}
                onClick={handlePrimaryRoofCreationToolClick}
              >
                {primaryRoofCreationTool === "roof" && (
                  <img src={hipRoofIconUrl} alt="" aria-hidden="true" className="h-6 w-6 object-contain" />
                )}
                {primaryRoofCreationTool === "multiGableRoof" && (
                  <img src={multiGableRoofIconUrl} alt="" aria-hidden="true" className="h-6 w-6 object-contain" />
                )}
                {primaryRoofCreationTool === "roof" ? (
                  <svg viewBox="0 0 586 360" aria-hidden="true" className="hidden h-5 w-5">
                    <g transform="translate(0,360) scale(0.1,-0.1)" fill="currentColor" stroke="none">
                      <path d="M2472 2611 c-1019 -401 -1855 -730 -1857 -733 -2 -2 8 -116 23 -252 28 -248 28 -248 450 -696 422 -449 422 -449 485 -421 34 15 341 157 682 314 341 157 625 287 631 289 9 2 266 -222 644 -562 439 -394 405 -373 494 -325 45 24 333 175 641 335 308 161 562 294 564 296 5 5 76 391 86 465 5 42 4 48 -20 63 -14 9 -31 14 -37 10 -6 -4 -8 -3 -4 4 3 6 -129 110 -324 252 -181 133 -330 244 -330 248 0 4 150 76 333 160 333 152 333 152 372 392 25 152 36 243 30 249 -17 15 -997 641 -1004 641 -3 -1 -840 -329 -1859 -729z m1844 654 c-3 -30 -11 -77 -17 -104 -10 -49 -10 -49 -462 -230 -249 -99 -459 -182 -467 -183 -8 -2 -17 -6 -20 -9 -4 -6 -1271 -517 -1295 -524 -5 -1 -26 -9 -45 -18 -227 -100 -241 -104 -755 -198 -442 -80 -553 -99 -505 -82 8 2 22 7 30 10 8 4 17 7 20 7 3 1 10 4 15 8 24 15 119 47 127 42 4 -3 8 0 8 6 0 7 7 9 16 6 8 -3 13 -2 10 3 -5 7 20 19 54 25 3 1 10 5 17 9 19 12 145 58 173 64 13 2 27 8 30 12 9 14 91 41 102 35 6 -4 8 -3 5 2 -3 6 29 22 71 38 42 15 84 30 92 33 8 4 17 7 20 8 3 1 19 7 35 14 17 8 41 18 55 23 14 6 33 14 43 20 10 5 22 6 27 3 4 -3 10 -2 12 3 1 4 27 18 56 29 29 12 49 17 46 11 -4 -7 6 -2 23 10 17 12 37 22 45 22 8 0 19 5 26 12 7 7 12 9 12 5 0 -4 10 -2 23 5 12 7 24 13 27 14 8 1 43 15 75 29 81 35 109 45 113 40 3 -3 13 2 23 11 10 9 25 14 34 10 9 -3 13 -2 10 4 -3 5 14 15 37 22 24 7 54 18 67 25 14 6 28 10 33 7 4 -3 8 0 8 7 0 7 3 9 7 6 3 -4 17 0 31 9 13 9 22 12 18 6 -3 -6 4 -5 19 1 14 7 25 15 25 20 0 4 11 7 24 7 13 0 26 4 28 10 2 5 12 11 23 13 11 2 40 13 65 24 25 11 55 19 67 18 12 -1 20 2 17 7 -5 8 35 25 45 19 2 -2 7 2 11 9 4 6 17 12 28 13 12 1 22 4 22 8 0 5 25 14 90 34 3 1 21 9 40 17 19 9 38 14 42 12 5 -3 8 -1 8 4 0 5 19 15 43 22 23 8 47 17 53 22 6 4 21 10 35 12 13 3 26 5 29 6 3 2 8 2 13 1 4 0 7 3 7 7 0 7 95 46 110 46 3 -1 26 10 53 23 26 13 52 21 57 18 6 -3 10 -1 10 4 0 6 3 10 8 9 4 -1 17 3 30 10 12 7 26 10 31 7 5 -3 11 0 13 5 2 6 10 12 18 14 8 2 37 13 63 24 27 12 52 22 57 22 4 0 21 7 36 15 16 8 36 15 45 15 8 0 19 4 25 8 5 5 36 19 69 31 33 13 69 27 80 31 19 8 55 21 65 25 3 1 16 6 30 12 14 6 45 19 70 28 60 23 91 35 113 47 10 5 22 6 27 3 6 -4 10 -1 10 4 0 6 8 11 18 11 10 0 22 3 26 8 7 6 83 40 95 41 2 1 1 -24 -3 -54z m111 3 c18 -11 33 -24 33 -30 0 -5 4 -7 8 -4 5 3 24 -8 43 -23 19 -16 44 -31 57 -35 12 -4 22 -12 22 -17 0 -5 5 -9 12 -9 6 0 25 -10 42 -23 17 -12 43 -29 59 -36 15 -8 27 -18 27 -23 0 -4 6 -8 14 -8 7 0 20 -7 27 -16 7 -9 23 -20 34 -24 12 -5 82 -48 156 -97 74 -48 139 -90 144 -93 24 -12 135 -83 135 -87 0 -2 -17 4 -37 13 -57 23 -855 341 -877 348 -19 7 -19 16 -1 121 3 17 8 45 11 63 5 34 5 34 31 16 15 -9 41 -25 60 -36z m387 -371 c506 -204 506 -204 -61 -453 -312 -137 -571 -248 -577 -246 -6 2 -238 93 -516 202 -278 110 -518 204 -535 210 -30 11 -30 11 25 31 30 10 298 117 595 238 297 120 545 219 552 220 7 0 239 -90 517 -202z m512 -234 c-6 -22 -23 -116 -31 -165 -12 -88 -16 -114 -20 -128 -2 -8 -7 -33 -10 -55 -14 -102 12 -84 -354 -252 -196 -90 -338 -150 -345 -145 -47 28 -366 267 -359 270 4 1 256 112 558 246 303 134 554 244 558 245 5 1 6 -7 3 -16z m-2256 -98 c-8 -38 -20 -103 -70 -390 -11 -66 -23 -131 -26 -145 -2 -14 -7 -41 -10 -60 -3 -19 -13 -78 -24 -130 -10 -52 -22 -120 -25 -150 -4 -30 -8 -56 -10 -58 -6 -6 -1408 -622 -1411 -620 -2 3 13 67 81 348 14 58 28 114 30 125 2 11 14 61 26 110 11 50 22 97 24 105 2 8 11 47 20 85 9 39 18 77 20 85 2 8 9 38 15 65 7 28 14 64 17 80 3 17 9 37 13 45 7 12 1330 553 1337 547 1 -2 -2 -20 -7 -42z m500 -319 c623 -474 559 -419 550 -468 -5 -24 -13 -88 -20 -143 -6 -55 -13 -116 -15 -135 -3 -19 -18 -147 -35 -285 -17 -137 -32 -263 -34 -280 -3 -16 -8 -64 -12 -105 -4 -41 -10 -83 -13 -93 -6 -15 -103 64 -531 428 -289 246 -529 450 -533 454 -4 4 17 147 47 317 30 170 57 329 61 354 8 51 11 64 24 135 6 28 13 68 16 90 3 22 8 52 11 67 6 32 -58 76 484 -336z m102 135 c497 -196 507 -200 594 -267 49 -37 91 -70 94 -73 3 -3 28 -22 57 -41 28 -19 49 -38 46 -44 -3 -5 -1 -6 5 -2 5 3 31 -10 56 -29 26 -19 49 -35 52 -35 2 0 30 -19 61 -42 116 -88 129 -98 176 -130 26 -18 47 -36 47 -40 0 -5 5 -8 10 -8 9 0 89 -55 139 -96 9 -7 41 -31 71 -53 30 -22 60 -44 65 -48 6 -4 29 -20 53 -36 23 -15 42 -29 42 -32 0 -3 -21 4 -47 14 -1150 450 -1015 381 -1532 775 -560 426 -507 385 -500 386 3 0 233 -90 511 -199z m-1946 -328 c-4 -9 -18 -62 -31 -117 -47 -200 -76 -322 -95 -401 -11 -44 -22 -93 -25 -110 -3 -16 -7 -34 -9 -40 -2 -5 -6 -23 -10 -40 -4 -16 -13 -55 -21 -85 -8 -30 -17 -71 -20 -90 -3 -19 -10 -48 -15 -65 -5 -16 -11 -43 -14 -59 -3 -16 -7 -30 -9 -32 -2 -2 -48 42 -103 99 -337 347 -726 738 -730 734 -3 -2 138 -150 314 -328 525 -534 515 -524 518 -567 11 -148 25 -424 22 -428 -4 -3 -662 690 -780 821 -36 41 -36 41 -61 265 -14 124 -25 233 -25 242 1 12 13 20 37 25 138 26 1039 191 1050 192 8 1 11 -5 7 -16z m2997 -465 c312 -122 567 -224 567 -227 0 -3 -290 -151 -645 -329 -551 -275 -645 -319 -641 -300 3 13 8 48 11 78 4 30 9 64 10 75 2 11 7 47 10 82 3 34 8 71 10 82 3 17 27 209 41 331 3 25 9 77 14 115 10 74 15 119 25 225 7 74 11 90 22 90 4 0 263 -100 576 -222z m-1827 -215 c-4 -131 -9 -239 -11 -241 -2 -2 -309 -145 -682 -317 -678 -312 -678 -312 -684 -276 -8 41 -23 307 -24 395 0 59 0 59 695 366 382 169 699 308 704 309 5 1 6 -101 2 -236z m102 170 c37 -32 76 -66 87 -76 11 -10 69 -60 129 -110 59 -51 147 -126 195 -167 48 -41 112 -96 142 -121 29 -26 106 -91 170 -145 64 -55 138 -117 165 -139 113 -93 105 -62 79 -305 -13 -118 -25 -228 -27 -243 -3 -27 -33 -2 -520 432 -516 461 -516 461 -512 499 2 20 6 125 8 232 3 107 5 196 5 198 3 8 20 -4 79 -55z m2296 -265 c-8 -40 -16 -84 -19 -98 -3 -14 -10 -56 -16 -95 -5 -38 -14 -83 -19 -100 -5 -16 -11 -50 -14 -74 -3 -24 -7 -45 -8 -46 -18 -14 -1260 -656 -1263 -653 -3 2 0 44 5 94 11 100 15 135 31 284 6 58 11 106 12 106 2 1 1298 651 1303 653 1 1 -5 -32 -12 -71z" />
                    </g>
                  </svg>
                ) : primaryRoofCreationTool === "gableRoof" ? (
                  <img src={twoPitchRoofIconUrl} alt="" aria-hidden="true" className="h-6 w-6 object-contain" />
                ) : primaryRoofCreationTool === "curvedRoof" ? (
                  <img src={curvedRoofIconUrl} alt="" aria-hidden="true" className="h-6 w-6 object-contain" />
                ) : primaryRoofCreationTool === "singlePitchedRoof" ? (
                  <img src={singlePitchedRoofIconUrl} alt="" aria-hidden="true" className="h-6 w-6 object-contain" />
                ) : primaryRoofCreationTool === "multiShedRoof" ? (
                  <img src={multiShedRoofIconUrl} alt="" aria-hidden="true" className="h-6 w-6 object-contain" />
                ) : primaryRoofCreationTool === "polyline" ? (
                  <img src={flatRoofIconUrl} alt="" aria-hidden="true" className="h-6 w-6 object-contain" />
                ) : primaryRoofCreationTool === "rectangle" ? (
                  <img src={squaredFlatRoofIconUrl} alt="" aria-hidden="true" className="h-6 w-6 object-contain" />
                ) : null}
              </button>

              {openToolGroupMenu === "roofCreation" && (
                <div className="absolute left-full top-1/2 z-10 ml-2 flex -translate-y-1/2 gap-2 rounded-md border border-[rgba(74,58,31,0.12)] bg-[rgba(245,240,230,0.98)] p-2 shadow-[0_12px_24px_rgba(58,45,20,0.16)] [&_button]:h-20 [&_button]:w-20 [&_img]:h-12 [&_img]:w-12 [&_svg]:h-8 [&_svg]:w-8 [&_span]:text-sm">
                  <button
                    type="button"
                    className={`inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border transition active:scale-110 ${getToolbarToneClass(
                      "gray",
                      toolMode === "polyline",
                    )}`}
                    aria-label="Custom Flat Room"
                    title="Custom Flat Room (P)"
                    onClick={() => handleRoofCreationToolSelection("polyline")}
                  >
                    <img src={flatRoofIconUrl} alt="" aria-hidden="true" className="h-6 w-6 object-contain" />
                  </button>
                  <button
                    type="button"
                    className={`inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border transition active:scale-110 ${getToolbarToneClass(
                      "gray",
                      toolMode === "rectangle",
                    )}`}
                    aria-label="Flat Roof"
                    title="Flat Roof (R)"
                    onClick={() => handleRoofCreationToolSelection("rectangle")}
                  >
                    <img src={squaredFlatRoofIconUrl} alt="" aria-hidden="true" className="h-6 w-6 object-contain" />
                  </button>
                  <button
                    type="button"
                    className={`inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border transition active:scale-110 ${getToolbarToneClass(
                      "gray",
                      toolMode === "curvedRoof",
                    )}`}
                    aria-label="Curved Roof"
                    title="Curved Roof"
                    onClick={() => handleRoofCreationToolSelection("curvedRoof")}
                  >
                    <img src={curvedRoofIconUrl} alt="" aria-hidden="true" className="h-6 w-6 object-contain" />
                  </button>
                  <button
                    type="button"
                    className={`inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border transition active:scale-110 ${getToolbarToneClass(
                      "gray",
                      toolMode === "roof",
                    )}`}
                    aria-label="Automatic Pitched Roof"
                    title="Automatic Pitched Roof"
                    onClick={() => handleRoofCreationToolSelection("roof")}
                  >
                    <img src={hipRoofIconUrl} alt="" aria-hidden="true" className="h-6 w-6 object-contain" />
                  </button>
                  <button
                    type="button"
                    className={`inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border transition active:scale-110 ${getToolbarToneClass(
                      "gray",
                      toolMode === "multiGableRoof",
                    )}`}
                    aria-label="Multi Gable Roof"
                    title="Multi Gable Roof"
                    onClick={() => handleRoofCreationToolSelection("multiGableRoof")}
                  >
                    <img src={multiGableRoofIconUrl} alt="" aria-hidden="true" className="h-6 w-6 object-contain" />
                  </button>
                  <button
                    type="button"
                    className={`inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border transition active:scale-110 ${getToolbarToneClass(
                      "gray",
                      toolMode === "singlePitchedRoof",
                    )}`}
                    aria-label="Single Pitched Roof"
                    title="Single Pitched Roof"
                    onClick={() => handleRoofCreationToolSelection("singlePitchedRoof")}
                  >
                    <img src={singlePitchedRoofIconUrl} alt="" aria-hidden="true" className="h-6 w-6 object-contain" />
                  </button>
                  <button
                    type="button"
                    className={`inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border transition active:scale-110 ${getToolbarToneClass(
                      "gray",
                      toolMode === "multiShedRoof",
                    )}`}
                    aria-label="Multi Shed Roof"
                    title="Multi Shed Roof"
                    onClick={() => handleRoofCreationToolSelection("multiShedRoof")}
                  >
                    <img src={multiShedRoofIconUrl} alt="" aria-hidden="true" className="h-6 w-6 object-contain" />
                  </button>
                  <button
                    type="button"
                    className={`inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border transition active:scale-110 ${getToolbarToneClass(
                      "gray",
                      toolMode === "gableRoof",
                    )}`}
                    aria-label="Single Gable Roof"
                    title="Single Gable Roof"
                    onClick={() => handleRoofCreationToolSelection("gableRoof")}
                  >
                    <img src={twoPitchRoofIconUrl} alt="" aria-hidden="true" className="h-6 w-6 object-contain" />
                  </button>
                </div>
              )}
            </div>

            <button
              type="button"
              className={`hidden h-10 w-10 self-center cursor-pointer items-center justify-center rounded-md border transition active:scale-110 ${getToolbarToneClass(
                "gray",
                toolMode === "gableRoof",
              )}`}
              aria-label="Single Gable Roof"
              title="Single Gable Roof"
              onMouseEnter={scheduleToolGroupClose}
              onClick={() => handleToolChange("gableRoof")}
            >
              <img src={twoPitchRoofIconUrl} alt="" aria-hidden="true" className="h-6 w-6 object-contain" />
            </button>

            <div
              className="relative self-center"
              onMouseEnter={() => {
                clearPendingToolGroupClose();
                setOpenToolGroupMenu("roofComponent");
              }}
            >
              <button
                type="button"
                className={`inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border transition active:scale-110 ${getToolbarToneClass(
                  "gray",
                  isRoofComponentToolMode(toolMode) || toolMode === "roofHipValley",
                )}`}
                aria-label={getRoofComponentToolLabel(primaryRoofComponentTool)}
                title={getRoofComponentToolLabel(primaryRoofComponentTool)}
                onClick={handlePrimaryRoofComponentToolClick}
              >
                {renderRoofComponentIcon(primaryRoofComponentTool)}
              </button>

              {openToolGroupMenu === "roofComponent" && (
                <div className="absolute left-full top-1/2 z-10 ml-2 flex -translate-y-1/2 gap-2 rounded-md border border-[rgba(74,58,31,0.12)] bg-[rgba(245,240,230,0.98)] p-2 shadow-[0_12px_24px_rgba(58,45,20,0.16)] [&_button]:h-20 [&_button]:w-20 [&_img]:h-12 [&_img]:w-12 [&_svg]:h-8 [&_svg]:w-8 [&_span]:text-sm">
                  {(["roofGronda", "roofColmo", "roofDiagonal", "roofSide"] as RoofComponentToolMode[]).map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      className={`inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border transition active:scale-110 ${getToolbarToneClass(
                        "gray",
                        toolMode === mode,
                      )}`}
                      aria-label={getRoofComponentToolLabel(mode)}
                      title={getRoofComponentToolLabel(mode)}
                      onClick={() => handleRoofComponentToolSelection(mode)}
                    >
                      {renderRoofComponentIcon(mode)}
                    </button>
                  ))}
                  <button
                    type="button"
                    className={`inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border text-[10px] font-bold transition active:scale-110 ${getToolbarToneClass(
                      "gray",
                      toolMode === "roofHipValley",
                    )}`}
                    aria-label="Automatic Hip/Valley"
                    title="Automatic Hip/Valley"
                    onClick={() => handleToolChange("roofHipValley")}
                  >
                    HV
                  </button>
                </div>
              )}
            </div>

            <div
              className="relative self-center"
              onMouseEnter={() => {
                clearPendingToolGroupClose();
                setOpenToolGroupMenu("surfaceFeature");
              }}
            >
              <button
                type="button"
                className={`inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border transition active:scale-110 ${getToolbarToneClass(
                  "gray",
                  isSurfaceFeatureToolMode(toolMode),
                )}`}
                aria-label={getSurfaceFeatureToolLabel(primarySurfaceFeatureTool)}
                title={getSurfaceFeatureToolLabel(primarySurfaceFeatureTool)}
                onClick={handlePrimarySurfaceFeatureToolClick}
              >
                {renderSurfaceFeatureToolIcon(primarySurfaceFeatureTool)}
              </button>

              {openToolGroupMenu === "surfaceFeature" && (
                <div className="absolute left-full top-1/2 z-10 ml-2 flex -translate-y-1/2 gap-2 rounded-md border border-[rgba(74,58,31,0.12)] bg-[rgba(245,240,230,0.98)] p-2 shadow-[0_12px_24px_rgba(58,45,20,0.16)] [&_button]:h-20 [&_button]:w-20 [&_img]:h-12 [&_img]:w-12 [&_svg]:h-8 [&_svg]:w-8 [&_span]:text-sm">
                  {(
                    ["holePolyline", "holeCircle", "obstaclePolyline", "obstacleCircle"] as SurfaceFeatureToolMode[]
                  ).map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      className={`inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border transition active:scale-110 ${getToolbarToneClass(
                        "gray",
                        toolMode === mode,
                      )}`}
                      aria-label={getSurfaceFeatureToolLabel(mode)}
                      title={getSurfaceFeatureToolLabel(mode)}
                      onClick={() => handleSurfaceFeatureToolSelection(mode)}
                    >
                      {renderSurfaceFeatureToolIcon(mode)}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div
              className="relative self-center"
              onMouseEnter={() => {
                clearPendingToolGroupClose();
                setOpenToolGroupMenu("lifeline");
              }}
            >
              <button
                type="button"
                className={`inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border transition active:scale-110 ${getToolbarToneClass(
                  "green",
                  isPrimaryLifeLineToolActive,
                )}`}
                aria-label={getAnchorToolLabel(primaryLifeLineTool)}
                title={getAnchorToolLabel(primaryLifeLineTool)}
                onClick={handlePrimaryLifeLineToolClick}
              >
                {renderAnchorToolIcon(primaryLifeLineTool)}
              </button>

              {openToolGroupMenu === "lifeline" && (
                <div className="absolute left-full top-1/2 z-10 ml-2 flex -translate-y-1/2 gap-2 rounded-md border border-[rgba(74,58,31,0.12)] bg-[rgba(245,240,230,0.98)] p-2 shadow-[0_12px_24px_rgba(58,45,20,0.16)] [&_button]:h-20 [&_button]:w-20 [&_svg]:h-8 [&_svg]:w-8 [&_span]:text-sm">
                  <button
                    type="button"
                    className={`inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border transition active:scale-110 ${getToolbarToneClass(
                      "green",
                      toolMode === "singleLifeLine",
                    )}`}
                    aria-label="Single Life Line"
                    title="Single Life Line"
                    onClick={() => handleLifeLineToolSelection("singleLifeLine")}
                  >
                    {renderAnchorToolIcon("singleLifeLine")}
                  </button>

                  <button
                    type="button"
                    className={`inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border transition active:scale-110 ${getToolbarToneClass(
                      "green",
                      toolMode === "automaticLifeLine",
                    )}`}
                    aria-label="Automatic Life Line"
                    title="Automatic Life Line"
                    onClick={() => handleLifeLineToolSelection("automaticLifeLine")}
                  >
                    {renderAnchorToolIcon("automaticLifeLine")}
                  </button>
                </div>
              )}
            </div>

            <div
              className="relative self-center"
              onMouseEnter={() => {
                clearPendingToolGroupClose();
                setOpenToolGroupMenu("anchor");
              }}
            >
              <button
                type="button"
                className={`inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border transition active:scale-110 ${getToolbarToneClass(
                  "green",
                  isPrimaryAnchorToolActive,
                )}`}
                aria-label={getAnchorToolLabel(primaryAnchorTool)}
                title={
                  primaryAnchorTool === "singlePointCircle"
                    ? "Insert Anchor Point (H)"
                    : getAnchorToolLabel(primaryAnchorTool)
                }
                onClick={handlePrimaryAnchorToolClick}
              >
                {renderAnchorToolIcon(primaryAnchorTool)}
              </button>

              {openToolGroupMenu === "anchor" && (
                <div className="absolute left-full top-1/2 z-10 ml-2 flex -translate-y-1/2 gap-2 rounded-md border border-[rgba(74,58,31,0.12)] bg-[rgba(245,240,230,0.98)] p-2 shadow-[0_12px_24px_rgba(58,45,20,0.16)] [&_button]:h-20 [&_button]:w-20 [&_img]:h-12 [&_img]:w-12 [&_svg]:h-8 [&_svg]:w-8 [&_span]:text-sm">
                  <button
                    type="button"
                    className={`inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border transition active:scale-110 ${getToolbarToneClass(
                      "green",
                      toolMode === "singlePointCircle",
                    )}`}
                    aria-label="Insert Anchor Point"
                    title="Insert Anchor Point (H)"
                    onClick={() => handleAnchorToolSelection("singlePointCircle")}
                  >
                    {renderAnchorToolIcon("singlePointCircle")}
                  </button>

                  <button
                    type="button"
                    className={`inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border transition active:scale-110 ${getToolbarToneClass(
                      "green",
                      toolMode === "pointSeries",
                    )}`}
                    aria-label="Single Anchor Points in Series"
                    title="Single Anchor Points in Series"
                    onClick={() => handleAnchorToolSelection("pointSeries")}
                  >
                    {renderAnchorToolIcon("pointSeries")}
                  </button>
                </div>
              )}
            </div>

            <div
              className="relative self-center"
              onMouseEnter={() => {
                clearPendingToolGroupClose();
                setOpenToolGroupMenu("dimension");
              }}
            >
              <button
                type="button"
                className={`inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border transition active:scale-110 ${getToolbarToneClass(
                  "red",
                  primaryDimensionTool === "dimension" ? toolMode === "dimension" : toolMode === "dimensionObject",
                )}`}
                aria-label={primaryDimensionTool === "dimension" ? "Dimension" : "Dimension object"}
                title={primaryDimensionTool === "dimension" ? "Dimension (D)" : "Dimension object"}
                onClick={handlePrimaryDimensionToolClick}
              >
                {primaryDimensionTool === "dimension" ? (
                  <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-none">
                    <path d="M6 6v12M18 6v12M6 12h12" stroke="currentColor" strokeWidth="2" />
                    <path d="M9 9 6 12l3 3M15 9l3 3-3 3" stroke="currentColor" strokeWidth="2" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-none">
                    <rect x="5" y="6" width="8" height="6" rx="1" stroke="currentColor" strokeWidth="1.8" />
                    <path d="M4 16h14M7 13v6M15 13v6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                    <path
                      d="M7 16l-2 2 2 2M15 16l2 2-2 2"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </button>

              {openToolGroupMenu === "dimension" && (
                <div className="absolute left-full top-1/2 z-10 ml-2 flex -translate-y-1/2 gap-2 rounded-md border border-[rgba(74,58,31,0.12)] bg-[rgba(245,240,230,0.98)] p-2 shadow-[0_12px_24px_rgba(58,45,20,0.16)] [&_button]:h-20 [&_button]:w-20 [&_img]:h-12 [&_img]:w-12 [&_svg]:h-8 [&_svg]:w-8 [&_span]:text-sm">
                  <button
                    type="button"
                    className={`inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border transition active:scale-110 ${getToolbarToneClass(
                      "red",
                      toolMode === "dimension",
                    )}`}
                    aria-label="Dimension"
                    title="Dimension (D)"
                    onClick={() => handleDimensionToolSelection("dimension")}
                  >
                    <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-none">
                      <path d="M6 6v12M18 6v12M6 12h12" stroke="currentColor" strokeWidth="2" />
                      <path d="M9 9 6 12l3 3M15 9l3 3-3 3" stroke="currentColor" strokeWidth="2" />
                    </svg>
                  </button>

                  <button
                    type="button"
                    className={`inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border transition active:scale-110 ${getToolbarToneClass(
                      "red",
                      toolMode === "dimensionObject",
                    )}`}
                    aria-label="Dimension object"
                    title="Dimension object"
                    onClick={() => handleDimensionToolSelection("dimensionObject")}
                  >
                    <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-none">
                      <rect x="5" y="6" width="8" height="6" rx="1" stroke="currentColor" strokeWidth="1.8" />
                      <path d="M4 16h14M7 13v6M15 13v6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                      <path
                        d="M7 16l-2 2 2 2M15 16l2 2-2 2"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              )}
            </div>

            <button
              type="button"
              className={`inline-flex h-10 w-10 self-center cursor-pointer items-center justify-center rounded-md border transition active:scale-110 ${getToolbarToneClass(
                "blue",
                toolMode === "ruler",
              )}`}
              aria-label="Ruler"
              title="Ruler (Shift+R)"
              onMouseEnter={scheduleToolGroupClose}
              onClick={() => handleToolChange("ruler")}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-none stroke-current">
                <path d="M4 15.5 15.5 4 20 8.5 8.5 20H4v-4.5Z" strokeWidth="1.8" strokeLinejoin="round" />
                <path d="M9 7.5 16.5 15M7.5 9l2-2M10.5 12l2-2M13.5 15l2-2" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            </button>

            <div
              className="relative self-center"
              onMouseEnter={() => {
                clearPendingToolGroupClose();
                setOpenToolGroupMenu("curve");
              }}
            >
              <button
                type="button"
                className={`inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border transition active:scale-110 ${getToolbarToneClass(
                  primaryCurveTool === "buildPolyline"
                    ? "teal"
                    : primaryCurveTool === "text"
                      ? "white"
                      : primaryCurveTool === "icon"
                        ? "white"
                        : primaryCurveTool === "hatch"
                          ? "gray"
                          : "blue",
                  toolMode === primaryCurveTool,
                )}`}
                aria-label={
                  primaryCurveTool === "buildPolyline"
                    ? "Build Polyline"
                    : primaryCurveTool === "hatch"
                      ? "Hatch"
                      : primaryCurveTool === "circle"
                        ? "Circle"
                        : primaryCurveTool === "arc"
                          ? "Arc"
                          : primaryCurveTool === "icon"
                            ? "Icons"
                            : "Text"
                }
                title={
                  primaryCurveTool === "buildPolyline"
                    ? "Build Polyline"
                    : primaryCurveTool === "hatch"
                      ? "Hatch (Shift+H)"
                      : primaryCurveTool === "circle"
                        ? "Circle (C)"
                        : primaryCurveTool === "arc"
                          ? "Arc"
                          : primaryCurveTool === "icon"
                            ? "Icons"
                            : "Text"
                }
                onClick={handlePrimaryCurveToolClick}
              >
                {primaryCurveTool === "buildPolyline" ? (
                  <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-none stroke-current">
                    <path d="M4 17 8.5 9.5 13 13l7-8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <circle cx="4" cy="17" r="1.8" fill="currentColor" stroke="none" />
                    <circle cx="8.5" cy="9.5" r="1.8" fill="currentColor" stroke="none" />
                    <circle cx="13" cy="13" r="1.8" fill="currentColor" stroke="none" />
                    <circle cx="20" cy="5" r="1.8" fill="currentColor" stroke="none" />
                  </svg>
                ) : primaryCurveTool === "hatch" ? (
                  <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-none stroke-current">
                    <path d="M5 5h14v14H5z" strokeWidth="1.8" />
                    <path d="m7 17 10-10M7 12l5-5M12 17l5-5" strokeWidth="1.8" strokeLinecap="round" />
                  </svg>
                ) : primaryCurveTool === "circle" ? (
                  <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-none">
                    <circle cx="12" cy="12" r="7" stroke="currentColor" strokeWidth="2" />
                  </svg>
                ) : primaryCurveTool === "arc" ? (
                  <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-none stroke-current">
                    <path d="M5 15a7 7 0 0 1 14 0" strokeWidth="2" strokeLinecap="round" />
                    <circle cx="5" cy="15" r="1.5" fill="currentColor" stroke="none" />
                    <circle cx="19" cy="15" r="1.5" fill="currentColor" stroke="none" />
                  </svg>
                ) : primaryCurveTool === "icon" ? (
                  <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-none stroke-current">
                    <path
                      d="M12 4l2.1 4.3 4.7.7-3.4 3.3.8 4.7L12 14.8 7.8 17l.8-4.7L5.2 9l4.7-.7L12 4Z"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : (
                  <span className="text-[16px] font-bold leading-none">T</span>
                )}
              </button>

              {openToolGroupMenu === "curve" && (
                <div className="absolute left-full top-1/2 z-10 ml-2 flex -translate-y-1/2 gap-2 rounded-md border border-[rgba(74,58,31,0.12)] bg-[rgba(245,240,230,0.98)] p-2 shadow-[0_12px_24px_rgba(58,45,20,0.16)] [&_button]:h-20 [&_button]:w-20 [&_img]:h-12 [&_img]:w-12 [&_svg]:h-8 [&_svg]:w-8 [&_span]:text-sm">
                  <button
                    type="button"
                    className={`inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border transition active:scale-110 ${getToolbarToneClass(
                      "teal",
                      toolMode === "buildPolyline",
                    )}`}
                    aria-label="Build Polyline"
                    title="Build Polyline"
                    onClick={() => handleCurveToolSelection("buildPolyline")}
                  >
                    <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-none stroke-current">
                      <path d="M4 17 8.5 9.5 13 13l7-8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <circle cx="4" cy="17" r="1.8" fill="currentColor" stroke="none" />
                      <circle cx="8.5" cy="9.5" r="1.8" fill="currentColor" stroke="none" />
                      <circle cx="13" cy="13" r="1.8" fill="currentColor" stroke="none" />
                      <circle cx="20" cy="5" r="1.8" fill="currentColor" stroke="none" />
                    </svg>
                  </button>

                  <button
                    type="button"
                    className={`inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border transition active:scale-110 ${getToolbarToneClass(
                      "gray",
                      toolMode === "hatch",
                    )}`}
                    aria-label="Hatch"
                    title="Hatch (Shift+H)"
                    onClick={() => handleCurveToolSelection("hatch")}
                  >
                    <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-none stroke-current">
                      <path d="M5 5h14v14H5z" strokeWidth="1.8" />
                      <path d="m7 17 10-10M7 12l5-5M12 17l5-5" strokeWidth="1.8" strokeLinecap="round" />
                    </svg>
                  </button>

                  <button
                    type="button"
                    className={`inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border transition active:scale-110 ${getToolbarToneClass(
                      "blue",
                      toolMode === "circle",
                    )}`}
                    aria-label="Circle"
                    title="Circle (C)"
                    onClick={() => handleCurveToolSelection("circle")}
                  >
                    <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-none">
                      <circle cx="12" cy="12" r="7" stroke="currentColor" strokeWidth="2" />
                    </svg>
                  </button>

                  <button
                    type="button"
                    className={`inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border transition active:scale-110 ${getToolbarToneClass(
                      "blue",
                      toolMode === "arc",
                    )}`}
                    aria-label="Arc"
                    title="Arc"
                    onClick={() => handleCurveToolSelection("arc")}
                  >
                    <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-none stroke-current">
                      <path d="M5 15a7 7 0 0 1 14 0" strokeWidth="2" strokeLinecap="round" />
                      <circle cx="5" cy="15" r="1.5" fill="currentColor" stroke="none" />
                      <circle cx="19" cy="15" r="1.5" fill="currentColor" stroke="none" />
                    </svg>
                  </button>

                  <button
                    type="button"
                    className={`inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border text-[16px] font-bold transition active:scale-110 ${getToolbarToneClass(
                      "white",
                      toolMode === "text",
                    )}`}
                    aria-label="Text"
                    title="Text"
                    onClick={() => handleCurveToolSelection("text")}
                  >
                    T
                  </button>

                  <button
                    type="button"
                    className={`inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border transition active:scale-110 ${getToolbarToneClass(
                      "white",
                      toolMode === "icon",
                    )}`}
                    aria-label="Icons"
                    title="Icons"
                    onClick={() => handleCurveToolSelection("icon")}
                  >
                    <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-none stroke-current">
                      <path
                        d="M12 4l2.1 4.3 4.7.7-3.4 3.3.8 4.7L12 14.8 7.8 17l.8-4.7L5.2 9l4.7-.7L12 4Z"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              )}
            </div>

            <div
              className="relative self-center"
              onMouseEnter={() => {
                clearPendingToolGroupClose();
                setOpenToolGroupMenu("transform");
              }}
            >
              <button
                type="button"
                className={`inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border transition active:scale-110 ${getToolbarToneClass(
                  "blue",
                  toolMode === primaryTransformTool,
                )}`}
                aria-label={
                  primaryTransformTool === "move"
                    ? "Move"
                    : primaryTransformTool === "copy"
                      ? "Copy"
                      : primaryTransformTool === "explode"
                        ? "Esplodi"
                        : primaryTransformTool === "scale"
                          ? "Scale"
                          : primaryTransformTool === "rotate"
                            ? "Rotate"
                            : "Offset"
                }
                title={
                  primaryTransformTool === "move"
                    ? "Move (M)"
                    : primaryTransformTool === "copy"
                      ? "Copy (Y)"
                      : primaryTransformTool === "explode"
                        ? "Esplodi"
                        : primaryTransformTool === "scale"
                          ? "Scale"
                          : primaryTransformTool === "rotate"
                            ? "Rotate"
                            : "Offset"
                }
                onClick={handlePrimaryTransformToolClick}
              >
                {renderTransformIcon(primaryTransformTool)}
              </button>

              {openToolGroupMenu === "transform" && (
                <div className="absolute left-full top-1/2 z-10 ml-2 flex -translate-y-1/2 gap-2 rounded-md border border-[rgba(74,58,31,0.12)] bg-[rgba(245,240,230,0.98)] p-2 shadow-[0_12px_24px_rgba(58,45,20,0.16)] [&_button]:h-20 [&_button]:w-20 [&_img]:h-12 [&_img]:w-12 [&_svg]:h-8 [&_svg]:w-8 [&_span]:text-sm">
                  <button
                    type="button"
                    className={`inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border transition active:scale-110 ${getToolbarToneClass(
                      "blue",
                      toolMode === "move",
                    )}`}
                    aria-label="Move"
                    title="Move (M)"
                    onClick={() => handleTransformToolSelection("move")}
                  >
                    {renderTransformIcon("move")}
                  </button>

                  <button
                    type="button"
                    className={`inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border transition active:scale-110 ${getToolbarToneClass(
                      "blue",
                      toolMode === "copy",
                    )}`}
                    aria-label="Copy"
                    title="Copy (Y)"
                    onClick={() => handleTransformToolSelection("copy")}
                  >
                    {renderTransformIcon("copy")}
                  </button>

                  <button
                    type="button"
                    className={`inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border transition active:scale-110 ${getToolbarToneClass(
                      "blue",
                      false,
                    )}`}
                    aria-label="Esplodi"
                    title="Esplodi"
                    onClick={() => handleTransformToolSelection("explode")}
                  >
                    {renderTransformIcon("explode")}
                  </button>

                  <button
                    type="button"
                    className={`inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border transition active:scale-110 ${getToolbarToneClass(
                      "blue",
                      toolMode === "scale",
                    )}`}
                    aria-label="Scale"
                    title="Scale"
                    onClick={() => handleTransformToolSelection("scale")}
                  >
                    {renderTransformIcon("scale")}
                  </button>

                  <button
                    type="button"
                    className={`inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border transition active:scale-110 ${getToolbarToneClass(
                      "blue",
                      toolMode === "rotate",
                    )}`}
                    aria-label="Rotate"
                    title="Rotate"
                    onClick={() => handleTransformToolSelection("rotate")}
                  >
                    {renderTransformIcon("rotate")}
                  </button>

                  <button
                    type="button"
                    className={`inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border transition active:scale-110 ${getToolbarToneClass(
                      "blue",
                      toolMode === "offset",
                    )}`}
                    aria-label="Offset"
                    title="Offset"
                    onClick={() => handleTransformToolSelection("offset")}
                  >
                    {renderTransformIcon("offset")}
                  </button>
                </div>
              )}
            </div>

            <div
              className="relative self-center"
              onMouseEnter={() => {
                clearPendingToolGroupClose();
                setOpenToolGroupMenu("cleanup");
              }}
            >
              <button
                type="button"
                className={`inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border transition active:scale-110 ${getToolbarToneClass(
                  "black",
                  false,
                )}`}
                aria-label={primaryCleanupTool === "delete" ? "Delete" : "Clear"}
                title={primaryCleanupTool === "delete" ? "Delete (Delete)" : "Clear"}
                onClick={handlePrimaryCleanupToolClick}
              >
                {primaryCleanupTool === "delete" ? (
                  <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-none stroke-current">
                    <path d="M4 7h16" strokeWidth="2" strokeLinecap="round" />
                    <path d="M9 7V5h6v2M7 7l1 12h8l1-12" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M10 11v5M14 11v5" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-none stroke-current">
                    <path d="m4 15 6-6 5 5-6 6H4v-5Z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="m10 9 2-2 5 5-2 2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M14 20h6" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                )}
              </button>

              {openToolGroupMenu === "cleanup" && (
                <div className="absolute left-full top-1/2 z-10 ml-2 flex -translate-y-1/2 gap-2 rounded-md border border-[rgba(74,58,31,0.12)] bg-[rgba(245,240,230,0.98)] p-2 shadow-[0_12px_24px_rgba(58,45,20,0.16)] [&_button]:h-20 [&_button]:w-20 [&_img]:h-12 [&_img]:w-12 [&_svg]:h-8 [&_svg]:w-8 [&_span]:text-sm">
                  <button
                    type="button"
                    className={`inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border transition active:scale-110 ${getToolbarToneClass(
                      "black",
                      false,
                    )}`}
                    aria-label="Delete"
                    title="Delete (Delete)"
                    onClick={() => handleCleanupToolSelection("delete")}
                  >
                    <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-none stroke-current">
                      <path d="M4 7h16" strokeWidth="2" strokeLinecap="round" />
                      <path
                        d="M9 7V5h6v2M7 7l1 12h8l1-12"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path d="M10 11v5M14 11v5" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                  </button>

                  <button
                    type="button"
                    className={`inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border transition active:scale-110 ${getToolbarToneClass(
                      "black",
                      false,
                    )}`}
                    aria-label="Clear"
                    title="Clear"
                    onClick={() => handleCleanupToolSelection("clear")}
                  >
                    <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-none stroke-current">
                      <path d="m4 15 6-6 5 5-6 6H4v-5Z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="m10 9 2-2 5 5-2 2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M14 20h6" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          </div>
        </aside>
      ) : (
        <button
          type="button"
          className="absolute left-7 top-7 z-20 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-[6px] border border-[rgba(74,58,31,0.12)] bg-[rgba(245,240,230,0.96)] px-5 py-2 text-[11px] font-medium text-[#332917] shadow-[0_12px_24px_rgba(0,0,0,0.14)]"
          style={getFloatingPanelStyle("drawing")}
          aria-label="Open Drawing Tool"
          title="Open Drawing Tool"
          onClick={() => {
            bringFloatingPanelToFront("drawing");
            setIsToolbarOpen(true);
          }}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true" className="h-[18px] w-[18px] fill-current">
            <path d="M4 7h16v2H4V7Zm0 5h16v2H4v-2Zm0 5h16v2H4v-2Z" />
          </svg>
          Drawing Tool
        </button>
      )}

      {isEntityPropertiesOpen ? (
        <aside
          ref={systemSettingsPanelRef}
          className="absolute right-7 top-7 z-20 flex max-h-[80vh] w-[min(440px,calc(100vw-84px))] flex-col gap-3 overflow-y-auto rounded-[6px] border border-[rgba(74,58,31,0.12)] bg-[rgba(245,240,230,0.96)] p-3 text-black shadow-[0_20px_40px_rgba(58,45,20,0.14)] backdrop-blur-[10px] [&_*]:!text-black [&_input]:!text-[14px] [&_input]:!font-normal [&_label]:!text-[13px] [&_select]:!text-[14px] [&_select]:!font-normal"
          style={getFloatingPanelStyle("system")}
          aria-label="System Settings"
          onMouseDownCapture={() => bringFloatingPanelToFront("system")}
          onFocusCapture={() => bringFloatingPanelToFront("system")}
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="m-0 text-[11px] font-bold uppercase tracking-[0.08em] text-[#4b3e27]">System Settings</h3>
              <div className="mt-1 text-[10px] text-[#6c5b3d]">{selectedEntitySubtitle}</div>
            </div>
            <div className="flex items-start gap-2">
              <div className="rounded-full bg-[rgba(29,92,171,0.12)] px-2 py-1 text-[10px] font-semibold text-[#1d5cab]">
                {selectedEntityTypeLabel}
              </div>
              <button
                type="button"
                className="inline-flex h-5 w-5 cursor-pointer items-center justify-center rounded-full border-0 bg-[rgba(33,27,18,0.09)] text-[#332917]"
                aria-label="Minimize System Settings"
                title="Minimize System Settings"
                onClick={() => setIsEntityPropertiesOpen(false)}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true" className="h-[14px] w-[14px] fill-current">
                  <path d="M4 7h16v2H4V7Zm0 5h16v2H4v-2Zm0 5h16v2H4v-2Z" />
                </svg>
              </button>
            </div>
          </div>

          <>
            <div className="grid grid-cols-3 gap-1 rounded-md bg-[rgba(74,58,31,0.08)] p-1">
              {[
                { key: "roofProperty", label: "Roof Property" },
                { key: "lifelineSystems", label: "Lifeline Systems" },
                { key: "anchorPoints", label: "Anchor Points" },
              ].map((page) => (
                <button
                  key={page.key}
                  type="button"
                  className={`rounded-md px-2 py-1 text-[9px] font-semibold leading-tight transition ${
                    activeConfigurationPage === page.key
                      ? "bg-white text-[#1f2937] shadow-sm"
                      : "text-[#6c5b3d] hover:bg-white/60"
                  }`}
                  onClick={() =>
                    setActiveConfigurationPage(page.key as "roofProperty" | "lifelineSystems" | "anchorPoints")
                  }
                >
                  {page.label}
                </button>
              ))}
            </div>

            {activeConfigurationPage === "roofProperty" && (
              <div className="grid grid-cols-2 gap-1 rounded-md bg-[rgba(74,58,31,0.06)] p-1">
                {[
                  { key: "substructure", label: "Substructure" },
                  { key: "roofInfo", label: "Roof Info" },
                ].map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    className={`rounded-md px-2 py-1 text-[9px] font-semibold leading-tight transition ${
                      activeRoofPropertyTab === tab.key
                        ? "bg-white text-[#1f2937] shadow-sm"
                        : "text-[#6c5b3d] hover:bg-white/60"
                    }`}
                    onClick={() => setActiveRoofPropertyTab(tab.key as "roofInfo" | "substructure")}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            )}

            {activeConfigurationPage === "lifelineSystems" && (
              <div className="grid grid-cols-3 gap-1 rounded-md bg-[rgba(74,58,31,0.06)] p-1">
                {[
                  { key: "info", label: "Supports" },
                  { key: "components", label: "Components" },
                  { key: "shuttles", label: "Shuttles / Labels" },
                ].map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    className={`rounded-md px-2 py-1 text-[9px] font-semibold leading-tight transition ${
                      activeLifeLineTab === tab.key
                        ? "bg-white text-[#1f2937] shadow-sm"
                        : "text-[#6c5b3d] hover:bg-white/60"
                    }`}
                    onClick={() => setActiveLifeLineTab(tab.key as "info" | "components" | "shuttles")}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            )}

            {activeConfigurationPage === "anchorPoints" && (
              <div className="grid grid-cols-1 gap-1 rounded-md bg-[rgba(74,58,31,0.06)] p-1">
                {[{ key: "anchorPoint", label: "Anchor Point" }].map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    className={`rounded-md px-2 py-1 text-[9px] font-semibold leading-tight transition ${
                      activeAnchorPointTab === tab.key
                        ? "bg-white text-[#1f2937] shadow-sm"
                        : "text-[#6c5b3d] hover:bg-white/60"
                    }`}
                    onClick={() => setActiveAnchorPointTab(tab.key as "anchorPoint")}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            )}

            {activeConfigurationPage === "roofProperty" && activeRoofPropertyTab === "roofInfo" && (
              <div className="grid grid-cols-1 gap-3 rounded-md border border-[rgba(74,58,31,0.12)] bg-[rgba(255,255,255,0.72)] p-3 md:grid-cols-2">
                <h4 className="m-0 text-[10px] font-bold uppercase tracking-[0.08em] text-[#6c5b3d] md:col-span-2">
                  Roof Info
                </h4>
                {!commonSelectedRoofGroup && !singleSelectedRoofLine && (
                  <div className="rounded-md border border-dashed border-[rgba(74,58,31,0.18)] bg-[rgba(255,255,255,0.68)] px-3 py-4 text-[11px] text-[#6c5b3d] md:col-span-2">
                    Select a roof group to see and modify its properties.
                  </div>
                )}
                {commonSelectedRoofGroup && (
                  <>
                    <div className="flex flex-col gap-3">
                      <label className="flex flex-col gap-1 text-[11px] font-medium text-[#4b3e27]">
                        Roof Reference
                        <input
                          className="rounded-md border border-[rgba(74,58,31,0.18)] bg-white px-3 py-2 text-[12px] font-semibold text-[#1f2937] outline-none focus:border-[#1d5cab]"
                          type="text"
                          value={commonSelectedRoofReference}
                          placeholder={commonSelectedRoofGroup.name}
                          onChange={(event) => updateSelectedRoofReference(event.target.value)}
                        />
                      </label>

                      <label className="flex flex-col gap-1 text-[11px] font-medium text-[#4b3e27]">
                        Roof Type
                        <select
                          className="rounded-md border border-[rgba(74,58,31,0.18)] bg-white px-3 py-2 text-[12px] text-[#1f2937] outline-none focus:border-[#1d5cab]"
                          value={commonSelectedRoofTypeId}
                          onChange={(event) => updateSelectedRoofBuildProperty("roofType", event.target.value)}
                        >
                          <option value="">Not selected</option>
                          {roofTypeOptions.map((roofType) => (
                            <option key={roofType.id} value={roofType.id}>
                              {roofType.name}
                            </option>
                          ))}
                        </select>
                      </label>

                      <div className="flex flex-col gap-1 rounded-md border border-[rgba(74,58,31,0.12)] bg-white/70 p-2 text-[11px]">
                        <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[#6c5b3d]">
                          Surface
                        </span>
                        <span className="font-semibold text-[#1f2937]">
                          {selectedRoofSurfaceArea > 0 ? formatAreaMeasurement(selectedRoofSurfaceArea) : "N/D"}
                        </span>
                      </div>
                    </div>

                    <div className="flex flex-col gap-3">
                      <div className="flex flex-col gap-1 rounded-md border border-[rgba(74,58,31,0.12)] bg-white/70 p-2 text-[11px]">
                        <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[#6c5b3d]">
                          Category
                        </span>
                        <span className="font-semibold text-[#1f2937]">
                          {commonSelectedRoofGroup.category ?? "Roof"}
                        </span>
                      </div>
                      {selectedRoofTypeIconUrl && (
                        <div className="flex flex-col gap-2 rounded-md border border-[rgba(74,58,31,0.12)] bg-white/70 p-2 text-[11px]">
                          <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[#6c5b3d]">
                            Roof Type
                          </span>
                          <div className="flex h-28 items-center justify-center overflow-hidden rounded-md bg-white">
                            <img
                              src={selectedRoofTypeIconUrl}
                              alt={selectedRoofTypeIconLabel}
                              className="max-h-full max-w-full object-contain"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}
                {singleSelectedRoofLine && (
                  <label className="flex flex-col gap-1 text-[11px] font-medium text-[#4b3e27]">
                    Fall Side
                    <select
                      className="rounded-md border border-[rgba(74,58,31,0.18)] bg-white px-3 py-2 text-[12px] text-[#1f2937] outline-none focus:border-[#1d5cab]"
                      value={getRoofShapeFallSide(singleSelectedRoofLine) ? "true" : "false"}
                      onChange={(event) => updateSelectedRoofFallSide(event.target.value === "true")}
                    >
                      <option value="true">True</option>
                      <option value="false">False</option>
                    </select>
                  </label>
                )}
              </div>
            )}

            {activeConfigurationPage === "roofProperty" &&
              activeRoofPropertyTab === "substructure" &&
              commonSelectedRoofGroup && (
                <div className="grid grid-cols-1 gap-3 rounded-md border border-[rgba(74,58,31,0.12)] bg-[rgba(255,255,255,0.72)] p-3 md:grid-cols-2">
                  <h4 className="m-0 text-[10px] font-bold uppercase tracking-[0.08em] text-[#6c5b3d] md:col-span-2">
                    Substructure
                  </h4>
                  <div className="flex flex-col gap-3">
                    <label className="flex flex-col gap-1 text-[11px] font-medium text-[#4b3e27]">
                      Pitch
                      <input
                        className="rounded-md border border-[rgba(74,58,31,0.18)] bg-white px-3 py-2 text-[12px] text-[#1f2937] outline-none focus:border-[#1d5cab]"
                        type="text"
                        inputMode="decimal"
                        value={commonSelectedRoofPitch || selectedRoofType?.roofCategory.defaultPitch?.toString() || ""}
                        placeholder="0"
                        onChange={(event) => updateSelectedRoofBuildProperty("pitch", event.target.value)}
                      />
                    </label>

                    <label className="flex flex-col gap-1 text-[11px] font-medium text-[#4b3e27]">
                      Material
                      <select
                        className="rounded-md border border-[rgba(74,58,31,0.18)] bg-white px-3 py-2 text-[12px] text-[#1f2937] outline-none focus:border-[#1d5cab]"
                        value={commonSelectedRoofMaterialId}
                        onChange={(event) => updateSelectedRoofBuildProperty("material", event.target.value)}
                      >
                        <option value="">Not selected</option>
                        {roofMaterialOptions.map((material) => (
                          <option key={material.id} value={material.id}>
                            {material.name}
                          </option>
                        ))}
                      </select>
                    </label>

                    {shouldShowRoofSheetMaterialSelection && (
                      <label className="flex flex-col gap-1 text-[11px] font-medium text-[#4b3e27]">
                        Sheet Material
                        <select
                          className="rounded-md border border-[rgba(74,58,31,0.18)] bg-white px-3 py-2 text-[12px] text-[#1f2937] outline-none focus:border-[#1d5cab]"
                          value={commonSelectedRoofSheetMaterialId}
                          onChange={(event) => updateSelectedRoofBuildProperty("sheetMaterial", event.target.value)}
                        >
                          <option value="">Not selected</option>
                          {roofSheetMaterialOptions.map((sheetMaterial) => (
                            <option key={sheetMaterial.id} value={sheetMaterial.id}>
                              {formatRoofSheetMaterialOption(sheetMaterial)}
                            </option>
                          ))}
                        </select>
                      </label>
                    )}

                    {shouldShowRoofSubstructureSelection && (
                      <label className="flex flex-col gap-1 text-[11px] font-medium text-[#4b3e27]">
                        Roof Substructure
                        <select
                          className="rounded-md border border-[rgba(74,58,31,0.18)] bg-white px-3 py-2 text-[12px] text-[#1f2937] outline-none focus:border-[#1d5cab]"
                          value={commonSelectedRoofSubstructureId}
                          onChange={(event) => updateSelectedRoofBuildProperty("substructure", event.target.value)}
                        >
                          <option value="">Not selected</option>
                          {roofSubstructureOptions.map((substructure) => (
                            <option key={substructure.id} value={substructure.id}>
                              {substructure.name}
                            </option>
                          ))}
                        </select>
                      </label>
                    )}

                    {shouldShowSubstructureSize && (
                      <div className="flex flex-col gap-1 text-[11px] font-medium text-[#4b3e27]">
                        Substructure Size
                        <div className="grid grid-cols-2 gap-2">
                          <label className="flex flex-col gap-1">
                            Height
                            <input
                              className="rounded-md border border-[rgba(74,58,31,0.18)] bg-white px-3 py-2 text-[12px] text-[#1f2937] outline-none focus:border-[#1d5cab]"
                              type="text"
                              inputMode="decimal"
                              value={commonSelectedRoofSubstructureSizeHeight}
                              placeholder={
                                selectedRoofMappingSummary.minSubstructureSize === "N/D"
                                  ? ""
                                  : selectedRoofMappingSummary.minSubstructureSize
                              }
                              onChange={(event) =>
                                updateSelectedRoofBuildProperty("substructureSizeHeight", event.target.value)
                              }
                            />
                          </label>
                          <label className="flex flex-col gap-1">
                            Width
                            <input
                              className="rounded-md border border-[rgba(74,58,31,0.18)] bg-white px-3 py-2 text-[12px] text-[#1f2937] outline-none focus:border-[#1d5cab]"
                              type="text"
                              inputMode="decimal"
                              value={commonSelectedRoofSubstructureSizeWidth}
                              placeholder={
                                selectedRoofMappingSummary.minSubstructureSize === "N/D"
                                  ? ""
                                  : selectedRoofMappingSummary.minSubstructureSize
                              }
                              onChange={(event) =>
                                updateSelectedRoofBuildProperty("substructureSizeWidth", event.target.value)
                              }
                            />
                          </label>
                        </div>
                      </div>
                    )}

                    {shouldShowSubstructureHeight && (
                      <label className="flex flex-col gap-1 text-[11px] font-medium text-[#4b3e27]">
                        Substructure Height
                        <input
                          className="rounded-md border border-[rgba(74,58,31,0.18)] bg-white px-3 py-2 text-[12px] text-[#1f2937] outline-none focus:border-[#1d5cab]"
                          type="text"
                          inputMode="decimal"
                          value={commonSelectedRoofSubstructureHeight}
                          placeholder={
                            selectedRoofMappingSummary.minSubstructureHeight === "N/D"
                              ? ""
                              : selectedRoofMappingSummary.minSubstructureHeight
                          }
                          onChange={(event) =>
                            updateSelectedRoofBuildProperty("substructureHeight", event.target.value)
                          }
                        />
                      </label>
                    )}

                    {shouldShowMetalPlateThickness && (
                      <label className="flex flex-col gap-1 text-[11px] font-medium text-[#4b3e27]">
                        Metal plate thickness
                        <input
                          className="rounded-md border border-[rgba(74,58,31,0.18)] bg-white px-3 py-2 text-[12px] text-[#1f2937] outline-none focus:border-[#1d5cab]"
                          type="text"
                          inputMode="decimal"
                          value={commonSelectedRoofMetalPlateThickness}
                          placeholder={
                            selectedRoofMappingSummary.minMetalPlateThickness === "N/D"
                              ? ""
                              : selectedRoofMappingSummary.minMetalPlateThickness
                          }
                          onChange={(event) =>
                            updateSelectedRoofBuildProperty("metalPlateThickness", event.target.value)
                          }
                        />
                      </label>
                    )}

                    {shouldShowSeamsSpan && (
                      <label className="flex flex-col gap-1 text-[11px] font-medium text-[#4b3e27]">
                        Seams Span
                        {shouldUseSelectedRoofSeamsSpanInput ? (
                          <input
                            className="rounded-md border border-[rgba(74,58,31,0.18)] bg-white px-3 py-2 text-[12px] text-[#1f2937] outline-none focus:border-[#1d5cab]"
                            type="text"
                            inputMode="decimal"
                            value={commonSelectedRoofSeamsSpan}
                            placeholder="500"
                            onChange={(event) => updateSelectedRoofBuildProperty("seamsSpan", event.target.value)}
                          />
                        ) : (
                          <select
                            className="rounded-md border border-[rgba(74,58,31,0.18)] bg-white px-3 py-2 text-[12px] text-[#1f2937] outline-none focus:border-[#1d5cab]"
                            value={commonSelectedRoofSeamsSpan}
                            onChange={(event) => updateSelectedRoofBuildProperty("seamsSpan", event.target.value)}
                          >
                            <option value="">Not selected</option>
                            {(settings?.seamsSpans ?? []).map((seamsSpan) => (
                              <option key={seamsSpan} value={seamsSpan}>
                                {seamsSpan} mm
                              </option>
                            ))}
                          </select>
                        )}
                      </label>
                    )}
                    {shouldShowRoofHipsSpan && (
                      <label className="flex flex-col gap-1 text-[11px] font-medium text-[#4b3e27]">
                        Hips Span
                        <select
                          className="rounded-md border border-[rgba(74,58,31,0.18)] bg-white px-3 py-2 text-[12px] text-[#1f2937] outline-none focus:border-[#1d5cab]"
                          value={commonSelectedRoofHipsSpan}
                          onChange={(event) => updateSelectedRoofBuildProperty("hipsSpan", event.target.value)}
                        >
                          <option value="">Not selected</option>
                          {patrolHipsSpans.map((hipsSpan) => (
                            <option key={hipsSpan.value} value={hipsSpan.value}>
                              {hipsSpan.value} mm
                            </option>
                          ))}
                        </select>
                      </label>
                    )}

                    {shouldShowRoofWaveSpan && (
                      <label className="flex flex-col gap-1 text-[11px] font-medium text-[#4b3e27]">
                        Wave Span
                        <select
                          className="rounded-md border border-[rgba(74,58,31,0.18)] bg-white px-3 py-2 text-[12px] text-[#1f2937] outline-none focus:border-[#1d5cab]"
                          value={commonSelectedRoofWaveSpan}
                          onChange={(event) => updateSelectedRoofBuildProperty("waveSpan", event.target.value)}
                        >
                          <option value="">Not selected</option>
                          {patrolWaveSpans.map((waveSpan) => (
                            <option key={waveSpan.value} value={waveSpan.value}>
                              {waveSpan.value} mm
                            </option>
                          ))}
                        </select>
                      </label>
                    )}

                    {shouldShowRoofInsulationThickness && (
                      <label className="flex flex-col gap-1 text-[11px] font-medium text-[#4b3e27]">
                        Insulation Thickness
                        <input
                          className="rounded-md border border-[rgba(74,58,31,0.18)] bg-white px-3 py-2 text-[12px] text-[#1f2937] outline-none focus:border-[#1d5cab]"
                          type="text"
                          inputMode="decimal"
                          value={commonSelectedRoofInsulationThickness}
                          placeholder="100"
                          onChange={(event) =>
                            updateSelectedRoofBuildProperty("insulationThickness", event.target.value)
                          }
                        />
                      </label>
                    )}
                  </div>
                  <div className="flex flex-col gap-3">
                    <div className="flex flex-col gap-2 rounded-md border border-[rgba(74,58,31,0.12)] bg-white/70 p-2 text-[11px]">
                      <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[#6c5b3d]">
                        Material
                      </span>
                      <div className="flex h-32 items-center justify-center overflow-hidden rounded-md bg-white">
                        {shouldShowRoofMaterialImage && selectedRoofMaterialImageUrl ? (
                          <img
                            src={selectedRoofMaterialImageUrl}
                            alt={selectedRoofMaterial?.name ?? "Roof material"}
                            className="max-h-full max-w-full object-contain"
                            onError={() => setFailedRoofMaterialImageUrl(selectedRoofMaterialImageUrl)}
                          />
                        ) : (
                          <div className="flex h-full w-full flex-col items-center justify-center gap-2 rounded-md border border-dashed border-[rgba(74,58,31,0.24)] bg-[rgba(245,240,230,0.58)] px-3 text-center text-[#6c5b3d]">
                            <svg viewBox="0 0 24 24" aria-hidden="true" className="h-7 w-7 fill-none stroke-current">
                              <rect x="4" y="5" width="16" height="14" rx="2" strokeWidth="1.8" />
                              <path d="m7 16 3.5-3.5 2.5 2.5 2-2 2 3" strokeWidth="1.8" strokeLinejoin="round" />
                              <circle cx="9" cy="9" r="1.2" fill="currentColor" stroke="none" />
                            </svg>
                            <span className="text-[10px] font-semibold leading-tight">
                              {selectedRoofMaterial?.image
                                ? `${selectedRoofMaterialPictureLabel} picture not loaded from server`
                                : commonSelectedRoofMaterialId
                                  ? `No ${selectedRoofMaterialPictureLabel} picture configured`
                                  : "No Material selected"}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {commonSelectedRoofSubstructureId && (
                      <div className="flex flex-col gap-2 rounded-md border border-[rgba(74,58,31,0.12)] bg-white/70 p-2 text-[11px]">
                        <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[#6c5b3d]">
                          Roof Substructure
                        </span>
                        <div className="flex h-32 items-center justify-center overflow-hidden rounded-md bg-white">
                          {shouldShowRoofSubstructureImage && selectedRoofSubstructureImageUrl ? (
                            <img
                              src={selectedRoofSubstructureImageUrl}
                              alt={selectedRoofSubstructure?.name ?? "Roof substructure"}
                              className="max-h-full max-w-full object-contain"
                              onError={() => setFailedRoofSubstructureImageUrl(selectedRoofSubstructureImageUrl)}
                            />
                          ) : (
                            <div className="flex h-full w-full flex-col items-center justify-center gap-2 rounded-md border border-dashed border-[rgba(74,58,31,0.24)] bg-[rgba(245,240,230,0.58)] px-3 text-center text-[#6c5b3d]">
                              <svg viewBox="0 0 24 24" aria-hidden="true" className="h-7 w-7 fill-none stroke-current">
                                <rect x="4" y="5" width="16" height="14" rx="2" strokeWidth="1.8" />
                                <path d="m7 16 3.5-3.5 2.5 2.5 2-2 2 3" strokeWidth="1.8" strokeLinejoin="round" />
                                <circle cx="9" cy="9" r="1.2" fill="currentColor" stroke="none" />
                              </svg>
                              <span className="text-[10px] font-semibold leading-tight">
                                {selectedRoofSubstructure?.image
                                  ? `${selectedRoofSubstructurePictureLabel} picture not loaded from server`
                                  : `No ${selectedRoofSubstructurePictureLabel} picture configured`}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

            {activeConfigurationPage === "roofProperty" &&
              activeRoofPropertyTab === "substructure" &&
              !commonSelectedRoofGroup && (
                <div className="rounded-md border border-dashed border-[rgba(74,58,31,0.18)] bg-[rgba(255,255,255,0.68)] px-3 py-4 text-[11px] text-[#6c5b3d]">
                  Select a roof group to see substructure properties.
                </div>
              )}

            {activeConfigurationPage === "lifelineSystems" &&
              activeLifeLineTab === "components" &&
              selectedRoofSystem && (
                <div className="grid grid-cols-1 gap-3 rounded-md border border-[rgba(74,58,31,0.12)] bg-[rgba(255,255,255,0.72)] p-3 md:grid-cols-2">
                  <h4 className="m-0 text-[10px] font-bold uppercase tracking-[0.08em] text-[#6c5b3d] md:col-span-2">
                    Components
                  </h4>

                  <div className="flex flex-col gap-3">
                    <label className="flex flex-col gap-1 text-[11px] font-bold text-[#1d5cab]">
                      System
                      <input
                        className="rounded-md border border-[rgba(74,58,31,0.18)] bg-[rgba(255,255,255,0.65)] px-3 py-2 text-[12px] font-semibold text-[#1d5cab] outline-none"
                        type="text"
                        value={selectedRoofSystem?.name ?? ""}
                        placeholder="No system selected"
                        disabled
                      />
                    </label>

                    <label className="flex flex-col gap-1 text-[11px] font-bold text-[#1d5cab]">
                      Component Material
                      <select
                        className="rounded-md border border-[rgba(74,58,31,0.18)] bg-white px-3 py-2 text-[12px] font-semibold text-[#1d5cab] outline-none focus:border-[#1d5cab] disabled:bg-[rgba(255,255,255,0.65)] disabled:text-[#1d5cab]"
                        value={selectedRoofComponentMaterial?.id ?? ""}
                        disabled={roofComponentMaterialOptions.length < 2}
                        onChange={(event) => updateSelectedRoofComponentProperty("material", event.target.value)}
                      >
                        {roofComponentMaterialOptions.length === 0 && <option value="">No compatible material</option>}
                        {roofComponentMaterialOptions.map((material) => (
                          <option key={material.id} value={material.id}>
                            {material.name}
                          </option>
                        ))}
                      </select>
                    </label>

                    <label className="flex flex-col gap-1 text-[11px] font-bold text-[#1d5cab]">
                      System Type
                      <select
                        className="rounded-md border border-[rgba(74,58,31,0.18)] bg-white px-3 py-2 text-[12px] font-semibold text-[#1d5cab] outline-none focus:border-[#1d5cab] disabled:bg-[rgba(255,255,255,0.65)] disabled:text-[#1d5cab]"
                        value={selectedRoofComponentSystemType?.id ?? ""}
                        disabled={roofComponentSystemTypeOptions.length < 2}
                        onChange={(event) => updateSelectedRoofComponentProperty("systemType", event.target.value)}
                      >
                        {roofComponentSystemTypeOptions.length === 0 && (
                          <option value="">No compatible system type</option>
                        )}
                        {roofComponentSystemTypeOptions.map((systemType) => (
                          <option key={systemType.id} value={systemType.id}>
                            {systemType.name}
                          </option>
                        ))}
                      </select>
                    </label>

                    <label className="flex flex-col gap-1 text-[11px] font-bold text-[#1d5cab]">
                      Absorber
                      <select
                        className="rounded-md border border-[rgba(74,58,31,0.18)] bg-white px-3 py-2 text-[12px] font-semibold text-[#1d5cab] outline-none focus:border-[#1d5cab] disabled:bg-[rgba(255,255,255,0.65)] disabled:text-[#1d5cab]"
                        value={selectedRoofComponentAbsorber?.item.id ?? ""}
                        disabled={roofComponentAbsorberOptions.length < 2}
                        onChange={(event) => updateSelectedRoofComponentProperty("absorber", event.target.value)}
                      >
                        {roofComponentAbsorberOptions.length === 0 && <option value="">No compatible absorber</option>}
                        {roofComponentAbsorberOptions.map((absorber) => (
                          <option key={absorber.item.id} value={absorber.item.id}>
                            {absorber.item.code}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>

                  <div className="flex flex-col gap-3">
                    <div className="flex flex-col gap-1 text-[11px] font-bold text-[#1d5cab]">
                      Terminals
                      <div className="flex flex-col gap-2">
                        {roofComponentTerminalDisplayItems.length === 0 ? (
                          <input
                            className="rounded-md border border-[rgba(74,58,31,0.18)] bg-[rgba(255,255,255,0.65)] px-3 py-2 text-[12px] font-semibold text-[#6c5b3d]"
                            type="text"
                            value="No terminal code configured"
                            disabled
                          />
                        ) : (
                          roofComponentTerminalDisplayItems.map((terminal) => (
                            <input
                              key={terminal.item.id}
                              className="rounded-md border border-[rgba(74,58,31,0.18)] bg-[rgba(255,255,255,0.65)] px-3 py-2 text-[12px] font-semibold text-[#1d5cab]"
                              type="text"
                              value={terminal.item.code}
                              disabled
                            />
                          ))
                        )}
                      </div>
                    </div>

                    <div className="flex flex-col gap-1 text-[11px] font-bold text-[#1d5cab]">
                      Intermediates
                      <div className="flex flex-col gap-2">
                        {roofComponentIntermediateDisplayItems.length === 0 ? (
                          <input
                            className="rounded-md border border-[rgba(74,58,31,0.18)] bg-[rgba(255,255,255,0.65)] px-3 py-2 text-[12px] font-semibold text-[#6c5b3d]"
                            type="text"
                            value="No intermediate code configured"
                            disabled
                          />
                        ) : (
                          roofComponentIntermediateDisplayItems.map((intermediate) => (
                            <input
                              key={intermediate.id}
                              className="rounded-md border border-[rgba(74,58,31,0.18)] bg-[rgba(255,255,255,0.65)] px-3 py-2 text-[12px] font-semibold text-[#1d5cab]"
                              type="text"
                              value={`${intermediate.item.code}${intermediate.extraItem ? `+${intermediate.extraItem.code}` : ""}`}
                              disabled
                            />
                          ))
                        )}
                      </div>
                    </div>

                    <div className="flex flex-col gap-1 text-[11px] font-bold text-[#1d5cab]">
                      Curves
                      <div className="flex flex-col gap-2">
                        {roofComponentCurveDisplayItems.length === 0 ? (
                          <input
                            className="rounded-md border border-[rgba(74,58,31,0.18)] bg-[rgba(255,255,255,0.65)] px-3 py-2 text-[12px] font-semibold text-[#6c5b3d]"
                            type="text"
                            value="No curve code configured"
                            disabled
                          />
                        ) : (
                          roofComponentCurveDisplayItems.map((curve) => (
                            <input
                              key={curve.id}
                              className="rounded-md border border-[rgba(74,58,31,0.18)] bg-[rgba(255,255,255,0.65)] px-3 py-2 text-[12px] font-semibold text-[#1d5cab]"
                              type="text"
                              value={`${curve.item.code}${curve.extraItem ? `+${curve.extraItem.code}` : ""}`}
                              disabled
                            />
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

            {activeConfigurationPage === "lifelineSystems" &&
              activeLifeLineTab === "components" &&
              !selectedRoofSystem && (
                <div className="rounded-md border border-dashed border-[rgba(74,58,31,0.18)] bg-[rgba(255,255,255,0.68)] px-3 py-4 text-[11px] text-[#6c5b3d]">
                  Select a Lifeline system to see component properties.
                </div>
              )}

            {activeConfigurationPage === "lifelineSystems" &&
              activeLifeLineTab === "shuttles" &&
              selectedRoofSystem && (
                <div className="grid grid-cols-1 gap-3 rounded-md border border-[rgba(74,58,31,0.12)] bg-[rgba(255,255,255,0.72)] p-3 md:grid-cols-2">
                  <h4 className="m-0 text-[10px] font-bold uppercase tracking-[0.08em] text-[#6c5b3d] md:col-span-2">
                    Shuttles / Labels
                  </h4>

                  <div className="flex flex-col gap-3">
                    <label className="flex flex-col gap-1 text-[11px] font-bold text-[#1d5cab]">
                      Type
                      <select
                        className="rounded-md border border-[rgba(74,58,31,0.18)] bg-white px-3 py-2 text-[12px] font-semibold text-[#1d5cab] outline-none focus:border-[#1d5cab]"
                        value={String(effectiveRoofFixed)}
                        onChange={(event) => updateSelectedRoofShuttleProperty("fixed", event.target.value)}
                      >
                        <option value="false">Removable</option>
                        <option value="true">Fixed</option>
                      </select>
                    </label>

                    <label className="flex flex-col gap-1 text-[11px] font-bold text-[#1d5cab]">
                      Shuttle
                      <select
                        className="rounded-md border border-[rgba(74,58,31,0.18)] bg-white px-3 py-2 text-[12px] font-semibold text-[#1d5cab] outline-none focus:border-[#1d5cab] disabled:bg-[rgba(255,255,255,0.65)] disabled:text-[#6c5b3d]"
                        value={selectedRoofShuttle?.id ?? ""}
                        disabled={roofShuttleOptions.length < 2}
                        onChange={(event) => updateSelectedRoofShuttleProperty("shuttle", event.target.value)}
                      >
                        {roofShuttleOptions.length === 0 && <option value="">No compatible shuttle</option>}
                        {roofShuttleOptions.map((shuttle) => (
                          <option key={shuttle.id} value={shuttle.id}>
                            {shuttle.item.code}
                          </option>
                        ))}
                      </select>
                    </label>

                    <label className="flex flex-col gap-1 text-[11px] font-bold text-[#1d5cab]">
                      Nr of shuttle requested
                      <input
                        className={`rounded-md border bg-white px-3 py-2 text-[12px] font-semibold text-[#1d5cab] outline-none focus:border-[#1d5cab] ${
                          isRoofShuttleQuantityOverMax ? "border-[#dc2626]" : "border-[rgba(74,58,31,0.18)]"
                        }`}
                        type="text"
                        inputMode="numeric"
                        value={effectiveRoofNrOfShuttles}
                        placeholder={selectedRoofShuttleMaxUsers ? String(selectedRoofShuttleMaxUsers) : "1"}
                        onChange={(event) => updateSelectedRoofShuttleProperty("nrOfShuttles", event.target.value)}
                      />
                      {isRoofShuttleQuantityOverMax && (
                        <span className="text-[10px] font-semibold text-[#dc2626]">
                          Selected absorber allows max {selectedRoofShuttleMaxUsers} users.
                        </span>
                      )}
                    </label>

                    <label className="flex flex-col gap-1 text-[11px] font-bold text-[#1d5cab]">
                      Language
                      <input
                        className="rounded-md border border-[rgba(74,58,31,0.18)] bg-[rgba(255,255,255,0.65)] px-3 py-2 text-[12px] font-semibold text-[#1d5cab] outline-none"
                        type="text"
                        value={`${labelLanguageCode} - ${patrolLabelCode} / ${lifeLineLabelCode}`}
                        disabled
                      />
                    </label>
                  </div>

                  <div className="flex flex-col gap-3">
                    <label className="flex flex-col gap-1 text-[11px] font-bold text-[#1d5cab]">
                      System
                      <input
                        className="rounded-md border border-[rgba(74,58,31,0.18)] bg-[rgba(255,255,255,0.65)] px-3 py-2 text-[12px] font-semibold text-[#1d5cab] outline-none"
                        type="text"
                        value={selectedRoofSystem?.name ?? ""}
                        disabled
                      />
                    </label>

                    <label className="flex flex-col gap-1 text-[11px] font-bold text-[#1d5cab]">
                      System Type
                      <input
                        className="rounded-md border border-[rgba(74,58,31,0.18)] bg-[rgba(255,255,255,0.65)] px-3 py-2 text-[12px] font-semibold text-[#1d5cab] outline-none"
                        type="text"
                        value={selectedRoofComponentSystemType?.name ?? "No system type selected"}
                        disabled
                      />
                    </label>

                    <div className="flex flex-col gap-2 rounded-md border border-[rgba(74,58,31,0.12)] bg-white/70 p-2 text-[11px]">
                      <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[#6c5b3d]">
                        {selectedRoofShuttlePictureLabel}
                      </span>
                      <div className="flex h-32 items-center justify-center overflow-hidden rounded-md bg-white">
                        {shouldShowRoofShuttleImage && selectedRoofShuttleImageUrl ? (
                          <img
                            src={selectedRoofShuttleImageUrl}
                            alt={selectedRoofShuttlePictureLabel}
                            className="max-h-full max-w-full object-contain"
                            onError={() => setFailedRoofShuttleImageUrl(selectedRoofShuttleImageUrl)}
                          />
                        ) : (
                          <div className="flex h-full w-full flex-col items-center justify-center gap-2 rounded-md border border-dashed border-[rgba(74,58,31,0.24)] bg-[rgba(245,240,230,0.58)] px-3 text-center text-[#6c5b3d]">
                            <svg viewBox="0 0 24 24" aria-hidden="true" className="h-7 w-7 fill-none stroke-current">
                              <rect x="4" y="5" width="16" height="14" rx="2" strokeWidth="1.8" />
                              <path d="m7 16 3.5-3.5 2.5 2.5 2-2 2 3" strokeWidth="1.8" strokeLinejoin="round" />
                              <circle cx="9" cy="9" r="1.2" fill="currentColor" stroke="none" />
                            </svg>
                            <span className="text-[10px] font-semibold leading-tight">
                              {selectedRoofShuttle?.item.image
                                ? `${selectedRoofShuttlePictureLabel} picture not loaded from server`
                                : selectedRoofShuttle
                                  ? `No ${selectedRoofShuttlePictureLabel} picture configured`
                                  : "No Shuttle selected"}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

            {activeConfigurationPage === "lifelineSystems" &&
              activeLifeLineTab === "shuttles" &&
              !selectedRoofSystem && (
                <div className="rounded-md border border-dashed border-[rgba(74,58,31,0.18)] bg-[rgba(255,255,255,0.68)] px-3 py-4 text-[11px] text-[#6c5b3d]">
                  Select a Lifeline system to see shuttle and label properties.
                </div>
              )}

            {activeConfigurationPage === "lifelineSystems" && activeLifeLineTab === "info" && (
              <div
                className="grid grid-cols-1 gap-3 rounded-md border border-[rgba(74,58,31,0.12)] bg-[rgba(255,255,255,0.72)] p-3 md:grid-cols-2"
                style={
                  selectedLifeLineShape ? { backgroundColor: SELECTED_SYSTEM_SECTION_BACKGROUND_COLOR } : undefined
                }
              >
                <h4 className="m-0 text-[10px] font-bold uppercase tracking-[0.08em] text-[#6c5b3d] md:col-span-2">
                  Supports
                </h4>

                <div className="flex flex-col gap-3">
                  <label className="flex flex-col gap-1 text-[11px] font-bold text-[#1d5cab]">
                    Lifeline System
                    <select
                      className="rounded-md border border-[rgba(74,58,31,0.18)] bg-white px-3 py-2 text-[12px] font-semibold text-[#1f2937] outline-none focus:border-[#1d5cab]"
                      value={selectedSafetySystem}
                      disabled={isLoading || safetySystemOptions.length === 0}
                      onChange={(event) => {
                        setSelectedSafetySystem(event.target.value);
                        setFallbackLifeLineNormativeId("");
                        setFallbackSupportMaterialId("");
                        setFallbackRoofComponentMaterialId("");
                        setFallbackRoofComponentAbsorberId("");
                        setFallbackRoofComponentSystemTypeId("");
                        setFallbackTerminalCurveSupportId("");
                        setFallbackIntermediateSupportId("");
                        if (commonSelectedRoofGroup) {
                          if (commonSelectedRoofUseOnlyAnchorPoints) {
                            updateSelectedRoofBuildProperty("useOnlyAnchorPoints", "false");
                          }
                          updateSelectedRoofBuildProperty("system", event.target.value);
                        }
                      }}
                    >
                      {safetySystemOptions.length === 0 ? (
                        <option value="">
                          {isLoading
                            ? "Loading..."
                            : hasRoofCategoryFilter
                              ? "No compatible system"
                              : "No Lifeline system"}
                        </option>
                      ) : (
                        safetySystemOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))
                      )}
                    </select>
                  </label>

                  <label className="flex flex-col gap-1 text-[11px] font-bold text-[#1d5cab]">
                    Normative
                    <select
                      className="rounded-md border border-[rgba(74,58,31,0.18)] bg-white px-3 py-2 text-[12px] text-[#1f2937] outline-none focus:border-[#1d5cab] disabled:bg-[rgba(255,255,255,0.6)] disabled:text-[#9ca3af]"
                      value={effectiveLifeLineNormativeId}
                      disabled={
                        !selectedLifeLineDefaultSystem ||
                        (!!commonSelectedRoofGroup && commonSelectedRoofUseOnlyAnchorPoints)
                      }
                      onChange={(event) => {
                        setFallbackSupportMaterialId("");
                        setFallbackTerminalCurveSupportId("");
                        setFallbackIntermediateSupportId("");
                        if (commonSelectedRoofGroup) {
                          if (!commonSelectedRoofUseOnlyAnchorPoints) {
                            updateSelectedRoofBuildProperty("normative", event.target.value);
                          }
                          return;
                        }

                        setFallbackLifeLineNormativeId(event.target.value);
                      }}
                    >
                      {selectedLifeLineNormativeOptions.length === 0 ? (
                        <option value="">No normative</option>
                      ) : (
                        selectedLifeLineNormativeOptions.map((normative) => (
                          <option key={normative.id} value={normative.id}>
                            {normative.name}
                          </option>
                        ))
                      )}
                    </select>
                  </label>

                  {lifeLineSupportMaterialOptions.length > 0 && (
                    <label className="flex flex-col gap-1 text-[11px] font-bold text-[#1d5cab]">
                      Support Material
                      <select
                        className="rounded-md border border-[rgba(74,58,31,0.18)] bg-white px-3 py-2 text-[12px] text-[#1f2937] outline-none focus:border-[#1d5cab] disabled:bg-[rgba(255,255,255,0.65)] disabled:text-[#6c5b3d]"
                        value={selectedLifeLineSupportMaterial?.id ?? ""}
                        disabled={lifeLineSupportMaterialOptions.length < 2}
                        onChange={(event) => updateFallProtectionSupportProperty("supportMaterial", event.target.value)}
                      >
                        <option value="">Not selected</option>
                        {lifeLineSupportMaterialOptions.map((material) => (
                          <option key={material.id} value={material.id}>
                            {material.name}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}

                  {lifeLineSeamsSpanRequired && (
                    <label className="flex flex-col gap-1 text-[11px] font-bold text-[#1d5cab]">
                      Seams span
                      {shouldUseLifeLineSeamsSpanInput ? (
                        <input
                          className="rounded-md border border-[rgba(74,58,31,0.18)] bg-white px-3 py-2 text-[12px] text-[#1f2937] outline-none focus:border-[#1d5cab]"
                          type="text"
                          inputMode="decimal"
                          value={effectiveRoofSeamsSpan}
                          placeholder="500"
                          onChange={(event) => updateFallProtectionSupportProperty("seamsSpan", event.target.value)}
                        />
                      ) : (
                        <select
                          className="rounded-md border border-[rgba(74,58,31,0.18)] bg-white px-3 py-2 text-[12px] text-[#1f2937] outline-none focus:border-[#1d5cab]"
                          value={effectiveRoofSeamsSpan}
                          disabled={(settings?.seamsSpans ?? []).length === 0}
                          onChange={(event) => updateFallProtectionSupportProperty("seamsSpan", event.target.value)}
                        >
                          <option value="">
                            {(settings?.seamsSpans ?? []).length === 0 ? "No seam spans configured" : "Select..."}
                          </option>
                          {(settings?.seamsSpans ?? []).map((seamsSpan) => (
                            <option key={seamsSpan} value={seamsSpan}>
                              {seamsSpan} mm
                            </option>
                          ))}
                        </select>
                      )}
                    </label>
                  )}

                  {lifeLineInsulationThicknessRequired && (
                    <label className="flex flex-col gap-1 text-[11px] font-medium text-[#4b3e27]">
                      Insulation Thickness
                      <input
                        className="rounded-md border border-[rgba(74,58,31,0.18)] bg-white px-3 py-2 text-[12px] text-[#1f2937] outline-none focus:border-[#1d5cab]"
                        type="text"
                        inputMode="decimal"
                        value={effectiveRoofInsulationThickness}
                        placeholder="100"
                        onChange={(event) =>
                          updateFallProtectionSupportProperty("insulationThickness", event.target.value)
                        }
                      />
                    </label>
                  )}

                  {shouldShowLifeLineTerminalCurveSupportSelect && (
                    <label className="flex flex-col gap-1 text-[11px] font-bold text-[#1d5cab]">
                      Terminal and Curves
                      {lifeLineTerminalCurveSupportOptions.length === 0 ? (
                        <input
                          className="rounded-md border border-[rgba(74,58,31,0.18)] bg-[rgba(255,255,255,0.65)] px-3 py-2 text-[12px] text-[#1f2937] outline-none"
                          type="text"
                          value={effectiveTerminalCurveSupportCode || "No compatible code"}
                          disabled
                        />
                      ) : lifeLineTerminalCurveSupportOptions.length === 1 ? (
                        <input
                          className="rounded-md border border-[rgba(74,58,31,0.18)] bg-[rgba(255,255,255,0.65)] px-3 py-2 text-[12px] text-[#1f2937] outline-none"
                          type="text"
                          value={lifeLineTerminalCurveSupportOptions[0]?.item.code ?? effectiveTerminalCurveSupportCode}
                          disabled
                        />
                      ) : (
                        <select
                          className="rounded-md border border-[rgba(74,58,31,0.18)] bg-white px-3 py-2 text-[12px] text-[#1f2937] outline-none focus:border-[#1d5cab]"
                          value={effectiveTerminalCurveSupportId}
                          onChange={(event) =>
                            updateFallProtectionSupportProperty("terminalCurveSupport", event.target.value)
                          }
                        >
                          <option value="">Select...</option>
                          {lifeLineTerminalCurveSupportOptions.map((support) => (
                            <option key={support.item.id} value={support.item.id}>
                              {support.item.code}
                            </option>
                          ))}
                        </select>
                      )}
                    </label>
                  )}

                  {shouldShowLifeLineIntermediateSupportSelect && (
                    <label className="flex flex-col gap-1 text-[11px] font-bold text-[#1d5cab]">
                      Intermediate
                      {lifeLineIntermediateSupportOptions.length === 0 ? (
                        <input
                          className="rounded-md border border-[rgba(74,58,31,0.18)] bg-[rgba(255,255,255,0.65)] px-3 py-2 text-[12px] text-[#1f2937] outline-none"
                          type="text"
                          value={effectiveIntermediateSupportCode || "No compatible code"}
                          disabled
                        />
                      ) : lifeLineIntermediateSupportOptions.length === 1 ? (
                        <input
                          className="rounded-md border border-[rgba(74,58,31,0.18)] bg-[rgba(255,255,255,0.65)] px-3 py-2 text-[12px] text-[#1f2937] outline-none"
                          type="text"
                          value={lifeLineIntermediateSupportOptions[0]?.item.code ?? effectiveIntermediateSupportCode}
                          disabled
                        />
                      ) : (
                        <select
                          className="rounded-md border border-[rgba(74,58,31,0.18)] bg-white px-3 py-2 text-[12px] text-[#1f2937] outline-none focus:border-[#1d5cab]"
                          value={effectiveIntermediateSupportId}
                          onChange={(event) =>
                            updateFallProtectionSupportProperty("intermediateSupport", event.target.value)
                          }
                        >
                          <option value="">Select...</option>
                          {lifeLineIntermediateSupportOptions.map((support) => (
                            <option key={support.item.id} value={support.item.id}>
                              {support.item.code}
                            </option>
                          ))}
                        </select>
                      )}
                    </label>
                  )}
                </div>

                <div className="flex flex-col gap-3">
                  <div className="flex flex-col gap-2 rounded-md border border-[rgba(74,58,31,0.12)] bg-white/70 p-2 text-[11px]">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[#6c5b3d]">
                      {selectedLifeLineSystemPictureLabel}
                    </span>
                    <div className="flex h-32 items-center justify-center overflow-hidden rounded-md bg-white">
                      {shouldShowLifeLineSystemImage && selectedLifeLineSystemImageUrl ? (
                        <img
                          src={selectedLifeLineSystemImageUrl}
                          alt={selectedLifeLineDefaultSystem?.name ?? "Lifeline system"}
                          className="max-h-full max-w-full object-contain"
                          onError={() => setFailedLifeLineSystemImageUrl(selectedLifeLineSystemImageUrl)}
                        />
                      ) : (
                        <div className="flex h-full w-full flex-col items-center justify-center gap-2 rounded-md border border-dashed border-[rgba(74,58,31,0.24)] bg-[rgba(245,240,230,0.58)] px-3 text-center text-[#6c5b3d]">
                          <svg viewBox="0 0 24 24" aria-hidden="true" className="h-7 w-7 fill-none stroke-current">
                            <rect x="4" y="5" width="16" height="14" rx="2" strokeWidth="1.8" />
                            <path d="m7 16 3.5-3.5 2.5 2.5 2-2 2 3" strokeWidth="1.8" strokeLinejoin="round" />
                            <circle cx="9" cy="9" r="1.2" fill="currentColor" stroke="none" />
                          </svg>
                          <span className="text-[10px] font-semibold leading-tight">
                            {selectedLifeLineDefaultSystem?.image
                              ? `${selectedLifeLineSystemPictureLabel} picture not loaded from server`
                              : `No ${selectedLifeLineSystemPictureLabel} picture configured`}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {[...lifeLineAdditionalProducts, ...lifeLineOptionalProducts].length > 0 && (
                    <div className="flex flex-col gap-1 rounded-md border border-[rgba(74,58,31,0.10)] bg-white/70 p-2 text-[11px]">
                      <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-[#1d5cab]">
                        Additional products
                      </span>
                      <span className="font-semibold text-[#1d5cab]">
                        {[...lifeLineAdditionalProducts, ...lifeLineOptionalProducts]
                          .map((product) => product.item.code)
                          .filter(Boolean)
                          .join(" / ")}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeConfigurationPage === "anchorPoints" && activeAnchorPointTab === "anchorPoint" && (
              <div
                className="grid grid-cols-1 gap-3 rounded-md border border-[rgba(74,58,31,0.12)] bg-[rgba(255,255,255,0.72)] p-3 md:grid-cols-2"
                style={
                  selectedAnchorPointShape ? { backgroundColor: SELECTED_SYSTEM_SECTION_BACKGROUND_COLOR } : undefined
                }
              >
                <h4 className="m-0 text-[10px] font-bold uppercase tracking-[0.08em] text-[#6c5b3d] md:col-span-2">
                  Anchor Point
                </h4>

                <div className="flex flex-col gap-3">
                  <label className="flex flex-col gap-1 text-[11px] font-bold text-[#1d5cab]">
                    Anchor Point System
                    <select
                      className="rounded-md border border-[rgba(74,58,31,0.18)] bg-white px-3 py-2 text-[12px] font-semibold text-[#1d5cab] outline-none focus:border-[#1d5cab]"
                      value={selectedAnchorPointSystem}
                      disabled={isLoading || anchorPointSystemOptions.length === 0}
                      onChange={(event) => {
                        setSelectedAnchorPointSystem(event.target.value);
                        setFallbackAnchorPointNormativeId("");
                        setFallbackAnchorPointMaterialId("");
                        setFallbackAnchorPointMappingId("");
                        if (commonSelectedRoofGroup) {
                          if (!commonSelectedRoofUseOnlyAnchorPoints) {
                            updateSelectedRoofBuildProperty("useOnlyAnchorPoints", "true");
                          }
                          updateSelectedRoofBuildProperty("system", event.target.value);
                        }
                      }}
                    >
                      {anchorPointSystemOptions.length === 0 ? (
                        <option value="">
                          {isLoading
                            ? "Loading..."
                            : hasRoofCategoryFilter
                              ? "No compatible system"
                              : "No Anchor Point system"}
                        </option>
                      ) : (
                        anchorPointSystemOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))
                      )}
                    </select>
                  </label>

                  <label className="flex flex-col gap-1 text-[11px] font-bold text-[#1d5cab]">
                    Normative
                    <select
                      className="rounded-md border border-[rgba(74,58,31,0.18)] bg-white px-3 py-2 text-[12px] font-semibold text-[#1d5cab] outline-none focus:border-[#1d5cab] disabled:bg-[rgba(255,255,255,0.6)] disabled:text-[#9ca3af]"
                      value={effectiveAnchorPointNormativeId}
                      disabled={
                        !selectedAnchorPointDefaultSystem ||
                        (!!commonSelectedRoofGroup && !commonSelectedRoofUseOnlyAnchorPoints)
                      }
                      onChange={(event) => {
                        if (commonSelectedRoofGroup) {
                          if (commonSelectedRoofUseOnlyAnchorPoints) {
                            updateSelectedRoofBuildProperty("normative", event.target.value);
                          }
                          return;
                        }

                        setFallbackAnchorPointNormativeId(event.target.value);
                      }}
                    >
                      {selectedAnchorPointNormativeOptions.length === 0 ? (
                        <option value="">No normative</option>
                      ) : (
                        selectedAnchorPointNormativeOptions.map((normative) => (
                          <option key={normative.id} value={normative.id}>
                            {normative.name}
                          </option>
                        ))
                      )}
                    </select>
                  </label>

                  {anchorPointSeamsSpanRequired && (
                    <label className="flex flex-col gap-1 text-[11px] font-bold text-[#1d5cab]">
                      Seams span
                      {shouldUseAnchorPointSeamsSpanInput ? (
                        <input
                          className="rounded-md border border-[rgba(74,58,31,0.18)] bg-white px-3 py-2 text-[12px] font-semibold text-[#1d5cab] outline-none focus:border-[#1d5cab]"
                          type="text"
                          inputMode="decimal"
                          value={effectiveRoofSeamsSpan}
                          placeholder="500"
                          onChange={(event) => updateFallProtectionSupportProperty("seamsSpan", event.target.value)}
                        />
                      ) : (
                        <select
                          className="rounded-md border border-[rgba(74,58,31,0.18)] bg-white px-3 py-2 text-[12px] font-semibold text-[#1d5cab] outline-none focus:border-[#1d5cab]"
                          value={effectiveRoofSeamsSpan}
                          disabled={(settings?.seamsSpans ?? []).length === 0}
                          onChange={(event) => updateFallProtectionSupportProperty("seamsSpan", event.target.value)}
                        >
                          <option value="">
                            {(settings?.seamsSpans ?? []).length === 0 ? "No seam spans configured" : "Select..."}
                          </option>
                          {(settings?.seamsSpans ?? []).map((seamsSpan) => (
                            <option key={seamsSpan} value={seamsSpan}>
                              {seamsSpan} mm
                            </option>
                          ))}
                        </select>
                      )}
                    </label>
                  )}

                  {anchorPointInsulationThicknessRequired && (
                    <label className="flex flex-col gap-1 text-[11px] font-bold text-[#1d5cab]">
                      Insulation thickness
                      <input
                        className="rounded-md border border-[rgba(74,58,31,0.18)] bg-white px-3 py-2 text-[12px] font-semibold text-[#1d5cab] outline-none focus:border-[#1d5cab]"
                        type="text"
                        inputMode="decimal"
                        value={effectiveRoofInsulationThickness}
                        placeholder="100"
                        onChange={(event) =>
                          updateFallProtectionSupportProperty("insulationThickness", event.target.value)
                        }
                      />
                    </label>
                  )}

                  {anchorPointMaterialOptions.length > 1 && (
                    <label className="flex flex-col gap-1 text-[11px] font-bold text-[#1d5cab]">
                      Material
                      <select
                        className="rounded-md border border-[rgba(74,58,31,0.18)] bg-white px-3 py-2 text-[12px] font-semibold text-[#1d5cab] outline-none focus:border-[#1d5cab]"
                        value={resolvedAnchorPointMaterialId}
                        onChange={(event) =>
                          updateFallProtectionSupportProperty("anchorPointMaterial", event.target.value)
                        }
                      >
                        {anchorPointMaterialOptions.map((material) => (
                          <option key={material.id} value={material.id}>
                            {material.name}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}

                  {shouldShowAnchorPointMappingSelect && (
                    <label className="flex flex-col gap-1 text-[11px] font-bold text-[#1d5cab]">
                      Anchor point code
                      <select
                        className="rounded-md border border-[rgba(74,58,31,0.18)] bg-white px-3 py-2 text-[12px] font-semibold text-[#1d5cab] outline-none focus:border-[#1d5cab]"
                        value={effectiveAnchorPointMappingId}
                        disabled={anchorPointDefaultCodeOptions.length === 0}
                        onChange={(event) =>
                          updateFallProtectionSupportProperty("anchorPointMapping", event.target.value)
                        }
                      >
                        <option value="">
                          {anchorPointDefaultCodeOptions.length === 0 ? "No compatible code" : "Select..."}
                        </option>
                        {anchorPointDefaultCodeOptions.map((mapping) => (
                          <option key={mapping.id} value={mapping.id}>
                            {mapping.item.code}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}

                  {!shouldShowAnchorPointMappingSelect && effectiveAnchorPointCode && (
                    <label className="flex flex-col gap-1 text-[11px] font-bold text-[#1d5cab]">
                      Anchor point code
                      <input
                        className="rounded-md border border-[rgba(74,58,31,0.18)] bg-[rgba(255,255,255,0.65)] px-3 py-2 text-[12px] font-semibold text-[#1d5cab] outline-none"
                        type="text"
                        value={effectiveAnchorPointCode}
                        disabled
                      />
                    </label>
                  )}

                  {effectiveAnchorPointMaterialLabel && anchorPointMaterialOptions.length <= 1 && (
                    <label className="flex flex-col gap-1 text-[11px] font-bold text-[#1d5cab]">
                      Material
                      <input
                        className="rounded-md border border-[rgba(74,58,31,0.18)] bg-[rgba(255,255,255,0.65)] px-3 py-2 text-[12px] font-semibold text-[#1d5cab] outline-none"
                        type="text"
                        value={effectiveAnchorPointMaterialLabel}
                        disabled
                      />
                    </label>
                  )}

                  {!anchorPointSeamsSpanRequired &&
                    !anchorPointInsulationThicknessRequired &&
                    !shouldShowAnchorPointMappingSelect &&
                    !effectiveAnchorPointCode &&
                    !effectiveAnchorPointMaterialLabel && (
                      <div className="rounded-md border border-[rgba(74,58,31,0.10)] bg-white/70 px-3 py-2 text-[11px] text-[#6c5b3d]">
                        No mandatory anchor point selection
                      </div>
                    )}
                </div>

                <div className="flex flex-col gap-3">
                  <div className="flex flex-col gap-2 rounded-md border border-[rgba(74,58,31,0.12)] bg-white/70 p-2 text-[11px]">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[#6c5b3d]">
                      {selectedAnchorPointSystemPictureLabel}
                    </span>
                    <div className="flex h-32 items-center justify-center overflow-hidden rounded-md bg-white">
                      {shouldShowAnchorPointSystemImage && selectedAnchorPointSystemImageUrl ? (
                        <img
                          src={selectedAnchorPointSystemImageUrl}
                          alt={selectedAnchorPointDefaultSystem?.name ?? "Anchor Point system"}
                          className="max-h-full max-w-full object-contain"
                          onError={() => setFailedAnchorPointSystemImageUrl(selectedAnchorPointSystemImageUrl)}
                        />
                      ) : (
                        <div className="flex h-full w-full flex-col items-center justify-center gap-2 rounded-md border border-dashed border-[rgba(74,58,31,0.24)] bg-[rgba(245,240,230,0.58)] px-3 text-center text-[#6c5b3d]">
                          <svg viewBox="0 0 24 24" aria-hidden="true" className="h-7 w-7 fill-none stroke-current">
                            <rect x="4" y="5" width="16" height="14" rx="2" strokeWidth="1.8" />
                            <path d="m7 16 3.5-3.5 2.5 2.5 2-2 2 3" strokeWidth="1.8" strokeLinejoin="round" />
                            <circle cx="9" cy="9" r="1.2" fill="currentColor" stroke="none" />
                          </svg>
                          <span className="text-[10px] font-semibold leading-tight">
                            {selectedAnchorPointDefaultSystem?.image
                              ? `${selectedAnchorPointSystemPictureLabel} picture not loaded from server`
                              : `No ${selectedAnchorPointSystemPictureLabel} picture configured`}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {effectiveAnchorPointProductCodes && (
                    <div className="flex flex-col gap-1 rounded-md border border-[rgba(74,58,31,0.10)] bg-white/70 p-2 text-[11px]">
                      <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-[#1d5cab]">
                        Additional products
                      </span>
                      <span className="font-semibold text-[#1d5cab]">{effectiveAnchorPointProductCodes}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        </aside>
      ) : (
        <button
          type="button"
          className="absolute right-10 top-10 z-20 inline-flex h-5 w-5 cursor-pointer items-center justify-center rounded-full border-0 bg-[rgba(245,240,230,0.96)] text-[#332917] shadow-[0_12px_24px_rgba(0,0,0,0.14)]"
          style={getFloatingPanelStyle("system")}
          aria-label="Open System Settings"
          title="Open System Settings"
          onClick={() => {
            bringFloatingPanelToFront("system");
            setIsEntityPropertiesOpen(true);
          }}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true" className="h-[14px] w-[14px] fill-current">
            <path d="M4 7h16v2H4V7Zm0 5h16v2H4v-2Zm0 5h16v2H4v-2Z" />
          </svg>
        </button>
      )}

      {isPropertiesOpen && (
        <aside
          className="absolute bottom-24 left-[7rem] z-20 flex max-h-[72vh] w-[340px] flex-col gap-3 overflow-y-auto rounded-[6px] border border-[rgba(74,58,31,0.12)] bg-[rgba(245,240,230,0.96)] p-3 text-[#1f2937] shadow-[0_20px_40px_rgba(58,45,20,0.14)] backdrop-blur-[10px]"
          style={getFloatingPanelStyle("propertiesPanel")}
          aria-label="Properties"
          onMouseDownCapture={() => bringFloatingPanelToFront("propertiesPanel")}
          onFocusCapture={() => bringFloatingPanelToFront("propertiesPanel")}
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="m-0 text-[11px] font-bold uppercase tracking-[0.08em] text-[#4b3e27]">Properties</h3>
              <div className="mt-1 text-[10px] text-[#6c5b3d]">{selectedEntitySubtitle}</div>
            </div>
            <button
              type="button"
              className="inline-flex h-5 w-5 cursor-pointer items-center justify-center rounded-full border-0 bg-[rgba(33,27,18,0.09)] text-[#332917]"
              aria-label="Close Properties"
              title="Close Properties"
              onClick={() => setIsPropertiesOpen(false)}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true" className="h-[14px] w-[14px] fill-current">
                <path d="M4 7h16v2H4V7Zm0 5h16v2H4v-2Zm0 5h16v2H4v-2Z" />
              </svg>
            </button>
          </div>

          <div className="grid grid-cols-2 gap-3 rounded-md border border-[rgba(74,58,31,0.12)] bg-[rgba(255,255,255,0.72)] p-3">
            <label className="flex flex-col gap-1 text-[11px] font-medium text-[#4b3e27]">
              Name
              <input
                className="rounded-md border border-[rgba(74,58,31,0.18)] bg-white px-3 py-2 text-[12px] text-[#1f2937] outline-none focus:border-[#1d5cab] disabled:bg-[rgba(255,255,255,0.6)] disabled:text-[#9ca3af]"
                value={singleSelectedEntity ? entityNameDraft : ""}
                placeholder={singleSelectedEntity ? "Entity name" : "One entity"}
                disabled={!singleSelectedEntity}
                onChange={(event) => {
                  setEntityNameDraft(event.target.value);
                  updateSingleSelectedEntityName(event.target.value);
                }}
                onFocus={() => beginHistoryAction()}
                onBlur={() => finishHistoryAction()}
                onKeyDown={(event) => {
                  if (event.key !== "Enter") return;
                  event.currentTarget.blur();
                }}
              />
            </label>

            <label className="flex flex-col gap-1 text-[11px] font-medium text-[#4b3e27]">
              Layer
              <select
                className="rounded-md border border-[rgba(74,58,31,0.18)] bg-white px-3 py-2 text-[12px] text-[#1f2937] outline-none focus:border-[#1d5cab]"
                value={entityLayerDraft}
                onChange={(event) => {
                  const nextLayer = event.target.value as EntityLayerName;
                  setEntityLayerDraft(nextLayer);
                  updateSelectedEntityLayer(nextLayer);
                }}
              >
                <option value="" disabled>
                  Mixed values
                </option>
                {ENTITY_ASSIGNABLE_LAYER_OPTIONS.map((layer) => (
                  <option key={layer} value={layer}>
                    {getLayerDisplayName(layer)}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex flex-col gap-1 text-[11px] font-medium text-[#4b3e27]">
              Line type
              <select
                className="rounded-md border border-[rgba(74,58,31,0.18)] bg-white px-3 py-2 text-[12px] text-[#1f2937] outline-none focus:border-[#1d5cab] disabled:bg-[rgba(255,255,255,0.6)] disabled:text-[#9ca3af]"
                value={commonSelectedEntityTypeStyle?.lineType ?? ""}
                disabled={!commonSelectedEntityStyleKind}
                onChange={(event) => {
                  if (!commonSelectedEntityStyleKind) return;
                  updateEntityTypeStyle(commonSelectedEntityStyleKind, "lineType", event.target.value as LineType);
                }}
              >
                <option value="" disabled>
                  Mixed values
                </option>
                {LINE_TYPE_VALUES.map((lineType) => (
                  <option key={lineType} value={lineType}>
                    {LINE_TYPE_LABELS[lineType]}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex flex-col gap-1 text-[11px] font-medium text-[#4b3e27]">
              Color
              <div className="relative rounded-md border border-[rgba(74,58,31,0.18)] bg-white px-2 py-2">
                <button
                  type="button"
                  className="flex w-full items-center gap-2 rounded-md text-left transition active:scale-[0.99]"
                  onClick={() => setIsEntityColorPaletteOpen((current) => !current)}
                >
                  <span
                    className="inline-flex h-8 w-10 shrink-0 rounded-md border border-[rgba(74,58,31,0.18)]"
                    style={{ backgroundColor: entityColorDraft }}
                    aria-hidden="true"
                  />
                  <span className="min-w-0 flex-1 truncate text-[12px] font-medium text-[#1f2937]">
                    {commonSelectedColor ?? "Mixed values"}
                  </span>
                  <svg
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                    className={`h-4 w-4 fill-none stroke-current text-[#6c5b3d] transition ${
                      isEntityColorPaletteOpen ? "rotate-180" : ""
                    }`}
                  >
                    <path d="m6 9 6 6 6-6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>

                {isEntityColorPaletteOpen && (
                  <div className="absolute left-0 top-full z-30 mt-2 grid w-full grid-cols-5 gap-2 rounded-md border border-[rgba(74,58,31,0.18)] bg-[rgba(245,240,230,0.98)] p-2 shadow-[0_14px_28px_rgba(58,45,20,0.14)]">
                    {AUTOCAD_STANDARD_COLORS.map((color) => {
                      const isActive = entityColorDraft.toLowerCase() === color.value;

                      return (
                        <button
                          key={color.aci}
                          type="button"
                          className={`aspect-square w-full rounded-md border transition active:scale-110 ${
                            isActive ? "border-[#1d5cab] ring-2 ring-[#1d5cab]/45" : "border-[rgba(74,58,31,0.18)]"
                          }`}
                          style={{ backgroundColor: color.value }}
                          title={`ACI ${color.aci} - ${color.label}`}
                          aria-label={`ACI ${color.aci} - ${color.label}`}
                          onClick={() => {
                            setEntityColorDraft(color.value);
                            updateSelectedEntityStrokeColor(color.value);
                            setIsEntityColorPaletteOpen(false);
                          }}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            </label>

            {selectedEntityHasVertexStyle && (
              <label className="flex flex-col gap-1 text-[11px] font-medium text-[#4b3e27]">
                Vertex color
                <div className="relative rounded-md border border-[rgba(74,58,31,0.18)] bg-white px-2 py-2">
                  <button
                    type="button"
                    disabled={!commonSelectedEntityStyleKind || !commonSelectedEntityTypeStyle}
                    className="flex w-full items-center gap-2 rounded-md text-left transition active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-50"
                    onClick={() => setIsEntityVertexColorPaletteOpen((current) => !current)}
                  >
                    <span
                      className="inline-flex h-8 w-10 shrink-0 rounded-md border border-[rgba(74,58,31,0.18)]"
                      style={{ backgroundColor: commonSelectedEntityTypeStyle?.vertexColor ?? "#ffffff" }}
                      aria-hidden="true"
                    />
                    <span className="min-w-0 flex-1 truncate text-[12px] font-medium text-[#1f2937]">
                      {commonSelectedEntityTypeStyle?.vertexColor ?? "Mixed values"}
                    </span>
                    <svg
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                      className={`h-4 w-4 fill-none stroke-current text-[#6c5b3d] transition ${
                        isEntityVertexColorPaletteOpen ? "rotate-180" : ""
                      }`}
                    >
                      <path d="m6 9 6 6 6-6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>

                  {isEntityVertexColorPaletteOpen && commonSelectedEntityStyleKind && commonSelectedEntityTypeStyle && (
                    <div className="absolute left-0 top-full z-30 mt-2 grid w-full grid-cols-5 gap-2 rounded-md border border-[rgba(74,58,31,0.18)] bg-[rgba(245,240,230,0.98)] p-2 shadow-[0_14px_28px_rgba(58,45,20,0.14)]">
                      {AUTOCAD_STANDARD_COLORS.map((color) => {
                        const isActive = commonSelectedEntityTypeStyle.vertexColor.toLowerCase() === color.value;

                        return (
                          <button
                            key={color.aci}
                            type="button"
                            className={`aspect-square w-full rounded-md border transition active:scale-110 ${
                              isActive ? "border-[#1d5cab] ring-2 ring-[#1d5cab]/45" : "border-[rgba(74,58,31,0.18)]"
                            }`}
                            style={{ backgroundColor: color.value }}
                            title={`ACI ${color.aci} - ${color.label}`}
                            aria-label={`ACI ${color.aci} - ${color.label}`}
                            onClick={() => {
                              updateEntityTypeStyle(commonSelectedEntityStyleKind, "vertexColor", color.value);
                              setIsEntityVertexColorPaletteOpen(false);
                            }}
                          />
                        );
                      })}
                    </div>
                  )}
                </div>
              </label>
            )}

            <div className="flex flex-col gap-1 text-[11px] font-medium text-[#4b3e27]">
              Style
              <div className="truncate rounded-md border border-[rgba(74,58,31,0.18)] bg-white px-3 py-2 text-[12px] text-[#1f2937]">
                {commonSelectedEntityStyleLabel ?? "Mixed values"}
              </div>
            </div>
          </div>
        </aside>
      )}

      {gripContextMenu && (
        <div
          ref={gripContextMenuRef}
          className="fixed z-40 min-w-[168px] rounded-md border border-[rgba(74,58,31,0.18)] bg-[rgba(245,240,230,0.98)] p-1 shadow-[0_14px_28px_rgba(58,45,20,0.18)]"
          style={{
            left: gripContextMenu.x + 8,
            top: gripContextMenu.y + 8,
          }}
          onMouseDown={(event) => event.stopPropagation()}
        >
          <button
            type="button"
            className="flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-[12px] font-medium text-[#1f2937] transition hover:bg-[rgba(29,92,171,0.08)] disabled:cursor-not-allowed disabled:opacity-45"
            onClick={handleOpenOrSplitGripVertex}
            disabled={
              !canOpenOrSplitGripVertex(
                gripContextMenu.entityId,
                gripContextMenu.vertexIndex,
                gripContextMenu.holeIndex,
                gripContextMenu.linkedVertexIndices,
              )
            }
          >
            <span> Break / Open</span>
            <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-none stroke-current">
              <path
                d="M5 7h6m2 0h6M7 17h4m2 0h4M11 7l2 10"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          <button
            type="button"
            className="flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-[12px] font-medium text-[#1f2937] transition hover:bg-[rgba(29,92,171,0.08)] disabled:cursor-not-allowed disabled:opacity-45"
            onClick={handleInsertGripVertex}
            disabled={
              !canInsertGripVertex(gripContextMenu.entityId, gripContextMenu.vertexIndex, gripContextMenu.holeIndex)
            }
          >
            <span>New vertex</span>
            <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-none stroke-current">
              <path d="M12 5v14M5 12h14" strokeWidth="1.8" strokeLinecap="round" />
              <circle cx="12" cy="12" r="7" strokeWidth="1.6" />
            </svg>
          </button>

          <button
            type="button"
            className="flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-[12px] font-medium text-[#1f2937] transition hover:bg-[rgba(29,92,171,0.08)] disabled:cursor-not-allowed disabled:opacity-45"
            onClick={handleRemoveGripVertex}
            disabled={
              !canRemoveGripVertex(
                gripContextMenu.entityId,
                gripContextMenu.holeIndex,
                gripContextMenu.linkedVertexIndices,
              )
            }
          >
            <span>Delete vertice</span>
            <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-none stroke-current">
              <path
                d="M4 7h16M9 7V5.5c0-.8.7-1.5 1.5-1.5h3c.8 0 1.5.7 1.5 1.5V7m-8 0 1 12h8l1-12M10 11v5M14 11v5"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      )}

      <aside
        className="absolute bottom-24 right-7 z-20 flex flex-col gap-2 rounded-[6px] border border-[rgba(74,58,31,0.12)] bg-[rgba(245,240,230,0.94)] p-2 shadow-[0_20px_40px_rgba(58,45,20,0.14)] backdrop-blur-[10px]"
        style={getFloatingPanelStyle("exportDock")}
        aria-label="EXPORT"
        onMouseDownCapture={() => bringFloatingPanelToFront("exportDock")}
        onFocusCapture={() => bringFloatingPanelToFront("exportDock")}
      >
        <button
          type="button"
          className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-[rgba(74,58,31,0.12)] bg-white text-[#1f2937] transition active:scale-110"
          aria-label={isExportToolbarOpen ? "Close EXPORT toolbar" : "Open EXPORT toolbar"}
          title={isExportToolbarOpen ? "Close EXPORT" : "EXPORT"}
          onClick={() => {
            bringFloatingPanelToFront("exportDock");
            setIsExportToolbarOpen((current) => !current);
          }}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-none stroke-current">
            <path d="M4 7h16M4 12h16M4 17h16" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>

        {isExportToolbarOpen && (
          <>
            <div className="px-1 text-center text-[9px] font-bold uppercase tracking-[0.08em] text-[#4b3e27]">
              EXPORT
            </div>

            <button
              type="button"
              className={`inline-flex h-10 w-10 items-center justify-center rounded-md bg-white text-[#1f2937] transition active:scale-110 ${
                isPrintExportOpen ? "border-2 border-[#1d5cab]" : "border border-[rgba(74,58,31,0.12)]"
              }`}
              aria-label="Print"
              title="Print"
              onClick={() => {
                bringFloatingPanelToFront("printExport");
                setIsPrintExportOpen((current) => !current);
              }}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-none stroke-current">
                <path d="M7 8V4h10v4M7 17H5V9h14v8h-2" strokeWidth="1.8" strokeLinejoin="round" />
                <path d="M8 14h8v6H8v-6Z" strokeWidth="1.8" strokeLinejoin="round" />
                <path d="M16.5 11h.01" strokeWidth="2.4" strokeLinecap="round" />
              </svg>
            </button>

            <button
              type="button"
              className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-[rgba(74,58,31,0.12)] bg-white text-[#1f2937] transition active:scale-110"
              aria-label="Import JSON"
              title="Import JSON"
              onClick={() => canvasObjectsInputRef.current?.click()}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-none stroke-current">
                <path d="M12 21V10" strokeWidth="2" strokeLinecap="round" />
                <path d="m7 14 5-5 5 5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M5 5h14" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>

            <button
              type="button"
              disabled={polylines.length === 0 && dimensions.length === 0 && textEntities.length === 0}
              className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-[rgba(74,58,31,0.12)] bg-white text-[#1f2937] transition active:scale-110 disabled:cursor-not-allowed disabled:opacity-40"
              aria-label="Export JSON"
              title="Export JSON"
              onClick={handleExportCanvasObjects}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-none stroke-current">
                <path d="M12 3v11" strokeWidth="2" strokeLinecap="round" />
                <path d="m7 10 5 5 5-5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M5 19h14" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>

            <button
              type="button"
              className={`inline-flex h-10 w-10 items-center justify-center rounded-md bg-white text-[#1f2937] transition active:scale-110 ${
                isBillOfMaterialsOpen ? "border-2 border-[#1d5cab]" : "border border-[rgba(74,58,31,0.12)]"
              }`}
              aria-label="Bill of Materials"
              title="Bill of Materials"
              onClick={() => {
                bringFloatingPanelToFront("billOfMaterials");
                setIsBillOfMaterialsOpen((current) => !current);
              }}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-none stroke-current">
                <path d="M7 4h10l2 2v14H5V4h2Z" strokeWidth="1.7" strokeLinejoin="round" />
                <path d="M8 9h8M8 13h8M8 17h5" strokeWidth="1.7" strokeLinecap="round" />
              </svg>
            </button>
          </>
        )}
      </aside>

      <aside
        className="absolute bottom-7 left-7 z-20 flex gap-2 rounded-[6px] border border-[rgba(74,58,31,0.12)] bg-[rgba(245,240,230,0.94)] p-2 shadow-[0_20px_40px_rgba(58,45,20,0.14)] backdrop-blur-[10px]"
        style={getFloatingPanelStyle("settingsDock")}
        aria-label="Settings"
        onMouseDownCapture={() => bringFloatingPanelToFront("settingsDock")}
        onFocusCapture={() => bringFloatingPanelToFront("settingsDock")}
      >
        <button
          type="button"
          className={`inline-flex h-10 w-10 items-center justify-center rounded-md bg-white text-[#1f2937] transition active:scale-110 ${
            isSettingsOpen ? "border-2 border-[#1d5cab]" : "border border-[rgba(74,58,31,0.12)]"
          }`}
          aria-label="Settings"
          title="Settings"
          onClick={() => {
            bringFloatingPanelToFront("settingsPanel");
            setIsSettingsOpen((current) => !current);
          }}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-none stroke-current">
            <path
              d="M10.3 3.3h3.4l.5 2.1c.4.1.8.3 1.2.5l1.9-1.1 2.4 2.4-1.1 1.9c.2.4.4.8.5 1.2l2.1.5v3.4l-2.1.5c-.1.4-.3.8-.5 1.2l1.1 1.9-2.4 2.4-1.9-1.1c-.4.2-.8.4-1.2.5l-.5 2.1h-3.4l-.5-2.1c-.4-.1-.8-.3-1.2-.5l-1.9 1.1-2.4-2.4 1.1-1.9c-.2-.4-.4-.8-.5-1.2l-2.1-.5v-3.4l2.1-.5c.1-.4.3-.8.5-1.2L4.9 7.2l2.4-2.4 1.9 1.1c.4-.2.8-.4 1.2-.5l.5-2.1Z"
              strokeWidth="1.7"
              strokeLinejoin="round"
            />
            <circle cx="12" cy="12" r="3.2" strokeWidth="1.7" />
          </svg>
        </button>

        <button
          type="button"
          className={`inline-flex h-10 w-10 items-center justify-center rounded-md bg-white text-[#1f2937] transition active:scale-110 ${
            isLayerVisibilityOpen ? "border-2 border-[#1d5cab]" : "border border-[rgba(74,58,31,0.12)]"
          }`}
          aria-label="Layer"
          title="Layer"
          onClick={() => {
            bringFloatingPanelToFront("layerPanel");
            setIsLayerVisibilityOpen((current) => !current);
          }}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-none stroke-current">
            <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12Z" strokeWidth="1.8" strokeLinejoin="round" />
            <circle cx="12" cy="12" r="3" strokeWidth="1.8" />
          </svg>
        </button>

        <button
          type="button"
          className={`inline-flex h-10 w-10 items-center justify-center rounded-md bg-white text-[#1f2937] transition active:scale-110 ${
            isPropertiesOpen ? "border-2 border-[#1d5cab]" : "border border-[rgba(74,58,31,0.12)]"
          }`}
          aria-label="Properties"
          title="Properties"
          onClick={() => {
            bringFloatingPanelToFront("propertiesPanel");
            setIsPropertiesOpen((current) => !current);
          }}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-none stroke-current">
            <path d="M5 5h14v14H5z" strokeWidth="1.8" strokeLinejoin="round" />
            <path d="M8 9h8M8 13h5" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        </button>
      </aside>

      {isBillOfMaterialsOpen && (
        <aside
          className="absolute bottom-[6.5rem] right-24 z-20 flex max-h-[72vh] w-[340px] flex-col gap-3 overflow-y-auto rounded-[6px] border border-[rgba(74,58,31,0.12)] bg-[rgba(245,240,230,0.96)] p-3 text-[#1f2937] shadow-[0_20px_40px_rgba(58,45,20,0.14)] backdrop-blur-[10px]"
          style={getFloatingPanelStyle("billOfMaterials")}
          aria-label="Bill of Materials"
          onMouseDownCapture={() => bringFloatingPanelToFront("billOfMaterials")}
          onFocusCapture={() => bringFloatingPanelToFront("billOfMaterials")}
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="m-0 text-[11px] font-bold uppercase tracking-[0.08em] text-[#4b3e27]">
                Bill of Materials
              </h3>
              <div className="mt-1 text-[10px] text-[#6c5b3d]">
                {billOfMaterialsSummary.lines.length} lifeline
                {billOfMaterialsSummary.lines.length === 1 ? "" : "s"}
              </div>
            </div>
            <button
              type="button"
              className="inline-flex h-5 w-5 cursor-pointer items-center justify-center rounded-full border-0 bg-[rgba(33,27,18,0.09)] text-[#332917]"
              aria-label="Close Bill of Materials"
              title="Close Bill of Materials"
              onClick={() => setIsBillOfMaterialsOpen(false)}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true" className="h-[14px] w-[14px] fill-current">
                <path d="M4 7h16v2H4V7Zm0 5h16v2H4v-2Zm0 5h16v2H4v-2Z" />
              </svg>
            </button>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-md border border-[rgba(74,58,31,0.10)] bg-white/70 p-2">
              <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[#6c5b3d]">Total Lengths</div>
              <div className="mt-1 text-[13px] font-bold text-[#1f2937]">
                {formatMeasurement(billOfMaterialsSummary.totalLength)}
              </div>
            </div>
            <div className="rounded-md border border-[rgba(74,58,31,0.10)] bg-white/70 p-2">
              <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[#6c5b3d]">Positions</div>
              <div className="mt-1 text-[13px] font-bold text-[#1f2937]">{billOfMaterialsSummary.totalPositions}</div>
            </div>
          </div>

          {billOfMaterialsSummary.lines.length === 0 ? (
            <div className="rounded-md border border-dashed border-[rgba(74,58,31,0.18)] bg-white/60 px-3 py-4 text-[11px] text-[#6c5b3d]">
              No visible lifelines.
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {billOfMaterialsSummary.lines.map((line) => {
                const isBillOfMaterialsLineSelected = selectedIds.includes(line.id);
                const selectBillOfMaterialsLine = () => {
                  setSelectedIds([line.id]);
                  setSelectionCycle(null);
                  setEditingId(null);
                  setToolMode("select");
                };

                return (
                  <div
                    key={line.id}
                    role="button"
                    tabIndex={0}
                    aria-label={`Select ${line.name}`}
                    className={`flex cursor-pointer flex-col gap-2 rounded-md border p-2 text-left transition hover:border-[#1d5cab] hover:bg-[rgba(29,92,171,0.08)] focus:outline-none focus:ring-2 focus:ring-[#1d5cab] ${
                      isBillOfMaterialsLineSelected
                        ? "border-[#1d5cab] bg-[rgba(29,92,171,0.12)]"
                        : "border-[rgba(74,58,31,0.10)] bg-white/70"
                    }`}
                    onClick={selectBillOfMaterialsLine}
                    onKeyDown={(event) => {
                      if (event.key !== "Enter" && event.key !== " ") return;
                      event.preventDefault();
                      selectBillOfMaterialsLine();
                    }}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="truncate text-[12px] font-bold text-[#1f2937]">{line.name}</div>
                        <div className="text-[10px] text-[#6c5b3d]">Lifeline {line.lineNumber}</div>
                      </div>
                      <div className="shrink-0 text-right text-[12px] font-bold text-[#1d5cab]">
                        {formatMeasurement(line.length)}
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-1">
                      {line.roleCounts.length > 0 ? (
                        line.roleCounts.map((item) => (
                          <span
                            key={`${line.id}-${item.role}`}
                            className="rounded-full bg-[rgba(29,92,171,0.10)] px-2 py-1 text-[10px] font-semibold text-[#1d5cab]"
                          >
                            {item.role}: {item.count}
                          </span>
                        ))
                      ) : (
                        <span className="text-[10px] text-[#6c5b3d]">No labels</span>
                      )}
                    </div>

                    <div className="max-h-20 overflow-y-auto rounded-md bg-[rgba(74,58,31,0.06)] px-2 py-1 text-[10px] leading-5 text-[#4b3e27]">
                      {line.positions.length > 0 ? line.positions.join(" / ") : "No positions"}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="flex flex-col gap-2 rounded-md border border-[rgba(74,58,31,0.10)] bg-white/70 p-2">
            <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[#6c5b3d]">
              Summary of total labels
            </div>
            <div className="flex flex-wrap gap-1">
              {billOfMaterialsSummary.roleTotals.length > 0 ? (
                billOfMaterialsSummary.roleTotals.map((item) => (
                  <span
                    key={item.role}
                    className="rounded-full bg-[rgba(22,163,74,0.10)] px-2 py-1 text-[10px] font-semibold text-[#15803d]"
                  >
                    {item.role}: {item.count}
                  </span>
                ))
              ) : (
                <span className="text-[10px] text-[#6c5b3d]">No labels</span>
              )}
            </div>
            {billOfMaterialsSummary.positionLabels.length > 0 && (
              <div className="max-h-24 overflow-y-auto rounded-md bg-[rgba(74,58,31,0.06)] px-2 py-1 text-[10px] leading-5 text-[#4b3e27]">
                {billOfMaterialsSummary.positionLabels.join(" / ")}
              </div>
            )}
          </div>
        </aside>
      )}

      <SettingsPanel
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        panelStyle={getFloatingPanelStyle("settingsPanel")}
        onActivate={() => bringFloatingPanelToFront("settingsPanel")}
        drawingUnit={drawingUnit}
        onDrawingUnitChange={(value) => setDrawingUnit(value as UnitOption)}
        decimalSeparator={decimalSeparator}
        onDecimalSeparatorChange={(value) => setDecimalSeparator(value as DecimalSeparator)}
        gridSpacingDraft={gridSpacingDraft}
        onGridSpacingDraftChange={setGridSpacingDraft}
        commitGridSpacingDraft={commitGridSpacingDraft}
        isGridSnapEnabled={isGridSnapEnabled}
        onGridSnapEnabledChange={setIsGridSnapEnabled}
        gridSnapStepDraft={gridSnapStepDraft}
        onGridSnapStepDraftChange={setGridSnapStepDraft}
        commitGridSnapStepDraft={commitGridSnapStepDraft}
        osnapTypes={Object.keys(enabledOsnaps)}
        enabledOsnaps={enabledOsnaps}
        onOsnapToggle={(type, value) =>
          setEnabledOsnaps((current) => ({
            ...current,
            [type]: value,
          }))
        }
        entityLineWidth={entityLineWidth}
        onEntityLineWidthChange={setEntityLineWidth}
        previewLineWidth={previewLineWidth}
        onPreviewLineWidthChange={setPreviewLineWidth}
        dimensionTextHeightDraft={dimensionTextHeightDraft}
        onDimensionTextHeightDraftChange={setDimensionTextHeightDraft}
        commitDimensionTextHeightDraft={commitDimensionTextHeightDraft}
        dimensionTextGapDraft={dimensionTextGapDraft}
        onDimensionTextGapDraftChange={setDimensionTextGapDraft}
        commitDimensionTextGapDraft={commitDimensionTextGapDraft}
        dimensionMarkerStyle={dimensionMarkerStyle}
        onDimensionMarkerStyleChange={(value) => setDimensionMarkerStyle(value as DimensionMarkerStyle)}
        safetySystemOptions={safetySystemOptions}
        selectedSafetySystem={selectedSafetySystem}
        onSelectedSafetySystemChange={setSelectedSafetySystem}
        safetySystemSettings={safetySystemSettings}
        updateSafetySystemSetting={(system, field, value) =>
          updateSafetySystemSetting(
            system,
            field as keyof SafetySystemSettings,
            value as SafetySystemSettings[keyof SafetySystemSettings],
          )
        }
        shortcutOptions={SHORTCUT_COMMAND_OPTIONS}
        keyboardShortcuts={keyboardShortcuts}
        onKeyboardShortcutChange={(command, value) => handleShortcutChange(command as ShortcutCommand, value)}
        entityStyleOptions={ENTITY_STYLE_OPTIONS}
        entityTypeStyles={entityTypeStyles}
        updateEntityTypeStyle={(kind, field, value) =>
          updateEntityTypeStyle(
            kind as EntityStyleKind,
            field as keyof EntityTypeStyle,
            value as EntityTypeStyle[keyof EntityTypeStyle],
          )
        }
      />

      <LayerPanel
        isOpen={isLayerVisibilityOpen}
        panelStyle={getFloatingPanelStyle("layerPanel")}
        onActivate={() => bringFloatingPanelToFront("layerPanel")}
        layers={[...normalizeLayerOrder(layerOrder)].reverse().map((layer, index, topToBottomLayers) => ({
          name: layer,
          label: getLayerDisplayName(layer),
          visible: visibleLayers[layer],
          locked: lockedLayers[layer],
          canMoveUp: index > 0,
          canMoveDown: index < topToBottomLayers.length - 1,
          onMoveUp: () => moveLayerZ(layer, "up"),
          onMoveDown: () => moveLayerZ(layer, "down"),
          onToggleVisible: () =>
            setVisibleLayers((current) => ({
              ...current,
              [layer]: !current[layer],
            })),
          onToggleLocked: () =>
            setLockedLayers((current) => ({
              ...current,
              [layer]: !current[layer],
            })),
        }))}
      />

      <PrintExportPanel
        isOpen={isPrintExportOpen}
        panelStyle={getFloatingPanelStyle("printExport", {
          bottom: "96px",
          left: "auto",
          right: "92px",
          borderRight: "2px solid rgba(74,58,31,0.20)",
        })}
        onActivate={() => bringFloatingPanelToFront("printExport")}
        paperFormat={printPaperFormat}
        onPaperFormatChange={setPrintPaperFormat}
        titleBlockMode={printTitleBlockMode}
        onTitleBlockModeChange={setPrintTitleBlockMode}
        colorMode={printColorMode}
        onColorModeChange={setPrintColorMode}
        savedTitleBlockEntityCount={savedTitleBlockEntityCount}
        selectedEntityCount={selectedIds.length}
        printableEntityCount={polylines.length + dimensions.length + textEntities.length + iconEntities.length}
        computedScaleLabel={computedPrintScaleLabel}
        colorPreview={printColorPreview}
        status={printExportStatus}
        onSaveTitleBlockSelection={handleSaveTitleBlockSelection}
        onExportPdf={handleExportPdf}
      />

      <PrimitiveStatusBar
        title={effectiveStatusBarTitle}
        hint={effectiveStatusBarHint}
        showMultiGableActions={showMultiGableStatusActions}
        multiGableActiveStep={activeMultiGableStep}
        multiGableCanSelectSecondaryRakes={canSelectMultiGableSecondaryRakes}
        multiGableCanSelectHips={canSelectMultiGableHips}
        multiGableCanFinalize={canFinalizeMultiGableRoof}
        onMultiGableMainRakes={() =>
          setMultiGableRoofDraft((current) =>
            current
              ? {
                  ...current,
                  stage: "mainRakes",
                }
              : current,
          )
        }
        onMultiGableSecondaryRakes={() =>
          setMultiGableRoofDraft((current) =>
            current && current.mainRakes.length === 2
              ? {
                  ...current,
                  stage: "secondaryRakes",
                }
              : current,
          )
        }
        onMultiGableHips={() => {
          if (multiGableRoofDraft && multiGableRoofDraft.mainRakes.length === 2) {
            advanceMultiGableSecondaryRakes(multiGableRoofDraft);
          }
        }}
        onMultiGableFinalize={() => {
          if (multiGableRoofDraft) {
            commitMultiGableRoofDraft(multiGableRoofDraft);
          }
        }}
        backgroundReferenceInput={backgroundReferenceInput}
        onBackgroundReferenceInputChange={(value) =>
          setBackgroundReferenceInput((current) => (current ? { ...current, value } : current))
        }
        onCommitBackgroundReference={commitBackgroundReferenceScale}
        referenceLengthInputRef={referenceLengthInputRef}
        hatchToolVisible={toolMode === "hatch" && !backgroundReferenceInput && !numericEdit}
        hatchToolPattern={hatchToolPattern}
        onHatchToolPatternChange={setHatchToolPattern}
        hatchToolScale={hatchToolScale}
        onHatchToolScaleChange={setHatchToolScale}
        hatchToolColor={hatchToolColor}
        onHatchToolColorChange={setHatchToolColor}
        hatchToolMode={hatchToolMode}
        onHatchToolModeChange={setHatchToolMode}
        iconToolVisible={toolMode === "icon" && !backgroundReferenceInput && !numericEdit}
        iconToolColor={iconToolColor}
        onIconToolColorChange={setIconToolColor}
        iconToolScale={iconToolScale}
        onIconToolScaleChange={setIconToolScale}
        textToolVisible={toolMode === "text" && !backgroundReferenceInput && !numericEdit}
        textToolValue={textToolValue}
        onTextToolValueChange={setTextToolValue}
        textToolHeight={textToolHeight}
        onTextToolHeightChange={setTextToolHeight}
        textToolJustification={textToolJustification}
        onTextToolJustificationChange={(value) => setTextToolJustification(value as TextJustificationPoint)}
        textToolColor={textToolColor}
        onTextToolColorChange={setTextToolColor}
        textToolFont={textToolFont}
        onTextToolFontChange={setTextToolFont}
        textToolFonts={[...TEXT_TOOL_FONTS]}
        numericEditVisible={Boolean(numericEdit)}
        showDirectionInput={showDirectionInput}
        directionInputRef={directionInputRef}
        directionValue={directionalNumericEdit?.directionValue ?? ""}
        onDirectionFocus={(event) => {
          setActiveNumericInput("direction");
          setOverwriteNumericInput("direction");
          event.currentTarget.select();
        }}
        onDirectionChange={handleNumericDirectionChange}
        onDirectionKeyDown={(event) => {
          if (handleOverwriteNumericInputKey(event, "direction")) {
            return;
          }

          if (event.key === "Tab") {
            event.preventDefault();
            focusNumericField("value");
            return;
          }

          if (event.key === "Enter") {
            event.preventDefault();
            commitNumericEdit();
          }
        }}
        showLineDistanceInput={numericEdit?.kind === "point-series"}
        lineDistanceInputRef={lineDistanceInputRef}
        lineDistanceValue={numericEdit?.kind === "point-series" ? numericEdit.lineDistanceValue : ""}
        onLineDistanceFocus={(event) => {
          setActiveNumericInput("lineDistance");
          setOverwriteNumericInput("lineDistance");
          event.currentTarget.select();
        }}
        onLineDistanceChange={(value) => handleNumericEditChange(value)}
        onLineDistanceKeyDown={(event) => {
          if (handleOverwriteNumericInputKey(event, "lineDistance")) {
            return;
          }

          if (event.key === "Tab") {
            event.preventDefault();
            focusNumericField(numericEdit?.kind === "point-series" ? "lineDistance" : "value");
            return;
          }

          if (event.key === "Enter") {
            event.preventDefault();
            commitNumericEdit();
          }
        }}
        numericInputRef={numericInputRef}
        numericLabel={numericEditLabel ?? ""}
        numericValue={numericEdit?.value ?? ""}
        onNumericFocus={(event) => {
          setActiveNumericInput("value");
          setOverwriteNumericInput("value");
          event.currentTarget.select();
        }}
        onNumericChange={handleNumericEditChange}
        onNumericKeyDown={(event) => {
          if (handleOverwriteNumericInputKey(event, "value")) {
            return;
          }

          if (event.key === "Tab" && showDirectionInput) {
            event.preventDefault();
            focusNumericField(numericEdit?.kind === "point-series" ? "direction" : "direction");
            return;
          }

          if (event.key === "Enter") {
            event.preventDefault();
            commitNumericEdit();
          }
        }}
        unitLabel={numericEdit?.kind === "multi-shed-count" ? "shed" : drawingUnit}
        onCommitNumericEdit={commitNumericEdit}
        activeSystemTitle={activeSystemStatusSummary?.title}
        activeSystemCodes={activeSystemStatusSummary?.codes}
        activeSystemProducts={activeSystemStatusSummary?.products}
        historyStatusLabel={historyStatusLabel}
        canUndo={canUndo}
        canRedo={canRedo}
        onUndo={handleUndo}
        onRedo={handleRedo}
      />

      <div
        className="relative min-h-0 flex-1 overflow-hidden rounded-xl border border-[rgba(74,58,31,0.14)] bg-[#fffefb]"
        style={{ cursor: viewportCursor }}
        ref={viewportRef}
      >
        <Stage
          ref={stageRef}
          width={viewportSize.width}
          height={viewportSize.height}
          style={{ cursor: viewportCursor }}
          x={viewport.x}
          y={viewport.y}
          scaleX={viewport.scale}
          scaleY={viewport.scale}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          onDblClick={handleDoubleClick}
          onWheel={handleWheel}
          className="block"
        >
          <Layer>
            {backgroundAsset && (
              <Fragment>
                <KonvaImage
                  image={backgroundAsset.image}
                  x={backgroundAsset.center.x}
                  y={backgroundAsset.center.y}
                  offsetX={backgroundAsset.width / 2}
                  offsetY={backgroundAsset.height / 2}
                  width={backgroundAsset.width}
                  height={backgroundAsset.height}
                  rotation={backgroundAsset.rotation}
                  opacity={1 - backgroundTransparency / 100}
                />
              </Fragment>
            )}
          </Layer>

          <Layer listening={false}>
            {(() => {
              const { minX, minY, maxX, maxY } = getVisibleWorldBounds();
              const startX = Math.floor(minX / gridSpacing) * gridSpacing;
              const startY = Math.floor(minY / gridSpacing) * gridSpacing;
              const verticalLines: number[] = [];
              const horizontalLines: number[] = [];

              for (let x = startX; x <= maxX + gridSpacing; x += gridSpacing) {
                verticalLines.push(x);
              }

              for (let y = startY; y <= maxY + gridSpacing; y += gridSpacing) {
                horizontalLines.push(y);
              }

              return (
                <>
                  {verticalLines.map((x) => (
                    <Line
                      key={`grid-v-${x}`}
                      points={[x, minY - gridSpacing, x, maxY + gridSpacing]}
                      stroke="rgba(124,134,145,0.14)"
                      strokeWidth={1 / viewport.scale}
                    />
                  ))}
                  {horizontalLines.map((y) => (
                    <Line
                      key={`grid-h-${y}`}
                      points={[minX - gridSpacing, y, maxX + gridSpacing, y]}
                      stroke="rgba(124,134,145,0.14)"
                      strokeWidth={1 / viewport.scale}
                    />
                  ))}
                </>
              );
            })()}
          </Layer>

          {orderedLayerItems.map((layerItem) => (
            <Layer key={layerItem.layer}>
              {layerItem.shapes.map(renderShapeEntity)}
              {layerItem.dimensions.map(renderDimensionEntity)}
              {layerItem.textEntities.map(renderTextEntity)}
              {layerItem.iconEntities.map(renderIconEntity)}
            </Layer>
          ))}

          {iconPreviewEntity && (
            <Layer listening={false}>
              <CanvasIconNode
                icon={iconPreviewEntity}
                isSelected
                isLocked
                toolMode={toolMode}
                selectedStrokeWidth={getScreenInvariantSize(1.5)}
                dash={[getScreenInvariantSize(6), getScreenInvariantSize(4)]}
                opacity={0.78}
                listening={false}
                onSelect={() => undefined}
                onDragStart={() => undefined}
                onDragMove={() => undefined}
                onDragEnd={() => undefined}
              />
            </Layer>
          )}

          <Layer>
            {isBackgroundToolbarOpen &&
              backgroundAsset?.reference &&
              (() => {
                const referenceWorld = getBackgroundReferenceWorldPoints(backgroundAsset);
                if (!referenceWorld) return null;

                return (
                  <>
                    <Line
                      points={[
                        referenceWorld.start.x,
                        referenceWorld.start.y,
                        referenceWorld.end.x,
                        referenceWorld.end.y,
                      ]}
                      stroke="#d9480f"
                      strokeWidth={getScreenInvariantSize(2)}
                      dash={[getScreenInvariantSize(10), getScreenInvariantSize(6)]}
                      lineCap="round"
                      onMouseDown={(event) => {
                        event.cancelBubble = true;
                        activateBackgroundReferenceEditing();
                      }}
                      onMouseUp={(event) => {
                        event.cancelBubble = true;
                      }}
                    />
                    <Text
                      x={getOffsetLabelPoint(referenceWorld.start, referenceWorld.end).x}
                      y={getOffsetLabelPoint(referenceWorld.start, referenceWorld.end).y}
                      text={formatMeasurement(backgroundAsset.reference.realLength)}
                      fontSize={getScreenInvariantSize(12)}
                      fontStyle="bold"
                      fill="#d9480f"
                      onMouseDown={(event) => {
                        event.cancelBubble = true;
                        activateBackgroundReferenceEditing();
                      }}
                      onMouseUp={(event) => {
                        event.cancelBubble = true;
                      }}
                    />
                    {editingId === BACKGROUND_ID && (
                      <>
                        {renderBackgroundReferenceTerminal(referenceWorld.start, "background-reference-start", {
                          onDragStart: (event) => {
                            event.cancelBubble = true;
                            beginHistoryAction();
                            setBackgroundReferenceInput({
                              value: formatMeasurementValue(backgroundAsset.reference!.realLength),
                            });
                          },
                          onDragMove: (event) => {
                            event.cancelBubble = true;
                            updateBackgroundReferencePoint("start", {
                              x: event.target.x(),
                              y: event.target.y(),
                            });
                          },
                          onDragEnd: (event) => {
                            event.cancelBubble = true;
                            updateBackgroundReferencePoint("start", {
                              x: event.target.x(),
                              y: event.target.y(),
                            });
                            finishHistoryAction();
                          },
                        })}
                        {renderBackgroundReferenceTerminal(referenceWorld.end, "background-reference-end", {
                          onDragStart: (event) => {
                            event.cancelBubble = true;
                            beginHistoryAction();
                            setBackgroundReferenceInput({
                              value: formatMeasurementValue(backgroundAsset.reference!.realLength),
                            });
                          },
                          onDragMove: (event) => {
                            event.cancelBubble = true;
                            updateBackgroundReferencePoint("end", {
                              x: event.target.x(),
                              y: event.target.y(),
                            });
                          },
                          onDragEnd: (event) => {
                            event.cancelBubble = true;
                            updateBackgroundReferencePoint("end", {
                              x: event.target.x(),
                              y: event.target.y(),
                            });
                            finishHistoryAction();
                          },
                        })}
                      </>
                    )}
                  </>
                );
              })()}

            {isBackgroundCropMode &&
              backgroundAsset &&
              backgroundCropRect &&
              (() => {
                const bounds = getBackgroundCropWorldBounds(
                  backgroundAsset,
                  backgroundCropRect.start,
                  backgroundCropRect.current,
                );

                return (
                  <Line
                    points={[
                      bounds.minX,
                      bounds.minY,
                      bounds.maxX,
                      bounds.minY,
                      bounds.maxX,
                      bounds.maxY,
                      bounds.minX,
                      bounds.maxY,
                    ]}
                    closed
                    stroke="#d9480f"
                    strokeWidth={getScreenInvariantSize(2)}
                    dash={[getScreenInvariantSize(10), getScreenInvariantSize(6)]}
                    fill="rgba(217, 72, 15, 0.12)"
                    listening={false}
                  />
                );
              })()}

            {isBackgroundToolbarOpen && backgroundReferenceLine && (
              <>
                {renderBackgroundReferenceTerminal(
                  backgroundReferenceLine.start,
                  "background-reference-preview-start",
                  { draggable: false },
                )}
                <Line
                  points={[
                    backgroundReferenceLine.start.x,
                    backgroundReferenceLine.start.y,
                    (backgroundReferenceLine.end ?? backgroundReferenceLine.pointer).x,
                    (backgroundReferenceLine.end ?? backgroundReferenceLine.pointer).y,
                  ]}
                  stroke="#d9480f"
                  strokeWidth={getScreenInvariantSize(2)}
                  dash={[getScreenInvariantSize(10), getScreenInvariantSize(6)]}
                  lineCap="round"
                />
                <Text
                  x={
                    getOffsetLabelPoint(
                      backgroundReferenceLine.start,
                      backgroundReferenceLine.end ?? backgroundReferenceLine.pointer,
                    ).x
                  }
                  y={
                    getOffsetLabelPoint(
                      backgroundReferenceLine.start,
                      backgroundReferenceLine.end ?? backgroundReferenceLine.pointer,
                    ).y
                  }
                  text={formatMeasurement(
                    getDistance(
                      backgroundReferenceLine.start,
                      backgroundReferenceLine.end ?? backgroundReferenceLine.pointer,
                    ),
                  )}
                  fontSize={getScreenInvariantSize(12)}
                  fontStyle="bold"
                  fill="#d9480f"
                  listening={false}
                />
              </>
            )}
            {false &&
              polylines.map((polyline) => {
                const isSelected = selectedIds.includes(polyline.id);
                const isEditing = editingId === polyline.id;
                const gripPoints = getShapeGripPoints(polyline);
                const styleKind = getShapeStyleKind(polyline);
                const shouldRenderPolylineFill =
                  polyline.kind !== "circle" &&
                  polyline.kind !== "arc" &&
                  !polyline.disableFill &&
                  !isNonFlatRoofSurfaceStyleKind(styleKind);

                return (
                  <Fragment key={polyline.id}>
                    {polyline.kind === "arc" ? (
                      <Line
                        points={getArcRenderPoints(polyline)}
                        stroke={polyline.strokeColor}
                        strokeWidth={getScreenInvariantSize(
                          isSelected ? polyline.strokeWidth + 1 : polyline.strokeWidth,
                        )}
                        dash={isSelected ? [getScreenInvariantSize(10), getScreenInvariantSize(6)] : undefined}
                        lineCap="round"
                        lineJoin="round"
                      />
                    ) : polyline.kind === "circle" ? (
                      <>
                        <Circle
                          x={polyline.center.x}
                          y={polyline.center.y}
                          radius={polyline.radius}
                          stroke={polyline.strokeColor}
                          strokeWidth={getScreenInvariantSize(
                            isSelected ? polyline.strokeWidth + 1 : polyline.strokeWidth,
                          )}
                          fill={toAlphaColor(polyline.strokeColor, isSelected ? 0.22 : 0.14)}
                          dash={isSelected ? [getScreenInvariantSize(10), getScreenInvariantSize(6)] : undefined}
                        />
                        {renderCircleCenterMarker(
                          polyline.center,
                          `${polyline.id}-center-marker`,
                          polyline.strokeColor,
                          { listening: false },
                        )}
                      </>
                    ) : (
                      <Line
                        points={polyline.points}
                        closed={polyline.closed}
                        stroke={polyline.strokeColor}
                        strokeWidth={getScreenInvariantSize(
                          isSelected ? polyline.strokeWidth + 1 : polyline.strokeWidth,
                        )}
                        fill={
                          polyline.closed && shouldRenderPolylineFill
                            ? toAlphaColor(polyline.strokeColor, isSelected ? 0.22 : 0.14)
                            : undefined
                        }
                        dash={isSelected ? [getScreenInvariantSize(10), getScreenInvariantSize(6)] : undefined}
                        lineCap="round"
                        lineJoin="round"
                      />
                    )}

                    {isEditing &&
                      gripPoints.map((grip, index) => {
                        return (
                          <Circle
                            key={`${polyline.id}-grip-${grip.holeIndex ?? "outer"}-${index}`}
                            x={grip.point.x}
                            y={grip.point.y}
                            radius={getScreenInvariantSize(GRIP_RADIUS)}
                            fill={grip.gripKind === "insert" ? "#fff4e6" : "#ffffff"}
                            stroke="#d9480f"
                            strokeWidth={getScreenInvariantSize(1.5)}
                            hitStrokeWidth={getScreenInvariantSize(GRIP_HIT_STROKE)}
                            draggable={grip.gripKind !== "insert"}
                            onClick={handleGripPointerClick(polyline, grip)}
                            onContextMenu={
                              grip.gripKind === "insert"
                                ? undefined
                                : handleGripContextMenu(
                                    polyline.id,
                                    grip.vertexIndex,
                                    grip.linkedVertexIndices,
                                    grip.holeIndex,
                                    grip.holeGripKind,
                                  )
                            }
                            {...(grip.gripKind === "insert"
                              ? {}
                              : getGripDragHandlers(
                                  polyline.id,
                                  grip.vertexIndex,
                                  grip.linkedVertexIndices,
                                  grip.holeIndex,
                                  grip.holeGripKind,
                                ))}
                          />
                        );
                      })}
                  </Fragment>
                );
              })}

            {false &&
              dimensions.map((dimension) => {
                const geometry = getDimensionLayout(dimension.start, dimension.end, dimension.offset);
                const isSelected = selectedIds.includes(dimension.id);
                const isEditing = editingId === dimension.id;
                if (!geometry) return null;

                const dimensionText = formatMeasurement(getDistance(dimension.start, dimension.end));
                const textAnchor = getDimensionTextAnchor(dimensionText);

                return (
                  <Fragment key={dimension.id}>
                    <Line
                      points={[
                        geometry.extensionStart.x,
                        geometry.extensionStart.y,
                        geometry.extensionStartOffset.x,
                        geometry.extensionStartOffset.y,
                      ]}
                      stroke={dimension.strokeColor}
                      strokeWidth={getScreenInvariantSize(
                        isSelected ? dimension.strokeWidth + 0.75 : dimension.strokeWidth,
                      )}
                      dash={isSelected ? [getScreenInvariantSize(10), getScreenInvariantSize(6)] : undefined}
                    />
                    <Line
                      points={[
                        geometry.extensionEnd.x,
                        geometry.extensionEnd.y,
                        geometry.extensionEndOffset.x,
                        geometry.extensionEndOffset.y,
                      ]}
                      stroke={dimension.strokeColor}
                      strokeWidth={getScreenInvariantSize(
                        isSelected ? dimension.strokeWidth + 0.75 : dimension.strokeWidth,
                      )}
                      dash={isSelected ? [getScreenInvariantSize(10), getScreenInvariantSize(6)] : undefined}
                    />
                    <Line
                      points={[
                        geometry.extensionStartOffset.x,
                        geometry.extensionStartOffset.y,
                        geometry.extensionEndOffset.x,
                        geometry.extensionEndOffset.y,
                      ]}
                      stroke={dimension.strokeColor}
                      strokeWidth={getScreenInvariantSize(
                        isSelected ? dimension.strokeWidth + 0.75 : dimension.strokeWidth,
                      )}
                      dash={isSelected ? [getScreenInvariantSize(10), getScreenInvariantSize(6)] : undefined}
                    />
                    {renderDimensionMarker(
                      geometry.extensionStartOffset,
                      { x: geometry.ux, y: geometry.uy },
                      dimension.strokeColor,
                      `${dimension.id}-start-marker`,
                    )}
                    {renderDimensionMarker(
                      geometry.extensionEndOffset,
                      { x: -geometry.ux, y: -geometry.uy },
                      dimension.strokeColor,
                      `${dimension.id}-end-marker`,
                    )}
                    <Text
                      x={geometry.textPoint.x}
                      y={geometry.textPoint.y}
                      offsetX={textAnchor.offsetX}
                      offsetY={textAnchor.offsetY}
                      text={dimensionText}
                      fontSize={textAnchor.fontSize}
                      fontStyle="bold"
                      fill={dimension.strokeColor}
                      rotation={geometry.textRotation}
                      listening={false}
                    />
                    {isEditing && (
                      <Circle
                        x={geometry.lineMidPoint.x}
                        y={geometry.lineMidPoint.y}
                        radius={getScreenInvariantSize(GRIP_RADIUS)}
                        fill="#ffffff"
                        stroke="#d9480f"
                        strokeWidth={getScreenInvariantSize(1.5)}
                        hitStrokeWidth={getScreenInvariantSize(GRIP_HIT_STROKE)}
                        draggable
                        onMouseDown={(event) => {
                          event.cancelBubble = true;
                        }}
                        onDragStart={(event) => {
                          event.cancelBubble = true;
                          beginHistoryAction();
                          setDraggedDimensionId(dimension.id);
                        }}
                        onDragMove={(event) => {
                          event.cancelBubble = true;
                          updateDimensionOffset(dimension.id, {
                            x: event.target.x(),
                            y: event.target.y(),
                          });
                        }}
                        onDragEnd={(event) => {
                          event.cancelBubble = true;
                          updateDimensionOffset(dimension.id, {
                            x: event.target.x(),
                            y: event.target.y(),
                          });
                          finishHistoryAction();
                          setDraggedDimensionId(null);
                        }}
                      />
                    )}
                  </Fragment>
                );
              })}

            {activeOffset &&
              numericEdit?.kind === "offset" &&
              (() => {
                const sourceShape = getShapeById(activeOffset.entityId);
                const offsetDistance = parseMeasurementValue(numericEdit.value);
                if (!sourceShape || offsetDistance == null) return null;

                const preview = buildOffsetShapePreview(
                  sourceShape,
                  activeOffset.pointer,
                  offsetDistance,
                  activeOffset.sourceContour,
                );
                if (!preview) return null;

                const labelText = `Offset ${formatMeasurement(offsetDistance)}`;
                const labelPoint =
                  preview.kind === "circle"
                    ? {
                        x: preview.center.x + preview.radius,
                        y: preview.center.y,
                      }
                    : {
                        x: preview.points[0],
                        y: preview.points[1],
                      };

                return (
                  <>
                    {preview.kind === "circle" ? (
                      <Circle
                        x={preview.center.x}
                        y={preview.center.y}
                        radius={preview.radius}
                        stroke={PREVIEW_STROKE_COLOR}
                        strokeWidth={getScreenInvariantSize(previewLineWidth)}
                        dash={[getScreenInvariantSize(10), getScreenInvariantSize(6)]}
                        fill={PREVIEW_FILL_COLOR}
                      />
                    ) : (
                      <Line
                        points={preview.points}
                        closed={preview.closed}
                        stroke={PREVIEW_STROKE_COLOR}
                        strokeWidth={getScreenInvariantSize(previewLineWidth)}
                        dash={[getScreenInvariantSize(10), getScreenInvariantSize(6)]}
                        lineCap="round"
                        lineJoin="round"
                      />
                    )}
                    <Text
                      x={labelPoint.x}
                      y={labelPoint.y}
                      text={labelText}
                      offsetX={(labelText.length * (6 / viewport.scale)) / 2}
                      offsetY={getScreenInvariantSize(10)}
                      fontSize={getScreenInvariantSize(13)}
                      fontStyle="bold"
                      fill={PREVIEW_TEXT_COLOR}
                      listening={false}
                    />
                  </>
                );
              })()}

            {moveState && (toolMode === "move" || toolMode === "copy") && numericEdit?.kind === "move" && (
              <>
                <Circle
                  x={moveState.anchorPoint.x}
                  y={moveState.anchorPoint.y}
                  radius={getScreenInvariantSize(3.5)}
                  fill="#228be6"
                  stroke="#ffffff"
                  strokeWidth={getScreenInvariantSize(1)}
                  listening={false}
                />
                <Line
                  points={[
                    moveState.anchorPoint.x,
                    moveState.anchorPoint.y,
                    numericEdit.position.x,
                    numericEdit.position.y,
                  ]}
                  stroke="#228be6"
                  strokeWidth={getScreenInvariantSize(2)}
                  dash={[getScreenInvariantSize(10), getScreenInvariantSize(6)]}
                  lineCap="round"
                  listening={false}
                />
              </>
            )}

            {multiGableRoofDraft && (
              <>
                {multiGableRoofDraft.outline.map((point, index) => {
                  const nextPoint =
                    multiGableRoofDraft.outline[(index + 1) % multiGableRoofDraft.outline.length] ?? point;
                  const isMainRake = multiGableRoofDraft.mainRakes.includes(index);
                  const isSecondaryRake = multiGableRoofDraft.secondaryRakes.includes(index);
                  const stroke = isMainRake ? "#7c3aed" : isSecondaryRake ? "#0891b2" : "#1d5cab";

                  return (
                    <Line
                      key={`multi-gable-edge-${index}`}
                      points={[point.x, point.y, nextPoint.x, nextPoint.y]}
                      stroke={stroke}
                      strokeWidth={getScreenInvariantSize(isMainRake || isSecondaryRake ? 3 : 2)}
                      dash={
                        isMainRake || isSecondaryRake
                          ? undefined
                          : [getScreenInvariantSize(8), getScreenInvariantSize(5)]
                      }
                      lineCap="round"
                      listening={false}
                    />
                  );
                })}
                {multiGableRoofDraft.roofLines.map((line) => {
                  const isHip = line.type === "hip";
                  const isValley = line.type === "valley";
                  const stroke = isHip ? "#d9480f" : isValley ? "#0891b2" : "#15803d";

                  return (
                    <Line
                      key={line.id}
                      points={[line.start.x, line.start.y, line.end.x, line.end.y]}
                      stroke={stroke}
                      strokeWidth={getScreenInvariantSize(isHip || isValley ? 2.5 : 2)}
                      dash={isHip || isValley ? undefined : [getScreenInvariantSize(7), getScreenInvariantSize(4)]}
                      lineCap="round"
                      listening={false}
                    />
                  );
                })}
              </>
            )}

            {activePolyline &&
              (toolMode === "polyline" ||
                toolMode === "buildPolyline" ||
                (toolMode === "hatch" && hatchToolMode === "build") ||
                isRoofComponentToolMode(toolMode) ||
                toolMode === "roof" ||
                toolMode === "multiGableRoof" ||
                toolMode === "holePolyline") && (
                <>
                  <Line
                    points={activePolyline}
                    stroke={PREVIEW_STROKE_COLOR}
                    strokeWidth={getScreenInvariantSize(
                      toolMode === "polyline"
                        ? Math.min(previewLineWidth, DEFAULT_PREVIEW_LINE_WIDTH)
                        : previewLineWidth,
                    )}
                    dash={[getScreenInvariantSize(10), getScreenInvariantSize(6)]}
                    lineCap="round"
                    lineJoin="round"
                  />
                  {activePolyline.length >= 4 &&
                    (() => {
                      const start = getPointFromCoordinateArray(activePolyline, activePolyline.length - 4);
                      const end = getPointFromCoordinateArray(activePolyline, activePolyline.length - 2);
                      if (!start || !end) return null;
                      const labelPoint = getCursorSideLabelPoint(end);
                      const text = formatMeasurement(getDistance(start, end));

                      return (
                        <Text
                          x={labelPoint.x}
                          y={labelPoint.y}
                          text={text}
                          fontSize={getScreenInvariantSize(13)}
                          fontStyle="bold"
                          fill={PREVIEW_TEXT_COLOR}
                          listening={false}
                        />
                      );
                    })()}
                </>
              )}
            {activePolyline && toolMode === "singleLifeLine" && (
              <>
                {(activeLifeLinePreview.length > 0
                  ? activeLifeLinePreview
                  : [{ lineNumber: 1, points: activePolyline, lifeLineVertices: [] }]
                ).map((renderedLifeLine) => (
                  <Fragment key={`active-lifeline-preview-${renderedLifeLine.lineNumber}`}>
                    <Line
                      points={renderedLifeLine.points}
                      stroke={PREVIEW_STROKE_COLOR}
                      strokeWidth={getScreenInvariantSize(previewLineWidth)}
                      dash={[getScreenInvariantSize(10), getScreenInvariantSize(6)]}
                      lineCap="round"
                      lineJoin="round"
                    />
                    {renderedLifeLine.lifeLineVertices.map((vertex, index) => (
                      <Group
                        key={`active-lifeline-preview-${renderedLifeLine.lineNumber}-vertex-${index}`}
                        x={vertex.x}
                        y={vertex.y}
                        listening={false}
                      >
                        <Circle
                          radius={getScreenInvariantSize(vertex.kind === "generated" ? 3.5 : 5)}
                          fill="#ffffff"
                          stroke={PREVIEW_TEXT_COLOR}
                          strokeWidth={getScreenInvariantSize(1.25)}
                          listening={false}
                        />
                      </Group>
                    ))}
                  </Fragment>
                ))}
                {activePolyline.length >= 4 &&
                  (() => {
                    const start = getPointFromCoordinateArray(activePolyline, activePolyline.length - 4);
                    const end = getPointFromCoordinateArray(activePolyline, activePolyline.length - 2);
                    if (!start || !end) return null;
                    const labelPoint = getCursorSideLabelPoint(end);
                    const text = formatMeasurement(getDistance(start, end));

                    return (
                      <Text
                        x={labelPoint.x}
                        y={labelPoint.y}
                        text={text}
                        fontSize={getScreenInvariantSize(13)}
                        fontStyle="bold"
                        fill={PREVIEW_TEXT_COLOR}
                        listening={false}
                      />
                    );
                  })()}
              </>
            )}
            {toolMode === "automaticLifeLine" && automaticLifeLineOffsetPreview.length > 0 && (
              <>
                {automaticLifeLineOffsetPreview.map((renderedLifeLine) => (
                  <Fragment key={`automatic-lifeline-preview-${renderedLifeLine.previewKey}`}>
                    <Line
                      points={renderedLifeLine.points}
                      stroke={PREVIEW_STROKE_COLOR}
                      strokeWidth={getScreenInvariantSize(previewLineWidth)}
                      dash={[getScreenInvariantSize(10), getScreenInvariantSize(6)]}
                      lineCap="round"
                      lineJoin="round"
                      listening={false}
                    />
                    {renderedLifeLine.lifeLineVertices.map((vertex, index) => (
                      <Group
                        key={`automatic-lifeline-preview-${renderedLifeLine.previewKey}-vertex-${index}`}
                        x={vertex.x}
                        y={vertex.y}
                        listening={false}
                      >
                        <Circle
                          radius={getScreenInvariantSize(vertex.kind === "generated" ? 3.5 : 5)}
                          fill="#ffffff"
                          stroke={PREVIEW_TEXT_COLOR}
                          strokeWidth={getScreenInvariantSize(1.25)}
                          listening={false}
                        />
                      </Group>
                    ))}
                  </Fragment>
                ))}
              </>
            )}
            {activeRectangle &&
              (() => {
                const previewRectangle =
                  toolMode === "multiShedRoof" && numericEdit?.kind === "multi-shed-count"
                    ? numericEdit.rectangle
                    : activeRectangle;
                const roofPreviewStrokeWidth = getScreenInvariantSize(
                  Math.min(previewLineWidth, DEFAULT_PREVIEW_LINE_WIDTH),
                );

                return previewRectangle.edgeEnd ? (
                  <>
                    <Line
                      points={buildRectanglePolyline(previewRectangle) ?? []}
                      closed
                      stroke={PREVIEW_STROKE_COLOR}
                      strokeWidth={roofPreviewStrokeWidth}
                      dash={[getScreenInvariantSize(10), getScreenInvariantSize(6)]}
                      lineCap="round"
                      lineJoin="round"
                    />
                    {(toolMode === "gableRoof" || toolMode === "curvedRoof") &&
                      (() => {
                        const rectanglePoints = buildRectanglePolyline(previewRectangle);
                        if (!rectanglePoints) return null;
                        const roofGeometry =
                          toolMode === "curvedRoof"
                            ? buildCurvedRoofGeometryRule(rectanglePoints)
                            : buildTwoPitchRoofGeometryRule(rectanglePoints);
                        const ridgeLine = roofGeometry?.ridgeLines[0];
                        if (!ridgeLine) return null;

                        return (
                          <Line
                            points={ridgeLine}
                            stroke={PREVIEW_STROKE_COLOR}
                            strokeWidth={roofPreviewStrokeWidth}
                            dash={[getScreenInvariantSize(10), getScreenInvariantSize(6)]}
                            lineCap="round"
                            lineJoin="round"
                          />
                        );
                      })()}
                    {toolMode === "singlePitchedRoof" &&
                      (() => {
                        const rectanglePoints = buildRectanglePolyline(previewRectangle);
                        if (!rectanglePoints) return null;
                        const roofGeometry = buildSinglePitchedRoofGeometryRule(rectanglePoints);
                        if (!roofGeometry) return null;

                        return (
                          <Line
                            points={roofGeometry.colmoLine}
                            stroke={PREVIEW_STROKE_COLOR}
                            strokeWidth={roofPreviewStrokeWidth}
                            dash={[getScreenInvariantSize(10), getScreenInvariantSize(6)]}
                            lineCap="round"
                            lineJoin="round"
                          />
                        );
                      })()}
                    {toolMode === "multiShedRoof" &&
                      numericEdit?.kind === "multi-shed-count" &&
                      (() => {
                        const rectanglePoints = buildRectanglePolyline(previewRectangle);
                        if (!rectanglePoints) return null;
                        const previewShedCount = Math.max(1, Math.floor(parseLocalizedNumber(numericEdit.value) ?? 1));
                        const roofGeometry = buildMultiShedRoofGeometryRule(rectanglePoints, previewShedCount);
                        if (!roofGeometry) return null;

                        return (
                          <>
                            {[...roofGeometry.colmoLines, ...roofGeometry.rakeLines].map((linePoints, index) => (
                              <Line
                                key={`multi-shed-preview-${index}`}
                                points={linePoints}
                                stroke={PREVIEW_STROKE_COLOR}
                                strokeWidth={roofPreviewStrokeWidth}
                                dash={[getScreenInvariantSize(10), getScreenInvariantSize(6)]}
                                lineCap="round"
                                lineJoin="round"
                              />
                            ))}
                          </>
                        );
                      })()}
                    {(() => {
                      const baseStart = previewRectangle.start;
                      const baseEnd = previewRectangle.edgeEnd;
                      const baseText = formatMeasurement(getDistance(baseStart, baseEnd));
                      const basePoint = getCursorSideLabelPoint(baseEnd);

                      const rectanglePoints = buildRectanglePolyline(previewRectangle);
                      if (!rectanglePoints) return null;

                      const heightStart = getPointFromCoordinateArray(rectanglePoints, 2);
                      const heightEnd = getPointFromCoordinateArray(rectanglePoints, 4);
                      if (!heightStart || !heightEnd) return null;
                      const heightText = formatMeasurement(getDistance(heightStart, heightEnd));
                      const heightPoint = getCursorSideLabelPoint(heightEnd);

                      return (
                        <>
                          <Text
                            x={basePoint.x}
                            y={basePoint.y}
                            text={baseText}
                            fontSize={getScreenInvariantSize(13)}
                            fontStyle="bold"
                            fill={PREVIEW_TEXT_COLOR}
                            listening={false}
                          />
                          <Text
                            x={heightPoint.x}
                            y={heightPoint.y}
                            text={heightText}
                            fontSize={getScreenInvariantSize(13)}
                            fontStyle="bold"
                            fill={PREVIEW_TEXT_COLOR}
                            listening={false}
                          />
                        </>
                      );
                    })()}
                  </>
                ) : (
                  <>
                    <Line
                      points={[
                        previewRectangle.start.x,
                        previewRectangle.start.y,
                        previewRectangle.pointer.x,
                        previewRectangle.pointer.y,
                      ]}
                      stroke={PREVIEW_STROKE_COLOR}
                      strokeWidth={roofPreviewStrokeWidth}
                      dash={[getScreenInvariantSize(10), getScreenInvariantSize(6)]}
                      lineCap="round"
                      lineJoin="round"
                    />
                    {(() => {
                      const text = formatMeasurement(getDistance(previewRectangle.start, previewRectangle.pointer));
                      const labelPoint = getCursorSideLabelPoint(previewRectangle.pointer);

                      return (
                        <Text
                          x={labelPoint.x}
                          y={labelPoint.y}
                          text={text}
                          fontSize={getScreenInvariantSize(13)}
                          fontStyle="bold"
                          fill={PREVIEW_TEXT_COLOR}
                          listening={false}
                        />
                      );
                    })()}
                  </>
                );
              })()}

            {activeCircle && (
              <>
                <Circle
                  x={activeCircle.center.x}
                  y={activeCircle.center.y}
                  radius={getActiveCircleRadius(activeCircle)}
                  stroke={PREVIEW_STROKE_COLOR}
                  strokeWidth={getScreenInvariantSize(previewLineWidth)}
                  dash={[getScreenInvariantSize(10), getScreenInvariantSize(6)]}
                  fill={toolMode === "singlePointCircle" ? undefined : PREVIEW_FILL_COLOR}
                />
                {toolMode === "singlePointCircle"
                  ? renderCircleCenterDot(activeCircle.center, "active-circle-center", "#b42318")
                  : renderCircleCenterMarker(activeCircle.center, "active-circle-center", "#b42318", {
                      draggable: toolMode === "circle",
                      listening: toolMode === "circle",
                      onDragMove: (event) => {
                        event.cancelBubble = true;
                        if (toolMode !== "circle") {
                          return;
                        }
                        const nextCenter = {
                          x: event.target.x(),
                          y: event.target.y(),
                        };
                        const deltaX = nextCenter.x - activeCircle.center.x;
                        const deltaY = nextCenter.y - activeCircle.center.y;

                        setActiveCircle({
                          center: nextCenter,
                          pointer: {
                            x: activeCircle.pointer.x + deltaX,
                            y: activeCircle.pointer.y + deltaY,
                          },
                        });
                      },
                    })}
                {(() => {
                  const text = `R ${formatMeasurement(getDistance(activeCircle.center, activeCircle.pointer))}`;
                  const labelPoint = getOffsetLabelPoint(activeCircle.center, activeCircle.pointer);

                  return (
                    <Text
                      x={labelPoint.x}
                      y={labelPoint.y}
                      text={text}
                      offsetX={(text.length * (6 / viewport.scale)) / 2}
                      offsetY={getScreenInvariantSize(10)}
                      fontSize={getScreenInvariantSize(13)}
                      fontStyle="bold"
                      fill={PREVIEW_TEXT_COLOR}
                      listening={false}
                    />
                  );
                })()}
              </>
            )}

            {activeArc && (
              <>
                {activeArc.start ? (
                  <Line
                    points={buildArcPoints(activeArc)}
                    stroke={PREVIEW_STROKE_COLOR}
                    strokeWidth={getScreenInvariantSize(previewLineWidth)}
                    dash={[getScreenInvariantSize(10), getScreenInvariantSize(6)]}
                    lineCap="round"
                    lineJoin="round"
                  />
                ) : (
                  <Line
                    points={[activeArc.center.x, activeArc.center.y, activeArc.pointer.x, activeArc.pointer.y]}
                    stroke={PREVIEW_STROKE_COLOR}
                    strokeWidth={getScreenInvariantSize(previewLineWidth)}
                    dash={[getScreenInvariantSize(10), getScreenInvariantSize(6)]}
                    lineCap="round"
                    lineJoin="round"
                  />
                )}
                {renderCircleCenterMarker(activeArc.center, "active-arc-center", "#b42318", {
                  listening: false,
                })}
                {activeArc.start &&
                  renderEntityVertexMarker("circle", activeArc.start, "active-arc-start", { listening: false })}
              </>
            )}

            {activePointSeries &&
              activePointSeries.length >= 2 &&
              (() => {
                const pointSeriesStyle = getEntityTypeStyle("point-series");
                const pointSeriesDash = getEntityTypeLineDash("point-series");

                return (
                  <>
                    {numericEdit?.kind === "point-series" &&
                      numericEdit.position &&
                      (() => {
                        const start = getPointFromCoordinateArray(activePointSeries, 0);
                        if (!start) return null;

                        return (
                          <Line
                            points={[start.x, start.y, numericEdit.position.x, numericEdit.position.y]}
                            stroke={pointSeriesStyle.strokeColor}
                            strokeWidth={getScreenInvariantSize(pointSeriesStyle.strokeWidth)}
                            dash={pointSeriesDash}
                            lineCap="round"
                            lineJoin="round"
                            listening={false}
                          />
                        );
                      })()}
                    <Line
                      points={activePointSeries}
                      stroke={pointSeriesStyle.strokeColor}
                      strokeWidth={getScreenInvariantSize(pointSeriesStyle.strokeWidth)}
                      dash={pointSeriesDash}
                      lineCap="round"
                      lineJoin="round"
                      listening={false}
                    />
                    {getRenderablePolylineVertices(activePointSeries, false).map((vertex, index) =>
                      renderEntityVertexMarker("point-series", vertex, `point-series-preview-${index}`, {
                        listening: false,
                      }),
                    )}
                  </>
                );
              })()}

            {activeDimension && !activeDimension.end && (
              <>
                <Line
                  points={[
                    activeDimension.start.x,
                    activeDimension.start.y,
                    activeDimension.pointer.x,
                    activeDimension.pointer.y,
                  ]}
                  stroke={PREVIEW_STROKE_COLOR}
                  strokeWidth={getScreenInvariantSize(1.5)}
                  dash={[getScreenInvariantSize(8), getScreenInvariantSize(4)]}
                />
                {(() => {
                  const text = formatMeasurement(getDistance(activeDimension.start, activeDimension.pointer));
                  const labelPoint = getOffsetLabelPoint(activeDimension.start, activeDimension.pointer);

                  return (
                    <Text
                      x={labelPoint.x}
                      y={labelPoint.y}
                      text={text}
                      offsetX={(text.length * (6 / viewport.scale)) / 2}
                      offsetY={getScreenInvariantSize(10)}
                      fontSize={getScreenInvariantSize(13)}
                      fontStyle="bold"
                      fill={PREVIEW_TEXT_COLOR}
                      listening={false}
                    />
                  );
                })()}
              </>
            )}

            {activeRuler &&
              (() => {
                const rulerStyle = getEntityTypeStyle("ruler");
                const rulerDash = getEntityTypeLineDash("ruler");
                const rulerEnd = activeRuler.end ?? activeRuler.pointer;
                const rulerLength = getDistance(activeRuler.start, rulerEnd);
                if (rulerLength <= 0.0001) return null;

                const rulerText = formatMeasurement(rulerLength);
                const rulerMidpoint = {
                  x: (activeRuler.start.x + rulerEnd.x) / 2,
                  y: (activeRuler.start.y + rulerEnd.y) / 2,
                };

                return (
                  <>
                    <Line
                      points={[activeRuler.start.x, activeRuler.start.y, rulerEnd.x, rulerEnd.y]}
                      stroke={rulerStyle.strokeColor}
                      strokeWidth={getScreenInvariantSize(rulerStyle.strokeWidth)}
                      dash={rulerDash}
                    />
                    <Circle
                      x={activeRuler.start.x}
                      y={activeRuler.start.y}
                      radius={getScreenInvariantSize(rulerStyle.vertexSize)}
                      fill={rulerStyle.vertexColor}
                      listening={false}
                    />
                    <Circle
                      x={rulerEnd.x}
                      y={rulerEnd.y}
                      radius={getScreenInvariantSize(rulerStyle.vertexSize)}
                      fill={rulerStyle.vertexColor}
                      listening={false}
                    />
                    <Text
                      x={rulerMidpoint.x}
                      y={rulerMidpoint.y}
                      text={rulerText}
                      offsetX={(rulerText.length * (6 / viewport.scale)) / 2}
                      offsetY={getScreenInvariantSize(12)}
                      fontSize={getScreenInvariantSize(Math.max(12, dimensionTextHeight + 1))}
                      fontStyle="bold"
                      fill={PREVIEW_TEXT_COLOR}
                      listening={false}
                    />
                  </>
                );
              })()}

            {constructionLines.map((constructionLine) => {
              const constructionLineLayer = getConstructionLineLayer(constructionLine);
              if (!isLayerVisible(constructionLineLayer)) return null;
              const constructionLineStrokeColor = getConstructionLineStrokeColor(constructionLine);
              const constructionLineStrokeWidth = getConstructionLineStrokeWidth(constructionLine);
              const constructionLineDash = getEntityTypeLineDash(
                constructionLine.entityStyleKind ?? "construction-line",
              );
              const deltaX = constructionLine.end.x - constructionLine.start.x;
              const deltaY = constructionLine.end.y - constructionLine.start.y;
              const length = Math.hypot(deltaX, deltaY);
              if (length <= 0.0001) return null;
              const extension = (Math.max(viewportSize.width, viewportSize.height) / viewport.scale) * 4;
              const direction = { x: deltaX / length, y: deltaY / length };
              const start = {
                x: constructionLine.start.x - direction.x * extension,
                y: constructionLine.start.y - direction.y * extension,
              };
              const end = {
                x: constructionLine.start.x + direction.x * extension,
                y: constructionLine.start.y + direction.y * extension,
              };

              return (
                <Line
                  key={constructionLine.id}
                  points={[start.x, start.y, end.x, end.y]}
                  stroke={selectedIds.includes(constructionLine.id) ? "#d9480f" : constructionLineStrokeColor}
                  strokeWidth={getScreenInvariantSize(
                    selectedIds.includes(constructionLine.id)
                      ? constructionLineStrokeWidth + 1
                      : constructionLineStrokeWidth,
                  )}
                  dash={
                    selectedIds.includes(constructionLine.id)
                      ? [getScreenInvariantSize(10), getScreenInvariantSize(6)]
                      : constructionLineDash
                  }
                  hitStrokeWidth={getScreenInvariantSize(18)}
                  draggable={toolMode === "select" && !isLayerLocked(constructionLineLayer)}
                  onMouseDown={(event) => {
                    event.cancelBubble = true;
                    setSelectedIds((current) =>
                      event.evt.shiftKey
                        ? current.includes(constructionLine.id)
                          ? current.filter((id) => id !== constructionLine.id)
                          : [...current, constructionLine.id]
                        : [constructionLine.id],
                    );
                    setEditingId(null);
                  }}
                  onDragStart={(event) => {
                    event.cancelBubble = true;
                    setSelectedIds((current) =>
                      current.includes(constructionLine.id) ? current : [constructionLine.id],
                    );
                  }}
                  onDragEnd={(event) => {
                    event.cancelBubble = true;
                    const delta = {
                      x: event.target.x(),
                      y: event.target.y(),
                    };
                    event.target.position({ x: 0, y: 0 });
                    if (Math.abs(delta.x) <= 0.0001 && Math.abs(delta.y) <= 0.0001) return;
                    setConstructionLines((current) =>
                      current.map((line) =>
                        line.id === constructionLine.id
                          ? {
                              ...line,
                              start: {
                                x: line.start.x + delta.x,
                                y: line.start.y + delta.y,
                              },
                              end: {
                                x: line.end.x + delta.x,
                                y: line.end.y + delta.y,
                              },
                            }
                          : line,
                      ),
                    );
                  }}
                />
              );
            })}

            {osnapTrackingLines.map((trackingLine) => {
              const trackingStyle = getEntityTypeStyle("osnap-tracking");
              const trackingDash = getEntityTypeLineDash("osnap-tracking");

              return (
                <Line
                  key={trackingLine.id}
                  points={[trackingLine.start.x, trackingLine.start.y, trackingLine.end.x, trackingLine.end.y]}
                  stroke={trackingStyle.strokeColor}
                  strokeWidth={getScreenInvariantSize(trackingStyle.strokeWidth)}
                  dash={trackingDash}
                  opacity={0.7}
                  listening={false}
                />
              );
            })}

            {activeDimension?.end &&
              (() => {
                const geometry = getDimensionGeometry(
                  activeDimension.start,
                  activeDimension.end,
                  activeDimension.pointer,
                );
                if (!geometry) return null;
                const dimensionText = formatMeasurement(geometry.length);
                const textAnchor = getDimensionTextAnchor(dimensionText);

                return (
                  <>
                    <Line
                      points={[
                        geometry.extensionStart.x,
                        geometry.extensionStart.y,
                        geometry.extensionStartOffset.x,
                        geometry.extensionStartOffset.y,
                      ]}
                      stroke={PREVIEW_STROKE_COLOR}
                      strokeWidth={getScreenInvariantSize(1.5)}
                      dash={[getScreenInvariantSize(8), getScreenInvariantSize(4)]}
                    />
                    <Line
                      points={[
                        geometry.extensionEnd.x,
                        geometry.extensionEnd.y,
                        geometry.extensionEndOffset.x,
                        geometry.extensionEndOffset.y,
                      ]}
                      stroke={PREVIEW_STROKE_COLOR}
                      strokeWidth={getScreenInvariantSize(1.5)}
                      dash={[getScreenInvariantSize(8), getScreenInvariantSize(4)]}
                    />
                    <Line
                      points={[
                        geometry.extensionStartOffset.x,
                        geometry.extensionStartOffset.y,
                        geometry.extensionEndOffset.x,
                        geometry.extensionEndOffset.y,
                      ]}
                      stroke={PREVIEW_STROKE_COLOR}
                      strokeWidth={getScreenInvariantSize(1.5)}
                      dash={[getScreenInvariantSize(8), getScreenInvariantSize(4)]}
                    />
                    {renderDimensionMarker(
                      geometry.extensionStartOffset,
                      { x: geometry.ux, y: geometry.uy },
                      PREVIEW_STROKE_COLOR,
                      "active-dimension-start-marker",
                    )}
                    {renderDimensionMarker(
                      geometry.extensionEndOffset,
                      { x: -geometry.ux, y: -geometry.uy },
                      PREVIEW_STROKE_COLOR,
                      "active-dimension-end-marker",
                    )}
                    <Text
                      x={geometry.textPoint.x}
                      y={geometry.textPoint.y}
                      text={dimensionText}
                      offsetX={textAnchor.offsetX}
                      offsetY={textAnchor.offsetY}
                      fontSize={textAnchor.fontSize}
                      fontStyle="bold"
                      fill={PREVIEW_TEXT_COLOR}
                      rotation={geometry.textRotation}
                      listening={false}
                    />
                  </>
                );
              })()}

            {activeObjectDimension &&
              (() => {
                const shape = getShapeById(activeObjectDimension.shapeId);
                if (!shape) return null;

                return renderAutoDimensionPreview(shape, activeObjectDimension.offset);
              })()}

            {transformState?.mode === "rotate" &&
              transformState.referencePoint &&
              numericEdit?.kind === "scale-rotate" && (
                <>
                  <Line
                    points={[
                      transformState.basePoint.x,
                      transformState.basePoint.y,
                      transformState.referencePoint.x,
                      transformState.referencePoint.y,
                    ]}
                    stroke={PREVIEW_STROKE_COLOR}
                    strokeWidth={getScreenInvariantSize(1.5)}
                    dash={[getScreenInvariantSize(8), getScreenInvariantSize(4)]}
                    listening={false}
                  />
                  <Line
                    points={[
                      transformState.basePoint.x,
                      transformState.basePoint.y,
                      numericEdit.position.x,
                      numericEdit.position.y,
                    ]}
                    stroke="#d9480f"
                    strokeWidth={getScreenInvariantSize(1.5)}
                    dash={[getScreenInvariantSize(8), getScreenInvariantSize(4)]}
                    listening={false}
                  />
                </>
              )}

            {hoverSnap &&
              (draggedGrip ||
                isGripModeActive ||
                toolMode === "move" ||
                toolMode === "copy" ||
                toolMode === "scale" ||
                toolMode === "rotate" ||
                toolMode === "polyline" ||
                toolMode === "singleLifeLine" ||
                toolMode === "buildPolyline" ||
                (toolMode === "hatch" && hatchToolMode === "build") ||
                isRoofComponentToolMode(toolMode) ||
                toolMode === "roof" ||
                toolMode === "multiGableRoof" ||
                toolMode === "gableRoof" ||
                toolMode === "curvedRoof" ||
                toolMode === "singlePitchedRoof" ||
                toolMode === "multiShedRoof" ||
                toolMode === "roofHipValley" ||
                toolMode === "obstaclePolyline" ||
                toolMode === "holePolyline" ||
                toolMode === "singlePointCircle" ||
                toolMode === "pointSeries" ||
                toolMode === "rectangle" ||
                toolMode === "holeCircle" ||
                toolMode === "obstacleCircle" ||
                toolMode === "circle" ||
                toolMode === "arc" ||
                toolMode === "ruler" ||
                toolMode === "dimension") && (
                <>
                  <Circle
                    x={hoverSnap.x}
                    y={hoverSnap.y}
                    radius={getScreenInvariantSize(7)}
                    stroke="#d9480f"
                    strokeWidth={getScreenInvariantSize(2)}
                    fill="#fff4e6"
                    listening={false}
                  />
                  <Text
                    x={hoverSnap.x + getScreenInvariantSize(10)}
                    y={hoverSnap.y - getScreenInvariantSize(18)}
                    text={hoverSnap.type}
                    fontSize={getScreenInvariantSize(12)}
                    fontStyle="bold"
                    fill="#d9480f"
                    listening={false}
                  />
                </>
              )}

            {invalidGripFeedback && (
              <>
                <Circle
                  x={invalidGripFeedback.point.x}
                  y={invalidGripFeedback.point.y}
                  radius={getScreenInvariantSize(7)}
                  stroke="#dc2626"
                  strokeWidth={getScreenInvariantSize(2)}
                  fill="rgba(254, 226, 226, 0.9)"
                  listening={false}
                />
                <Line
                  points={[
                    invalidGripFeedback.point.x - getScreenInvariantSize(5),
                    invalidGripFeedback.point.y - getScreenInvariantSize(5),
                    invalidGripFeedback.point.x + getScreenInvariantSize(5),
                    invalidGripFeedback.point.y + getScreenInvariantSize(5),
                  ]}
                  stroke="#dc2626"
                  strokeWidth={getScreenInvariantSize(2)}
                  lineCap="round"
                  listening={false}
                />
                <Line
                  points={[
                    invalidGripFeedback.point.x - getScreenInvariantSize(5),
                    invalidGripFeedback.point.y + getScreenInvariantSize(5),
                    invalidGripFeedback.point.x + getScreenInvariantSize(5),
                    invalidGripFeedback.point.y - getScreenInvariantSize(5),
                  ]}
                  stroke="#dc2626"
                  strokeWidth={getScreenInvariantSize(2)}
                  lineCap="round"
                  listening={false}
                />
              </>
            )}

            {selectionBox && toolMode === "select" && (
              <Line
                points={[
                  selectionBox.start.x,
                  selectionBox.start.y,
                  selectionBox.current.x,
                  selectionBox.start.y,
                  selectionBox.current.x,
                  selectionBox.current.y,
                  selectionBox.start.x,
                  selectionBox.current.y,
                ]}
                closed
                stroke="#1d5cab"
                strokeWidth={getScreenInvariantSize(1.5)}
                dash={
                  selectionBox.current.x < selectionBox.start.x
                    ? [getScreenInvariantSize(8), getScreenInvariantSize(4)]
                    : undefined
                }
                fill="rgba(29, 92, 171, 0.12)"
                listening={false}
              />
            )}
          </Layer>
        </Stage>
        {editingTextEntity && editingTextInputStyle && (
          <textarea
            ref={textEditInputRef}
            className="shadow-[0_6px_18px_rgba(29,92,171,0.22)]"
            style={editingTextInputStyle}
            value={editingTextDraft}
            onMouseDown={(event) => event.stopPropagation()}
            onPointerDown={(event) => event.stopPropagation()}
            onChange={(event) => setEditingTextDraft(event.target.value)}
            onBlur={commitTextEditing}
            onKeyDown={(event) => {
              event.stopPropagation();
              if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
                event.preventDefault();
                commitTextEditing();
                return;
              }

              if (event.key === "Escape") {
                event.preventDefault();
                cancelTextEditing();
              }
            }}
          />
        )}
      </div>
    </div>
  );
}
